/**
 * A function to create a log object for the 'openConnector' event (evid: 101)
 *
 * Event description: User opened WCE connector in Google Data Studio
 *
 * @param {openConnectorParams} params - The parameters for this event
 * @return {LogObject<openConnectorParams>} LogObject<openConnectorParams>
 */
export function openConnector(params) {
    return { evid: 101, src: 103, endpoint: '', params };
}
/**
 * A function to create a log object for the 'authenticationStart' event (evid: 102)
 *
 * Event description: User's authentication has been started
 *
 * @param {authenticationStartParams} params - The parameters for this event
 * @return {LogObject<authenticationStartParams>} LogObject<authenticationStartParams>
 */
export function authenticationStart(params) {
    return { evid: 102, src: 103, endpoint: '', params };
}
/**
 * A function to create a log object for the 'authenticationFinish' event (evid: 103)
 *
 * Event description: User's authentication has been finished
 *
 * @param {authenticationFinishParams} params - The parameters for this event
 * @property {number} accessCheckMillisSpent - Milliseconds spent on checking access
 * @property {number} accessCheckTimeSpent - Milliseconds spent on checking access
 * @property {number} timeSpent - Milliseconds spent on whole operation
 * @property {number} millisSpent - Milliseconds spent on whole operation
 * @property {boolean} hasAccess - has_access
 * @return {LogObject<authenticationFinishParams>} LogObject<authenticationFinishParams>
 */
export function authenticationFinish(params) {
    return { evid: 103, src: 103, endpoint: '', params };
}
/**
 * A function to create a log object for the 'tablesRetrievingStart' event (evid: 104)
 *
 * Event description: Start retrieving from server data about available tables and their schemas
 *
 * @param {tablesRetrievingStartParams} params - The parameters for this event
 * @return {LogObject<tablesRetrievingStartParams>} LogObject<tablesRetrievingStartParams>
 */
export function tablesRetrievingStart(params) {
    return { evid: 104, src: 103, endpoint: '', params };
}
/**
 * A function to create a log object for the 'tablesRetrievingFinish' event (evid: 105)
 *
 * Event description: Retrieving data from server about available tables and their schemas has been finished
 *
 * @param {tablesRetrievingFinishParams} params - The parameters for this event
 * @property {number} serverCallMillisSpent - Milliseconds spent on retrieving response from server
 * @property {number} accessCheckMillisSpent - Milliseconds spent on checking access
 * @property {number} numMetasites - Number of metasites fetched
 * @property {number} accessCheckTimeSpent - Milliseconds spent on checking access
 * @property {number} jsonParsingMillisSpent - Milliseconds spent on parsing JSON response
 * @property {number} jsonParsingTimeSpent - Milliseconds spent on parsing JSON response
 * @property {number} numTables - Number of tables fetched
 * @property {number} timeSpent - Milliseconds spent on whole operation
 * @property {number} serverCallTimeSpent - Milliseconds spent on retrieving response from server
 * @property {number} millisSpent - Milliseconds spent on whole operation
 * @return {LogObject<tablesRetrievingFinishParams>} LogObject<tablesRetrievingFinishParams>
 */
export function tablesRetrievingFinish(params) {
    return { evid: 105, src: 103, endpoint: '', params };
}
/**
 * A function to create a log object for the 'extractSchema' event (evid: 106)
 *
 * Event description: Extract fields schema from tables data
 *
 * @param {extractSchemaParams} params - The parameters for this event
 * @property {number} amountFields - Number of fields in schema
 * @property {string} msid - Metasite ID
 * @property {string} table - Table name
 * @return {LogObject<extractSchemaParams>} LogObject<extractSchemaParams>
 */
export function extractSchema(params) {
    return { evid: 106, src: 103, endpoint: '', params };
}
/**
 * A function to create a log object for the 'dataRetrievingStart' event (evid: 107)
 *
 * Event description: Start retrieving events from server
 *
 * @param {dataRetrievingStartParams} params - The parameters for this event
 * @property {DateTime} dateRangeEnd - End of request's date range
 * @property {DateTime} dateRangeStart - Start of request's date range
 * @property {number} amountFields - Number of requested fields
 * @property {boolean} isSampleRequest - Indicates whether it is sample request or not
 * @property {string} fields - Serialized JSON array with field names
 * @property {string} requestHash - Hash of request params along with created date
 * @property {string} msid - Metasite ID
 * @property {string} table - Table name
 * @return {LogObject<dataRetrievingStartParams>} LogObject<dataRetrievingStartParams>
 */
export function dataRetrievingStart(params) {
    return { evid: 107, src: 103, endpoint: '', params };
}
/**
 * A function to create a log object for the 'dataRetrievingFinish' event (evid: 108)
 *
 * Event description: Finish retrieving events from server
 *
 * @param {dataRetrievingFinishParams} params - The parameters for this event
 * @property {number} serverCallMillisSpent - Milliseconds spent on retrieving response from server
 * @property {number} accessCheckMillisSpent - Milliseconds spent on checking access
 * @property {DateTime} dateRangeEnd - End of request's date range
 * @property {DateTime} dateRangeStart - Start of request's date range
 * @property {number} amountFields - Number of requested fields
 * @property {boolean} isSampleRequest - Indicates whether it is sample request or not
 * @property {number} accessCheckTimeSpent - Milliseconds spent on checking access
 * @property {number} jsonParsingMillisSpent - Milliseconds spent on parsing JSON response
 * @property {number} jsonParsingTimeSpent - Milliseconds spent on parsing JSON response
 * @property {number} timeSpent - Milliseconds spent on whole operation
 * @property {number} serverCallTimeSpent - Milliseconds spent on retrieving response from server
 * @property {number} responseSize - Response size in bytes
 * @property {number} millisSpent - Milliseconds spent on whole operation
 * @property {string} fields - Serialized JSON array with field names
 * @property {string} requestHash - Hash of request params along with created date
 * @property {string} msid - Metasite ID
 * @property {number} numRows - Number of rows fetched
 * @property {string} table - Table name
 * @return {LogObject<dataRetrievingFinishParams>} LogObject<dataRetrievingFinishParams>
 */
export function dataRetrievingFinish(params) {
    return { evid: 108, src: 103, endpoint: '', params };
}
/**
 * A function to create a log object for the 'serverCallFailed' event (evid: 110)
 *
 * Event description: Failed call to server
 *
 * @param {serverCallFailedParams} params - The parameters for this event
 * @property {string} url - Server endpoint
 * @property {string} fetchOpts - Additional options for request to server
 * @property {string} operation - Name of operation
 * @property {string} error - Error details
 * @return {LogObject<serverCallFailedParams>} LogObject<serverCallFailedParams>
 */
export function serverCallFailed(params) {
    return { evid: 110, src: 103, endpoint: '', params };
}
/**
 * A function to create a log object for the 'jsonParsingFailed' event (evid: 111)
 *
 * Event description: JSON parsing failed
 *
 * @param {jsonParsingFailedParams} params - The parameters for this event
 * @property {number} sizeBytes - Size in bytes
 * @property {string} operation - Name of operation
 * @property {string} error - Error details
 * @return {LogObject<jsonParsingFailedParams>} LogObject<jsonParsingFailedParams>
 */
export function jsonParsingFailed(params) {
    return { evid: 111, src: 103, endpoint: '', params };
}
/**
 * A function to create a log object for the 'processedEventCount' event (evid: 200)
 *
 * Event description: Count of events, processed by WCE mapreduce jobs (Cruncher, Cowboy, Lasso).
 *
 * @param {processedEventCountParams} params - The parameters for this event
 * @property {string} jobName - Name of the job that processed events
 * @property {number} total - Total event count
 * @property {number} invalid - Invalid event count
 * @property {string} jobId - Id of the job
 * @property {number} valid - Valid event count
 * @return {LogObject<processedEventCountParams>} LogObject<processedEventCountParams>
 */
export function processedEventCount(params) {
    return { evid: 200, src: 103, endpoint: '', params };
}
/**
 * A function to create a log object for the 'googleCloudLoaderModalShowSrc105Evid1' event (evid: 1)
 *
 * Event description: Fiers when the loader modal pops, while connecting to google cloud
 *
 * @param {googleCloudLoaderModalShowSrc105Evid1Params} params - The parameters for this event
 * @property {string} endpoint_url - endpoint_url
 * @property {GUID} uuid - uuid
 * @property {string} service - stackdriver
 * @property {GUID} msid - msid
 * @property {string} action - connect
 * @return {LogObject<googleCloudLoaderModalShowSrc105Evid1Params>} LogObject<googleCloudLoaderModalShowSrc105Evid1Params>
 */
export function googleCloudLoaderModalShowSrc105Evid1(params) {
    return { evid: 1, src: 105, endpoint: '', params };
}
/**
 * A function to create a log object for the 'viewRcAsALiveSite' event (evid: 101)
 *
 * Event description: Fires when a user clicks on the view RC as live site button in the release manager
 *
 * @param {viewRcAsALiveSiteParams} params - The parameters for this event
 * @property {GUID} uuid - uuid
 * @property {number} revision - revision num
 * @property {GUID} msid - msid
 * @return {LogObject<viewRcAsALiveSiteParams>} LogObject<viewRcAsALiveSiteParams>
 */
export function viewRcAsALiveSite(params) {
    return { evid: 101, src: 105, endpoint: '', params };
}
/**
 * A function to create a log object for the 'rcQuickMenuActionClicked' event (evid: 103)
 *
 * Event description: Fires when a user clicks on one of the RC's menu options
 *
 * @param {rcQuickMenuActionClickedParams} params - The parameters for this event
 * @property {GUID} uuid - uuid
 * @property {number} revision - revision num
 * @property {string} origin - one of { site_branch_menu}
 * @property {GUID} msid - msid
 * @property {string} action - one of: edit-rc, publish-rc, delete-rc
 * @return {LogObject<rcQuickMenuActionClickedParams>} LogObject<rcQuickMenuActionClickedParams>
 */
export function rcQuickMenuActionClicked(params) {
    return { evid: 103, src: 105, endpoint: '', params };
}
/**
 * A function to create a log object for the 'changeRcExposureClicked' event (evid: 104)
 *
 * Event description: Fires when a user approves a change in the collection's exposure and it is changed successfully
 *
 * @param {changeRcExposureClickedParams} params - The parameters for this event
 * @property {GUID} uuid - uuid
 * @property {string} label - label
 * @property {number} revision - revision num
 * @property {number} old_exposure - old exposure percentage
 * @property {GUID} msid - msid
 * @property {number} new_exposure - new exposure percentage
 * @return {LogObject<changeRcExposureClickedParams>} LogObject<changeRcExposureClickedParams>
 */
export function changeRcExposureClicked(params) {
    return { evid: 104, src: 105, endpoint: '', params };
}
/**
 * A function to create a log object for the 'publishRcClicked' event (evid: 105)
 *
 * Event description: Fires when a user approves publishing RC and it is published successfully
 *
 * @param {publishRcClickedParams} params - The parameters for this event
 * @property {GUID} uuid - uuid
 * @property {number} revision - revision num
 * @property {GUID} msid - msid
 * @return {LogObject<publishRcClickedParams>} LogObject<publishRcClickedParams>
 */
export function publishRcClicked(params) {
    return { evid: 105, src: 105, endpoint: '', params };
}
/**
 * A function to create a log object for the 'deleteRcClicked' event (evid: 106)
 *
 * Event description: Fires when a user click the delete button on a delete RC popup and
 *
 * @param {deleteRcClickedParams} params - The parameters for this event
 * @property {GUID} uuid - uuid
 * @property {number} revision - revision num
 * @property {GUID} msid - msid
 * @return {LogObject<deleteRcClickedParams>} LogObject<deleteRcClickedParams>
 */
export function deleteRcClicked(params) {
    return { evid: 106, src: 105, endpoint: '', params };
}
/**
 * A function to create a log object for the 'restoreVersionClicked' event (evid: 107)
 *
 * Event description: Fires when a user clicks on the restore version buttons
 *
 * @param {restoreVersionClickedParams} params - The parameters for this event
 * @property {GUID} uuid - uuid
 * @property {GUID} msid - msid
 * @return {LogObject<restoreVersionClickedParams>} LogObject<restoreVersionClickedParams>
 */
export function restoreVersionClicked(params) {
    return { evid: 107, src: 105, endpoint: '', params };
}
/**
 * A function to create a log object for the 'monitorYourSiteClicked' event (evid: 108)
 *
 * Event description: Fires when a user clicks on the  site monitor/logging from the Release Manager
 *
 * @param {monitorYourSiteClickedParams} params - The parameters for this event
 * @property {GUID} uuid - uuid
 * @property {GUID} msid - msid
 * @return {LogObject<monitorYourSiteClickedParams>} LogObject<monitorYourSiteClickedParams>
 */
export function monitorYourSiteClicked(params) {
    return { evid: 108, src: 105, endpoint: '', params };
}
/**
 * A function to create a log object for the 'productionToolsPanelOpened' event (evid: 109)
 *
 * Event description: Fires when a panel opens in the Business manager
 *
 * @param {productionToolsPanelOpenedParams} params - The parameters for this event
 * @property {GUID} uuid - uuid
 * @property {string} panel_name - panel name
 * @property {GUID} msid - msid
 * @return {LogObject<productionToolsPanelOpenedParams>} LogObject<productionToolsPanelOpenedParams>
 */
export function productionToolsPanelOpened(params) {
    return { evid: 109, src: 105, endpoint: '', params };
}
/**
 * A function to create a log object for the 'productionToolsPanelClosed' event (evid: 110)
 *
 * Event description: Fires when a panel closes in the Business Manager. Since November 2019 (new version) the event is sent only when the user discards/cancels the action.
 *
 * @param {productionToolsPanelClosedParams} params - The parameters for this event
 * @property {GUID} uuid - uuid
 * @property {string} panel_name - panel name
 * @property {string} origin - x/keep_button/cancel_button
 * @property {GUID} msid - msid
 * @return {LogObject<productionToolsPanelClosedParams>} LogObject<productionToolsPanelClosedParams>
 */
export function productionToolsPanelClosed(params) {
    return { evid: 110, src: 105, endpoint: '', params };
}
/**
 * A function to create a log object for the 'gradualRolloutUpdated' event (evid: 115)
 *
 * Event description: Fired by the Release-Manager server when the percentages of a gradual-rollout are changed
 *
 * @param {gradualRolloutUpdatedParams} params - The parameters for this event
 * @property {string} rcPercentages - JSON array of RC revision exposure percentages
 * @property {GUID} siteId - siteId
 * @property {number} publishedPercentage - Exposure percentage of published revision
 * @property {string} rcRevisions - JSON array of RC revisions
 * @property {number} publishedRevision - Published revision number
 * @property {GUID} metaSiteId - metaSiteId
 * @return {LogObject<gradualRolloutUpdatedParams>} LogObject<gradualRolloutUpdatedParams>
 */
export function gradualRolloutUpdated(params) {
    return { evid: 115, src: 105, endpoint: '', params };
}
/**
 * A function to create a log object for the 'gradualRolloutDeleted' event (evid: 116)
 *
 * Event description: Fired from the release-manager server when a gradual-rollout is terminated by the user
 *
 * @param {gradualRolloutDeletedParams} params - The parameters for this event
 * @property {GUID} siteId - siteId
 * @property {GUID} metaSiteId - metaSiteId
 * @return {LogObject<gradualRolloutDeletedParams>} LogObject<gradualRolloutDeletedParams>
 */
export function gradualRolloutDeleted(params) {
    return { evid: 116, src: 105, endpoint: '', params };
}
/**
 * A function to create a log object for the 'rcPublished' event (evid: 117)
 *
 * Event description: Fired from the release-manager server when a user publishes an RC revision
 *
 * @param {rcPublishedParams} params - The parameters for this event
 * @property {number} rcRevisionPublished - The RC revision published
 * @property {GUID} siteId - site_id
 * @property {GUID} metaSiteId - msid
 * @return {LogObject<rcPublishedParams>} LogObject<rcPublishedParams>
 */
export function rcPublished(params) {
    return { evid: 117, src: 105, endpoint: '', params };
}
/**
 * A function to create a log object for the 'secretKeyManagerAction' event (evid: 200)
 *
 * Event description: When the user does action on a secret key in the Secrets Manager
 *
 * @param {secretKeyManagerActionParams} params - The parameters for this event
 * @property {string} description - description
 * @property {GUID} key_id - key_id
 * @property {number} row_number - item row index: 1,2,3,etc...
 * @property {string} origin - store_new_key, edit_key
 * @property {GUID} msid - msid
 * @property {string} key_name - key_name
 * @property {string} action - which action the user choose: add_key, edit, delete
 * @return {LogObject<secretKeyManagerActionParams>} LogObject<secretKeyManagerActionParams>
 */
export function secretKeyManagerAction(params) {
    return { evid: 200, src: 105, endpoint: '', params };
}
/**
 * A function to create a log object for the 'secretKeyManagerOpenPopup' event (evid: 201)
 *
 * Event description: When a pop-up form opens in the Secrets Manager
 *
 * @param {secretKeyManagerOpenPopupParams} params - The parameters for this event
 * @property {string} name - pop-up form name
 * @property {string} origin - from where the user open the page: store_new_key_button, more_actions_button
 * @property {GUID} msid - msid
 * @property {string} action - which action the user choose: add_key, edit
 * @return {LogObject<secretKeyManagerOpenPopupParams>} LogObject<secretKeyManagerOpenPopupParams>
 */
export function secretKeyManagerOpenPopup(params) {
    return { evid: 201, src: 105, endpoint: '', params };
}
/**
 * A function to create a log object for the 'secretKeyManagerDiscardPopup' event (evid: 202)
 *
 * Event description: When a pop-up form closed in the Secrets Manager
 *
 * @param {secretKeyManagerDiscardPopupParams} params - The parameters for this event
 * @property {string} name - pop-up form name
 * @property {string} origin - discard_changes, x, keep_my_key, cancel
 * @property {GUID} msid - msid
 * @return {LogObject<secretKeyManagerDiscardPopupParams>} LogObject<secretKeyManagerDiscardPopupParams>
 */
export function secretKeyManagerDiscardPopup(params) {
    return { evid: 202, src: 105, endpoint: '', params };
}
/**
 * A function to create a log object for the 'monitorServiceUserDidnTConnectAccount' event (evid: 300)
 *
 * Event description: when Google login popup is closing
 *
 * @param {monitorServiceUserDidnTConnectAccountParams} params - The parameters for this event
 * @property {GUID} uuid - uuid
 * @property {string} service - stackdriver
 * @property {GUID} msid - msid
 * @property {string} action - connect/not_connect
 * @return {LogObject<monitorServiceUserDidnTConnectAccountParams>} LogObject<monitorServiceUserDidnTConnectAccountParams>
 */
export function monitorServiceUserDidnTConnectAccount(params) {
    return { evid: 300, src: 105, endpoint: '', params };
}
/**
 * A function to create a log object for the 'siteMonitoringServiceClick' event (evid: 301)
 *
 * Event description: Fires when a user chooses a monitoring service from the site monitoring main page section
 *
 * @param {siteMonitoringServiceClickParams} params - The parameters for this event
 * @property {GUID} uuid - uuid
 * @property {string} service - http endpoint, stackdriver, etc.
 * @property {GUID} msid - msid
 * @return {LogObject<siteMonitoringServiceClickParams>} LogObject<siteMonitoringServiceClickParams>
 */
export function siteMonitoringServiceClick(params) {
    return { evid: 301, src: 105, endpoint: '', params };
}
/**
 * A function to create a log object for the 'monitorServiceActionClick' event (evid: 302)
 *
 * Event description: Fires when a user connects/disconnects a monitoring service
 *
 * @param {monitorServiceActionClickParams} params - The parameters for this event
 * @property {string} endpoint_url - endpoint_url
 * @property {GUID} uuid - uuid
 * @property {string} service - http endpoint, stackdriver, etc.
 * @property {GUID} msid - msid
 * @property {string} action - connect, disconnect
 * @return {LogObject<monitorServiceActionClickParams>} LogObject<monitorServiceActionClickParams>
 */
export function monitorServiceActionClick(params) {
    return { evid: 302, src: 105, endpoint: '', params };
}
/**
 * A function to create a log object for the 'monitorServiceSuccessPopupApproveClick' event (evid: 303)
 *
 * Event description: when the user clicks OK or X in successful monitoring connection popup
 *
 * @param {monitorServiceSuccessPopupApproveClickParams} params - The parameters for this event
 * @property {string} endpoint_url - endpoint_url
 * @property {GUID} uuid - uuid
 * @property {string} service - http endpoint, stackdriver, etc.
 * @property {GUID} msid - msid
 * @return {LogObject<monitorServiceSuccessPopupApproveClickParams>} LogObject<monitorServiceSuccessPopupApproveClickParams>
 */
export function monitorServiceSuccessPopupApproveClick(params) {
    return { evid: 303, src: 105, endpoint: '', params };
}
/**
 * A function to create a log object for the 'viewSiteEventsClick' event (evid: 304)
 *
 * Event description: Fires when a user click on "view site events"
 *
 * @param {viewSiteEventsClickParams} params - The parameters for this event
 * @property {GUID} uuid - uuid
 * @property {GUID} msid - msid
 * @return {LogObject<viewSiteEventsClickParams>} LogObject<viewSiteEventsClickParams>
 */
export function viewSiteEventsClick(params) {
    return { evid: 304, src: 105, endpoint: '', params };
}
/**
 * A function to create a log object for the 'liveEventsViewActionClick' event (evid: 305)
 *
 * Event description: Fires when a user click/ view state at wix logs (formerly live events). use action field to distinct.
 *
 * @param {liveEventsViewActionClickParams} params - The parameters for this event
 * @property {GUID} uuid - uuid
 * @property {GUID} msid - msid
 * @property {string} action - pause, play, clear, more, less, backward_navigation_logs, forward_navigation_logs,   more_logs_banner_view, more_logs_banner_click, connection_error_view.
 * @return {LogObject<liveEventsViewActionClickParams>} LogObject<liveEventsViewActionClickParams>
 */
export function liveEventsViewActionClick(params) {
    return { evid: 305, src: 105, endpoint: '', params };
}
/**
 * A function to create a log object for the 'monitorServiceDisconnectPopupClick' event (evid: 306)
 *
 * Event description: Fires when a user OKs a disconnect from a monitoring service
 *
 * @param {monitorServiceDisconnectPopupClickParams} params - The parameters for this event
 * @property {GUID} uuid - uuid
 * @property {string} service - http endpoint, stackdriver, etc.
 * @property {GUID} msid - msid
 * @return {LogObject<monitorServiceDisconnectPopupClickParams>} LogObject<monitorServiceDisconnectPopupClickParams>
 */
export function monitorServiceDisconnectPopupClick(params) {
    return { evid: 306, src: 105, endpoint: '', params };
}
/**
 * A function to create a log object for the 'monitorServiceFailToConnectPopup' event (evid: 307)
 *
 * Event description: Fires when a user click ok on a successful monitoring connection popup
 *
 * @param {monitorServiceFailToConnectPopupParams} params - The parameters for this event
 * @property {string} endpoint_url - endpoint_url
 * @property {GUID} uuid - uuid
 * @property {string} service - http endpoint, stackdriver, etc.
 * @property {GUID} msid - msid
 * @property {string} action - connect, disconnect
 * @return {LogObject<monitorServiceFailToConnectPopupParams>} LogObject<monitorServiceFailToConnectPopupParams>
 */
export function monitorServiceFailToConnectPopup(params) {
    return { evid: 307, src: 105, endpoint: '', params };
}
/**
 * A function to create a log object for the 'existingLogInViewSiteEvents' event (evid: 308)
 *
 * Event description: Fires when a user click on "view site events" and receives at least 1 log
 *
 * @param {existingLogInViewSiteEventsParams} params - The parameters for this event
 * @property {GUID} msid - msid
 * @property {GUID} uuid - uuid
 * @return {LogObject<existingLogInViewSiteEventsParams>} LogObject<existingLogInViewSiteEventsParams>
 */
export function existingLogInViewSiteEvents(params) {
    return { evid: 308, src: 105, endpoint: '', params };
}
/**
 * A function to create a log object for the 'successNavigationToSdBackendEvent' event (evid: 309)
 *
 * Event description: Fires when the backend side authorized the user and  provided a direct link to SD
 *
 * @param {successNavigationToSdBackendEventParams} params - The parameters for this event
 * @property {boolean} first_time_for_the_user - true/false
 * @property {GUID} uuid - uuid
 * @property {string} service - http endpoint, stackdriver, etc.
 * @property {GUID} msid - msid
 * @return {LogObject<successNavigationToSdBackendEventParams>} LogObject<successNavigationToSdBackendEventParams>
 */
export function successNavigationToSdBackendEvent(params) {
    return { evid: 309, src: 105, endpoint: '', params };
}
/**
 * A function to create a log object for the 'openServiceClick' event (evid: 310)
 *
 * Event description: Fires when a user click on 'Open Stackdriver" which is an attempt to navigate to SD
 *
 * @param {openServiceClickParams} params - The parameters for this event
 * @property {GUID} uuid - uuid
 * @property {string} service - http endpoint, stackdriver, etc.
 * @property {GUID} msid - msid
 * @return {LogObject<openServiceClickParams>} LogObject<openServiceClickParams>
 */
export function openServiceClick(params) {
    return { evid: 310, src: 105, endpoint: '', params };
}
/**
 * A function to create a log object for the 'googleCloudLoaderModalSrc105Evid333' event (evid: 333)
 *
 * Event description: Fiers when the loader modal pops, while connecting to google cloud
 *
 * @param {googleCloudLoaderModalSrc105Evid333Params} params - The parameters for this event
 * @property {string} endpoint_url - endpoint_url
 * @property {GUID} uuid - uuid
 * @property {string} service - stackdriver
 * @property {GUID} msid - msid
 * @property {string} action - connect
 * @return {LogObject<googleCloudLoaderModalSrc105Evid333Params>} LogObject<googleCloudLoaderModalSrc105Evid333Params>
 */
export function googleCloudLoaderModalSrc105Evid333(params) {
    return { evid: 333, src: 105, endpoint: '', params };
}
/**
 * A function to create a log object for the 'platformVisibilityRequestAdditionalResources' event (evid: 400)
 *
 * Event description: When the server gets a new request for resource
 *
 * @param {platformVisibilityRequestAdditionalResourcesParams} params - The parameters for this event
 * @property {string} details - The details the user adds in the request
 * @property {string} resource_type - The type of the requested resource
 * @return {LogObject<platformVisibilityRequestAdditionalResourcesParams>} LogObject<platformVisibilityRequestAdditionalResourcesParams>
 */
export function platformVisibilityRequestAdditionalResources(params) {
    return { evid: 400, src: 105, endpoint: '', params };
}
/**
 * A function to create a log object for the 'requestMonitoringLearnMoreClicked' event (evid: 406)
 *
 * Event description: When the user clicks “Learn More” on the Request Monitoring page in the BM
 *
 * @param {requestMonitoringLearnMoreClickedParams} params - The parameters for this event
 * @property {Url} url - the url of the documentation
 * @property {string} dashboardTitle - Name of a tile that was loaded (e.g. backend_request, request_failure, request_duration, request_rate, top_100_requests) page_haeder for head of page
 * @property {string} pageName - Name of the main page that was loaded (e.g. backend_request).
 * @return {LogObject<requestMonitoringLearnMoreClickedParams>} LogObject<requestMonitoringLearnMoreClickedParams>
 */
export function requestMonitoringLearnMoreClicked(params) {
    return { evid: 406, src: 105, endpoint: '', params };
}
/**
 * A function to create a log object for the 'requestMonitoringHoverOnInfoBadge' event (evid: 407)
 *
 * Event description: When the user hovers on Info Badges on the Request Monitoring page in the BM
 *
 * @param {requestMonitoringHoverOnInfoBadgeParams} params - The parameters for this event
 * @property {string} dashboardTitle - Name of a tile that was loaded (e.g. backend_request, request_failure, request_duration, request_rate, top_100_requests)
 * @property {string} pageName - Name of the main page that was loaded (e.g. backend_request).
 * @property {string} value - Name of a measure or a title that was hovered (e.g. overview, )
 * @return {LogObject<requestMonitoringHoverOnInfoBadgeParams>} LogObject<requestMonitoringHoverOnInfoBadgeParams>
 */
export function requestMonitoringHoverOnInfoBadge(params) {
    return { evid: 407, src: 105, endpoint: '', params };
}
/**
 * A function to create a log object for the 'requestMonitoringClickOnActions' event (evid: 410)
 *
 * Event description: When the user clicks on actions at the bottom of the page of the Request Monitoring in the BM
 *
 * @param {requestMonitoringClickOnActionsParams} params - The parameters for this event
 * @property {string} actionName - Name of an action that was clicked   (e.g. optimize_your_code, get_more_resources, connect_to_site_logs)
 * @property {string} origin - where this action was pressed - one of - bottom page, top page, overview (if null then bottom page)
 * @return {LogObject<requestMonitoringClickOnActionsParams>} LogObject<requestMonitoringClickOnActionsParams>
 */
export function requestMonitoringClickOnActions(params) {
    return { evid: 410, src: 105, endpoint: '', params };
}
/**
 * A function to create a log object for the 'requestMonitoringBreakdownTabChange' event (evid: 411)
 *
 * Event description: When the user changes between tabs in the monitoring dashboard
 *
 * @param {requestMonitoringBreakdownTabChangeParams} params - The parameters for this event
 * @property {string} newTab - the tab switched to
 * @property {number} oldTab - the tab switched from
 * @return {LogObject<requestMonitoringBreakdownTabChangeParams>} LogObject<requestMonitoringBreakdownTabChangeParams>
 */
export function requestMonitoringBreakdownTabChange(params) {
    return { evid: 411, src: 105, endpoint: '', params };
}
/**
 * A function to create a log object for the 'newVersionClicked' event (evid: 501)
 *
 * Event description: Fires when a user clicks on Create New Version
 *
 * @param {newVersionClickedParams} params - The parameters for this event
 * @property {string} origin - one of: version_manager, etc'
 * @property {GUID} msid - msid
 * @return {LogObject<newVersionClickedParams>} LogObject<newVersionClickedParams>
 */
export function newVersionClicked(params) {
    return { evid: 501, src: 105, endpoint: '', params };
}
/**
 * A function to create a log object for the 'createVersionClicked' event (evid: 502)
 *
 * Event description: Fires when a user clicks Create version clicked on the new version window
 *
 * @param {createVersionClickedParams} params - The parameters for this event
 * @property {GUID} original_version_id - original_version_id
 * @property {string} origin - one of: versions_manager, etc'
 * @property {string} new_version_name - new_version_name
 * @property {string} original_version_name - original_version_name
 * @property {GUID} msid - msid
 * @return {LogObject<createVersionClickedParams>} LogObject<createVersionClickedParams>
 */
export function createVersionClicked(params) {
    return { evid: 502, src: 105, endpoint: '', params };
}
/**
 * A function to create a log object for the 'cancelCreateVersionClicked' event (evid: 503)
 *
 * Event description: Fires when a user clicks on Cancel on Create version
 *
 * @param {cancelCreateVersionClickedParams} params - The parameters for this event
 * @property {string} origin - one of: x_button, cancel_button
 * @property {string} button_name - one of: x_button, cancel_button
 * @property {GUID} msid - msid
 * @return {LogObject<cancelCreateVersionClickedParams>} LogObject<cancelCreateVersionClickedParams>
 */
export function cancelCreateVersionClicked(params) {
    return { evid: 503, src: 105, endpoint: '', params };
}
/**
 * A function to create a log object for the 'newVersionCreated' event (evid: 504)
 *
 * Event description: Fires when the process of version creation is done
 *
 * @param {newVersionCreatedParams} params - The parameters for this event
 * @property {GUID} original_version_id - original_version_id
 * @property {GUID} site_version_id - new site_version_id
 * @property {string} origin - one of: version_manager, etc'
 * @property {string} new_version_name - new_version_name
 * @property {string} original_version_name - original_version_name
 * @property {GUID} msid - msid
 * @return {LogObject<newVersionCreatedParams>} LogObject<newVersionCreatedParams>
 */
export function newVersionCreated(params) {
    return { evid: 504, src: 105, endpoint: '', params };
}
/**
 * A function to create a log object for the 'versionsManagerActionsClicked' event (evid: 505)
 *
 * Event description: Fires when a user clicks on an action (delete, edit, rename,live_site,test_site) in the Versions manager
 *
 * @param {versionsManagerActionsClickedParams} params - The parameters for this event
 * @property {boolean} published_version - is the version published?
 * @property {GUID} site_version_id - site_version_id
 * @property {string} version_name - version_name
 * @property {string} origin - one of: version_manager, etc'
 * @property {GUID} msid - msid
 * @property {string} action - {delete, edit, rename,live_site,test_site}
 * @return {LogObject<versionsManagerActionsClickedParams>} LogObject<versionsManagerActionsClickedParams>
 */
export function versionsManagerActionsClicked(params) {
    return { evid: 505, src: 105, endpoint: '', params };
}
/**
 * A function to create a log object for the 'deleteVersionClicked' event (evid: 506)
 *
 * Event description: Fires when a user clicks Delete version on the Delete version window
 *
 * @param {deleteVersionClickedParams} params - The parameters for this event
 * @property {GUID} site_id - siteId
 * @property {GUID} site_version_id - site_version_id
 * @property {string} version_name - version_name
 * @property {string} origin - one of: version_manager, etc'
 * @property {GUID} msid - msid
 * @return {LogObject<deleteVersionClickedParams>} LogObject<deleteVersionClickedParams>
 */
export function deleteVersionClicked(params) {
    return { evid: 506, src: 105, endpoint: '', params };
}
/**
 * A function to create a log object for the 'cancelActionOnVersionManagerPopup' event (evid: 507)
 *
 * Event description: Fires when a user clicks on Cancel or X button on the version manager action (delete, rename) popup
 *
 * @param {cancelActionOnVersionManagerPopupParams} params - The parameters for this event
 * @property {boolean} published_version - is the version published?
 * @property {GUID} site_version_id - site_version_id
 * @property {string} button_name - one of: x_button, cancel_button
 * @property {string} version_name - version_name
 * @property {string} origin - one of: version_manager, etc'
 * @property {GUID} msid - msid
 * @property {string} popup_name - {delete, rename}
 * @return {LogObject<cancelActionOnVersionManagerPopupParams>} LogObject<cancelActionOnVersionManagerPopupParams>
 */
export function cancelActionOnVersionManagerPopup(params) {
    return { evid: 507, src: 105, endpoint: '', params };
}
/**
 * A function to create a log object for the 'versionWasDeleted' event (evid: 508)
 *
 * Event description: Fires when the process of version delete is done
 *
 * @param {versionWasDeletedParams} params - The parameters for this event
 * @property {boolean} published_version - is the version published?
 * @property {GUID} site_version_id - new site_version_id
 * @property {string} version_name - version_name
 * @property {string} origin - one of: version_manager, etc'
 * @property {GUID} msid - msid
 * @return {LogObject<versionWasDeletedParams>} LogObject<versionWasDeletedParams>
 */
export function versionWasDeleted(params) {
    return { evid: 508, src: 105, endpoint: '', params };
}
/**
 * A function to create a log object for the 'saveRenameVersionClicked' event (evid: 509)
 *
 * Event description: Fires when a user clicks save on the Rename version window
 *
 * @param {saveRenameVersionClickedParams} params - The parameters for this event
 * @property {boolean} published_version - is the version published?
 * @property {GUID} site_version_id - site_version_id
 * @property {string} origin - one of: version_manager, etc'
 * @property {string} new_version_name - new_version_name
 * @property {string} original_version_name - original_version_name
 * @property {GUID} msid - msid
 * @return {LogObject<saveRenameVersionClickedParams>} LogObject<saveRenameVersionClickedParams>
 */
export function saveRenameVersionClicked(params) {
    return { evid: 509, src: 105, endpoint: '', params };
}
/**
 * A function to create a log object for the 'createNewBranchClicked' event (evid: 520)
 *
 * Event description: fires when a users clicks on create a new branch on one of the entry points.
 *
 * @param {createNewBranchClickedParams} params - The parameters for this event
 * @property {string} origin - origin
 * @property {string} environment - business manager
 * @return {LogObject<createNewBranchClickedParams>} LogObject<createNewBranchClickedParams>
 */
export function createNewBranchClicked(params) {
    return { evid: 520, src: 105, endpoint: '', params };
}
/**
 * A function to create a log object for the 'createBranchClickedFromCreateBranchPopup' event (evid: 521)
 *
 * Event description: fires when a user press "Create Branch" button from the create branch pop up (after choosing which branch to copy form and the new branch name)
 *
 * @param {createBranchClickedFromCreateBranchPopupParams} params - The parameters for this event
 * @property {string} site_branch_name_origin - the site_branch_name which we copy
 * @property {GUID} site_branch_id_origin - the site_branch_id from which we copy
 * @property {string} site_branch_name - the site_branch_name of the new branch
 * @property {string} environment - business manager
 * @return {LogObject<createBranchClickedFromCreateBranchPopupParams>} LogObject<createBranchClickedFromCreateBranchPopupParams>
 */
export function createBranchClickedFromCreateBranchPopup(params) {
    return { evid: 521, src: 105, endpoint: '', params };
}
/**
 * A function to create a log object for the 'newBranchCreated' event (evid: 522)
 *
 * Event description: created when a new branch is created
 *
 * @param {newBranchCreatedParams} params - The parameters for this event
 * @property {string} site_branch_name_origin - the site_branch_name from which we copy
 * @property {boolean} success - was the branch successfully created?
 * @property {GUID} site_branch_id_origin - the site_branch_id from which we copy
 * @property {GUID} site_branch_id - the site_branch_id of the new branch (first time we have this new guid)
 * @property {string} site_branch_name - the name of the new branch
 * @property {string} action - how was this branch created: {create/clone}
 * @return {LogObject<newBranchCreatedParams>} LogObject<newBranchCreatedParams>
 */
export function newBranchCreated(params) {
    return { evid: 522, src: 105, endpoint: '', params };
}
/**
 * A function to create a log object for the 'branchesTutorialsClicked' event (evid: 523)
 *
 * Event description: fires when a users clicks on one of the tutorials (learn_more/watch_video/FAQ)
 *
 * @param {branchesTutorialsClickedParams} params - The parameters for this event
 * @property {string} origin - one of the following: header_learn_more/watch_video/FAQ
 * @return {LogObject<branchesTutorialsClickedParams>} LogObject<branchesTutorialsClickedParams>
 */
export function branchesTutorialsClicked(params) {
    return { evid: 523, src: 105, endpoint: '', params };
}
/**
 * A function to create a log object for the 'siteBranchManagerActionClicked' event (evid: 524)
 *
 * Event description: fires when a user clicked when an action button is clicked on the site branch manager
 *
 * @param {siteBranchManagerActionClickedParams} params - The parameters for this event
 * @property {GUID} site_branch_id - site_branch_id
 * @property {string} site_branch_name - the name of the branch
 * @property {string} environment - where was the action clicked {business_manager}
 * @property {string} action - action pressed: {rename, edit, delete, duplicate}
 * @return {LogObject<siteBranchManagerActionClickedParams>} LogObject<siteBranchManagerActionClickedParams>
 */
export function siteBranchManagerActionClicked(params) {
    return { evid: 524, src: 105, endpoint: '', params };
}
/**
 * A function to create a log object for the 'deleteBranchClicked' event (evid: 525)
 *
 * Event description: fires when a user click on the delete button in the delete this branch pop up
 *
 * @param {deleteBranchClickedParams} params - The parameters for this event
 * @property {GUID} site_branch_id - site_branch_id
 * @property {string} site_branch_name - name of the branch
 * @property {boolean} is_live_branch - is this branch a live one
 * @return {LogObject<deleteBranchClickedParams>} LogObject<deleteBranchClickedParams>
 */
export function deleteBranchClicked(params) {
    return { evid: 525, src: 105, endpoint: '', params };
}
/**
 * A function to create a log object for the 'branchRenameSaveClicked' event (evid: 526)
 *
 * Event description: fires when a user clicks on save in the rename this branch popup
 *
 * @param {branchRenameSaveClickedParams} params - The parameters for this event
 * @property {string} old_site_branch_name - the old name of the branch
 * @property {GUID} site_branch_id - site_branch_id
 * @property {string} environment - where it happened: {business_manager}
 * @property {string} new_site_branch_name - the new name of the branch
 * @property {boolean} is_live_branch - is this branch live
 * @return {LogObject<branchRenameSaveClickedParams>} LogObject<branchRenameSaveClickedParams>
 */
export function branchRenameSaveClicked(params) {
    return { evid: 526, src: 105, endpoint: '', params };
}
/**
 * A function to create a log object for the 'branchNameHover' event (evid: 527)
 *
 * Event description: fires when a users hover over the branch name in the editor's top panel
 *
 * @param {branchNameHoverParams} params - The parameters for this event
 * @property {GUID} site_branch_id - site_branch_id
 * @property {string} site_branch_name - site_branch_name
 * @property {string} environment - editor
 * @return {LogObject<branchNameHoverParams>} LogObject<branchNameHoverParams>
 */
export function branchNameHover(params) {
    return { evid: 527, src: 105, endpoint: '', params };
}
/**
 * A function to create a log object for the 'manageBranchClicked' event (evid: 528)
 *
 * Event description: fires when a user presses Manage branch button on the site branch lightbox in the editor
 *
 * @param {manageBranchClickedParams} params - The parameters for this event
 * @property {GUID} site_branch_id - site_branch_id
 * @property {string} origin - Origin: {site_menu/branch_indication}
 * @property {string} site_branch_name - site_branch_name
 * @return {LogObject<manageBranchClickedParams>} LogObject<manageBranchClickedParams>
 */
export function manageBranchClicked(params) {
    return { evid: 528, src: 105, endpoint: '', params };
}
/**
 * A function to create a log object for the 'changeInABranchWarningPopup' event (evid: 529)
 *
 * Event description: fires when the popup that warns a users about change in the dashboard appears
 *
 * @param {changeInABranchWarningPopupParams} params - The parameters for this event
 * @property {GUID} site_branch_id - site_branch_id
 * @property {string} site_branch_name - site_branch_name
 * @property {string} target - where this popup leads
 * @return {LogObject<changeInABranchWarningPopupParams>} LogObject<changeInABranchWarningPopupParams>
 */
export function changeInABranchWarningPopup(params) {
    return { evid: 529, src: 105, endpoint: '', params };
}
/**
 * A function to create a log object for the 'changeInBranchWarningPopupAction' event (evid: 530)
 *
 * Event description: fires when a user presses a button in the change in branch warning popup
 *
 * @param {changeInBranchWarningPopupActionParams} params - The parameters for this event
 * @property {GUID} site_branch_id - site_branch_id
 * @property {string} site_branch_name - site_branch_name
 * @property {string} environment - business manager
 * @property {boolean} isDontShow - was the don't show button pressed?
 * @property {string} action - action: continue/X
 * @return {LogObject<changeInBranchWarningPopupActionParams>} LogObject<changeInBranchWarningPopupActionParams>
 */
export function changeInBranchWarningPopupAction(params) {
    return { evid: 530, src: 105, endpoint: '', params };
}
/**
 * A function to create a log object for the 'viewLiveSiteButtonClickedOnBranchManager' event (evid: 531)
 *
 * Event description: fired when a users clicked on the View live site after a test site is published
 *
 * @param {viewLiveSiteButtonClickedOnBranchManagerParams} params - The parameters for this event
 * @property {GUID} site_branch_id - site_branch_id
 * @property {string} site_branch_name - site_branch_name
 * @property {string} environment - business manager
 * @return {LogObject<viewLiveSiteButtonClickedOnBranchManagerParams>} LogObject<viewLiveSiteButtonClickedOnBranchManagerParams>
 */
export function viewLiveSiteButtonClickedOnBranchManager(params) {
    return { evid: 531, src: 105, endpoint: '', params };
}
/**
 * A function to create a log object for the 'successfullyPublishedTestVersion' event (evid: 532)
 *
 * Event description: fired when a users published a test site
 *
 * @param {successfullyPublishedTestVersionParams} params - The parameters for this event
 * @property {boolean} success - was the site published successfully
 * @property {GUID} site_branch_id - site_branch_id
 * @property {string} site_branch_name - site_branch_name
 * @property {string} environment - business manager
 * @return {LogObject<successfullyPublishedTestVersionParams>} LogObject<successfullyPublishedTestVersionParams>
 */
export function successfullyPublishedTestVersion(params) {
    return { evid: 532, src: 105, endpoint: '', params };
}
/**
 * A function to create a log object for the 'tabNavigation' event (evid: 700)
 *
 * Event description: tab-navigation.
 *
 * @param {tabNavigationParams} params - The parameters for this event
 * @property {string} grid_app_id - grid_app_id
 * @property {string} target_tab - ISSUES or OVERVIEW
 * @property {GUID} msid - msid
 * @return {LogObject<tabNavigationParams>} LogObject<tabNavigationParams>
 */
export function tabNavigation(params) {
    return { evid: 700, src: 105, endpoint: '', params };
}
/**
 * A function to create a log object for the 'cacheHit' event (evid: 7)
 *
 * Event description: Fired from Micro POP on a cache hit
 *
 * @param {cacheHitParams} params - The parameters for this event
 * @property {GUID} msid - msid
 * @property {Url} request_url - Full requested url, including parameters
 * @property {number} duration_ms - Cache retrieval duration
 * @property {string} request_url_str - Full requested url, including parameters, as a string
 * @return {LogObject<cacheHitParams>} LogObject<cacheHitParams>
 */
export function cacheHit(params) {
    return { evid: 7, src: 19, endpoint: '', params };
}
/**
 * A function to create a log object for the 'ideLoaded' event (evid: 122)
 *
 * Event description: ide_loaded
 *
 * @param {ideLoadedParams} params - The parameters for this event
 * @property {GUID} ide_session_id - guid
 * @property {string} server - server
 * @property {string} builderType - builder_type
 * @property {string} component_path - url
 * @property {number} ts - ts
 * @property {string} viewmode - viewmode
 * @property {GUID} did - site_id
 * @property {string} ver - ver
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {GUID} app_id - guid
 * @property {GUID} esi - global session id
 * @property {GUID} msid - msid
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<ideLoadedParams>} LogObject<ideLoadedParams>
 */
export function ideLoaded(params) {
    return { evid: 122, src: 39, endpoint: '', params };
}
/**
 * A function to create a log object for the 'ideUrlChanged' event (evid: 125)
 *
 * Event description: ide_url_changed
 *
 * @param {ideUrlChangedParams} params - The parameters for this event
 * @property {GUID} ide_session_id - guid
 * @property {string} server - server
 * @property {string} builderType - builder_type
 * @property {number} ts - ts
 * @property {string} viewmode - viewmode
 * @property {string} previous_url - url
 * @property {GUID} did - site_id
 * @property {string} ver - ver
 * @property {string} current_url - url
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {GUID} app_id - guid
 * @property {GUID} esi - global session id
 * @property {GUID} msid - msid
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<ideUrlChangedParams>} LogObject<ideUrlChangedParams>
 */
export function ideUrlChanged(params) {
    return { evid: 125, src: 39, endpoint: '', params };
}
/**
 * A function to create a log object for the 'propertiesPanelNicknameEdited' event (evid: 133)
 *
 * Event description: User successfully edited the ID of a component
 *
 * @param {propertiesPanelNicknameEditedParams} params - The parameters for this event
 * @property {string} element_id - ID
 * @property {GUID} ide_session_id - deprecated
 * @property {string} server - server
 * @property {string} builderType - builder_type
 * @property {number} ts - ts
 * @property {string} viewmode - viewmode
 * @property {GUID} did - site_id
 * @property {string} ver - ver
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {GUID} app_id - deprecated
 * @property {string} element_type - element_type
 * @property {GUID} esi - Editor session id
 * @property {string} component_type - component_type
 * @property {GUID} msid - msid
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<propertiesPanelNicknameEditedParams>} LogObject<propertiesPanelNicknameEditedParams>
 */
export function propertiesPanelNicknameEdited(params) {
    return { evid: 133, src: 39, endpoint: '', params };
}
/**
 * A function to create a log object for the 'propertiesPanelVisibleToggle' event (evid: 134)
 *
 * Event description: User clicks on the referral in the properties panel
 *
 * @param {propertiesPanelVisibleToggleParams} params - The parameters for this event
 * @property {GUID} ide_session_id - deprecated
 * @property {string} referral_name - 'Open MasterPage.js'
 * @property {string} server - server
 * @property {string} builderType - builder_type
 * @property {string} file_name - file_name
 * @property {number} ts - ts
 * @property {string} viewmode - viewmode
 * @property {GUID} did - site_id
 * @property {string} ver - ver
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {GUID} app_id - deprecated
 * @property {GUID} esi - editor session id
 * @property {GUID} msid - msid
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<propertiesPanelVisibleToggleParams>} LogObject<propertiesPanelVisibleToggleParams>
 */
export function propertiesPanelVisibleToggle(params) {
    return { evid: 134, src: 39, endpoint: '', params };
}
/**
 * A function to create a log object for the 'propertiesPanelHelpClicked' event (evid: 138)
 *
 * Event description: User clicked the '?' icon in the properties panel
 *
 * @param {propertiesPanelHelpClickedParams} params - The parameters for this event
 * @property {GUID} ide_session_id - deprecated
 * @property {string} server - server
 * @property {string} builderType - builder_type
 * @property {number} ts - ts
 * @property {string} viewmode - viewmode
 * @property {GUID} did - site_id
 * @property {string} link_text - the text / name of the link
 * @property {string} ver - ver
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {GUID} app_id - deprecated
 * @property {GUID} esi - editor session id
 * @property {GUID} msid - msid
 * @property {string} related_link - the URL the link open
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<propertiesPanelHelpClickedParams>} LogObject<propertiesPanelHelpClickedParams>
 */
export function propertiesPanelHelpClicked(params) {
    return { evid: 138, src: 39, endpoint: '', params };
}
/**
 * A function to create a log object for the 'propertiesPanelClosedOpen' event (evid: 141)
 *
 * Event description: Properties Panel Closed/open
 *
 * @param {propertiesPanelClosedOpenParams} params - The parameters for this event
 * @property {GUID} ide_session_id - deprecated
 * @property {string} server - server
 * @property {string} builderType - builder_type
 * @property {string} state - open,close
 * @property {GUID} site_id - site_id
 * @property {number} ts - ts
 * @property {string} viewmode - viewmode
 * @property {GUID} did - site_id
 * @property {string} origin - code_panel_toolbar, tools_menu, component_right_click_menu, keyboard_shortcut
 * @property {string} ver - ver
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {GUID} app_id - deprecated
 * @property {GUID} esi - global session id
 * @property {GUID} msid - msid
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<propertiesPanelClosedOpenParams>} LogObject<propertiesPanelClosedOpenParams>
 */
export function propertiesPanelClosedOpen(params) {
    return { evid: 141, src: 39, endpoint: '', params };
}
/**
 * A function to create a log object for the 'propertiesPanelNicknameFocused' event (evid: 142)
 *
 * Event description: Properties Panel Nickname focused (element ID)
 *
 * @param {propertiesPanelNicknameFocusedParams} params - The parameters for this event
 * @property {string} element_id - element ID
 * @property {GUID} ide_session_id - deprecated
 * @property {string} server - server
 * @property {string} builderType - builder_type
 * @property {GUID} site_id - site_id
 * @property {number} ts - ts
 * @property {string} viewmode - viewmode
 * @property {GUID} did - site_id
 * @property {string} ver - ver
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {GUID} app_id - deprecated
 * @property {string} element_type - element_type
 * @property {GUID} esi - global session id
 * @property {GUID} msid - msid
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<propertiesPanelNicknameFocusedParams>} LogObject<propertiesPanelNicknameFocusedParams>
 */
export function propertiesPanelNicknameFocused(params) {
    return { evid: 142, src: 39, endpoint: '', params };
}
/**
 * A function to create a log object for the 'propertiesPanelEnableDisableChanged' event (evid: 145)
 *
 * Event description: Properties Panel Enable/Disable changed
 *
 * @param {propertiesPanelEnableDisableChangedParams} params - The parameters for this event
 * @property {string} element_id - element ID
 * @property {GUID} ide_session_id - ide_session_id
 * @property {string} server - server
 * @property {string} builderType - builder_type
 * @property {string} state - enable, disable
 * @property {GUID} site_id - site_id
 * @property {number} ts - ts
 * @property {string} viewmode - viewmode
 * @property {GUID} did - site_id
 * @property {string} ver - ver
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {GUID} app_id - app_id
 * @property {string} element_type - element_type
 * @property {GUID} esi - global session id
 * @property {GUID} msid - msid
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<propertiesPanelEnableDisableChangedParams>} LogObject<propertiesPanelEnableDisableChangedParams>
 */
export function propertiesPanelEnableDisableChanged(params) {
    return { evid: 145, src: 39, endpoint: '', params };
}
/**
 * A function to create a log object for the 'propertiesPanelVisibilityOnLoadChanged' event (evid: 146)
 *
 * Event description: Properties Panel Visibility on load changed
 *
 * @param {propertiesPanelVisibilityOnLoadChangedParams} params - The parameters for this event
 * @property {string} element_id - chosen element ID
 * @property {GUID} ide_session_id - ide_session_id
 * @property {string} server - server
 * @property {string} builderType - builder_type
 * @property {GUID} site_id - site_id
 * @property {number} ts - ts
 * @property {string} viewmode - viewmode
 * @property {boolean} is_visible - 1- on , 0 -off
 * @property {GUID} did - site_id
 * @property {string} ver - ver
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {GUID} app_id - app_id
 * @property {string} element_type - element_type
 * @property {GUID} esi - global session id
 * @property {GUID} msid - msid
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<propertiesPanelVisibilityOnLoadChangedParams>} LogObject<propertiesPanelVisibilityOnLoadChangedParams>
 */
export function propertiesPanelVisibilityOnLoadChanged(params) {
    return { evid: 146, src: 39, endpoint: '', params };
}
/**
 * A function to create a log object for the 'propertiesPanelCollapsedOnLoadChanged' event (evid: 147)
 *
 * Event description: Properties Panel Collapsed on load changed
 *
 * @param {propertiesPanelCollapsedOnLoadChangedParams} params - The parameters for this event
 * @property {string} element_id - element ID
 * @property {GUID} ide_session_id - ide_session_id
 * @property {string} server - server
 * @property {string} builderType - builder_type
 * @property {GUID} site_id - site_id
 * @property {boolean} is_collapsed - 1- on , 0 -off
 * @property {number} ts - ts
 * @property {string} viewmode - viewmode
 * @property {GUID} did - site_id
 * @property {string} ver - ver
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {GUID} app_id - app_id
 * @property {string} element_type - element_type
 * @property {GUID} esi - global session id
 * @property {GUID} msid - msid
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<propertiesPanelCollapsedOnLoadChangedParams>} LogObject<propertiesPanelCollapsedOnLoadChangedParams>
 */
export function propertiesPanelCollapsedOnLoadChanged(params) {
    return { evid: 147, src: 39, endpoint: '', params };
}
/**
 * A function to create a log object for the 'propertiesPanelEventHandlerAddPressed' event (evid: 148)
 *
 * Event description: Properties Panel Event Handler add pressed
 *
 * @param {propertiesPanelEventHandlerAddPressedParams} params - The parameters for this event
 * @property {string} element_id - element_id
 * @property {GUID} ide_session_id - ide_session_id
 * @property {string} server - server
 * @property {string} builderType - builder_type
 * @property {GUID} site_id - site_id
 * @property {string} event - event name
 * @property {number} ts - ts
 * @property {string} default_handler_name - default_handler_name
 * @property {string} viewmode - viewmode
 * @property {GUID} did - site_id
 * @property {string} ver - ver
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {GUID} app_id - app_id
 * @property {string} element_type - element_type
 * @property {GUID} esi - global session id
 * @property {GUID} msid - msid
 * @property {boolean} Is_dynamic_event - Is_dynamic_event
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<propertiesPanelEventHandlerAddPressedParams>} LogObject<propertiesPanelEventHandlerAddPressedParams>
 */
export function propertiesPanelEventHandlerAddPressed(params) {
    return { evid: 148, src: 39, endpoint: '', params };
}
/**
 * A function to create a log object for the 'propertiesPanelEventHandlerCodeGenerated' event (evid: 149)
 *
 * Event description: Properties Panel Event Handler Code Generated
 *
 * @param {propertiesPanelEventHandlerCodeGeneratedParams} params - The parameters for this event
 * @property {string} element_id - element_id
 * @property {GUID} ide_session_id - deprecated
 * @property {string} server - server
 * @property {string} builderType - builder_type
 * @property {GUID} site_id - site_id
 * @property {string} event - event name
 * @property {string} handler_name - handler_name
 * @property {number} ts - ts
 * @property {string} viewmode - viewmode
 * @property {GUID} did - site_id
 * @property {string} origin - origin
 * @property {string} ver - ver
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {GUID} app_id - deprecated
 * @property {string} element_type - element_type
 * @property {string} triggered_by - triggered_by
 * @property {GUID} esi - global session id
 * @property {GUID} msid - msid
 * @property {boolean} Is_dynamic_event - Is_dynamic_event
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<propertiesPanelEventHandlerCodeGeneratedParams>} LogObject<propertiesPanelEventHandlerCodeGeneratedParams>
 */
export function propertiesPanelEventHandlerCodeGenerated(params) {
    return { evid: 149, src: 39, endpoint: '', params };
}
/**
 * A function to create a log object for the 'propertiesPanelEventHandlerNavigation' event (evid: 150)
 *
 * Event description: Properties Panel Event Handler Navigation
 *
 * @param {propertiesPanelEventHandlerNavigationParams} params - The parameters for this event
 * @property {string} element_id - ID
 * @property {GUID} ide_session_id - ide_session_id
 * @property {string} server - server
 * @property {string} builderType - builder_type
 * @property {GUID} site_id - site_id
 * @property {string} event - event name
 * @property {string} handler_name - handler_name
 * @property {number} ts - ts
 * @property {string} viewmode - viewmode
 * @property {GUID} did - site_id
 * @property {string} ver - ver
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {GUID} app_id - app_id
 * @property {string} element_type - element_type
 * @property {GUID} esi - global session id
 * @property {GUID} msid - msid
 * @property {boolean} is_dynamic_event - is_dynamic_event
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<propertiesPanelEventHandlerNavigationParams>} LogObject<propertiesPanelEventHandlerNavigationParams>
 */
export function propertiesPanelEventHandlerNavigation(params) {
    return { evid: 150, src: 39, endpoint: '', params };
}
/**
 * A function to create a log object for the 'propertiesPanelEventHandlerRemoved' event (evid: 151)
 *
 * Event description: Properties Panel Event Handler removed
 *
 * @param {propertiesPanelEventHandlerRemovedParams} params - The parameters for this event
 * @property {string} element_id - ID
 * @property {GUID} ide_session_id - ide_session_id
 * @property {string} server - server
 * @property {string} builderType - builder_type
 * @property {GUID} site_id - site_id
 * @property {string} event - event name
 * @property {string} handler_name - handler_name
 * @property {number} ts - ts
 * @property {string} viewmode - viewmode
 * @property {GUID} did - site_id
 * @property {string} ver - ver
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {GUID} app_id - app_id
 * @property {string} element_type - element_type
 * @property {GUID} esi - global session id
 * @property {GUID} msid - msid
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<propertiesPanelEventHandlerRemovedParams>} LogObject<propertiesPanelEventHandlerRemovedParams>
 */
export function propertiesPanelEventHandlerRemoved(params) {
    return { evid: 151, src: 39, endpoint: '', params };
}
/**
 * A function to create a log object for the 'dynamicEventHandlerTooltipModalSrc39Evid157' event (evid: 157)
 *
 * Event description: dynamic event handler tooltip modal
 *
 * @param {dynamicEventHandlerTooltipModalSrc39Evid157Params} params - The parameters for this event
 * @property {string} element_id - ID
 * @property {GUID} ide_session_id - ide_session_id
 * @property {string} builderType - builder_type
 * @property {GUID} site_id - site_id
 * @property {string} event - event name
 * @property {string} element_type - element_type
 * @property {string} action - show, learn_why, migrate
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<dynamicEventHandlerTooltipModalSrc39Evid157Params>} LogObject<dynamicEventHandlerTooltipModalSrc39Evid157Params>
 */
export function dynamicEventHandlerTooltipModalSrc39Evid157(params) {
    return { evid: 157, src: 39, endpoint: '', params };
}
/**
 * A function to create a log object for the 'wixCodeInit' event (evid: 200)
 *
 * Event description: Fires when a user uses the init command
 *
 * @param {wixCodeInitParams} params - The parameters for this event
 * @property {string} csi - Corvid Session ID
 * @property {GUID} ide_session_id - **Deprecated**
 * @property {string} server - server
 * @property {string} builderType - builder_type
 * @property {GUID} uuid - uuid
 * @property {number} ts - ts
 * @property {string} viewmode - viewmode
 * @property {GUID} did - site_id
 * @property {string} status - {start / success / fail}
 * @property {string} ver - ver
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {GUID} app_id - **Deprecated**
 * @property {GUID} esi - **Deprecated**
 * @property {GUID} msid - msid
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<wixCodeInitParams>} LogObject<wixCodeInitParams>
 */
export function wixCodeInit(params) {
    return { evid: 200, src: 39, endpoint: '', params };
}
/**
 * A function to create a log object for the 'wixCodeOpenEditor' event (evid: 201)
 *
 * Event description: Fires when a user uses the open editor command
 *
 * @param {wixCodeOpenEditorParams} params - The parameters for this event
 * @property {string} csi - Corvid Session ID
 * @property {GUID} ide_session_id - **Deprecated**
 * @property {string} server - server
 * @property {string} builderType - builder_type
 * @property {GUID} uuid - uuid
 * @property {number} ts - ts
 * @property {string} viewmode - viewmode
 * @property {GUID} did - site_id
 * @property {string} status - one of: 'start', 'success', 'fail'
 * @property {string} ver - ver
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {GUID} app_id - **Deprecated**
 * @property {GUID} esi - editor session id
 * @property {GUID} msid - msid
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<wixCodeOpenEditorParams>} LogObject<wixCodeOpenEditorParams>
 */
export function wixCodeOpenEditor(params) {
    return { evid: 201, src: 39, endpoint: '', params };
}
/**
 * A function to create a log object for the 'wixCodePull' event (evid: 202)
 *
 * Event description: Fires when a user uses the open editor command
 *
 * @param {wixCodePullParams} params - The parameters for this event
 * @property {string} csi - Corvid Session ID
 * @property {GUID} ide_session_id - **Deprecated**
 * @property {string} server - server
 * @property {string} builderType - builder_type
 * @property {GUID} uuid - uuid
 * @property {number} ts - ts
 * @property {string} viewmode - viewmode
 * @property {GUID} did - site_id
 * @property {string} status - one of: 'start', 'success', 'fail'
 * @property {string} ver - ver
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {GUID} app_id - **Deprecated**
 * @property {string} type - regular, override, merge
 * @property {GUID} esi - editor session id
 * @property {GUID} msid - msid
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<wixCodePullParams>} LogObject<wixCodePullParams>
 */
export function wixCodePull(params) {
    return { evid: 202, src: 39, endpoint: '', params };
}
/**
 * A function to create a log object for the 'propertiesPanelLoadedWithHandlersSrc39Evid3' event (evid: 3)
 *
 * Event description: Properties Panel loaded with handlers
 *
 * @param {propertiesPanelLoadedWithHandlersSrc39Evid3Params} params - The parameters for this event
 * @property {string} renderingFlow - Rendering flow
 * @property {GUID} ide_session_id - deprecated
 * @property {string} server - server
 * @property {string} builderType - builder_type
 * @property {string} state - open,close
 * @property {GUID} site_id - site_id
 * @property {number} ts - ts
 * @property {string} viewmode - viewmode
 * @property {string} origin - code_panel_toolbar, tools_menu, component_right_click_menu, keyboard_shortcut
 * @property {GUID} app_id - deprecated
 * @property {GUID} esi - global session id
 * @property {GUID} msid - msid
 * @property {string} has_static_functions - has_static_functions
 * @property {string} is_static - is_static
 * @return {LogObject<propertiesPanelLoadedWithHandlersSrc39Evid3Params>} LogObject<propertiesPanelLoadedWithHandlersSrc39Evid3Params>
 */
export function propertiesPanelLoadedWithHandlersSrc39Evid3(params) {
    return { evid: 3, src: 39, endpoint: '', params };
}
/**
 * A function to create a log object for the 'propertiesPanelActionClick' event (evid: 401)
 *
 * Event description: User clicked on an item in properties panel
 *
 * @param {propertiesPanelActionClickParams} params - The parameters for this event
 * @property {GUID} ide_session_id - ide_session_id
 * @property {string} server - server
 * @property {string} builderType - builder_type
 * @property {number} ts - ts
 * @property {string} viewmode - viewmode
 * @property {GUID} did - site_id
 * @property {string} ver - ver
 * @property {string} cta - The item the user clicked on
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {GUID} app_id - app_id
 * @property {string} element_type - element type name
 * @property {GUID} esi - esi
 * @property {string} component_id - component_id
 * @property {string} component_type - component type name
 * @property {GUID} msid - msid
 * @property {string} action_type - action type
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<propertiesPanelActionClickParams>} LogObject<propertiesPanelActionClickParams>
 */
export function propertiesPanelActionClick(params) {
    return { evid: 401, src: 39, endpoint: '', params };
}
/**
 * A function to create a log object for the 'propertiesPanelEnterCustomClass' event (evid: 402)
 *
 * Event description: add/delete/select (from autocomplete) custom class in the properties panel
 *
 * @param {propertiesPanelEnterCustomClassParams} params - The parameters for this event
 * @property {string} custom_class - The name of the class the user entered
 * @property {GUID} ide_session_id - ide_session_id
 * @property {string} server - server
 * @property {string} builderType - builder_type
 * @property {number} ts - ts
 * @property {string} viewmode - viewmode
 * @property {GUID} did - site_id
 * @property {string} ver - ver
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {GUID} app_id - app_id
 * @property {string} element_type - element type (button/image
 * @property {GUID} esi - esi
 * @property {string} component_id - component_id
 * @property {string} component_type - component_type
 * @property {GUID} msid - msid
 * @property {string} action_type - Add/Delete/Select (select from auto complete)
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<propertiesPanelEnterCustomClassParams>} LogObject<propertiesPanelEnterCustomClassParams>
 */
export function propertiesPanelEnterCustomClass(params) {
    return { evid: 402, src: 39, endpoint: '', params };
}
/**
 * A function to create a log object for the 'aiCodeAssistantClicked' event (evid: 403)
 *
 * Event description: The user clicked on AI code assistant link via the propertie panel entry point
 *
 * @param {aiCodeAssistantClickedParams} params - The parameters for this event
 * @property {GUID} ide_session_id - ide_session_id
 * @property {string} server - server
 * @property {string} builderType - builder_type
 * @property {number} ts - ts
 * @property {string} viewmode - viewmode
 * @property {GUID} did - site_id
 * @property {string} ver - ver
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {GUID} app_id - app_id
 * @property {GUID} esi - editor session id
 * @property {string} component_id - {}
 * @property {string} component_type - {}
 * @property {GUID} msid - msid
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<aiCodeAssistantClickedParams>} LogObject<aiCodeAssistantClickedParams>
 */
export function aiCodeAssistantClicked(params) {
    return { evid: 403, src: 39, endpoint: '', params };
}
/**
 * A function to create a log object for the 'addEventHandlerFromComponentRightClickMenu' event (evid: 404)
 *
 * Event description: fires when the user adds an event from the 'add JavaScript' section in the component right-click menu
 *
 * @param {addEventHandlerFromComponentRightClickMenuParams} params - The parameters for this event
 * @property {GUID} ide_session_id - ide_session_id
 * @property {string} server - server
 * @property {string} builderType - builder_type
 * @property {string} handler_name - handler_name
 * @property {number} ts - ts
 * @property {string} viewmode - viewmode
 * @property {GUID} did - site_id
 * @property {string} ver - ver
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {GUID} app_id - app_id
 * @property {GUID} esi - esi
 * @property {string} component_id - component_id
 * @property {string} component_type - component_type
 * @property {GUID} msid - msid
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<addEventHandlerFromComponentRightClickMenuParams>} LogObject<addEventHandlerFromComponentRightClickMenuParams>
 */
export function addEventHandlerFromComponentRightClickMenu(params) {
    return { evid: 404, src: 39, endpoint: '', params };
}
/**
 * A function to create a log object for the 'openCodeTabFromComponentRightClickMenu' event (evid: 405)
 *
 * Event description: fires when the user clicks on the 'open code panel' from the 'add JavaScript' section in the component right-click menu
 *
 * @param {openCodeTabFromComponentRightClickMenuParams} params - The parameters for this event
 * @property {GUID} ide_session_id - ide_session_id
 * @property {string} server - server
 * @property {string} builderType - builder_type
 * @property {number} ts - ts
 * @property {string} viewmode - viewmode
 * @property {GUID} did - site_id
 * @property {string} ver - ver
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {GUID} app_id - app_id
 * @property {GUID} esi - esi
 * @property {string} component_id - component_id
 * @property {string} component_type - component_type
 * @property {GUID} msid - msid
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<openCodeTabFromComponentRightClickMenuParams>} LogObject<openCodeTabFromComponentRightClickMenuParams>
 */
export function openCodeTabFromComponentRightClickMenu(params) {
    return { evid: 405, src: 39, endpoint: '', params };
}
/**
 * A function to create a log object for the 'veloAiAssistantSuccessfulResponse' event (evid: 700)
 *
 * Event description: Contents of a successful response returned from the models
 *
 * @param {veloAiAssistantSuccessfulResponseParams} params - The parameters for this event
 * @property {string} embedding_model_config - A json containing the embedding model’s configurations (instruction sentence, vector sizes, and etc.)
 * @property {string} user_prompt - The actual input prompt submitted by the user
 * @property {string} search_index_config - A json containing configurations of the search index (index_name, provider, config, and etc.)
 * @property {string} chat_model_name - The name of the chat model used
 * @property {string} chat_model_config - A json containing the chat model’s configurations (system message, temperature, and etc.)
 * @property {string} prompt_id - A GUID for the prompt submitted by the user
 * @property {string} model_version - A version identifier that reflects all the different parameter configurations
 * @property {string} token_usage - A json with the token usage data as returned from the model API
 * @property {string} messages_array - The complete array of messages sent to model API, including the roles and content of each message
 * @property {string} prompt_intent - Intent the user chose for the prompt (get_code / discover_documents / fix_code)
 * @property {string} response_times - A json containing response times of he different models (chat_model,  embedding_model, search_index)
 * @property {string} embedding_model_name - The name of the embedding model used
 * @property {number} message_number - The number of the message in the conversation
 * @property {string} conversation_id - A GUID for the conversation
 * @property {string} model_response - The response message from the chat model
 * @property {string} prompt_config - A json containing all configurations and data related to the prompt (prompt template, context, and etc.)
 * @property {string} ai_origin - wix_ide_ai/pp_ai
 * @property {string} platform - platform
 * @property {string} entryPoint - entry_point
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<veloAiAssistantSuccessfulResponseParams>} LogObject<veloAiAssistantSuccessfulResponseParams>
 */
export function veloAiAssistantSuccessfulResponse(params) {
    return { evid: 700, src: 39, endpoint: '', params };
}
/**
 * A function to create a log object for the 'veloAiAssistantErrorEvent' event (evid: 701)
 *
 * Event description: Sent when there was an error in the response of the Velo AI assitant and contains all the details about it
 *
 * @param {veloAiAssistantErrorEventParams} params - The parameters for this event
 * @property {string} embedding_model_config - A json containing the embedding model’s configurations (instruction sentence, vector sizes, and etc.)
 * @property {string} user_prompt - The actual input prompt submitted by the user
 * @property {string} search_index_config - A json containing configurations of the search index (index_name, provider, config, and etc.)
 * @property {string} chat_model_name - The name of the chat model used
 * @property {string} chat_model_config - A json containing the chat model’s configurations (system message, temperature, and etc.)
 * @property {string} prompt_id - A GUID for the prompt submitted by the user
 * @property {string} model_version - A version identifier that reflects all the different parameter configurations
 * @property {string} messages_array - The complete array of messages sent to model API, including the roles and content of each message
 * @property {string} prompt_intent - Intent the user chose for the prompt (get_code / discover_documents / fix_code)
 * @property {string} response_times - A json containing response times of he different models (chat_model,  embedding_model, search_index)
 * @property {string} embedding_model_name - The name of the embedding model used
 * @property {string} error_message - The error message
 * @property {number} message_number - The number of the message in the conversation
 * @property {string} conversation_id - A GUID for the conversation
 * @property {string} model_response - The response message from the chat model
 * @property {string} prompt_config - A json containing all configurations and data related to the prompt (prompt template, context, and etc.)
 * @property {string} ai_origin - ai_origin
 * @property {string} platform - platform
 * @property {string} entryPoint - entry_point
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<veloAiAssistantErrorEventParams>} LogObject<veloAiAssistantErrorEventParams>
 */
export function veloAiAssistantErrorEvent(params) {
    return { evid: 701, src: 39, endpoint: '', params };
}
/**
 * A function to create a log object for the 'veloAiAssistantRatingSubmitted' event (evid: 702)
 *
 * Event description: Fired when a user submits a star rating for a response
 *
 * @param {veloAiAssistantRatingSubmittedParams} params - The parameters for this event
 * @property {string} embedding_model_config - A json containing the embedding model’s configurations (instruction sentence, vector sizes, and etc.)
 * @property {string} user_prompt - The actual input prompt submitted by the user
 * @property {string} search_index_config - A json containing configurations of the search index (index_name, provider, config, and etc.)
 * @property {string} chat_model_name - The name of the chat model used
 * @property {string} chat_model_config - A json containing the chat model’s configurations (system message, temperature, and etc.)
 * @property {string} prompt_id - A GUID for the prompt submitted by the user
 * @property {number} rating - A number from 1-5 representing the star rating the user rated the response
 * @property {string} model_version - A version identifier that reflects all the different parameter configurations
 * @property {string} token_usage - A json with the token usage data as returned from the model API
 * @property {string} messages_array - The complete array of messages sent to model API, including the roles and content of each message
 * @property {string} prompt_intent - Intent the user chose for the prompt (get_code / discover_documents / fix_code)
 * @property {string} response_times - A json containing response times of he different models (chat_model,  embedding_model, search_index)
 * @property {string} embedding_model_name - The name of the embedding model used
 * @property {number} message_number - The number of the message in the conversation
 * @property {string} conversation_id - A GUID for the conversation
 * @property {string} model_response - The response message from the chat model
 * @property {string} prompt_config - A json containing all configurations and data related to the prompt (prompt template, context, and etc.)
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<veloAiAssistantRatingSubmittedParams>} LogObject<veloAiAssistantRatingSubmittedParams>
 */
export function veloAiAssistantRatingSubmitted(params) {
    return { evid: 702, src: 39, endpoint: '', params };
}
/**
 * A function to create a log object for the 'veloAiAssistantCopyInsertCode' event (evid: 703)
 *
 * Event description: When the user clicks on Copy / Insert code buttons for a code snippet in the response
 *
 * @param {veloAiAssistantCopyInsertCodeParams} params - The parameters for this event
 * @property {string} code_snippet - The code snippet the user copied / inserted
 * @property {string} embedding_model_config - A json containing the embedding model’s configurations (instruction sentence, vector sizes, and etc.)
 * @property {string} user_prompt - The actual input prompt submitted by the user
 * @property {string} search_index_config - A json containing configurations of the search index (index_name, provider, config, and etc.)
 * @property {string} chat_model_name - The name of the chat model used
 * @property {string} chat_model_config - A json containing the chat model’s configurations (system message, temperature, and etc.)
 * @property {string} prompt_id - A GUID for the prompt submitted by the user
 * @property {string} model_version - A version identifier that reflects all the different parameter configurations
 * @property {string} token_usage - A json with the token usage data as returned from the model API
 * @property {string} messages_array - The complete array of messages sent to model API, including the roles and content of each message
 * @property {string} prompt_intent - Intent the user chose for the prompt (get_code / discover_documents / fix_code)
 * @property {string} response_times - A json containing response times of he different models (chat_model,  embedding_model, search_index)
 * @property {string} embedding_model_name - The name of the embedding model used
 * @property {number} message_number - The number of the message in the conversation
 * @property {string} conversation_id - A GUID for the conversation
 * @property {string} model_response - The response message from the chat model
 * @property {string} prompt_config - A json containing all configurations and data related to the prompt (prompt template, context, and etc.)
 * @property {string} action - The action the user clicked on (copy / insert)
 * @property {string} ai_origin - ai_origin
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<veloAiAssistantCopyInsertCodeParams>} LogObject<veloAiAssistantCopyInsertCodeParams>
 */
export function veloAiAssistantCopyInsertCode(params) {
    return { evid: 703, src: 39, endpoint: '', params };
}
/**
 * A function to create a log object for the 'veloAiAssistantCancelResponseClicked' event (evid: 704)
 *
 * Event description: Sent when the user clicks on the "Cancel" button in order to cancel the response generation of the Velo AI assistant and contains all the details about it
 *
 * @param {veloAiAssistantCancelResponseClickedParams} params - The parameters for this event
 * @property {string} user_prompt - The actual input prompt submitted by the user
 * @property {string} chat_model_config - chat_model_config
 * @property {string} prompt_id - A GUID for the prompt submitted by the user
 * @property {string} messages_array - The complete array of messages sent to model API, including the roles and content of each message
 * @property {string} prompt_intent - Intent the user chose for the prompt (get_code / discover_documents / fix_code)
 * @property {number} message_number - The number of the message in the conversation
 * @property {string} conversation_id - A GUID for the conversation
 * @property {string} model_response - The response message from the chat model
 * @property {string} prompt_config - prompt_config
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<veloAiAssistantCancelResponseClickedParams>} LogObject<veloAiAssistantCancelResponseClickedParams>
 */
export function veloAiAssistantCancelResponseClicked(params) {
    return { evid: 704, src: 39, endpoint: '', params };
}
/**
 * A function to create a log object for the 'veloAiAssistantCodeCompilerResultSrc39Evid705' event (evid: 705)
 *
 * Event description: The results of the code compiler ran on the model response
 *
 * @param {veloAiAssistantCodeCompilerResultSrc39Evid705Params} params - The parameters for this event
 * @property {string} user_prompt - The actual input prompt submitted by the user
 * @property {string} prompt_id - A GUID for the prompt submitted by the user
 * @property {string} model_version - A version identifier that reflects all the different parameter configurations
 * @property {string} messages_array - The complete array of messages sent to model API, including the roles and content of each message
 * @property {string} prompt_intent - Intent the user chose for the prompt (get_code / discover_documents / fix_code)
 * @property {number} message_number - The number of the message in the conversation
 * @property {string} conversation_id - A GUID for the conversation
 * @property {string} model_response - The response message from the chat model
 * @property {string} compilation_status - Whether the compilation of all code snippets succeeded or failed
 * @property {string} compilation_results_json - A JSON array with the detailed compilation results
 * @property {string} chat_model_name - The name of the chat model used
 * @return {LogObject<veloAiAssistantCodeCompilerResultSrc39Evid705Params>} LogObject<veloAiAssistantCodeCompilerResultSrc39Evid705Params>
 */
export function veloAiAssistantCodeCompilerResultSrc39Evid705(params) {
    return { evid: 705, src: 39, endpoint: '', params };
}
/**
 * A function to create a log object for the 'cmsExpressionsExpressionSetCopySrc79Evid1' event (evid: 1)
 *
 * Event description: CMS Expressions - Expression input suggestion used
 *
 * @param {cmsExpressionsExpressionSetCopySrc79Evid1Params} params - The parameters for this event
 * @property {string} suggestion - suggestion
 * @property {string} component_id - component_id
 * @property {string} ds_id - ds_id
 * @property {string} collection_id - collection_id
 * @property {string} component_type - component_type
 * @property {string} origin - origin
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<cmsExpressionsExpressionSetCopySrc79Evid1Params>} LogObject<cmsExpressionsExpressionSetCopySrc79Evid1Params>
 */
export function cmsExpressionsExpressionSetCopySrc79Evid1(params) {
    return { evid: 1, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'errorEvent' event (evid: 10)
 *
 * Event description: error event.
 *
 * @param {errorEventParams} params - The parameters for this event
 * @property {string} error_desc - dsc
 * @property {string} dsc - dsc
 * @property {number} response_time - Time in ms from load to error
 * @property {string} vsi - vsi
 * @property {boolean} is_rollout - is_rollout
 * @property {string} initiator_name - initiator_name
 * @property {string} server - server
 * @property {string} builderType - builder_type
 * @property {string} errscp - error scope
 * @property {string} ds_type - ds_type
 * @property {GUID} site_id - changed from did -> site_id on Oct 03, 2013
 * @property {string} errn - error name
 * @property {string} panel_id - panel_id
 * @property {string} p4 - 4st extra parameter
 * @property {string} majorver - the major version of html editor
 * @property {string} trgt - target name, api call or class
 * @property {boolean} isLiveEditor - is_live_editor
 * @property {number} ts - Time in ms from load to error
 * @property {string} playerVersion - player_version
 * @property {string} artifactVersion - artifact_version
 * @property {GUID} visitor_id - visitor_id
 * @property {number} usersOnPageCount - users_on_page_count
 * @property {string} __cookies_prefix___wixStatemap - state_map
 * @property {string} viewmode - viewmode
 * @property {string} error_description_short - error_description_short
 * @property {string} data_center - data_center
 * @property {string} screenResolution - screen_resolution
 * @property {string} p3 - 3rd extra parameter
 * @property {GUID} did - changed from did -> site_id on Oct 03, 2013
 * @property {string} pageId - page_id
 * @property {boolean} isSg - is_sg
 * @property {string} dsOrigin - ds_origin
 * @property {string} p2 - 2nd extra parameter
 * @property {string} token - token
 * @property {string} ver - sender version
 * @property {string} sessionId - session_id
 * @property {number} errc - error code
 * @property {string} stack - stacktrace
 * @property {GUID} metasiteid - msid
 * @property {string} viewerName - viewer_name
 * @property {string} editor_working_mode - editor_working_mode
 * @property {string} app_id - app_id
 * @property {number} sev - severity of the error
 * @property {GUID} esi - esi
 * @property {number} cat - category id
 * @property {string} errormsg - dsc
 * @property {number} usersOnSiteCount - users_on_site_count
 * @property {GUID} __cookies_prefix___wixCIDX - client/computer id
 * @property {string} p1 - 1st extra parameter
 * @property {GUID} msid - msid
 * @property {number} base_version - base_version
 * @property {number} iss - issue (sub-category)
 * @property {string} ds_id - ds_id
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<errorEventParams>} LogObject<errorEventParams>
 */
export function errorEvent(params) {
    return { evid: 10, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'ideFileFolderCreated' event (evid: 100)
 *
 * Event description: file/folder created (from Code Editor or Left Tree)
 *
 * @param {ideFileFolderCreatedParams} params - The parameters for this event
 * @property {string} app_name - app_name
 * @property {string} app_id - app_id
 * @property {number} users_with_write_devmode_count - users_with_write_devmode_count
 * @property {string} builderType - builder_type
 * @property {string} path - path
 * @property {GUID} visitorId - deprecated
 * @property {string} section_name - section_name
 * @property {string} state - focused, blurred
 * @property {string} ds_type - ds_type
 * @property {GUID} site_id - site_id
 * @property {string} panel_id - panel_id
 * @property {string} appName - app_name
 * @property {number} users_on_site_count - users_on_site_count
 * @property {number} ts - ts
 * @property {string} button_name - button_name
 * @property {string} panel_name - panel_name
 * @property {string} viewmode - viewmode
 * @property {string} input_name - deprecated
 * @property {string} pageId - page_id
 * @property {string} origin - from where the file was added - one of { left_tree / code_editor}
 * @property {string} token - token
 * @property {number} users_with_devmode_count - users_with_devmode_count
 * @property {string} ver - version
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {string} dev_mode - dev_mode
 * @property {string} type - type(folder or any file extension)
 * @property {string} appId - app_id
 * @property {GUID} esi - esi
 * @property {GUID} msid - msid
 * @property {string} ds_id - ds_id
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<ideFileFolderCreatedParams>} LogObject<ideFileFolderCreatedParams>
 */
export function ideFileFolderCreated(params) {
    return { evid: 100, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'contatinerOpened' event (evid: 1000)
 *
 * Event description: a container is opened for a site
 *
 * @param {contatinerOpenedParams} params - The parameters for this event
 * @property {string} code_app_id - code app id (instance id)
 * @property {string} segment - the segment (dev or public)
 * @property {string} grid_app_id - The id of the current code version
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<contatinerOpenedParams>} LogObject<contatinerOpenedParams>
 */
export function contatinerOpened(params) {
    return { evid: 1000, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'contatinerClosed' event (evid: 1001)
 *
 * Event description: a container is closed for a site
 *
 * @param {contatinerClosedParams} params - The parameters for this event
 * @property {number} duration - time container was opened in miliseconds
 * @property {string} code_app_id - code app id (instance id)
 * @property {string} segment - the segment (dev or public)
 * @property {string} grid_app_id - the id of the current code version
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<contatinerClosedParams>} LogObject<contatinerClosedParams>
 */
export function contatinerClosed(params) {
    return { evid: 1001, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'developerErrorMessage' event (evid: 1002)
 *
 * Event description: This event is fired for every error that our users (developer) sees in live site events/google operations or in developer tools
 *
 * @param {developerErrorMessageParams} params - The parameters for this event
 * @property {boolean} operation_first - {}
 * @property {string} json_payload_message - {Payload of the log entry in JSON format.}
 * @property {string} labels_namespace - {Name of Wix application with which the log entry is associated}
 * @property {number} source_location_line - {Source code location information associated with the log entry}
 * @property {number} source_location_column - {Source code location information associated with the log entry }
 * @property {string} labels_view_mode - {UNKNOWN / Site / Preview}
 * @property {string} operation_producer - {Context of the operation, such as a URL path for page code or a web module function name.}
 * @property {string} insert_id - insert_id
 * @property {boolean} operation_last - {}
 * @property {DateTime} receivedTimestamp - {}
 * @property {string} operation_id - operation_id
 * @property {string} labels_siteUrl - {}
 * @property {string} severity - "INFO": Status, performance, or other general information. "WARNING": An unexpected or undesired development that may cause problems. "ERROR": A serious issue that is likely to cause problems.
 * @property {string} labels_revision - {Unique identifier for a specific site revision}
 * @property {GUID} msid - {}
 * @property {string} labels_tenant_id - {Unique identifier for the site. Does not change even if the site name or other site attributes change.}
 * @property {string} source_location_file_path - {Source code location information associated with the log entry}
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<developerErrorMessageParams>} LogObject<developerErrorMessageParams>
 */
export function developerErrorMessage(params) {
    return { evid: 1002, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'workerCreated' event (evid: 101)
 *
 * Event description: web worker created
 *
 * @param {workerCreatedParams} params - The parameters for this event
 * @property {GUID} _event_hash - event_hash
 * @property {GUID} visitor_id - visitor_id
 * @property {string} app_name - app_name
 * @property {string} app_id - app_id
 * @property {string} worker_id - worker id
 * @property {string} builderType - builder_type
 * @property {string} ds_type - ds_type
 * @property {GUID} site_id - site_id
 * @property {string} panel_id - panel_id
 * @property {string} appName - app_name
 * @property {number} ts - loading_time
 * @property {string} panel_name - panel_name
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {number} time_since_load - time_since_load
 * @property {string} pageId - page_id
 * @property {boolean} is_server_side - {1,0}
 * @property {GUID} vsi - vsi
 * @property {string} token - token
 * @property {string} ver - version
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {string} appId - app_id
 * @property {GUID} esi - esi
 * @property {GUID} msid - msid
 * @property {number} tsp - tsp
 * @property {string} ds_id - ds_id
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<workerCreatedParams>} LogObject<workerCreatedParams>
 */
export function workerCreated(params) {
    return { evid: 101, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'workerTerminated' event (evid: 102)
 *
 * Event description: web worker terminated
 *
 * @param {workerTerminatedParams} params - The parameters for this event
 * @property {GUID} _event_hash - event_hash
 * @property {GUID} visitor_id - visitor_id
 * @property {string} app_name - app_name
 * @property {string} app_id - app_id
 * @property {string} worker_id - worker id
 * @property {string} builderType - builder_type
 * @property {string} ds_type - ds_type
 * @property {GUID} site_id - site_id
 * @property {string} panel_id - panel_id
 * @property {string} appName - app_name
 * @property {number} ts - ts
 * @property {string} panel_name - panel_name
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {number} time_since_load - time_since_load
 * @property {string} pageId - page_id
 * @property {GUID} vsi - vsi
 * @property {string} token - token
 * @property {string} ver - version
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {string} appId - app_id
 * @property {GUID} esi - esi
 * @property {GUID} msid - msid
 * @property {number} tsp - tsp
 * @property {string} ds_id - ds_id
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<workerTerminatedParams>} LogObject<workerTerminatedParams>
 */
export function workerTerminated(params) {
    return { evid: 102, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'scriptLoaded' event (evid: 103)
 *
 * Event description: script loaded
 *
 * @param {scriptLoadedParams} params - The parameters for this event
 * @property {GUID} _event_hash - event_hash
 * @property {GUID} visitor_id - visitor_id
 * @property {string} app_name - app_name
 * @property {string} app_id - app_id
 * @property {string} worker_id - worker id
 * @property {string} builderType - builder_type
 * @property {string} ds_type - ds_type
 * @property {GUID} site_id - site_id
 * @property {string} panel_id - panel_id
 * @property {string} appName - app_name
 * @property {number} ts - ts
 * @property {string} script_name - script_name
 * @property {string} panel_name - panel_name
 * @property {string} viewmode - viewmode
 * @property {string} script_url - script_url
 * @property {string} input_name - input_name
 * @property {number} time_since_load - time_since_load
 * @property {string} pageId - page_id
 * @property {boolean} is_server_side - {1,0}
 * @property {GUID} vsi - vsi
 * @property {string} token - token
 * @property {string} ver - version
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {string} appId - app_id
 * @property {GUID} esi - esi
 * @property {GUID} msid - msid
 * @property {number} tsp - tsp
 * @property {number} time_spent - time_spent
 * @property {string} ds_id - ds_id
 * @property {string} application_id - application_id
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<scriptLoadedParams>} LogObject<scriptLoadedParams>
 */
export function scriptLoaded(params) {
    return { evid: 103, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'scriptFailToLoad' event (evid: 104)
 *
 * Event description: script fail to load
 *
 * @param {scriptFailToLoadParams} params - The parameters for this event
 * @property {GUID} _event_hash - event_hash
 * @property {GUID} visitor_id - visitor_id
 * @property {string} app_name - app_name
 * @property {string} app_id - app_id
 * @property {string} worker_id - worker id
 * @property {string} builderType - builder_type
 * @property {string} ds_type - ds_type
 * @property {GUID} site_id - site_id
 * @property {string} panel_id - panel_id
 * @property {string} appName - app_name
 * @property {number} ts - ts
 * @property {string} script_name - script_name
 * @property {string} panel_name - panel_name
 * @property {string} viewmode - viewmode
 * @property {string} script_url - script_url
 * @property {string} input_name - input_name
 * @property {number} time_since_load - time_since_load
 * @property {string} pageId - page_id
 * @property {string} error - error_desc
 * @property {GUID} vsi - vsi
 * @property {string} token - token
 * @property {string} ver - version
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {string} appId - app_id
 * @property {GUID} esi - esi
 * @property {GUID} msid - msid
 * @property {number} tsp - tsp
 * @property {number} time_spent - time_spent
 * @property {string} ds_id - ds_id
 * @property {string} application_id - application_id
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<scriptFailToLoadParams>} LogObject<scriptFailToLoadParams>
 */
export function scriptFailToLoad(params) {
    return { evid: 104, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'initAppForPage' event (evid: 105)
 *
 * Event description: initAppForPage - part of the viewer loading with app apps data
 *
 * @param {initAppForPageParams} params - The parameters for this event
 * @property {GUID} _event_hash - event_hash
 * @property {GUID} visitor_id - visitor_id
 * @property {string} app_name - app_name
 * @property {string} app_id - app_id
 * @property {string} worker_id - worker id
 * @property {string} builderType - builder_type
 * @property {string} ds_type - ds_type
 * @property {GUID} site_id - site_id
 * @property {string} panel_id - panel_id
 * @property {string} appName - app_name
 * @property {number} ts - ts
 * @property {string} panel_name - panel_name
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {number} time_since_load - time_since_load
 * @property {string} pageId - page_id
 * @property {boolean} is_server_side - {1,0}
 * @property {GUID} vsi - vsi
 * @property {string} token - token
 * @property {string} ver - version
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {string} appId - app_id
 * @property {GUID} esi - esi
 * @property {GUID} msid - msid
 * @property {number} tsp - tsp
 * @property {number} time_spent - time_spent - milliseconds
 * @property {string} ds_id - ds_id
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<initAppForPageParams>} LogObject<initAppForPageParams>
 */
export function initAppForPage(params) {
    return { evid: 105, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'initAppForPageFailed' event (evid: 106)
 *
 * Event description: initAppForPage failed
 *
 * @param {initAppForPageFailedParams} params - The parameters for this event
 * @property {GUID} _event_hash - event_hash
 * @property {GUID} visitor_id - visitor_id
 * @property {string} app_name - app_name
 * @property {string} app_id - app_id
 * @property {string} worker_id - worker id
 * @property {string} builderType - builder_type
 * @property {string} ds_type - ds_type
 * @property {GUID} site_id - site_id
 * @property {string} panel_id - panel_id
 * @property {string} appName - app_name
 * @property {number} ts - ts
 * @property {string} panel_name - panel_name
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {number} time_since_load - time_since_load
 * @property {string} pageId - page_id
 * @property {string} error - error desc
 * @property {GUID} vsi - vsi
 * @property {string} token - token
 * @property {string} ver - version
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {string} appId - app_id
 * @property {GUID} esi - esi
 * @property {GUID} msid - msid
 * @property {number} tsp - tsp
 * @property {number} time_spent - time_spent - milliseconds
 * @property {string} ds_id - ds_id
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<initAppForPageFailedParams>} LogObject<initAppForPageFailedParams>
 */
export function initAppForPageFailed(params) {
    return { evid: 106, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'createControllers' event (evid: 107)
 *
 * Event description: create Controllers - part of the viewer loading with app apps data
 *
 * @param {createControllersParams} params - The parameters for this event
 * @property {GUID} _event_hash - event_hash
 * @property {GUID} visitor_id - visitor_id
 * @property {string} app_name - app_name
 * @property {string} app_id - app_id
 * @property {string} worker_id - worker id
 * @property {string} builderType - builder_type
 * @property {string} ds_type - ds_type
 * @property {GUID} site_id - site_id
 * @property {string} panel_id - panel_id
 * @property {string} appName - app_name
 * @property {string} control_id - control_id
 * @property {number} ts - ts
 * @property {string} panel_name - panel_name
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {number} time_since_load - time_since_load
 * @property {string} pageId - page_id
 * @property {boolean} is_server_side - {1,0}
 * @property {GUID} vsi - vsi
 * @property {string} token - token
 * @property {string} ver - version
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {string} widget_array - widget_id, widget_id, widget_id...
 * @property {string} appId - app_id
 * @property {GUID} esi - esi
 * @property {GUID} msid - msid
 * @property {number} tsp - tsp
 * @property {number} time_spent - time_spent - milliseconds
 * @property {string} ds_id - ds_id
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<createControllersParams>} LogObject<createControllersParams>
 */
export function createControllers(params) {
    return { evid: 107, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'createControllersFailed' event (evid: 108)
 *
 * Event description: create Controllers failed
 *
 * @param {createControllersFailedParams} params - The parameters for this event
 * @property {GUID} _event_hash - event_hash
 * @property {GUID} visitor_id - visitor_id
 * @property {string} app_name - app_name
 * @property {string} app_id - app_id
 * @property {string} worker_id - worker id
 * @property {string} builderType - builder_type
 * @property {string} ds_type - ds_type
 * @property {GUID} site_id - site_id
 * @property {string} panel_id - panel_id
 * @property {string} appName - app_name
 * @property {string} control_id - control_id
 * @property {number} ts - ts
 * @property {string} panel_name - panel_name
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {number} time_since_load - time_since_load
 * @property {string} pageId - page_id
 * @property {string} error - error desc
 * @property {GUID} vsi - vsi
 * @property {string} token - token
 * @property {string} ver - version
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {string} appId - app_id
 * @property {GUID} esi - esi
 * @property {GUID} msid - msid
 * @property {number} tsp - tsp
 * @property {number} time_spent - time_spent - milliseconds
 * @property {string} ds_id - ds_id
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<createControllersFailedParams>} LogObject<createControllersFailedParams>
 */
export function createControllersFailed(params) {
    return { evid: 108, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'pageReadyAllApps' event (evid: 109)
 *
 * Event description: pageReady all apps
 *
 * @param {pageReadyAllAppsParams} params - The parameters for this event
 * @property {GUID} _event_hash - event_hash
 * @property {GUID} visitor_id - visitor_id
 * @property {string} app_name - app_name
 * @property {string} app_id - app_id
 * @property {string} worker_id - worker id
 * @property {string} builderType - builder_type
 * @property {GUID} session_id - session_id
 * @property {string} ds_type - ds_type
 * @property {GUID} site_id - site_id
 * @property {string} panel_id - panel_id
 * @property {string} appName - app_name
 * @property {number} ts - ts
 * @property {string} panel_name - panel_name
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {number} time_since_load - time_since_load
 * @property {string} pageId - page_id
 * @property {GUID} vsi - vsi
 * @property {string} token - token
 * @property {string} ver - version
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {string} appId - app_id
 * @property {GUID} esi - esi
 * @property {GUID} msid - msid
 * @property {number} tsp - tsp
 * @property {number} time_spent - time_spent - milliseconds
 * @property {string} ds_id - ds_id
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<pageReadyAllAppsParams>} LogObject<pageReadyAllAppsParams>
 */
export function pageReadyAllApps(params) {
    return { evid: 109, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'pageReadyByApp' event (evid: 110)
 *
 * Event description: pageReady by app
 *
 * @param {pageReadyByAppParams} params - The parameters for this event
 * @property {GUID} _event_hash - event_hash
 * @property {GUID} visitor_id - visitor_id
 * @property {string} app_name - app_name
 * @property {string} app_id - app_id
 * @property {string} worker_id - worker id
 * @property {string} builderType - builder_type
 * @property {GUID} session_id - session_id
 * @property {string} ds_type - ds_type
 * @property {GUID} site_id - site_id
 * @property {string} panel_id - panel_id
 * @property {string} appName - app_name
 * @property {number} ts - ts
 * @property {string} panel_name - panel_name
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {number} time_since_load - time_since_load
 * @property {string} pageId - page_id
 * @property {GUID} vsi - vsi
 * @property {string} token - token
 * @property {string} ver - version
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {string} appId - app_id
 * @property {GUID} esi - esi
 * @property {GUID} msid - msid
 * @property {number} tsp - tsp
 * @property {number} time_spent - time_spent - milliseconds
 * @property {string} ds_id - ds_id
 * @property {string} application_id - application_id
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<pageReadyByAppParams>} LogObject<pageReadyByAppParams>
 */
export function pageReadyByApp(params) {
    return { evid: 110, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'pageReadyFailed' event (evid: 111)
 *
 * Event description: pageReady failed
 *
 * @param {pageReadyFailedParams} params - The parameters for this event
 * @property {GUID} _event_hash - event_hash
 * @property {GUID} visitor_id - visitor_id
 * @property {string} app_name - app_name
 * @property {string} app_id - app_id
 * @property {string} worker_id - worker id
 * @property {string} builderType - builder_type
 * @property {string} ds_type - ds_type
 * @property {GUID} site_id - site_id
 * @property {string} panel_id - panel_id
 * @property {string} appName - app_name
 * @property {number} ts - ts
 * @property {string} panel_name - panel_name
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} controller_type - type
 * @property {string} controller_id - controller_id
 * @property {number} time_since_load - time_since_load
 * @property {string} pageId - page_id
 * @property {string} error - error desc
 * @property {GUID} vsi - vsi
 * @property {string} token - token
 * @property {string} ver - version
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {string} appId - app_id
 * @property {GUID} esi - esi
 * @property {GUID} msid - msid
 * @property {number} tsp - tsp
 * @property {number} time_spent - time_spent - milliseconds
 * @property {string} ds_id - ds_id
 * @property {string} application_id - application_id
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<pageReadyFailedParams>} LogObject<pageReadyFailedParams>
 */
export function pageReadyFailed(params) {
    return { evid: 111, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'ideToggleBetweenJsAndSiteCode' event (evid: 112)
 *
 * Event description: IDE - Toggle between js and site code
 *
 * @param {ideToggleBetweenJsAndSiteCodeParams} params - The parameters for this event
 * @property {string} app_name - app_name
 * @property {string} app_id - app_id
 * @property {number} users_with_write_devmode_count - users_with_write_devmode_count
 * @property {string} builderType - builder_type
 * @property {GUID} visitorId - deprecated
 * @property {string} state - focused, blurred
 * @property {string} ds_type - ds_type
 * @property {GUID} site_id - site_id
 * @property {string} panel_id - panel_id
 * @property {string} appName - app_name
 * @property {number} users_on_site_count - users_on_site_count
 * @property {number} ts - ts
 * @property {string} panel_name - panel_name
 * @property {string} viewmode - viewmode
 * @property {string} input_name - deprecated
 * @property {string} pageId - page_id
 * @property {string} token - token
 * @property {number} users_with_devmode_count - users_with_devmode_count
 * @property {string} ver - version
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {string} toggle_name - toggle_name(js,site)
 * @property {string} dev_mode - dev_mode
 * @property {string} appId - app_id
 * @property {GUID} esi - esi
 * @property {GUID} msid - msid
 * @property {string} ds_id - ds_id
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<ideToggleBetweenJsAndSiteCodeParams>} LogObject<ideToggleBetweenJsAndSiteCodeParams>
 */
export function ideToggleBetweenJsAndSiteCode(params) {
    return { evid: 112, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'ideToolbarAction' event (evid: 113)
 *
 * Event description: When a user clicks on a button in IDE toolbar
 *
 * @param {ideToolbarActionParams} params - The parameters for this event
 * @property {string} app_name - app_name
 * @property {string} app_id - app_id
 * @property {number} users_with_write_devmode_count - users_with_write_devmode_count
 * @property {string} builderType - builder_type
 * @property {GUID} visitorId - deprecated
 * @property {string} state - focused, blurred
 * @property {string} ds_type - ds_type
 * @property {GUID} site_id - site_id
 * @property {string} panel_id - panel_id
 * @property {string} appName - app_name
 * @property {number} users_on_site_count - users_on_site_count
 * @property {number} ts - ts
 * @property {string} panel_name - panel_name
 * @property {string} viewmode - viewmode
 * @property {string} input_name - deprecated
 * @property {string} pageId - page_id
 * @property {string} item_name - item_name(undo,redo,search,format,maximize,minimize,open)
 * @property {string} origin - IDE_top_bar / which shortcut used
 * @property {string} token - token
 * @property {number} users_with_devmode_count - users_with_devmode_count
 * @property {string} ver - version
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {string} dev_mode - dev_mode
 * @property {string} appId - app_id
 * @property {GUID} esi - esi
 * @property {GUID} msid - msid
 * @property {string} action - {}
 * @property {string} ds_id - ds_id
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<ideToolbarActionParams>} LogObject<ideToolbarActionParams>
 */
export function ideToolbarAction(params) {
    return { evid: 113, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'ideToggleIdeViaIdeHeader' event (evid: 114)
 *
 * Event description: IDE - Toggle IDE via IDE header
 *
 * @param {ideToggleIdeViaIdeHeaderParams} params - The parameters for this event
 * @property {string} app_name - app_name
 * @property {string} app_id - app_id
 * @property {number} users_with_write_devmode_count - users_with_write_devmode_count
 * @property {string} builderType - builder_type
 * @property {GUID} visitorId - deprecated
 * @property {string} state - focused, blurred
 * @property {string} ds_type - ds_type
 * @property {GUID} site_id - site_id
 * @property {string} panel_id - panel_id
 * @property {string} appName - app_name
 * @property {number} users_on_site_count - users_on_site_count
 * @property {number} ts - ts
 * @property {string} panel_name - panel_name
 * @property {string} viewmode - viewmode
 * @property {string} input_name - deprecated
 * @property {string} pageId - page_id
 * @property {string} origin - {user_click, dev_mode_on}
 * @property {string} token - token
 * @property {number} users_with_devmode_count - users_with_devmode_count
 * @property {string} ver - version
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {string} toggle_name - toggle_name(open,close)
 * @property {string} dev_mode - dev_mode
 * @property {string} appId - app_id
 * @property {GUID} esi - esi
 * @property {GUID} msid - msid
 * @property {string} ds_id - ds_id
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<ideToggleIdeViaIdeHeaderParams>} LogObject<ideToggleIdeViaIdeHeaderParams>
 */
export function ideToggleIdeViaIdeHeader(params) {
    return { evid: 114, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'ideClickToOpenMediaManager' event (evid: 115)
 *
 * Event description: IDE - Click to open media manager
 *
 * @param {ideClickToOpenMediaManagerParams} params - The parameters for this event
 * @property {string} app_name - app_name
 * @property {string} app_id - app_id
 * @property {number} users_with_write_devmode_count - users_with_write_devmode_count
 * @property {string} builderType - builder_type
 * @property {GUID} visitorId - deprecated
 * @property {string} state - focused, blurred
 * @property {string} ds_type - ds_type
 * @property {GUID} site_id - site_id
 * @property {string} panel_id - panel_id
 * @property {string} appName - app_name
 * @property {number} users_on_site_count - users_on_site_count
 * @property {number} ts - ts
 * @property {string} panel_name - panel_name
 * @property {string} viewmode - viewmode
 * @property {string} input_name - deprecated
 * @property {string} pageId - page_id
 * @property {string} origin - completion/hover_image
 * @property {string} token - token
 * @property {number} users_with_devmode_count - users_with_devmode_count
 * @property {string} ver - version
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {string} dev_mode - dev_mode
 * @property {string} type - media type e.g image,vector
 * @property {string} appId - app_id
 * @property {GUID} esi - esi
 * @property {GUID} msid - msid
 * @property {string} ds_id - ds_id
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<ideClickToOpenMediaManagerParams>} LogObject<ideClickToOpenMediaManagerParams>
 */
export function ideClickToOpenMediaManager(params) {
    return { evid: 115, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'ideMediaAddedFromMediaManager' event (evid: 116)
 *
 * Event description: IDE - media added from media manager opened through IDE
 *
 * @param {ideMediaAddedFromMediaManagerParams} params - The parameters for this event
 * @property {string} app_name - app_name
 * @property {string} app_id - app_id
 * @property {number} users_with_write_devmode_count - users_with_write_devmode_count
 * @property {string} builderType - builder_type
 * @property {GUID} visitorId - deprecated
 * @property {string} state - focused, blurred
 * @property {string} ds_type - ds_type
 * @property {GUID} site_id - site_id
 * @property {string} panel_id - panel_id
 * @property {string} appName - app_name
 * @property {number} users_on_site_count - users_on_site_count
 * @property {number} ts - ts
 * @property {string} panel_name - panel_name
 * @property {string} viewmode - viewmode
 * @property {string} input_name - deprecated
 * @property {string} pageId - page_id
 * @property {string} token - token
 * @property {number} users_with_devmode_count - users_with_devmode_count
 * @property {string} ver - version
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {string} dev_mode - dev_mode
 * @property {string} type - media type e.g image,vector
 * @property {string} appId - app_id
 * @property {GUID} esi - esi
 * @property {GUID} msid - msid
 * @property {string} ds_id - ds_id
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<ideMediaAddedFromMediaManagerParams>} LogObject<ideMediaAddedFromMediaManagerParams>
 */
export function ideMediaAddedFromMediaManager(params) {
    return { evid: 116, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'connectDropdownOptionsValuesDisconnected' event (evid: 117)
 *
 * Event description: Connect dropdown options values disconnected
 *
 * @param {connectDropdownOptionsValuesDisconnectedParams} params - The parameters for this event
 * @property {string} app_name - app_name
 * @property {string} app_id - app_id
 * @property {number} users_with_write_devmode_count - users_with_write_devmode_count
 * @property {string} builderType - builder_type
 * @property {GUID} visitorId - deprecated
 * @property {string} ds_type - ds_type
 * @property {GUID} site_id - site_id
 * @property {string} panel_id - panel_id
 * @property {string} appName - app_name
 * @property {number} users_on_site_count - users_on_site_count
 * @property {number} ts - ts
 * @property {string} panel_name - panel_name
 * @property {string} viewmode - viewmode
 * @property {string} input_name - deprecated
 * @property {string} pageId - page_id
 * @property {string} token - token
 * @property {number} users_with_devmode_count - users_with_devmode_count
 * @property {string} ver - version
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {string} property - property
 * @property {string} dev_mode - dev_mode
 * @property {string} appId - app_id
 * @property {GUID} esi - esi
 * @property {string} component_id - component_id
 * @property {string} component_type - component_type
 * @property {GUID} msid - msid
 * @property {string} ds_id - ds_id
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<connectDropdownOptionsValuesDisconnectedParams>} LogObject<connectDropdownOptionsValuesDisconnectedParams>
 */
export function connectDropdownOptionsValuesDisconnected(params) {
    return { evid: 117, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'connectTableLinkCleared' event (evid: 118)
 *
 * Event description: Connect table link cleared
 *
 * @param {connectTableLinkClearedParams} params - The parameters for this event
 * @property {string} app_name - app_name
 * @property {string} app_id - app_id
 * @property {number} users_with_write_devmode_count - users_with_write_devmode_count
 * @property {string} builderType - builder_type
 * @property {GUID} visitorId - deprecated
 * @property {string} ds_type - ds_type
 * @property {GUID} site_id - site_id
 * @property {string} panel_id - panel_id
 * @property {string} appName - app_name
 * @property {number} users_on_site_count - users_on_site_count
 * @property {number} ts - ts
 * @property {string} panel_name - panel_name
 * @property {string} viewmode - viewmode
 * @property {string} input_name - deprecated
 * @property {string} pageId - page_id
 * @property {string} token - token
 * @property {number} users_with_devmode_count - users_with_devmode_count
 * @property {string} ver - version
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {string} property - property
 * @property {string} dev_mode - dev_mode
 * @property {string} appId - app_id
 * @property {GUID} esi - esi
 * @property {string} component_id - component_id
 * @property {string} component_type - component_type
 * @property {GUID} msid - msid
 * @property {string} ds_id - ds_id
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<connectTableLinkClearedParams>} LogObject<connectTableLinkClearedParams>
 */
export function connectTableLinkCleared(params) {
    return { evid: 118, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'backButtonClicked' event (evid: 119)
 *
 * Event description: back button clicked
 *
 * @param {backButtonClickedParams} params - The parameters for this event
 * @property {string} app_name - app_name
 * @property {string} app_id - app_id
 * @property {number} users_with_write_devmode_count - users_with_write_devmode_count
 * @property {string} builderType - builder_type
 * @property {GUID} visitorId - visitor_id
 * @property {string} ds_type - ds_type
 * @property {GUID} site_id - site_id
 * @property {string} panel_id - panel_id
 * @property {string} appName - app_name
 * @property {number} users_on_site_count - users_on_site_count
 * @property {number} ts - ts
 * @property {string} panel_name - panel_name
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} pageId - page_id
 * @property {string} token - token
 * @property {number} users_with_devmode_count - users_with_devmode_count
 * @property {string} ver - version
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {string} dev_mode - dev_mode
 * @property {string} appId - app_id
 * @property {GUID} esi - esi
 * @property {GUID} msid - msid
 * @property {string} ds_id - ds_id
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<backButtonClickedParams>} LogObject<backButtonClickedParams>
 */
export function backButtonClicked(params) {
    return { evid: 119, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'conflictingDynamicPageUrlError' event (evid: 120)
 *
 * Event description: conflicting dynamic page url error
 *
 * @param {conflictingDynamicPageUrlErrorParams} params - The parameters for this event
 * @property {string} app_name - app_name
 * @property {string} app_id - app_id
 * @property {string} existing_url - existing_url
 * @property {number} users_with_write_devmode_count - users_with_write_devmode_count
 * @property {string} builderType - builder_type
 * @property {GUID} visitorId - visitor_id
 * @property {string} conflicting_url - conflicting_url
 * @property {string} ds_type - ds_type
 * @property {GUID} site_id - site_id
 * @property {string} panel_id - panel_id
 * @property {string} appName - app_name
 * @property {number} users_on_site_count - users_on_site_count
 * @property {number} ts - ts
 * @property {string} panel_name - panel_name
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} pageId - page_id
 * @property {string} token - token
 * @property {number} users_with_devmode_count - users_with_devmode_count
 * @property {string} ver - version
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {string} dev_mode - dev_mode
 * @property {string} appId - app_id
 * @property {GUID} esi - esi
 * @property {GUID} msid - msid
 * @property {string} ds_id - ds_id
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<conflictingDynamicPageUrlErrorParams>} LogObject<conflictingDynamicPageUrlErrorParams>
 */
export function conflictingDynamicPageUrlError(params) {
    return { evid: 120, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'checkedIfPageIsCachable' event (evid: 1200)
 *
 * Event description: analyzing automatic cache for pages, based on existence and type of user code. sent for each page every publish
 *
 * @param {checkedIfPageIsCachableParams} params - The parameters for this event
 * @property {string} reason - reason why not cachable (only if not cachable)
 * @property {string} page_id - page id
 * @property {string} grid_app_id - grid_app_id
 * @property {boolean} is_cacheable - true if the page will be cached
 * @property {GUID} msid - msid
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<checkedIfPageIsCachableParams>} LogObject<checkedIfPageIsCachableParams>
 */
export function checkedIfPageIsCachable(params) {
    return { evid: 1200, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'siteCodeBundle' event (evid: 1201)
 *
 * Event description: when a site with code change tries bundling in the background bundler after publish was clicked. NOTICE - this BI is triggered 3 times on each publish click (once for each data center)
 *
 * @param {siteCodeBundleParams} params - The parameters for this event
 * @property {string} renderingFlow - Rendering flow
 * @property {number} duration - the bundling's duration
 * @property {boolean} is_cached - true if the bundler runs again
 * @property {string} codeAppId - code_app_id
 * @property {string} flowType - publish-pipeline/legacy-publish/self-healing
 * @property {string} response - user-error/system-error/success
 * @property {GUID} msid - msid
 * @property {string} taskErrorName - task_error_nam
 * @property {string} deploymentId - deployment_id, unique per bundling
 * @return {LogObject<siteCodeBundleParams>} LogObject<siteCodeBundleParams>
 */
export function siteCodeBundle(params) {
    return { evid: 1201, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'changeCollectionConfirmed' event (evid: 121)
 *
 * Event description: Change collection confirmed
 *
 * @param {changeCollectionConfirmedParams} params - The parameters for this event
 * @property {string} app_name - app_name
 * @property {string} app_id - app_id
 * @property {string} name - name
 * @property {number} users_with_write_devmode_count - users_with_write_devmode_count
 * @property {string} builderType - builder_type
 * @property {GUID} visitorId - visitor_id
 * @property {string} ds_type - ds_type
 * @property {GUID} site_id - site_id
 * @property {string} panel_id - panel_id
 * @property {string} appName - app_name
 * @property {number} users_on_site_count - users_on_site_count
 * @property {number} ts - ts
 * @property {string} collection_id - collection_id
 * @property {string} panel_name - panel_name
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} ds_name - ds_name
 * @property {string} pageId - page_id
 * @property {string} token - token
 * @property {number} users_with_devmode_count - users_with_devmode_count
 * @property {string} ver - version
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {string} category - ["cancel", "close", "continue"]
 * @property {string} dev_mode - dev_mode
 * @property {string} appId - app_id
 * @property {GUID} esi - esi
 * @property {string} collection_name - collection_name
 * @property {GUID} msid - msid
 * @property {string} ds_id - ds_id
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<changeCollectionConfirmedParams>} LogObject<changeCollectionConfirmedParams>
 */
export function changeCollectionConfirmed(params) {
    return { evid: 121, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'addFormMessage' event (evid: 126)
 *
 * Event description: add form message
 *
 * @param {addFormMessageParams} params - The parameters for this event
 * @property {string} app_name - app_name
 * @property {string} app_id - app_id
 * @property {number} users_with_write_devmode_count - users_with_write_devmode_count
 * @property {string} builderType - builder_type
 * @property {GUID} visitorId - visitor_id
 * @property {string} ds_type - ds_type
 * @property {GUID} site_id - site_id
 * @property {string} panel_id - panel_id
 * @property {string} data_set_name - data_set_name
 * @property {string} appName - app_name
 * @property {number} users_on_site_count - users_on_site_count
 * @property {string} field_type - field_type
 * @property {number} ts - ts
 * @property {string} panel_name - panel_name
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} pageId - page_id
 * @property {string} token - token
 * @property {number} users_with_devmode_count - users_with_devmode_count
 * @property {string} ver - version
 * @property {string} token_id - token_id
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {string} message - {Add Success Message / Add Failure Message}
 * @property {string} property - property
 * @property {string} dev_mode - dev_mode
 * @property {string} appId - app_id
 * @property {GUID} esi - esi
 * @property {string} component_id - component_id
 * @property {string} component_type - comp type
 * @property {GUID} msid - msid
 * @property {string} field_name - field_name
 * @property {string} value - {message value}
 * @property {string} ds_id - ds_id
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<addFormMessageParams>} LogObject<addFormMessageParams>
 */
export function addFormMessage(params) {
    return { evid: 126, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'dsFilterPanelValueTypeChanged' event (evid: 127)
 *
 * Event description: DS filter panel value type changed
 *
 * @param {dsFilterPanelValueTypeChangedParams} params - The parameters for this event
 * @property {string} app_name - app_name
 * @property {string} app_id - app_id
 * @property {string} renderingFlow - Rendering flow
 * @property {number} users_with_write_devmode_count - users_with_write_devmode_count
 * @property {string} value_type - value  (value/dataset selected)
 * @property {string} builderType - builder_type
 * @property {GUID} visitorId - visitor_id(deprecated)
 * @property {string} ds_type - ds_type
 * @property {GUID} site_id - site_id
 * @property {string} panel_id - panel_id
 * @property {string} appName - app_name
 * @property {number} users_on_site_count - users_on_site_count
 * @property {number} ts - ts
 * @property {string} panel_name - panel_name
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} pageId - page_id
 * @property {string} token - token
 * @property {number} users_with_devmode_count - users_with_devmode_count
 * @property {string} ver - version
 * @property {string} token_id - token
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {string} mode - {mode: add/edit}
 * @property {string} dev_mode - dev_mode
 * @property {string} appId - app_id
 * @property {GUID} esi - esi
 * @property {GUID} msid - msid
 * @property {string} value - value  (value/dataset selected) Canceled
 * @property {string} ds_id - ds_id
 * @property {string} collection_id - collection_id
 * @return {LogObject<dsFilterPanelValueTypeChangedParams>} LogObject<dsFilterPanelValueTypeChangedParams>
 */
export function dsFilterPanelValueTypeChanged(params) {
    return { evid: 127, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'filterByDatasetChanged' event (evid: 128)
 *
 * Event description: filter by dataset changed
 *
 * @param {filterByDatasetChangedParams} params - The parameters for this event
 * @property {string} app_name - app_name
 * @property {string} app_id - app_id
 * @property {string} renderingFlow - Rendering flow
 * @property {number} users_with_write_devmode_count - users_with_write_devmode_count
 * @property {string} builderType - builder_type
 * @property {GUID} visitorId - visitor_id(deprecated)
 * @property {string} ds_type - ds_type
 * @property {GUID} site_id - site_id
 * @property {string} panel_id - panel_id
 * @property {string} appName - app_name
 * @property {number} users_on_site_count - users_on_site_count
 * @property {number} ts - ts
 * @property {string} panel_name - panel_name
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} pageId - page_id
 * @property {string} token - token
 * @property {number} users_with_devmode_count - users_with_devmode_count
 * @property {string} ver - version
 * @property {string} token_id - token
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {string} mode - {mode: add/edit}
 * @property {string} dev_mode - dev_mode
 * @property {string} appId - app_id
 * @property {GUID} esi - esi
 * @property {string} new_ds_id - new_ds_id
 * @property {GUID} msid - msid
 * @property {string} ds_id - ds_id
 * @property {string} collection_id - collection_id
 * @return {LogObject<filterByDatasetChangedParams>} LogObject<filterByDatasetChangedParams>
 */
export function filterByDatasetChanged(params) {
    return { evid: 128, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'filterByDatasetFieldChanged' event (evid: 129)
 *
 * Event description: filter by dataset field changed
 *
 * @param {filterByDatasetFieldChangedParams} params - The parameters for this event
 * @property {string} app_name - app_name
 * @property {string} app_id - app_id
 * @property {string} renderingFlow - Rendering flow
 * @property {number} users_with_write_devmode_count - users_with_write_devmode_count
 * @property {string} builderType - builder_type
 * @property {GUID} visitorId - visitor_id(deprecated)
 * @property {string} ds_type - ds_type
 * @property {GUID} site_id - site_id
 * @property {string} panel_id - panel_id
 * @property {string} appName - app_name
 * @property {number} users_on_site_count - users_on_site_count
 * @property {string} field_type - field_type
 * @property {number} ts - ts
 * @property {string} panel_name - panel_name
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} pageId - page_id
 * @property {string} token - token
 * @property {number} users_with_devmode_count - users_with_devmode_count
 * @property {string} ver - version
 * @property {string} token_id - token
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {string} mode - {mode: add/edit}
 * @property {string} dev_mode - dev_mode
 * @property {string} appId - app_id
 * @property {GUID} esi - esi
 * @property {GUID} msid - msid
 * @property {string} field_name - field_name
 * @property {string} ds_id - ds_id
 * @property {string} collection_id - collection_id
 * @return {LogObject<filterByDatasetFieldChangedParams>} LogObject<filterByDatasetFieldChangedParams>
 */
export function filterByDatasetFieldChanged(params) {
    return { evid: 129, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'dsNumberOfItemSet' event (evid: 130)
 *
 * Event description: DS number of item set
 *
 * @param {dsNumberOfItemSetParams} params - The parameters for this event
 * @property {string} app_name - app_name
 * @property {string} app_id - app_id
 * @property {number} users_with_write_devmode_count - users_with_write_devmode_count
 * @property {string} builderType - builder_type
 * @property {GUID} visitorId - visitor id
 * @property {string} ds_type - ds_type
 * @property {GUID} site_id - site_id
 * @property {string} panel_id - panel_id
 * @property {string} appName - app_name
 * @property {number} users_on_site_count - users_on_site_count
 * @property {string} old_value - old_value
 * @property {number} ts - ts
 * @property {string} panel_name - panel_name
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} pageId - page_id
 * @property {string} token - token
 * @property {number} users_with_devmode_count - users_with_devmode_count
 * @property {string} ver - version
 * @property {string} userEnv - user_env
 * @property {string} new_value - new_value
 * @property {string} editor_working_mode - editor_working_mode
 * @property {string} dev_mode - dev_mode
 * @property {string} appId - app_id
 * @property {GUID} esi - esi
 * @property {GUID} msid - msid
 * @property {string} ds_id - ds_id
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<dsNumberOfItemSetParams>} LogObject<dsNumberOfItemSetParams>
 */
export function dsNumberOfItemSet(params) {
    return { evid: 130, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'bootstrapDone' event (evid: 132)
 *
 * Event description: will fire it when the bootstrap step was completed - loading es6runtime(not always), Wix-code SDK(always) and all app's scripts(if exist)
 *
 * @param {bootstrapDoneParams} params - The parameters for this event
 * @property {GUID} _event_hash - event_hash
 * @property {GUID} visitor_id - visitor_id
 * @property {string} app_name - app_name
 * @property {string} app_id - app_id
 * @property {string} worker_id - worker id( canceled )
 * @property {string} builderType - builder_type
 * @property {GUID} session_id - session_id
 * @property {string} ds_type - ds_type
 * @property {GUID} site_id - site_id
 * @property {string} panel_id - panel_id
 * @property {string} appName - app_name
 * @property {number} ts - ts
 * @property {string} panel_name - panel_name
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {number} time_since_load - time_since_load
 * @property {string} pageId - page_id
 * @property {GUID} vsi - vsi
 * @property {string} token - token
 * @property {string} ver - version
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {string} appId - app_id
 * @property {GUID} esi - esi
 * @property {GUID} msid - msid
 * @property {number} tsp - tsp
 * @property {string} ds_id - ds_id
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<bootstrapDoneParams>} LogObject<bootstrapDoneParams>
 */
export function bootstrapDone(params) {
    return { evid: 132, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'userCodeLoaded' event (evid: 133)
 *
 * Event description: will fire it after user code wad loaded - relevant only for sites with code. For example, site with only members app won't fire this event
 *
 * @param {userCodeLoadedParams} params - The parameters for this event
 * @property {GUID} _event_hash - event_hash
 * @property {GUID} visitor_id - visitor_id
 * @property {string} app_name - app_name
 * @property {string} app_id - app_id
 * @property {number} duration - the load's duration
 * @property {string} worker_id - worker id
 * @property {string} builderType - builder_type
 * @property {GUID} session_id - session_id
 * @property {string} ds_type - ds_type
 * @property {boolean} isServerSide - is_server_side
 * @property {GUID} site_id - site_id
 * @property {string} panel_id - panel_id
 * @property {string} appName - app_name
 * @property {Url} pageUrl - []
 * @property {number} ts - ts
 * @property {string} panel_name - panel_name
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {number} time_since_load - time_since_load
 * @property {string} pageId - page_id
 * @property {GUID} vsi - vsi
 * @property {string} token - token
 * @property {string} ver - version
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {string} appId - app_id
 * @property {GUID} esi - esi
 * @property {GUID} msid - msid
 * @property {number} tsp - tsp
 * @property {string} ds_id - ds_id
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<userCodeLoadedParams>} LogObject<userCodeLoadedParams>
 */
export function userCodeLoaded(params) {
    return { evid: 133, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'userCodeUseSdk' event (evid: 136)
 *
 * Event description:  Only for live sites (Not preview mode)  once per page view
 *  for any $w interaction with elements on the page
 *  including - page url
 *
 * @param {userCodeUseSdkParams} params - The parameters for this event
 * @property {GUID} _event_hash - event_hash
 * @property {GUID} visitor_id - visitor_id
 * @property {string} app_name - app_name
 * @property {string} app_id - app_id
 * @property {Url} page_url - []
 * @property {string} worker_id - worker id
 * @property {string} builderType - builder_type
 * @property {GUID} session_id - session_id
 * @property {string} ds_type - ds_type
 * @property {boolean} isServerSide - []
 * @property {GUID} site_id - site_id
 * @property {string} panel_id - panel_id
 * @property {string} appName - app_name
 * @property {Url} pageUrl - []
 * @property {number} pn - page_number
 * @property {number} ts - ts
 * @property {string} element_name - element name
 * @property {number} tsn - time_from_start_network_ms
 * @property {string} panel_name - panel_name
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input name
 * @property {boolean} is_lightbox - []
 * @property {string} pageId - page_id
 * @property {GUID} vsi - vsi
 * @property {string} token - token
 * @property {string} ver - version
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {string} appId - app_id
 * @property {Url} pageurl - []
 * @property {GUID} esi - esi
 * @property {GUID} msid - msid
 * @property {number} tsp - tsp
 * @property {string} ds_id - ds_id
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<userCodeUseSdkParams>} LogObject<userCodeUseSdkParams>
 */
export function userCodeUseSdk(params) {
    return { evid: 136, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'logConsoleDeeplinkDialogAction' event (evid: 138)
 *
 * Event description: Log Console - Deeplink dialog action
 *
 * @param {logConsoleDeeplinkDialogActionParams} params - The parameters for this event
 * @property {string} app_name - app_name
 * @property {string} app_id - app_id
 * @property {number} users_with_write_devmode_count - users_with_write_devmode_count
 * @property {string} builderType - builder_type
 * @property {GUID} visitorId - visitor_id
 * @property {string} section_name - (pages,public,backend,site)
 * @property {string} ds_type - ds_type
 * @property {GUID} site_id - site_id
 * @property {string} panel_id - panel_id
 * @property {string} appName - app_name
 * @property {number} users_on_site_count - users_on_site_count
 * @property {number} ts - ts
 * @property {string} panel_name - panel_name
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} pageId - page_id
 * @property {string} token - token
 * @property {number} users_with_devmode_count - users_with_devmode_count
 * @property {string} ver - version
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {string} dev_mode - dev_mode
 * @property {string} appId - app_id
 * @property {GUID} esi - esi
 * @property {GUID} msid - msid
 * @property {string} action - (deeplink, dismiss)
 * @property {string} ds_id - ds_id
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<logConsoleDeeplinkDialogActionParams>} LogObject<logConsoleDeeplinkDialogActionParams>
 */
export function logConsoleDeeplinkDialogAction(params) {
    return { evid: 138, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'logConsoleSeverityOnChange' event (evid: 139)
 *
 * Event description: Log Console - Severity OnChange
 *
 * @param {logConsoleSeverityOnChangeParams} params - The parameters for this event
 * @property {string} app_name - app_name
 * @property {string} app_id - app_id
 * @property {number} users_with_write_devmode_count - users_with_write_devmode_count
 * @property {string} builderType - builder_type
 * @property {GUID} visitorId - visitor_id
 * @property {string} ds_type - ds_type
 * @property {GUID} site_id - site_id
 * @property {string} panel_id - panel_id
 * @property {string} appName - app_name
 * @property {number} users_on_site_count - users_on_site_count
 * @property {number} ts - ts
 * @property {string} panel_name - panel_name
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} pageId - page_id
 * @property {string} severity_name - (error, warning, info, debug, verbose)
 * @property {string} token - token
 * @property {number} users_with_devmode_count - users_with_devmode_count
 * @property {string} ver - version
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {string} dev_mode - dev_mode
 * @property {string} appId - app_id
 * @property {GUID} esi - esi
 * @property {GUID} msid - msid
 * @property {string} action - (on,off)
 * @property {string} ds_id - ds_id
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<logConsoleSeverityOnChangeParams>} LogObject<logConsoleSeverityOnChangeParams>
 */
export function logConsoleSeverityOnChange(params) {
    return { evid: 139, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'uninstallNpmPackageMessage' event (evid: 142)
 *
 * Event description: controller_pageReady per widget
 *
 * @param {uninstallNpmPackageMessageParams} params - The parameters for this event
 * @property {GUID} _event_hash - event_hash
 * @property {GUID} visitor_id - visitor_id
 * @property {string} app_name - app_name
 * @property {string} app_id - app_id
 * @property {GUID} widget_id - widget_id
 * @property {string} worker_id - worker id
 * @property {string} builderType - builder_type
 * @property {string} ds_type - ds_type
 * @property {GUID} site_id - site_id
 * @property {string} panel_id - panel_id
 * @property {string} appName - app_name
 * @property {number} ts - ts
 * @property {string} panel_name - panel_name
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} controller_type - type
 * @property {string} controller_id - controller_id
 * @property {string} pageId - page_id
 * @property {boolean} is_server_side - {1,0}
 * @property {GUID} vsi - vsi
 * @property {string} token - token
 * @property {string} ver - version
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {string} appId - app_id
 * @property {GUID} esi - esi
 * @property {GUID} msid - guid
 * @property {number} tsp - tsp
 * @property {number} time_spent - time_spent - milliseconds
 * @property {string} ds_id - ds_id
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<uninstallNpmPackageMessageParams>} LogObject<uninstallNpmPackageMessageParams>
 */
export function uninstallNpmPackageMessage(params) {
    return { evid: 142, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'uninstallNpmPackageSelectClick' event (evid: 143)
 *
 * Event description: Uninstall NPM package select click
 *
 * @param {uninstallNpmPackageSelectClickParams} params - The parameters for this event
 * @property {string} app_name - app_name
 * @property {string} app_id - app_id
 * @property {string} item_version - {}
 * @property {number} users_with_write_devmode_count - users_with_write_devmode_count
 * @property {string} builderType - builder_type
 * @property {GUID} visitorId - visitor_id
 * @property {string} ds_type - ds_type
 * @property {GUID} site_id - site_id
 * @property {string} panel_id - panel_id
 * @property {string} appName - app_name
 * @property {number} users_on_site_count - users_on_site_count
 * @property {number} ts - ts
 * @property {GUID} visitor_id - visitor_id
 * @property {string} panel_name - panel_name
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} pageId - page_id
 * @property {string} item_name - item_name
 * @property {string} token - token
 * @property {number} users_with_devmode_count - users_with_devmode_count
 * @property {string} ver - version
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {string} category - {cancel/delete}
 * @property {string} dev_mode - dev_mode
 * @property {string} appId - app_id
 * @property {GUID} esi - esi
 * @property {string} item_type - item_type (npm, internal_package)
 * @property {GUID} msid - {}
 * @property {string} ds_id - ds_id
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<uninstallNpmPackageSelectClickParams>} LogObject<uninstallNpmPackageSelectClickParams>
 */
export function uninstallNpmPackageSelectClick(params) {
    return { evid: 143, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'searchNpmPackageInstallClicked' event (evid: 144)
 *
 * Event description: search NPM package install clicked
 *
 * @param {searchNpmPackageInstallClickedParams} params - The parameters for this event
 * @property {string} app_name - app_name
 * @property {string} app_id - app_id
 * @property {string} item_version - {}
 * @property {number} users_with_write_devmode_count - users_with_write_devmode_count
 * @property {string} builderType - builder_type
 * @property {GUID} visitorId - visitor_id
 * @property {string} ds_type - ds_type
 * @property {GUID} site_id - site_id
 * @property {string} panel_id - panel_id
 * @property {string} appName - app_name
 * @property {number} users_on_site_count - users_on_site_count
 * @property {number} ts - ts
 * @property {GUID} visitor_id - visitor_id
 * @property {string} panel_name - panel_name
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} pageId - page_id
 * @property {string} item_name - item_name
 * @property {string} origin - package_manager_main_view, built_versions
 * @property {string} token - token
 * @property {number} users_with_devmode_count - users_with_devmode_count
 * @property {string} ver - version
 * @property {string} userEnv - user_env
 * @property {boolean} is_latest_version - is_latest_version
 * @property {string} editor_working_mode - editor_working_mode
 * @property {string} dev_mode - dev_mode
 * @property {string} appId - app_id
 * @property {GUID} esi - esi
 * @property {string} item_type - item_type (npm, internal, private)
 * @property {GUID} msid - {}
 * @property {string} ds_id - ds_id
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<searchNpmPackageInstallClickedParams>} LogObject<searchNpmPackageInstallClickedParams>
 */
export function searchNpmPackageInstallClicked(params) {
    return { evid: 144, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'searchNpmPackageReadMoreClicked' event (evid: 145)
 *
 * Event description: search NPM quick action clicked
 *
 * @param {searchNpmPackageReadMoreClickedParams} params - The parameters for this event
 * @property {string} app_name - app_name
 * @property {string} app_id - app_id
 * @property {string} item_version - item  version
 * @property {number} users_with_write_devmode_count - users_with_write_devmode_count
 * @property {string} builderType - builder_type
 * @property {GUID} visitorId - visitor_id
 * @property {string} ds_type - ds_type
 * @property {GUID} site_id - site_id
 * @property {string} panel_id - panel_id
 * @property {string} appName - app_name
 * @property {number} users_on_site_count - users_on_site_count
 * @property {number} ts - ts
 * @property {GUID} visitor_id - visitor_id
 * @property {string} panel_name - panel_name
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} pageId - page_id
 * @property {string} item_name - {}
 * @property {string} menu_entry_name - {internal_packages, installed_packages, npm_packages, my_packages}
 * @property {string} origin - {}
 * @property {string} token - token
 * @property {number} users_with_devmode_count - users_with_devmode_count
 * @property {string} ver - version
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {string} category - {documentation ,download_zip, view, uninstall, install, see all versions, see_whats_new, update_version, downgrade_version, request_new_package}
 * @property {string} dev_mode - dev_mode
 * @property {string} appId - app_id
 * @property {GUID} esi - esi
 * @property {string} item_type - item_type (npm, internal,private)
 * @property {GUID} msid - {}
 * @property {string} ds_id - ds_id
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<searchNpmPackageReadMoreClickedParams>} LogObject<searchNpmPackageReadMoreClickedParams>
 */
export function searchNpmPackageReadMoreClicked(params) {
    return { evid: 145, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'submitRequestForNewNpmPackage' event (evid: 147)
 *
 * Event description: Submit request for new NPM package
 *
 * @param {submitRequestForNewNpmPackageParams} params - The parameters for this event
 * @property {string} app_name - app_name
 * @property {string} app_id - app_id
 * @property {string} item_version - {}
 * @property {number} users_with_write_devmode_count - users_with_write_devmode_count
 * @property {string} builderType - builder_type
 * @property {GUID} visitorId - visitor_id
 * @property {string} ds_type - ds_type
 * @property {GUID} site_id - site_id
 * @property {string} panel_id - panel_id
 * @property {string} appName - app_name
 * @property {number} users_on_site_count - users_on_site_count
 * @property {number} ts - ts
 * @property {GUID} visitor_id - visitor_id
 * @property {string} panel_name - panel_name
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} reason - {}
 * @property {string} pageId - page_id
 * @property {string} item_name - {}
 * @property {string} token - token
 * @property {number} users_with_devmode_count - users_with_devmode_count
 * @property {string} ver - version
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {string} dev_mode - dev_mode
 * @property {string} appId - app_id
 * @property {GUID} esi - esi
 * @property {GUID} msid - {}
 * @property {string} ds_id - ds_id
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<submitRequestForNewNpmPackageParams>} LogObject<submitRequestForNewNpmPackageParams>
 */
export function submitRequestForNewNpmPackage(params) {
    return { evid: 147, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'prefetchScriptEvalFailed' event (evid: 149)
 *
 * Event description: Failed to evaluate prefetch script
 *
 * @param {prefetchScriptEvalFailedParams} params - The parameters for this event
 * @property {GUID} _event_hash - event_hash
 * @property {GUID} visitor_id - visitor_id
 * @property {string} app_name - app_name
 * @property {string} app_id - app_id
 * @property {string} worker_id - worker id
 * @property {string} builderType - builder_type
 * @property {DateTime} timestamp - {}
 * @property {string} ds_type - ds_type
 * @property {GUID} site_id - site_id
 * @property {string} panel_id - panel_id
 * @property {string} appName - app_name
 * @property {number} ts - ts
 * @property {string} panel_name - panel_name
 * @property {string} viewmode - viewmode
 * @property {string} script_url - {}
 * @property {string} input_name - input_name
 * @property {number} time_since_load - time_since_load
 * @property {string} pageId - page_id
 * @property {string} error - error_desc
 * @property {GUID} vsi - {}
 * @property {string} token - token
 * @property {string} ver - version
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {string} appId - app_id
 * @property {GUID} esi - esi
 * @property {GUID} msid - {}
 * @property {number} tsp - tsp
 * @property {string} ds_id - ds_id
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<prefetchScriptEvalFailedParams>} LogObject<prefetchScriptEvalFailedParams>
 */
export function prefetchScriptEvalFailed(params) {
    return { evid: 149, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'userCodeUsedWSelectorPreviewMode' event (evid: 150)
 *
 * Event description:  Only for preview mode   (Not live sites) once per page view
 *  for any $w interaction with elements on the page
 *
 * @param {userCodeUsedWSelectorPreviewModeParams} params - The parameters for this event
 * @property {string} app_name - app_name
 * @property {string} app_id - app_id
 * @property {number} users_with_write_devmode_count - users_with_write_devmode_count
 * @property {string} worker_id - not sent
 * @property {string} builderType - builder_type
 * @property {GUID} visitorId - not sent
 * @property {string} ds_type - ds_type
 * @property {GUID} site_id - site_id
 * @property {string} panel_id - panel_id
 * @property {string} appName - app_name
 * @property {number} users_on_site_count - users_on_site_count
 * @property {number} pn - page_number
 * @property {number} ts - ts
 * @property {string} element_name - not sent
 * @property {number} tsn - time_from_start_network_ms
 * @property {string} panel_name - panel_name
 * @property {string} viewmode - viewmode
 * @property {string} input_name - not sent
 * @property {string} page_name - not sent
 * @property {string} pageId - page_id
 * @property {string} token - token
 * @property {number} users_with_devmode_count - users_with_devmode_count
 * @property {string} ver - version
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {string} dev_mode - dev_mode
 * @property {string} appId - app_id
 * @property {Url} pageurl - page_url
 * @property {GUID} esi - esi
 * @property {GUID} msid - metasite id
 * @property {number} tsp - not sent
 * @property {Url} page_url - not sent
 * @property {string} ds_id - ds_id
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<userCodeUsedWSelectorPreviewModeParams>} LogObject<userCodeUsedWSelectorPreviewModeParams>
 */
export function userCodeUsedWSelectorPreviewMode(params) {
    return { evid: 150, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'dataSetActiveOnPageLiveSite' event (evid: 152)
 *
 * Event description: Data set active on page - live site  (read action was made)
 *
 * @param {dataSetActiveOnPageLiveSiteParams} params - The parameters for this event
 * @property {GUID} _event_hash - event_hash
 * @property {GUID} visitor_id - visitor_id
 * @property {string} app_name - app_name
 * @property {string} app_id - app_id
 * @property {string} worker_id - worker id
 * @property {string} builderType - builder_type
 * @property {string} ds_type - ds_type
 * @property {GUID} site_id - site_id
 * @property {string} panel_id - panel_id
 * @property {string} appName - app_name
 * @property {number} ts - ts
 * @property {string} collection_id - collection_id
 * @property {string} collection_origin - one of: 'manual', 'external', 'preset', 'template'
 * @property {string} panel_name - panel_name
 * @property {string} viewmode - viewmode
 * @property {string} input_name - {}
 * @property {string} ds_name - data set name
 * @property {string} pageId - page_id
 * @property {GUID} vsi - vsi
 * @property {string} token - token
 * @property {string} ver - version
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {string} mode - {read_write, read, write}
 * @property {string} appId - app_id
 * @property {GUID} esi - esi
 * @property {string} collection_name - this is also collection_id!
 * @property {GUID} msid - msid
 * @property {number} tsp - {}
 * @property {Url} page_url - page url
 * @property {string} ds_id - ds_id
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<dataSetActiveOnPageLiveSiteParams>} LogObject<dataSetActiveOnPageLiveSiteParams>
 */
export function dataSetActiveOnPageLiveSite(params) {
    return { evid: 152, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'dataSetActiveOnPagePreviewMode' event (evid: 153)
 *
 * Event description: Data set active on page - preview mode (read action was made)
 *
 * @param {dataSetActiveOnPagePreviewModeParams} params - The parameters for this event
 * @property {string} app_name - app_name
 * @property {string} app_id - app_id
 * @property {number} users_with_write_devmode_count - users_with_write_devmode_count
 * @property {string} worker_id - worker id
 * @property {string} builderType - builder_type
 * @property {GUID} visitorId - visitor_id
 * @property {string} ds_type - ds_type
 * @property {GUID} site_id - site_id
 * @property {string} panel_id - panel_id
 * @property {string} appName - app_name
 * @property {number} users_on_site_count - users_on_site_count
 * @property {number} ts - ts
 * @property {string} collection_id - collection_id
 * @property {string} collection_origin - one of: 'manual', 'external', 'preset', 'template'
 * @property {string} panel_name - panel_name
 * @property {string} viewmode - viewmode
 * @property {string} input_name - {}
 * @property {string} ds_name - data set name
 * @property {string} page_name - {}
 * @property {string} pageId - page_id
 * @property {string} token - token
 * @property {number} users_with_devmode_count - users_with_devmode_count
 * @property {string} ver - version
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {string} mode - {read-write, read, write}
 * @property {string} dev_mode - dev_mode
 * @property {string} appId - app_id
 * @property {GUID} esi - esi
 * @property {string} collection_name - this is also collection_id!
 * @property {GUID} msid - msid
 * @property {number} tsp - {}
 * @property {Url} page_url - {page_url}
 * @property {string} ds_id - ds_id
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<dataSetActiveOnPagePreviewModeParams>} LogObject<dataSetActiveOnPagePreviewModeParams>
 */
export function dataSetActiveOnPagePreviewMode(params) {
    return { evid: 153, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'activeFormOnPageLiveSite' event (evid: 156)
 *
 * Event description: Active form on page - live site (visitor can submit value to a database)
 *
 * @param {activeFormOnPageLiveSiteParams} params - The parameters for this event
 * @property {GUID} _event_hash - event_hash
 * @property {GUID} visitor_id - visitor_id
 * @property {string} app_name - app_name
 * @property {string} app_id - app_id
 * @property {string} worker_id - worker id
 * @property {string} builderType - builder_type
 * @property {string} ds_type - ds_type
 * @property {GUID} site_id - site_id
 * @property {string} panel_id - panel_id
 * @property {string} appName - app_name
 * @property {number} ts - ts
 * @property {string} collection_id - collection_id
 * @property {string} collection_origin - one of: 'manual', 'external', 'preset', 'template'
 * @property {string} panel_name - panel_name
 * @property {string} viewmode - viewmode
 * @property {string} input_name - {}
 * @property {string} ds_name - data set name
 * @property {string} pageId - page_id
 * @property {GUID} vsi - vsi
 * @property {string} token - token
 * @property {string} ver - version
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {string} mode - {read-write, read, write}
 * @property {string} appId - app_id
 * @property {GUID} esi - esi
 * @property {string} collection_name - this is also collection_id!
 * @property {GUID} msid - msid
 * @property {number} tsp - {}
 * @property {Url} page_url - page url
 * @property {string} ds_id - ds_id
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<activeFormOnPageLiveSiteParams>} LogObject<activeFormOnPageLiveSiteParams>
 */
export function activeFormOnPageLiveSite(params) {
    return { evid: 156, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'activeFormOnPagePreviewMode' event (evid: 157)
 *
 * Event description: Active form on page -preview mode  (visitor can submit value to a database)
 *
 * @param {activeFormOnPagePreviewModeParams} params - The parameters for this event
 * @property {string} app_name - app_name
 * @property {string} app_id - app_id
 * @property {number} users_with_write_devmode_count - users_with_write_devmode_count
 * @property {string} worker_id - worker id
 * @property {string} builderType - builder_type
 * @property {GUID} visitorId - visitor_id
 * @property {string} ds_type - ds_type
 * @property {GUID} site_id - site_id
 * @property {string} panel_id - panel_id
 * @property {string} appName - app_name
 * @property {number} users_on_site_count - users_on_site_count
 * @property {number} ts - ts
 * @property {string} collection_id - collection_id
 * @property {string} collection_origin - one of: 'manual', 'external', 'preset', 'template'
 * @property {string} panel_name - panel_name
 * @property {string} viewmode - viewmode
 * @property {string} input_name - {}
 * @property {string} ds_name - data set name
 * @property {string} page_name - {}
 * @property {string} pageId - page_id
 * @property {string} token - token
 * @property {number} users_with_devmode_count - users_with_devmode_count
 * @property {string} ver - version
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {string} mode - {read-write, read, write}
 * @property {string} dev_mode - dev_mode
 * @property {string} appId - app_id
 * @property {GUID} esi - esi
 * @property {string} collection_name - this is also collection_id!
 * @property {GUID} msid - msid
 * @property {number} tsp - {}
 * @property {Url} page_url - {}
 * @property {string} ds_id - ds_id
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<activeFormOnPagePreviewModeParams>} LogObject<activeFormOnPagePreviewModeParams>
 */
export function activeFormOnPagePreviewMode(params) {
    return { evid: 157, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'importScriptWithCachekillerFailed' event (evid: 158)
 *
 * Event description: Import script without cachekiller failed
 *
 * @param {importScriptWithCachekillerFailedParams} params - The parameters for this event
 * @property {GUID} _event_hash - event_hash
 * @property {GUID} visitor_id - visitor_id
 * @property {string} app_name - app_name
 * @property {string} app_id - app_id
 * @property {string} worker_id - worker id
 * @property {string} builderType - builder_type
 * @property {DateTime} timestamp - {}
 * @property {string} ds_type - ds_type
 * @property {GUID} site_id - site_id
 * @property {string} panel_id - panel_id
 * @property {string} appName - app_name
 * @property {number} ts - ts
 * @property {string} panel_name - panel_name
 * @property {string} viewmode - viewmode
 * @property {string} script_url - {}
 * @property {string} input_name - input_name
 * @property {number} time_since_load - time_since_load
 * @property {string} pageId - page_id
 * @property {string} error - error_desc
 * @property {GUID} vsi - {}
 * @property {string} token - token
 * @property {string} ver - version
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {string} appId - app_id
 * @property {GUID} esi - esi
 * @property {GUID} msid - {}
 * @property {number} tsp - tsp
 * @property {string} ds_id - ds_id
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<importScriptWithCachekillerFailedParams>} LogObject<importScriptWithCachekillerFailedParams>
 */
export function importScriptWithCachekillerFailed(params) {
    return { evid: 158, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'dynamicPageDeletePopupAction' event (evid: 160)
 *
 * Event description: Dynamic page delete popup action
 *
 * @param {dynamicPageDeletePopupActionParams} params - The parameters for this event
 * @property {string} app_name - app_name
 * @property {string} app_id - app_id
 * @property {number} users_with_write_devmode_count - users_with_write_devmode_count
 * @property {string} default_url - {page_url}
 * @property {string} builderType - builder_type
 * @property {GUID} visitorId - visitor_id
 * @property {string} ds_type - ds_type
 * @property {GUID} site_id - site_id
 * @property {string} panel_id - panel_id
 * @property {string} appName - app_name
 * @property {number} users_on_site_count - users_on_site_count
 * @property {number} ts - ts
 * @property {string} collection_id - {connected_collection_id}
 * @property {string} panel_name - panel_name
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} pageId - page_id
 * @property {string} origin - ["Left_tree", "pages_menu"]
 * @property {string} token - token
 * @property {number} users_with_devmode_count - users_with_devmode_count
 * @property {string} ver - version
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {string} category - ["cancel", "close", "delete"]
 * @property {string} dev_mode - dev_mode
 * @property {string} appId - app_id
 * @property {GUID} esi - esi
 * @property {string} collection_name - {connected_collection_name}
 * @property {GUID} msid - {msid}
 * @property {string} ds_id - ds_id
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<dynamicPageDeletePopupActionParams>} LogObject<dynamicPageDeletePopupActionParams>
 */
export function dynamicPageDeletePopupAction(params) {
    return { evid: 160, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'disconnectDynamicPagePopupAction' event (evid: 161)
 *
 * Event description: Disconnect dynamic page popup action
 *
 * @param {disconnectDynamicPagePopupActionParams} params - The parameters for this event
 * @property {string} app_name - app_name
 * @property {string} app_id - app_id
 * @property {number} users_with_write_devmode_count - users_with_write_devmode_count
 * @property {string} default_url - {page_url}
 * @property {string} builderType - builder_type
 * @property {GUID} visitorId - visitor_id
 * @property {string} ds_type - ds_type
 * @property {GUID} site_id - site_id
 * @property {string} panel_id - panel_id
 * @property {string} appName - app_name
 * @property {number} users_on_site_count - users_on_site_count
 * @property {number} ts - ts
 * @property {string} collection_id - {connected_collection_id}
 * @property {string} panel_name - panel_name
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} pageId - page_id
 * @property {string} origin - ["left_tree","pages_menu"]
 * @property {string} token - token
 * @property {number} users_with_devmode_count - users_with_devmode_count
 * @property {string} ver - version
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {string} category - ["cancel", "close", "continue"]
 * @property {string} dev_mode - dev_mode
 * @property {string} appId - app_id
 * @property {GUID} esi - esi
 * @property {string} collection_name - {connected_collection_name}
 * @property {GUID} msid - {msid}
 * @property {string} ds_id - ds_id
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<disconnectDynamicPagePopupActionParams>} LogObject<disconnectDynamicPagePopupActionParams>
 */
export function disconnectDynamicPagePopupAction(params) {
    return { evid: 161, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'dynamicPageHooksMenuAction' event (evid: 162)
 *
 * Event description: Dynamic_page_hooks_menu_clicked
 *
 * @param {dynamicPageHooksMenuActionParams} params - The parameters for this event
 * @property {string} app_name - app_name
 * @property {string} app_id - app_id
 * @property {number} users_with_write_devmode_count - users_with_write_devmode_count
 * @property {string} builderType - builder_type
 * @property {GUID} visitorId - visitor_id
 * @property {string} ds_type - ds_type
 * @property {GUID} site_id - site_id
 * @property {string} panel_id - panel_id
 * @property {string} hook_type - {Hook_type}
 * @property {string} appName - app_name
 * @property {number} users_on_site_count - users_on_site_count
 * @property {number} ts - ts
 * @property {string} panel_name - panel_name
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} pageId - page_id
 * @property {string} token - token
 * @property {number} users_with_devmode_count - users_with_devmode_count
 * @property {string} ver - version
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {string} dev_mode - dev_mode
 * @property {string} appId - app_id
 * @property {GUID} esi - esi
 * @property {GUID} msid - {msid}
 * @property {string} action - ["add&edit","edit_code","close"]
 * @property {string} ds_id - ds_id
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<dynamicPageHooksMenuActionParams>} LogObject<dynamicPageHooksMenuActionParams>
 */
export function dynamicPageHooksMenuAction(params) {
    return { evid: 162, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'widgetReady' event (evid: 164)
 *
 * Event description: Fire when the worker  finalized "load and execute" work
 *
 * @param {widgetReadyParams} params - The parameters for this event
 * @property {GUID} _event_hash - event_hash
 * @property {GUID} visitor_id - visitor_id
 * @property {string} app_name - app_name
 * @property {string} app_id - app_id
 * @property {string} worker_id - worker id
 * @property {string} builderType - builder_type
 * @property {GUID} session_id - session_id
 * @property {string} ds_type - ds_type
 * @property {GUID} site_id - site_id
 * @property {string} panel_id - panel_id
 * @property {string} appName - app_name
 * @property {number} ts - ts
 * @property {string} panel_name - panel_name
 * @property {string} viewmode - viewmode
 * @property {string} input_name - {}
 * @property {number} time_since_load - time_since_load
 * @property {string} pageId - page_id
 * @property {GUID} vsi - vsi
 * @property {string} token - token
 * @property {string} ver - version
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {string} appId - app_id
 * @property {GUID} esi - esi
 * @property {GUID} msid - msid
 * @property {number} tsp - {}
 * @property {string} ds_id - ds_id
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<widgetReadyParams>} LogObject<widgetReadyParams>
 */
export function widgetReady(params) {
    return { evid: 164, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'setFieldOptions' event (evid: 166)
 *
 * Event description: When binding a field from a collection to a component, in some cases, field options may exist (e.g. format of date and time - short date/long date etc.)
 *
 * @param {setFieldOptionsParams} params - The parameters for this event
 * @property {string} app_name - app_name
 * @property {string} app_id - app_id
 * @property {number} users_with_write_devmode_count - users_with_write_devmode_count
 * @property {string} builderType - builder_type
 * @property {GUID} visitorId - visitor_id
 * @property {string} ds_type - ds_type
 * @property {GUID} site_id - site_id
 * @property {string} panel_id - panel_id
 * @property {string} data_set_name - data_set_name
 * @property {string} appName - app_name
 * @property {number} users_on_site_count - users_on_site_count
 * @property {GUID} uuid - uuid
 * @property {string} field_type - field_type
 * @property {number} ts - ts
 * @property {string} collection_id - collection id
 * @property {string} panel_name - panel_name
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} pageId - page_id
 * @property {string} token - token
 * @property {number} users_with_devmode_count - users_with_devmode_count
 * @property {string} ver - version
 * @property {string} userEnv - user_env
 * @property {string} repeater_parent_id - if attached to repeater - this will be the repeater id
 * @property {string} editor_working_mode - editor_working_mode
 * @property {string} property - property
 * @property {string} dev_mode - dev_mode
 * @property {string} appId - app_id
 * @property {GUID} esi - esi
 * @property {string} component_id - component_id
 * @property {string} collection_name - collection name
 * @property {string} component_type - comp type
 * @property {GUID} msid - msid
 * @property {string} field_name - field_name
 * @property {string} field_option_selected - {field option selected}
 * @property {string} ds_id - ds_id
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<setFieldOptionsParams>} LogObject<setFieldOptionsParams>
 */
export function setFieldOptions(params) {
    return { evid: 166, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'requestForNewNpmPackageTrackingClick' event (evid: 167)
 *
 * Event description: Fires when a user clicks on the link to check the status of its package request
 *
 * @param {requestForNewNpmPackageTrackingClickParams} params - The parameters for this event
 * @property {string} app_name - app_name
 * @property {string} app_id - app_id
 * @property {string} stage - pre-request, post-request
 * @property {number} users_with_write_devmode_count - users_with_write_devmode_count
 * @property {string} builderType - builder_type
 * @property {GUID} visitorId - visitor_id
 * @property {string} ds_type - ds_type
 * @property {GUID} site_id - site_id
 * @property {string} panel_id - panel_id
 * @property {string} appName - app_name
 * @property {number} users_on_site_count - users_on_site_count
 * @property {GUID} uuid - {}
 * @property {number} ts - ts
 * @property {string} panel_name - panel_name
 * @property {string} viewmode - viewmode
 * @property {string} input_name - **Deprecated**
 * @property {string} pageId - page_id
 * @property {string} token - token
 * @property {number} users_with_devmode_count - users_with_devmode_count
 * @property {string} ver - version
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {string} dev_mode - dev_mode
 * @property {string} appId - app_id
 * @property {GUID} esi - esi
 * @property {GUID} msid - {}
 * @property {string} ds_id - ds_id
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<requestForNewNpmPackageTrackingClickParams>} LogObject<requestForNewNpmPackageTrackingClickParams>
 */
export function requestForNewNpmPackageTrackingClick(params) {
    return { evid: 167, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'presetsPanelLoaded' event (evid: 169)
 *
 * Event description: Presets panel finish loading
 *
 * @param {presetsPanelLoadedParams} params - The parameters for this event
 * @property {string} app_name - app_name
 * @property {string} app_id - app_id
 * @property {number} users_with_write_devmode_count - users_with_write_devmode_count
 * @property {string} builderType - builder_type
 * @property {GUID} visitorId - visitor_id
 * @property {string} ds_type - ds_type
 * @property {GUID} site_id - site_id
 * @property {string} panel_id - panel_id
 * @property {string} appName - app_name
 * @property {number} users_on_site_count - users_on_site_count
 * @property {GUID} uuid - uuid
 * @property {number} ts - ts
 * @property {string} panel_name - panel_name
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} pageId - page_id
 * @property {string} origin - {add_panel, first_time}
 * @property {string} token - token
 * @property {number} users_with_devmode_count - users_with_devmode_count
 * @property {string} ver - version
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {string} dev_mode - dev_mode
 * @property {string} appId - app_id
 * @property {GUID} esi - esi
 * @property {GUID} msid - msid
 * @property {string} ds_id - ds_id
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<presetsPanelLoadedParams>} LogObject<presetsPanelLoadedParams>
 */
export function presetsPanelLoaded(params) {
    return { evid: 169, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'presetSelectionClick' event (evid: 170)
 *
 * Event description: Event fires when the user clicks 'Open' on a preset option
 *
 * @param {presetSelectionClickParams} params - The parameters for this event
 * @property {string} app_name - app_name
 * @property {string} app_id - app_id
 * @property {number} users_with_write_devmode_count - users_with_write_devmode_count
 * @property {string} builderType - builder_type
 * @property {GUID} visitorId - visitor_id
 * @property {string} ds_type - ds_type
 * @property {GUID} site_id - site_id
 * @property {string} panel_id - panel_id
 * @property {string} appName - app_name
 * @property {number} users_on_site_count - users_on_site_count
 * @property {GUID} uuid - uuid
 * @property {number} ts - ts
 * @property {string} panel_name - panel_name
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} preset_name - preset_name
 * @property {string} pageId - page_id
 * @property {string} token - token
 * @property {number} users_with_devmode_count - users_with_devmode_count
 * @property {string} ver - version
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {string} dev_mode - dev_mode
 * @property {string} appId - app_id
 * @property {GUID} esi - esi
 * @property {GUID} msid - msid
 * @property {string} preset_id - preset_id
 * @property {string} ds_id - ds_id
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<presetSelectionClickParams>} LogObject<presetSelectionClickParams>
 */
export function presetSelectionClick(params) {
    return { evid: 170, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'presetsFinalPanelAction' event (evid: 171)
 *
 * Event description: Indicates which action was taken by the user on the presets final panel
 *
 * @param {presetsFinalPanelActionParams} params - The parameters for this event
 * @property {string} app_name - app_name
 * @property {string} app_id - app_id
 * @property {number} users_with_write_devmode_count - users_with_write_devmode_count
 * @property {string} builderType - builder_type
 * @property {GUID} visitorId - visitor_id
 * @property {string} ds_type - ds_type
 * @property {GUID} site_id - site_id
 * @property {string} panel_id - panel_id
 * @property {string} appName - app_name
 * @property {number} users_on_site_count - users_on_site_count
 * @property {GUID} uuid - uuid
 * @property {number} ts - ts
 * @property {string} panel_name - panel_name
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} preset_name - preset_name
 * @property {string} pageId - page_id
 * @property {string} token - token
 * @property {number} users_with_devmode_count - users_with_devmode_count
 * @property {string} ver - version
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {string} dev_mode - dev_mode
 * @property {string} appId - app_id
 * @property {GUID} esi - esi
 * @property {GUID} msid - msid
 * @property {string} preset_id - preset_id
 * @property {string} action - {view, add to site}
 * @property {string} ds_id - ds_id
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<presetsFinalPanelActionParams>} LogObject<presetsFinalPanelActionParams>
 */
export function presetsFinalPanelAction(params) {
    return { evid: 171, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'presetCollectionCreated' event (evid: 172)
 *
 * Event description: Indicates the preset created the collection
 *
 * @param {presetCollectionCreatedParams} params - The parameters for this event
 * @property {string} app_name - app_name
 * @property {string} app_id - app_id
 * @property {string} renderingFlow - Rendering flow
 * @property {number} users_with_write_devmode_count - users_with_write_devmode_count
 * @property {string} builderType - builder_type
 * @property {GUID} visitorId - visitor_id
 * @property {boolean} is_first_data_modal - {true,false}
 * @property {string} ds_type - ds_type
 * @property {GUID} site_id - site_id
 * @property {string} panel_id - panel_id
 * @property {string} appName - app_name
 * @property {number} users_on_site_count - users_on_site_count
 * @property {GUID} uuid - uuid
 * @property {number} ts - ts
 * @property {string} collection_id - collection_id
 * @property {string} permissions - json
 * @property {boolean} is_draft_publish_enabled - (true, false)
 * @property {string} panel_name - panel_name
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} preset_name - preset_name
 * @property {string} pageId - page_id
 * @property {string} collection_type - collection's permission preset
 * @property {string} origin - {flow that led to preset modal & creation}
 * @property {string} token - token
 * @property {number} users_with_devmode_count - users_with_devmode_count
 * @property {string} ver - version
 * @property {boolean} is_sandbox_enabled - (true/false)
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {string} dev_mode - dev_mode
 * @property {string} default_new_item_status - (draft, published)
 * @property {string} appId - app_id
 * @property {GUID} esi - esi
 * @property {string} collection_name - collection_name
 * @property {GUID} msid - msid
 * @property {string} preset_id - preset_id
 * @property {string} ds_id - ds_id
 * @property {number} num_of_rows - num_of_rows
 * @property {number} num_of_fields - num_of_fields
 * @return {LogObject<presetCollectionCreatedParams>} LogObject<presetCollectionCreatedParams>
 */
export function presetCollectionCreated(params) {
    return { evid: 172, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'presetDatasetCreated' event (evid: 173)
 *
 * Event description: Indicates the preset created the page's dataset
 *
 * @param {presetDatasetCreatedParams} params - The parameters for this event
 * @property {string} app_name - app_name
 * @property {string} app_id - app_id
 * @property {number} users_with_write_devmode_count - users_with_write_devmode_count
 * @property {string} builderType - builder_type
 * @property {GUID} visitorId - visitor_id
 * @property {boolean} is_first_data_modal - is this the first data modal the user see
 * @property {string} ds_type - ds_type
 * @property {GUID} site_id - site_id
 * @property {string} panel_id - panel_id
 * @property {string} appName - app_name
 * @property {number} users_on_site_count - users_on_site_count
 * @property {GUID} uuid - uuid
 * @property {number} ts - ts
 * @property {string} collection_id - collection_id
 * @property {string} panel_name - panel_name
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} ds_name - dataset name
 * @property {string} pageId - page_id
 * @property {string} token - token
 * @property {number} users_with_devmode_count - users_with_devmode_count
 * @property {string} ver - version
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {string} dev_mode - dev_mode
 * @property {string} appId - app_id
 * @property {GUID} esi - esi
 * @property {string} collection_name - collection_name
 * @property {GUID} msid - msid
 * @property {string} preset_id - preset_id
 * @property {string} ds_id - ds_id
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<presetDatasetCreatedParams>} LogObject<presetDatasetCreatedParams>
 */
export function presetDatasetCreated(params) {
    return { evid: 173, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'presetPageCreated' event (evid: 174)
 *
 * Event description: Indicates the preset created a page
 *
 * @param {presetPageCreatedParams} params - The parameters for this event
 * @property {string} app_name - app_name
 * @property {string} app_id - app_id
 * @property {number} users_with_write_devmode_count - users_with_write_devmode_count
 * @property {string} builderType - builder_type
 * @property {GUID} visitorId - visitor_id
 * @property {boolean} is_first_data_modal - is this the first data modal the user see
 * @property {string} ds_type - ds_type
 * @property {GUID} site_id - site_id
 * @property {string} panel_id - panel_id
 * @property {string} appName - app_name
 * @property {number} users_on_site_count - users_on_site_count
 * @property {GUID} uuid - uuid
 * @property {number} ts - ts
 * @property {string} collection_id - collection_id
 * @property {string} panel_name - panel_name
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} page_name - page name
 * @property {string} pageId - page_id
 * @property {string} origin - {flow that led to preset modal & creation}
 * @property {string} token - token
 * @property {number} users_with_devmode_count - users_with_devmode_count
 * @property {string} ver - version
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {string} dev_mode - dev_mode
 * @property {string} appId - app_id
 * @property {GUID} esi - esi
 * @property {string} collection_name - collection_name
 * @property {GUID} msid - msid
 * @property {string} preset_id - preset_id
 * @property {string} ds_id - ds_id
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<presetPageCreatedParams>} LogObject<presetPageCreatedParams>
 */
export function presetPageCreated(params) {
    return { evid: 174, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'presetDynamicPageCreated' event (evid: 175)
 *
 * Event description: Indicates the preset created a dynamic page
 *
 * @param {presetDynamicPageCreatedParams} params - The parameters for this event
 * @property {string} app_name - app_name
 * @property {string} app_id - app_id
 * @property {number} users_with_write_devmode_count - users_with_write_devmode_count
 * @property {string} builderType - builder_type
 * @property {GUID} visitorId - visitor_id
 * @property {boolean} is_first_data_modal - {true,false}
 * @property {string} ds_type - ds_type
 * @property {GUID} site_id - site_id
 * @property {string} panel_id - panel_id
 * @property {string} appName - app_name
 * @property {number} users_on_site_count - users_on_site_count
 * @property {GUID} uuid - uuid
 * @property {number} ts - ts
 * @property {string} collection_id - collection_id
 * @property {string} panel_name - panel_name
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} page_name - page name
 * @property {string} pageId - page_id
 * @property {string} origin - {flow that led to preset modal & creation}
 * @property {string} token - token
 * @property {number} users_with_devmode_count - users_with_devmode_count
 * @property {string} ver - version
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {string} dev_mode - dev_mode
 * @property {string} appId - app_id
 * @property {GUID} esi - esi
 * @property {string} collection_name - collection_name
 * @property {GUID} msid - msid
 * @property {string} preset_id - preset_id
 * @property {string} ds_id - ds_id
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<presetDynamicPageCreatedParams>} LogObject<presetDynamicPageCreatedParams>
 */
export function presetDynamicPageCreated(params) {
    return { evid: 175, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'presetInstallationResult' event (evid: 176)
 *
 * Event description: Indicates the preset's installation is complete
 *
 * @param {presetInstallationResultParams} params - The parameters for this event
 * @property {string} app_name - app_name
 * @property {string} app_id - app_id
 * @property {number} users_with_write_devmode_count - users_with_write_devmode_count
 * @property {string} builderType - builder_type
 * @property {GUID} visitorId - visitor_id
 * @property {boolean} is_first_data_modal - {true,false}
 * @property {string} ds_type - ds_type
 * @property {GUID} site_id - site_id
 * @property {string} panel_id - panel_id
 * @property {string} appName - app_name
 * @property {number} users_on_site_count - users_on_site_count
 * @property {GUID} uuid - uuid
 * @property {string} result - **depreceated**
 * @property {number} ts - ts
 * @property {string} collection_id - collection_id
 * @property {string} panel_name - panel_name
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} preset_name - preset_name
 * @property {string} pageId - page_id
 * @property {string} failReason - **depreceated**
 * @property {string} origin - {flow that led to preset modal & creation}
 * @property {string} token - token
 * @property {number} users_with_devmode_count - users_with_devmode_count
 * @property {string} ver - version
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {string} dev_mode - dev_mode
 * @property {string} appId - app_id
 * @property {GUID} esi - esi
 * @property {string} collection_name - collection_name
 * @property {GUID} msid - msid
 * @property {string} preset_id - preset_id
 * @property {string} ds_id - ds_id
 * @property {number} time_to_complete - time to complete preset installation
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<presetInstallationResultParams>} LogObject<presetInstallationResultParams>
 */
export function presetInstallationResult(params) {
    return { evid: 176, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'ideAutocompleteUse' event (evid: 177)
 *
 * Event description: Fires when a user uses the autocomplete feature of the IDE
 *
 * @param {ideAutocompleteUseParams} params - The parameters for this event
 * @property {string} app_name - app_name
 * @property {string} app_id - app_id
 * @property {number} users_with_write_devmode_count - users_with_write_devmode_count
 * @property {string} builderType - builder_type
 * @property {GUID} visitorId - deprecated
 * @property {string} ds_type - ds_type
 * @property {GUID} site_id - site_id
 * @property {string} panel_id - panel_id
 * @property {string} appName - app_name
 * @property {number} users_on_site_count - users_on_site_count
 * @property {GUID} uuid - uuid
 * @property {number} ts - ts
 * @property {string} panel_name - panel_name
 * @property {string} viewmode - viewmode
 * @property {string} input_name - deprecated
 * @property {string} pageId - page_id
 * @property {string} user_input - the characters/word the user writes
 * @property {number} character_count - number of characters the user write
 * @property {string} origin - autocomplete panel opened auto/manual
 * @property {string} chosenOption - Content of the selected item
 * @property {string} token - token
 * @property {number} users_with_devmode_count - users_with_devmode_count
 * @property {string} ver - version
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {string} file_path - code file name
 * @property {string} category - item category {'Templates', 'Javascript', 'Keywords' etc..}
 * @property {string} panel_type - completions / hover_content / media_popup
 * @property {string} dev_mode - dev_mode
 * @property {string} appId - app_id
 * @property {GUID} esi - esi
 * @property {GUID} msid - msid
 * @property {string} ds_id - ds_id
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<ideAutocompleteUseParams>} LogObject<ideAutocompleteUseParams>
 */
export function ideAutocompleteUse(params) {
    return { evid: 177, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'ideAutocompleteReadMoreClick' event (evid: 178)
 *
 * Event description: Fires when a user clicks on the 'read more' link in the autocomplete feature of the IDE
 *
 * @param {ideAutocompleteReadMoreClickParams} params - The parameters for this event
 * @property {string} app_name - app_name
 * @property {string} app_id - app_id
 * @property {number} users_with_write_devmode_count - users_with_write_devmode_count
 * @property {string} builderType - builder_type
 * @property {GUID} visitorId - deprecated
 * @property {string} ds_type - ds_type
 * @property {GUID} site_id - site_id
 * @property {string} panel_id - panel_id
 * @property {string} appName - app_name
 * @property {number} users_on_site_count - users_on_site_count
 * @property {GUID} uuid - uuid
 * @property {number} ts - ts
 * @property {string} panel_name - panel_name
 * @property {string} viewmode - viewmode
 * @property {string} input_name - deprecated
 * @property {string} pageId - page_id
 * @property {string} user_input - the characters/word the user writes
 * @property {number} character_count - number of characters the user write
 * @property {string} origin - autocomplete panel opened auto/manual
 * @property {string} chosenOption - Content of the selected 'read more'
 * @property {string} token - token
 * @property {number} users_with_devmode_count - users_with_devmode_count
 * @property {string} ver - version
 * @property {string} userEnv - user_env
 * @property {string} link_url - read more url
 * @property {string} editor_working_mode - editor_working_mode
 * @property {string} category - item category {'Templates', 'Javascript', 'Keywords' etc..}
 * @property {string} panel_type - completions / hover_content / media_popup
 * @property {string} dev_mode - dev_mode
 * @property {string} appId - app_id
 * @property {GUID} esi - esi
 * @property {GUID} msid - msid
 * @property {string} ds_id - ds_id
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<ideAutocompleteReadMoreClickParams>} LogObject<ideAutocompleteReadMoreClickParams>
 */
export function ideAutocompleteReadMoreClick(params) {
    return { evid: 178, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'ideSearchUiButtonClick' event (evid: 179)
 *
 * Event description: Fires when a user clicks on the search all files (left tree) or uses keyboard shortcut
 *
 * @param {ideSearchUiButtonClickParams} params - The parameters for this event
 * @property {string} app_name - app_name
 * @property {string} app_id - app_id
 * @property {number} users_with_write_devmode_count - users_with_write_devmode_count
 * @property {string} builderType - builder_type
 * @property {GUID} visitorId - deprecated
 * @property {string} ds_type - ds_type
 * @property {GUID} site_id - site_id
 * @property {string} panel_id - panel_id
 * @property {string} appName - app_name
 * @property {number} users_on_site_count - users_on_site_count
 * @property {GUID} uuid - uuid
 * @property {number} ts - ts
 * @property {string} button_name - deprecated
 * @property {string} panel_name - panel_name
 * @property {string} viewmode - viewmode
 * @property {string} input_name - deprecated
 * @property {string} pageId - page_id
 * @property {string} origin - example values: left tree icon, keyboard
 * @property {string} token - token
 * @property {number} users_with_devmode_count - users_with_devmode_count
 * @property {string} ver - version
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {string} dev_mode - dev_mode
 * @property {string} appId - app_id
 * @property {GUID} esi - esi
 * @property {GUID} msid - msid
 * @property {string} ds_id - ds_id
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<ideSearchUiButtonClickParams>} LogObject<ideSearchUiButtonClickParams>
 */
export function ideSearchUiButtonClick(params) {
    return { evid: 179, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'httpFunctionCalledInLiveSite' event (evid: 181)
 *
 * Event description:  Only for  published sites (Not preview mode)
 *  for any HTTP function
 *
 * @param {httpFunctionCalledInLiveSiteParams} params - The parameters for this event
 * @property {GUID} _event_hash - event_hash
 * @property {GUID} visitor_id - visitor_id
 * @property {string} app_name - app_name
 * @property {string} app_id - app_id
 * @property {string} exceptionType - {}
 * @property {string} builderType - builder_type
 * @property {string} ds_type - ds_type
 * @property {GUID} site_id - site_id
 * @property {string} panel_id - panel_id
 * @property {string} file_name - {}
 * @property {string} _referer - http_referrer
 * @property {string} appName - app_name
 * @property {number} ts - ts
 * @property {boolean} exception - {true, false}
 * @property {string} _user_agent - user_agent
 * @property {string} panel_name - panel_name
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} pageId - page_id
 * @property {GUID} vsi - vsi
 * @property {string} token - token
 * @property {string} ver - version
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {string} appId - app_id
 * @property {GUID} esi - esi
 * @property {GUID} msid - msid
 * @property {number} tsp - tsp
 * @property {string} ds_id - ds_id
 * @property {string} requestOrigin - request_origin
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<httpFunctionCalledInLiveSiteParams>} LogObject<httpFunctionCalledInLiveSiteParams>
 */
export function httpFunctionCalledInLiveSite(params) {
    return { evid: 181, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'dataHooksCalledInLiveSite' event (evid: 182)
 *
 * Event description:  Only for  published sites (Not preview mode)
 *
 * @param {dataHooksCalledInLiveSiteParams} params - The parameters for this event
 * @property {string} exceptionType - {}
 * @property {string} db_type - db type
 * @property {string} file_name - {}
 * @property {boolean} exception - {true/false}
 * @property {string} _user_agent - user_agent
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<dataHooksCalledInLiveSiteParams>} LogObject<dataHooksCalledInLiveSiteParams>
 */
export function dataHooksCalledInLiveSite(params) {
    return { evid: 182, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'backEndEvenExecutedInLiveSite' event (evid: 183)
 *
 * Event description:  Only for  published sites (Not preview mode) backend  event was triggered on a site by a vertical app
 *
 * @param {backEndEvenExecutedInLiveSiteParams} params - The parameters for this event
 * @property {GUID} _event_hash - event_hash
 * @property {GUID} visitor_id - visitor_id
 * @property {string} app_name - app_name
 * @property {string} app_id - app_id
 * @property {string} exceptionType - {}
 * @property {string} builderType - builder_type
 * @property {string} ds_type - ds_type
 * @property {GUID} site_id - site_id
 * @property {string} panel_id - panel_id
 * @property {string} file_name - {}
 * @property {string} appName - app_name
 * @property {number} ts - ts
 * @property {boolean} exception - {true/false}
 * @property {string} _user_agent - user_agent
 * @property {string} panel_name - panel_name
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} pageId - page_id
 * @property {GUID} vsi - vsi
 * @property {string} token - token
 * @property {string} ver - version
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {string} appId - app_id
 * @property {GUID} esi - esi
 * @property {GUID} msid - msid
 * @property {number} tsp - tsp
 * @property {string} ds_id - ds_id
 * @property {string} requestOrigin - code_package/ site_code
 *
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<backEndEvenExecutedInLiveSiteParams>} LogObject<backEndEvenExecutedInLiveSiteParams>
 */
export function backEndEvenExecutedInLiveSite(params) {
    return { evid: 183, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'scheduledJobRunInLiveSite' event (evid: 184)
 *
 * Event description:  Only for  published sites (Not preview mode)
 *
 * @param {scheduledJobRunInLiveSiteParams} params - The parameters for this event
 * @property {GUID} _event_hash - event_hash
 * @property {GUID} visitor_id - visitor_id
 * @property {string} app_name - app_name
 * @property {string} app_id - app_id
 * @property {string} exceptionType - {}
 * @property {string} builderType - builder_type
 * @property {string} ds_type - ds_type
 * @property {GUID} site_id - site_id
 * @property {string} panel_id - panel_id
 * @property {string} file_name - {}
 * @property {string} appName - app_name
 * @property {number} ts - ts
 * @property {boolean} exception - {true/false}
 * @property {string} _user_agent - user_agent
 * @property {string} panel_name - panel_name
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} pageId - page_id
 * @property {GUID} vsi - vsi
 * @property {string} token - token
 * @property {string} ver - version
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {string} appId - app_id
 * @property {GUID} esi - esi
 * @property {GUID} msid - msid
 * @property {number} tsp - tsp
 * @property {string} ds_id - ds_id
 * @property {string} requestOrigin - code_package/ site_code
 *
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<scheduledJobRunInLiveSiteParams>} LogObject<scheduledJobRunInLiveSiteParams>
 */
export function scheduledJobRunInLiveSite(params) {
    return { evid: 184, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'customRoutersWasTriggeredInLiveSite' event (evid: 185)
 *
 * Event description:  Only for  published sites (Not preview mode)
 * Includes page_id
 *
 * @param {customRoutersWasTriggeredInLiveSiteParams} params - The parameters for this event
 * @property {GUID} _event_hash - event_hash
 * @property {GUID} visitor_id - visitor_id
 * @property {string} app_name - app_name
 * @property {string} app_id - app_id
 * @property {string} exceptionType - {}
 * @property {string} builderType - builder_type
 * @property {string} ds_type - ds_type
 * @property {GUID} site_id - site_id
 * @property {string} panel_id - panel_id
 * @property {string} file_name - {}
 * @property {string} appName - app_name
 * @property {number} ts - ts
 * @property {boolean} exception - {true/false}
 * @property {string} _user_agent - user_agent
 * @property {string} panel_name - panel_name
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} pageId - page_id
 * @property {GUID} vsi - {}
 * @property {string} token - token
 * @property {string} ver - version
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {string} appId - app_id
 * @property {GUID} esi - esi
 * @property {GUID} msid - msid
 * @property {number} tsp - tsp
 * @property {Url} page_url - {}
 * @property {string} ds_id - ds_id
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<customRoutersWasTriggeredInLiveSiteParams>} LogObject<customRoutersWasTriggeredInLiveSiteParams>
 */
export function customRoutersWasTriggeredInLiveSite(params) {
    return { evid: 185, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'userCodeUsedLibraryApiEdmNpmInLiveSite' event (evid: 186)
 *
 * Event description: sent when an NPM library was imported into code on live site  (sent from server bundler).
 * Sent when the site is being published after code change.
 * sent for FE files only
 *
 *
 * @param {userCodeUsedLibraryApiEdmNpmInLiveSiteParams} params - The parameters for this event
 * @property {string} renderingFlow - Rendering flow
 * @property {string} exceptionType - {}
 * @property {string} file_name - file name
 * @property {boolean} exception - {true/false}
 * @property {string} _user_agent - user_agent
 * @property {string} npm_module_name - npm module name
 * @property {string} grid_app_id - grid_app_id
 * @property {Url} page_url - page url
 * @property {number} packages_count - packages_count
 * @return {LogObject<userCodeUsedLibraryApiEdmNpmInLiveSiteParams>} LogObject<userCodeUsedLibraryApiEdmNpmInLiveSiteParams>
 */
export function userCodeUsedLibraryApiEdmNpmInLiveSite(params) {
    return { evid: 186, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'customRoutersWasTriggeredInPreviewMode' event (evid: 188)
 *
 * Event description:  Only for preview mode   (Not published sites)
 *  once per pageview
 *
 * @param {customRoutersWasTriggeredInPreviewModeParams} params - The parameters for this event
 * @property {string} app_name - app_name
 * @property {string} app_id - app_id
 * @property {string} exceptionType - {}
 * @property {number} users_with_write_devmode_count - users_with_write_devmode_count
 * @property {string} builderType - builder_type
 * @property {GUID} visitorId - visitor_id
 * @property {string} ds_type - ds_type
 * @property {GUID} site_id - site_id
 * @property {string} panel_id - panel_id
 * @property {string} file_name - {}
 * @property {string} appName - app_name
 * @property {number} users_on_site_count - users_on_site_count
 * @property {GUID} uuid - {}
 * @property {number} ts - ts
 * @property {boolean} exception - {true,false}
 * @property {string} panel_name - panel_name
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} pageId - page_id
 * @property {string} token - token
 * @property {number} users_with_devmode_count - users_with_devmode_count
 * @property {string} ver - version
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {string} dev_mode - dev_mode
 * @property {string} appId - app_id
 * @property {GUID} esi - esi
 * @property {GUID} msid - msid
 * @property {Url} page_url - page url
 * @property {string} ds_id - ds_id
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<customRoutersWasTriggeredInPreviewModeParams>} LogObject<customRoutersWasTriggeredInPreviewModeParams>
 */
export function customRoutersWasTriggeredInPreviewMode(params) {
    return { evid: 188, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'dataHooksCalledInPreviewMode' event (evid: 189)
 *
 * Event description:  Only for preview mode   (Not published sites)
 *  once per pageview
 * Includes page_id, esi (editor session id)
 *
 * @param {dataHooksCalledInPreviewModeParams} params - The parameters for this event
 * @property {string} app_name - app_name
 * @property {string} app_id - app_id
 * @property {string} exceptionType - {}
 * @property {number} users_with_write_devmode_count - users_with_write_devmode_count
 * @property {string} builderType - builder_type
 * @property {GUID} visitorId - visitor_id
 * @property {string} ds_type - ds_type
 * @property {GUID} site_id - site_id
 * @property {string} db_type - {dev,live}
 * @property {string} panel_id - panel_id
 * @property {string} file_name - {}
 * @property {string} appName - app_name
 * @property {number} users_on_site_count - users_on_site_count
 * @property {GUID} uuid - {}
 * @property {number} ts - ts
 * @property {boolean} exception - {true,false}
 * @property {string} panel_name - panel_name
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} pageId - page_id
 * @property {string} token - token
 * @property {number} users_with_devmode_count - users_with_devmode_count
 * @property {string} ver - version
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {string} dev_mode - dev_mode
 * @property {string} appId - app_id
 * @property {GUID} esi - esi
 * @property {GUID} msid - msid
 * @property {Url} page_url - page url
 * @property {string} ds_id - ds_id
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<dataHooksCalledInPreviewModeParams>} LogObject<dataHooksCalledInPreviewModeParams>
 */
export function dataHooksCalledInPreviewMode(params) {
    return { evid: 189, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'ideFileFolderRenamed' event (evid: 190)
 *
 * Event description: IDE - file/folder renamed
 *
 * @param {ideFileFolderRenamedParams} params - The parameters for this event
 * @property {string} app_name - app_name
 * @property {string} app_id - app_id
 * @property {number} users_with_write_devmode_count - users_with_write_devmode_count
 * @property {string} builderType - builder_type
 * @property {string} path - {the new path after rename}
 * @property {GUID} visitorId - deprecated
 * @property {string} section_name - section_name
 * @property {string} ds_type - ds_type
 * @property {GUID} site_id - site_id
 * @property {string} panel_id - panel_id
 * @property {string} appName - app_name
 * @property {number} users_on_site_count - users_on_site_count
 * @property {number} ts - ts
 * @property {string} panel_name - panel_name
 * @property {string} viewmode - viewmode
 * @property {string} input_name - deprecated
 * @property {string} pageId - page_id
 * @property {string} token - token
 * @property {number} users_with_devmode_count - users_with_devmode_count
 * @property {string} ver - version
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {string} dev_mode - dev_mode
 * @property {string} type - type(folder or any file extension)
 * @property {string} appId - app_id
 * @property {GUID} esi - esi
 * @property {GUID} msid - msid
 * @property {string} ds_id - ds_id
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<ideFileFolderRenamedParams>} LogObject<ideFileFolderRenamedParams>
 */
export function ideFileFolderRenamed(params) {
    return { evid: 190, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'ideCodeHasChanged' event (evid: 191)
 *
 * Event description: IDE - Code has changed by the user
 * please report esi
 *
 * @param {ideCodeHasChangedParams} params - The parameters for this event
 * @property {string} app_name - app_name
 * @property {string} app_id - app_id
 * @property {number} users_with_write_devmode_count - users_with_write_devmode_count
 * @property {string} builderType - builder_type
 * @property {GUID} visitorId - deprecated
 * @property {string} ds_type - ds_type
 * @property {GUID} site_id - site_id
 * @property {string} panel_id - panel_id
 * @property {string} file_name - {file_name or page_id}
 * @property {string} appName - app_name
 * @property {number} users_on_site_count - users_on_site_count
 * @property {number} ts - ts
 * @property {string} panel_name - panel_name
 * @property {string} viewmode - viewmode
 * @property {string} input_name - deprecated
 * @property {string} grid_app_id - grid_app_id
 * @property {string} pageId - page_id
 * @property {string} token - token
 * @property {number} users_with_devmode_count - users_with_devmode_count
 * @property {string} ver - version
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {string} dev_mode - dev_mode
 * @property {string} appId - app_id
 * @property {GUID} esi - esi
 * @property {GUID} msid - msid
 * @property {string} ds_id - ds_id
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<ideCodeHasChangedParams>} LogObject<ideCodeHasChangedParams>
 */
export function ideCodeHasChanged(params) {
    return { evid: 191, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'ideSearchAllFilesSearch' event (evid: 192)
 *
 * Event description: user searches in all files (by Enter)
 *
 * @param {ideSearchAllFilesSearchParams} params - The parameters for this event
 * @property {string} app_name - app_name
 * @property {string} app_id - app_id
 * @property {boolean} is_reg_exp - true if reg exp is selected
 * @property {boolean} is_case - true if cases match is selected
 * @property {number} users_with_write_devmode_count - users_with_write_devmode_count
 * @property {string} builderType - builder_type
 * @property {GUID} visitorId - deprecated
 * @property {string} ds_type - ds_type
 * @property {GUID} site_id - site_id
 * @property {string} panel_id - panel_id
 * @property {string} appName - app_name
 * @property {number} users_on_site_count - users_on_site_count
 * @property {number} ts - ts
 * @property {string} panel_name - panel_name
 * @property {string} viewmode - viewmode
 * @property {string} input_name - deprecated
 * @property {number} num_of_results - number of returned relusts
 * @property {string} pageId - page_id
 * @property {string} origin - origin='left tree'/'IDE'
 * @property {string} token - token
 * @property {number} users_with_devmode_count - users_with_devmode_count
 * @property {string} ver - version
 * @property {boolean} is_whole_word - true if whole word is selected
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {string} dev_mode - dev_mode
 * @property {string} appId - app_id
 * @property {GUID} esi - esi
 * @property {GUID} msid - msid
 * @property {string} ds_id - ds_id
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<ideSearchAllFilesSearchParams>} LogObject<ideSearchAllFilesSearchParams>
 */
export function ideSearchAllFilesSearch(params) {
    return { evid: 192, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'ideSearchAllFilesSelectResult' event (evid: 193)
 *
 * Event description: user clicks on a returned result
 *
 * @param {ideSearchAllFilesSelectResultParams} params - The parameters for this event
 * @property {string} app_name - app_name
 * @property {string} app_id - app_id
 * @property {number} users_with_write_devmode_count - users_with_write_devmode_count
 * @property {string} builderType - builder_type
 * @property {GUID} visitorId - deprecated
 * @property {string} ds_type - ds_type
 * @property {GUID} site_id - site_id
 * @property {string} panel_id - panel_id
 * @property {string} file_name - file of selected result
 * @property {string} appName - app_name
 * @property {number} users_on_site_count - users_on_site_count
 * @property {number} ts - ts
 * @property {string} panel_name - panel_name
 * @property {string} viewmode - viewmode
 * @property {string} input_name - deprecated
 * @property {string} pageId - page_id
 * @property {string} token - token
 * @property {number} users_with_devmode_count - users_with_devmode_count
 * @property {string} ver - version
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {string} dev_mode - dev_mode
 * @property {string} appId - app_id
 * @property {GUID} esi - esi
 * @property {GUID} msid - msid
 * @property {string} ds_id - ds_id
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<ideSearchAllFilesSelectResultParams>} LogObject<ideSearchAllFilesSelectResultParams>
 */
export function ideSearchAllFilesSelectResult(params) {
    return { evid: 193, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'activeSecretKeyInLiveSite' event (evid: 194)
 *
 * Event description:  Only for published sites (Not preview mode)
 *
 * @param {activeSecretKeyInLiveSiteParams} params - The parameters for this event
 * @property {GUID} _event_hash - event_hash
 * @property {GUID} visitor_id - visitor_id
 * @property {string} app_name - app_name
 * @property {string} app_id - app_id
 * @property {string} builderType - builder_type
 * @property {string} ds_type - ds_type
 * @property {GUID} site_id - site_id
 * @property {string} panel_id - panel_id
 * @property {string} file_name - file_name
 * @property {string} appName - app_name
 * @property {number} ts - ts
 * @property {string} panel_name - panel_name
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} key_id - key_id
 * @property {string} pageId - page_id
 * @property {GUID} vsi - vsi
 * @property {string} token - token
 * @property {string} ver - version
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {string} appId - app_id
 * @property {GUID} esi - esi
 * @property {GUID} msid - msid
 * @property {number} tsp - tsp
 * @property {string} ds_id - ds_id
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<activeSecretKeyInLiveSiteParams>} LogObject<activeSecretKeyInLiveSiteParams>
 */
export function activeSecretKeyInLiveSite(params) {
    return { evid: 194, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'activeSecretKeyPreviewMode' event (evid: 195)
 *
 * Event description:  Only for preview mode (Not published sites)
 *  once per pageview
 *
 * @param {activeSecretKeyPreviewModeParams} params - The parameters for this event
 * @property {string} app_name - app_name
 * @property {string} app_id - app_id
 * @property {number} users_with_write_devmode_count - users_with_write_devmode_count
 * @property {string} builderType - builder_type
 * @property {GUID} visitorId - deprecated
 * @property {string} ds_type - ds_type
 * @property {GUID} site_id - site_id
 * @property {string} panel_id - panel_id
 * @property {string} file_name - file name
 * @property {string} appName - app_name
 * @property {number} users_on_site_count - users_on_site_count
 * @property {GUID} uuid - uuid
 * @property {number} ts - ts
 * @property {string} panel_name - panel_name
 * @property {string} viewmode - viewmode
 * @property {string} input_name - deprecated
 * @property {string} key_id - key_id
 * @property {string} pageId - page_id
 * @property {string} token - token
 * @property {number} users_with_devmode_count - users_with_devmode_count
 * @property {string} ver - version
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {string} dev_mode - dev_mode
 * @property {string} appId - app_id
 * @property {GUID} esi - esi
 * @property {GUID} msid - msid
 * @property {string} ds_id - ds_id
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<activeSecretKeyPreviewModeParams>} LogObject<activeSecretKeyPreviewModeParams>
 */
export function activeSecretKeyPreviewMode(params) {
    return { evid: 195, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'webMethodBeCodeRunOnLiveSite' event (evid: 196)
 *
 * Event description: web method BE code run on live site
 * (sent from live site)
 *
 * @param {webMethodBeCodeRunOnLiveSiteParams} params - The parameters for this event
 * @property {GUID} _event_hash - event_hash
 * @property {GUID} visitor_id - visitor_id
 * @property {string} app_name - app_name
 * @property {string} app_id - app_id
 * @property {string} renderingFlow - Rendering flow
 * @property {GUID} gridAppId - gridappid
 * @property {string} builderType - builder_type
 * @property {string} ds_type - ds_type
 * @property {GUID} site_id - site_id
 * @property {string} panel_id - panel_id
 * @property {string} file_name - file name
 * @property {string} appName - app_name
 * @property {number} ts - ts
 * @property {string} panel_name - panel_name
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} pageId - page_id
 * @property {GUID} vsi - vsi
 * @property {string} token - token
 * @property {string} ver - version
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {string} exceptiontype - exception type
 * @property {string} appId - app_id
 * @property {GUID} esi - esi
 * @property {GUID} msid - msid
 * @property {number} tsp - tsp
 * @property {string} ds_id - ds_id
 * @property {string} requestOrigin - requestOrigin
 * @return {LogObject<webMethodBeCodeRunOnLiveSiteParams>} LogObject<webMethodBeCodeRunOnLiveSiteParams>
 */
export function webMethodBeCodeRunOnLiveSite(params) {
    return { evid: 196, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'userWebMethodBeCodeRunOnPreview' event (evid: 197)
 *
 * Event description: web method BE code run onsite preview
 * (sent from preview)
 *
 * @param {userWebMethodBeCodeRunOnPreviewParams} params - The parameters for this event
 * @property {string} app_name - app_name
 * @property {string} app_id - app_id
 * @property {number} users_with_write_devmode_count - users_with_write_devmode_count
 * @property {string} builderType - builder_type
 * @property {GUID} visitorId - visitor_id
 * @property {string} ds_type - ds_type
 * @property {GUID} site_id - site_id
 * @property {string} panel_id - panel_id
 * @property {string} file_name - file name
 * @property {string} appName - app_name
 * @property {number} users_on_site_count - users_on_site_count
 * @property {number} ts - ts
 * @property {boolean} exception - {}
 * @property {string} panel_name - panel_name
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} pageId - page_id
 * @property {GUID} vsi - vsi
 * @property {string} token - token
 * @property {number} users_with_devmode_count - users_with_devmode_count
 * @property {string} ver - version
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {string} dev_mode - dev_mode
 * @property {string} appId - app_id
 * @property {GUID} esi - esi
 * @property {GUID} msid - msid
 * @property {string} ds_id - ds_id
 * @property {string} requestOrigin - requestOrigin
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<userWebMethodBeCodeRunOnPreviewParams>} LogObject<userWebMethodBeCodeRunOnPreviewParams>
 */
export function userWebMethodBeCodeRunOnPreview(params) {
    return { evid: 197, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'httpFunctionCalledInPreview' event (evid: 198)
 *
 * Event description: sent when a HTTP function is used in preview
 *
 * @param {httpFunctionCalledInPreviewParams} params - The parameters for this event
 * @property {string} app_name - app_name
 * @property {string} app_id - app_id
 * @property {string} exceptionType - {}
 * @property {number} users_with_write_devmode_count - users_with_write_devmode_count
 * @property {string} builderType - builder_type
 * @property {string} ds_type - ds_type
 * @property {GUID} site_id - site_id
 * @property {string} panel_id - panel_id
 * @property {string} file_name - file name
 * @property {string} appName - app_name
 * @property {number} users_on_site_count - users_on_site_count
 * @property {number} ts - ts
 * @property {boolean} exception - {true, false}
 * @property {string} panel_name - panel_name
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} pageId - page_id
 * @property {string} token - token
 * @property {number} users_with_devmode_count - users_with_devmode_count
 * @property {string} ver - version
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {string} dev_mode - dev_mode
 * @property {string} appId - app_id
 * @property {GUID} esi - esi
 * @property {GUID} msid - msid
 * @property {string} ds_id - ds_id
 * @property {string} requestOrigin - requestOrigin
 * @property {string} visitor_id - visitor ID
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<httpFunctionCalledInPreviewParams>} LogObject<httpFunctionCalledInPreviewParams>
 */
export function httpFunctionCalledInPreview(params) {
    return { evid: 198, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'namespacesUsedInUserCodeOnLiveSite' event (evid: 199)
 *
 * Event description: sent when an namespaces (Wix-provided-npm) library was imported into code on live site  (sent from server bundler)
 * Sent when the site is being published after code change
 *
 *
 * @param {namespacesUsedInUserCodeOnLiveSiteParams} params - The parameters for this event
 * @property {string} exceptionType - {}
 * @property {string} file_name - file name
 * @property {boolean} exception - {true/false}
 * @property {string} page_id - page id
 * @property {string} grid_app_id - grid app id
 * @property {GUID} msid - msid
 * @property {string} namespace_module_name - namespace module name
 * @property {Url} page_url - page url
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<namespacesUsedInUserCodeOnLiveSiteParams>} LogObject<namespacesUsedInUserCodeOnLiveSiteParams>
 */
export function namespacesUsedInUserCodeOnLiveSite(params) {
    return { evid: 199, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'ideTopbarDragResize' event (evid: 20)
 *
 * Event description: Fired when a user manually change the size of the IDE windows
 *
 * @param {ideTopbarDragResizeParams} params - The parameters for this event
 * @property {string} app_name - app_name
 * @property {string} app_id - app_id
 * @property {number} users_with_write_devmode_count - users_with_write_devmode_count
 * @property {string} builderType - builder_type
 * @property {GUID} visitorId - visitor_id
 * @property {string} ds_type - ds_type
 * @property {GUID} site_id - site_id
 * @property {string} panel_id - panel_id
 * @property {string} appName - app_name
 * @property {number} users_on_site_count - users_on_site_count
 * @property {number} ts - ts
 * @property {string} panel_name - panel_name
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} pageId - page_id
 * @property {string} token - token
 * @property {number} users_with_devmode_count - users_with_devmode_count
 * @property {string} ver - version
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {string} old_hight - old ide hight
 * @property {string} dev_mode - dev_mode
 * @property {string} appId - app_id
 * @property {GUID} esi - esi
 * @property {GUID} msid - msid
 * @property {string} ds_id - ds_id
 * @property {string} new_hight - new ide hight
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<ideTopbarDragResizeParams>} LogObject<ideTopbarDragResizeParams>
 */
export function ideTopbarDragResize(params) {
    return { evid: 20, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'siteMapRequestWasTriggeredInLiveSite' event (evid: 200)
 *
 * Event description: site map request was triggered in live site
 *
 * @param {siteMapRequestWasTriggeredInLiveSiteParams} params - The parameters for this event
 * @property {GUID} _event_hash - event_hash
 * @property {GUID} visitor_id - visitor_id
 * @property {string} app_name - app_name
 * @property {string} app_id - app_id
 * @property {string} exceptionType - {}
 * @property {string} builderType - builder_type
 * @property {string} ds_type - ds_type
 * @property {GUID} site_id - site_id
 * @property {string} panel_id - panel_id
 * @property {string} file_name - {}
 * @property {string} appName - app_name
 * @property {number} ts - ts
 * @property {boolean} exception - {true/false}
 * @property {string} panel_name - panel_name
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} pageId - page_id
 * @property {GUID} vsi - {}
 * @property {string} token - token
 * @property {string} ver - version
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {string} appId - app_id
 * @property {GUID} esi - esi
 * @property {GUID} msid - msid
 * @property {number} tsp - tsp
 * @property {Url} page_url - {}
 * @property {string} ds_id - ds_id
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<siteMapRequestWasTriggeredInLiveSiteParams>} LogObject<siteMapRequestWasTriggeredInLiveSiteParams>
 */
export function siteMapRequestWasTriggeredInLiveSite(params) {
    return { evid: 200, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'koreBrokerAppDeployment' event (evid: 2000)
 *
 * Event description: Kore Broker app deployment.
 *
 * @param {koreBrokerAppDeploymentParams} params - The parameters for this event
 * @property {number} duration - {}
 * @property {string} pod_name - {}
 * @property {number} failed_locks - {}
 * @property {string} grid_app_id - {}
 * @property {number} successful_locks - {}
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<koreBrokerAppDeploymentParams>} LogObject<koreBrokerAppDeploymentParams>
 */
export function koreBrokerAppDeployment(params) {
    return { evid: 2000, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'siteMapRequestWasTriggeredInPreviewMode' event (evid: 201)
 *
 * Event description: site map request was triggered in preview mode
 *  Only for preview mode   (Not published sites)
 *  once per pageview
 *
 * @param {siteMapRequestWasTriggeredInPreviewModeParams} params - The parameters for this event
 * @property {string} app_name - app_name
 * @property {string} app_id - app_id
 * @property {string} exceptionType - {}
 * @property {number} users_with_write_devmode_count - users_with_write_devmode_count
 * @property {string} builderType - builder_type
 * @property {GUID} visitorId - visitor_id
 * @property {string} ds_type - ds_type
 * @property {GUID} site_id - site_id
 * @property {string} panel_id - panel_id
 * @property {string} file_name - {}
 * @property {string} appName - app_name
 * @property {number} users_on_site_count - users_on_site_count
 * @property {GUID} uuid - {}
 * @property {number} ts - ts
 * @property {boolean} exception - {true,false}
 * @property {string} panel_name - panel_name
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} pageId - page_id
 * @property {string} token - token
 * @property {number} users_with_devmode_count - users_with_devmode_count
 * @property {string} ver - version
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {string} dev_mode - dev_mode
 * @property {string} appId - app_id
 * @property {GUID} esi - esi
 * @property {GUID} msid - msid
 * @property {Url} page_url - page url
 * @property {string} ds_id - ds_id
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<siteMapRequestWasTriggeredInPreviewModeParams>} LogObject<siteMapRequestWasTriggeredInPreviewModeParams>
 */
export function siteMapRequestWasTriggeredInPreviewMode(params) {
    return { evid: 201, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'edmRunOnUserCodeOnLiveSite' event (evid: 202)
 *
 * Event description: sent when an edm (Wix-provided-BE-npm) runs on live site (sent via elementory)
 * auto edm only
 *
 * @param {edmRunOnUserCodeOnLiveSiteParams} params - The parameters for this event
 * @property {GUID} _event_hash - event_hash
 * @property {GUID} visitor_id - visitor_id
 * @property {string} app_name - app_name
 * @property {string} app_id - app_id
 * @property {string} exceptionType - {}
 * @property {string} builderType - builder_type
 * @property {string} ds_type - ds_type
 * @property {GUID} site_id - site_id
 * @property {string} panel_id - panel_id
 * @property {string} file_name - file name
 * @property {string} appName - app_name
 * @property {number} ts - ts
 * @property {boolean} exception - {true/false}
 * @property {string} panel_name - panel_name
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} edm_module_name - edm module name
 * @property {string} pageId - page_id
 * @property {GUID} vsi - viewer session id
 * @property {string} token - token
 * @property {string} ver - version
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {string} api_name - the function used in the edm
 * @property {string} appId - app_id
 * @property {GUID} esi - esi
 * @property {GUID} msid - msid
 * @property {number} tsp - time to respond (success/failure), miliseconds
 * @property {string} ds_id - ds_id
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<edmRunOnUserCodeOnLiveSiteParams>} LogObject<edmRunOnUserCodeOnLiveSiteParams>
 */
export function edmRunOnUserCodeOnLiveSite(params) {
    return { evid: 202, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'edmRunOnUserCodeInPreview' event (evid: 203)
 *
 * Event description: sent when an edm (Wix-provided-BE-npm) runs in preview mode
 *
 * @param {edmRunOnUserCodeInPreviewParams} params - The parameters for this event
 * @property {string} app_name - app_name
 * @property {string} app_id - app_id
 * @property {string} exceptionType - {}
 * @property {number} users_with_write_devmode_count - users_with_write_devmode_count
 * @property {string} builderType - builder_type
 * @property {GUID} visitorId - visitor_id
 * @property {string} ds_type - ds_type
 * @property {GUID} site_id - site_id
 * @property {string} panel_id - panel_id
 * @property {string} file_name - file name
 * @property {string} appName - app_name
 * @property {number} users_on_site_count - users_on_site_count
 * @property {GUID} uuid - uuid
 * @property {number} ts - ts
 * @property {boolean} exception - {true/false}
 * @property {string} panel_name - panel_name
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} edm_module_name - edm module name
 * @property {string} pageId - page_id
 * @property {string} token - token
 * @property {number} users_with_devmode_count - users_with_devmode_count
 * @property {string} ver - version
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {string} dev_mode - dev_mode
 * @property {string} api_name - the function used in the edm
 * @property {string} appId - app_id
 * @property {GUID} esi - esi
 * @property {GUID} msid - msid
 * @property {string} ds_id - ds_id
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<edmRunOnUserCodeInPreviewParams>} LogObject<edmRunOnUserCodeInPreviewParams>
 */
export function edmRunOnUserCodeInPreview(params) {
    return { evid: 203, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'isPotentialForSsrCacheCorvidPage' event (evid: 204)
 *
 * Event description:  Only for preview mode
 *  Only for non automated ssr cache pages
 *  Sent each time the user click preview and the page found as potential to be cached
 * Include pageId,esi (implicit fields)
 *
 * @param {isPotentialForSsrCacheCorvidPageParams} params - The parameters for this event
 * @property {string} app_name - app_name
 * @property {string} app_id - app_id
 * @property {number} users_with_write_devmode_count - users_with_write_devmode_count
 * @property {string} builderType - builder_type
 * @property {GUID} visitorId - visitor_id
 * @property {string} ds_type - ds_type
 * @property {GUID} site_id - site_id
 * @property {string} panel_id - panel_id
 * @property {string} appName - app_name
 * @property {number} users_on_site_count - users_on_site_count
 * @property {number} ts - ts
 * @property {string} panel_name - panel_name
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} page_name - page_name
 * @property {string} pageId - page_id
 * @property {string} token - token
 * @property {number} users_with_devmode_count - users_with_devmode_count
 * @property {string} ver - version
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {string} dev_mode - dev_mode
 * @property {string} appId - app_id
 * @property {Url} pageurl - page_url
 * @property {GUID} esi - esi
 * @property {GUID} msid - metasite id
 * @property {string} ds_id - ds_id
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<isPotentialForSsrCacheCorvidPageParams>} LogObject<isPotentialForSsrCacheCorvidPageParams>
 */
export function isPotentialForSsrCacheCorvidPage(params) {
    return { evid: 204, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'clickedOnTurningOnCachingIde' event (evid: 205)
 *
 * Event description:  Sent when the user clicks on "turn on caching" option in the IDE at preview mode
 *  Only for non automated ssr cache pages
 *  Include pageId,esi (implicit fields)
 *
 * @param {clickedOnTurningOnCachingIdeParams} params - The parameters for this event
 * @property {string} app_name - app_name
 * @property {string} app_id - app_id
 * @property {number} users_with_write_devmode_count - users_with_write_devmode_count
 * @property {string} builderType - builder_type
 * @property {GUID} visitorId - visitor_id
 * @property {string} ds_type - ds_type
 * @property {GUID} site_id - site_id
 * @property {string} panel_id - panel_id
 * @property {string} appName - app_name
 * @property {number} users_on_site_count - users_on_site_count
 * @property {number} ts - ts
 * @property {string} panel_name - panel_name
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} page_name - page_name
 * @property {string} pageId - page_id
 * @property {string} token - token
 * @property {number} users_with_devmode_count - users_with_devmode_count
 * @property {string} ver - version
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {string} dev_mode - dev_mode
 * @property {string} appId - app_id
 * @property {Url} pageurl - page_url
 * @property {GUID} esi - esi
 * @property {GUID} msid - metasite id
 * @property {string} ds_id - ds_id
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<clickedOnTurningOnCachingIdeParams>} LogObject<clickedOnTurningOnCachingIdeParams>
 */
export function clickedOnTurningOnCachingIde(params) {
    return { evid: 205, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'clickedOnLearnMoreRegardingCachingIde' event (evid: 206)
 *
 * Event description:  Sent when the user clicks on "learn more" about caching option in the IDE at preview mode
 *  Only for non automated ssr cache pages
 *  Include pageId,esi (implicit fields)
 *
 * @param {clickedOnLearnMoreRegardingCachingIdeParams} params - The parameters for this event
 * @property {string} app_name - app_name
 * @property {string} app_id - app_id
 * @property {number} users_with_write_devmode_count - users_with_write_devmode_count
 * @property {string} builderType - builder_type
 * @property {GUID} visitorId - visitor_id
 * @property {string} ds_type - ds_type
 * @property {GUID} site_id - site_id
 * @property {string} panel_id - panel_id
 * @property {string} appName - app_name
 * @property {number} users_on_site_count - users_on_site_count
 * @property {number} ts - ts
 * @property {string} panel_name - panel_name
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} page_name - page_name
 * @property {string} pageId - page_id
 * @property {string} token - token
 * @property {number} users_with_devmode_count - users_with_devmode_count
 * @property {string} ver - version
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {string} dev_mode - dev_mode
 * @property {string} appId - app_id
 * @property {Url} pageurl - page_url
 * @property {GUID} esi - esi
 * @property {GUID} msid - metasite id
 * @property {string} ds_id - ds_id
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<clickedOnLearnMoreRegardingCachingIdeParams>} LogObject<clickedOnLearnMoreRegardingCachingIdeParams>
 */
export function clickedOnLearnMoreRegardingCachingIde(params) {
    return { evid: 206, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'clickedOnDismissCachingOptionIde' event (evid: 207)
 *
 * Event description:  Sent when the user clicks on "dismiss" caching option in the IDE at preview mode
 *  Only for non automated ssr cache pages
 *  Include pageId,esi (implicit fields)
 *
 * @param {clickedOnDismissCachingOptionIdeParams} params - The parameters for this event
 * @property {string} app_name - app_name
 * @property {string} app_id - app_id
 * @property {number} users_with_write_devmode_count - users_with_write_devmode_count
 * @property {string} builderType - builder_type
 * @property {GUID} visitorId - visitor_id
 * @property {string} ds_type - ds_type
 * @property {GUID} site_id - site_id
 * @property {string} panel_id - panel_id
 * @property {string} appName - app_name
 * @property {number} users_on_site_count - users_on_site_count
 * @property {number} ts - ts
 * @property {string} panel_name - panel_name
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} page_name - page_name
 * @property {string} pageId - page_id
 * @property {string} token - token
 * @property {number} users_with_devmode_count - users_with_devmode_count
 * @property {string} ver - version
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {string} dev_mode - dev_mode
 * @property {string} appId - app_id
 * @property {Url} pageurl - page_url
 * @property {GUID} esi - esi
 * @property {GUID} msid - metasite id
 * @property {string} ds_id - ds_id
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<clickedOnDismissCachingOptionIdeParams>} LogObject<clickedOnDismissCachingOptionIdeParams>
 */
export function clickedOnDismissCachingOptionIde(params) {
    return { evid: 207, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'idePasteInjectedCode' event (evid: 208)
 *
 * Event description: When a user pastes injected code or copies code from IDE
 *
 * @param {idePasteInjectedCodeParams} params - The parameters for this event
 * @property {string} app_name - app_name
 * @property {string} app_id - app_id
 * @property {number} users_with_write_devmode_count - users_with_write_devmode_count
 * @property {string} builderType - builder_type
 * @property {GUID} visitorId - visitor_id
 * @property {string} ds_type - ds_type
 * @property {GUID} site_id - site_id
 * @property {string} panel_id - panel_id
 * @property {string} appName - app_name
 * @property {number} users_on_site_count - users_on_site_count
 * @property {number} ts - ts
 * @property {string} panel_name - panel_name
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} code - the code that paste to the IDE
 * @property {string} pageId - page_id
 * @property {number} character_count - number of characters change
 * @property {string} token - token
 * @property {number} users_with_devmode_count - users_with_devmode_count
 * @property {string} ver - version
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {string} file_path - code file name
 * @property {string} dev_mode - dev_mode
 * @property {string} appId - app_id
 * @property {GUID} esi - esi
 * @property {GUID} msid - msid
 * @property {string} action - copy/paste
 * @property {string} ds_id - ds_id
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<idePasteInjectedCodeParams>} LogObject<idePasteInjectedCodeParams>
 */
export function idePasteInjectedCode(params) {
    return { evid: 208, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'ideAutocompletePanelShow' event (evid: 209)
 *
 * Event description: Fires when the autocomplete panel shows in the IDE
 *
 * @param {ideAutocompletePanelShowParams} params - The parameters for this event
 * @property {string} app_name - app_name
 * @property {string} app_id - app_id
 * @property {number} users_with_write_devmode_count - users_with_write_devmode_count
 * @property {string} builderType - builder_type
 * @property {GUID} visitorId - visitor_id
 * @property {string} ds_type - ds_type
 * @property {GUID} site_id - site_id
 * @property {string} panel_id - panel_id
 * @property {string} appName - app_name
 * @property {number} users_on_site_count - users_on_site_count
 * @property {number} ts - ts
 * @property {string} panel_name - panel_name
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} pageId - page_id
 * @property {string} user_input - the characters/word the user writes
 * @property {number} character_count - number of characters the user write
 * @property {string} origin - autocomplete panel opened auto/manual
 * @property {string} token - token
 * @property {number} users_with_devmode_count - users_with_devmode_count
 * @property {string} ver - version
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {string} panel_type - completions / hover_content / media_popup
 * @property {string} dev_mode - dev_mode
 * @property {string} appId - app_id
 * @property {GUID} esi - esi
 * @property {GUID} msid - msid
 * @property {string} ds_id - ds_id
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<ideAutocompletePanelShowParams>} LogObject<ideAutocompletePanelShowParams>
 */
export function ideAutocompletePanelShow(params) {
    return { evid: 209, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'leftTreeOpenClose' event (evid: 21)
 *
 * Event description: fire when user open or closing
 *
 * @param {leftTreeOpenCloseParams} params - The parameters for this event
 * @property {string} app_name - app_name
 * @property {string} app_id - app_id
 * @property {number} users_with_write_devmode_count - users_with_write_devmode_count
 * @property {string} builderType - builder_type
 * @property {GUID} visitorId - deprecated
 * @property {string} state - sends only close (should send: open, close)
 * @property {string} ds_type - ds_type
 * @property {GUID} site_id - site_id
 * @property {string} panel_id - panel_id
 * @property {string} appName - app_name
 * @property {number} users_on_site_count - users_on_site_count
 * @property {number} ts - ts
 * @property {string} panel_name - panel_name
 * @property {string} viewmode - viewmode
 * @property {string} input_name - deprecated
 * @property {string} pageId - page_id
 * @property {string} origin - how the user collapse/expand - tab_clicked / arrow_button
 * @property {string} token - token
 * @property {number} users_with_devmode_count - users_with_devmode_count
 * @property {string} ver - version
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {string} dev_mode - dev_mode
 * @property {string} appId - app_id
 * @property {GUID} esi - esi
 * @property {GUID} msid - msid
 * @property {string} action - deprecated
 * @property {string} ds_id - ds_id
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<leftTreeOpenCloseParams>} LogObject<leftTreeOpenCloseParams>
 */
export function leftTreeOpenClose(params) {
    return { evid: 21, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'ideClickOnLinkInTheCode' event (evid: 210)
 *
 * Event description: Fires when a user clicks on the link in the code
 *
 * @param {ideClickOnLinkInTheCodeParams} params - The parameters for this event
 * @property {string} app_name - app_name
 * @property {string} app_id - app_id
 * @property {number} users_with_write_devmode_count - users_with_write_devmode_count
 * @property {string} builderType - builder_type
 * @property {GUID} visitorId - visitor_id
 * @property {string} ds_type - ds_type
 * @property {GUID} site_id - site_id
 * @property {string} panel_id - panel_id
 * @property {string} appName - app_name
 * @property {number} users_on_site_count - users_on_site_count
 * @property {number} ts - ts
 * @property {string} panel_name - panel_name
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} pageId - page_id
 * @property {string} token - token
 * @property {number} users_with_devmode_count - users_with_devmode_count
 * @property {string} ver - version
 * @property {string} userEnv - user_env
 * @property {string} link_url - the url's title
 * @property {string} editor_working_mode - editor_working_mode
 * @property {string} file_path - code file name
 * @property {string} dev_mode - dev_mode
 * @property {string} title - the link's title
 * @property {string} appId - app_id
 * @property {GUID} esi - esi
 * @property {GUID} msid - msid
 * @property {string} ds_id - ds_id
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<ideClickOnLinkInTheCodeParams>} LogObject<ideClickOnLinkInTheCodeParams>
 */
export function ideClickOnLinkInTheCode(params) {
    return { evid: 210, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'checkIfPageIsCacheable' event (evid: 2101)
 *
 * Event description: Analyzing automatic cacheability for pages, based on existence and type of user code.
 * Fired for each page every publish or miss when getting page-details from user-code-analyzer.
 *
 * @param {checkIfPageIsCacheableParams} params - The parameters for this event
 * @property {string} reason - reason for being not cacheable (only if not cacheable)
 * @property {string} page_id - page id
 * @property {string} grid_app_id - grid_app_id
 * @property {boolean} is_cacheable - true if the page will be cached
 * @property {GUID} msid - msid
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<checkIfPageIsCacheableParams>} LogObject<checkIfPageIsCacheableParams>
 */
export function checkIfPageIsCacheable(params) {
    return { evid: 2101, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'ideCodeErrorWarningPanelShow' event (evid: 211)
 *
 * Event description: when a code error or warning shows by hovering over it in the IDE
 *
 * @param {ideCodeErrorWarningPanelShowParams} params - The parameters for this event
 * @property {string} app_name - app_name
 * @property {string} app_id - app_id
 * @property {string} renderingFlow - Rendering flow
 * @property {number} users_with_write_devmode_count - users_with_write_devmode_count
 * @property {string} builderType - builder_type
 * @property {GUID} visitorId - deprecated
 * @property {string} ds_type - ds_type
 * @property {GUID} site_id - site_id
 * @property {string} panel_id - panel_id
 * @property {number} line - the line of code where the error is
 * @property {string} appName - app_name
 * @property {number} users_on_site_count - users_on_site_count
 * @property {number} ts - ts
 * @property {string} message_type - the type of the message
 * @property {string} panel_name - panel_name
 * @property {string} viewmode - viewmode
 * @property {string} line_of_code - line of code
 * @property {string} input_name - deprecated
 * @property {string} message_text - the text in the panel
 * @property {string} page_name - optional - sends the page name
 * @property {string} pageId - page_id
 * @property {string} user_input - the characters/word the user writes
 * @property {number} message_code - a numeric code that group similar errors together
 * @property {string} token - token
 * @property {number} users_with_devmode_count - users_with_devmode_count
 * @property {string} ver - version
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {string} file_path - code file name
 * @property {string} panel_type - warning / error
 * @property {string} dev_mode - dev_mode
 * @property {string} appId - app_id
 * @property {GUID} esi - esi
 * @property {GUID} msid - msid
 * @property {string} ds_id - ds_id
 * @property {string} action - action
 * @property {string} package_name - package_name
 * @return {LogObject<ideCodeErrorWarningPanelShowParams>} LogObject<ideCodeErrorWarningPanelShowParams>
 */
export function ideCodeErrorWarningPanelShow(params) {
    return { evid: 211, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'edmFemMethodCallStart' event (evid: 212)
 *
 * Event description: When any of the EDM' method called
 * Sent when the EDM/FEM starts/try to call
 *
 * @param {edmFemMethodCallStartParams} params - The parameters for this event
 * @property {GUID} _event_hash - event_hash
 * @property {GUID} visitor_id - visitor_id
 * @property {string} app_name - app_name
 * @property {string} app_id - app_id
 * @property {string} methodName - method called
 * @property {string} builderType - builder_type
 * @property {string} ds_type - ds_type
 * @property {GUID} site_id - site_id
 * @property {string} panel_id - panel_id
 * @property {string} appName - app_name
 * @property {number} ts - ts
 * @property {string} panel_name - panel_name
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} pageId - page_id
 * @property {string} id - call id - to join with the success/fail
 * @property {string} methodType - delete/read/create/update/add
 * @property {GUID} vsi - vsi
 * @property {string} origin - WixData , DB UI filter, DB UI sort, DB UI open, DB UI visible
 * @property {string} token - token
 * @property {string} ver - version
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {string} file_path - {page_id/ path+file name}
 * @property {string} module_name - {}
 * @property {string} mode - {live or preview mode}
 * @property {string} appId - app_id
 * @property {GUID} esi - esi
 * @property {GUID} msid - msid
 * @property {number} tsp - tsp
 * @property {string} ds_id - ds_id
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<edmFemMethodCallStartParams>} LogObject<edmFemMethodCallStartParams>
 */
export function edmFemMethodCallStart(params) {
    return { evid: 212, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'edmFemMethodCallFinished' event (evid: 213)
 *
 * Event description: When any of the stores' method finished (successfully/failed)
 * Sent when the EDM/FEM receives info from the API
 *
 * @param {edmFemMethodCallFinishedParams} params - The parameters for this event
 * @property {GUID} _event_hash - event_hash
 * @property {GUID} visitor_id - visitor_id
 * @property {string} app_name - app_name
 * @property {string} app_id - app_id
 * @property {string} methodName - method called
 * @property {string} failure_reason - {}
 * @property {string} builderType - builder_type
 * @property {string} ds_type - ds_type
 * @property {GUID} site_id - site_id
 * @property {string} panel_id - panel_id
 * @property {string} appName - app_name
 * @property {number} ts - ts
 * @property {string} panel_name - panel_name
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {number} response_time - {}
 * @property {boolean} isSuccess - {}
 * @property {string} pageId - page_id
 * @property {string} id - call id - to join with the success/fail
 * @property {string} methodType - delete/read/create/update/add
 * @property {GUID} vsi - vsi
 * @property {string} origin - WixData , DB UI filter, DB UI sort, DB UI open, DB UI visible
 * @property {string} token - token
 * @property {string} ver - version
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {string} file_path - {page_id/ path+file name}
 * @property {string} module_name - {}
 * @property {string} mode - {live or preview mode}
 * @property {string} appId - app_id
 * @property {GUID} esi - esi
 * @property {GUID} msid - msid
 * @property {number} tsp - tsp
 * @property {string} ds_id - ds_id
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<edmFemMethodCallFinishedParams>} LogObject<edmFemMethodCallFinishedParams>
 */
export function edmFemMethodCallFinished(params) {
    return { evid: 213, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'gettingStartedClickOnALink' event (evid: 214)
 *
 * Event description: Corvid Getting started page (opened right after the user turned Dev Mode On) - Click on a link
 *
 * @param {gettingStartedClickOnALinkParams} params - The parameters for this event
 * @property {string} app_name - app_name
 * @property {string} app_id - app_id
 * @property {string} item_url - the URL of the item the user clicks on
 * @property {number} users_with_write_devmode_count - users_with_write_devmode_count
 * @property {string} builderType - builder_type
 * @property {GUID} visitorId - deprecated
 * @property {string} ds_type - ds_type
 * @property {GUID} site_id - site_id
 * @property {string} panel_id - panel_id
 * @property {string} appName - app_name
 * @property {number} users_on_site_count - users_on_site_count
 * @property {number} ts - ts
 * @property {string} panel_name - panel_name
 * @property {string} viewmode - viewmode
 * @property {string} input_name - deprecated
 * @property {string} pageId - page_id
 * @property {string} item_name - The title of the link as display in page
 * @property {string} token - token
 * @property {number} users_with_devmode_count - users_with_devmode_count
 * @property {string} ver - version
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {string} dev_mode - dev_mode
 * @property {string} appId - app_id
 * @property {GUID} esi - esi
 * @property {string} item_type - the type of the link {video, article, example}
 * @property {GUID} msid - msid
 * @property {string} ds_id - ds_id
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<gettingStartedClickOnALinkParams>} LogObject<gettingStartedClickOnALinkParams>
 */
export function gettingStartedClickOnALink(params) {
    return { evid: 214, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'gettingStartedClickOnStartCoding' event (evid: 217)
 *
 * Event description: Corvid Getting started page (opened right after the user turned Dev Mode On) - Click on start coding button
 *
 * @param {gettingStartedClickOnStartCodingParams} params - The parameters for this event
 * @property {string} app_name - app_name
 * @property {string} app_id - app_id
 * @property {number} users_with_write_devmode_count - users_with_write_devmode_count
 * @property {string} builderType - builder_type
 * @property {GUID} visitorId - deprecated
 * @property {string} ds_type - ds_type
 * @property {GUID} site_id - site_id
 * @property {string} panel_id - panel_id
 * @property {string} appName - app_name
 * @property {number} users_on_site_count - users_on_site_count
 * @property {number} ts - ts
 * @property {GUID} userGuid - uuid
 * @property {string} panel_name - panel_name
 * @property {string} viewmode - viewmode
 * @property {string} input_name - deprecated
 * @property {string} pageId - page_id
 * @property {string} token - token
 * @property {number} users_with_devmode_count - users_with_devmode_count
 * @property {string} ver - version
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {string} dev_mode - dev_mode
 * @property {string} appId - app_id
 * @property {GUID} esi - esi
 * @property {GUID} msid - msid
 * @property {string} ds_id - ds_id
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<gettingStartedClickOnStartCodingParams>} LogObject<gettingStartedClickOnStartCodingParams>
 */
export function gettingStartedClickOnStartCoding(params) {
    return { evid: 217, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'datasetWasAddedAutomatically' event (evid: 218)
 *
 * Event description: Dataset was added automatically
 * sent only when a user migrate the data in the repeater, for QA
 *
 * @param {datasetWasAddedAutomaticallyParams} params - The parameters for this event
 * @property {string} app_name - app_name
 * @property {string} app_id - app_id
 * @property {number} users_with_write_devmode_count - users_with_write_devmode_count
 * @property {string} builderType - builder_type
 * @property {GUID} visitorId - deprecated
 * @property {string} ds_type - ds_type
 * @property {GUID} site_id - site_id
 * @property {string} panel_id - panel_id
 * @property {string} data_set_name - {}
 * @property {string} appName - app_name
 * @property {number} users_on_site_count - users_on_site_count
 * @property {number} ts - ts
 * @property {string} collection_id - collection_id
 * @property {string} panel_name - panel_name
 * @property {string} viewmode - viewmode
 * @property {string} input_name - deprecated
 * @property {string} pageId - page_id
 * @property {string} token - token
 * @property {number} users_with_devmode_count - users_with_devmode_count
 * @property {string} ver - version
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {string} mode - READ, WRITE, READ_WRITE
 * @property {string} dev_mode - dev_mode
 * @property {string} appId - app_id
 * @property {GUID} esi - esi
 * @property {string} component_id - {}
 * @property {string} collection_name - collection_name
 * @property {string} component_type - {}
 * @property {GUID} msid - msid
 * @property {string} ds_id - ds_id
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<datasetWasAddedAutomaticallyParams>} LogObject<datasetWasAddedAutomaticallyParams>
 */
export function datasetWasAddedAutomatically(params) {
    return { evid: 218, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'wixLocalSearchClicked' event (evid: 219)
 *
 * Event description: -- WIX LOCAL --
 * Shot when click on "Search"
 * Includes search phrase/word, number of results, results URLs
 *
 * @param {wixLocalSearchClickedParams} params - The parameters for this event
 * @property {GUID} _event_hash - event_hash
 * @property {GUID} visitor_id - visitor_id
 * @property {string} app_name - app_name
 * @property {string} app_id - app_id
 * @property {string} builderType - builder_type
 * @property {string} ds_type - ds_type
 * @property {GUID} site_id - site_id
 * @property {string} panel_id - panel_id
 * @property {string} appName - app_name
 * @property {string} results_urls - results_URLs, separated by ","
 * @property {number} ts - ts
 * @property {number} number_results - number_results
 * @property {string} results_msids - results_MSIDs, separated by ","
 * @property {string} panel_name - panel_name
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} search_phrase - search_phrase
 * @property {string} pageId - page_id
 * @property {GUID} vsi - vsi
 * @property {string} origin - (manual, popular tag)
 * @property {string} token - token
 * @property {string} ver - version
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {string} appId - app_id
 * @property {GUID} esi - esi
 * @property {GUID} msid - msid
 * @property {number} tsp - tsp
 * @property {string} ds_id - ds_id
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<wixLocalSearchClickedParams>} LogObject<wixLocalSearchClickedParams>
 */
export function wixLocalSearchClicked(params) {
    return { evid: 219, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'ideFocusedOrBlurred' event (evid: 22)
 *
 * Event description: when the IDE focused
 *
 * @param {ideFocusedOrBlurredParams} params - The parameters for this event
 * @property {string} app_name - app_name
 * @property {string} app_id - app_id
 * @property {number} users_with_write_devmode_count - users_with_write_devmode_count
 * @property {string} builderType - builder_type
 * @property {GUID} visitorId - visitor_id
 * @property {string} state - focus_out / focus_in
 * @property {string} ds_type - ds_type
 * @property {GUID} site_id - site_id
 * @property {string} panel_id - panel_id
 * @property {number} time_in_focus - time in focus in ms
 * @property {string} appName - app_name
 * @property {number} users_on_site_count - users_on_site_count
 * @property {number} ts - ts
 * @property {string} panel_name - panel_name
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} pageId - page_id
 * @property {number} character_count - number of characters change
 * @property {string} token - token
 * @property {number} users_with_devmode_count - users_with_devmode_count
 * @property {string} ver - version
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {string} dev_mode - dev_mode
 * @property {string} appId - app_id
 * @property {GUID} esi - esi
 * @property {GUID} msid - msid
 * @property {string} ds_id - ds_id
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<ideFocusedOrBlurredParams>} LogObject<ideFocusedOrBlurredParams>
 */
export function ideFocusedOrBlurred(params) {
    return { evid: 22, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'wixLocalPopularSearchClick' event (evid: 220)
 *
 * Event description: -- WIX LOCAL --
 * Shot when click on one of the "Popular Search" tags
 *
 * @param {wixLocalPopularSearchClickParams} params - The parameters for this event
 * @property {GUID} _event_hash - event_hash
 * @property {GUID} visitor_id - visitor_id
 * @property {string} app_name - app_name
 * @property {string} app_id - app_id
 * @property {string} builderType - builder_type
 * @property {string} ds_type - ds_type
 * @property {GUID} site_id - site_id
 * @property {string} panel_id - panel_id
 * @property {string} appName - app_name
 * @property {number} ts - ts
 * @property {string} panel_name - panel_name
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} pageId - page_id
 * @property {GUID} vsi - vsi
 * @property {string} token - token
 * @property {string} ver - version
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {string} appId - app_id
 * @property {GUID} esi - esi
 * @property {GUID} msid - msid
 * @property {number} tsp - tsp
 * @property {string} tag_name - tag_name
 * @property {string} ds_id - ds_id
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<wixLocalPopularSearchClickParams>} LogObject<wixLocalPopularSearchClickParams>
 */
export function wixLocalPopularSearchClick(params) {
    return { evid: 220, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'wixLocalDiscoverBusinessesClick' event (evid: 221)
 *
 * Event description: -- WIX LOCAL --
 * Shot when click on one of the "Discover Businesses"
 *
 * @param {wixLocalDiscoverBusinessesClickParams} params - The parameters for this event
 * @property {GUID} _event_hash - event_hash
 * @property {GUID} visitor_id - visitor_id
 * @property {string} app_name - app_name
 * @property {string} app_id - app_id
 * @property {string} builderType - builder_type
 * @property {string} ds_type - ds_type
 * @property {GUID} site_id - site_id
 * @property {string} panel_id - panel_id
 * @property {Url} url - url_clicked
 * @property {string} appName - app_name
 * @property {number} ts - ts
 * @property {string} panel_name - panel_name
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} pageId - page_id
 * @property {GUID} vsi - vsi
 * @property {string} token - token
 * @property {string} ver - version
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {string} appId - app_id
 * @property {GUID} esi - esi
 * @property {GUID} msid - msid
 * @property {number} tsp - tsp
 * @property {string} ds_id - ds_id
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<wixLocalDiscoverBusinessesClickParams>} LogObject<wixLocalDiscoverBusinessesClickParams>
 */
export function wixLocalDiscoverBusinessesClick(params) {
    return { evid: 221, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'ideTabAction' event (evid: 222)
 *
 * Event description: When a user closes a tab in the IDE
 *
 * @param {ideTabActionParams} params - The parameters for this event
 * @property {string} app_name - app_name
 * @property {string} app_id - app_id
 * @property {string} function_name - the function name in case we are talking on trigger BE Tool
 * @property {number} users_with_write_devmode_count - users_with_write_devmode_count
 * @property {string} builderType - builder_type
 * @property {GUID} visitorId - deprecated
 * @property {string} ds_type - ds_type
 * @property {GUID} site_id - site_id
 * @property {string} panel_id - panel_id
 * @property {string} file_name - the current file_name the user on it. If it's internal file please sent his name with underscore - welcome_page / hello_world
 * @property {string} appName - app_name
 * @property {number} users_on_site_count - users_on_site_count
 * @property {number} ts - ts
 * @property {string} panel_name - panel_name
 * @property {string} viewmode - viewmode
 * @property {string} input_name - deprecated
 * @property {number} tab_number - the number of the current file tab (optional when pinned send)
 * @property {string} pageId - page_id
 * @property {string} origin - from where the user closes the tab - "X" or from where the user pinned - {'left_tree_double_click'/'IDE_double_click'/'search_tab_double_click'}
 * @property {string} token - token
 * @property {number} users_with_devmode_count - users_with_devmode_count
 * @property {string} ver - version
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {string} dev_mode - dev_mode
 * @property {string} appId - app_id
 * @property {GUID} esi - esi
 * @property {GUID} msid - msid
 * @property {string} action - close / pinned / navigate / scroll
 * @property {string} ds_id - ds_id
 * @property {number} num_of_opened_tabs - the number of  tabs that were open before the close action (optional when pinned send)
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<ideTabActionParams>} LogObject<ideTabActionParams>
 */
export function ideTabAction(params) {
    return { evid: 222, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'wixLocalClickedOnResult' event (evid: 223)
 *
 * Event description: -- WIX LOCAL --
 * Shot when clicking on one of the search results,
 * Includes search phrase/word, name/url/msid of clicked site
 *
 * @param {wixLocalClickedOnResultParams} params - The parameters for this event
 * @property {GUID} _event_hash - event_hash
 * @property {GUID} visitor_id - visitor_id
 * @property {string} app_name - app_name
 * @property {string} app_id - app_id
 * @property {string} builderType - builder_type
 * @property {string} ds_type - ds_type
 * @property {GUID} site_id - site_id
 * @property {string} panel_id - panel_id
 * @property {string} appName - app_name
 * @property {number} ts - ts
 * @property {string} panel_name - panel_name
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} search_phrase - search_phrase
 * @property {string} pageId - page_id
 * @property {string} site_name - site name
 * @property {GUID} vsi - vsi
 * @property {string} token - token
 * @property {string} ver - version
 * @property {Url} site_url - site url
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {GUID} site_msid - clicked site msid
 * @property {string} appId - app_id
 * @property {GUID} esi - esi
 * @property {GUID} msid - site msid
 * @property {number} tsp - tsp
 * @property {string} ds_id - ds_id
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<wixLocalClickedOnResultParams>} LogObject<wixLocalClickedOnResultParams>
 */
export function wixLocalClickedOnResult(params) {
    return { evid: 223, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'wixLocalClickedGetListed' event (evid: 224)
 *
 * Event description: -- WIX LOCAL --
 * Shot when clicking on 'Get Listed' button
 *
 * @param {wixLocalClickedGetListedParams} params - The parameters for this event
 * @property {GUID} _event_hash - event_hash
 * @property {GUID} visitor_id - visitor_id
 * @property {string} app_name - app_name
 * @property {string} app_id - app_id
 * @property {string} builderType - builder_type
 * @property {string} ds_type - ds_type
 * @property {GUID} site_id - site_id
 * @property {string} panel_id - panel_id
 * @property {string} appName - app_name
 * @property {number} ts - ts
 * @property {string} panel_name - panel_name
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} pageId - page_id
 * @property {GUID} vsi - vsi
 * @property {string} token - token
 * @property {string} ver - version
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {string} appId - app_id
 * @property {GUID} esi - esi
 * @property {GUID} msid - msid
 * @property {number} tsp - tsp
 * @property {string} ds_id - ds_id
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<wixLocalClickedGetListedParams>} LogObject<wixLocalClickedGetListedParams>
 */
export function wixLocalClickedGetListed(params) {
    return { evid: 224, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'wixLocalClickedSubmit' event (evid: 225)
 *
 * Event description: -- WIX LOCAL --
 * Shot when clicking on 'Submit' button, Includes data about the submitted site and the form questions
 *
 * @param {wixLocalClickedSubmitParams} params - The parameters for this event
 * @property {GUID} _event_hash - event_hash
 * @property {GUID} visitor_id - visitor_id
 * @property {string} app_name - app_name
 * @property {string} app_id - app_id
 * @property {boolean} business_image - did the user upload an image
 * @property {string} builderType - builder_type
 * @property {string} ds_type - ds_type
 * @property {GUID} site_id - site_id
 * @property {string} panel_id - panel_id
 * @property {string} appName - app_name
 * @property {boolean} special_filters - did the user filled this question
 * @property {GUID} submit_site_msid - submit_site_msid
 * @property {number} ts - ts
 * @property {boolean} business_description - did the user filled this question
 * @property {string} panel_name - panel_name
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} submit_site_name - submit_site_name
 * @property {string} pageId - page_id
 * @property {GUID} vsi - vsi
 * @property {string} token - token
 * @property {string} ver - version
 * @property {Url} submit_site_url - submit site url
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {string} appId - app_id
 * @property {GUID} esi - esi
 * @property {GUID} msid - msid
 * @property {number} tsp - tsp
 * @property {string} ds_id - ds_id
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<wixLocalClickedSubmitParams>} LogObject<wixLocalClickedSubmitParams>
 */
export function wixLocalClickedSubmit(params) {
    return { evid: 225, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'leftTreeClickOnATab' event (evid: 226)
 *
 * Event description: Left tree - Click on a tab in the left tree (page code, site code, search, databases, help, github integration)
 *
 * @param {leftTreeClickOnATabParams} params - The parameters for this event
 * @property {string} app_name - app_name
 * @property {string} app_id - app_id
 * @property {number} users_with_write_devmode_count - users_with_write_devmode_count
 * @property {string} builderType - builder_type
 * @property {GUID} visitorId - deprecated
 * @property {string} ds_type - ds_type
 * @property {GUID} site_id - site_id
 * @property {string} panel_id - panel_id
 * @property {string} appName - app_name
 * @property {number} users_on_site_count - users_on_site_count
 * @property {string} tab_name - page_code, site_code, search, databases, help, github integration
 * @property {number} ts - ts
 * @property {string} panel_name - panel_name
 * @property {string} viewmode - viewmode
 * @property {string} input_name - deprecated
 * @property {string} pageId - page_id
 * @property {string} origin - {left tree}
 * @property {string} token - token
 * @property {number} users_with_devmode_count - users_with_devmode_count
 * @property {string} ver - version
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {string} dev_mode - dev_mode
 * @property {string} appId - app_id
 * @property {GUID} esi - esi
 * @property {GUID} msid - msid
 * @property {string} ds_id - ds_id
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<leftTreeClickOnATabParams>} LogObject<leftTreeClickOnATabParams>
 */
export function leftTreeClickOnATab(params) {
    return { evid: 226, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'modalPanelTabOpen' event (evid: 227)
 *
 * Event description: Within the Modal Panel, the user can navigate between Templates (the default tab)/ His own collections/ Wix APIs
 *
 * @param {modalPanelTabOpenParams} params - The parameters for this event
 * @property {string} app_name - app_name
 * @property {string} app_id - app_id
 * @property {number} users_with_write_devmode_count - users_with_write_devmode_count
 * @property {string} builderType - builder_type
 * @property {GUID} visitorId - deprecated
 * @property {string} ds_type - ds_type
 * @property {GUID} site_id - site_id
 * @property {string} panel_id - panel_id
 * @property {string} appName - app_name
 * @property {number} users_on_site_count - users_on_site_count
 * @property {string} tab_name - {templates/collections/apis}
 * @property {GUID} uuid - uuid
 * @property {number} ts - ts
 * @property {string} panel_name - panel_name
 * @property {string} viewmode - viewmode
 * @property {string} input_name - deprecated
 * @property {string} pageId - page_id
 * @property {string} token - token
 * @property {number} users_with_devmode_count - users_with_devmode_count
 * @property {string} ver - version
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {string} dev_mode - dev_mode
 * @property {string} appId - app_id
 * @property {GUID} esi - esi
 * @property {GUID} msid - msid
 * @property {string} ds_id - ds_id
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<modalPanelTabOpenParams>} LogObject<modalPanelTabOpenParams>
 */
export function modalPanelTabOpen(params) {
    return { evid: 227, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'ideContextMenuOpen' event (evid: 228)
 *
 * Event description: When a user opens the context menu in the IDE (right-click)
 *
 * @param {ideContextMenuOpenParams} params - The parameters for this event
 * @property {string} app_name - app_name
 * @property {string} app_id - app_id
 * @property {number} users_with_write_devmode_count - users_with_write_devmode_count
 * @property {string} builderType - builder_type
 * @property {GUID} visitorId - deprecated
 * @property {string} ds_type - ds_type
 * @property {GUID} site_id - site_id
 * @property {string} panel_id - panel_id
 * @property {string} appName - app_name
 * @property {number} users_on_site_count - users_on_site_count
 * @property {number} ts - ts
 * @property {string} panel_name - panel_name
 * @property {string} viewmode - viewmode
 * @property {string} input_name - deprecated
 * @property {string} pageId - page_id
 * @property {string} item_name - deprecated
 * @property {string} origin - right-click
 * @property {string} token - token
 * @property {number} users_with_devmode_count - users_with_devmode_count
 * @property {string} ver - version
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {string} dev_mode - dev_mode
 * @property {string} appId - app_id
 * @property {GUID} esi - esi
 * @property {GUID} msid - msid
 * @property {string} ds_id - ds_id
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<ideContextMenuOpenParams>} LogObject<ideContextMenuOpenParams>
 */
export function ideContextMenuOpen(params) {
    return { evid: 228, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'corvidEducationBindingAnswersAndWixIdentifiers' event (evid: 230)
 *
 * Event description: The event is fired every time: 1. A user loads the Corvid Education site  2. A wixCIDX and/or wixUIDX cookie was detected
 *
 * @param {corvidEducationBindingAnswersAndWixIdentifiersParams} params - The parameters for this event
 * @property {string} app_name - app_name
 * @property {string} app_id - app_id
 * @property {GUID} tenant_id - {answers tenant id}
 * @property {GUID} answers_client_id - Answers client(Anonymous) id
 * @property {number} users_with_write_devmode_count - users_with_write_devmode_count
 * @property {string} builderType - builder_type
 * @property {GUID} visitorId - visitor_id
 * @property {string} ds_type - ds_type
 * @property {GUID} site_id - site_id
 * @property {string} panel_id - panel_id
 * @property {string} appName - app_name
 * @property {number} users_on_site_count - users_on_site_count
 * @property {GUID} uuid - Wix User ID
 * @property {number} ts - ts
 * @property {string} panel_name - panel_name
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {GUID} client_id - Wix client(Anonymous) id
 * @property {GUID} answers_uuid - Answers User ID
 * @property {string} pageId - page_id
 * @property {string} token - token
 * @property {number} users_with_devmode_count - users_with_devmode_count
 * @property {string} ver - version
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {string} dev_mode - dev_mode
 * @property {string} appId - app_id
 * @property {GUID} esi - esi
 * @property {GUID} msid - msid
 * @property {string} ds_id - ds_id
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<corvidEducationBindingAnswersAndWixIdentifiersParams>} LogObject<corvidEducationBindingAnswersAndWixIdentifiersParams>
 */
export function corvidEducationBindingAnswersAndWixIdentifiers(params) {
    return { evid: 230, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'checkIfPageIsCacheableSrc79Evid2301' event (evid: 2301)
 *
 * Event description: Analyzing automatic cacheability for pages, based on existence and type of user code.
 * Fired for each page every publish or miss when getting page-details from user-code-analyzer.
 *
 * @param {checkIfPageIsCacheableSrc79Evid2301Params} params - The parameters for this event
 * @property {string} reason - reason for being not cacheable (only if not cacheable)
 * @property {string} page_id - page id
 * @property {string} grid_app_id - grid_app_id
 * @property {boolean} is_cacheable - true if the page will be cached
 * @property {GUID} msid - msid
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<checkIfPageIsCacheableSrc79Evid2301Params>} LogObject<checkIfPageIsCacheableSrc79Evid2301Params>
 */
export function checkIfPageIsCacheableSrc79Evid2301(params) {
    return { evid: 2301, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'idePopupShown' event (evid: 231)
 *
 * Event description: IDE - Popup is shown
 *
 * @param {idePopupShownParams} params - The parameters for this event
 * @property {string} app_name - app_name
 * @property {string} app_id - app_id
 * @property {number} users_with_write_devmode_count - users_with_write_devmode_count
 * @property {string} builderType - builder_type
 * @property {GUID} visitorId - deprecated
 * @property {string} ds_type - ds_type
 * @property {GUID} site_id - site_id
 * @property {string} panel_id - panel_id
 * @property {string} appName - app_name
 * @property {number} users_on_site_count - users_on_site_count
 * @property {number} ts - ts
 * @property {string} panel_name - panel_name
 * @property {string} viewmode - viewmode
 * @property {string} input_name - deprecated
 * @property {string} pageId - page_id
 * @property {string} origin - got_it
 * @property {string} token - token
 * @property {number} users_with_devmode_count - users_with_devmode_count
 * @property {string} ver - version
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {string} dev_mode - dev_mode
 * @property {string} appId - app_id
 * @property {GUID} esi - esi
 * @property {GUID} msid - msid
 * @property {string} popup_name - ide_shortcuts_popover, multi_tabs_popover, etc...
 * @property {string} ds_id - ds_id
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<idePopupShownParams>} LogObject<idePopupShownParams>
 */
export function idePopupShown(params) {
    return { evid: 231, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'idePopupClosed' event (evid: 232)
 *
 * Event description: IDE - Popup closed
 *
 * @param {idePopupClosedParams} params - The parameters for this event
 * @property {string} app_name - app_name
 * @property {string} app_id - app_id
 * @property {number} users_with_write_devmode_count - users_with_write_devmode_count
 * @property {string} builderType - builder_type
 * @property {GUID} visitorId - deprecated
 * @property {string} ds_type - ds_type
 * @property {GUID} site_id - site_id
 * @property {string} panel_id - panel_id
 * @property {string} appName - app_name
 * @property {number} users_on_site_count - users_on_site_count
 * @property {number} ts - ts
 * @property {string} panel_name - panel_name
 * @property {string} viewmode - viewmode
 * @property {string} input_name - deprecated
 * @property {string} pageId - page_id
 * @property {string} origin - {X/ got_it}
 * @property {string} token - token
 * @property {number} users_with_devmode_count - users_with_devmode_count
 * @property {string} ver - version
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {string} dev_mode - dev_mode
 * @property {string} appId - app_id
 * @property {GUID} esi - esi
 * @property {GUID} msid - msid
 * @property {string} popup_name - multi_tabs_popover, ide_shortcuts_popover, etc...
 * @property {string} ds_id - ds_id
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<idePopupClosedParams>} LogObject<idePopupClosedParams>
 */
export function idePopupClosed(params) {
    return { evid: 232, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'corvidFeedbackFormActionClicked' event (evid: 234)
 *
 * Event description: Corvid feedback form --> action clicked
 *
 * @param {corvidFeedbackFormActionClickedParams} params - The parameters for this event
 * @property {string} app_name - app_name
 * @property {string} app_id - app_id
 * @property {number} users_with_write_devmode_count - users_with_write_devmode_count
 * @property {string} builderType - builder_type
 * @property {GUID} visitorId - visitor_id
 * @property {string} ds_type - ds_type
 * @property {GUID} site_id - site_id
 * @property {string} panel_id - panel_id
 * @property {string} appName - app_name
 * @property {number} users_on_site_count - users_on_site_count
 * @property {number} ts - ts
 * @property {string} feedback_topic - {new_workspace}
 * @property {string} panel_name - panel_name
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} pageId - page_id
 * @property {string} token - token
 * @property {number} users_with_devmode_count - users_with_devmode_count
 * @property {string} ver - version
 * @property {string} actionName - {send_us_fedback, something_went_wrong}
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {string} dev_mode - dev_mode
 * @property {string} appId - app_id
 * @property {GUID} esi - esi
 * @property {GUID} msid - msid
 * @property {string} ds_id - ds_id
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<corvidFeedbackFormActionClickedParams>} LogObject<corvidFeedbackFormActionClickedParams>
 */
export function corvidFeedbackFormActionClicked(params) {
    return { evid: 234, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'userCodeUsedWSelectorTemporaryForQaOnly' event (evid: 238)
 *
 * Event description:  Only for live sites (Not preview mode)  once per page view
 *  for any $w interaction with elements on the page
 *  including - page url
 *  Temporary, for QA only
 *
 * @param {userCodeUsedWSelectorTemporaryForQaOnlyParams} params - The parameters for this event
 * @property {GUID} _event_hash - event_hash
 * @property {GUID} visitor_id - visitor_id
 * @property {string} app_name - app_name
 * @property {string} app_id - app_id
 * @property {Url} page_url - []
 * @property {string} builderType - builder_type
 * @property {string} ds_type - ds_type
 * @property {boolean} isServerSide - []
 * @property {GUID} site_id - site_id
 * @property {string} panel_id - panel_id
 * @property {string} appName - app_name
 * @property {Url} pageUrl - []
 * @property {number} pn - page_number
 * @property {number} ts - ts
 * @property {number} tsn - time_from_start_network_ms
 * @property {string} panel_name - panel_name
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {boolean} is_lightbox - []
 * @property {string} pageId - page_id
 * @property {GUID} vsi - vsi
 * @property {string} token - token
 * @property {string} ver - version
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {string} appId - app_id
 * @property {Url} pageurl - []
 * @property {GUID} esi - esi
 * @property {GUID} msid - msid
 * @property {number} tsp - tsp
 * @property {string} ds_id - ds_id
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<userCodeUsedWSelectorTemporaryForQaOnlyParams>} LogObject<userCodeUsedWSelectorTemporaryForQaOnlyParams>
 */
export function userCodeUsedWSelectorTemporaryForQaOnly(params) {
    return { evid: 238, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'siteEventsLogsToolDropdownClickOrTheExpandedValuesOfItSrc79Evid239' event (evid: 239)
 *
 * Event description: site events - logs tool dropdown click or the expanded values of it
 *
 * @param {siteEventsLogsToolDropdownClickOrTheExpandedValuesOfItSrc79Evid239Params} params - The parameters for this event
 * @property {string} input_name - logs_tools/ connect_to_go/ connect_to_3rd
 * @property {string} 'environment ' - bizm_site_events, preview_console, TB_function_tool
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<siteEventsLogsToolDropdownClickOrTheExpandedValuesOfItSrc79Evid239Params>} LogObject<siteEventsLogsToolDropdownClickOrTheExpandedValuesOfItSrc79Evid239Params>
 */
export function siteEventsLogsToolDropdownClickOrTheExpandedValuesOfItSrc79Evid239(params) {
    return { evid: 239, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'platformGenericApiCall' event (evid: 24)
 *
 * Event description: Will be sent with every API call the editor is getting from an app.
 *
 * @param {platformGenericApiCallParams} params - The parameters for this event
 * @property {string} app_name - app_name
 * @property {string} app_id - app_id
 * @property {string} module - module
 * @property {number} users_with_write_devmode_count - users_with_write_devmode_count
 * @property {string} builderType - builder_type
 * @property {GUID} visitorId - visitor_id
 * @property {string} ds_type - ds_type
 * @property {GUID} site_id - site_id
 * @property {string} panel_id - panel_id
 * @property {string} appName - app_name
 * @property {number} users_on_site_count - users_on_site_count
 * @property {number} ts - ts
 * @property {string} panel_name - panel_name
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} pageId - page_id
 * @property {string} function - function_name
 * @property {string} token - token
 * @property {number} users_with_devmode_count - users_with_devmode_count
 * @property {string} ver - version
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {string} dev_mode - dev_mode
 * @property {string} pageid - page_id
 * @property {string} appId - app_id
 * @property {GUID} esi - esi
 * @property {GUID} msid - msid
 * @property {string} ds_id - ds_id
 * @property {string} arguments - arguments
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<platformGenericApiCallParams>} LogObject<platformGenericApiCallParams>
 */
export function platformGenericApiCall(params) {
    return { evid: 24, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'triggerFunctionClickToOpenTriggerBeFunctionPanel' event (evid: 240)
 *
 * Event description: Send when a user clicks on the “play” icon or clicks on the function name to open the Trigger BE Function panel
 *
 * @param {triggerFunctionClickToOpenTriggerBeFunctionPanelParams} params - The parameters for this event
 * @property {string} app_name - app_name
 * @property {string} app_id - app_id
 * @property {string} function_name - What is the functions that triggered
 * @property {number} users_with_write_devmode_count - users_with_write_devmode_count
 * @property {string} builderType - builder_type
 * @property {number} parameters_count - the amount of parameters the function gets
 * @property {GUID} visitorId - visitor_id
 * @property {string} ds_type - ds_type
 * @property {GUID} site_id - site_id
 * @property {string} panel_id - panel_id
 * @property {string} file_name - code file name
 * @property {string} appName - app_name
 * @property {number} users_on_site_count - users_on_site_count
 * @property {number} ts - ts
 * @property {boolean} is_default_function - is this the empty state function
 * @property {string} panel_name - panel_name
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} pageId - page_id
 * @property {string} origin - From where the users open the panel - play_icon / function_name
 * @property {string} token - token
 * @property {number} users_with_devmode_count - users_with_devmode_count
 * @property {string} ver - version
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {string} dev_mode - dev_mode
 * @property {string} appId - app_id
 * @property {GUID} esi - esi
 * @property {string} origins - IGNORE!!
 * @property {GUID} msid - msid
 * @property {string} ds_id - ds_id
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<triggerFunctionClickToOpenTriggerBeFunctionPanelParams>} LogObject<triggerFunctionClickToOpenTriggerBeFunctionPanelParams>
 */
export function triggerFunctionClickToOpenTriggerBeFunctionPanel(params) {
    return { evid: 240, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'triggerFunctionClickOnActionInTriggerBeFunctionPanel' event (evid: 241)
 *
 * Event description: Send when a user clicks on action as the “reset parameters” or "clear output" (trash icon) in the Trigger BE Function panel
 *
 * @param {triggerFunctionClickOnActionInTriggerBeFunctionPanelParams} params - The parameters for this event
 * @property {string} app_name - app_name
 * @property {string} app_id - app_id
 * @property {string} function_name - What is the functions that triggered
 * @property {number} users_with_write_devmode_count - users_with_write_devmode_count
 * @property {string} builderType - builder_type
 * @property {number} parameters_count - the amount of parameters the function gets
 * @property {GUID} visitorId - visitor_id
 * @property {string} ds_type - ds_type
 * @property {GUID} site_id - site_id
 * @property {string} panel_id - panel_id
 * @property {string} file_name - file name
 * @property {string} appName - app_name
 * @property {number} users_on_site_count - users_on_site_count
 * @property {number} ts - ts
 * @property {boolean} is_default_function - is this the empty state function
 * @property {string} panel_name - panel_name
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} pageId - page_id
 * @property {string} origin - From where the users reset the parameters - button/keyboard_shortcut
 * @property {string} token - token
 * @property {number} users_with_devmode_count - users_with_devmode_count
 * @property {string} ver - version
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {string} dev_mode - dev_mode
 * @property {string} appId - app_id
 * @property {GUID} esi - esi
 * @property {GUID} msid - msid
 * @property {string} action - reset_parameters/clear_output
 * @property {string} ds_id - ds_id
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<triggerFunctionClickOnActionInTriggerBeFunctionPanelParams>} LogObject<triggerFunctionClickOnActionInTriggerBeFunctionPanelParams>
 */
export function triggerFunctionClickOnActionInTriggerBeFunctionPanel(params) {
    return { evid: 241, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'triggerFunctionClickToRunBeFunctionInTriggerBeFunctionPanel' event (evid: 242)
 *
 * Event description: Send when a user clicks on the “run” to trigger backend function in the Trigger BE Function panel
 *
 * @param {triggerFunctionClickToRunBeFunctionInTriggerBeFunctionPanelParams} params - The parameters for this event
 * @property {string} app_name - app_name
 * @property {string} app_id - app_id
 * @property {string} function_name - What is the functions that triggered
 * @property {number} users_with_write_devmode_count - users_with_write_devmode_count
 * @property {string} builderType - builder_type
 * @property {number} parameters_count - the amount of parameters the function gets
 * @property {GUID} visitorId - visitor_id
 * @property {string} ds_type - ds_type
 * @property {GUID} site_id - site_id
 * @property {string} panel_id - panel_id
 * @property {string} file_name - file name
 * @property {string} appName - app_name
 * @property {number} users_on_site_count - users_on_site_count
 * @property {number} ts - ts
 * @property {boolean} is_default_function - is this the empty state function
 * @property {string} panel_name - panel_name
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} pageId - page_id
 * @property {string} origin - From where the users run the panel - run_button /  keyboard_shortcut
 * @property {string} token - token
 * @property {number} users_with_devmode_count - users_with_devmode_count
 * @property {string} ver - version
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {string} dev_mode - dev_mode
 * @property {string} appId - app_id
 * @property {GUID} esi - esi
 * @property {GUID} msid - msid
 * @property {string} ds_id - ds_id
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<triggerFunctionClickToRunBeFunctionInTriggerBeFunctionPanelParams>} LogObject<triggerFunctionClickToRunBeFunctionInTriggerBeFunctionPanelParams>
 */
export function triggerFunctionClickToRunBeFunctionInTriggerBeFunctionPanel(params) {
    return { evid: 242, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'triggerFunctionBackendFunctionFinishRunManuallySuccessFailure' event (evid: 243)
 *
 * Event description: Backend function runs on Trigger BE function panel
 * (sent from "run")
 *
 * @param {triggerFunctionBackendFunctionFinishRunManuallySuccessFailureParams} params - The parameters for this event
 * @property {string} app_name - app_name
 * @property {string} app_id - app_id
 * @property {string} function_name - What is the functions that triggered
 * @property {number} users_with_write_devmode_count - users_with_write_devmode_count
 * @property {string} builderType - builder_type
 * @property {number} parameters_count - the amount of parameters the function gets
 * @property {GUID} visitorId - visitor_id
 * @property {string} ds_type - ds_type
 * @property {GUID} site_id - site_id
 * @property {string} panel_id - panel_id
 * @property {string} file_name - file name
 * @property {string} appName - app_name
 * @property {number} users_on_site_count - users_on_site_count
 * @property {number} ts - ts
 * @property {boolean} is_default_function - is this the empty state function
 * @property {boolean} exception - exception
 * @property {string} panel_name - panel_name
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {boolean} is_default_params - the params are default or the user changed them
 * @property {string} pageId - page_id
 * @property {string} exception_type - exception_type
 * @property {string} token - token
 * @property {number} users_with_devmode_count - users_with_devmode_count
 * @property {string} ver - version
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {string} file_type - code file type {js, jsw, etc.}
 * @property {string} dev_mode - dev_mode
 * @property {string} appId - app_id
 * @property {GUID} esi - esi
 * @property {GUID} msid - msid
 * @property {number} run_time - function run time
 * @property {string} ds_id - ds_id
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<triggerFunctionBackendFunctionFinishRunManuallySuccessFailureParams>} LogObject<triggerFunctionBackendFunctionFinishRunManuallySuccessFailureParams>
 */
export function triggerFunctionBackendFunctionFinishRunManuallySuccessFailure(params) {
    return { evid: 243, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'leftTreeClickOnAPopUpLink' event (evid: 245)
 *
 * Event description: Click on a link in a pop-up window in the left tree
 *
 * @param {leftTreeClickOnAPopUpLinkParams} params - The parameters for this event
 * @property {string} app_name - app_name
 * @property {string} app_id - app_id
 * @property {number} users_with_write_devmode_count - users_with_write_devmode_count
 * @property {string} builderType - builder_type
 * @property {GUID} visitorId - visitor_id
 * @property {string} ds_type - ds_type
 * @property {GUID} site_id - site_id
 * @property {string} panel_id - panel_id
 * @property {string} appName - app_name
 * @property {number} users_on_site_count - users_on_site_count
 * @property {number} ts - ts
 * @property {string} link_name - displayed linked name in the pop-up
 * @property {string} panel_name - panel_name
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} pageId - page_id
 * @property {string} origin - {section name, sub section name, item name, icon name}}
 * @property {string} token - token
 * @property {number} users_with_devmode_count - users_with_devmode_count
 * @property {string} ver - version
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {string} link_type - {update,  dependency, resource}
 * @property {string} dev_mode - dev_mode
 * @property {string} appId - app_id
 * @property {GUID} esi - esi
 * @property {GUID} msid - msid
 * @property {string} ds_id - ds_id
 * @property {string} related_link - {learn more, update, app market ex. }
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<leftTreeClickOnAPopUpLinkParams>} LogObject<leftTreeClickOnAPopUpLinkParams>
 */
export function leftTreeClickOnAPopUpLink(params) {
    return { evid: 245, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'triggerFunctionValidFunctionInIdePlayButtonShouldAppear' event (evid: 247)
 *
 * Event description: once there is a legit function the event happens
 * export function() {} is legit function
 * the happens Every time the site reloads and there is a valid function.
 * does not reset.
 *
 * @param {triggerFunctionValidFunctionInIdePlayButtonShouldAppearParams} params - The parameters for this event
 * @property {string} app_name - app_name
 * @property {string} app_id - app_id
 * @property {number} users_with_write_devmode_count - users_with_write_devmode_count
 * @property {string} builderType - builder_type
 * @property {GUID} visitorId - visitor_id
 * @property {string} ds_type - ds_type
 * @property {GUID} site_id - site_id
 * @property {string} panel_id - panel_id
 * @property {string} file_name - file_name
 * @property {string} appName - app_name
 * @property {number} users_on_site_count - users_on_site_count
 * @property {number} ts - ts
 * @property {boolean} is_default_function - is the first function in the file is the empty state function
 * @property {string} panel_name - panel_name
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} pageId - page_id
 * @property {string} token - token
 * @property {number} users_with_devmode_count - users_with_devmode_count
 * @property {string} ver - version
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {string} file_type - {js,jsw}
 * @property {string} dev_mode - dev_mode
 * @property {string} appId - app_id
 * @property {GUID} esi - esi
 * @property {GUID} msid - msid
 * @property {string} ds_id - ds_id
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<triggerFunctionValidFunctionInIdePlayButtonShouldAppearParams>} LogObject<triggerFunctionValidFunctionInIdePlayButtonShouldAppearParams>
 */
export function triggerFunctionValidFunctionInIdePlayButtonShouldAppear(params) {
    return { evid: 247, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'dsFetchAfterPageLoadToggleChanged' event (evid: 248)
 *
 * Event description: When a user asks to fetch the DS after the page loads
 *
 * @param {dsFetchAfterPageLoadToggleChangedParams} params - The parameters for this event
 * @property {string} app_name - app_name
 * @property {string} app_id - app_id
 * @property {string} dataset_name - {}
 * @property {number} users_with_write_devmode_count - users_with_write_devmode_count
 * @property {string} builderType - builder_type
 * @property {GUID} visitorId - visitor_id
 * @property {string} ds_type - ds_type
 * @property {GUID} site_id - site_id
 * @property {string} panel_id - panel_id
 * @property {string} appName - app_name
 * @property {number} users_on_site_count - users_on_site_count
 * @property {number} ts - ts
 * @property {string} collection_id - collection_id
 * @property {string} panel_name - panel_name
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} pageId - page_id
 * @property {string} token - token
 * @property {number} users_with_devmode_count - users_with_devmode_count
 * @property {string} ver - version
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {string} dev_mode - dev_mode
 * @property {string} appId - app_id
 * @property {GUID} esi - esi
 * @property {string} collection_name - collection_name
 * @property {GUID} msid - {}
 * @property {string} ds_id - ds_id
 * @property {string} toggle_value - {On,Off}
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<dsFetchAfterPageLoadToggleChangedParams>} LogObject<dsFetchAfterPageLoadToggleChangedParams>
 */
export function dsFetchAfterPageLoadToggleChanged(params) {
    return { evid: 248, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'dataModeOnExistingCollectionsIndicator' event (evid: 249)
 *
 * Event description: When a user did mode on, does he have already db drivers(form,stores,booking and so on...) on his site?.
 * meaning does he have any collection before the data mode on
 *
 * @param {dataModeOnExistingCollectionsIndicatorParams} params - The parameters for this event
 * @property {string} app_name - app_name
 * @property {string} app_id - app_id
 * @property {number} users_with_write_devmode_count - users_with_write_devmode_count
 * @property {string} builderType - builder_type
 * @property {GUID} visitorId - visitor_id
 * @property {string} ds_type - ds_type
 * @property {GUID} site_id - site_id
 * @property {string} panel_id - panel_id
 * @property {string} appName - app_name
 * @property {number} users_on_site_count - users_on_site_count
 * @property {number} ts - ts
 * @property {boolean} is_existing_collection_before_data_on - upon data mode on do the user have any DB drivers - any collection? should happen once when user turn Data mode on
 * @property {string} panel_name - panel_name
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} pageId - page_id
 * @property {string} token - token
 * @property {number} users_with_devmode_count - users_with_devmode_count
 * @property {string} ver - version
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {string} dev_mode - dev_mode
 * @property {string} appId - app_id
 * @property {GUID} esi - esi
 * @property {GUID} msid - {}
 * @property {string} ds_id - ds_id
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<dataModeOnExistingCollectionsIndicatorParams>} LogObject<dataModeOnExistingCollectionsIndicatorParams>
 */
export function dataModeOnExistingCollectionsIndicator(params) {
    return { evid: 249, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'openedEditorWithPlatformApps' event (evid: 25)
 *
 * Event description: will be sent when the editor is loaded and user has apps in the site.
 *
 * @param {openedEditorWithPlatformAppsParams} params - The parameters for this event
 * @property {string} app_name - app_name
 * @property {string} app_id - app_id
 * @property {number} users_with_write_devmode_count - users_with_write_devmode_count
 * @property {string} builderType - builder_type
 * @property {GUID} visitorId - visitor_id
 * @property {string} ds_type - ds_type
 * @property {GUID} site_id - site_id
 * @property {string} panel_id - panel_id
 * @property {string} appName - app_name
 * @property {number} users_on_site_count - users_on_site_count
 * @property {number} ts - ts
 * @property {string} panel_name - panel_name
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} pageId - page_id
 * @property {string} token - token
 * @property {number} users_with_devmode_count - users_with_devmode_count
 * @property {string} ver - version
 * @property {number} number_of_apps - total number of apps in the site
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {string} dev_mode - dev_mode
 * @property {string} appId - app_id
 * @property {GUID} esi - esi
 * @property {GUID} msid - msid
 * @property {string} app_list - JSON with all the apps in the site
 * @property {string} ds_id - ds_id
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<openedEditorWithPlatformAppsParams>} LogObject<openedEditorWithPlatformAppsParams>
 */
export function openedEditorWithPlatformApps(params) {
    return { evid: 25, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'consolePreviewLoggingToolsDropdownClickOrTheExpandedValuesOfItSrc79Evid250' event (evid: 250)
 *
 * Event description: console preview - logging tools dropdown click or the expanded values of it
 *
 * @param {consolePreviewLoggingToolsDropdownClickOrTheExpandedValuesOfItSrc79Evid250Params} params - The parameters for this event
 * @property {string} input_name - input_name
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<consolePreviewLoggingToolsDropdownClickOrTheExpandedValuesOfItSrc79Evid250Params>} LogObject<consolePreviewLoggingToolsDropdownClickOrTheExpandedValuesOfItSrc79Evid250Params>
 */
export function consolePreviewLoggingToolsDropdownClickOrTheExpandedValuesOfItSrc79Evid250(params) {
    return { evid: 250, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'eventHandlerErrorModalSrc79Evid251' event (evid: 251)
 *
 * Event description: trigger when the user interacts with the event handler error modal
 *
 * @param {eventHandlerErrorModalSrc79Evid251Params} params - The parameters for this event
 * @property {string} action - action
 * @property {string} Component_id - Component_id
 * @property {string} Component_type - component_type
 * @property {string} origin - origin
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<eventHandlerErrorModalSrc79Evid251Params>} LogObject<eventHandlerErrorModalSrc79Evid251Params>
 */
export function eventHandlerErrorModalSrc79Evid251(params) {
    return { evid: 251, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'wixLogsFinishLoadingSrc79Evid252' event (evid: 252)
 *
 * Event description: wix logs > finish loading
 * (formerly site events)
 *
 * @param {wixLogsFinishLoadingSrc79Evid252Params} params - The parameters for this event
 * @property {GUID} uuid - uuid
 * @property {GUID} msid - msid
 * @property {number} loading_time - loading_time
 * @property {string} prev_view - prev_view
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<wixLogsFinishLoadingSrc79Evid252Params>} LogObject<wixLogsFinishLoadingSrc79Evid252Params>
 */
export function wixLogsFinishLoadingSrc79Evid252(params) {
    return { evid: 252, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'veloAiAssistantCopyCodeClickedSrc79Evid253' event (evid: 253)
 *
 * Event description: Velo AI assistant insert copy code clicked
 *
 * @param {veloAiAssistantCopyCodeClickedSrc79Evid253Params} params - The parameters for this event
 * @property {string} app_name - app_name
 * @property {string} app_id - app_id
 * @property {number} users_with_write_devmode_count - users_with_write_devmode_count
 * @property {string} builderType - builder_type
 * @property {GUID} visitorId - visitor_id
 * @property {string} ds_type - ds_type
 * @property {GUID} site_id - site_id
 * @property {string} panel_id - panel_id
 * @property {string} appName - app_name
 * @property {number} users_on_site_count - users_on_site_count
 * @property {string} handler_name - {is_clicked, is_double_clicked,is_hovered_on etc}
 * @property {number} ts - ts
 * @property {string} panel_name - panel_name
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} pageId - page_id
 * @property {string} action_desc - {the required action that is described, to be translated to code}
 * @property {string} origin - {editor event hanler, editor css etc}
 * @property {string} token - token
 * @property {number} users_with_devmode_count - users_with_devmode_count
 * @property {string} ver - version
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {string} dev_mode - dev_mode
 * @property {string} appId - app_id
 * @property {GUID} esi - esi
 * @property {string} component_id - {}
 * @property {string} conversation_id - {}
 * @property {string} component_type - {}
 * @property {GUID} msid - msid
 * @property {string} ds_id - ds_id
 * @property {string} code_snippet - code_snippet
 * @property {string} platform - platform
 * @property {string} entryPoint - entry_point
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<veloAiAssistantCopyCodeClickedSrc79Evid253Params>} LogObject<veloAiAssistantCopyCodeClickedSrc79Evid253Params>
 */
export function veloAiAssistantCopyCodeClickedSrc79Evid253(params) {
    return { evid: 253, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'veloAiAssistantPanelInteractionSrc79Evid254' event (evid: 254)
 *
 * Event description: Velo AI assistant panel interaction
 *
 * @param {veloAiAssistantPanelInteractionSrc79Evid254Params} params - The parameters for this event
 * @property {string} app_name - app_name
 * @property {string} app_id - app_id
 * @property {number} users_with_write_devmode_count - users_with_write_devmode_count
 * @property {string} builderType - builder_type
 * @property {GUID} visitorId - visitor_id
 * @property {string} ds_type - ds_type
 * @property {GUID} site_id - site_id
 * @property {string} panel_id - panel_id
 * @property {string} appName - app_name
 * @property {number} users_on_site_count - users_on_site_count
 * @property {string} handler_name - {is_clicked, is_double_clicked,is_hovered_on etc}
 * @property {number} ts - ts
 * @property {string} panel_name - panel_name
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} pageId - page_id
 * @property {string} action_desc - {the required action that is described, to be translated to code}
 * @property {string} origin - {editor event hanler, editor css etc}
 * @property {string} token - token
 * @property {number} users_with_devmode_count - users_with_devmode_count
 * @property {string} ver - version
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {string} dev_mode - dev_mode
 * @property {string} appId - app_id
 * @property {GUID} esi - esi
 * @property {string} component_id - {}
 * @property {string} conversation_id - {}
 * @property {string} component_type - {}
 * @property {GUID} msid - msid
 * @property {string} ds_id - ds_id
 * @property {string} button_name - button_name
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<veloAiAssistantPanelInteractionSrc79Evid254Params>} LogObject<veloAiAssistantPanelInteractionSrc79Evid254Params>
 */
export function veloAiAssistantPanelInteractionSrc79Evid254(params) {
    return { evid: 254, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'cmsUserScrollSrc79Evid255' event (evid: 255)
 *
 * Event description: A generic scroll event for CMS platform
 *
 * @param {cmsUserScrollSrc79Evid255Params} params - The parameters for this event
 * @property {number} percent_scrolled - percent of the page scrolled
 * @property {string} origin - the scroll's origin
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<cmsUserScrollSrc79Evid255Params>} LogObject<cmsUserScrollSrc79Evid255Params>
 */
export function cmsUserScrollSrc79Evid255(params) {
    return { evid: 255, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'veloCertificationStatus' event (evid: 256)
 *
 * Event description: Velo Certification Status
 *
 * @param {veloCertificationStatusParams} params - The parameters for this event
 * @property {string} app_name - app_name
 * @property {string} app_id - app_id
 * @property {number} users_with_write_devmode_count - users_with_write_devmode_count
 * @property {string} builderType - builder_type
 * @property {GUID} session_id - {}
 * @property {GUID} visitorId - visitor_id
 * @property {string} ds_type - ds_type
 * @property {GUID} site_id - site_id
 * @property {string} panel_id - panel_id
 * @property {string} appName - app_name
 * @property {number} users_on_site_count - users_on_site_count
 * @property {GUID} uuid - uuid
 * @property {number} ts - ts
 * @property {string} panel_name - panel_name
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {GUID} client_id - client_id
 * @property {string} pageId - page_id
 * @property {string} token - token
 * @property {number} users_with_devmode_count - users_with_devmode_count
 * @property {string} ver - version
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {string} dev_mode - dev_mode
 * @property {string} appId - app_id
 * @property {GUID} esi - esi
 * @property {GUID} msid - msid
 * @property {string} candidate_uuid - candidate_uuid
 * @property {Url} page_url - {}
 * @property {string} velo_certification_status - {failed_first_test , failed_second_test , project_failed , project_success}
 * @property {string} ds_id - ds_id
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<veloCertificationStatusParams>} LogObject<veloCertificationStatusParams>
 */
export function veloCertificationStatus(params) {
    return { evid: 256, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'veloCertificationTestAnswers' event (evid: 257)
 *
 * Event description: Velo Certification Test Answers
 *
 * @param {veloCertificationTestAnswersParams} params - The parameters for this event
 * @property {string} app_name - app_name
 * @property {string} app_id - app_id
 * @property {number} users_with_write_devmode_count - users_with_write_devmode_count
 * @property {string} builderType - builder_type
 * @property {GUID} session_id - {}
 * @property {GUID} visitorId - visitor_id
 * @property {string} ds_type - ds_type
 * @property {GUID} site_id - site_id
 * @property {string} panel_id - panel_id
 * @property {string} appName - app_name
 * @property {number} users_on_site_count - users_on_site_count
 * @property {GUID} uuid - uuid
 * @property {number} ts - ts
 * @property {string} panel_name - panel_name
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {GUID} client_id - client_id
 * @property {number} velo_certification_test_correct_answears - number of correct answers
 * @property {string} pageId - page_id
 * @property {number} test_number - test number
 * @property {number} velo_certification_test_wrong_answears - number of wrong answers
 * @property {string} token - token
 * @property {number} users_with_devmode_count - users_with_devmode_count
 * @property {string} ver - version
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {string} dev_mode - dev_mode
 * @property {string} appId - app_id
 * @property {GUID} esi - esi
 * @property {GUID} applicant_uuid - the applicant uuid
 * @property {GUID} msid - msid
 * @property {string} candidate_uuid - candidate_uuid
 * @property {Url} page_url - {}
 * @property {string} ds_id - ds_id
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<veloCertificationTestAnswersParams>} LogObject<veloCertificationTestAnswersParams>
 */
export function veloCertificationTestAnswers(params) {
    return { evid: 257, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'functionOfInternalPackageUsedInUserCodeOnLiveSite' event (evid: 258)
 *
 * Event description: sent when a function of internal package library was imported into code on live site  (sent from server bundler).Sent when a function is actually triggered.
 *
 * @param {functionOfInternalPackageUsedInUserCodeOnLiveSiteParams} params - The parameters for this event
 * @property {GUID} _event_hash - event_hash
 * @property {GUID} visitor_id - visitor_id
 * @property {string} app_name - app_name
 * @property {string} app_id - app_id
 * @property {string} exceptionType - {}
 * @property {string} function_name - function name
 * @property {string} builderType - builder_type
 * @property {string} ds_type - ds_type
 * @property {GUID} site_id - site_id
 * @property {string} panel_id - panel_id
 * @property {string} file_name - file name
 * @property {string} appName - app_name
 * @property {number} ts - ts
 * @property {boolean} exception - {true/false}
 * @property {string} internal_package_module_name - internal_package_ module name
 * @property {string} panel_name - panel_name
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} grid_app_id - grid_app_id
 * @property {string} pageId - page_id
 * @property {GUID} vsi - vsi
 * @property {string} token - token
 * @property {string} ver - version
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {string} appId - app_id
 * @property {GUID} esi - esi
 * @property {GUID} msid - msid
 * @property {number} tsp - tsp
 * @property {Url} page_url - page url
 * @property {string} ds_id - ds_id
 * @property {boolean} is_backend - {true/false}
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<functionOfInternalPackageUsedInUserCodeOnLiveSiteParams>} LogObject<functionOfInternalPackageUsedInUserCodeOnLiveSiteParams>
 */
export function functionOfInternalPackageUsedInUserCodeOnLiveSite(params) {
    return { evid: 258, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'internalPackageUsedInUserCodeOnLiveSite' event (evid: 259)
 *
 * Event description: sent when an internal package library was imported into code on live site
 *
 * @param {internalPackageUsedInUserCodeOnLiveSiteParams} params - The parameters for this event
 * @property {GUID} vsi - vsi
 * @property {GUID} _event_hash - event_hash
 * @property {string} error_desc - dsc
 * @property {GUID} visitor_id - visitor_id
 * @property {string} dsc - dsc
 * @property {number} response_time - ts
 * @property {string} app_name - app_name
 * @property {string} app_id - app_id
 * @property {string} exceptionType - {}
 * @property {number} users_with_write_devmode_count - users_with_write_devmode_count
 * @property {string} server - server
 * @property {string} builderType - builder_type
 * @property {GUID} visitorId - visitor_id
 * @property {string} errscp - errscp
 * @property {string} ds_type - ds_type
 * @property {GUID} site_id - site_id
 * @property {string} errn - errn
 * @property {string} panel_id - panel_id
 * @property {string} file_name - file name
 * @property {string} p4 - p4
 * @property {string} majorver - majorver
 * @property {string} appName - app_name
 * @property {number} users_on_site_count - users_on_site_count
 * @property {string} trgt - trgt
 * @property {boolean} isLiveEditor - is_live_editor
 * @property {number} ts - ts
 * @property {boolean} exception - {true/false}
 * @property {string} internal_package_module_name - internal_package_ module name
 * @property {string} __cookies_prefix___wixStatemap - state_map
 * @property {string} panel_name - panel_name
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} error_description_short - error_description_short
 * @property {string} page_id - page_id
 * @property {string} p3 - p3
 * @property {GUID} did - site_id
 * @property {string} grid_app_id - grid_app_id
 * @property {string} pageId - page_id
 * @property {boolean} isSg - is_sg
 * @property {string} p2 - p2
 * @property {string} token - token
 * @property {number} users_with_devmode_count - users_with_devmode_count
 * @property {string} ver - ver
 * @property {number} errc - errc
 * @property {string} stack - stacktrace
 * @property {GUID} metasiteid - msid
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {string} dev_mode - dev_mode
 * @property {number} sev - sev
 * @property {string} appId - app_id
 * @property {GUID} esi - esi
 * @property {number} cat - cat
 * @property {string} errormsg - dsc
 * @property {GUID} __cookies_prefix___wixCIDX - client_id
 * @property {string} p1 - p1
 * @property {GUID} msid - msid
 * @property {number} tsp - tsp
 * @property {Url} page_url - page url
 * @property {number} iss - iss
 * @property {string} ds_id - ds_id
 * @property {boolean} is_backend - {true/false}
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<internalPackageUsedInUserCodeOnLiveSiteParams>} LogObject<internalPackageUsedInUserCodeOnLiveSiteParams>
 */
export function internalPackageUsedInUserCodeOnLiveSite(params) {
    return { evid: 259, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'controllerGfppClicked' event (evid: 26)
 *
 * Event description: Controller GFPP clicked
 *
 * @param {controllerGfppClickedParams} params - The parameters for this event
 * @property {string} app_name - app_name
 * @property {string} app_id - app_id
 * @property {number} users_with_write_devmode_count - users_with_write_devmode_count
 * @property {string} builderType - builder_type
 * @property {GUID} visitorId - visitor_id
 * @property {string} ds_type - ds_type
 * @property {string} action_name - action_name
 * @property {GUID} site_id - site_id
 * @property {string} panel_id - panel_id
 * @property {string} appName - app_name
 * @property {number} users_on_site_count - users_on_site_count
 * @property {number} ts - ts
 * @property {string} panel_name - panel_name
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} controller_type - controller_type
 * @property {string} pageId - page_id
 * @property {string} token - token
 * @property {number} users_with_devmode_count - users_with_devmode_count
 * @property {string} ver - version
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {string} dev_mode - dev_mode
 * @property {string} appId - app_id
 * @property {GUID} esi - esi
 * @property {string} component_id - component_id
 * @property {GUID} msid - msid
 * @property {string} ds_id - ds_id
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<controllerGfppClickedParams>} LogObject<controllerGfppClickedParams>
 */
export function controllerGfppClicked(params) {
    return { evid: 26, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'internalPackageUsedInUserCodeOnLiveSiteSrc79Evid260' event (evid: 260)
 *
 * Event description: sent when an internal package library was imported into code on live site (its not a call to the function but an import in a live site)
 * Sent when the site is being published after code change
 *
 * @param {internalPackageUsedInUserCodeOnLiveSiteSrc79Evid260Params} params - The parameters for this event
 * @property {GUID} _event_hash - event_hash
 * @property {GUID} visitor_id - visitor_id
 * @property {string} app_name - app_name
 * @property {string} app_id - app_id
 * @property {string} exceptionType - {}
 * @property {string} builderType - builder_type
 * @property {string} ds_type - ds_type
 * @property {GUID} site_id - site_id
 * @property {string} panel_id - panel_id
 * @property {string} file_name - file name
 * @property {string} appName - app_name
 * @property {number} ts - ts
 * @property {boolean} exception - {true/false}
 * @property {string} internal_package_module_name - internal_package_ module name
 * @property {string} panel_name - panel_name
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} page_id - page_id
 * @property {string} grid_app_id - grid_app_id
 * @property {string} pageId - page_id
 * @property {GUID} vsi - vsi
 * @property {string} token - token
 * @property {string} ver - version
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {string} appId - app_id
 * @property {GUID} esi - esi
 * @property {GUID} msid - msid
 * @property {number} tsp - tsp
 * @property {Url} page_url - page url
 * @property {string} ds_id - ds_id
 * @property {boolean} is_backend - {true/false}
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<internalPackageUsedInUserCodeOnLiveSiteSrc79Evid260Params>} LogObject<internalPackageUsedInUserCodeOnLiveSiteSrc79Evid260Params>
 */
export function internalPackageUsedInUserCodeOnLiveSiteSrc79Evid260(params) {
    return { evid: 260, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'dataModalCloseModal' event (evid: 261)
 *
 * Event description: click the X button on the data modal
 *
 * @param {dataModalCloseModalParams} params - The parameters for this event
 * @property {string} app_name - app_name
 * @property {string} app_id - app_id
 * @property {number} users_with_write_devmode_count - users_with_write_devmode_count
 * @property {string} builderType - builder_type
 * @property {GUID} visitorId - visitor_id
 * @property {boolean} is_first_data_modal - is this the first data modal the user see
 * @property {string} ds_type - ds_type
 * @property {GUID} site_id - site_id
 * @property {string} panel_id - panel_id
 * @property {string} appName - app_name
 * @property {number} users_on_site_count - users_on_site_count
 * @property {number} ts - ts
 * @property {string} panel_name - panel_name
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} pageId - page_id
 * @property {string} token - token
 * @property {number} users_with_devmode_count - users_with_devmode_count
 * @property {string} ver - version
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {string} dev_mode - dev_mode
 * @property {string} appId - app_id
 * @property {GUID} esi - esi
 * @property {GUID} msid - msid
 * @property {string} ds_id - ds_id
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<dataModalCloseModalParams>} LogObject<dataModalCloseModalParams>
 */
export function dataModalCloseModal(params) {
    return { evid: 261, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'dataModalClickResource' event (evid: 262)
 *
 * Event description: user click any resource link in the data modal
 *
 * @param {dataModalClickResourceParams} params - The parameters for this event
 * @property {string} app_name - app_name
 * @property {string} app_id - app_id
 * @property {number} users_with_write_devmode_count - users_with_write_devmode_count
 * @property {string} builderType - builder_type
 * @property {GUID} visitorId - visitor_id
 * @property {boolean} is_first_data_modal - is this the first data modal the user see
 * @property {string} ds_type - ds_type
 * @property {string} action_name - {learn_more, video_button, question_mark}
 * @property {GUID} site_id - site_id
 * @property {string} panel_id - panel_id
 * @property {string} appName - app_name
 * @property {number} users_on_site_count - users_on_site_count
 * @property {number} ts - ts
 * @property {string} panel_name - panel_name
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} pageId - page_id
 * @property {string} origin - {preset_page, info_page}
 * @property {string} token - token
 * @property {number} users_with_devmode_count - users_with_devmode_count
 * @property {string} ver - version
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {string} dev_mode - dev_mode
 * @property {string} appId - app_id
 * @property {GUID} esi - esi
 * @property {GUID} msid - msid
 * @property {string} preset_id - can be null in case the click is on the main preset modal page
 * @property {string} ds_id - ds_id
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<dataModalClickResourceParams>} LogObject<dataModalClickResourceParams>
 */
export function dataModalClickResource(params) {
    return { evid: 262, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'dataModalHoverInfo' event (evid: 263)
 *
 * Event description: data modal hover info
 *
 * @param {dataModalHoverInfoParams} params - The parameters for this event
 * @property {string} app_name - app_name
 * @property {string} app_id - app_id
 * @property {number} users_with_write_devmode_count - users_with_write_devmode_count
 * @property {string} builderType - builder_type
 * @property {GUID} visitorId - visitor_id
 * @property {boolean} is_first_data_modal - is this the first data modal the user see
 * @property {string} ds_type - ds_type
 * @property {GUID} site_id - site_id
 * @property {string} panel_id - panel_id
 * @property {string} appName - app_name
 * @property {number} users_on_site_count - users_on_site_count
 * @property {number} ts - ts
 * @property {string} panel_name - panel_name
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} pageId - page_id
 * @property {string} token - token
 * @property {number} users_with_devmode_count - users_with_devmode_count
 * @property {string} ver - version
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {string} dev_mode - dev_mode
 * @property {string} appId - app_id
 * @property {GUID} esi - esi
 * @property {GUID} msid - msid
 * @property {string} preset_id - can be null in case the click is on the main preset modal page
 * @property {string} ds_id - ds_id
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<dataModalHoverInfoParams>} LogObject<dataModalHoverInfoParams>
 */
export function dataModalHoverInfo(params) {
    return { evid: 263, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'dataModalClickAddInfoButton' event (evid: 264)
 *
 * Event description: user click on add/info button in the data modal
 *
 * @param {dataModalClickAddInfoButtonParams} params - The parameters for this event
 * @property {string} app_name - app_name
 * @property {string} app_id - app_id
 * @property {number} users_with_write_devmode_count - users_with_write_devmode_count
 * @property {string} builderType - builder_type
 * @property {GUID} visitorId - visitor_id
 * @property {boolean} is_first_data_modal - is this the first data modal the user see
 * @property {string} ds_type - ds_type
 * @property {string} action_name - {more_info, add_preset}
 * @property {GUID} site_id - site_id
 * @property {string} panel_id - panel_id
 * @property {string} appName - app_name
 * @property {number} users_on_site_count - users_on_site_count
 * @property {number} ts - ts
 * @property {string} panel_name - panel_name
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} pageId - page_id
 * @property {string} token - token
 * @property {number} users_with_devmode_count - users_with_devmode_count
 * @property {string} ver - version
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {string} dev_mode - dev_mode
 * @property {string} appId - app_id
 * @property {GUID} esi - esi
 * @property {GUID} msid - msid
 * @property {string} preset_id - preset of the user
 * @property {string} ds_id - ds_id
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<dataModalClickAddInfoButtonParams>} LogObject<dataModalClickAddInfoButtonParams>
 */
export function dataModalClickAddInfoButton(params) {
    return { evid: 264, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'dataModalClickBackButton' event (evid: 265)
 *
 * Event description: user click back to the main data modal page
 *
 * @param {dataModalClickBackButtonParams} params - The parameters for this event
 * @property {string} app_name - app_name
 * @property {string} app_id - app_id
 * @property {number} users_with_write_devmode_count - users_with_write_devmode_count
 * @property {string} builderType - builder_type
 * @property {GUID} visitorId - visitor_id
 * @property {boolean} is_first_data_modal - is this the first data modal the user see
 * @property {string} ds_type - ds_type
 * @property {GUID} site_id - site_id
 * @property {string} panel_id - panel_id
 * @property {string} appName - app_name
 * @property {number} users_on_site_count - users_on_site_count
 * @property {number} ts - ts
 * @property {string} panel_name - panel_name
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} pageId - page_id
 * @property {string} token - token
 * @property {number} users_with_devmode_count - users_with_devmode_count
 * @property {string} ver - version
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {string} dev_mode - dev_mode
 * @property {string} appId - app_id
 * @property {GUID} esi - esi
 * @property {GUID} msid - msid
 * @property {string} preset_id - preset of the user
 * @property {string} ds_id - ds_id
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<dataModalClickBackButtonParams>} LogObject<dataModalClickBackButtonParams>
 */
export function dataModalClickBackButton(params) {
    return { evid: 265, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'dataModalClickAddToSite' event (evid: 266)
 *
 * Event description: when user click add to site in the data modal
 * page_type represent if he added collection/item/both of them
 *
 * @param {dataModalClickAddToSiteParams} params - The parameters for this event
 * @property {string} app_name - app_name
 * @property {string} app_id - app_id
 * @property {number} users_with_write_devmode_count - users_with_write_devmode_count
 * @property {string} builderType - builder_type
 * @property {GUID} visitorId - visitor_id
 * @property {boolean} is_first_data_modal - is this the first data modal the user see
 * @property {string} ds_type - ds_type
 * @property {GUID} site_id - site_id
 * @property {string} panel_id - panel_id
 * @property {string} appName - app_name
 * @property {number} users_on_site_count - users_on_site_count
 * @property {number} ts - ts
 * @property {string} panel_name - panel_name
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} pageId - page_id
 * @property {string} token - token
 * @property {number} users_with_devmode_count - users_with_devmode_count
 * @property {string} ver - version
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {string} dev_mode - dev_mode
 * @property {string} appId - app_id
 * @property {GUID} esi - esi
 * @property {string} page_type - {‘item, collection’ , 'collection', 'item'}
 * @property {GUID} msid - msid
 * @property {string} preset_id - preset of the user
 * @property {string} ds_id - ds_id
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<dataModalClickAddToSiteParams>} LogObject<dataModalClickAddToSiteParams>
 */
export function dataModalClickAddToSite(params) {
    return { evid: 266, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'dataModalCustomizationOfListItem' event (evid: 267)
 *
 * Event description: when user in the data modal enter customziation of preset and toggle on or off the  list/item page of dynamic pages
 *
 * @param {dataModalCustomizationOfListItemParams} params - The parameters for this event
 * @property {string} app_name - app_name
 * @property {string} app_id - app_id
 * @property {number} users_with_write_devmode_count - users_with_write_devmode_count
 * @property {string} builderType - builder_type
 * @property {GUID} visitorId - visitor_id
 * @property {boolean} is_first_data_modal - is this the first data modal the user see
 * @property {string} ds_type - ds_type
 * @property {GUID} site_id - site_id
 * @property {string} panel_id - panel_id
 * @property {string} appName - app_name
 * @property {number} users_on_site_count - users_on_site_count
 * @property {number} ts - ts
 * @property {string} panel_name - panel_name
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} pageId - page_id
 * @property {boolean} status - is it toggle off or on?
 * @property {string} token - token
 * @property {number} users_with_devmode_count - users_with_devmode_count
 * @property {string} ver - version
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {string} dev_mode - dev_mode
 * @property {string} appId - app_id
 * @property {GUID} esi - esi
 * @property {string} page_type - {'collection', 'item'}
 * @property {GUID} msid - msid
 * @property {string} preset_id - preset of the user
 * @property {string} ds_id - ds_id
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<dataModalCustomizationOfListItemParams>} LogObject<dataModalCustomizationOfListItemParams>
 */
export function dataModalCustomizationOfListItem(params) {
    return { evid: 267, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'greenIslandSendCodeClick' event (evid: 268)
 *
 * Event description: Send code to employee number, for start the log in.
 *
 * @param {greenIslandSendCodeClickParams} params - The parameters for this event
 * @property {string} app_name - app_name
 * @property {string} app_id - app_id
 * @property {number} users_with_write_devmode_count - users_with_write_devmode_count
 * @property {string} builderType - builder_type
 * @property {GUID} visitorId - visitor_id
 * @property {string} ds_type - ds_type
 * @property {GUID} site_id - site_id
 * @property {string} panel_id - panel_id
 * @property {string} appName - app_name
 * @property {number} users_on_site_count - users_on_site_count
 * @property {number} ts - ts
 * @property {string} office - The employee's office
 * @property {string} panel_name - panel_name
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} pageId - page_id
 * @property {string} bsi - bsi
 * @property {string} token - token
 * @property {number} users_with_devmode_count - users_with_devmode_count
 * @property {string} ver - version
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {string} dev_mode - dev_mode
 * @property {string} appId - app_id
 * @property {GUID} esi - esi
 * @property {GUID} msid - msid
 * @property {string} ds_id - ds_id
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<greenIslandSendCodeClickParams>} LogObject<greenIslandSendCodeClickParams>
 */
export function greenIslandSendCodeClick(params) {
    return { evid: 268, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'greenIslandSubmitCodeClick' event (evid: 269)
 *
 * Event description: Submit the code to complete log in.
 *
 * @param {greenIslandSubmitCodeClickParams} params - The parameters for this event
 * @property {string} app_name - app_name
 * @property {string} app_id - app_id
 * @property {number} users_with_write_devmode_count - users_with_write_devmode_count
 * @property {string} builderType - builder_type
 * @property {GUID} visitorId - visitor_id
 * @property {string} ds_type - ds_type
 * @property {GUID} site_id - site_id
 * @property {string} panel_id - panel_id
 * @property {string} appName - app_name
 * @property {number} users_on_site_count - users_on_site_count
 * @property {number} ts - ts
 * @property {string} office - The employee's office
 * @property {string} panel_name - panel_name
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {number} verification_response_status - the verification response status(200/401/402)
 * @property {string} pageId - page_id
 * @property {string} bsi - bsi
 * @property {string} token - token
 * @property {number} users_with_devmode_count - users_with_devmode_count
 * @property {string} ver - version
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {string} dev_mode - dev_mode
 * @property {string} appId - app_id
 * @property {GUID} esi - esi
 * @property {GUID} msid - msid
 * @property {string} ds_id - ds_id
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<greenIslandSubmitCodeClickParams>} LogObject<greenIslandSubmitCodeClickParams>
 */
export function greenIslandSubmitCodeClick(params) {
    return { evid: 269, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'dsPanelOpenTriggeredInWorker' event (evid: 27)
 *
 * Event description: DS panel open triggered (in worker)
 *
 * @param {dsPanelOpenTriggeredInWorkerParams} params - The parameters for this event
 * @property {string} app_name - app_name
 * @property {string} app_id - app_id
 * @property {number} users_with_write_devmode_count - users_with_write_devmode_count
 * @property {string} worker_id - worker id
 * @property {string} builderType - builder_type
 * @property {GUID} visitorId - visitor_id
 * @property {string} ds_type - ds_type
 * @property {GUID} site_id - site_id
 * @property {string} panel_id - panel_id
 * @property {string} appName - app_name
 * @property {number} users_on_site_count - users_on_site_count
 * @property {number} ts - ts
 * @property {string} panel_name - panel_name
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} pageId - page_id
 * @property {string} token - token
 * @property {number} users_with_devmode_count - users_with_devmode_count
 * @property {string} ver - version
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {string} dev_mode - dev_mode
 * @property {string} appId - app_id
 * @property {GUID} esi - esi
 * @property {GUID} msid - msid
 * @property {string} ds_id - ds_id
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<dsPanelOpenTriggeredInWorkerParams>} LogObject<dsPanelOpenTriggeredInWorkerParams>
 */
export function dsPanelOpenTriggeredInWorker(params) {
    return { evid: 27, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'greenIslandRegisterForATestClick' event (evid: 270)
 *
 * Event description: Registration for a test
 *
 * @param {greenIslandRegisterForATestClickParams} params - The parameters for this event
 * @property {string} app_name - app_name
 * @property {string} app_id - app_id
 * @property {number} users_with_write_devmode_count - users_with_write_devmode_count
 * @property {string} builderType - builder_type
 * @property {GUID} visitorId - visitor_id
 * @property {string} ds_type - ds_type
 * @property {GUID} site_id - site_id
 * @property {string} panel_id - panel_id
 * @property {string} appName - app_name
 * @property {number} users_on_site_count - users_on_site_count
 * @property {number} ts - ts
 * @property {string} office - The employee's office
 * @property {string} panel_name - panel_name
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} pageId - page_id
 * @property {string} bsi - bsi
 * @property {string} token - token
 * @property {number} users_with_devmode_count - users_with_devmode_count
 * @property {string} ver - version
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {string} dev_mode - dev_mode
 * @property {string} appId - app_id
 * @property {GUID} esi - esi
 * @property {GUID} msid - msid
 * @property {string} ds_id - ds_id
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<greenIslandRegisterForATestClickParams>} LogObject<greenIslandRegisterForATestClickParams>
 */
export function greenIslandRegisterForATestClick(params) {
    return { evid: 270, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'greenIslandUpdateStatusAccessClick' event (evid: 271)
 *
 * Event description: Access to self-update status
 *
 * @param {greenIslandUpdateStatusAccessClickParams} params - The parameters for this event
 * @property {string} app_name - app_name
 * @property {string} app_id - app_id
 * @property {number} users_with_write_devmode_count - users_with_write_devmode_count
 * @property {string} builderType - builder_type
 * @property {GUID} visitorId - visitor_id
 * @property {string} ds_type - ds_type
 * @property {GUID} site_id - site_id
 * @property {string} panel_id - panel_id
 * @property {string} appName - app_name
 * @property {number} users_on_site_count - users_on_site_count
 * @property {number} ts - ts
 * @property {string} office - The employee's office
 * @property {string} panel_name - panel_name
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} pageId - page_id
 * @property {string} bsi - bsi
 * @property {string} token - token
 * @property {number} users_with_devmode_count - users_with_devmode_count
 * @property {string} ver - version
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {string} dev_mode - dev_mode
 * @property {string} appId - app_id
 * @property {GUID} esi - esi
 * @property {GUID} msid - msid
 * @property {string} ds_id - ds_id
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<greenIslandUpdateStatusAccessClickParams>} LogObject<greenIslandUpdateStatusAccessClickParams>
 */
export function greenIslandUpdateStatusAccessClick(params) {
    return { evid: 271, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'copyOfGreenIslandSubmitStatusSelfUpdateClick' event (evid: 272)
 *
 * Event description: submitted the self-status
 * update form
 *
 * @param {copyOfGreenIslandSubmitStatusSelfUpdateClickParams} params - The parameters for this event
 * @property {GUID} vsi - vsi
 * @property {GUID} _event_hash - event_hash
 * @property {string} error_desc - dsc
 * @property {GUID} visitor_id - visitor_id
 * @property {string} dsc - dsc
 * @property {number} response_time - ts
 * @property {string} page_id - page id
 * @property {string} app_name - app_name
 * @property {string} app_id - app_id
 * @property {number} users_with_write_devmode_count - users_with_write_devmode_count
 * @property {string} server - server
 * @property {string} builderType - builder_type
 * @property {string} path - file code name (not full path)
 * @property {GUID} visitorId - visitor_id
 * @property {string} errscp - errscp
 * @property {string} ds_type - ds_type
 * @property {GUID} site_id - site_id
 * @property {string} errn - errn
 * @property {string} panel_id - panel_id
 * @property {string} file_name - file_name
 * @property {string} p4 - p4
 * @property {string} majorver - majorver
 * @property {string} appName - app_name
 * @property {number} users_on_site_count - users_on_site_count
 * @property {string} trgt - trgt
 * @property {string} running_environment - site / preview
 * @property {boolean} isLiveEditor - is_live_editor
 * @property {string} file_code - file_code
 * @property {string} code_app_id - code_app_id
 * @property {number} ts - ts
 * @property {number} tsn - tsn
 * @property {string} __cookies_prefix___wixStatemap - state_map
 * @property {string} panel_name - panel_name
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} error_description_short - error_description_short
 * @property {string} p3 - p3
 * @property {GUID} did - site_id
 * @property {string} pageId - page id
 * @property {boolean} isSg - is_sg
 * @property {string} p2 - p2
 * @property {string} token - token
 * @property {number} users_with_devmode_count - users_with_devmode_count
 * @property {string} ver - ver
 * @property {number} errc - errc
 * @property {string} stack - stacktrace
 * @property {GUID} metasiteid - msid
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {string} dev_mode - dev_mode
 * @property {number} sev - sev
 * @property {string} appId - app_id
 * @property {GUID} esi - esi
 * @property {number} cat - cat
 * @property {string} errormsg - dsc
 * @property {GUID} __cookies_prefix___wixCIDX - client_id
 * @property {string} p1 - p1
 * @property {GUID} msid - msid
 * @property {number} tsp - tsp
 * @property {Url} page_url - page url
 * @property {number} iss - iss
 * @property {string} ds_id - ds_id
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<copyOfGreenIslandSubmitStatusSelfUpdateClickParams>} LogObject<copyOfGreenIslandSubmitStatusSelfUpdateClickParams>
 */
export function copyOfGreenIslandSubmitStatusSelfUpdateClick(params) {
    return { evid: 272, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'copyOfCopyOfGreenIslandStatusChange' event (evid: 273)
 *
 * Event description: Status change in the system
 *
 * @param {copyOfCopyOfGreenIslandStatusChangeParams} params - The parameters for this event
 * @property {string} app_name - app_name
 * @property {string} app_id - app_id
 * @property {number} users_with_write_devmode_count - users_with_write_devmode_count
 * @property {string} builderType - builder_type
 * @property {GUID} visitorId - visitor_id
 * @property {boolean} is_first_self_update - is first self update- at first time the user can accesses out of app
 * @property {string} ds_type - ds_type
 * @property {GUID} site_id - site_id
 * @property {string} panel_id - panel_id
 * @property {string} appName - app_name
 * @property {number} users_on_site_count - users_on_site_count
 * @property {number} ts - ts
 * @property {string} office - The employee's office
 * @property {string} panel_name - panel_name
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} pageId - page_id
 * @property {string} origin - the reason of changing employee status
 * @property {string} token - token
 * @property {number} users_with_devmode_count - users_with_devmode_count
 * @property {string} ver - version
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {string} dev_mode - dev_mode
 * @property {string} appId - app_id
 * @property {GUID} esi - esi
 * @property {string} employee_new_status - The new  status of employee (Covid wise)
 * @property {GUID} msid - msid
 * @property {string} ds_id - ds_id
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<copyOfCopyOfGreenIslandStatusChangeParams>} LogObject<copyOfCopyOfGreenIslandStatusChangeParams>
 */
export function copyOfCopyOfGreenIslandStatusChange(params) {
    return { evid: 273, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'googleCloudLogsFinishLoadingSrc79Evid274' event (evid: 274)
 *
 * Event description: google cloud cloud> finish loading
 *
 *
 * @param {googleCloudLogsFinishLoadingSrc79Evid274Params} params - The parameters for this event
 * @property {GUID} uuid - uuid
 * @property {GUID} msid - msid
 * @property {number} loading_time - loading_time
 * @property {string} prev_view - prev_view
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<googleCloudLogsFinishLoadingSrc79Evid274Params>} LogObject<googleCloudLogsFinishLoadingSrc79Evid274Params>
 */
export function googleCloudLogsFinishLoadingSrc79Evid274(params) {
    return { evid: 274, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'connectExternalLoggingToolFinishLoadingSrc79Evid275' event (evid: 275)
 *
 * Event description: Connect external logging tool> finish loading
 *
 *
 * @param {connectExternalLoggingToolFinishLoadingSrc79Evid275Params} params - The parameters for this event
 * @property {GUID} uuid - uuid
 * @property {GUID} msid - msid
 * @property {number} loading_time - loading_time
 * @property {string} prev_view - prev_view
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<connectExternalLoggingToolFinishLoadingSrc79Evid275Params>} LogObject<connectExternalLoggingToolFinishLoadingSrc79Evid275Params>
 */
export function connectExternalLoggingToolFinishLoadingSrc79Evid275(params) {
    return { evid: 275, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'developerConsoleShowsInPreviewSrc79Evid276' event (evid: 276)
 *
 * Event description: developer console shows in preview
 *
 * @param {developerConsoleShowsInPreviewSrc79Evid276Params} params - The parameters for this event
 * @property {GUID} uuid - uuid
 * @property {GUID} msid - msid
 * @property {number} loading_time - loading_time
 * @property {string} prev_view - prev_view
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<developerConsoleShowsInPreviewSrc79Evid276Params>} LogObject<developerConsoleShowsInPreviewSrc79Evid276Params>
 */
export function developerConsoleShowsInPreviewSrc79Evid276(params) {
    return { evid: 276, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'internalPackageUsedInUserCodeOnPreview' event (evid: 278)
 *
 * Event description: sent when an Internal package library was imported into code on preview (velo package/private package/spi). (it's not a call to the function but an import in a live site)
 *
 * @param {internalPackageUsedInUserCodeOnPreviewParams} params - The parameters for this event
 * @property {GUID} _event_hash - event_hash
 * @property {GUID} visitor_id - visitor_id
 * @property {string} app_name - app_name
 * @property {string} app_id - app_id
 * @property {string} exceptionType - {}
 * @property {string} builderType - builder_type
 * @property {string} ds_type - ds_type
 * @property {GUID} site_id - site_id
 * @property {string} panel_id - panel_id
 * @property {string} file_name - file name
 * @property {string} appName - app_name
 * @property {number} ts - ts
 * @property {boolean} exception - {true/false}
 * @property {string} internal_package_module_name - internal_package_ module name
 * @property {string} panel_name - panel_name
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} page_id - page id
 * @property {string} grid_app_id - grid_app_id
 * @property {string} pageId - page_id
 * @property {GUID} vsi - vsi
 * @property {string} token - token
 * @property {string} ver - version
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {string} appId - app_id
 * @property {GUID} esi - esi
 * @property {GUID} msid - msid
 * @property {number} tsp - tsp
 * @property {Url} page_url - page url
 * @property {string} ds_id - ds_id
 * @property {boolean} is_backend - {true/false}
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<internalPackageUsedInUserCodeOnPreviewParams>} LogObject<internalPackageUsedInUserCodeOnPreviewParams>
 */
export function internalPackageUsedInUserCodeOnPreview(params) {
    return { evid: 278, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'functionOfInternalPackageUsedInUserCodeOnPreview' event (evid: 279)
 *
 * Event description: sent when a function of Internal package library was imported into code on preview (velo package/private package/spi). Sent when a function is actually triggered.
 *
 * @param {functionOfInternalPackageUsedInUserCodeOnPreviewParams} params - The parameters for this event
 * @property {GUID} _event_hash - event_hash
 * @property {GUID} visitor_id - visitor_id
 * @property {string} app_name - app_name
 * @property {string} app_id - app_id
 * @property {string} exceptionType - {}
 * @property {string} function_name - function name
 * @property {string} builderType - builder_type
 * @property {string} ds_type - ds_type
 * @property {GUID} site_id - site_id
 * @property {string} panel_id - panel_id
 * @property {string} file_name - file name
 * @property {string} appName - app_name
 * @property {number} ts - ts
 * @property {boolean} exception - {true/false}
 * @property {string} internal_package_module_name - internal_package_ module name
 * @property {string} panel_name - panel_name
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} page_id - page id
 * @property {string} grid_app_id - grid_app_id
 * @property {string} pageId - page_id
 * @property {GUID} vsi - vsi
 * @property {string} token - token
 * @property {string} ver - version
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {string} appId - app_id
 * @property {GUID} esi - esi
 * @property {GUID} msid - msid
 * @property {number} tsp - tsp
 * @property {Url} page_url - page url
 * @property {string} ds_id - ds_id
 * @property {boolean} is_backend - {true/false}
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<functionOfInternalPackageUsedInUserCodeOnPreviewParams>} LogObject<functionOfInternalPackageUsedInUserCodeOnPreviewParams>
 */
export function functionOfInternalPackageUsedInUserCodeOnPreview(params) {
    return { evid: 279, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'dsPanelLoadedSuccessfully' event (evid: 28)
 *
 * Event description: DataSet panel loaded successfully
 *
 * @param {dsPanelLoadedSuccessfullyParams} params - The parameters for this event
 * @property {string} app_name - app_name
 * @property {string} app_id - app_id
 * @property {number} duration - duration (in milliseconds)
 * @property {number} users_with_write_devmode_count - users_with_write_devmode_count
 * @property {string} builderType - builder_type
 * @property {GUID} visitorId - visitor_id
 * @property {string} ds_type - ds_type
 * @property {GUID} site_id - site_id
 * @property {string} panel_id - panel_id
 * @property {string} appName - app_name
 * @property {number} users_on_site_count - users_on_site_count
 * @property {number} ts - ts
 * @property {string} panel_name - panel_name
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} pageId - page_id
 * @property {string} origin - origin of opening the DS panel {e.g gfpp,dp_settings}
 * @property {string} token - token
 * @property {number} users_with_devmode_count - users_with_devmode_count
 * @property {string} ver - version
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {string} dev_mode - dev_mode
 * @property {string} appId - app_id
 * @property {GUID} esi - esi
 * @property {GUID} msid - msid
 * @property {string} ds_id - ds_id
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<dsPanelLoadedSuccessfullyParams>} LogObject<dsPanelLoadedSuccessfullyParams>
 */
export function dsPanelLoadedSuccessfully(params) {
    return { evid: 28, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'fetchApiUsedInUserCodeError' event (evid: 280)
 *
 * Event description: sent when a fetch api in user code is invalid
 *
 * @param {fetchApiUsedInUserCodeErrorParams} params - The parameters for this event
 * @property {GUID} _event_hash - event_hash
 * @property {GUID} visitor_id - visitor_id
 * @property {string} app_name - app_name
 * @property {string} app_id - app_id
 * @property {string} exceptionType - {}
 * @property {string} builderType - builder_type
 * @property {string} ds_type - ds_type
 * @property {GUID} site_id - site_id
 * @property {string} panel_id - panel_id
 * @property {string} file_name - file name
 * @property {string} appName - app_name
 * @property {number} ts - ts
 * @property {boolean} exception - {true/false}
 * @property {string} fetch_api_name - fetch_api_name
 * @property {string} panel_name - panel_name
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} page_id - page_id
 * @property {string} grid_app_id - grid_app_id
 * @property {string} pageId - page_id
 * @property {GUID} vsi - vsi
 * @property {string} token - token
 * @property {string} ver - version
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {string} appId - app_id
 * @property {GUID} esi - esi
 * @property {GUID} msid - msid
 * @property {number} tsp - tsp
 * @property {Url} page_url - page url
 * @property {string} ds_id - ds_id
 * @property {boolean} is_backend - {true/false}
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<fetchApiUsedInUserCodeErrorParams>} LogObject<fetchApiUsedInUserCodeErrorParams>
 */
export function fetchApiUsedInUserCodeError(params) {
    return { evid: 280, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'loggingToolsDropdownClickSrc79Evid281' event (evid: 281)
 *
 * Event description: logs tool dropdown click or the expanded values of it
 *
 * @param {loggingToolsDropdownClickSrc79Evid281Params} params - The parameters for this event
 * @property {string} input_name - logs_tools/ connect_to_go/ connect_to_3rd
 * @property {string} 'environment ' - bizm_site_events, preview_console, TB_function_tool
 * @property {string} action_origin - bizm_site_events, preview_console, TB_function_tool
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<loggingToolsDropdownClickSrc79Evid281Params>} LogObject<loggingToolsDropdownClickSrc79Evid281Params>
 */
export function loggingToolsDropdownClickSrc79Evid281(params) {
    return { evid: 281, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'leftTreeClickOnGitHubSignIn' event (evid: 282)
 *
 * Event description: Click on GitHub Sign In
 *
 * @param {leftTreeClickOnGitHubSignInParams} params - The parameters for this event
 * @property {string} app_name - app_name
 * @property {string} app_id - app_id
 * @property {number} users_with_write_devmode_count - users_with_write_devmode_count
 * @property {string} builderType - builder_type
 * @property {GUID} visitorId - visitor_id
 * @property {string} section_name - section_name
 * @property {string} ds_type - ds_type
 * @property {GUID} site_id - site_id
 * @property {string} panel_id - panel_id
 * @property {string} appName - app_name
 * @property {number} users_on_site_count - users_on_site_count
 * @property {number} ts - ts
 * @property {string} panel_name - panel_name
 * @property {string} viewmode - viewmode
 * @property {string} input_name - deprecated
 * @property {string} pageId - page_id
 * @property {string} token - token
 * @property {number} users_with_devmode_count - users_with_devmode_count
 * @property {string} ver - version
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {string} sub_section - sub_section
 * @property {string} dev_mode - dev_mode
 * @property {string} appId - app_id
 * @property {GUID} esi - esi
 * @property {GUID} msid - msid
 * @property {string} ds_id - ds_id
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<leftTreeClickOnGitHubSignInParams>} LogObject<leftTreeClickOnGitHubSignInParams>
 */
export function leftTreeClickOnGitHubSignIn(params) {
    return { evid: 282, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'veloAiAssistantScrollDownArrowClickSrc79Evid285' event (evid: 285)
 *
 * Event description: Velo AI assistant scroll down arrow click
 *
 * @param {veloAiAssistantScrollDownArrowClickSrc79Evid285Params} params - The parameters for this event
 * @property {string} app_name - app_name
 * @property {string} app_id - app_id
 * @property {number} users_with_write_devmode_count - users_with_write_devmode_count
 * @property {string} builderType - builder_type
 * @property {GUID} visitorId - visitor_id
 * @property {string} ds_type - ds_type
 * @property {GUID} site_id - site_id
 * @property {string} panel_id - panel_id
 * @property {string} appName - app_name
 * @property {number} users_on_site_count - users_on_site_count
 * @property {string} handler_name - {is_clicked, is_double_clicked,is_hovered_on etc}
 * @property {number} ts - ts
 * @property {string} panel_name - panel_name
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} pageId - page_id
 * @property {string} action_desc - {the required action that is described, to be translated to code}
 * @property {string} origin - {editor event hanler, editor css etc}
 * @property {string} token - token
 * @property {number} users_with_devmode_count - users_with_devmode_count
 * @property {string} ver - version
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {string} dev_mode - dev_mode
 * @property {string} appId - app_id
 * @property {GUID} esi - esi
 * @property {string} component_id - {}
 * @property {string} conversation_id - {}
 * @property {string} component_type - {}
 * @property {GUID} msid - msid
 * @property {string} ds_id - ds_id
 * @property {string} code_snippet - code_snippet
 * @property {string} platform - platform
 * @property {string} entryPoint - entry_point
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<veloAiAssistantScrollDownArrowClickSrc79Evid285Params>} LogObject<veloAiAssistantScrollDownArrowClickSrc79Evid285Params>
 */
export function veloAiAssistantScrollDownArrowClickSrc79Evid285(params) {
    return { evid: 285, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'leftTreeClickOnGitHubLearnMore' event (evid: 287)
 *
 * Event description: [Deprecated] - Click on GitHub Learn more.
 *
 * @param {leftTreeClickOnGitHubLearnMoreParams} params - The parameters for this event
 * @property {string} app_name - app_name
 * @property {string} app_id - app_id
 * @property {number} users_with_write_devmode_count - users_with_write_devmode_count
 * @property {string} builderType - builder_type
 * @property {GUID} visitorId - visitor_id
 * @property {string} section_name - section_name
 * @property {string} ds_type - ds_type
 * @property {GUID} site_id - site_id
 * @property {string} panel_id - panel_id
 * @property {string} appName - app_name
 * @property {number} users_on_site_count - users_on_site_count
 * @property {number} ts - ts
 * @property {string} panel_name - panel_name
 * @property {string} viewmode - viewmode
 * @property {string} input_name - deprecated
 * @property {string} pageId - page_id
 * @property {string} token - token
 * @property {number} users_with_devmode_count - users_with_devmode_count
 * @property {string} ver - version
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {string} sub_section - sub_section
 * @property {string} dev_mode - dev_mode
 * @property {string} appId - app_id
 * @property {GUID} esi - esi
 * @property {GUID} msid - msid
 * @property {string} ds_id - ds_id
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<leftTreeClickOnGitHubLearnMoreParams>} LogObject<leftTreeClickOnGitHubLearnMoreParams>
 */
export function leftTreeClickOnGitHubLearnMore(params) {
    return { evid: 287, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'ideFunctionPlayButtonAdded' event (evid: 289)
 *
 * Event description: IDE - function Play button added
 *
 * @param {ideFunctionPlayButtonAddedParams} params - The parameters for this event
 * @property {string} app_name - app_name
 * @property {string} app_id - app_id
 * @property {number} users_with_write_devmode_count - users_with_write_devmode_count
 * @property {string} builderType - builder_type
 * @property {GUID} visitorId - deprecated
 * @property {string} ds_type - ds_type
 * @property {GUID} site_id - site_id
 * @property {string} panel_id - panel_id
 * @property {string} file_name - file_name
 * @property {string} appName - app_name
 * @property {number} users_on_site_count - users_on_site_count
 * @property {GUID} uuid - uuid
 * @property {number} ts - ts
 * @property {string} panel_name - panel_name
 * @property {string} viewmode - viewmode
 * @property {string} input_name - deprecated
 * @property {string} pageId - page_id
 * @property {string} user_input - the characters/word the user writes
 * @property {number} character_count - number of characters the user write
 * @property {string} origin - autocomplete panel opened auto/manual
 * @property {string} chosenOption - Content of the selected item
 * @property {string} token - token
 * @property {number} users_with_devmode_count - users_with_devmode_count
 * @property {string} ver - version
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {string} file_path - file_path
 * @property {string} category - item category {'Templates', 'Javascript', 'Keywords' etc..}
 * @property {string} panel_type - panel_type
 * @property {string} dev_mode - dev_mode
 * @property {string} appId - app_id
 * @property {GUID} esi - esi
 * @property {GUID} msid - msid
 * @property {string} ds_id - ds_id
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<ideFunctionPlayButtonAddedParams>} LogObject<ideFunctionPlayButtonAddedParams>
 */
export function ideFunctionPlayButtonAdded(params) {
    return { evid: 289, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'dsCollectionSelected' event (evid: 29)
 *
 * Event description: User connects dataset to collection
 *
 * @param {dsCollectionSelectedParams} params - The parameters for this event
 * @property {string} c1 - collection id
 * @property {string} app_name - app_name
 * @property {string} app_id - app_id
 * @property {number} users_with_write_devmode_count - users_with_write_devmode_count
 * @property {string} builderType - builder_type
 * @property {GUID} visitorId - visitor_id
 * @property {string} ds_type - ds_type
 * @property {GUID} site_id - site_id
 * @property {string} panel_id - panel_id
 * @property {string} appName - app_name
 * @property {number} users_on_site_count - users_on_site_count
 * @property {number} ts - ts
 * @property {string} collection_id - collection id
 * @property {string} collection_origin - one of: 'manual', 'external', 'preset', 'template'
 * @property {string} panel_name - panel_name
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} pageId - page_id
 * @property {string} token - token
 * @property {number} users_with_devmode_count - users_with_devmode_count
 * @property {string} ver - version
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {string} mode - READ, WRITE, READ_WRITE
 * @property {string} dev_mode - dev_mode
 * @property {string} appId - app_id
 * @property {GUID} esi - esi
 * @property {boolean} is_single_item_collection - (true/false)
 * @property {string} collection_name - collection_name
 * @property {GUID} msid - msid
 * @property {string} ds_id - ds_id
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<dsCollectionSelectedParams>} LogObject<dsCollectionSelectedParams>
 */
export function dsCollectionSelected(params) {
    return { evid: 29, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'dispatcherRequestThrottling' event (evid: 2900)
 *
 * Event description: Dispatcher Request Throttling from com.wixpress.wixcode.wix-code-public-dispatcher for backend analytics
 *
 * @param {dispatcherRequestThrottlingParams} params - The parameters for this event
 * @property {string} renderingFlow - Rendering flow
 * @property {string} isFirstRequest - is_first_request
 * @property {number} duration - duration
 * @property {string} runtimeFunctionExecutionDuration - runtimeFunctionExecutionDuration
 * @property {string} source - source
 * @property {string} path - path
 * @property {string} rawError - the raw error the dispatcher returns
 * @property {string} error_origin - specifies the origin of error. e.g. user-code, system etc
 * @property {string} nodeVersion - nodeVersion
 * @property {string} exceptionName - exceptionName
 * @property {string} code_app_id - Deprecated
 * @property {string} result - result
 * @property {string} cloudProvider - cloudProvider
 * @property {string} segment - segment
 * @property {string} user_agent - user_agent
 * @property {string} runtimeResultMappingDuration - runtimeResultMappingDuration
 * @property {string} gridAppId - gridAppId
 * @property {number} runtimeDuration - runtime_duration
 * @property {string} grid_app_id - Deprecated
 * @property {string} runtimeBuildEnvironmentDuration - runtimeBuildEnvironmentDuration
 * @property {string} runtimeExtractModulePathAndFunctionNameDuration - runtimeExtractModulePathAndFunctionNameDuration
 * @property {string} exceptionMessage - exception_message
 * @property {string} runtimeAuthorizeDuration - runtimeAuthorizeDuration
 * @property {string} elementory_response_headers - elementory_response_headers
 * @property {string} runtimeFunctionLoadDuration - runtimeFunctionLoadDuration
 * @property {number} coderepoDuration - coderepo_duration
 * @property {string} eType - event type
 * @property {boolean} is_cache_hit - indicates if the request was served from cache
 * @property {string} runtimeArgumentsParseDuration - runtimeArgumentsParseDuration
 * @property {number} supervisorDuration - indicate the time since request got to supervisor, proxied to runtime and returned back
 * @property {string} elementory_etype - elementory error type
 * @property {number} wixExecutorDuration - executor duration
 * @property {number} inGridDuration - ingrid_duration
 * @property {GUID} msid - msid
 * @property {number} error_code - error_code
 * @property {string} requestOrigin - code_package/ site_code
 * @property {string} sitePlan - site_plan
 * @property {string} fileName - file_name
 * @return {LogObject<dispatcherRequestThrottlingParams>} LogObject<dispatcherRequestThrottlingParams>
 */
export function dispatcherRequestThrottling(params) {
    return { evid: 2900, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'dispatcherRuntimeTimeout' event (evid: 2901)
 *
 * Event description: Dispatcher runtime timeout from com.wixpress.wixcode.wix-code-public-dispatcher for backend analytics
 *
 * @param {dispatcherRuntimeTimeoutParams} params - The parameters for this event
 * @property {string} renderingFlow - Rendering flow
 * @property {string} isFirstRequest - is_first_request
 * @property {number} duration - total duration of the request
 * @property {string} runtimeFunctionExecutionDuration - runtimeFunctionExecutionDuration
 * @property {string} source - source
 * @property {string} path - path
 * @property {string} error_origin - specifies the origin of error. e.g. user-code, system etc
 * @property {string} nodeVersion - nodeVersion
 * @property {string} code_app_id - Deprecated
 * @property {string} cloudProvider - cloudProvider
 * @property {string} segment - segment
 * @property {string} user_agent - user_agent
 * @property {string} runtimeResultMappingDuration - runtime_result_mapping_duration
 * @property {string} gridAppId - Deprecated
 * @property {number} runtimeDuration - runtime_duration
 * @property {string} grid_app_id - grid_app_id
 * @property {string} runtimeBuildEnvironmentDuration - runtime_build_environment_duration
 * @property {string} runtimeExtractModulePathAndFunctionNameDuration - runtimeExtractModulePathAndFunctionNameDuration
 * @property {string} runtimeAuthorizeDuration - runtime_authorize_duration
 * @property {string} runtimeFunctionLoadDuration - runtime_function_load_duration
 * @property {number} coderepoDuration - coderepo_duration
 * @property {string} eType - event type
 * @property {boolean} is_cache_hit - indicates if the request was served from cache
 * @property {string} runtimeArgumentsParseDuration - runtime_arguments_parse_duration
 * @property {number} supervisorDuration - supervisor_duration
 * @property {string} elementory_etype - elementory_etype
 * @property {number} wixExecutorDuration - executor duration
 * @property {number} inGridDuration - ingrid_duration
 * @property {GUID} msid - msid
 * @property {number} error_code - error_code
 * @property {string} requestOrigin - request_origin
 * @property {string} sitePlan - site plan
 * @property {string} fileName - file_name
 * @property {string} result - Success or not
 * @return {LogObject<dispatcherRuntimeTimeoutParams>} LogObject<dispatcherRuntimeTimeoutParams>
 */
export function dispatcherRuntimeTimeout(params) {
    return { evid: 2901, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'eventsDispatcherSrc79Evid2922' event (evid: 2922)
 *
 * Event description: Events Dispatcher
 *
 * @param {eventsDispatcherSrc79Evid2922Params} params - The parameters for this event
 * @property {string} isFirstRequest - is_first_request
 * @property {number} duration - total duration of the request
 * @property {string} source - source
 * @property {string} path - path
 * @property {string} error_origin - specifies the origin of error. e.g. user-code, system etc
 * @property {string} nodeVersion - nodeVersion
 * @property {string} code_app_id - Deprecated
 * @property {string} cloudProvider - cloudProvider
 * @property {string} user_agent - user_agent
 * @property {string} gridAppId - Deprecated
 * @property {string} grid_app_id - grid_app_id
 * @property {string} runtimeAuthorizeDuration - runtime_authorize_duration
 * @property {string} runtimeFunctionLoadDuration - runtime_function_load_duration
 * @property {number} coderepoDuration - coderepo_duration
 * @property {string} eType - event type
 * @property {boolean} is_cache_hit - indicates if the request was served from cache
 * @property {string} runtimeArgumentsParseDuration - runtime_arguments_parse_duration
 * @property {number} supervisorDuration - supervisor_duration
 * @property {string} elementory_etype - elementory_etype
 * @property {number} wixExecutorDuration - executor duration
 * @property {number} inGridDuration - ingrid_duration
 * @property {GUID} msid - msid
 * @property {number} error_code - error_code
 * @property {string} requestOrigin - request_origin
 * @property {string} sitePlan - site plan
 * @property {string} result - Success or not
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<eventsDispatcherSrc79Evid2922Params>} LogObject<eventsDispatcherSrc79Evid2922Params>
 */
export function eventsDispatcherSrc79Evid2922(params) {
    return { evid: 2922, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'scheduledTasksDispatcherSrc79Evid2923' event (evid: 2923)
 *
 * Event description: Scheduled Tasks Dispatcher
 *
 * @param {scheduledTasksDispatcherSrc79Evid2923Params} params - The parameters for this event
 * @property {string} isFirstRequest - is_first_request
 * @property {number} duration - total duration of the request
 * @property {string} source - source
 * @property {string} path - path
 * @property {string} error_origin - specifies the origin of error. e.g. user-code, system etc
 * @property {string} nodeVersion - nodeVersion
 * @property {string} code_app_id - Deprecated
 * @property {string} cloudProvider - cloudProvider
 * @property {string} user_agent - user_agent
 * @property {string} gridAppId - Deprecated
 * @property {string} grid_app_id - grid_app_id
 * @property {string} runtimeAuthorizeDuration - runtime_authorize_duration
 * @property {string} runtimeFunctionLoadDuration - runtime_function_load_duration
 * @property {number} coderepoDuration - coderepo_duration
 * @property {string} eType - event type
 * @property {boolean} is_cache_hit - indicates if the request was served from cache
 * @property {string} runtimeArgumentsParseDuration - runtime_arguments_parse_duration
 * @property {number} supervisorDuration - supervisor_duration
 * @property {string} elementory_etype - elementory_etype
 * @property {number} wixExecutorDuration - executor duration
 * @property {number} inGridDuration - ingrid_duration
 * @property {GUID} msid - msid
 * @property {number} error_code - error_code
 * @property {string} requestOrigin - request_origin
 * @property {string} sitePlan - site plan
 * @property {string} result - Success or not
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<scheduledTasksDispatcherSrc79Evid2923Params>} LogObject<scheduledTasksDispatcherSrc79Evid2923Params>
 */
export function scheduledTasksDispatcherSrc79Evid2923(params) {
    return { evid: 2923, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'grpcDispatcherPluginsSrc79Evid2924' event (evid: 2924)
 *
 * Event description: GRPC Dispatcher - Plugins
 *
 * @param {grpcDispatcherPluginsSrc79Evid2924Params} params - The parameters for this event
 * @property {string} isFirstRequest - is_first_request
 * @property {number} duration - total duration of the request
 * @property {string} source - source
 * @property {string} path - path
 * @property {string} error_origin - specifies the origin of error. e.g. user-code, system etc
 * @property {string} nodeVersion - nodeVersion
 * @property {string} code_app_id - Deprecated
 * @property {string} cloudProvider - cloudProvider
 * @property {string} user_agent - user_agent
 * @property {string} gridAppId - Deprecated
 * @property {string} grid_app_id - grid_app_id
 * @property {string} runtimeAuthorizeDuration - runtime_authorize_duration
 * @property {string} runtimeFunctionLoadDuration - runtime_function_load_duration
 * @property {number} coderepoDuration - coderepo_duration
 * @property {string} eType - event type
 * @property {boolean} is_cache_hit - indicates if the request was served from cache
 * @property {string} runtimeArgumentsParseDuration - runtime_arguments_parse_duration
 * @property {number} supervisorDuration - supervisor_duration
 * @property {string} elementory_etype - elementory_etype
 * @property {number} wixExecutorDuration - executor duration
 * @property {number} inGridDuration - ingrid_duration
 * @property {GUID} msid - msid
 * @property {number} error_code - error_code
 * @property {string} requestOrigin - request_origin
 * @property {string} sitePlan - site plan
 * @property {string} result - Success or not
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<grpcDispatcherPluginsSrc79Evid2924Params>} LogObject<grpcDispatcherPluginsSrc79Evid2924Params>
 */
export function grpcDispatcherPluginsSrc79Evid2924(params) {
    return { evid: 2924, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'grpcDispatcherSpiSrc79Evid2925' event (evid: 2925)
 *
 * Event description: GRPC Dispatcher - Spi
 *
 * @param {grpcDispatcherSpiSrc79Evid2925Params} params - The parameters for this event
 * @property {string} isFirstRequest - is_first_request
 * @property {number} duration - total duration of the request
 * @property {string} source - source
 * @property {string} path - path
 * @property {string} error_origin - specifies the origin of error. e.g. user-code, system etc
 * @property {string} nodeVersion - nodeVersion
 * @property {string} code_app_id - Deprecated
 * @property {string} cloudProvider - cloudProvider
 * @property {string} user_agent - user_agent
 * @property {string} gridAppId - Deprecated
 * @property {string} grid_app_id - grid_app_id
 * @property {string} runtimeAuthorizeDuration - runtime_authorize_duration
 * @property {string} runtimeFunctionLoadDuration - runtime_function_load_duration
 * @property {number} coderepoDuration - coderepo_duration
 * @property {string} eType - event type
 * @property {boolean} is_cache_hit - indicates if the request was served from cache
 * @property {string} runtimeArgumentsParseDuration - runtime_arguments_parse_duration
 * @property {number} supervisorDuration - supervisor_duration
 * @property {string} elementory_etype - elementory_etype
 * @property {number} wixExecutorDuration - executor duration
 * @property {number} inGridDuration - ingrid_duration
 * @property {GUID} msid - msid
 * @property {number} error_code - error_code
 * @property {string} requestOrigin - request_origin
 * @property {string} sitePlan - site plan
 * @property {string} result - Success or not
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<grpcDispatcherSpiSrc79Evid2925Params>} LogObject<grpcDispatcherSpiSrc79Evid2925Params>
 */
export function grpcDispatcherSpiSrc79Evid2925(params) {
    return { evid: 2925, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'addCollectionClicked' event (evid: 3)
 *
 * Event description: Add collection clicked
 *
 * @param {addCollectionClickedParams} params - The parameters for this event
 * @property {string} app_name - app_name
 * @property {string} app_id - app_id
 * @property {number} users_with_write_devmode_count - users_with_write_devmode_count
 * @property {string} builderType - builder_type
 * @property {GUID} visitorId - visitor_id
 * @property {string} ds_type - ds_type
 * @property {GUID} site_id - site_id
 * @property {string} panel_id - panel_id
 * @property {string} appName - app_name
 * @property {number} users_on_site_count - users_on_site_count
 * @property {number} ts - ts
 * @property {string} adding_method - adding_method
 * @property {string} c2 - component_type
 * @property {string} panel_name - panel_name
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} pageId - page_id
 * @property {string} origin - origin
 * @property {string} token - token
 * @property {number} users_with_devmode_count - users_with_devmode_count
 * @property {string} ver - version
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {string} dev_mode - dev_mode
 * @property {string} appId - app_id
 * @property {GUID} esi - esi
 * @property {string} component_id - component_id
 * @property {GUID} msid - msid
 * @property {string} ds_id - ds_id
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<addCollectionClickedParams>} LogObject<addCollectionClickedParams>
 */
export function addCollectionClicked(params) {
    return { evid: 3, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'dsNameSet' event (evid: 30)
 *
 * Event description: DS name set
 *
 * @param {dsNameSetParams} params - The parameters for this event
 * @property {string} app_name - app_name
 * @property {string} app_id - app_id
 * @property {string} name - name
 * @property {number} users_with_write_devmode_count - users_with_write_devmode_count
 * @property {string} builderType - builder_type
 * @property {GUID} visitorId - visitor_id
 * @property {string} ds_type - ds_type
 * @property {GUID} site_id - site_id
 * @property {string} panel_id - panel_id
 * @property {string} appName - app_name
 * @property {number} users_on_site_count - users_on_site_count
 * @property {number} ts - ts
 * @property {string} panel_name - panel_name
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} pageId - page_id
 * @property {string} token - token
 * @property {number} users_with_devmode_count - users_with_devmode_count
 * @property {string} ver - version
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {string} dev_mode - dev_mode
 * @property {string} appId - app_id
 * @property {GUID} esi - esi
 * @property {GUID} msid - msid
 * @property {string} ds_id - ds_id
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<dsNameSetParams>} LogObject<dsNameSetParams>
 */
export function dsNameSet(params) {
    return { evid: 30, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'sourceMapRequestInLiveSite' event (evid: 300)
 *
 * Event description: (sent from live site) debug FE code
 *
 * @param {sourceMapRequestInLiveSiteParams} params - The parameters for this event
 * @property {string} file_name - file name
 * @property {string} page_id - page id
 * @property {GUID} msid - msid
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<sourceMapRequestInLiveSiteParams>} LogObject<sourceMapRequestInLiveSiteParams>
 */
export function sourceMapRequestInLiveSite(params) {
    return { evid: 300, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'sourceMapRequestInPreview' event (evid: 301)
 *
 * Event description: (sent from site preview) debug FE code
 *
 * @param {sourceMapRequestInPreviewParams} params - The parameters for this event
 * @property {string} page_id - page id
 * @property {GUID} msid - msid
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<sourceMapRequestInPreviewParams>} LogObject<sourceMapRequestInPreviewParams>
 */
export function sourceMapRequestInPreview(params) {
    return { evid: 301, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'npmUsedInUserCodeOnPreview' event (evid: 302)
 *
 * Event description: sent when an NPM library was imported into code when the site is being saved (sent from bundler dev server)
 *
 * @param {npmUsedInUserCodeOnPreviewParams} params - The parameters for this event
 * @property {string} exceptionType - {}
 * @property {string} file_name - file name
 * @property {boolean} exception - {true/false}
 * @property {string} npm_module_name - npm name
 * @property {string} page_id - page id
 * @property {string} grid_app_id - grid app id
 * @property {GUID} msid - msid
 * @property {Url} page_url - page url
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<npmUsedInUserCodeOnPreviewParams>} LogObject<npmUsedInUserCodeOnPreviewParams>
 */
export function npmUsedInUserCodeOnPreview(params) {
    return { evid: 302, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'namespacesUsedInUserCodeOnPreview' event (evid: 303)
 *
 * Event description: sent when a namespaces (wix-provided-npm) library was imported into code the site is being saved  (sent from bundler dev server)
 *
 * @param {namespacesUsedInUserCodeOnPreviewParams} params - The parameters for this event
 * @property {string} exceptionType - {}
 * @property {string} file_name - file name
 * @property {boolean} exception - {true/false}
 * @property {string} page_id - page id
 * @property {string} grid_app_id - grid app id
 * @property {GUID} msid - msid
 * @property {string} namespace_module_name - namespace module name
 * @property {Url} page_url - page url
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<namespacesUsedInUserCodeOnPreviewParams>} LogObject<namespacesUsedInUserCodeOnPreviewParams>
 */
export function namespacesUsedInUserCodeOnPreview(params) {
    return { evid: 303, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'internalPackageUsedInUserCodeOnPreviewSrc79Evid304' event (evid: 304)
 *
 * Event description: sent when an Internal package library was imported into code when the site is being saved  (sent from bundler dev server)
 *
 * @param {internalPackageUsedInUserCodeOnPreviewSrc79Evid304Params} params - The parameters for this event
 * @property {string} exceptionType - {}
 * @property {string} file_name - file name
 * @property {boolean} exception - {true/false}
 * @property {string} internal_package_module_name - Internal package name
 * @property {string} page_id - page id
 * @property {string} grid_app_id - grid app id
 * @property {GUID} msid - msid
 * @property {Url} page_url - page url
 * @property {boolean} is_backend - {true/false}
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<internalPackageUsedInUserCodeOnPreviewSrc79Evid304Params>} LogObject<internalPackageUsedInUserCodeOnPreviewSrc79Evid304Params>
 */
export function internalPackageUsedInUserCodeOnPreviewSrc79Evid304(params) {
    return { evid: 304, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'staticToDynamicClickX' event (evid: 308)
 *
 * Event description: User click X in the panel
 *
 * @param {staticToDynamicClickXParams} params - The parameters for this event
 * @property {string} app_name - app_name
 * @property {string} app_id - app_id
 * @property {number} users_with_write_devmode_count - users_with_write_devmode_count
 * @property {string} builderType - builder_type
 * @property {GUID} visitorId - visitor_id
 * @property {string} ds_type - ds_type
 * @property {GUID} site_id - site_id
 * @property {string} panel_id - panel_id
 * @property {string} appName - app_name
 * @property {number} users_on_site_count - users_on_site_count
 * @property {number} ts - ts
 * @property {string} panel_name - panel_name
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} page_name - the place where user clicked X : intro/ choose_collection / new_collection_name / connect_fields/    added_dataset/no_componenets_available
 * @property {string} pageId - page_id
 * @property {string} origin - origin
 * @property {string} token - token
 * @property {number} users_with_devmode_count - users_with_devmode_count
 * @property {string} ver - version
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {string} dev_mode - dev_mode
 * @property {string} appId - app_id
 * @property {GUID} esi - esi
 * @property {GUID} msid - msid
 * @property {string} ds_id - ds_id
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<staticToDynamicClickXParams>} LogObject<staticToDynamicClickXParams>
 */
export function staticToDynamicClickX(params) {
    return { evid: 308, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'staticToDynamicPageView' event (evid: 309)
 *
 * Event description: User page view in new static to dynamic.
 *
 * @param {staticToDynamicPageViewParams} params - The parameters for this event
 * @property {string} app_name - app_name
 * @property {string} app_id - app_id
 * @property {number} users_with_write_devmode_count - users_with_write_devmode_count
 * @property {string} builderType - builder_type
 * @property {GUID} visitorId - visitor_id
 * @property {boolean} is_exisitng_collection_available - does the user has any existing collections
 * @property {string} ds_type - ds_type
 * @property {GUID} site_id - site_id
 * @property {string} panel_id - panel_id
 * @property {string} appName - app_name
 * @property {number} users_on_site_count - users_on_site_count
 * @property {number} ts - ts
 * @property {string} panel_name - panel_name
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} page_name - intro/ choose_collection / new_collection_name / connect_fields/    added_dataset/no_componenets_available
 * @property {string} pageId - page_id
 * @property {string} origin - origin
 * @property {string} token - token
 * @property {number} users_with_devmode_count - users_with_devmode_count
 * @property {string} ver - version
 * @property {string} userEnv - user_env
 * @property {boolean} is_dmo - does the user see dev mode on experience in out feature?
 * @property {string} editor_working_mode - editor_working_mode
 * @property {string} dev_mode - dev_mode
 * @property {string} appId - app_id
 * @property {GUID} esi - esi
 * @property {string} available_fields - {text:5, boolean:2…}
 * @property {GUID} msid - msid
 * @property {string} ds_id - ds_id
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<staticToDynamicPageViewParams>} LogObject<staticToDynamicPageViewParams>
 */
export function staticToDynamicPageView(params) {
    return { evid: 309, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'dsModeChanged' event (evid: 31)
 *
 * Event description: DS mode changed
 *
 * @param {dsModeChangedParams} params - The parameters for this event
 * @property {string} app_name - app_name
 * @property {string} app_id - app_id
 * @property {number} users_with_write_devmode_count - users_with_write_devmode_count
 * @property {string} builderType - builder_type
 * @property {GUID} visitorId - visitor_id
 * @property {string} ds_type - ds_type
 * @property {GUID} site_id - site_id
 * @property {string} panel_id - panel_id
 * @property {string} new_mode - new_mode
 * @property {string} appName - app_name
 * @property {number} users_on_site_count - users_on_site_count
 * @property {number} ts - ts
 * @property {string} old_mode - old_mode
 * @property {string} panel_name - panel_name
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} pageId - page_id
 * @property {string} token - token
 * @property {number} users_with_devmode_count - users_with_devmode_count
 * @property {string} ver - version
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {string} dev_mode - dev_mode
 * @property {string} appId - app_id
 * @property {GUID} esi - esi
 * @property {GUID} msid - msid
 * @property {string} ds_id - ds_id
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<dsModeChangedParams>} LogObject<dsModeChangedParams>
 */
export function dsModeChanged(params) {
    return { evid: 31, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'staticToDynamicClickNextIntroScreen' event (evid: 310)
 *
 * Event description: Static To Dynamic - Click Next intro screen.
 *
 * @param {staticToDynamicClickNextIntroScreenParams} params - The parameters for this event
 * @property {string} app_name - app_name
 * @property {string} app_id - app_id
 * @property {number} users_with_write_devmode_count - users_with_write_devmode_count
 * @property {string} builderType - builder_type
 * @property {GUID} visitorId - visitor_id
 * @property {string} ds_type - ds_type
 * @property {GUID} site_id - site_id
 * @property {string} panel_id - panel_id
 * @property {string} appName - app_name
 * @property {number} users_on_site_count - users_on_site_count
 * @property {number} ts - ts
 * @property {string} panel_name - panel_name
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} pageId - page_id
 * @property {string} origin - origin
 * @property {string} token - token
 * @property {number} users_with_devmode_count - users_with_devmode_count
 * @property {string} ver - version
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {string} dev_mode - dev_mode
 * @property {string} appId - app_id
 * @property {GUID} esi - esi
 * @property {GUID} msid - msid
 * @property {string} ds_id - ds_id
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<staticToDynamicClickNextIntroScreenParams>} LogObject<staticToDynamicClickNextIntroScreenParams>
 */
export function staticToDynamicClickNextIntroScreen(params) {
    return { evid: 310, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'containerStarted' event (evid: 3100)
 *
 * Event description: sends when a container start (backend event)
 *
 * @param {containerStartedParams} params - The parameters for this event
 * @property {number} duration - duration
 * @property {string} dc - data center
 * @property {string} segment - segment
 * @property {number} num_of_containers - num_of_containers
 * @property {string} grid_app_id - grid_app_id
 * @property {number} event_type - event type
 * @property {GUID} msid - msid
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<containerStartedParams>} LogObject<containerStartedParams>
 */
export function containerStarted(params) {
    return { evid: 3100, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'staticToDynamicDropdownExistingCollectionExtended' event (evid: 311)
 *
 * Event description: Static To Dynamic -  Dropdown existing collection extended.
 *
 * @param {staticToDynamicDropdownExistingCollectionExtendedParams} params - The parameters for this event
 * @property {string} app_name - app_name
 * @property {string} app_id - app_id
 * @property {number} users_with_write_devmode_count - users_with_write_devmode_count
 * @property {string} builderType - builder_type
 * @property {GUID} visitorId - visitor_id
 * @property {string} ds_type - ds_type
 * @property {GUID} site_id - site_id
 * @property {string} panel_id - panel_id
 * @property {string} appName - app_name
 * @property {number} users_on_site_count - users_on_site_count
 * @property {number} ts - ts
 * @property {string} panel_name - panel_name
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} pageId - page_id
 * @property {string} origin - quickActionsMenu / idePagesTree/ panel/pages
 * @property {string} token - token
 * @property {number} users_with_devmode_count - users_with_devmode_count
 * @property {string} ver - version
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {string} dev_mode - dev_mode
 * @property {string} appId - app_id
 * @property {GUID} esi - esi
 * @property {GUID} msid - msid
 * @property {string} ds_id - ds_id
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<staticToDynamicDropdownExistingCollectionExtendedParams>} LogObject<staticToDynamicDropdownExistingCollectionExtendedParams>
 */
export function staticToDynamicDropdownExistingCollectionExtended(params) {
    return { evid: 311, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'staticToDynamicClickCloseAfterFinishProccessExisting' event (evid: 312)
 *
 * Event description: Static To Dynamic -  Click Close after finish proccess for existing collections (dp were created)
 *
 * @param {staticToDynamicClickCloseAfterFinishProccessExistingParams} params - The parameters for this event
 * @property {string} app_name - app_name
 * @property {string} app_id - app_id
 * @property {number} users_with_write_devmode_count - users_with_write_devmode_count
 * @property {string} builderType - builder_type
 * @property {GUID} visitorId - visitor_id
 * @property {string} ds_type - ds_type
 * @property {GUID} site_id - site_id
 * @property {string} panel_id - panel_id
 * @property {string} appName - app_name
 * @property {number} users_on_site_count - users_on_site_count
 * @property {number} ts - ts
 * @property {string} panel_name - panel_name
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} pageId - page_id
 * @property {string} origin - quickActionsMenu / idePagesTree/ panel/pages
 * @property {string} token - token
 * @property {number} users_with_devmode_count - users_with_devmode_count
 * @property {string} ver - version
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {string} dev_mode - dev_mode
 * @property {string} appId - app_id
 * @property {GUID} esi - esi
 * @property {GUID} msid - msid
 * @property {string} ds_id - ds_id
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<staticToDynamicClickCloseAfterFinishProccessExistingParams>} LogObject<staticToDynamicClickCloseAfterFinishProccessExistingParams>
 */
export function staticToDynamicClickCloseAfterFinishProccessExisting(params) {
    return { evid: 312, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'staticToDynamicClickCreateNewCollectionButton' event (evid: 313)
 *
 * Event description: Static To Dynamic -  Click create new collection button
 *
 * @param {staticToDynamicClickCreateNewCollectionButtonParams} params - The parameters for this event
 * @property {string} app_name - app_name
 * @property {string} app_id - app_id
 * @property {number} users_with_write_devmode_count - users_with_write_devmode_count
 * @property {string} builderType - builder_type
 * @property {GUID} visitorId - visitor_id
 * @property {string} ds_type - ds_type
 * @property {GUID} site_id - site_id
 * @property {string} panel_id - panel_id
 * @property {string} appName - app_name
 * @property {number} users_on_site_count - users_on_site_count
 * @property {number} ts - ts
 * @property {string} panel_name - panel_name
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} pageId - page_id
 * @property {string} origin - origin
 * @property {string} token - token
 * @property {number} users_with_devmode_count - users_with_devmode_count
 * @property {string} ver - version
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {string} dev_mode - dev_mode
 * @property {string} appId - app_id
 * @property {GUID} esi - esi
 * @property {GUID} msid - msid
 * @property {string} ds_id - ds_id
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<staticToDynamicClickCreateNewCollectionButtonParams>} LogObject<staticToDynamicClickCreateNewCollectionButtonParams>
 */
export function staticToDynamicClickCreateNewCollectionButton(params) {
    return { evid: 313, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'staticToDynamicClickNextInNewCollectionNamePage' event (evid: 314)
 *
 * Event description: Static To Dynamic -  click next in new collection name page
 *
 * @param {staticToDynamicClickNextInNewCollectionNamePageParams} params - The parameters for this event
 * @property {string} app_name - app_name
 * @property {string} app_id - app_id
 * @property {number} users_with_write_devmode_count - users_with_write_devmode_count
 * @property {string} builderType - builder_type
 * @property {GUID} visitorId - visitor_id
 * @property {string} ds_type - ds_type
 * @property {GUID} site_id - site_id
 * @property {string} panel_id - panel_id
 * @property {string} appName - app_name
 * @property {number} users_on_site_count - users_on_site_count
 * @property {number} ts - ts
 * @property {string} collection_id - collection_id
 * @property {string} panel_name - panel_name
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} pageId - page_id
 * @property {string} origin - origin
 * @property {string} token - token
 * @property {number} users_with_devmode_count - users_with_devmode_count
 * @property {string} ver - version
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {string} dev_mode - dev_mode
 * @property {string} appId - app_id
 * @property {GUID} esi - esi
 * @property {string} collection_name - the collection name
 * @property {GUID} msid - msid
 * @property {string} ds_id - ds_id
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<staticToDynamicClickNextInNewCollectionNamePageParams>} LogObject<staticToDynamicClickNextInNewCollectionNamePageParams>
 */
export function staticToDynamicClickNextInNewCollectionNamePage(params) {
    return { evid: 314, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'staticToDynamicClickNextExisitingCollection' event (evid: 315)
 *
 * Event description: Static To Dynamic - Click Next Exisiting Collection.
 *
 * @param {staticToDynamicClickNextExisitingCollectionParams} params - The parameters for this event
 * @property {string} app_name - app_name
 * @property {string} app_id - app_id
 * @property {number} users_with_write_devmode_count - users_with_write_devmode_count
 * @property {string} builderType - builder_type
 * @property {GUID} visitorId - visitor_id
 * @property {string} ds_type - ds_type
 * @property {GUID} site_id - site_id
 * @property {string} panel_id - panel_id
 * @property {string} appName - app_name
 * @property {number} users_on_site_count - users_on_site_count
 * @property {number} ts - ts
 * @property {string} collection_id - existing collection id
 * @property {string} panel_name - panel_name
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} pageId - page_id
 * @property {string} origin - origin
 * @property {string} token - token
 * @property {number} users_with_devmode_count - users_with_devmode_count
 * @property {string} ver - version
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {string} dev_mode - dev_mode
 * @property {string} appId - app_id
 * @property {GUID} esi - esi
 * @property {string} collection_name - existing collection name
 * @property {GUID} msid - msid
 * @property {string} ds_id - ds_id
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<staticToDynamicClickNextExisitingCollectionParams>} LogObject<staticToDynamicClickNextExisitingCollectionParams>
 */
export function staticToDynamicClickNextExisitingCollection(params) {
    return { evid: 315, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'staticToDynamicNewCollectionNamePageChangedName' event (evid: 316)
 *
 * Event description: Static To Dynamic -  new collection name page, the user changed the name of the collection
 *
 * @param {staticToDynamicNewCollectionNamePageChangedNameParams} params - The parameters for this event
 * @property {string} app_name - app_name
 * @property {string} app_id - app_id
 * @property {number} users_with_write_devmode_count - users_with_write_devmode_count
 * @property {string} builderType - builder_type
 * @property {GUID} visitorId - visitor_id
 * @property {string} ds_type - ds_type
 * @property {GUID} site_id - site_id
 * @property {string} panel_id - panel_id
 * @property {string} appName - app_name
 * @property {number} users_on_site_count - users_on_site_count
 * @property {number} ts - ts
 * @property {string} collection_id - the collection id
 * @property {string} panel_name - panel_name
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} pageId - page_id
 * @property {string} origin - quickActionsMenu / idePagesTree/ panel/pages
 * @property {string} token - token
 * @property {number} users_with_devmode_count - users_with_devmode_count
 * @property {string} ver - version
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {string} dev_mode - dev_mode
 * @property {string} appId - app_id
 * @property {GUID} esi - esi
 * @property {string} collection_name - the collection name
 * @property {GUID} msid - msid
 * @property {string} action - change_name/ change_id
 * @property {string} ds_id - ds_id
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<staticToDynamicNewCollectionNamePageChangedNameParams>} LogObject<staticToDynamicNewCollectionNamePageChangedNameParams>
 */
export function staticToDynamicNewCollectionNamePageChangedName(params) {
    return { evid: 316, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'staticToDynamicNoComponentPageClickOk' event (evid: 317)
 *
 * Event description: Static To Dynamic -  no component page user clicks OK
 *
 * @param {staticToDynamicNoComponentPageClickOkParams} params - The parameters for this event
 * @property {string} app_name - app_name
 * @property {string} app_id - app_id
 * @property {number} users_with_write_devmode_count - users_with_write_devmode_count
 * @property {string} builderType - builder_type
 * @property {GUID} visitorId - visitor_id
 * @property {string} ds_type - ds_type
 * @property {GUID} site_id - site_id
 * @property {string} panel_id - panel_id
 * @property {string} appName - app_name
 * @property {number} users_on_site_count - users_on_site_count
 * @property {number} ts - ts
 * @property {string} panel_name - panel_name
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} pageId - page_id
 * @property {string} origin - quickActionsMenu / idePagesTree/ panel/pages
 * @property {string} token - token
 * @property {number} users_with_devmode_count - users_with_devmode_count
 * @property {string} ver - version
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {string} dev_mode - dev_mode
 * @property {string} appId - app_id
 * @property {GUID} esi - esi
 * @property {GUID} msid - msid
 * @property {string} ds_id - ds_id
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<staticToDynamicNoComponentPageClickOkParams>} LogObject<staticToDynamicNoComponentPageClickOkParams>
 */
export function staticToDynamicNoComponentPageClickOk(params) {
    return { evid: 317, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'staticToDynamicClickBack' event (evid: 318)
 *
 * Event description: User clciks back on the top of the page
 *
 * @param {staticToDynamicClickBackParams} params - The parameters for this event
 * @property {string} app_name - app_name
 * @property {string} app_id - app_id
 * @property {number} users_with_write_devmode_count - users_with_write_devmode_count
 * @property {string} builderType - builder_type
 * @property {GUID} visitorId - visitor_id
 * @property {string} ds_type - ds_type
 * @property {GUID} site_id - site_id
 * @property {string} panel_id - panel_id
 * @property {string} appName - app_name
 * @property {number} users_on_site_count - users_on_site_count
 * @property {number} ts - ts
 * @property {string} panel_name - panel_name
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} page_name - intro/ choose_collection / new_collection_name / connect_fields/    added_dataset/no_componenets_available
 * @property {string} pageId - page_id
 * @property {string} origin - origin
 * @property {string} token - token
 * @property {number} users_with_devmode_count - users_with_devmode_count
 * @property {string} ver - version
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {string} dev_mode - dev_mode
 * @property {string} appId - app_id
 * @property {GUID} esi - esi
 * @property {GUID} msid - msid
 * @property {string} ds_id - ds_id
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<staticToDynamicClickBackParams>} LogObject<staticToDynamicClickBackParams>
 */
export function staticToDynamicClickBack(params) {
    return { evid: 318, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'staticToDynamicClickEditComponent' event (evid: 319)
 *
 * Event description: User click t edit a compnent
 *
 * @param {staticToDynamicClickEditComponentParams} params - The parameters for this event
 * @property {string} app_name - app_name
 * @property {string} app_id - app_id
 * @property {number} users_with_write_devmode_count - users_with_write_devmode_count
 * @property {string} builderType - builder_type
 * @property {GUID} visitorId - visitor_id
 * @property {string} ds_type - ds_type
 * @property {GUID} site_id - site_id
 * @property {string} panel_id - panel_id
 * @property {string} appName - app_name
 * @property {number} users_on_site_count - users_on_site_count
 * @property {string} field_key - field_key
 * @property {string} field_type - field_type
 * @property {number} ts - ts
 * @property {string} panel_name - panel_name
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} pageId - page_id
 * @property {string} origin - origin
 * @property {string} token - token
 * @property {number} users_with_devmode_count - users_with_devmode_count
 * @property {string} ver - version
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {string} dev_mode - dev_mode
 * @property {string} appId - app_id
 * @property {GUID} esi - esi
 * @property {string} component_type - $w.text/$w.image…
 * @property {GUID} msid - msid
 * @property {string} field_name - text 1/ text 2
 * @property {string} ds_id - ds_id
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<staticToDynamicClickEditComponentParams>} LogObject<staticToDynamicClickEditComponentParams>
 */
export function staticToDynamicClickEditComponent(params) {
    return { evid: 319, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'dsAddFilterButtonClicked' event (evid: 32)
 *
 * Event description: DS add filter button clicked
 *
 * @param {dsAddFilterButtonClickedParams} params - The parameters for this event
 * @property {string} app_name - app_name
 * @property {string} app_id - app_id
 * @property {number} users_with_write_devmode_count - users_with_write_devmode_count
 * @property {string} builderType - builder_type
 * @property {GUID} visitorId - visitor_id
 * @property {string} ds_type - ds_type
 * @property {GUID} site_id - site_id
 * @property {string} panel_id - panel_id
 * @property {string} appName - app_name
 * @property {number} users_on_site_count - users_on_site_count
 * @property {number} ts - ts
 * @property {string} panel_name - panel_name
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} pageId - page_id
 * @property {string} token - token
 * @property {number} users_with_devmode_count - users_with_devmode_count
 * @property {string} ver - version
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {string} dev_mode - dev_mode
 * @property {string} appId - app_id
 * @property {GUID} esi - esi
 * @property {GUID} msid - msid
 * @property {string} ds_id - ds_id
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<dsAddFilterButtonClickedParams>} LogObject<dsAddFilterButtonClickedParams>
 */
export function dsAddFilterButtonClicked(params) {
    return { evid: 32, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'staticToDynamicEditComponentFields' event (evid: 320)
 *
 * Event description: Static To Dynamic - Edit component fields.
 *
 * @param {staticToDynamicEditComponentFieldsParams} params - The parameters for this event
 * @property {string} app_name - app_name
 * @property {string} app_id - app_id
 * @property {number} users_with_write_devmode_count - users_with_write_devmode_count
 * @property {string} builderType - builder_type
 * @property {GUID} visitorId - visitor_id
 * @property {string} ds_type - ds_type
 * @property {GUID} site_id - site_id
 * @property {string} panel_id - panel_id
 * @property {string} appName - app_name
 * @property {number} users_on_site_count - users_on_site_count
 * @property {string} field_key - relevant for velo users
 * @property {string} field_type - what is the component type
 * @property {number} ts - ts
 * @property {string} panel_name - panel_name
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} pageId - page_id
 * @property {string} origin - origin
 * @property {string} token - token
 * @property {number} users_with_devmode_count - users_with_devmode_count
 * @property {string} ver - version
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {string} dev_mode - dev_mode
 * @property {string} appId - app_id
 * @property {GUID} esi - esi
 * @property {GUID} msid - msid
 * @property {string} field_name - text 1/ text 2
 * @property {string} value - value
 * @property {string} action - action
 * @property {string} ds_id - ds_id
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<staticToDynamicEditComponentFieldsParams>} LogObject<staticToDynamicEditComponentFieldsParams>
 */
export function staticToDynamicEditComponentFields(params) {
    return { evid: 320, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'staticToDynamicEditComponentFieldsPanelClosed' event (evid: 321)
 *
 * Event description: Static To Dynamic - after Edit component fields user can click save or close the modal somehow. than it goes back to the main panel and we log the event (notice that panel_name will be for the main panel and not the edit compnent panel)
 *
 * @param {staticToDynamicEditComponentFieldsPanelClosedParams} params - The parameters for this event
 * @property {string} app_name - app_name
 * @property {string} app_id - app_id
 * @property {boolean} is_type_changed - did the component type changed?
 * @property {number} users_with_write_devmode_count - users_with_write_devmode_count
 * @property {string} builderType - builder_type
 * @property {GUID} visitorId - visitor_id
 * @property {string} ds_type - ds_type
 * @property {GUID} site_id - site_id
 * @property {string} panel_id - panel_id
 * @property {string} appName - app_name
 * @property {number} users_on_site_count - users_on_site_count
 * @property {string} field_key - relevant for velo users
 * @property {string} field_type - what is the component type
 * @property {number} ts - ts
 * @property {string} panel_name - panel_name
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} pageId - page_id
 * @property {string} origin - quickActionsMenu / idePagesTree/ panel/pages
 * @property {string} token - token
 * @property {number} users_with_devmode_count - users_with_devmode_count
 * @property {string} ver - version
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {boolean} is_name_changed - did the component name changed?
 * @property {string} dev_mode - dev_mode
 * @property {string} appId - app_id
 * @property {GUID} esi - esi
 * @property {string} component_type - $w.text/$w.image…
 * @property {GUID} msid - msid
 * @property {string} field_name - text 1/ text 2
 * @property {string} action - save/cancel/close
 * @property {boolean} is_key_changed - is_key_changed
 * @property {string} ds_id - ds_id
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<staticToDynamicEditComponentFieldsPanelClosedParams>} LogObject<staticToDynamicEditComponentFieldsPanelClosedParams>
 */
export function staticToDynamicEditComponentFieldsPanelClosed(params) {
    return { evid: 321, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'staticToDynamicClickConnectFields' event (evid: 322)
 *
 * Event description: Static To Dynamic - user click connect fields (when he click the static to dynamic starts to happen for new collection!!)
 *
 * @param {staticToDynamicClickConnectFieldsParams} params - The parameters for this event
 * @property {string} app_name - app_name
 * @property {string} app_id - app_id
 * @property {number} users_with_write_devmode_count - users_with_write_devmode_count
 * @property {string} builderType - builder_type
 * @property {GUID} visitorId - visitor_id
 * @property {string} ds_type - ds_type
 * @property {GUID} site_id - site_id
 * @property {string} panel_id - panel_id
 * @property {string} appName - app_name
 * @property {number} users_on_site_count - users_on_site_count
 * @property {number} ts - ts
 * @property {string} panel_name - panel_name
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} pageId - page_id
 * @property {string} origin - origin
 * @property {string} token - token
 * @property {number} users_with_devmode_count - users_with_devmode_count
 * @property {string} ver - version
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {string} dev_mode - dev_mode
 * @property {string} appId - app_id
 * @property {GUID} esi - esi
 * @property {string} available_fields - which compnents were available? JSON
 * @property {GUID} msid - msid
 * @property {string} ds_id - ds_id
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<staticToDynamicClickConnectFieldsParams>} LogObject<staticToDynamicClickConnectFieldsParams>
 */
export function staticToDynamicClickConnectFields(params) {
    return { evid: 322, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'staticToDynamicClickRetry' event (evid: 323)
 *
 * Event description: Click Retry on the static to dynamic page
 *
 * @param {staticToDynamicClickRetryParams} params - The parameters for this event
 * @property {string} app_name - app_name
 * @property {string} app_id - app_id
 * @property {number} users_with_write_devmode_count - users_with_write_devmode_count
 * @property {string} builderType - builder_type
 * @property {GUID} visitorId - visitor_id
 * @property {string} ds_type - ds_type
 * @property {GUID} site_id - site_id
 * @property {string} panel_id - panel_id
 * @property {string} appName - app_name
 * @property {number} users_on_site_count - users_on_site_count
 * @property {number} ts - ts
 * @property {string} panel_name - panel_name
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} pageId - page_id
 * @property {string} origin - origin
 * @property {string} token - token
 * @property {number} users_with_devmode_count - users_with_devmode_count
 * @property {string} ver - version
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {string} dev_mode - dev_mode
 * @property {string} appId - app_id
 * @property {GUID} esi - esi
 * @property {GUID} msid - msid
 * @property {string} ds_id - ds_id
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<staticToDynamicClickRetryParams>} LogObject<staticToDynamicClickRetryParams>
 */
export function staticToDynamicClickRetry(params) {
    return { evid: 323, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'staticToDynamicConnectFieldsSuccess' event (evid: 324)
 *
 * Event description: Static To Dynamic - Connect Fields Success.
 *
 * @param {staticToDynamicConnectFieldsSuccessParams} params - The parameters for this event
 * @property {string} app_name - app_name
 * @property {string} app_id - app_id
 * @property {number} users_with_write_devmode_count - users_with_write_devmode_count
 * @property {string} builderType - builder_type
 * @property {GUID} visitorId - visitor_id
 * @property {string} ds_type - ds_type
 * @property {GUID} site_id - site_id
 * @property {string} panel_id - panel_id
 * @property {string} appName - app_name
 * @property {number} users_on_site_count - users_on_site_count
 * @property {number} ts - ts
 * @property {string} panel_name - panel_name
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} pageId - page_id
 * @property {string} origin - origin
 * @property {string} token - token
 * @property {number} users_with_devmode_count - users_with_devmode_count
 * @property {string} ver - version
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {string} dev_mode - dev_mode
 * @property {string} appId - app_id
 * @property {GUID} esi - esi
 * @property {string} available_fields - which compnents were available? JSON
 * @property {GUID} msid - msid
 * @property {string} ds_id - ds_id
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<staticToDynamicConnectFieldsSuccessParams>} LogObject<staticToDynamicConnectFieldsSuccessParams>
 */
export function staticToDynamicConnectFieldsSuccess(params) {
    return { evid: 324, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'veloTranslationKeyUsage' event (evid: 325)
 *
 * Event description: event is sending when translation key is used
 *
 * @param {veloTranslationKeyUsageParams} params - The parameters for this event
 * @property {string} app_name - app_name
 * @property {string} app_id - app_id
 * @property {boolean} is_rollout - is_rollout
 * @property {string} jsHeapSizeLimit - js_heap_size_limit
 * @property {string} multilingual - multilingual
 * @property {number} users_with_write_devmode_count - users_with_write_devmode_count
 * @property {string} builderType - builder_type
 * @property {GUID} visitorId - visitor_id
 * @property {string} translationKeys - translation_keys
 * @property {string} ds_type - ds_type
 * @property {GUID} site_id - site_id
 * @property {string} panel_id - panel_id
 * @property {string} appName - app_name
 * @property {number} users_on_site_count - users_on_site_count
 * @property {number} ts - ts
 * @property {boolean} isZoomOut - isZoomOut
 * @property {string} panel_name - panel_name
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} pageId - page_id
 * @property {string} ds_origin - ds_origin
 * @property {string} totalJsHeapSize - totalJsHeapSize
 * @property {string} viewerVersion - viewerVersion
 * @property {string} token - token
 * @property {number} users_with_devmode_count - users_with_devmode_count
 * @property {string} ver - version
 * @property {string} userEnv - user_env
 * @property {string} viewerName - viewer Name
 * @property {string} usedJsHeapSize - usedJsHeapSize hey hey hey
 * @property {string} editor_working_mode - editor_working_mode
 * @property {string} dev_mode - dev_mode
 * @property {string} appId - app_id
 * @property {GUID} esi - esi
 * @property {GUID} msid - msid
 * @property {string} ds_id - ds_id
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<veloTranslationKeyUsageParams>} LogObject<veloTranslationKeyUsageParams>
 */
export function veloTranslationKeyUsage(params) {
    return { evid: 325, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'staticToDynamicClickCloseAfterFinishProccessNewCollection' event (evid: 326)
 *
 * Event description: Static To Dynamic -  Click Close after finish proccess for new collections
 *
 * @param {staticToDynamicClickCloseAfterFinishProccessNewCollectionParams} params - The parameters for this event
 * @property {string} app_name - app_name
 * @property {string} app_id - app_id
 * @property {number} users_with_write_devmode_count - users_with_write_devmode_count
 * @property {string} builderType - builder_type
 * @property {GUID} visitorId - visitor_id
 * @property {string} ds_type - ds_type
 * @property {GUID} site_id - site_id
 * @property {string} panel_id - panel_id
 * @property {string} appName - app_name
 * @property {number} users_on_site_count - users_on_site_count
 * @property {number} ts - ts
 * @property {string} panel_name - panel_name
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} pageId - page_id
 * @property {string} origin - quickActionsMenu / idePagesTree/ panel/pages
 * @property {string} token - token
 * @property {number} users_with_devmode_count - users_with_devmode_count
 * @property {string} ver - version
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {string} dev_mode - dev_mode
 * @property {string} appId - app_id
 * @property {GUID} esi - esi
 * @property {GUID} msid - msid
 * @property {string} ds_id - ds_id
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<staticToDynamicClickCloseAfterFinishProccessNewCollectionParams>} LogObject<staticToDynamicClickCloseAfterFinishProccessNewCollectionParams>
 */
export function staticToDynamicClickCloseAfterFinishProccessNewCollection(params) {
    return { evid: 326, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'customIntegretionsQuickActionClicked' event (evid: 328)
 *
 * Event description: Custom integration quick action clicked
 *
 * @param {customIntegretionsQuickActionClickedParams} params - The parameters for this event
 * @property {string} app_name - app_name
 * @property {string} app_id - app_id
 * @property {number} users_with_write_devmode_count - users_with_write_devmode_count
 * @property {string} builderType - builder_type
 * @property {GUID} visitorId - visitor_id
 * @property {string} ds_type - ds_type
 * @property {GUID} site_id - site_id
 * @property {string} panel_id - panel_id
 * @property {string} appName - app_name
 * @property {number} users_on_site_count - users_on_site_count
 * @property {number} ts - ts
 * @property {GUID} visitor_id - visitor_id
 * @property {string} panel_name - panel_name
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} pageId - page_id
 * @property {string} item_name - {shipping_rates}
 * @property {string} token - token
 * @property {number} users_with_devmode_count - users_with_devmode_count
 * @property {string} ver - version
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {string} category - {start_now, learn_more, accept_legal, cancel_legal}
 * @property {string} dev_mode - dev_mode
 * @property {string} appId - app_id
 * @property {GUID} esi - esi
 * @property {string} item_type - custom_integrations
 * @property {GUID} msid - {}
 * @property {string} ds_id - ds_id
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<customIntegretionsQuickActionClickedParams>} LogObject<customIntegretionsQuickActionClickedParams>
 */
export function customIntegretionsQuickActionClicked(params) {
    return { evid: 328, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'veloAiAssistantErrorModalSrc79Evid329' event (evid: 329)
 *
 * Event description: Velo AI assistant error modal
 *
 * @param {veloAiAssistantErrorModalSrc79Evid329Params} params - The parameters for this event
 * @property {string} builderType - builder_type
 * @property {GUID} visitorId - visitor_id
 * @property {string} panel_name - panel_name
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} pageId - page_id
 * @property {string} action_desc - {the required action that is described, to be translated to code}
 * @property {string} origin - {editor event hanler, editor css etc}
 * @property {string} token - token
 * @property {number} users_with_devmode_count - users_with_devmode_count
 * @property {string} action - show/ edit_page_code/ continue
 * @property {string} platform - platform
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<veloAiAssistantErrorModalSrc79Evid329Params>} LogObject<veloAiAssistantErrorModalSrc79Evid329Params>
 */
export function veloAiAssistantErrorModalSrc79Evid329(params) {
    return { evid: 329, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'dsFilterPanelFieldSelected' event (evid: 33)
 *
 * Event description: DS filter panel field selected
 *
 * @param {dsFilterPanelFieldSelectedParams} params - The parameters for this event
 * @property {string} app_name - app_name
 * @property {string} app_id - app_id
 * @property {string} renderingFlow - Rendering flow
 * @property {number} users_with_write_devmode_count - users_with_write_devmode_count
 * @property {string} builderType - builder_type
 * @property {GUID} visitorId - visitor_id
 * @property {string} ds_type - ds_type
 * @property {GUID} site_id - site_id
 * @property {string} panel_id - panel_id
 * @property {string} appName - app_name
 * @property {number} users_on_site_count - users_on_site_count
 * @property {string} field_type - field type
 * @property {number} ts - ts
 * @property {string} panel_name - panel_name
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} pageId - page_id
 * @property {string} token - token
 * @property {number} users_with_devmode_count - users_with_devmode_count
 * @property {string} ver - version
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {string} mode - add/edit
 * @property {string} dev_mode - dev_mode
 * @property {string} appId - app_id
 * @property {GUID} esi - esi
 * @property {GUID} msid - msid
 * @property {string} field_name - field name
 * @property {string} ds_id - ds_id
 * @property {string} origin - origin
 * @property {string} collection_id - collection_id
 * @return {LogObject<dsFilterPanelFieldSelectedParams>} LogObject<dsFilterPanelFieldSelectedParams>
 */
export function dsFilterPanelFieldSelected(params) {
    return { evid: 33, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'dsSortPanelCancelButtonClickedSrc79Evid330' event (evid: 330)
 *
 * Event description: DS sort panel cancel button clicked
 *
 * @param {dsSortPanelCancelButtonClickedSrc79Evid330Params} params - The parameters for this event
 * @property {string} app_name - app_name
 * @property {string} app_id - app_id
 * @property {number} users_with_write_devmode_count - users_with_write_devmode_count
 * @property {string} builderType - builder_type
 * @property {GUID} visitorId - visitor_id
 * @property {string} ds_type - ds_type
 * @property {GUID} site_id - site_id
 * @property {string} panel_id - panel_id
 * @property {string} appName - app_name
 * @property {number} users_on_site_count - users_on_site_count
 * @property {number} ts - ts
 * @property {string} collection_id - collection id
 * @property {boolean} is_collection_order_applied - is_collection_order_applied
 * @property {string} panel_name - panel_name
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} pageId - page_id
 * @property {string} token - token
 * @property {number} users_with_devmode_count - users_with_devmode_count
 * @property {string} ver - version
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {string} mode - mode
 * @property {string} dev_mode - dev_mode
 * @property {string} sort - sort (json)
 * @property {string} appId - app_id
 * @property {GUID} esi - esi
 * @property {GUID} msid - msid
 * @property {string} ds_id - ds_id
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<dsSortPanelCancelButtonClickedSrc79Evid330Params>} LogObject<dsSortPanelCancelButtonClickedSrc79Evid330Params>
 */
export function dsSortPanelCancelButtonClickedSrc79Evid330(params) {
    return { evid: 330, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'eligibleCodeCheck' event (evid: 3300)
 *
 * Event description: The event sends on save/publish and check if the code is eligible code per code file (path)
 * On FE code
 *
 * @param {eligibleCodeCheckParams} params - The parameters for this event
 * @property {boolean} isPreview - was this event was triggered by a preview action (null / false -> save or publish action, true -> preview action)
 * @property {string} path - code file
 * @property {string} file_name - file name
 * @property {string} code_app_id - code_app_id
 * @property {string} components - list of the attached components in the code
 * @property {boolean} is_preview - was this event was triggered by a preview action (null / false -> save or publish action, true -> preview action)
 * @property {boolean} is_eligible - true if the code file is eligible. false if not
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<eligibleCodeCheckParams>} LogObject<eligibleCodeCheckParams>
 */
export function eligibleCodeCheck(params) {
    return { evid: 3300, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'cacheableCodeCheck' event (evid: 3301)
 *
 * Event description: The event sends on save/publish and checks if the code is cacheable per code file (path)
 *
 * @param {cacheableCodeCheckParams} params - The parameters for this event
 * @property {string} failure_reason - NonCacheableReason
 * @property {string} path - code file
 * @property {string} file_name - file name
 * @property {string} code_app_id - code_app_id
 * @property {string} analysis_name - {indicate the type of analysis algorithm performed}
 * @property {string} components - list of the attached components in the code
 * @property {boolean} is_cacheable - true if the code file is eligible. false if not
 * @property {boolean} is_preview - was this event was triggered by a preview action (null / false -> save or publish action, true -> preview action)
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<cacheableCodeCheckParams>} LogObject<cacheableCodeCheckParams>
 */
export function cacheableCodeCheck(params) {
    return { evid: 3301, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'installPackageDoneSrc79Evid331' event (evid: 331)
 *
 * Event description: install package done
 *
 * @param {installPackageDoneSrc79Evid331Params} params - The parameters for this event
 * @property {string} item_version - {}
 * @property {string} builderType - builder_type
 * @property {GUID} visitorId - visitor_id
 * @property {string} pageId - page_id
 * @property {string} item_name - item_name
 * @property {string} origin - package_manager_main_view, built_versions
 * @property {boolean} is_latest_version - is_latest_version
 * @property {GUID} msid - {}
 * @property {string} item_type - item_type
 * @property {number} duration - duration
 * @property {string} result - result
 * @return {LogObject<installPackageDoneSrc79Evid331Params>} LogObject<installPackageDoneSrc79Evid331Params>
 */
export function installPackageDoneSrc79Evid331(params) {
    return { evid: 331, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'uninstallPackageDoneSrc79Evid332' event (evid: 332)
 *
 * Event description: uninstall package done
 *
 * @param {uninstallPackageDoneSrc79Evid332Params} params - The parameters for this event
 * @property {string} item_version - {}
 * @property {string} builderType - builder_type
 * @property {GUID} visitorId - visitor_id
 * @property {string} pageId - page_id
 * @property {string} item_name - item_name
 * @property {string} origin - package_manager_main_view, built_versions
 * @property {boolean} is_latest_version - is_latest_version
 * @property {GUID} msid - {}
 * @property {string} item_type - item_type
 * @property {number} duration - duration
 * @property {string} result - result
 * @return {LogObject<uninstallPackageDoneSrc79Evid332Params>} LogObject<uninstallPackageDoneSrc79Evid332Params>
 */
export function uninstallPackageDoneSrc79Evid332(params) {
    return { evid: 332, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'veloPublishPipelineStartSrc79Evid333' event (evid: 333)
 *
 * Event description: when a request is getting to the Velo publish pipeline service host
 *
 * @param {veloPublishPipelineStartSrc79Evid333Params} params - The parameters for this event
 * @property {string} codeAppId - code_app_id
 * @property {string} response - user-error/system-error/success
 * @property {GUID} msid - msid
 * @property {string} renderingFlow - Rendering flow
 * @property {string} implementers - implementers list
 * @property {string} deploymentId - deployment_id, unique per bundling
 * @return {LogObject<veloPublishPipelineStartSrc79Evid333Params>} LogObject<veloPublishPipelineStartSrc79Evid333Params>
 */
export function veloPublishPipelineStartSrc79Evid333(params) {
    return { evid: 333, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'veloPublishPipelineFinishSrc79Evid334' event (evid: 334)
 *
 * Event description: trigger when pp sending result to the DM server
 *
 * @param {veloPublishPipelineFinishSrc79Evid334Params} params - The parameters for this event
 * @property {string} codeAppId - code_app_id
 * @property {string} response - user-error/system-error/success
 * @property {GUID} msid - msid
 * @property {string} renderingFlow - Rendering flow
 * @property {string} implementers - implementers list
 * @property {string} deploymentId - deployment_id, unique per bundling
 * @return {LogObject<veloPublishPipelineFinishSrc79Evid334Params>} LogObject<veloPublishPipelineFinishSrc79Evid334Params>
 */
export function veloPublishPipelineFinishSrc79Evid334(params) {
    return { evid: 334, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'veloPublishPipelineImplementerResponseSrc79Evid335' event (evid: 335)
 *
 * Event description: triggered on velo pp spi implementer side with response details
 *
 * @param {veloPublishPipelineImplementerResponseSrc79Evid335Params} params - The parameters for this event
 * @property {string} codeAppId - code_app_id
 * @property {string} response - response
 * @property {GUID} msid - msid
 * @property {string} renderingFlow - Rendering flow
 * @property {string} implementers - implementers list
 * @property {GUID} implementerName - implementerName
 * @property {string} deploymentId - deployment_id, unique per bundling
 * @return {LogObject<veloPublishPipelineImplementerResponseSrc79Evid335Params>} LogObject<veloPublishPipelineImplementerResponseSrc79Evid335Params>
 */
export function veloPublishPipelineImplementerResponseSrc79Evid335(params) {
    return { evid: 335, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'veloPublishPipelineImplementerFinishSrc79Evid337' event (evid: 337)
 *
 * Event description: triggered on velo pp spi host upon implementer task finished
 *
 * @param {veloPublishPipelineImplementerFinishSrc79Evid337Params} params - The parameters for this event
 * @property {string} codeAppId - code_app_id
 * @property {string} response - response
 * @property {GUID} msid - msid
 * @property {string} renderingFlow - Rendering flow
 * @property {string} implementers - implementers list
 * @property {GUID} implementerName - implementerName
 * @property {string} deploymentId - deployment_id, unique per bundling
 * @return {LogObject<veloPublishPipelineImplementerFinishSrc79Evid337Params>} LogObject<veloPublishPipelineImplementerFinishSrc79Evid337Params>
 */
export function veloPublishPipelineImplementerFinishSrc79Evid337(params) {
    return { evid: 337, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'veloAiAssistantSuggestedInputSrc79Evid338' event (evid: 338)
 *
 * Event description: when user clicks on one of the suggested ai inputs
 *
 * @param {veloAiAssistantSuggestedInputSrc79Evid338Params} params - The parameters for this event
 * @property {string} renderingFlow - Rendering flow
 * @property {string} builderType - builder_type
 * @property {string} handler_name - {is_clicked, is_double_clicked,is_hovered_on etc}
 * @property {number} ts - ts
 * @property {string} input_name - input_name
 * @property {string} component_id - {}
 * @property {string} component_type - {}
 * @property {string} selected_input - selected_input
 * @property {GUID} conversation_id - conversation_id
 * @property {string} platform - platform
 * @property {string} entryPoint - entry_point
 * @return {LogObject<veloAiAssistantSuggestedInputSrc79Evid338Params>} LogObject<veloAiAssistantSuggestedInputSrc79Evid338Params>
 */
export function veloAiAssistantSuggestedInputSrc79Evid338(params) {
    return { evid: 338, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'dynamicPageNavigateToSeoPageSettingsSrc79Evid339' event (evid: 339)
 *
 * Event description: When a user navigates to 'SEO' tab in dynamic page setting menu
 *
 * @param {dynamicPageNavigateToSeoPageSettingsSrc79Evid339Params} params - The parameters for this event
 * @property {string} collection_id - collection_id
 * @property {string} page_id - the id of the page its settings is viewed
 * @property {string} panel_id - panel_id
 * @property {string} panel_name - panel_name
 * @property {string} page_type - item\ list page
 * @return {LogObject<dynamicPageNavigateToSeoPageSettingsSrc79Evid339Params>} LogObject<dynamicPageNavigateToSeoPageSettingsSrc79Evid339Params>
 */
export function dynamicPageNavigateToSeoPageSettingsSrc79Evid339(params) {
    return { evid: 339, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'dsFilterPanelConditionSelected' event (evid: 34)
 *
 * Event description: DS filter panel condition selected
 *
 * @param {dsFilterPanelConditionSelectedParams} params - The parameters for this event
 * @property {string} app_name - app_name
 * @property {string} app_id - app_id
 * @property {string} renderingFlow - Rendering flow
 * @property {number} users_with_write_devmode_count - users_with_write_devmode_count
 * @property {string} builderType - builder_type
 * @property {GUID} visitorId - visitor_id
 * @property {string} ds_type - ds_type
 * @property {GUID} site_id - site_id
 * @property {string} panel_id - panel_id
 * @property {string} appName - app_name
 * @property {number} users_on_site_count - users_on_site_count
 * @property {number} ts - ts
 * @property {string} panel_name - panel_name
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} pageId - page_id
 * @property {string} condition - condition (e.g. eq)
 * @property {string} token - token
 * @property {number} users_with_devmode_count - users_with_devmode_count
 * @property {string} ver - version
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {string} mode - add/edit
 * @property {string} dev_mode - dev_mode
 * @property {string} appId - app_id
 * @property {GUID} esi - esi
 * @property {GUID} msid - msid
 * @property {string} ds_id - ds_id
 * @property {string} origin - origin
 * @property {string} collection_id - collection_id
 * @return {LogObject<dsFilterPanelConditionSelectedParams>} LogObject<dsFilterPanelConditionSelectedParams>
 */
export function dsFilterPanelConditionSelected(params) {
    return { evid: 34, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'filterSortDataSetOnPageWereTriggerViaCodeLiveSiteSrc79Evid341' event (evid: 341)
 *
 * Event description: Filter/Sort DS events were triggered via code (wix data api) on live site
 *
 * @param {filterSortDataSetOnPageWereTriggerViaCodeLiveSiteSrc79Evid341Params} params - The parameters for this event
 * @property {GUID} visitor_id - visitor_id
 * @property {string} collection_id - collection_id
 * @property {string} ds_name - data set name
 * @property {string} pageId - page_id
 * @property {GUID} vsi - vsi
 * @property {string} collection_name - this is also collection_id!
 * @property {GUID} msid - msid
 * @property {Url} page_url - page url
 * @property {string} ds_id - ds_id
 * @return {LogObject<filterSortDataSetOnPageWereTriggerViaCodeLiveSiteSrc79Evid341Params>} LogObject<filterSortDataSetOnPageWereTriggerViaCodeLiveSiteSrc79Evid341Params>
 */
export function filterSortDataSetOnPageWereTriggerViaCodeLiveSiteSrc79Evid341(params) {
    return { evid: 341, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'ideLinkToElementsApiReferenceClicked' event (evid: 344)
 *
 * Event description: When a user clicks on an the link to the api reference of an element in the IDE
 *
 * @param {ideLinkToElementsApiReferenceClickedParams} params - The parameters for this event
 * @property {string} app_name - app_name
 * @property {string} app_id - app_id
 * @property {number} users_with_write_devmode_count - users_with_write_devmode_count
 * @property {string} builderType - builder_type
 * @property {string} path - the code page on which this reference was presented - for example public/utils.js or page/home.js
 * @property {GUID} visitorId - Deprecated
 * @property {string} ds_type - ds_type
 * @property {GUID} site_id - site_id
 * @property {boolean} is_css - true in case it's css element
 * @property {string} panel_id - panel_id
 * @property {string} appName - app_name
 * @property {number} users_on_site_count - users_on_site_count
 * @property {number} ts - ts
 * @property {string} panel_name - panel_name
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} pageId - page_id
 * @property {string} token - token
 * @property {number} users_with_devmode_count - users_with_devmode_count
 * @property {string} ver - version
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {string} dev_mode - dev_mode
 * @property {string} api_name - the name of the api that is being presented (for example repeater element)
 * @property {string} appId - app_id
 * @property {GUID} esi - esi
 * @property {GUID} msid - msid
 * @property {string} ds_id - ds_id
 * @property {Url} refrence_url - the url where this link is referencing to
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<ideLinkToElementsApiReferenceClickedParams>} LogObject<ideLinkToElementsApiReferenceClickedParams>
 */
export function ideLinkToElementsApiReferenceClicked(params) {
    return { evid: 344, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'gettingStartedSectionAction' event (evid: 345)
 *
 * Event description: When a user clicks to expand or collapse a section in the getting started tab
 *
 * @param {gettingStartedSectionActionParams} params - The parameters for this event
 * @property {string} app_name - app_name
 * @property {string} app_id - app_id
 * @property {number} users_with_write_devmode_count - users_with_write_devmode_count
 * @property {string} builderType - builder_type
 * @property {GUID} visitorId - visitor_id
 * @property {string} ds_type - ds_type
 * @property {GUID} site_id - site_id
 * @property {string} panel_id - panel_id
 * @property {string} appName - app_name
 * @property {number} users_on_site_count - users_on_site_count
 * @property {number} ts - ts
 * @property {string} panel_name - panel_name
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} section - the name of the section that the action is being performed on
 * @property {string} pageId - page_id
 * @property {string} token - token
 * @property {number} users_with_devmode_count - users_with_devmode_count
 * @property {string} ver - version
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {string} dev_mode - dev_mode
 * @property {string} appId - app_id
 * @property {GUID} esi - esi
 * @property {GUID} msid - msid
 * @property {string} action - the action that was preformed one of {expand, collapse)
 * @property {string} ds_id - ds_id
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<gettingStartedSectionActionParams>} LogObject<gettingStartedSectionActionParams>
 */
export function gettingStartedSectionAction(params) {
    return { evid: 345, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'gettingStartedBoxAction' event (evid: 346)
 *
 * Event description: When a users makes any action on one of the boxes inside the getting started tab sections
 *
 * @param {gettingStartedBoxActionParams} params - The parameters for this event
 * @property {string} app_name - app_name
 * @property {string} app_id - app_id
 * @property {number} users_with_write_devmode_count - users_with_write_devmode_count
 * @property {string} builderType - builder_type
 * @property {GUID} visitorId - visitor_id
 * @property {string} ds_type - ds_type
 * @property {GUID} site_id - site_id
 * @property {string} panel_id - panel_id
 * @property {Url} url - In case the action leads to a url
 * @property {string} appName - app_name
 * @property {number} users_on_site_count - users_on_site_count
 * @property {number} ts - ts
 * @property {string} panel_name - panel_name
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} section - Section: where is this code at  (e.g work with site elements)
 * @property {string} code - the code string(e.g “console.log(...)
 * @property {string} pageId - page_id
 * @property {string} origin - where the action was done (box, copy,info)
 * @property {string} token - token
 * @property {number} users_with_devmode_count - users_with_devmode_count
 * @property {string} ver - version
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {string} dev_mode - dev_mode
 * @property {string} title - title of the code box(e.g “Print Hello World!”)
 * @property {string} appId - app_id
 * @property {GUID} esi - esi
 * @property {GUID} msid - msid
 * @property {string} action - what action was done  (click, hover)
 * @property {string} ds_id - ds_id
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<gettingStartedBoxActionParams>} LogObject<gettingStartedBoxActionParams>
 */
export function gettingStartedBoxAction(params) {
    return { evid: 346, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'spiRequestWasTriggeredInLiveSite' event (evid: 348)
 *
 * Event description: SPI request request was triggered in live site
 *
 * @param {spiRequestWasTriggeredInLiveSiteParams} params - The parameters for this event
 * @property {GUID} _event_hash - event_hash
 * @property {GUID} visitor_id - visitor_id
 * @property {string} app_name - app_name
 * @property {string} app_id - app_id
 * @property {string} exceptionType - {}
 * @property {string} builderType - builder_type
 * @property {string} ds_type - ds_type
 * @property {GUID} site_id - site_id
 * @property {string} panel_id - panel_id
 * @property {string} file_name - {}
 * @property {string} appName - app_name
 * @property {number} ts - ts
 * @property {boolean} exception - {true/false}
 * @property {string} panel_name - panel_name
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} pageId - page_id
 * @property {GUID} vsi - {}
 * @property {string} token - token
 * @property {string} ver - version
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {string} appId - app_id
 * @property {GUID} esi - esi
 * @property {GUID} msid - msid
 * @property {number} tsp - tsp
 * @property {Url} page_url - {}
 * @property {string} ds_id - ds_id
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<spiRequestWasTriggeredInLiveSiteParams>} LogObject<spiRequestWasTriggeredInLiveSiteParams>
 */
export function spiRequestWasTriggeredInLiveSite(params) {
    return { evid: 348, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'pluginsWereTriggeredInLiveSite' event (evid: 349)
 *
 * Event description: plugins were triggered in live site
 * plugins is a feature in Elementory that is used for code execution which is not in the user code itself, as opposed to webMethods/httpFunctions/other Velo features which runs user code
 *
 * @param {pluginsWereTriggeredInLiveSiteParams} params - The parameters for this event
 * @property {GUID} _event_hash - event_hash
 * @property {GUID} visitor_id - visitor_id
 * @property {string} app_name - app_name
 * @property {string} app_id - app_id
 * @property {string} exceptionType - {}
 * @property {string} builderType - builder_type
 * @property {string} ds_type - ds_type
 * @property {GUID} site_id - site_id
 * @property {string} panel_id - panel_id
 * @property {string} file_name - {}
 * @property {string} appName - app_name
 * @property {number} ts - ts
 * @property {boolean} exception - {true/false}
 * @property {string} panel_name - panel_name
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} pageId - page_id
 * @property {GUID} vsi - {}
 * @property {string} token - token
 * @property {string} ver - version
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {string} appId - app_id
 * @property {GUID} esi - esi
 * @property {GUID} msid - msid
 * @property {number} tsp - tsp
 * @property {Url} page_url - {}
 * @property {string} ds_id - ds_id
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<pluginsWereTriggeredInLiveSiteParams>} LogObject<pluginsWereTriggeredInLiveSiteParams>
 */
export function pluginsWereTriggeredInLiveSite(params) {
    return { evid: 349, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'dsFilterPanelValueSet' event (evid: 35)
 *
 * Event description: DS filter panel value set
 *
 * @param {dsFilterPanelValueSetParams} params - The parameters for this event
 * @property {string} app_name - app_name
 * @property {string} app_id - app_id
 * @property {string} renderingFlow - Rendering flow
 * @property {number} users_with_write_devmode_count - users_with_write_devmode_count
 * @property {string} builderType - builder_type
 * @property {GUID} visitorId - visitor_id
 * @property {string} ds_type - ds_type
 * @property {GUID} site_id - site_id
 * @property {string} panel_id - panel_id
 * @property {string} appName - app_name
 * @property {number} users_on_site_count - users_on_site_count
 * @property {number} ts - ts
 * @property {string} panel_name - panel_name
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} pageId - page_id
 * @property {string} token - token
 * @property {number} users_with_devmode_count - users_with_devmode_count
 * @property {string} ver - version
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {string} mode - add/edit
 * @property {string} dev_mode - dev_mode
 * @property {string} appId - app_id
 * @property {GUID} esi - esi
 * @property {GUID} msid - msid
 * @property {string} value - value
 * @property {string} ds_id - ds_id
 * @property {string} origin - origin
 * @property {string} collection_id - collection_id
 * @return {LogObject<dsFilterPanelValueSetParams>} LogObject<dsFilterPanelValueSetParams>
 */
export function dsFilterPanelValueSet(params) {
    return { evid: 35, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'gitHubOnboardingAction' event (evid: 350)
 *
 * Event description: sends events for every steps of the github onboarding as part of the any IDE/ local IDE.
 * the steps are:
 * before you get started
 * sign in to github
 * create a new repository
 * error with create new repository
 * sign in to github
 * velo application is installed
 *
 * @param {gitHubOnboardingActionParams} params - The parameters for this event
 * @property {string} app_name - app_name
 * @property {string} app_id - app_id
 * @property {number} users_with_write_devmode_count - users_with_write_devmode_count
 * @property {string} step - on of: before you get started, sign in to github, create a new repository, error with create new repository,sign in to github, velo application is installed
 * @property {string} builderType - builder_type
 * @property {GUID} visitorId - visitor_id
 * @property {string} referral_url - in case there is a url - where does it leads
 * @property {string} ds_type - ds_type
 * @property {GUID} site_id - site_id
 * @property {string} panel_id - panel_id
 * @property {string} description - if a user provides description for creating the repo
 * @property {string} appName - app_name
 * @property {number} users_on_site_count - users_on_site_count
 * @property {number} ts - ts
 * @property {string} sectionName - the section that was copied (in case the action is copy)
 * @property {string} repoName - the name of the github repository  - only when available
 * @property {string} panel_name - panel_name
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} pageId - page_id
 * @property {string} token - token
 * @property {number} users_with_devmode_count - users_with_devmode_count
 * @property {string} ver - version
 * @property {string} gitUserName - github_user_name
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {string} dev_mode - dev_mode
 * @property {string} appId - app_id
 * @property {GUID} esi - esi
 * @property {GUID} msid - msid
 * @property {string} action - which action a user clicked on
 * @property {string} ds_id - ds_id
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<gitHubOnboardingActionParams>} LogObject<gitHubOnboardingActionParams>
 */
export function gitHubOnboardingAction(params) {
    return { evid: 350, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'gitHubOnboardingSuccessStatus' event (evid: 351)
 *
 * Event description: When a user finish with their onboarding to github as part of the any IDE / local IDE and an indication wether it was successful and the error that might happen
 *
 * @param {gitHubOnboardingSuccessStatusParams} params - The parameters for this event
 * @property {string} app_name - app_name
 * @property {string} app_id - app_id
 * @property {boolean} success - did the user properly finished connecting their site to github
 * @property {number} users_with_write_devmode_count - users_with_write_devmode_count
 * @property {string} builderType - builder_type
 * @property {GUID} visitorId - visitor_id
 * @property {string} ds_type - ds_type
 * @property {GUID} site_id - site_id
 * @property {string} panel_id - panel_id
 * @property {string} appName - app_name
 * @property {number} users_on_site_count - users_on_site_count
 * @property {number} ts - ts
 * @property {string} panel_name - panel_name
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} pageId - page_id
 * @property {string} errorType - optional - in case there was an error with the onboarding / the user quit in the middle - on which step was it
 * @property {string} token - token
 * @property {number} users_with_devmode_count - users_with_devmode_count
 * @property {string} ver - version
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {string} dev_mode - dev_mode
 * @property {string} appId - app_id
 * @property {GUID} esi - esi
 * @property {GUID} msid - msid
 * @property {string} ds_id - ds_id
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<gitHubOnboardingSuccessStatusParams>} LogObject<gitHubOnboardingSuccessStatusParams>
 */
export function gitHubOnboardingSuccessStatus(params) {
    return { evid: 351, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'githubDisconnectAction' event (evid: 352)
 *
 * Event description: when a users attempt to disconnect from github
 *
 * @param {githubDisconnectActionParams} params - The parameters for this event
 * @property {string} app_name - app_name
 * @property {string} app_id - app_id
 * @property {number} users_with_write_devmode_count - users_with_write_devmode_count
 * @property {string} builderType - builder_type
 * @property {GUID} visitorId - visitor_id
 * @property {string} ds_type - ds_type
 * @property {GUID} site_id - site_id
 * @property {string} panel_id - panel_id
 * @property {string} appName - app_name
 * @property {number} users_on_site_count - users_on_site_count
 * @property {number} ts - ts
 * @property {string} repoName - the repository name
 * @property {string} panel_name - panel_name
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} pageId - page_id
 * @property {string} errorType - what is the error for which the modal appeared (null = manual disconnect)
 * @property {string} token - token
 * @property {number} users_with_devmode_count - users_with_devmode_count
 * @property {string} ver - version
 * @property {string} gitUserName - github_user_name
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {string} dev_mode - dev_mode
 * @property {string} appId - app_id
 * @property {GUID} esi - esi
 * @property {GUID} msid - msid
 * @property {string} action - what did the user try to do
 * @property {string} ds_id - ds_id
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<githubDisconnectActionParams>} LogObject<githubDisconnectActionParams>
 */
export function githubDisconnectAction(params) {
    return { evid: 352, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'gitHubDisconnectStatus' event (evid: 353)
 *
 * Event description: when a users attempt to disconnect from github
 *
 * @param {gitHubDisconnectStatusParams} params - The parameters for this event
 * @property {string} app_name - app_name
 * @property {string} app_id - app_id
 * @property {boolean} success - was the disconnect success
 * @property {number} users_with_write_devmode_count - users_with_write_devmode_count
 * @property {string} builderType - builder_type
 * @property {GUID} visitorId - visitor_id
 * @property {string} ds_type - ds_type
 * @property {GUID} site_id - site_id
 * @property {string} panel_id - panel_id
 * @property {string} appName - app_name
 * @property {number} users_on_site_count - users_on_site_count
 * @property {number} ts - ts
 * @property {string} repoName - the site's repository name
 * @property {string} panel_name - panel_name
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} pageId - page_id
 * @property {string} errorType - what is the error for which the modal appeared (null = manual disconnect)
 * @property {string} token - token
 * @property {number} users_with_devmode_count - users_with_devmode_count
 * @property {string} ver - version
 * @property {string} gitUserName - the github username
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {string} dev_mode - dev_mode
 * @property {string} appId - app_id
 * @property {GUID} esi - esi
 * @property {GUID} msid - msid
 * @property {string} ds_id - ds_id
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<gitHubDisconnectStatusParams>} LogObject<gitHubDisconnectStatusParams>
 */
export function gitHubDisconnectStatus(params) {
    return { evid: 353, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'cloneAndDevelopClicked' event (evid: 354)
 *
 * Event description: when a wix site is connected to github and with dev mode users can see a snakebar indicating that the code is read only and that they can change the code using github - this event is fired when this clone& develop buttons is clicked
 *
 * @param {cloneAndDevelopClickedParams} params - The parameters for this event
 * @property {string} app_name - app_name
 * @property {string} app_id - app_id
 * @property {number} users_with_write_devmode_count - users_with_write_devmode_count
 * @property {string} builderType - builder_type
 * @property {GUID} visitorId - visitor_id
 * @property {string} ds_type - ds_type
 * @property {GUID} site_id - site_id
 * @property {string} panel_id - panel_id
 * @property {string} appName - app_name
 * @property {number} users_on_site_count - users_on_site_count
 * @property {number} ts - ts
 * @property {string} repoName - the github repo name
 * @property {string} panel_name - panel_name
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} pageId - page_id
 * @property {string} token - token
 * @property {number} users_with_devmode_count - users_with_devmode_count
 * @property {string} ver - version
 * @property {string} gitUserName - github user name
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {string} dev_mode - dev_mode
 * @property {string} appId - app_id
 * @property {GUID} esi - esi
 * @property {GUID} msid - msid
 * @property {string} ds_id - ds_id
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<cloneAndDevelopClickedParams>} LogObject<cloneAndDevelopClickedParams>
 */
export function cloneAndDevelopClicked(params) {
    return { evid: 354, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'syncContentChangesPopUpAction' event (evid: 357)
 *
 * Event description: pop up that is presented if a user was tries to sync their changes with the live editor
 *
 * @param {syncContentChangesPopUpActionParams} params - The parameters for this event
 * @property {string} app_name - app_name
 * @property {string} app_id - app_id
 * @property {number} users_with_write_devmode_count - users_with_write_devmode_count
 * @property {string} builderType - builder_type
 * @property {GUID} visitorId - visitor_id
 * @property {string} ds_type - ds_type
 * @property {GUID} site_id - site_id
 * @property {string} panel_id - panel_id
 * @property {string} appName - app_name
 * @property {number} users_on_site_count - users_on_site_count
 * @property {number} ts - ts
 * @property {string} panel_name - panel_name
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} pageId - page_id
 * @property {boolean} dontShowAgain - did the user click on don't show again
 * @property {string} token - token
 * @property {number} users_with_devmode_count - users_with_devmode_count
 * @property {string} ver - version
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {string} dev_mode - dev_mode
 * @property {string} appId - app_id
 * @property {GUID} esi - esi
 * @property {GUID} msid - msid
 * @property {string} action - one of {continue, cancel, x, learn more}
 * @property {string} ds_id - ds_id
 * @property {number} siterevision - the revision of the site
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<syncContentChangesPopUpActionParams>} LogObject<syncContentChangesPopUpActionParams>
 */
export function syncContentChangesPopUpAction(params) {
    return { evid: 357, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'syncContentChangesSuccessful' event (evid: 358)
 *
 * Event description: when a user syncs their changes in the live editor to the local code - will fire after the sync was attempted to provide the successful status
 *
 * @param {syncContentChangesSuccessfulParams} params - The parameters for this event
 * @property {string} app_name - app_name
 * @property {string} app_id - app_id
 * @property {boolean} success - was the sync success
 * @property {number} users_with_write_devmode_count - users_with_write_devmode_count
 * @property {string} builderType - builder_type
 * @property {GUID} visitorId - visitor_id
 * @property {string} ds_type - ds_type
 * @property {GUID} site_id - site_id
 * @property {string} panel_id - panel_id
 * @property {string} appName - app_name
 * @property {number} users_on_site_count - users_on_site_count
 * @property {number} ts - ts
 * @property {string} panel_name - panel_name
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} pageId - page_id
 * @property {string} token - token
 * @property {number} users_with_devmode_count - users_with_devmode_count
 * @property {string} ver - version
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {string} dev_mode - dev_mode
 * @property {string} appId - app_id
 * @property {GUID} esi - esi
 * @property {GUID} msid - msid
 * @property {string} ds_id - ds_id
 * @property {number} siterevision - the revision of the site
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<syncContentChangesSuccessfulParams>} LogObject<syncContentChangesSuccessfulParams>
 */
export function syncContentChangesSuccessful(params) {
    return { evid: 358, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'unableToSyncContentChangesPopUpAction' event (evid: 359)
 *
 * Event description: pop up that is presented if a user was unable to sync their changes with the live editor
 *
 * @param {unableToSyncContentChangesPopUpActionParams} params - The parameters for this event
 * @property {string} app_name - app_name
 * @property {string} app_id - app_id
 * @property {number} users_with_write_devmode_count - users_with_write_devmode_count
 * @property {string} builderType - builder_type
 * @property {GUID} visitorId - visitor_id
 * @property {string} ds_type - ds_type
 * @property {GUID} site_id - site_id
 * @property {string} panel_id - panel_id
 * @property {string} appName - app_name
 * @property {number} users_on_site_count - users_on_site_count
 * @property {number} ts - ts
 * @property {string} panel_name - panel_name
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} pageId - page_id
 * @property {string} token - token
 * @property {number} users_with_devmode_count - users_with_devmode_count
 * @property {string} ver - version
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {string} dev_mode - dev_mode
 * @property {string} appId - app_id
 * @property {GUID} esi - esi
 * @property {GUID} msid - msid
 * @property {string} action - one of {continue, cancel, x, learn more}
 * @property {string} ds_id - ds_id
 * @property {number} siterevision - the revision of the site
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<unableToSyncContentChangesPopUpActionParams>} LogObject<unableToSyncContentChangesPopUpActionParams>
 */
export function unableToSyncContentChangesPopUpAction(params) {
    return { evid: 359, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'dsFilterPanelSaveButtonClicked' event (evid: 36)
 *
 * Event description: DS filter panel save button clicked
 *
 * @param {dsFilterPanelSaveButtonClickedParams} params - The parameters for this event
 * @property {string} app_name - app_name
 * @property {string} app_id - app_id
 * @property {string} renderingFlow - Rendering flow
 * @property {number} users_with_write_devmode_count - users_with_write_devmode_count
 * @property {string} builderType - builder_type
 * @property {GUID} visitorId - visitor_id
 * @property {string} ds_type - ds_type
 * @property {GUID} site_id - site_id
 * @property {string} panel_id - panel_id
 * @property {string} appName - app_name
 * @property {number} users_on_site_count - users_on_site_count
 * @property {number} ts - ts
 * @property {string} panel_name - panel_name
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} filter - filter (json)
 * @property {string} pageId - page_id
 * @property {string} token - token
 * @property {number} users_with_devmode_count - users_with_devmode_count
 * @property {string} ver - version
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {string} mode - mode
 * @property {string} dev_mode - dev_mode
 * @property {string} appId - app_id
 * @property {GUID} esi - esi
 * @property {GUID} msid - msid
 * @property {string} ds_id - ds_id
 * @property {string} origin - origin
 * @property {string} collection_id - collection_id
 * @return {LogObject<dsFilterPanelSaveButtonClickedParams>} LogObject<dsFilterPanelSaveButtonClickedParams>
 */
export function dsFilterPanelSaveButtonClicked(params) {
    return { evid: 36, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'localCodeChangesSyncToLiveEditor' event (evid: 360)
 *
 * Event description: when a user on live editor's code is being sync from the local code change
 *
 * @param {localCodeChangesSyncToLiveEditorParams} params - The parameters for this event
 * @property {string} app_name - app_name
 * @property {string} app_id - app_id
 * @property {number} users_with_write_devmode_count - users_with_write_devmode_count
 * @property {string} builderType - builder_type
 * @property {GUID} visitorId - visitor_id
 * @property {string} ds_type - ds_type
 * @property {GUID} site_id - site_id
 * @property {string} panel_id - panel_id
 * @property {string} appName - app_name
 * @property {string} editor_view_mode - preview/edit
 * @property {number} users_on_site_count - users_on_site_count
 * @property {number} ts - ts
 * @property {string} panel_name - panel_name
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} pageId - page_id
 * @property {string} token - token
 * @property {number} users_with_devmode_count - users_with_devmode_count
 * @property {string} ver - version
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {string} dev_mode - dev_mode
 * @property {string} appId - app_id
 * @property {GUID} esi - esi
 * @property {GUID} msid - msid
 * @property {string} ds_id - ds_id
 * @property {number} siterevision - the revision of the site
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<localCodeChangesSyncToLiveEditorParams>} LogObject<localCodeChangesSyncToLiveEditorParams>
 */
export function localCodeChangesSyncToLiveEditor(params) {
    return { evid: 360, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'convertToDynamicOnDuplicateModal' event (evid: 361)
 *
 * Event description: Actions with 'Convert To Dynamic' Modal which is shown to user after performing duplicate page actions
 *
 * @param {convertToDynamicOnDuplicateModalParams} params - The parameters for this event
 * @property {string} app_name - app_name
 * @property {string} app_id - app_id
 * @property {number} users_with_write_devmode_count - users_with_write_devmode_count
 * @property {string} builderType - builder_type
 * @property {GUID} visitorId - visitor_id
 * @property {boolean} is_first_data_modal - is this the first data modal the user see
 * @property {string} ds_type - ds_type
 * @property {string} action_name - {show, close, click_convert}
 * @property {GUID} site_id - site_id
 * @property {string} panel_id - panel_id
 * @property {string} appName - app_name
 * @property {number} users_on_site_count - users_on_site_count
 * @property {number} ts - ts
 * @property {string} panel_name - panel_name
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} pageId - page_id
 * @property {string} origin - duplicate_3_pages
 * @property {string} token - token
 * @property {number} users_with_devmode_count - users_with_devmode_count
 * @property {string} ver - version
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {string} dev_mode - dev_mode
 * @property {string} appId - app_id
 * @property {GUID} esi - esi
 * @property {GUID} msid - msid
 * @property {string} ds_id - ds_id
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<convertToDynamicOnDuplicateModalParams>} LogObject<convertToDynamicOnDuplicateModalParams>
 */
export function convertToDynamicOnDuplicateModal(params) {
    return { evid: 361, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'clickConnectToDataButtonAtUouFilterSelector' event (evid: 362)
 *
 * Event description: User clicks at dropdown / radio_button / slider / multi_checkbox / selecting_tags / single_checkbox 'Connect to Data' settings in order to make it filter selector
 *
 * @param {clickConnectToDataButtonAtUouFilterSelectorParams} params - The parameters for this event
 * @property {string} app_name - app_name
 * @property {string} app_id - app_id
 * @property {number} users_with_write_devmode_count - users_with_write_devmode_count
 * @property {string} builderType - builder_type
 * @property {GUID} visitorId - visitor_id
 * @property {string} ds_type - ds_type
 * @property {GUID} site_id - site_id
 * @property {string} panel_id - panel_id
 * @property {string} appName - app_name
 * @property {number} users_on_site_count - users_on_site_count
 * @property {string} uuid - name
 * @property {number} ts - ts
 * @property {string} panel_name - panel_name
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} page_name - page_name
 * @property {string} pageId - page_id
 * @property {string} token - token
 * @property {number} users_with_devmode_count - users_with_devmode_count
 * @property {string} ver - version
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {string} dev_mode - dev_mode
 * @property {string} appId - app_id
 * @property {GUID} esi - esi
 * @property {string} component_type - dropdown/radio_button/slider/multi_checkbox/selecting_tags/single_checkbox
 * @property {GUID} msid - msid
 * @property {string} ds_id - ds_id
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<clickConnectToDataButtonAtUouFilterSelectorParams>} LogObject<clickConnectToDataButtonAtUouFilterSelectorParams>
 */
export function clickConnectToDataButtonAtUouFilterSelector(params) {
    return { evid: 362, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'selectPurposeOfUouFilterSelector' event (evid: 363)
 *
 * Event description: User selects whether to use dropdown / radio_button / slider / multi_checkbox / selecting_tags / single_checkbox in order to filter or to submit value
 *
 * @param {selectPurposeOfUouFilterSelectorParams} params - The parameters for this event
 * @property {string} app_name - app_name
 * @property {string} app_id - app_id
 * @property {number} users_with_write_devmode_count - users_with_write_devmode_count
 * @property {string} builderType - builder_type
 * @property {GUID} visitorId - visitor_id
 * @property {string} ds_type - ds_type
 * @property {GUID} site_id - site_id
 * @property {string} panel_id - panel_id
 * @property {string} appName - app_name
 * @property {number} users_on_site_count - users_on_site_count
 * @property {string} uuid - name
 * @property {number} ts - ts
 * @property {string} panel_name - panel_name
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} page_name - page_name
 * @property {string} pageId - page_id
 * @property {string} token - token
 * @property {number} users_with_devmode_count - users_with_devmode_count
 * @property {string} ver - version
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {string} dev_mode - dev_mode
 * @property {string} appId - app_id
 * @property {GUID} esi - esi
 * @property {string} component_type - dropdown/radio_button/slider/multi_checkbox/selecting_tags/single_checkbox
 * @property {GUID} msid - msid
 * @property {string} action - submit_value/filter
 * @property {string} ds_id - ds_id
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<selectPurposeOfUouFilterSelectorParams>} LogObject<selectPurposeOfUouFilterSelectorParams>
 */
export function selectPurposeOfUouFilterSelector(params) {
    return { evid: 363, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'uouFilterConnectToDataset' event (evid: 364)
 *
 * Event description: User connects filtering dropdown / radio_button / slider / multi_checkbox / selecting_tags / single_checkbox to dataset
 *
 * @param {uouFilterConnectToDatasetParams} params - The parameters for this event
 * @property {string} app_name - app_name
 * @property {string} app_id - app_id
 * @property {string} dataset_name - dataset name
 * @property {number} users_with_write_devmode_count - users_with_write_devmode_count
 * @property {string} builderType - builder_type
 * @property {GUID} visitorId - visitor_id
 * @property {string} ds_type - ds_type
 * @property {GUID} site_id - site_id
 * @property {string} panel_id - panel_id
 * @property {string} appName - app_name
 * @property {number} users_on_site_count - users_on_site_count
 * @property {string} uuid - name
 * @property {number} ts - ts
 * @property {string} collection_id - collection_id
 * @property {string} panel_name - panel_name
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} page_name - page_name
 * @property {string} pageId - page_id
 * @property {string} token - token
 * @property {number} users_with_devmode_count - users_with_devmode_count
 * @property {string} ver - version
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {string} dev_mode - dev_mode
 * @property {string} appId - app_id
 * @property {GUID} esi - esi
 * @property {string} collection_name - collection_name
 * @property {string} component_type - dropdown/radio_button/slider/multi_checkbox/selecting_tags/single_checkbox
 * @property {GUID} msid - msid
 * @property {string} ds_id - ds_id
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<uouFilterConnectToDatasetParams>} LogObject<uouFilterConnectToDatasetParams>
 */
export function uouFilterConnectToDataset(params) {
    return { evid: 364, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'uouFilterSelectFilteringField' event (evid: 365)
 *
 * Event description: User selects field for filtering dropdown / radio_button / slider / multi_checkbox / selecting_tags / single_checkbox
 *
 * @param {uouFilterSelectFilteringFieldParams} params - The parameters for this event
 * @property {string} app_name - app_name
 * @property {string} app_id - app_id
 * @property {string} dataset_name - dataset name
 * @property {number} users_with_write_devmode_count - users_with_write_devmode_count
 * @property {string} builderType - builder_type
 * @property {GUID} visitorId - visitor_id
 * @property {string} ds_type - ds_type
 * @property {GUID} site_id - site_id
 * @property {string} panel_id - panel_id
 * @property {string} appName - app_name
 * @property {number} users_on_site_count - users_on_site_count
 * @property {string} uuid - name
 * @property {number} ts - ts
 * @property {string} collection_id - collection_id
 * @property {string} panel_name - panel_name
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} page_name - page name
 * @property {string} pageId - page_id
 * @property {string} token - token
 * @property {number} users_with_devmode_count - users_with_devmode_count
 * @property {string} ver - version
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {string} dev_mode - dev_mode
 * @property {string} appId - app_id
 * @property {GUID} esi - esi
 * @property {string} collection_name - collection name
 * @property {string} component_type - dropdown/radio_button/slider/multi_checkbox/selecting_tags/single_checkbox
 * @property {GUID} msid - msid
 * @property {string} field_name - selected field name
 * @property {string} ds_id - ds_id
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<uouFilterSelectFilteringFieldParams>} LogObject<uouFilterSelectFilteringFieldParams>
 */
export function uouFilterSelectFilteringField(params) {
    return { evid: 365, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'uouFilterFilteringDataOnActivePagePreviewMode' event (evid: 366)
 *
 * Event description: Filter was applied to data set active on page - preview mode
 *
 * @param {uouFilterFilteringDataOnActivePagePreviewModeParams} params - The parameters for this event
 * @property {string} app_name - app_name
 * @property {string} app_id - app_id
 * @property {string} filtered_by - {name of column}
 * @property {number} users_with_write_devmode_count - users_with_write_devmode_count
 * @property {string} builderType - builder_type
 * @property {GUID} visitorId - visitor_id
 * @property {string} ds_type - ds_type
 * @property {GUID} site_id - site_id
 * @property {string} panel_id - panel_id
 * @property {string} appName - app_name
 * @property {number} users_on_site_count - users_on_site_count
 * @property {number} ts - ts
 * @property {string} collection_id - collection_id
 * @property {string} collection_origin - one of: 'manual', 'external', 'preset', 'template'
 * @property {string} panel_name - panel_name
 * @property {string} viewmode - viewmode
 * @property {string} input_name - {}
 * @property {string} ds_name - data set name
 * @property {string} page_name - {}
 * @property {string} pageId - page_id
 * @property {string} token - token
 * @property {number} users_with_devmode_count - users_with_devmode_count
 * @property {string} ver - version
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {string} mode - {read-write, read, write}
 * @property {string} dev_mode - dev_mode
 * @property {string} appId - app_id
 * @property {GUID} esi - esi
 * @property {string} collection_name - this is also collection_id!
 * @property {GUID} msid - msid
 * @property {Url} page_url - {page_url}
 * @property {string} number_of_results - filter_results
 * @property {string} ds_id - ds_id
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<uouFilterFilteringDataOnActivePagePreviewModeParams>} LogObject<uouFilterFilteringDataOnActivePagePreviewModeParams>
 */
export function uouFilterFilteringDataOnActivePagePreviewMode(params) {
    return { evid: 366, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'spiCalledInPreviewMode' event (evid: 367)
 *
 * Event description: SPI runs on-site preview
 *  Only for preview mode (Not published sites)
 *
 * @param {spiCalledInPreviewModeParams} params - The parameters for this event
 * @property {string} app_name - app_name
 * @property {string} app_id - app_id
 * @property {number} users_with_write_devmode_count - users_with_write_devmode_count
 * @property {string} builderType - builder_type
 * @property {GUID} visitorId - visitor_id
 * @property {string} ds_type - ds_type
 * @property {GUID} site_id - site_id
 * @property {string} panel_id - panel_id
 * @property {string} file_name - file name
 * @property {string} appName - app_name
 * @property {number} users_on_site_count - users_on_site_count
 * @property {number} ts - ts
 * @property {boolean} exception - {}
 * @property {string} panel_name - panel_name
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} pageId - page_id
 * @property {GUID} vsi - vsi
 * @property {string} token - token
 * @property {number} users_with_devmode_count - users_with_devmode_count
 * @property {string} ver - version
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {string} dev_mode - dev_mode
 * @property {string} appId - app_id
 * @property {GUID} esi - esi
 * @property {GUID} msid - msid
 * @property {Url} page_url - page_url
 * @property {string} ds_id - ds_id
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<spiCalledInPreviewModeParams>} LogObject<spiCalledInPreviewModeParams>
 */
export function spiCalledInPreviewMode(params) {
    return { evid: 367, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'pluginsRunOnPreview' event (evid: 368)
 *
 * Event description: plugins run onsite preview
 * (sent from preview)
 *
 * @param {pluginsRunOnPreviewParams} params - The parameters for this event
 * @property {string} app_name - app_name
 * @property {string} app_id - app_id
 * @property {number} users_with_write_devmode_count - users_with_write_devmode_count
 * @property {string} builderType - builder_type
 * @property {string} ds_type - ds_type
 * @property {GUID} site_id - site_id
 * @property {string} panel_id - panel_id
 * @property {string} file_name - file name
 * @property {string} appName - app_name
 * @property {number} users_on_site_count - users_on_site_count
 * @property {number} ts - ts
 * @property {boolean} exception - {}
 * @property {string} panel_name - panel_name
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} pageId - page_id
 * @property {GUID} vsi - vsi
 * @property {string} token - token
 * @property {number} users_with_devmode_count - users_with_devmode_count
 * @property {string} ver - version
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {string} dev_mode - dev_mode
 * @property {string} appId - app_id
 * @property {GUID} esi - esi
 * @property {GUID} msid - msid
 * @property {string} ds_id - ds_id
 * @property {string} visitor_id - visitor_id
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<pluginsRunOnPreviewParams>} LogObject<pluginsRunOnPreviewParams>
 */
export function pluginsRunOnPreview(params) {
    return { evid: 368, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'connectExternalDbPanelAction' event (evid: 369)
 *
 * Event description: Fires when the user interacts with the 'connect external DB' panel
 *
 * @param {connectExternalDbPanelActionParams} params - The parameters for this event
 * @property {string} app_name - app_name
 * @property {string} app_id - app_id
 * @property {number} users_with_write_devmode_count - users_with_write_devmode_count
 * @property {string} builderType - builder_type
 * @property {GUID} visitorId - visitor_id
 * @property {string} ds_type - ds_type
 * @property {GUID} site_id - site_id
 * @property {string} panel_id - panel_id
 * @property {string} appName - app_name
 * @property {number} users_on_site_count - users_on_site_count
 * @property {number} ts - ts
 * @property {string} target_url - {}
 * @property {string} button_name - {connect, back, info, next, configure_adapter, x, cancel, edit_settings }
 * @property {string} panel_name - panel_name
 * @property {string} viewmode - viewmode
 * @property {string} input_name - {wixCode.panels.externalDB.select_provider, wixCode.panels.externalDB.connect_adapter, wixCode.panels.externalDB.success_page, wixCode.panels.externalDB.edit_adapter, wixCode.panels.externalDB.connection_issue
 * @property {string} selected_provider - {google_cloud, azure, amazon_web_services, custom}
 * @property {string} selected_database_type - selected_database_type
 * @property {boolean} isSuccess - connect adapter succescfuly?
 * @property {string} pageId - page_id
 * @property {string} selected_flow - selected_flow
 * @property {string} token - token
 * @property {number} users_with_devmode_count - users_with_devmode_count
 * @property {string} ver - version
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {string} dev_mode - dev_mode
 * @property {string} databaseName - database_name
 * @property {string} appId - app_id
 * @property {GUID} esi - esi
 * @property {GUID} msid - msid
 * @property {string} action_type - {click, hover}
 * @property {string} ds_id - ds_id
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<connectExternalDbPanelActionParams>} LogObject<connectExternalDbPanelActionParams>
 */
export function connectExternalDbPanelAction(params) {
    return { evid: 369, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'platformApiCallHybridMethods' event (evid: 370)
 *
 * Event description: Platform API call Hybrid methods
 *
 * @param {platformApiCallHybridMethodsParams} params - The parameters for this event
 * @property {string} app_name - app_name
 * @property {string} app_id - app_id
 * @property {string} module - module
 * @property {number} users_with_write_devmode_count - users_with_write_devmode_count
 * @property {string} builderType - builder_type
 * @property {GUID} visitorId - visitor_id
 * @property {string} ds_type - ds_type
 * @property {GUID} site_id - site_id
 * @property {string} panel_id - panel_id
 * @property {string} appName - app_name
 * @property {number} users_on_site_count - users_on_site_count
 * @property {number} ts - ts
 * @property {string} panel_name - panel_name
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} appDefinitionId - app definition id
 * @property {string} pageId - page_id
 * @property {string} function - function name
 * @property {string} token - token
 * @property {number} users_with_devmode_count - users_with_devmode_count
 * @property {string} ver - version
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {string} dev_mode - dev_mode
 * @property {string} appId - app_id
 * @property {GUID} esi - esi
 * @property {GUID} msid - msid
 * @property {string} ds_id - ds_id
 * @property {string} arguments - argument
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<platformApiCallHybridMethodsParams>} LogObject<platformApiCallHybridMethodsParams>
 */
export function platformApiCallHybridMethods(params) {
    return { evid: 370, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'uouFilterFilteringDataOnActivePageLiveSite' event (evid: 371)
 *
 * Event description: UoU filter - filtering data on active page (live site)
 *
 * @param {uouFilterFilteringDataOnActivePageLiveSiteParams} params - The parameters for this event
 * @property {GUID} _event_hash - event_hash
 * @property {GUID} visitor_id - visitor_id
 * @property {string} app_name - app_name
 * @property {string} app_id - app_id
 * @property {string} filtered_by - filtered by which field
 * @property {string} builderType - builder_type
 * @property {string} ds_type - ds_type
 * @property {GUID} site_id - site_id
 * @property {string} panel_id - panel_id
 * @property {string} appName - app_name
 * @property {number} ts - ts
 * @property {string} collection_id - collection_id
 * @property {string} collection_origin - one of: 'manual', 'external', 'preset', 'template'
 * @property {string} panel_name - panel_name
 * @property {string} viewmode - viewmode
 * @property {string} input_name - {}
 * @property {string} ds_name - data set name
 * @property {string} page_name - page name
 * @property {string} pageId - page_id
 * @property {GUID} vsi - vsi
 * @property {string} token - token
 * @property {string} ver - version
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {string} mode - {read-write, read, write}
 * @property {string} appId - app_id
 * @property {GUID} esi - esi
 * @property {string} collection_name - this is also collection_id!
 * @property {GUID} msid - msid
 * @property {number} tsp - tsp
 * @property {Url} page_url - page url
 * @property {string} number_of_results - number of results
 * @property {string} ds_id - ds_id
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<uouFilterFilteringDataOnActivePageLiveSiteParams>} LogObject<uouFilterFilteringDataOnActivePageLiveSiteParams>
 */
export function uouFilterFilteringDataOnActivePageLiveSite(params) {
    return { evid: 371, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'propertiesPanelClickOnTab' event (evid: 372)
 *
 * Event description: Sent when the user clicks on CSS properties/JS properties tab in properties panel
 *
 * @param {propertiesPanelClickOnTabParams} params - The parameters for this event
 * @property {string} app_name - app_name
 * @property {string} app_id - app_id
 * @property {number} users_with_write_devmode_count - users_with_write_devmode_count
 * @property {string} builderType - builder_type
 * @property {string} path - the file path of the opened file
 * @property {GUID} visitorId - visitor_id
 * @property {string} ds_type - ds_type
 * @property {GUID} site_id - site_id
 * @property {string} panel_id - panel_id
 * @property {string} tab - the tab clicked on-  CSS properties/ JS properties
 * @property {string} appName - app_name
 * @property {number} users_on_site_count - users_on_site_count
 * @property {number} ts - ts
 * @property {string} panel_name - panel_name
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} pageId - page_id
 * @property {string} token - token
 * @property {number} users_with_devmode_count - users_with_devmode_count
 * @property {string} ver - version
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {string} dev_mode - dev_mode
 * @property {string} appId - app_id
 * @property {GUID} esi - esi
 * @property {GUID} msid - msid
 * @property {string} ds_id - ds_id
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<propertiesPanelClickOnTabParams>} LogObject<propertiesPanelClickOnTabParams>
 */
export function propertiesPanelClickOnTab(params) {
    return { evid: 372, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'globalCssFileDelete' event (evid: 373)
 *
 * Event description: Approved delete global CSS file from the modal
 *
 * @param {globalCssFileDeleteParams} params - The parameters for this event
 * @property {string} app_name - app_name
 * @property {string} app_id - app_id
 * @property {number} users_with_write_devmode_count - users_with_write_devmode_count
 * @property {string} builderType - builder_type
 * @property {GUID} visitorId - visitor_id
 * @property {string} ds_type - ds_type
 * @property {GUID} site_id - site_id
 * @property {string} panel_id - panel_id
 * @property {string} appName - app_name
 * @property {number} users_on_site_count - users_on_site_count
 * @property {number} ts - ts
 * @property {string} panel_name - panel_name
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} pageId - page_id
 * @property {string} item_name - item_name
 * @property {string} token - token
 * @property {number} users_with_devmode_count - users_with_devmode_count
 * @property {string} ver - version
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {string} dev_mode - dev_mode
 * @property {string} appId - app_id
 * @property {GUID} esi - esi
 * @property {string} item_type - item_type (page,file,collection)
 * @property {GUID} msid - msid
 * @property {string} ds_id - ds_id
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<globalCssFileDeleteParams>} LogObject<globalCssFileDeleteParams>
 */
export function globalCssFileDelete(params) {
    return { evid: 373, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'connectToDataChooseLinkBehaviour' event (evid: 374)
 *
 * Event description: User chooses how the link will be opened while connecting to a link
 *
 * @param {connectToDataChooseLinkBehaviourParams} params - The parameters for this event
 * @property {string} app_name - app_name
 * @property {string} app_id - app_id
 * @property {string} source - source of panel opening
 * @property {number} users_with_write_devmode_count - users_with_write_devmode_count
 * @property {string} builderType - builder_type
 * @property {GUID} visitorId - visitor_id
 * @property {string} ds_type - ds_type
 * @property {GUID} site_id - site_id
 * @property {string} panel_id - panel_id
 * @property {string} data_set_name - data_set_name
 * @property {string} appName - app_name
 * @property {number} users_on_site_count - users_on_site_count
 * @property {string} field_type - field_type
 * @property {number} ts - ts
 * @property {string} collection_id - collection id
 * @property {string} ref_field_name - ref_field_name
 * @property {string} collection_origin - one of: 'manual', 'external', 'preset', 'template'
 * @property {string} panel_name - panel_name
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} pageId - page_id
 * @property {string} token - token
 * @property {number} users_with_devmode_count - users_with_devmode_count
 * @property {string} ver - version
 * @property {string} userEnv - user_env
 * @property {string} repeater_parent_id - if attached to repeater - this will be the repeater id
 * @property {string} editor_working_mode - editor_working_mode
 * @property {string} property - property
 * @property {string} dev_mode - dev_mode
 * @property {string} appId - app_id
 * @property {GUID} esi - esi
 * @property {string} component_id - component_id
 * @property {boolean} is_single_item_collection - (true/false)
 * @property {string} collection_name - collection name
 * @property {string} component_type - comp type
 * @property {GUID} msid - msid
 * @property {string} field_name - field_name
 * @property {string} value - {current_window, new_window}
 * @property {string} connect_option - dp / field
 * @property {string} ds_id - ds_id
 * @property {string} ref_field_type - reference  field type
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<connectToDataChooseLinkBehaviourParams>} LogObject<connectToDataChooseLinkBehaviourParams>
 */
export function connectToDataChooseLinkBehaviour(params) {
    return { evid: 374, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'autoCachOnAndMightHaveNegativeImpact' event (evid: 375)
 *
 * Event description:  Only for preview mode
 *  Only for automated ssr cache pages
 *  Sent each time the user click preview and the page is found as non cach eligible (meaning that cache might have a negative impact)
 * Include pageId,esi (implicit fields)
 *
 * @param {autoCachOnAndMightHaveNegativeImpactParams} params - The parameters for this event
 * @property {string} app_name - app_name
 * @property {string} app_id - app_id
 * @property {number} users_with_write_devmode_count - users_with_write_devmode_count
 * @property {string} builderType - builder_type
 * @property {GUID} visitorId - {}
 * @property {string} ds_type - ds_type
 * @property {GUID} site_id - site_id
 * @property {string} panel_id - panel_id
 * @property {string} appName - app_name
 * @property {number} users_on_site_count - users_on_site_count
 * @property {number} ts - ts
 * @property {string} panel_name - panel_name
 * @property {string} viewmode - viewmode
 * @property {string} input_name - {}
 * @property {string} page_name - page_name
 * @property {string} pageId - page_id
 * @property {string} token - token
 * @property {number} users_with_devmode_count - users_with_devmode_count
 * @property {string} ver - version
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {string} dev_mode - dev_mode
 * @property {string} appId - app_id
 * @property {Url} pageurl - page_url
 * @property {GUID} esi - esi
 * @property {GUID} msid - metasite id
 * @property {string} ds_id - ds_id
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<autoCachOnAndMightHaveNegativeImpactParams>} LogObject<autoCachOnAndMightHaveNegativeImpactParams>
 */
export function autoCachOnAndMightHaveNegativeImpact(params) {
    return { evid: 375, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'disconnectModalShown' event (evid: 376)
 *
 * Event description: when a users is shown a disconnect modal for github integration (user clicks disconnect or there is an issue from github side)
 *
 * @param {disconnectModalShownParams} params - The parameters for this event
 * @property {string} app_name - app_name
 * @property {string} app_id - app_id
 * @property {number} users_with_write_devmode_count - users_with_write_devmode_count
 * @property {string} builderType - builder_type
 * @property {GUID} visitorId - visitor_id
 * @property {string} ds_type - ds_type
 * @property {GUID} site_id - site_id
 * @property {string} panel_id - panel_id
 * @property {string} appName - app_name
 * @property {number} users_on_site_count - users_on_site_count
 * @property {number} ts - ts
 * @property {string} repoName - the repository name
 * @property {string} panel_name - panel_name
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} pageId - page_id
 * @property {string} errorType - what is the error for which the modal appeared (null = manual disconnect)
 * @property {string} token - token
 * @property {number} users_with_devmode_count - users_with_devmode_count
 * @property {string} ver - version
 * @property {string} gitUserName - github_user_name
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {string} dev_mode - dev_mode
 * @property {string} appId - app_id
 * @property {GUID} esi - esi
 * @property {GUID} msid - msid
 * @property {string} ds_id - ds_id
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<disconnectModalShownParams>} LogObject<disconnectModalShownParams>
 */
export function disconnectModalShown(params) {
    return { evid: 376, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'uouFilterLinkFromRepeaterClick' event (evid: 377)
 *
 * Event description: UoU filter - Users clicks the Link from Repeater
 *
 * @param {uouFilterLinkFromRepeaterClickParams} params - The parameters for this event
 * @property {string} app_name - app_name
 * @property {string} app_id - app_id
 * @property {string} dataset_name - dataset name
 * @property {number} users_with_write_devmode_count - users_with_write_devmode_count
 * @property {string} builderType - builder_type
 * @property {GUID} visitorId - visitor_id
 * @property {string} ds_type - ds_type
 * @property {GUID} site_id - site_id
 * @property {string} panel_id - panel_id
 * @property {string} appName - app_name
 * @property {number} users_on_site_count - users_on_site_count
 * @property {string} uuid - name
 * @property {number} ts - ts
 * @property {string} collection_id - collection_id
 * @property {string} panel_name - panel_name
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} page_name - page name
 * @property {string} pageId - page_id
 * @property {string} token - token
 * @property {number} users_with_devmode_count - users_with_devmode_count
 * @property {string} ver - version
 * @property {string} userEnv - user_env
 * @property {string} repeater_parent_id - repeater_id
 * @property {string} editor_working_mode - editor_working_mode
 * @property {string} dev_mode - dev_mode
 * @property {string} appId - app_id
 * @property {GUID} esi - esi
 * @property {string} collection_name - collection_name
 * @property {GUID} msid - msid
 * @property {string} ds_id - ds_id
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<uouFilterLinkFromRepeaterClickParams>} LogObject<uouFilterLinkFromRepeaterClickParams>
 */
export function uouFilterLinkFromRepeaterClick(params) {
    return { evid: 377, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'staticToDynamicSelectNewExistingCollection' event (evid: 378)
 *
 * Event description: Static To Dynamic - Select New/Existing Collection
 *
 * @param {staticToDynamicSelectNewExistingCollectionParams} params - The parameters for this event
 * @property {string} app_name - app_name
 * @property {string} app_id - app_id
 * @property {number} users_with_write_devmode_count - users_with_write_devmode_count
 * @property {string} builderType - builder_type
 * @property {GUID} visitorId - visitor_id
 * @property {string} ds_type - ds_type
 * @property {GUID} site_id - site_id
 * @property {string} panel_id - panel_id
 * @property {string} appName - app_name
 * @property {number} users_on_site_count - users_on_site_count
 * @property {number} ts - ts
 * @property {string} panel_name - panel_name
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} pageId - page_id
 * @property {string} selection - new / existing
 * @property {string} origin - quickActionsMenu
 * @property {string} token - token
 * @property {number} users_with_devmode_count - users_with_devmode_count
 * @property {string} ver - version
 * @property {string} userEnv - user_env
 * @property {boolean} is_dmo - does the user see dev mode on experience in out feature?
 * @property {string} editor_working_mode - editor_working_mode
 * @property {string} dev_mode - dev_mode
 * @property {string} appId - app_id
 * @property {GUID} esi - esi
 * @property {GUID} msid - msid
 * @property {string} ds_id - ds_id
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<staticToDynamicSelectNewExistingCollectionParams>} LogObject<staticToDynamicSelectNewExistingCollectionParams>
 */
export function staticToDynamicSelectNewExistingCollection(params) {
    return { evid: 378, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'previewModeIdeMessageViewTestLiveSite' event (evid: 379)
 *
 * Event description:  Only for preview mode
 *
 * the message displays on ide when a user moves to preview mode
 * the message offers the user to view instead  test or live site since some wix features are only partially functional when using preview
 *
 * @param {previewModeIdeMessageViewTestLiveSiteParams} params - The parameters for this event
 * @property {string} app_name - app_name
 * @property {string} app_id - app_id
 * @property {number} users_with_write_devmode_count - users_with_write_devmode_count
 * @property {string} builderType - builder_type
 * @property {GUID} visitorId - {}
 * @property {string} ds_type - ds_type
 * @property {GUID} site_id - site_id
 * @property {string} panel_id - panel_id
 * @property {string} appName - app_name
 * @property {number} users_on_site_count - users_on_site_count
 * @property {number} ts - ts
 * @property {string} panel_name - panel_name
 * @property {string} viewmode - viewmode
 * @property {string} input_name - {}
 * @property {string} page_name - page_name
 * @property {string} pageId - page_id
 * @property {string} token - token
 * @property {number} users_with_devmode_count - users_with_devmode_count
 * @property {string} ver - version
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {string} dev_mode - dev_mode
 * @property {string} appId - app_id
 * @property {Url} pageurl - page_url
 * @property {GUID} esi - esi
 * @property {GUID} msid - metasite id
 * @property {string} ds_id - ds_id
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<previewModeIdeMessageViewTestLiveSiteParams>} LogObject<previewModeIdeMessageViewTestLiveSiteParams>
 */
export function previewModeIdeMessageViewTestLiveSite(params) {
    return { evid: 379, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'dsAddSortButtonClicked' event (evid: 38)
 *
 * Event description: DS add sort button clicked
 *
 * @param {dsAddSortButtonClickedParams} params - The parameters for this event
 * @property {string} app_name - app_name
 * @property {string} app_id - app_id
 * @property {number} users_with_write_devmode_count - users_with_write_devmode_count
 * @property {string} builderType - builder_type
 * @property {GUID} visitorId - visitor_id
 * @property {string} ds_type - ds_type
 * @property {GUID} site_id - site_id
 * @property {string} panel_id - panel_id
 * @property {string} appName - app_name
 * @property {number} users_on_site_count - users_on_site_count
 * @property {number} ts - ts
 * @property {string} collection_id - collection_id
 * @property {boolean} is_collection_order_applied - is_collection_order_applied
 * @property {string} panel_name - panel_name
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} pageId - page_id
 * @property {string} token - token
 * @property {number} users_with_devmode_count - users_with_devmode_count
 * @property {string} ver - version
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {string} dev_mode - dev_mode
 * @property {string} appId - app_id
 * @property {GUID} esi - esi
 * @property {GUID} msid - msid
 * @property {string} ds_id - ds_id
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<dsAddSortButtonClickedParams>} LogObject<dsAddSortButtonClickedParams>
 */
export function dsAddSortButtonClicked(params) {
    return { evid: 38, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'clickedOnLearnMoreViewInsteadTestLiveSiteMessageOnIde' event (evid: 380)
 *
 * Event description:  Sent when the user clicks on "learn more" on 'view instead test/live site' message on IDE
 *  preview mode
 *
 * @param {clickedOnLearnMoreViewInsteadTestLiveSiteMessageOnIdeParams} params - The parameters for this event
 * @property {string} app_name - app_name
 * @property {string} app_id - app_id
 * @property {number} users_with_write_devmode_count - users_with_write_devmode_count
 * @property {string} builderType - builder_type
 * @property {GUID} visitorId - visitorId
 * @property {string} ds_type - ds_type
 * @property {GUID} site_id - site_id
 * @property {string} panel_id - panel_id
 * @property {string} appName - app_name
 * @property {number} users_on_site_count - users_on_site_count
 * @property {number} ts - ts
 * @property {string} panel_name - panel_name
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} page_name - page_name
 * @property {string} pageId - page_id
 * @property {string} token - token
 * @property {number} users_with_devmode_count - users_with_devmode_count
 * @property {string} ver - version
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {string} dev_mode - dev_mode
 * @property {string} appId - app_id
 * @property {Url} pageurl - page_url
 * @property {GUID} esi - esi
 * @property {GUID} msid - metasite id
 * @property {string} ds_id - ds_id
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<clickedOnLearnMoreViewInsteadTestLiveSiteMessageOnIdeParams>} LogObject<clickedOnLearnMoreViewInsteadTestLiveSiteMessageOnIdeParams>
 */
export function clickedOnLearnMoreViewInsteadTestLiveSiteMessageOnIde(params) {
    return { evid: 380, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'ofClickedOnDismissViewInsteadTestLiveSiteMessageOnIde' event (evid: 381)
 *
 * Event description:  Sent when the user clicks on "dismiss" on 'view instead test/live site' message on IDE
 *  preview mode
 *
 * @param {ofClickedOnDismissViewInsteadTestLiveSiteMessageOnIdeParams} params - The parameters for this event
 * @property {string} app_name - app_name
 * @property {string} app_id - app_id
 * @property {number} users_with_write_devmode_count - users_with_write_devmode_count
 * @property {string} builderType - builder_type
 * @property {GUID} visitorId - visitorId
 * @property {string} ds_type - ds_type
 * @property {GUID} site_id - site_id
 * @property {string} panel_id - panel_id
 * @property {string} appName - app_name
 * @property {number} users_on_site_count - users_on_site_count
 * @property {number} ts - ts
 * @property {string} panel_name - panel_name
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} page_name - page_name
 * @property {string} pageId - page_id
 * @property {string} token - token
 * @property {number} users_with_devmode_count - users_with_devmode_count
 * @property {string} ver - version
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {string} dev_mode - dev_mode
 * @property {string} appId - app_id
 * @property {Url} pageurl - page_url
 * @property {GUID} esi - esi
 * @property {GUID} msid - metasite id
 * @property {string} ds_id - ds_id
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<ofClickedOnDismissViewInsteadTestLiveSiteMessageOnIdeParams>} LogObject<ofClickedOnDismissViewInsteadTestLiveSiteMessageOnIdeParams>
 */
export function ofClickedOnDismissViewInsteadTestLiveSiteMessageOnIde(params) {
    return { evid: 381, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'openAddDynamicPageModal' event (evid: 382)
 *
 * Event description: Modal open after user clicks on add dynamic page button
 *
 * @param {openAddDynamicPageModalParams} params - The parameters for this event
 * @property {string} app_name - app_name
 * @property {string} app_id - app_id
 * @property {number} users_with_write_devmode_count - users_with_write_devmode_count
 * @property {string} builderType - builder_type
 * @property {GUID} visitorId - visitor ID
 * @property {boolean} is_first_data_modal - is this the first data modal the user see
 * @property {string} ds_type - ds_type
 * @property {string} action_name - add_dynamic_page
 * @property {GUID} site_id - site_id
 * @property {string} panel_id - panel_id
 * @property {string} appName - app_name
 * @property {number} users_on_site_count - users_on_site_count
 * @property {number} ts - ts
 * @property {string} panel_name - panel_name
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} pageId - page_id
 * @property {string} origin - pagesPanel
 * @property {string} token - token
 * @property {number} users_with_devmode_count - users_with_devmode_count
 * @property {string} ver - version
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {string} dev_mode - dev_mode
 * @property {string} appId - app_id
 * @property {GUID} esi - esi
 * @property {GUID} msid - msid
 * @property {string} ds_id - ds_id
 * @property {boolean} isScrollable - True if the modal is scrollable
 * @property {boolean} isVisible - True if the modal has additional partially visible items
 * @property {number} numberOfItems - Number of available items in the modal
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<openAddDynamicPageModalParams>} LogObject<openAddDynamicPageModalParams>
 */
export function openAddDynamicPageModal(params) {
    return { evid: 382, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'addDynamicPageModalSelectCollection' event (evid: 383)
 *
 * Event description: Add dynamic Page Modal Select Collection
 *
 * @param {addDynamicPageModalSelectCollectionParams} params - The parameters for this event
 * @property {string} app_name - app_name
 * @property {string} app_id - app_id
 * @property {number} users_with_write_devmode_count - users_with_write_devmode_count
 * @property {string} builderType - builder_type
 * @property {GUID} visitorId - visitor ID
 * @property {boolean} is_first_data_modal - is this the first data modal the user see
 * @property {string} ds_type - ds_type
 * @property {string} action_name - add_dynamic_page
 * @property {GUID} site_id - site_id
 * @property {string} panel_id - panel_id
 * @property {boolean} preselected_collection - automatically selected / selected by user
 * @property {string} appName - app_name
 * @property {number} users_on_site_count - users_on_site_count
 * @property {number} ts - ts
 * @property {string} collection_id - collection id
 * @property {string} panel_name - panel_name
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} pageId - page_id
 * @property {string} origin - pagesPanel
 * @property {string} token - token
 * @property {number} users_with_devmode_count - users_with_devmode_count
 * @property {string} ver - version
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {string} dev_mode - dev_mode
 * @property {string} appId - app_id
 * @property {GUID} esi - esi
 * @property {string} collection_name - collection name
 * @property {GUID} msid - msid
 * @property {string} ds_id - ds_id
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<addDynamicPageModalSelectCollectionParams>} LogObject<addDynamicPageModalSelectCollectionParams>
 */
export function addDynamicPageModalSelectCollection(params) {
    return { evid: 383, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'addDynamicPageModalSelectPage' event (evid: 384)
 *
 * Event description: Add dynamic Page Modal Select Page
 *
 * @param {addDynamicPageModalSelectPageParams} params - The parameters for this event
 * @property {string} app_name - app_name
 * @property {string} app_id - app_id
 * @property {number} users_with_write_devmode_count - users_with_write_devmode_count
 * @property {string} builderType - builder_type
 * @property {GUID} visitorId - visitor ID
 * @property {boolean} is_first_data_modal - is this the first data modal the user see
 * @property {string} ds_type - ds_type
 * @property {string} action_name - add_dynamic_page
 * @property {GUID} site_id - site_id
 * @property {string} panel_id - panel_id
 * @property {string} appName - app_name
 * @property {number} users_on_site_count - users_on_site_count
 * @property {number} ts - ts
 * @property {string} collection_id - collection id
 * @property {string} panel_name - panel_name
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} pageId - page_id
 * @property {string} origin - pagesPanel
 * @property {string} token - token
 * @property {number} users_with_devmode_count - users_with_devmode_count
 * @property {string} ver - version
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {string} dev_mode - dev_mode
 * @property {string} appId - app_id
 * @property {GUID} esi - esi
 * @property {string} collection_name - collection name
 * @property {string} page_type - item_page, list_page, blank_page
 * @property {GUID} msid - msid
 * @property {string} ds_id - ds_id
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<addDynamicPageModalSelectPageParams>} LogObject<addDynamicPageModalSelectPageParams>
 */
export function addDynamicPageModalSelectPage(params) {
    return { evid: 384, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'addDynamicPageModalClickAddToSite' event (evid: 385)
 *
 * Event description: Add dynamic Page Modal Click 'Add to Site'
 *
 * @param {addDynamicPageModalClickAddToSiteParams} params - The parameters for this event
 * @property {string} app_name - app_name
 * @property {string} app_id - app_id
 * @property {number} users_with_write_devmode_count - users_with_write_devmode_count
 * @property {string} builderType - builder_type
 * @property {GUID} visitorId - visitor ID
 * @property {boolean} is_first_data_modal - is this the first data modal the user see
 * @property {string} ds_type - ds_type
 * @property {string} action_name - add_dynamic_page
 * @property {GUID} site_id - site_id
 * @property {string} panel_id - panel_id
 * @property {string} appName - app_name
 * @property {number} users_on_site_count - users_on_site_count
 * @property {number} ts - ts
 * @property {string} collection_id - collection id
 * @property {string} panel_name - panel_name
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} pageId - page_id
 * @property {string} origin - pagesPanel
 * @property {string} token - token
 * @property {number} users_with_devmode_count - users_with_devmode_count
 * @property {string} ver - version
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {string} dev_mode - dev_mode
 * @property {string} appId - app_id
 * @property {GUID} esi - esi
 * @property {string} collection_name - collection name
 * @property {string} page_type - item_page, list_page, blank_page
 * @property {GUID} msid - msid
 * @property {string} ds_id - ds_id
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<addDynamicPageModalClickAddToSiteParams>} LogObject<addDynamicPageModalClickAddToSiteParams>
 */
export function addDynamicPageModalClickAddToSite(params) {
    return { evid: 385, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'addDynamicPageModalErrorShown' event (evid: 386)
 *
 * Event description: Add dynamic Page Modal Click 'Add to Site'
 *
 * @param {addDynamicPageModalErrorShownParams} params - The parameters for this event
 * @property {string} app_name - app_name
 * @property {string} app_id - app_id
 * @property {number} users_with_write_devmode_count - users_with_write_devmode_count
 * @property {string} builderType - builder_type
 * @property {GUID} visitorId - visitor ID
 * @property {boolean} is_first_data_modal - is this the first data modal the user see
 * @property {string} ds_type - ds_type
 * @property {string} action_name - add_dynamic_page
 * @property {GUID} site_id - site_id
 * @property {string} panel_id - panel_id
 * @property {string} appName - app_name
 * @property {number} users_on_site_count - users_on_site_count
 * @property {number} ts - ts
 * @property {string} collection_id - collection id
 * @property {string} panel_name - panel_name
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} pageId - page_id
 * @property {string} error_type - no_collection, no_page, no_page_and_collection
 * @property {string} origin - pagesPanel
 * @property {string} token - token
 * @property {number} users_with_devmode_count - users_with_devmode_count
 * @property {string} ver - version
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {string} dev_mode - dev_mode
 * @property {string} appId - app_id
 * @property {GUID} esi - esi
 * @property {string} collection_name - collection name
 * @property {string} page_type - item_page, list_page, blank_page
 * @property {GUID} msid - msid
 * @property {string} ds_id - ds_id
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<addDynamicPageModalErrorShownParams>} LogObject<addDynamicPageModalErrorShownParams>
 */
export function addDynamicPageModalErrorShown(params) {
    return { evid: 386, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'openStartWithAPresetPanel' event (evid: 387)
 *
 * Event description: Open Start with a Preset Panel after user clicks 'Add Dynamic Page' from EditorX
 *
 * @param {openStartWithAPresetPanelParams} params - The parameters for this event
 * @property {string} app_name - app_name
 * @property {string} app_id - app_id
 * @property {number} users_with_write_devmode_count - users_with_write_devmode_count
 * @property {string} builderType - builder_type
 * @property {GUID} visitorId - visitor ID
 * @property {boolean} is_first_data_modal - is this the first data modal the user see
 * @property {string} ds_type - ds_type
 * @property {string} action_name - action_name
 * @property {GUID} site_id - site_id
 * @property {string} panel_id - panel_id
 * @property {string} appName - app_name
 * @property {number} users_on_site_count - users_on_site_count
 * @property {number} ts - ts
 * @property {string} panel_name - panel_name
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} pageId - page_id
 * @property {string} origin - pagesPanel
 * @property {string} token - token
 * @property {number} users_with_devmode_count - users_with_devmode_count
 * @property {string} ver - version
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {string} dev_mode - dev_mode
 * @property {string} appId - app_id
 * @property {GUID} esi - esi
 * @property {GUID} msid - msid
 * @property {string} action - open / create_preset / create_collection
 * @property {string} ds_id - ds_id
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<openStartWithAPresetPanelParams>} LogObject<openStartWithAPresetPanelParams>
 */
export function openStartWithAPresetPanel(params) {
    return { evid: 387, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'addDynamicPageModalClickToAddPreset' event (evid: 388)
 *
 * Event description: Add dynamic Page Modal - Click to Add Preset (EditorX)
 *
 * @param {addDynamicPageModalClickToAddPresetParams} params - The parameters for this event
 * @property {string} app_name - app_name
 * @property {string} app_id - app_id
 * @property {number} users_with_write_devmode_count - users_with_write_devmode_count
 * @property {string} builderType - builder_type
 * @property {GUID} visitorId - visitor ID
 * @property {boolean} is_first_data_modal - is this the first data modal the user see
 * @property {string} ds_type - ds_type
 * @property {string} action_name - add_dynamic_page
 * @property {GUID} site_id - site_id
 * @property {string} panel_id - panel_id
 * @property {string} appName - app_name
 * @property {number} users_on_site_count - users_on_site_count
 * @property {number} ts - ts
 * @property {string} panel_name - panel_name
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} pageId - page_id
 * @property {string} origin - pagesPanel
 * @property {string} token - token
 * @property {number} users_with_devmode_count - users_with_devmode_count
 * @property {string} ver - version
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {string} dev_mode - dev_mode
 * @property {string} appId - app_id
 * @property {GUID} esi - esi
 * @property {GUID} msid - msid
 * @property {string} ds_id - ds_id
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<addDynamicPageModalClickToAddPresetParams>} LogObject<addDynamicPageModalClickToAddPresetParams>
 */
export function addDynamicPageModalClickToAddPreset(params) {
    return { evid: 388, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'dataBindingRightPanelElementClicked' event (evid: 389)
 *
 * Event description: Data Binding Right Panel Element
 *
 * @param {dataBindingRightPanelElementClickedParams} params - The parameters for this event
 * @property {string} app_name - app_name
 * @property {string} app_id - app_id
 * @property {string} renderingFlow - Rendering flow
 * @property {number} users_with_write_devmode_count - users_with_write_devmode_count
 * @property {string} builderType - builder_type
 * @property {GUID} visitorId - visitor id
 * @property {string} ds_type - ds_type
 * @property {string} action_name - settings / choose_dataset / connect_field / add_dataset / add_filter / add_sort
 * @property {GUID} site_id - site_id
 * @property {string} panel_id - panel_id
 * @property {string} appName - app_name
 * @property {number} users_on_site_count - users_on_site_count
 * @property {number} ts - ts
 * @property {string} panel_name - panel_name
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} controller_type - controller_type
 * @property {string} ds_name - ds name
 * @property {string} section - main / settings
 * @property {string} pageId - page_id
 * @property {string} origin - dp_connections / gfpp
 * @property {string} token - token
 * @property {number} users_with_devmode_count - users_with_devmode_count
 * @property {string} ver - version
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {string} dev_mode - dev_mode
 * @property {string} appId - app_id
 * @property {GUID} esi - esi
 * @property {string} component_id - component_id
 * @property {string} component_type - component type
 * @property {GUID} msid - msid
 * @property {string} ds_id - ds_id
 * @property {string} user_input - the value changed by the user
 * @property {string} item - item
 * @property {string} property - if there are several linkable properties- this field will show the which link is relevant to which property
 * @return {LogObject<dataBindingRightPanelElementClickedParams>} LogObject<dataBindingRightPanelElementClickedParams>
 */
export function dataBindingRightPanelElementClicked(params) {
    return { evid: 389, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'dsSortPanelFieldSelected' event (evid: 39)
 *
 * Event description: DS sort panel field selected
 *
 * @param {dsSortPanelFieldSelectedParams} params - The parameters for this event
 * @property {string} app_name - app_name
 * @property {string} app_id - app_id
 * @property {number} users_with_write_devmode_count - users_with_write_devmode_count
 * @property {string} builderType - builder_type
 * @property {GUID} visitorId - visitor_id
 * @property {string} ds_type - ds_type
 * @property {GUID} site_id - site_id
 * @property {string} panel_id - panel_id
 * @property {string} appName - app_name
 * @property {number} users_on_site_count - users_on_site_count
 * @property {string} field_type - field type
 * @property {number} ts - ts
 * @property {string} collection_id - collection id
 * @property {boolean} is_collection_order_applied - is_collection_order_applied
 * @property {string} panel_name - panel_name
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} pageId - page_id
 * @property {string} token - token
 * @property {number} users_with_devmode_count - users_with_devmode_count
 * @property {string} ver - version
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {string} mode - add/edit
 * @property {string} dev_mode - dev_mode
 * @property {string} appId - app_id
 * @property {GUID} esi - esi
 * @property {GUID} msid - msid
 * @property {string} field_name - field name
 * @property {string} ds_id - ds_id
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<dsSortPanelFieldSelectedParams>} LogObject<dsSortPanelFieldSelectedParams>
 */
export function dsSortPanelFieldSelected(params) {
    return { evid: 39, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'dataBindingRightPanelErrorShown' event (evid: 390)
 *
 * Event description: Data Binding Right Panel Element Clicked
 *
 * @param {dataBindingRightPanelErrorShownParams} params - The parameters for this event
 * @property {string} app_name - app_name
 * @property {string} app_id - app_id
 * @property {number} users_with_write_devmode_count - users_with_write_devmode_count
 * @property {string} builderType - builder_type
 * @property {GUID} visitorId - visitor id
 * @property {string} ds_type - ds_type
 * @property {GUID} site_id - site_id
 * @property {string} panel_id - panel_id
 * @property {string} appName - app_name
 * @property {number} users_on_site_count - users_on_site_count
 * @property {number} ts - ts
 * @property {string} panel_name - panel_name
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} controller_type - controller_type
 * @property {string} ds_name - ds name
 * @property {string} section - main / settings
 * @property {string} pageId - page_id
 * @property {string} error_type - collection_deleted_dp / collection_deleted_ds / invalid_field_fs /  invalid_field_connect
 * @property {string} origin - dp_connections / gfpp
 * @property {string} token - token
 * @property {number} users_with_devmode_count - users_with_devmode_count
 * @property {string} ver - version
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {string} dev_mode - dev_mode
 * @property {string} appId - app_id
 * @property {GUID} esi - esi
 * @property {string} component_id - component_id
 * @property {string} component_type - component type
 * @property {GUID} msid - msid
 * @property {string} ds_id - ds_id
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<dataBindingRightPanelErrorShownParams>} LogObject<dataBindingRightPanelErrorShownParams>
 */
export function dataBindingRightPanelErrorShown(params) {
    return { evid: 390, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'datasetDeleted' event (evid: 391)
 *
 * Event description: Dataset Deleted
 *
 * @param {datasetDeletedParams} params - The parameters for this event
 * @property {string} app_name - app_name
 * @property {string} app_id - app_id
 * @property {string} renderingFlow - Rendering flow
 * @property {number} users_with_write_devmode_count - users_with_write_devmode_count
 * @property {string} builderType - builder_type
 * @property {GUID} visitorId - visitor id
 * @property {string} ds_type - ds_type
 * @property {GUID} site_id - site_id
 * @property {string} panel_id - panel_id
 * @property {string} appName - app_name
 * @property {number} users_on_site_count - users_on_site_count
 * @property {number} ts - ts
 * @property {string} panel_name - panel_name
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} ds_name - ds name
 * @property {string} section - main / settings
 * @property {string} pageId - page_id
 * @property {string} origin - dp_connections / gfpp
 * @property {string} token - token
 * @property {number} users_with_devmode_count - users_with_devmode_count
 * @property {string} ver - version
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {string} dev_mode - dev_mode
 * @property {string} appId - app_id
 * @property {GUID} esi - esi
 * @property {string} component_id - component_id
 * @property {string} component_type - component type
 * @property {GUID} msid - msid
 * @property {string} ds_id - ds_id
 * @property {string} collection_id - collection_id
 * @return {LogObject<datasetDeletedParams>} LogObject<datasetDeletedParams>
 */
export function datasetDeleted(params) {
    return { evid: 391, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'vsCodeSessionHasExpiredMessage' event (evid: 393)
 *
 * Event description: After an idle of 10 minutes, VS code session is expired and the user is redirected to another page saying that the session is expired. When the user sees that page, the event is fired (client event)
 *
 * @param {vsCodeSessionHasExpiredMessageParams} params - The parameters for this event
 * @property {string} app_name - app_name
 * @property {string} app_id - app_id
 * @property {number} users_with_write_devmode_count - users_with_write_devmode_count
 * @property {string} builderType - builder_type
 * @property {string} customParams - stringified object with custom parameters
 * @property {GUID} visitorId - visitor_id
 * @property {string} ds_type - ds_type
 * @property {GUID} site_id - site_id
 * @property {string} panel_id - panel_id
 * @property {string} appName - app_name
 * @property {number} users_on_site_count - users_on_site_count
 * @property {string} uuid - uuid
 * @property {number} ts - ts
 * @property {string} panel_name - panel_name
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} pageId - page_id
 * @property {string} token - token
 * @property {number} users_with_devmode_count - users_with_devmode_count
 * @property {string} ver - version
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {string} dev_mode - dev_mode
 * @property {string} appId - app_id
 * @property {GUID} esi - esi
 * @property {GUID} msid - msid
 * @property {GUID} logged_session_uuid - logged_session_uuid
 * @property {GUID} vs_code_session_id - unique session id which expired
 * @property {string} ds_id - ds_id
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<vsCodeSessionHasExpiredMessageParams>} LogObject<vsCodeSessionHasExpiredMessageParams>
 */
export function vsCodeSessionHasExpiredMessage(params) {
    return { evid: 393, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'leftTreeCopyId' event (evid: 394)
 *
 * Event description: when the user copies collection/ field ID
 * left tree -> databases
 *
 * @param {leftTreeCopyIdParams} params - The parameters for this event
 * @property {string} app_name - app_name
 * @property {string} app_id - app_id
 * @property {number} users_with_write_devmode_count - users_with_write_devmode_count
 * @property {string} builderType - builder_type
 * @property {GUID} visitorId - deprecated
 * @property {string} section_name - my_collections/ external_db/ wix_app_collections
 * @property {string} ds_type - ds_type
 * @property {GUID} site_id - site_id
 * @property {string} panel_id - panel_id
 * @property {string} appName - app_name
 * @property {number} users_on_site_count - users_on_site_count
 * @property {number} ts - ts
 * @property {string} panel_name - panel_name
 * @property {string} viewmode - viewmode
 * @property {string} input_name - deprecated
 * @property {string} pageId - page_id
 * @property {string} item_name - item_name
 * @property {string} token - token
 * @property {number} users_with_devmode_count - users_with_devmode_count
 * @property {string} ver - version
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {string} dev_mode - dev_mode
 * @property {string} appId - app_id
 * @property {GUID} esi - esi
 * @property {string} item_type - item_type (collection,field)
 * @property {GUID} msid - msid
 * @property {string} action - click/ hover
 * @property {string} ds_id - ds_id
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<leftTreeCopyIdParams>} LogObject<leftTreeCopyIdParams>
 */
export function leftTreeCopyId(params) {
    return { evid: 394, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'edmWasInvokedAndSentToSupervisor' event (evid: 395)
 *
 * Event description: sent when an EDM (Wix-provided-BE-npm) was invoked (sent via the supervisor)
 *
 * @param {edmWasInvokedAndSentToSupervisorParams} params - The parameters for this event
 * @property {GUID} _event_hash - event_hash
 * @property {GUID} visitor_id - visitor_id
 * @property {string} app_name - app_name
 * @property {string} app_id - app_id
 * @property {GUID} instanceId - {}
 * @property {string} artifactId - {x-wix-client-artifact-id from header}
 * @property {string} viewMode - {x-wix-view-mode from header}
 * @property {number} responseCode - {api_response_status}
 * @property {string} builderType - builder_type
 * @property {string} ds_type - ds_type
 * @property {GUID} site_id - site_id
 * @property {string} panel_id - panel_id
 * @property {string} file_name - file name
 * @property {string} appName - app_name
 * @property {number} ts - ts
 * @property {string} target_url - {entire path}
 * @property {boolean} exception - {true/false}
 * @property {string} panel_name - panel_name
 * @property {string} viewmode - viewmode
 * @property {string} input_name - not relevant
 * @property {string} edm_module_name - edm module name
 * @property {GUID} gridAppId - {}
 * @property {string} pageId - page_id
 * @property {GUID} vsi - viewer session id
 * @property {string} requestid - {x-wix-request-id from header }
 * @property {string} token - token
 * @property {string} ver - version
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {string} api_name - the function used in the edm
 * @property {string} appId - app_id
 * @property {GUID} esi - esi
 * @property {GUID} msid - msid
 * @property {number} tsp - time to respond (success/failure), miliseconds
 * @property {string} ds_id - ds_id
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<edmWasInvokedAndSentToSupervisorParams>} LogObject<edmWasInvokedAndSentToSupervisorParams>
 */
export function edmWasInvokedAndSentToSupervisor(params) {
    return { evid: 395, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'vsCodeVeloIdeSessionEnd' event (evid: 396)
 *
 * Event description: sent when VS code session end (server event). The session would be alive as long as at least 1 user is connected to it.
 *
 * @param {vsCodeVeloIdeSessionEndParams} params - The parameters for this event
 * @property {string} app_name - app_name
 * @property {string} app_id - app_id
 * @property {number} duration - {session time in minutes}
 * @property {number} users_with_write_devmode_count - users_with_write_devmode_count
 * @property {string} builderType - builder_type
 * @property {GUID} visitorId - visitor_id
 * @property {string} ds_type - ds_type
 * @property {GUID} site_id - site_id
 * @property {string} panel_id - panel_id
 * @property {string} appName - app_name
 * @property {number} users_on_site_count - users_on_site_count
 * @property {GUID} uuid - uuid
 * @property {number} ts - ts
 * @property {string} panel_name - panel_name
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} reasonCode - {idle time, lease time expired}
 * @property {string} grid_app_id - {}
 * @property {string} pageId - page_id
 * @property {GUID} velo_session_id - {VS code session Id}
 * @property {string} token - token
 * @property {number} users_with_devmode_count - users_with_devmode_count
 * @property {string} ver - version
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {string} dev_mode - dev_mode
 * @property {string} appId - app_id
 * @property {GUID} esi - esi
 * @property {GUID} msid
 * @property {string} ds_id - ds_id
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<vsCodeVeloIdeSessionEndParams>} LogObject<vsCodeVeloIdeSessionEndParams>
 */
export function vsCodeVeloIdeSessionEnd(params) {
    return { evid: 396, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'dynamicPageSeoPanelInfoHover' event (evid: 397)
 *
 * Event description: Dynamic Page SEO Panel Info Hover
 *
 * @param {dynamicPageSeoPanelInfoHoverParams} params - The parameters for this event
 * @property {string} app_name - app_name
 * @property {string} app_id - app_id
 * @property {number} users_with_write_devmode_count - users_with_write_devmode_count
 * @property {string} builderType - builder_type
 * @property {GUID} visitorId - visitor id
 * @property {string} ds_type - ds_type
 * @property {GUID} site_id - site_id
 * @property {string} panel_id - panel_id
 * @property {string} appName - app_name
 * @property {number} users_on_site_count - users_on_site_count
 * @property {number} ts - ts
 * @property {string} info_text - 'slug' etc.
 * @property {string} panel_name - panel_name
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} pageId - page_id
 * @property {string} token - token
 * @property {number} users_with_devmode_count - users_with_devmode_count
 * @property {string} ver - version
 * @property {string} token_id - token_id
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {string} dev_mode - dev_mode
 * @property {string} appId - app_id
 * @property {GUID} esi - esi
 * @property {string} page_type - item_page, list_page
 * @property {GUID} msid - msid
 * @property {string} ds_id - ds_id
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<dynamicPageSeoPanelInfoHoverParams>} LogObject<dynamicPageSeoPanelInfoHoverParams>
 */
export function dynamicPageSeoPanelInfoHover(params) {
    return { evid: 397, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'dynamicPageGoToSeoSettings' event (evid: 398)
 *
 * Event description: Dynamic Page Go to SEO Settings
 *
 * @param {dynamicPageGoToSeoSettingsParams} params - The parameters for this event
 * @property {string} app_name - app_name
 * @property {string} app_id - app_id
 * @property {string} renderingFlow - Rendering flow
 * @property {number} users_with_write_devmode_count - users_with_write_devmode_count
 * @property {string} builderType - builder_type
 * @property {GUID} visitorId - visitor id
 * @property {string} ds_type - ds_type
 * @property {GUID} site_id - site_id
 * @property {string} panel_id - panel_id
 * @property {string} appName - app_name
 * @property {number} users_on_site_count - users_on_site_count
 * @property {number} ts - ts
 * @property {string} collection_id - collection_id
 * @property {string} panel_name - panel_name
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} ds_name - ds_name
 * @property {string} pageId - page_id
 * @property {string} token - token
 * @property {number} users_with_devmode_count - users_with_devmode_count
 * @property {string} ver - version
 * @property {string} token_id - token_id
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {string} dev_mode - dev_mode
 * @property {string} appId - app_id
 * @property {GUID} esi - esi
 * @property {string} page_type - item_page, list_page
 * @property {GUID} msid - msid
 * @property {string} ds_id - ds_id
 * @return {LogObject<dynamicPageGoToSeoSettingsParams>} LogObject<dynamicPageGoToSeoSettingsParams>
 */
export function dynamicPageGoToSeoSettings(params) {
    return { evid: 398, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'leftTreeFormsCollectionsMoveAlert' event (evid: 399)
 *
 * Event description: when the user interacts with the alerts regarding the forms collections move to the forms namespace
 *
 * @param {leftTreeFormsCollectionsMoveAlertParams} params - The parameters for this event
 * @property {string} app_name - app_name
 * @property {string} app_id - app_id
 * @property {number} users_with_write_devmode_count - users_with_write_devmode_count
 * @property {string} builderType - builder_type
 * @property {GUID} visitorId - visitor_id
 * @property {string} ds_type - ds_type
 * @property {GUID} site_id - site_id
 * @property {string} panel_id - panel_id
 * @property {string} appName - app_name
 * @property {number} users_on_site_count - users_on_site_count
 * @property {number} ts - ts
 * @property {string} panel_name - panel_name
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} pageId - page_id
 * @property {string} item_name - "take_me_to_forms"/ "got_it"
 * @property {string} token - token
 * @property {number} users_with_devmode_count - users_with_devmode_count
 * @property {string} ver - version
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {string} dev_mode - dev_mode
 * @property {string} appId - app_id
 * @property {GUID} esi - esi
 * @property {GUID} msid - msid
 * @property {string} action - click
 * @property {string} ds_id - ds_id
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<leftTreeFormsCollectionsMoveAlertParams>} LogObject<leftTreeFormsCollectionsMoveAlertParams>
 */
export function leftTreeFormsCollectionsMoveAlert(params) {
    return { evid: 399, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'dsSortPanelSortOrderChanged' event (evid: 40)
 *
 * Event description: DS sort panel sort order changed
 *
 * @param {dsSortPanelSortOrderChangedParams} params - The parameters for this event
 * @property {string} app_name - app_name
 * @property {string} app_id - app_id
 * @property {number} users_with_write_devmode_count - users_with_write_devmode_count
 * @property {string} builderType - builder_type
 * @property {GUID} visitorId - visitor_id
 * @property {string} ds_type - ds_type
 * @property {GUID} site_id - site_id
 * @property {string} panel_id - panel_id
 * @property {string} appName - app_name
 * @property {number} users_on_site_count - users_on_site_count
 * @property {number} ts - ts
 * @property {string} collection_id - collection id
 * @property {boolean} is_collection_order_applied - is_collection_order_applied
 * @property {string} panel_name - panel_name
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} pageId - page_id
 * @property {string} order - order (asc / desc)
 * @property {string} token - token
 * @property {number} users_with_devmode_count - users_with_devmode_count
 * @property {string} ver - version
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {string} mode - add/edit
 * @property {string} dev_mode - dev_mode
 * @property {string} appId - app_id
 * @property {GUID} esi - esi
 * @property {GUID} msid - msid
 * @property {string} ds_id - ds_id
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<dsSortPanelSortOrderChangedParams>} LogObject<dsSortPanelSortOrderChangedParams>
 */
export function dsSortPanelSortOrderChanged(params) {
    return { evid: 40, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'veloSiteAnalysisStatus' event (evid: 400)
 *
 * Event description: This is event represents an invocation....
 *
 * @param {veloSiteAnalysisStatusParams} params - The parameters for this event
 * @property {boolean} success - success
 * @property {string} result - A summary of the applicative result
 * @property {string} gridAppId - gridappid
 * @property {string} eType - Error description for cases where we failed to process the request (null if we succeeded)
 * @property {GUID} msid - msid
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<veloSiteAnalysisStatusParams>} LogObject<veloSiteAnalysisStatusParams>
 */
export function veloSiteAnalysisStatus(params) {
    return { evid: 400, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'veloInternalPageRendered' event (evid: 4001)
 *
 * Event description: Fired every time a UoU rendered a page
 *
 * @param {veloInternalPageRenderedParams} params - The parameters for this event
 * @property {string} test_name - the name of the stress test
 * @property {string} page_id - the page that was rendered
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<veloInternalPageRenderedParams>} LogObject<veloInternalPageRenderedParams>
 */
export function veloInternalPageRendered(params) {
    return { evid: 4001, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'veloInternalActionClicked' event (evid: 4002)
 *
 * Event description: fires every time a UoU did an action (clicked on a button)
 *
 * @param {veloInternalActionClickedParams} params - The parameters for this event
 * @property {string} test_name - the name of the stress test
 * @property {string} action_name - for example (search_button_clicked, payment_button_clicked)
 * @property {string} page_id - the page that was rendered
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<veloInternalActionClickedParams>} LogObject<veloInternalActionClickedParams>
 */
export function veloInternalActionClicked(params) {
    return { evid: 4002, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'veloInternalQueryPerformance' event (evid: 4003)
 *
 * Event description: fired from the code backed to measure the time it took for a query to load
 *
 * @param {veloInternalQueryPerformanceParams} params - The parameters for this event
 * @property {string} test_name - the name of the stress test
 * @property {string} path - the path of the function
 * @property {number} rowCount - the number of rows returned
 * @property {number} ts - time_from_start_ms
 * @property {boolean} is_successful - was the query successful?
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<veloInternalQueryPerformanceParams>} LogObject<veloInternalQueryPerformanceParams>
 */
export function veloInternalQueryPerformance(params) {
    return { evid: 4003, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'dynamicPageInfoTabNavigation' event (evid: 401)
 *
 * Event description: Dynamic Page Info Tab Navigation
 *
 * @param {dynamicPageInfoTabNavigationParams} params - The parameters for this event
 * @property {string} app_name - app_name
 * @property {string} app_id - app_id
 * @property {string} renderingFlow - Rendering flow
 * @property {number} users_with_write_devmode_count - users_with_write_devmode_count
 * @property {string} builderType - builder_type
 * @property {GUID} visitorId - visitor id
 * @property {string} ds_type - ds_type
 * @property {GUID} site_id - site_id
 * @property {string} panel_id - panel_id
 * @property {string} appName - app_name
 * @property {number} users_on_site_count - users_on_site_count
 * @property {GUID} uuid - uuid
 * @property {number} ts - ts
 * @property {string} collection_id - collection_id
 * @property {string} panel_name - panel_name
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} ds_name - ds_name
 * @property {string} pageId - page_id
 * @property {string} token - token
 * @property {number} users_with_devmode_count - users_with_devmode_count
 * @property {string} ver - version
 * @property {string} token_id - token_id
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {string} dev_mode - dev_mode
 * @property {string} appId - app_id
 * @property {GUID} esi - esi
 * @property {string} page_type - item_page, list_page
 * @property {GUID} msid - msid
 * @property {string} action - content_manager / dataset_settings / seo_settings / advanced_settings
 * @property {string} ds_id - ds_id
 * @return {LogObject<dynamicPageInfoTabNavigationParams>} LogObject<dynamicPageInfoTabNavigationParams>
 */
export function dynamicPageInfoTabNavigation(params) {
    return { evid: 401, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'generateCodeViaAiAssistantClickedSrc79Evid4015' event (evid: 4015)
 *
 * Event description: when a user submits a request to generate code via AI assistant
 *
 *
 * @param {generateCodeViaAiAssistantClickedSrc79Evid4015Params} params - The parameters for this event
 * @property {string} action_desc - {the required action that is described, to be translated to code}
 * @property {string} component_id - component_id
 * @property {string} component_type - component_type
 * @property {string} handler_name	 - handler_name
 * @property {string} input_name	 - input_name
 * @property {GUID} msid - msid
 * @property {GUID} visitorId	 - visitorId
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<generateCodeViaAiAssistantClickedSrc79Evid4015Params>} LogObject<generateCodeViaAiAssistantClickedSrc79Evid4015Params>
 */
export function generateCodeViaAiAssistantClickedSrc79Evid4015(params) {
    return { evid: 4015, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'leftTreeCollectionsTabClick' event (evid: 402)
 *
 * Event description: Left tree - Click on the collections tab in the left tree
 *
 * @param {leftTreeCollectionsTabClickParams} params - The parameters for this event
 * @property {string} app_name - app_name
 * @property {string} app_id - app_id
 * @property {number} users_with_write_devmode_count - users_with_write_devmode_count
 * @property {string} builderType - builder_type
 * @property {GUID} visitorId - deprecated
 * @property {string} ds_type - ds_type
 * @property {GUID} site_id - site_id
 * @property {string} panel_id - panel_id
 * @property {string} appName - app_name
 * @property {number} users_on_site_count - users_on_site_count
 * @property {string} tab_name - collections
 * @property {number} ts - ts
 * @property {boolean} hasCollections - Does the tab contain at least one collection- exclude billing and media collections which were added by default to every new site
 * @property {string} panel_name - panel_name
 * @property {string} viewmode - viewmode
 * @property {string} input_name - deprecated
 * @property {string} pageId - page_id
 * @property {string} origin - {left tree}
 * @property {string} token - token
 * @property {number} users_with_devmode_count - users_with_devmode_count
 * @property {string} ver - version
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {string} dev_mode - dev_mode
 * @property {string} appId - app_id
 * @property {GUID} esi - esi
 * @property {GUID} msid - msid
 * @property {string} ds_id - ds_id
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<leftTreeCollectionsTabClickParams>} LogObject<leftTreeCollectionsTabClickParams>
 */
export function leftTreeCollectionsTabClick(params) {
    return { evid: 402, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'veloLiteIntroduction' event (evid: 403)
 *
 * Event description: when the user interacts with the 'site structure' tab's elements (-> velo lite) in the editor studio (first dev mode on)
 *
 * @param {veloLiteIntroductionParams} params - The parameters for this event
 * @property {string} app_name - app_name
 * @property {string} app_id - app_id
 * @property {string} renderingFlow - Rendering flow
 * @property {number} users_with_write_devmode_count - users_with_write_devmode_count
 * @property {string} builderType - builder_type
 * @property {GUID} visitorId - deprecated
 * @property {string} ds_type - ds_type
 * @property {GUID} site_id - site_id
 * @property {string} panel_id - panel_id
 * @property {string} appName - app_name
 * @property {number} users_on_site_count - users_on_site_count
 * @property {number} ts - ts
 * @property {string} panel_name - panel_name
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} pageId - page_id
 * @property {string} item_name - start_coding, learn_more, dev_mode_off
 * @property {string} token - token
 * @property {number} users_with_devmode_count - users_with_devmode_count
 * @property {string} ver - version
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {string} dev_mode - dev_mode
 * @property {string} appId - app_id
 * @property {GUID} esi - esi
 * @property {GUID} msid - msid
 * @property {string} action - click
 * @property {string} ds_id - ds_id
 * @property {string} origin - Indication of where the user performed the action.
 * @property {string} get_started_page_id - get_started_page_id
 * @return {LogObject<veloLiteIntroductionParams>} LogObject<veloLiteIntroductionParams>
 */
export function veloLiteIntroduction(params) {
    return { evid: 403, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'elementoryErrorResponse' event (evid: 404)
 *
 * Event description: An event that triggers on every error that returns from elementory
 *
 * @param {elementoryErrorResponseParams} params - The parameters for this event
 * @property {GUID} _event_hash - event_hash
 * @property {GUID} visitor_id - visitor_id
 * @property {string} app_name - app_name
 * @property {string} app_id - app_id
 * @property {string} builderType - builder_type
 * @property {string} path - path
 * @property {string} codeAppId - code_app_id
 * @property {string} ds_type - ds_type
 * @property {GUID} site_id - site_id
 * @property {string} panel_id - panel_id
 * @property {string} appName - app_name
 * @property {number} ts - ts
 * @property {string} panel_name - panel_name
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} pageId - page_id
 * @property {GUID} vsi - vsi
 * @property {string} token - token
 * @property {string} ver - version
 * @property {string} errorDescription - the entire error trace
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {string} appId - app_id
 * @property {GUID} esi - esi
 * @property {GUID} msid - msid
 * @property {number} tsp - tsp
 * @property {string} ds_id - ds_id
 * @property {string} edmName - if error from edm, the name of the edm
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<elementoryErrorResponseParams>} LogObject<elementoryErrorResponseParams>
 */
export function elementoryErrorResponse(params) {
    return { evid: 404, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'wixIdeButton' event (evid: 405)
 *
 * Event description: When a user clicks on the diff buttons that leads to VS code
 *
 * @param {wixIdeButtonParams} params - The parameters for this event
 * @property {string} app_name - app_name
 * @property {string} app_id - app_id
 * @property {number} users_with_write_devmode_count - users_with_write_devmode_count
 * @property {string} builderType - builder_type
 * @property {GUID} visitorId - visitor_id
 * @property {string} ds_type - ds_type
 * @property {GUID} site_id - site_id
 * @property {string} panel_id - panel_id
 * @property {string} appName - app_name
 * @property {number} users_on_site_count - users_on_site_count
 * @property {number} ts - ts
 * @property {string} panel_name - panel_name
 * @property {string} viewmode - viewmode
 * @property {string} input_name - go_to_wix_ide/ code_in_wix_ide/ start_coding
 * @property {string} pageId - page_id
 * @property {string} origin - backend_tab/ external_ide_banner/ ide_snack_bar
 * @property {string} token - token
 * @property {number} users_with_devmode_count - users_with_devmode_count
 * @property {string} ver - version
 * @property {string} environment - page_code_tab/ global_code_tab, css_tab
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {string} dev_mode - dev_mode
 * @property {string} appId - app_id
 * @property {GUID} esi - esi
 * @property {GUID} msid - msid
 * @property {string} action - click
 * @property {string} ds_id - ds_id
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<wixIdeButtonParams>} LogObject<wixIdeButtonParams>
 */
export function wixIdeButton(params) {
    return { evid: 405, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'dropdownClickWixLogsSrc79Evid406' event (evid: 406)
 *
 * Event description: user action on wix logs and logs indication (buttom banner) view.
 *
 * @param {dropdownClickWixLogsSrc79Evid406Params} params - The parameters for this event
 * @property {string} renderingFlow - Rendering flow
 * @property {string} input_name - button name
 *
 * @property {string} action_type - click/ open dropdown/ view
 * @property {string} action_origin - bizm_site_events, preview_console, TB_function_tool
 * @return {LogObject<dropdownClickWixLogsSrc79Evid406Params>} LogObject<dropdownClickWixLogsSrc79Evid406Params>
 */
export function dropdownClickWixLogsSrc79Evid406(params) {
    return { evid: 406, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'ideSnackBarMessage' event (evid: 407)
 *
 * Event description: When a user saw/interacts with the dev control message (snack bar) in the Wix IDE
 *
 * @param {ideSnackBarMessageParams} params - The parameters for this event
 * @property {string} app_name - app_name
 * @property {string} app_id - app_id
 * @property {string} renderingFlow - Rendering flow
 * @property {number} users_with_write_devmode_count - users_with_write_devmode_count
 * @property {string} builderType - builder_type
 * @property {GUID} visitorId - visitor_id
 * @property {string} ds_type - ds_type
 * @property {GUID} site_id - site_id
 * @property {string} panel_id - panel_id
 * @property {string} appName - app_name
 * @property {number} users_on_site_count - users_on_site_count
 * @property {number} ts - ts
 * @property {string} panel_name - panel_name
 * @property {string} viewmode - viewmode
 * @property {string} input_name - start_coding_click/ go_to_wix_ide
 * @property {string} pageId - page_id
 * @property {string} token - token
 * @property {number} users_with_devmode_count - users_with_devmode_count
 * @property {string} ver - version
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {string} dev_mode - dev_mode
 * @property {string} appId - app_id
 * @property {GUID} esi - esi
 * @property {GUID} msid - msid
 * @property {string} action - click/ show
 * @property {string} ds_id - ds_id
 * @property {string} package_name - package_name
 * @return {LogObject<ideSnackBarMessageParams>} LogObject<ideSnackBarMessageParams>
 */
export function ideSnackBarMessage(params) {
    return { evid: 407, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'leftTreeClouddbCreationLabel' event (evid: 409)
 *
 * Event description: interaction with the 'in progress' label that appears next to the namespace of the could DB while it is being created
 *
 * @param {leftTreeClouddbCreationLabelParams} params - The parameters for this event
 * @property {string} app_name - app_name
 * @property {string} app_id - app_id
 * @property {number} users_with_write_devmode_count - users_with_write_devmode_count
 * @property {string} builderType - builder_type
 * @property {GUID} visitorId - visitor_id
 * @property {string} ds_type - ds_type
 * @property {GUID} site_id - site_id
 * @property {string} panel_id - panel_id
 * @property {string} appName - app_name
 * @property {number} users_on_site_count - users_on_site_count
 * @property {number} ts - ts
 * @property {string} panel_name - panel_name
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} pageId - page_id
 * @property {string} item_name - in_progress, learn_more, error_
 * @property {string} token - token
 * @property {number} users_with_devmode_count - users_with_devmode_count
 * @property {string} ver - version
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {string} dev_mode - dev_mode
 * @property {string} appId - app_id
 * @property {GUID} esi - esi
 * @property {GUID} msid - msid
 * @property {string} action - click, hover
 * @property {string} ds_id - ds_id
 * @property {string} namespace - namespace
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<leftTreeClouddbCreationLabelParams>} LogObject<leftTreeClouddbCreationLabelParams>
 */
export function leftTreeClouddbCreationLabel(params) {
    return { evid: 409, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'dsSortPanelSaveButtonClicked' event (evid: 41)
 *
 * Event description: DS sort panel save button clicked
 *
 * @param {dsSortPanelSaveButtonClickedParams} params - The parameters for this event
 * @property {string} app_name - app_name
 * @property {string} app_id - app_id
 * @property {number} users_with_write_devmode_count - users_with_write_devmode_count
 * @property {string} builderType - builder_type
 * @property {GUID} visitorId - visitor_id
 * @property {string} ds_type - ds_type
 * @property {GUID} site_id - site_id
 * @property {string} panel_id - panel_id
 * @property {string} appName - app_name
 * @property {number} users_on_site_count - users_on_site_count
 * @property {number} ts - ts
 * @property {string} collection_id - collection id
 * @property {boolean} is_collection_order_applied - is_collection_order_applied
 * @property {string} panel_name - panel_name
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} pageId - page_id
 * @property {string} token - token
 * @property {number} users_with_devmode_count - users_with_devmode_count
 * @property {string} ver - version
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {string} mode - mode
 * @property {string} dev_mode - dev_mode
 * @property {string} sort - sort (json)
 * @property {string} appId - app_id
 * @property {GUID} esi - esi
 * @property {GUID} msid - msid
 * @property {string} ds_id - ds_id
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<dsSortPanelSaveButtonClickedParams>} LogObject<dsSortPanelSaveButtonClickedParams>
 */
export function dsSortPanelSaveButtonClicked(params) {
    return { evid: 41, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'googleDatabaseAlert' event (evid: 410)
 *
 * Event description: when the user interacts with the GoogleDatabase delete alert
 *
 * @param {googleDatabaseAlertParams} params - The parameters for this event
 * @property {string} app_name - app_name
 * @property {string} app_id - app_id
 * @property {number} users_with_write_devmode_count - users_with_write_devmode_count
 * @property {string} builderType - builder_type
 * @property {GUID} visitorId - visitor_id
 * @property {string} ds_type - ds_type
 * @property {GUID} site_id - site_id
 * @property {string} panel_id - panel_id
 * @property {string} appName - app_name
 * @property {number} users_on_site_count - users_on_site_count
 * @property {number} ts - ts
 * @property {string} panel_name - panel_name
 * @property {string} viewmode - viewmode
 * @property {string} input_name - delete
 * @property {string} pageId - page_id
 * @property {string} token - token
 * @property {number} users_with_devmode_count - users_with_devmode_count
 * @property {string} ver - version
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {string} dev_mode - dev_mode
 * @property {string} appId - app_id
 * @property {GUID} esi - esi
 * @property {GUID} msid - msid
 * @property {string} action - close, show
 * @property {string} ds_id - ds_id
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<googleDatabaseAlertParams>} LogObject<googleDatabaseAlertParams>
 */
export function googleDatabaseAlert(params) {
    return { evid: 410, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'externalCollectionsAddedSuccessfully' event (evid: 411)
 *
 * Event description: Fires when the endpoint for the external collections is connected successfully
 *
 * @param {externalCollectionsAddedSuccessfullyParams} params - The parameters for this event
 * @property {string} app_name - app_name
 * @property {string} app_id - app_id
 * @property {number} users_with_write_devmode_count - users_with_write_devmode_count
 * @property {string} builderType - builder_type
 * @property {GUID} visitorId - visitor_id
 * @property {string} ds_type - ds_type
 * @property {GUID} site_id - site_id
 * @property {string} panel_id - panel_id
 * @property {string} appName - app_name
 * @property {number} users_on_site_count - users_on_site_count
 * @property {number} ts - ts
 * @property {string} panel_name - panel_name
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {number} number_of_collections_exposed - number of collections exposed on endpoint setting
 * @property {string} pageId - page_id
 * @property {string} token - token
 * @property {number} users_with_devmode_count - users_with_devmode_count
 * @property {string} ver - version
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {string} dev_mode - dev_mode
 * @property {string} appId - app_id
 * @property {GUID} esi - esi
 * @property {GUID} msid - msid
 * @property {string} ds_id - ds_id
 * @property {string} namespace - namespace
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<externalCollectionsAddedSuccessfullyParams>} LogObject<externalCollectionsAddedSuccessfullyParams>
 */
export function externalCollectionsAddedSuccessfully(params) {
    return { evid: 411, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'platformApiCallLayoutMethods' event (evid: 412)
 *
 * Event description: Platform API call Layout methods
 *
 * @param {platformApiCallLayoutMethodsParams} params - The parameters for this event
 * @property {string} app_name - app_name
 * @property {string} app_id - app_id
 * @property {string} module - module
 * @property {number} users_with_write_devmode_count - users_with_write_devmode_count
 * @property {string} builderType - builder_type
 * @property {GUID} visitorId - {visitor id}
 * @property {string} ds_type - ds_type
 * @property {GUID} site_id - site_id
 * @property {string} panel_id - panel_id
 * @property {string} appName - app_name
 * @property {number} users_on_site_count - users_on_site_count
 * @property {number} ts - ts
 * @property {string} panel_name - panel_name
 * @property {string} viewmode - viewmode
 * @property {string} input_name - {input name}
 * @property {string} appDefinitionId - app definition id
 * @property {string} pageId - page_id
 * @property {string} function - function name
 * @property {string} token - token
 * @property {number} users_with_devmode_count - users_with_devmode_count
 * @property {string} ver - version
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {string} dev_mode - dev_mode
 * @property {string} appId - app_id
 * @property {GUID} esi - esi
 * @property {GUID} msid - msid
 * @property {string} ds_id - ds_id
 * @property {string} arguments - argument
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<platformApiCallLayoutMethodsParams>} LogObject<platformApiCallLayoutMethodsParams>
 */
export function platformApiCallLayoutMethods(params) {
    return { evid: 412, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'installPackageFailed' event (evid: 413)
 *
 * Event description: install-package failed
 *
 * @param {installPackageFailedParams} params - The parameters for this event
 * @property {string} app_name - app_name
 * @property {string} app_id - app_id
 * @property {string} item_version - {}
 * @property {number} users_with_write_devmode_count - users_with_write_devmode_count
 * @property {string} builderType - builder_type
 * @property {GUID} visitorId - visitor_id
 * @property {string} ds_type - ds_type
 * @property {GUID} site_id - site_id
 * @property {string} panel_id - panel_id
 * @property {string} appName - app_name
 * @property {number} users_on_site_count - users_on_site_count
 * @property {number} ts - ts
 * @property {GUID} visitor_id - visitor_id
 * @property {string} panel_name - panel_name
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} pageId - page_id
 * @property {string} item_name - item_name
 * @property {string} origin - package_manager_main_view, built_versions
 * @property {string} token - token
 * @property {number} users_with_devmode_count - users_with_devmode_count
 * @property {string} ver - version
 * @property {string} userEnv - user_env
 * @property {boolean} is_latest_version - is_latest_version
 * @property {string} editor_working_mode - editor_working_mode
 * @property {string} dev_mode - dev_mode
 * @property {string} appId - app_id
 * @property {GUID} esi - esi
 * @property {string} item_type - item_type (npm, internal, private)
 * @property {GUID} msid - {}
 * @property {string} ds_id - ds_id
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<installPackageFailedParams>} LogObject<installPackageFailedParams>
 */
export function installPackageFailed(params) {
    return { evid: 413, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'generateCodeViaAiAssitantClicked' event (evid: 415)
 *
 * Event description: when a user submit request to generate code via AI assistant
 *
 * @param {generateCodeViaAiAssitantClickedParams} params - The parameters for this event
 * @property {string} app_name - app_name
 * @property {string} app_id - app_id
 * @property {number} users_with_write_devmode_count - users_with_write_devmode_count
 * @property {string} builderType - builder_type
 * @property {GUID} visitorId - visitor_id
 * @property {string} ds_type - ds_type
 * @property {GUID} site_id - site_id
 * @property {string} panel_id - panel_id
 * @property {string} appName - app_name
 * @property {number} users_on_site_count - users_on_site_count
 * @property {string} handler_name - {is_clicked, is_double_clicked,is_hovered_on etc}
 * @property {number} ts - ts
 * @property {string} panel_name - panel_name
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} pageId - page_id
 * @property {string} action_desc - {the required action that is described, to be translated to code}
 * @property {string} token - token
 * @property {number} users_with_devmode_count - users_with_devmode_count
 * @property {string} ver - version
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {string} dev_mode - dev_mode
 * @property {string} appId - app_id
 * @property {GUID} esi - esi
 * @property {string} component_id - {}
 * @property {string} component_type - {}
 * @property {GUID} msid - msid
 * @property {string} ds_id - ds_id
 * @property {GUID} conversation_id - conversation_id
 * @property {string} platform - platform
 * @property {string} entryPoint - entry_point
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<generateCodeViaAiAssitantClickedParams>} LogObject<generateCodeViaAiAssitantClickedParams>
 */
export function generateCodeViaAiAssitantClicked(params) {
    return { evid: 415, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'veloAiAssistatRequestSent' event (evid: 416)
 *
 * Event description: when a user submit request to generate code via AI assistant
 *
 * @param {veloAiAssistatRequestSentParams} params - The parameters for this event
 * @property {string} app_name - app_name
 * @property {string} app_id - app_id
 * @property {number} users_with_write_devmode_count - users_with_write_devmode_count
 * @property {string} builderType - builder_type
 * @property {GUID} visitorId - visitor_id
 * @property {string} ds_type - ds_type
 * @property {GUID} site_id - site_id
 * @property {string} panel_id - panel_id
 * @property {string} appName - app_name
 * @property {number} users_on_site_count - users_on_site_count
 * @property {string} handler_name - {is_clicked, is_double_clicked,is_hovered_on etc}
 * @property {number} ts - ts
 * @property {string} panel_name - panel_name
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} pageId - page_id
 * @property {string} prompt_id - {}
 * @property {string} action_desc - {the required action that is described, to be translated to code}
 * @property {string} origin - {editor event hanler, editor css etc}
 * @property {string} token - token
 * @property {number} users_with_devmode_count - users_with_devmode_count
 * @property {string} ver - version
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {string} dev_mode - dev_mode
 * @property {string} appId - app_id
 * @property {GUID} esi - esi
 * @property {string} component_id - {}
 * @property {string} conversation_id - {}
 * @property {string} component_type - {}
 * @property {GUID} msid - msid
 * @property {string} ds_id - ds_id
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<veloAiAssistatRequestSentParams>} LogObject<veloAiAssistatRequestSentParams>
 */
export function veloAiAssistatRequestSent(params) {
    return { evid: 416, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'veloAiAssistatRequestResult' event (evid: 417)
 *
 * Event description: The result of a request sent to Velo AI assistant (success, failure)
 *
 * @param {veloAiAssistatRequestResultParams} params - The parameters for this event
 * @property {string} app_name - app_name
 * @property {string} app_id - app_id
 * @property {number} users_with_write_devmode_count - users_with_write_devmode_count
 * @property {string} builderType - builder_type
 * @property {GUID} visitorId - visitor_id
 * @property {string} ds_type - ds_type
 * @property {GUID} site_id - site_id
 * @property {string} panel_id - panel_id
 * @property {string} appName - app_name
 * @property {number} users_on_site_count - users_on_site_count
 * @property {string} handler_name - {is_clicked, is_double_clicked,is_hovered_on etc}
 * @property {number} ts - ts
 * @property {string} panel_name - panel_name
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {boolean} isSuccess - {}
 * @property {string} pageId - page_id
 * @property {number} responseTime - {}
 * @property {string} action_desc - {the required action that is described, to be translated to code}
 * @property {string} origin - {editor event hanler, editor css etc}
 * @property {string} token - token
 * @property {number} users_with_devmode_count - users_with_devmode_count
 * @property {string} ver - version
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {string} error_message - {}
 * @property {string} dev_mode - dev_mode
 * @property {string} appId - app_id
 * @property {GUID} esi - esi
 * @property {string} component_id - {}
 * @property {string} conversation_id - {}
 * @property {string} component_type - {}
 * @property {GUID} msid - msid
 * @property {string} ds_id - ds_id
 * @property {string} platform - platform
 * @property {string} entryPoint - entry_point
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<veloAiAssistatRequestResultParams>} LogObject<veloAiAssistatRequestResultParams>
 */
export function veloAiAssistatRequestResult(params) {
    return { evid: 417, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'veloAiAssistantErrorMessageShown' event (evid: 418)
 *
 * Event description: Velo AI assistant error message shown
 *
 * @param {veloAiAssistantErrorMessageShownParams} params - The parameters for this event
 * @property {string} app_name - app_name
 * @property {string} app_id - app_id
 * @property {number} users_with_write_devmode_count - users_with_write_devmode_count
 * @property {string} builderType - builder_type
 * @property {GUID} visitorId - visitor_id
 * @property {string} ds_type - ds_type
 * @property {GUID} site_id - site_id
 * @property {string} panel_id - panel_id
 * @property {string} appName - app_name
 * @property {number} users_on_site_count - users_on_site_count
 * @property {string} handler_name - {is_clicked, is_double_clicked,is_hovered_on etc}
 * @property {number} ts - ts
 * @property {string} panel_name - panel_name
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} pageId - page_id
 * @property {string} action_desc - {the required action that is described, to be translated to code}
 * @property {string} origin - {editor event hanler, editor css etc}
 * @property {string} token - token
 * @property {number} users_with_devmode_count - users_with_devmode_count
 * @property {string} ver - version
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {string} dev_mode - dev_mode
 * @property {string} appId - app_id
 * @property {GUID} esi - esi
 * @property {string} component_id - {}
 * @property {string} conversation_id - {}
 * @property {string} component_type - {}
 * @property {GUID} msid - msid
 * @property {string} ds_id - ds_id
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<veloAiAssistantErrorMessageShownParams>} LogObject<veloAiAssistantErrorMessageShownParams>
 */
export function veloAiAssistantErrorMessageShown(params) {
    return { evid: 418, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'veloAiAssistantRetryGenerateCodeClicked' event (evid: 419)
 *
 * Event description: Velo AI assistant retry generate code clicked
 *
 * @param {veloAiAssistantRetryGenerateCodeClickedParams} params - The parameters for this event
 * @property {string} app_name - app_name
 * @property {string} app_id - app_id
 * @property {number} users_with_write_devmode_count - users_with_write_devmode_count
 * @property {string} builderType - builder_type
 * @property {GUID} visitorId - visitor_id
 * @property {string} ds_type - ds_type
 * @property {GUID} site_id - site_id
 * @property {string} panel_id - panel_id
 * @property {string} appName - app_name
 * @property {number} users_on_site_count - users_on_site_count
 * @property {string} handler_name - {is_clicked, is_double_clicked,is_hovered_on etc}
 * @property {number} ts - ts
 * @property {string} panel_name - panel_name
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} pageId - page_id
 * @property {string} action_desc - {the required action that is described, to be translated to code}
 * @property {string} origin - {editor event hanler, editor css etc}
 * @property {string} token - token
 * @property {number} users_with_devmode_count - users_with_devmode_count
 * @property {string} ver - version
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {string} dev_mode - dev_mode
 * @property {string} appId - app_id
 * @property {GUID} esi - esi
 * @property {string} component_id - {}
 * @property {string} conversation_id - {}
 * @property {string} component_type - {}
 * @property {GUID} msid - msid
 * @property {string} ds_id - ds_id
 * @property {string} platform - platform
 * @property {string} entryPoint - entry_point
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<veloAiAssistantRetryGenerateCodeClickedParams>} LogObject<veloAiAssistantRetryGenerateCodeClickedParams>
 */
export function veloAiAssistantRetryGenerateCodeClicked(params) {
    return { evid: 419, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'veloAiAssistantInsertGeneratedCodeClicked' event (evid: 420)
 *
 * Event description: Velo AI assistant insert generated code clicked
 *
 * @param {veloAiAssistantInsertGeneratedCodeClickedParams} params - The parameters for this event
 * @property {string} app_name - app_name
 * @property {string} app_id - app_id
 * @property {number} users_with_write_devmode_count - users_with_write_devmode_count
 * @property {string} builderType - builder_type
 * @property {GUID} visitorId - visitor_id
 * @property {string} ds_type - ds_type
 * @property {GUID} site_id - site_id
 * @property {string} panel_id - panel_id
 * @property {string} appName - app_name
 * @property {number} users_on_site_count - users_on_site_count
 * @property {string} handler_name - {is_clicked, is_double_clicked,is_hovered_on etc}
 * @property {number} ts - ts
 * @property {string} panel_name - panel_name
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} pageId - page_id
 * @property {string} action_desc - {the required action that is described, to be translated to code}
 * @property {string} origin - {editor event hanler, editor css etc}
 * @property {string} token - token
 * @property {number} users_with_devmode_count - users_with_devmode_count
 * @property {string} ver - version
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {string} dev_mode - dev_mode
 * @property {string} appId - app_id
 * @property {GUID} esi - esi
 * @property {string} component_id - {}
 * @property {string} conversation_id - {}
 * @property {string} component_type - {}
 * @property {GUID} msid - msid
 * @property {string} ds_id - ds_id
 * @property {string} model_response - model_response
 * @property {string} entryPoint - entry_point
 * @property {string} platform - platform
 * @property {boolean} success - success
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<veloAiAssistantInsertGeneratedCodeClickedParams>} LogObject<veloAiAssistantInsertGeneratedCodeClickedParams>
 */
export function veloAiAssistantInsertGeneratedCodeClicked(params) {
    return { evid: 420, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'veloBackendRequestResourcesUsage' event (evid: 4201)
 *
 * Event description: This event is designed to track actual resource usage from backend requests
 *
 * @param {veloBackendRequestResourcesUsageParams} params - The parameters for this event
 * @property {string} containerType - for now only 'Regular' is relevant
 * @property {number} cpuUsage - percentage of used CPU
 * @property {string} errorType - null for success/ OOM (out of memory)/CPU throttled
 * @property {number} memoryUsage - memory used (MB)
 * @property {string} containerId - container_id
 * @property {GUID} msid - msid
 * @property {string} containerStatus - Started/Running/Stopped/Error
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<veloBackendRequestResourcesUsageParams>} LogObject<veloBackendRequestResourcesUsageParams>
 */
export function veloBackendRequestResourcesUsage(params) {
    return { evid: 4201, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'veloBackendRequestContainerStoped' event (evid: 4202)
 *
 * Event description: This events is fired when a container is shutting down - this events also indicates if there was an error
 *
 * @param {veloBackendRequestContainerStopedParams} params - The parameters for this event
 * @property {number} duration - amount of time the container was up
 * @property {string} containerType - for now only 'Regular' is relevant
 * @property {DateTime} startTime - the actual time the container started
 * @property {string} errorType - null for success/ OOM (out of memory)/CPU throttled
 * @property {string} containerId - container_id
 * @property {GUID} msid - msid
 * @property {DateTime} stopTime - the actual time the container stop
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<veloBackendRequestContainerStopedParams>} LogObject<veloBackendRequestContainerStopedParams>
 */
export function veloBackendRequestContainerStoped(params) {
    return { evid: 4202, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'veloBackendRequestUsage' event (evid: 4203)
 *
 * Event description: fire every minute the container is up with information about the usage of that container
 *
 * @param {veloBackendRequestUsageParams} params - The parameters for this event
 * @property {string} containerType - for now only 'Regular' is relevant
 * @property {number} cpuLimit - the current limit on CPU in millicpu
 * @property {number} memoryLimit - the current limit on memory in byte
 * @property {number} cpuUsage - the current usage of CPU in millicpu
 * @property {number} memoryUsage - the current use of memory in byte
 * @property {string} containerId - container_id
 * @property {GUID} msid - msid
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<veloBackendRequestUsageParams>} LogObject<veloBackendRequestUsageParams>
 */
export function veloBackendRequestUsage(params) {
    return { evid: 4203, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'veloBackendRequestContainerError' event (evid: 4204)
 *
 * Event description: This events is fired when a container got an error
 *
 * @param {veloBackendRequestContainerErrorParams} params - The parameters for this event
 * @property {string} containerType - for now only 'Regular' is relevant
 * @property {string} errorType - null for success/ OOM (out of memory)/CPU throttled
 * @property {DateTime} realTime - the actual time the container got the error
 * @property {string} containerId - container_id
 * @property {GUID} msid - msid
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<veloBackendRequestContainerErrorParams>} LogObject<veloBackendRequestContainerErrorParams>
 */
export function veloBackendRequestContainerError(params) {
    return { evid: 4204, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'wixToVeloRoundtripLatency' event (evid: 4205)
 *
 * Event description: measure the roundtrip between velo to Wix DCs periodically
 *
 * @param {wixToVeloRoundtripLatencyParams} params - The parameters for this event
 * @property {number} duration - duration
 * @property {string} httpStatusCode - 200/408
 * @property {string} dc - dc
 * @property {string} cloudProvider - cloud_provider
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<wixToVeloRoundtripLatencyParams>} LogObject<wixToVeloRoundtripLatencyParams>
 */
export function wixToVeloRoundtripLatency(params) {
    return { evid: 4205, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'platformSdkApiCallDuration' event (evid: 421)
 *
 * Event description: Platform SDK API call duration
 *
 * @param {platformSdkApiCallDurationParams} params - The parameters for this event
 * @property {string} app_name - app_name
 * @property {string} app_id - app_id
 * @property {number} duration - {duration}
 * @property {string} module - module
 * @property {number} users_with_write_devmode_count - users_with_write_devmode_count
 * @property {string} builderType - builder_type
 * @property {GUID} visitorId - {visitor id}
 * @property {string} ds_type - ds_type
 * @property {GUID} site_id - site_id
 * @property {string} panel_id - panel_id
 * @property {string} appName - app_name
 * @property {number} users_on_site_count - users_on_site_count
 * @property {number} ts - ts
 * @property {string} panel_name - panel_name
 * @property {string} viewmode - viewmode
 * @property {string} input_name - {input name}
 * @property {string} appDefinitionId - app definition id
 * @property {string} pageId - page_id
 * @property {string} function - function name
 * @property {string} token - token
 * @property {number} users_with_devmode_count - users_with_devmode_count
 * @property {string} ver - version
 * @property {string} userEnv - user_env
 * @property {string} editorType - { editor type}
 * @property {string} editor_working_mode - editor_working_mode
 * @property {string} dev_mode - dev_mode
 * @property {string} appId - app_id
 * @property {GUID} esi - esi
 * @property {GUID} msid - msid
 * @property {string} ds_id - ds_id
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<platformSdkApiCallDurationParams>} LogObject<platformSdkApiCallDurationParams>
 */
export function platformSdkApiCallDuration(params) {
    return { evid: 421, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'buildLogLoaded' event (evid: 422)
 *
 * Event description: when a user enter the build log in the publish pipeline flow
 *
 * @param {buildLogLoadedParams} params - The parameters for this event
 * @property {string} app_name - app_name
 * @property {string} app_id - app_id
 * @property {number} users_with_write_devmode_count - users_with_write_devmode_count
 * @property {string} builderType - builder_type
 * @property {GUID} visitorId - visitor_id
 * @property {string} codeAppId - code_app_id
 * @property {string} ds_type - ds_type
 * @property {GUID} site_id - site_id
 * @property {string} panel_id - panel_id
 * @property {string} appName - app_name
 * @property {number} users_on_site_count - users_on_site_count
 * @property {string} pagesIds - the pages that their bundling failed
 * @property {number} ts - ts
 * @property {string} panel_name - panel_name
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} pageId - page_id
 * @property {string} origin - developer_tools/publish_modal
 * @property {string} token - token
 * @property {number} users_with_devmode_count - users_with_devmode_count
 * @property {string} ver - version
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {string} dev_mode - dev_mode
 * @property {string} appId - app_id
 * @property {GUID} esi - esi
 * @property {GUID} msid - msid
 * @property {string} ds_id - ds_id
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<buildLogLoadedParams>} LogObject<buildLogLoadedParams>
 */
export function buildLogLoaded(params) {
    return { evid: 422, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'dataBindingRightPanelShowOnAllPagesToggleChange' event (evid: 423)
 *
 * Event description: Data Binding Right Panel - Show on all pages toggle change
 *
 * @param {dataBindingRightPanelShowOnAllPagesToggleChangeParams} params - The parameters for this event
 * @property {string} app_name - app_name
 * @property {string} app_id - app_id
 * @property {number} users_with_write_devmode_count - users_with_write_devmode_count
 * @property {boolean} toggle_state - true / false
 * @property {string} builderType - builder_type
 * @property {GUID} visitorId - visitor id
 * @property {string} ds_type - ds_type
 * @property {GUID} site_id - site_id
 * @property {string} panel_id - panel_id
 * @property {string} appName - app_name
 * @property {number} users_on_site_count - users_on_site_count
 * @property {number} ts - ts
 * @property {string} panel_name - panel_name
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} controller_type - controller_type
 * @property {string} ds_name - ds name
 * @property {string} section - main / settings
 * @property {string} pageId - page_id
 * @property {string} origin - dp_connections / gfpp
 * @property {string} token - token
 * @property {number} users_with_devmode_count - users_with_devmode_count
 * @property {string} ver - version
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {string} dev_mode - dev_mode
 * @property {string} appId - app_id
 * @property {GUID} esi - esi
 * @property {string} component_id - component_id
 * @property {string} component_type - component type
 * @property {GUID} msid - msid
 * @property {string} ds_id - ds_id
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<dataBindingRightPanelShowOnAllPagesToggleChangeParams>} LogObject<dataBindingRightPanelShowOnAllPagesToggleChangeParams>
 */
export function dataBindingRightPanelShowOnAllPagesToggleChange(params) {
    return { evid: 423, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'addDynamicPageManageItemModal' event (evid: 424)
 *
 * Event description: Add dynamic Page Manage item Modal
 *
 * @param {addDynamicPageManageItemModalParams} params - The parameters for this event
 * @property {string} app_name - app_name
 * @property {string} app_id - app_id
 * @property {number} users_with_write_devmode_count - users_with_write_devmode_count
 * @property {string} builderType - builder_type
 * @property {GUID} visitorId - visitor ID
 * @property {boolean} is_first_data_modal - is this the first data modal the user see
 * @property {string} ds_type - ds_type
 * @property {string} action_name - show / create_page / close / back / learn_more
 * @property {GUID} site_id - site_id
 * @property {string} panel_id - panel_id
 * @property {string} appName - app_name
 * @property {number} users_on_site_count - users_on_site_count
 * @property {number} ts - ts
 * @property {string} collection_id - collection id
 * @property {string} panel_name - panel_name
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} collection_permissions - collection_permissions
 * @property {string} pageId - page_id
 * @property {string} origin - pagesPanel
 * @property {string} token - token
 * @property {number} users_with_devmode_count - users_with_devmode_count
 * @property {string} ver - version
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {string} dev_mode - dev_mode
 * @property {string} appId - app_id
 * @property {GUID} esi - esi
 * @property {string} collection_name - collection name
 * @property {GUID} msid - msid
 * @property {string} ds_id - ds_id
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<addDynamicPageManageItemModalParams>} LogObject<addDynamicPageManageItemModalParams>
 */
export function addDynamicPageManageItemModal(params) {
    return { evid: 424, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'cmsExpressionsExpressionSet' event (evid: 425)
 *
 * Event description: CMS Expressions - Expression set
 *
 * @param {cmsExpressionsExpressionSetParams} params - The parameters for this event
 * @property {string} expression_set - expression_set
 * @property {string} component_id - component_id
 * @property {string} ds_id - ds_id
 * @property {string} collection_id - collection_id
 * @property {string} component_type - component_type
 * @property {string} origin - origin
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<cmsExpressionsExpressionSetParams>} LogObject<cmsExpressionsExpressionSetParams>
 */
export function cmsExpressionsExpressionSet(params) {
    return { evid: 425, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'cmsExpressionsExpressionErrorShown' event (evid: 426)
 *
 * Event description: CMS Expressions - Expression error shown
 *
 * @param {cmsExpressionsExpressionErrorShownParams} params - The parameters for this event
 * @property {string} expression_set - expression_set
 * @property {string} component_id - component_id
 * @property {string} ds_id - ds_id
 * @property {string} collection_id - collection_id
 * @property {string} component_type - component_type
 * @property {string} origin - origin
 * @property {string} error_type - error_type
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<cmsExpressionsExpressionErrorShownParams>} LogObject<cmsExpressionsExpressionErrorShownParams>
 */
export function cmsExpressionsExpressionErrorShown(params) {
    return { evid: 426, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'cmsExpressionsExpressionSuggestionUsed' event (evid: 427)
 *
 * Event description: CMS Expressions - Expression suggestion used
 *
 * @param {cmsExpressionsExpressionSuggestionUsedParams} params - The parameters for this event
 * @property {string} suggestion - suggestion
 * @property {string} component_id - component_id
 * @property {string} ds_id - ds_id
 * @property {string} collection_id - collection_id
 * @property {string} component_type - component_type
 * @property {string} origin - origin
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<cmsExpressionsExpressionSuggestionUsedParams>} LogObject<cmsExpressionsExpressionSuggestionUsedParams>
 */
export function cmsExpressionsExpressionSuggestionUsed(params) {
    return { evid: 427, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'cmsExpressionsFieldIdActions' event (evid: 428)
 *
 * Event description: CMS Expressions - Field id actions
 *
 * @param {cmsExpressionsFieldIdActionsParams} params - The parameters for this event
 * @property {string} action - click_view, list_open, copy_id, close
 * @property {string} component_id - component_id
 * @property {string} ds_id - ds_id
 * @property {string} collection_id - collection_id
 * @property {string} component_type - component_type
 * @property {string} origin - origin
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<cmsExpressionsFieldIdActionsParams>} LogObject<cmsExpressionsFieldIdActionsParams>
 */
export function cmsExpressionsFieldIdActions(params) {
    return { evid: 428, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'cmsExpressionsExpressionAdditionalInfo' event (evid: 429)
 *
 * Event description: CMS Expressions - Expression additional info
 *
 * @param {cmsExpressionsExpressionAdditionalInfoParams} params - The parameters for this event
 * @property {string} component_id - component_id
 * @property {string} ds_id - ds_id
 * @property {string} collection_id - collection_id
 * @property {string} component_type - component_type
 * @property {string} origin - origin
 * @property {string} action - view_info, click_link, field_info
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<cmsExpressionsExpressionAdditionalInfoParams>} LogObject<cmsExpressionsExpressionAdditionalInfoParams>
 */
export function cmsExpressionsExpressionAdditionalInfo(params) {
    return { evid: 429, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'dsSortReorder' event (evid: 43)
 *
 * Event description: DS sort reorder
 *
 * @param {dsSortReorderParams} params - The parameters for this event
 * @property {string} app_name - app_name
 * @property {string} app_id - app_id
 * @property {number} users_with_write_devmode_count - users_with_write_devmode_count
 * @property {string} builderType - builder_type
 * @property {GUID} visitorId - visitor_id
 * @property {string} ds_type - ds_type
 * @property {GUID} site_id - site_id
 * @property {string} panel_id - panel_id
 * @property {string} appName - app_name
 * @property {number} users_on_site_count - users_on_site_count
 * @property {number} ts - ts
 * @property {string} panel_name - panel_name
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} pageId - page_id
 * @property {string} new_order - new_order
 * @property {string} order - order (json)
 * @property {string} token - token
 * @property {number} users_with_devmode_count - users_with_devmode_count
 * @property {string} ver - version
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {string} dev_mode - dev_mode
 * @property {string} appId - app_id
 * @property {GUID} esi - esi
 * @property {GUID} msid - msid
 * @property {string} ds_id - ds_id
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<dsSortReorderParams>} LogObject<dsSortReorderParams>
 */
export function dsSortReorder(params) {
    return { evid: 43, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'uouFilterAdvancedFiltering' event (evid: 430)
 *
 * Event description: User changed advance filtering in UoU filter -  dropdown / radio_button / slider / multi_checkbox / selecting_tags / single_checkbox
 *
 * @param {uouFilterAdvancedFilteringParams} params - The parameters for this event
 * @property {string} app_name - app_name
 * @property {string} app_id - app_id
 * @property {string} dataset_name - dataset name
 * @property {number} users_with_write_devmode_count - users_with_write_devmode_count
 * @property {string} builderType - builder_type
 * @property {GUID} visitorId - visitor_id
 * @property {string} ds_type - ds_type
 * @property {GUID} site_id - site_id
 * @property {string} panel_id - panel_id
 * @property {string} appName - app_name
 * @property {number} users_on_site_count - users_on_site_count
 * @property {string} uuid - name
 * @property {string} field_type - field type text/ numbers/ date /tags
 * @property {number} ts - ts
 * @property {string} collection_id - collection_id
 * @property {string} panel_name - panel_name
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} page_name - page name
 * @property {string} pageId - page_id
 * @property {string} condition - condition
 * @property {string} token - token
 * @property {number} users_with_devmode_count - users_with_devmode_count
 * @property {string} ver - version
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {string} dev_mode - dev_mode
 * @property {string} appId - app_id
 * @property {GUID} esi - esi
 * @property {string} collection_name - collection name
 * @property {string} component_type - dropdown/radio_button/slider/multi_checkbox/selecting_tags/single_checkbox
 * @property {GUID} msid - msid
 * @property {string} field_name - selected field name
 * @property {string} ds_id - ds_id
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<uouFilterAdvancedFilteringParams>} LogObject<uouFilterAdvancedFilteringParams>
 */
export function uouFilterAdvancedFiltering(params) {
    return { evid: 430, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'codeLinkedElementErrorModalSrc79Evid431' event (evid: 431)
 *
 * Event description: code linked element - error modal
 *
 * @param {codeLinkedElementErrorModalSrc79Evid431Params} params - The parameters for this event
 * @property {string} action - view/ cancel/ x/ ?/ delete anyway
 * @property {boolean} dontShowAgain - true false
 * @return {LogObject<codeLinkedElementErrorModalSrc79Evid431Params>} LogObject<codeLinkedElementErrorModalSrc79Evid431Params>
 */
export function codeLinkedElementErrorModalSrc79Evid431(params) {
    return { evid: 431, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'codeLinkedElementCodeLabelClickSrc79Evid432' event (evid: 432)
 *
 * Event description: code linked element - code label click
 *
 * @param {codeLinkedElementCodeLabelClickSrc79Evid432Params} params - The parameters for this event
 * @property {string} element_type - elementType
 * @property {string} devMode - off/ on
 * @property {string} element_id - element_id
 * @return {LogObject<codeLinkedElementCodeLabelClickSrc79Evid432Params>} LogObject<codeLinkedElementCodeLabelClickSrc79Evid432Params>
 */
export function codeLinkedElementCodeLabelClickSrc79Evid432(params) {
    return { evid: 432, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'codeLinkedElementElementClickSrc79Evid433' event (evid: 433)
 *
 * Event description: code linked element - element click
 *
 * @param {codeLinkedElementElementClickSrc79Evid433Params} params - The parameters for this event
 * @property {string} element_type - elementType
 * @property {string} devMode - off/ on
 * @property {string} element_id - element_id
 * @return {LogObject<codeLinkedElementElementClickSrc79Evid433Params>} LogObject<codeLinkedElementElementClickSrc79Evid433Params>
 */
export function codeLinkedElementElementClickSrc79Evid433(params) {
    return { evid: 433, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'packageManagerSearchSrc79Evid435' event (evid: 435)
 *
 * Event description: package manager - search
 *
 * @param {packageManagerSearchSrc79Evid435Params} params - The parameters for this event
 * @property {string} inputText - input
 * @property {string} item_type - npm/velo_packages
 * @property {boolean} only_show_npms_developed_by_wix - true/ false
 * @return {LogObject<packageManagerSearchSrc79Evid435Params>} LogObject<packageManagerSearchSrc79Evid435Params>
 */
export function packageManagerSearchSrc79Evid435(params) {
    return { evid: 435, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'blocksWidgetDataBindingRightPanelElementSrc79Evid437' event (evid: 437)
 *
 * Event description: Data Binding Right Panel Element- for Blocks widget  properties binding
 *
 * @param {blocksWidgetDataBindingRightPanelElementSrc79Evid437Params} params - The parameters for this event
 * @property {string} builderType - builder_type
 * @property {string} ds_type - ds_type
 * @property {string} action_name - Perfomed action in the panel
 * @property {string} panel_id - panel_id
 * @property {string} panel_name - panel_name
 * @property {string} ds_name - ds name
 * @property {string} section - main / settings
 * @property {string} pageId - page_id
 * @property {string} origin - blocks_widget
 * @property {string} component_id - component_id
 * @property {string} component_type - component type
 * @property {string} ds_id - ds_id
 * @property {string} widget_id - widget_id
 * @property {string} blocks_app_id - ID of blocks app
 * @property {string} blocks_app_name - name of blocks app
 * @return {LogObject<blocksWidgetDataBindingRightPanelElementSrc79Evid437Params>} LogObject<blocksWidgetDataBindingRightPanelElementSrc79Evid437Params>
 */
export function blocksWidgetDataBindingRightPanelElementSrc79Evid437(params) {
    return { evid: 437, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'blocksWidgetPropertiesDatasetDeletedSrc79Evid438' event (evid: 438)
 *
 * Event description: Dataset Deleted- of blocks wiget properties
 *
 * @param {blocksWidgetPropertiesDatasetDeletedSrc79Evid438Params} params - The parameters for this event
 * @property {string} builderType - builder_type
 * @property {string} ds_type - ds_type
 * @property {string} ds_name - ds name
 * @property {string} section - main / settings
 * @property {string} pageId - page_id
 * @property {string} origin - origin
 * @property {string} ds_id - ds_id
 * @property {string} widget_id - json array of all widget id connected to that DS
 * @property {string} action - delete\ cancel
 * @property {string} blocks_app_id - blocks_app_id
 * @property {string} blocks_app_name - blocks_app_name
 * @return {LogObject<blocksWidgetPropertiesDatasetDeletedSrc79Evid438Params>} LogObject<blocksWidgetPropertiesDatasetDeletedSrc79Evid438Params>
 */
export function blocksWidgetPropertiesDatasetDeletedSrc79Evid438(params) {
    return { evid: 438, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'blocksWidgetPropertyConnectedSrc79Evid439' event (evid: 439)
 *
 * Event description: User connects element to field of a widget property in Blocks
 *
 * @param {blocksWidgetPropertyConnectedSrc79Evid439Params} params - The parameters for this event
 * @property {string} blocks_app_name - blocks_app_name
 * @property {string} blocks_app_id - blocks_app_id
 * @property {string} builderType - builder_type
 * @property {string} ds_type - ds_type
 * @property {string} panel_id - panel_id
 * @property {string} field_type - field_type
 * @property {string} widget_id - widget_id
 * @property {string} ref_field_name - reference field name
 * @property {string} panel_name - panel_name
 * @property {string} pageId - page_id
 * @property {string} origin - {confirm_suggestion, drop_down}
 * @property {string} repeater_parent_id - if attached to repeater - this will be the repeater id
 * @property {string} property - property
 * @property {string} component_id - component_id
 * @property {string} component_type - comp type
 * @property {string} field_name - field_name
 * @property {string} ds_id - ds_id
 * @property {string} ref_field_type - reference  field type
 * @property {string} ds_name - ds_name
 * @return {LogObject<blocksWidgetPropertyConnectedSrc79Evid439Params>} LogObject<blocksWidgetPropertyConnectedSrc79Evid439Params>
 */
export function blocksWidgetPropertyConnectedSrc79Evid439(params) {
    return { evid: 439, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'dsEditFilterClicked' event (evid: 44)
 *
 * Event description: DS edit filter clicked
 *
 * @param {dsEditFilterClickedParams} params - The parameters for this event
 * @property {string} app_name - app_name
 * @property {string} app_id - app_id
 * @property {string} renderingFlow - Rendering flow
 * @property {number} users_with_write_devmode_count - users_with_write_devmode_count
 * @property {string} builderType - builder_type
 * @property {GUID} visitorId - visitor_id
 * @property {string} ds_type - ds_type
 * @property {GUID} site_id - site_id
 * @property {string} panel_id - panel_id
 * @property {string} appName - app_name
 * @property {number} users_on_site_count - users_on_site_count
 * @property {number} ts - ts
 * @property {string} panel_name - panel_name
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} filter - filter (as json)
 * @property {string} pageId - page_id
 * @property {string} token - token
 * @property {number} users_with_devmode_count - users_with_devmode_count
 * @property {string} ver - version
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {string} dev_mode - dev_mode
 * @property {string} appId - app_id
 * @property {GUID} esi - esi
 * @property {GUID} msid - msid
 * @property {string} ds_id - ds_id
 * @property {string} collection_id - collection_id
 * @return {LogObject<dsEditFilterClickedParams>} LogObject<dsEditFilterClickedParams>
 */
export function dsEditFilterClicked(params) {
    return { evid: 44, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'connectPanelAddPropertyButtonClickedSrc79Evid440' event (evid: 440)
 *
 * Event description: add Blocks widget property button clicked
 *
 * @param {connectPanelAddPropertyButtonClickedSrc79Evid440Params} params - The parameters for this event
 * @property {string} blocks_app_name - blocks_app_name
 * @property {string} blocks_app_id - blocks_app_id
 * @property {string} builderType - builder_type
 * @property {string} panel_id - panel_id
 * @property {string} pageId - page_id
 * @property {string} origin - from where the user click add dataset = connect_component_empty_state_dataset/ connect_component_create_new_dataset
 * @property {string} component_id - component_id
 * @property {string} component_type - component_type
 * @property {string} ds_id - ds_id
 * @property {string} ds_name - ds_name
 * @property {string} ds_type - ds_type
 * @property {string} widget_id - widget_id
 * @return {LogObject<connectPanelAddPropertyButtonClickedSrc79Evid440Params>} LogObject<connectPanelAddPropertyButtonClickedSrc79Evid440Params>
 */
export function connectPanelAddPropertyButtonClickedSrc79Evid440(params) {
    return { evid: 440, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'newCommitWasPulledFromGitHub' event (evid: 4400)
 *
 * Event description: When the site rebase the new code from github (branch origin-main) back to the editor
 *
 * @param {newCommitWasPulledFromGitHubParams} params - The parameters for this event
 * @property {string} codeAppId - the code_app_id (grid_app_id) of the code that was comitted
 * @property {string} gitHubUserName - the user name in github
 * @property {string} repoName - the name of the repository
 * @property {number} siteRevision - the revision of the site
 * @property {string} latestCommitHash - the latest commit hash
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<newCommitWasPulledFromGitHubParams>} LogObject<newCommitWasPulledFromGitHubParams>
 */
export function newCommitWasPulledFromGitHub(params) {
    return { evid: 4400, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'localCodePushedCommitToGithub' event (evid: 4403)
 *
 * Event description: users local code was committed to Github
 * This event should be fired for each commit in the push.
 *
 * @param {localCodePushedCommitToGithubParams} params - The parameters for this event
 * @property {string} addedFilesPaths - array of all the files that were added in this commit
 * @property {string} commitHash - the hash of the commit
 * @property {string} pushId - the id for that push
 * @property {boolean} defaultBranch - is this branch the default branch
 * @property {string} deletedFilesPaths - array of all the files that were deleted in this commit
 * @property {string} modifiedFilesPaths - array of all the files that were modified in this commit
 * @property {string} branchName - the name of the branch
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<localCodePushedCommitToGithubParams>} LogObject<localCodePushedCommitToGithubParams>
 */
export function localCodePushedCommitToGithub(params) {
    return { evid: 4403, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'widgetPropertyAddedViaCmsDataBindingPanelSrc79Evid441' event (evid: 441)
 *
 * Event description: Widget property added via CMS Data Binding Panel
 *
 * @param {widgetPropertyAddedViaCmsDataBindingPanelSrc79Evid441Params} params - The parameters for this event
 * @property {string} action - action- cancel\ create
 * @property {string} page_id - page_id
 * @property {string} panel_name - panel_na
 * @property {string} property_type - property_type
 * @property {string} widget_id - widget_id
 * @property {string} property_name - property_name
 * @property {string} origin - origin
 * @return {LogObject<widgetPropertyAddedViaCmsDataBindingPanelSrc79Evid441Params>} LogObject<widgetPropertyAddedViaCmsDataBindingPanelSrc79Evid441Params>
 */
export function widgetPropertyAddedViaCmsDataBindingPanelSrc79Evid441(params) {
    return { evid: 441, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'beNpmImportOnPublishSrc79Evid442' event (evid: 442)
 *
 * Event description: sent when an NPM library was imported into BE code on live site Event that is being fired upon site-publish, it’s being reported for each of the site’s backend entrypoints (files) and counts the number of NPM packages that’s being required during each entrypoint loading process.
 *
 * @param {beNpmImportOnPublishSrc79Evid442Params} params - The parameters for this event
 * @property {string} renderingFlow - Rendering flow
 * @property {string} exceptionType - {}
 * @property {string} file_name - file name
 * @property {boolean} exception - {true/false}
 * @property {string} npm_module_name - npm module name
 * @property {string} grid_app_id - grid_app_id
 * @property {Url} page_url - page url
 * @property {number} packages_count - packages_count
 * @property {string} npmpackagesnames - npm-packages-names
 * @property {string} veloPkgsCount - velo_pkgs_count
 * @property {string} veloPkgsName - velo_pkgs_name
 * @return {LogObject<beNpmImportOnPublishSrc79Evid442Params>} LogObject<beNpmImportOnPublishSrc79Evid442Params>
 */
export function beNpmImportOnPublishSrc79Evid442(params) {
    return { evid: 442, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'uninstalledNpmIncludedInUserCodeSrc79Evid444' event (evid: 444)
 *
 * Event description: This event is triggered whenever a user hovers over an NPM package in their code that is referenced but not installed in their project
 *
 *
 *
 * @param {uninstalledNpmIncludedInUserCodeSrc79Evid444Params} params - The parameters for this event
 * @property {string} renderingFlow - Rendering flow
 * @property {string} input_name - input_name
 * @property {string} item_name - item_name
 * @property {string} item_type - item_type (npm, internal, private)
 * @property {GUID} msid - {}
 * @return {LogObject<uninstalledNpmIncludedInUserCodeSrc79Evid444Params>} LogObject<uninstalledNpmIncludedInUserCodeSrc79Evid444Params>
 */
export function uninstalledNpmIncludedInUserCodeSrc79Evid444(params) {
    return { evid: 444, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'gridManagerInPageNavigationSrc79Evid445' event (evid: 445)
 *
 * Event description: switching tabs inside grid manager pages
 *
 * @param {gridManagerInPageNavigationSrc79Evid445Params} params - The parameters for this event
 * @property {string} 'page url' - grid manager page url
 * @property {string} 'tab name' - the tab the user go to
 * @property {string} uuid - user id
 * @return {LogObject<gridManagerInPageNavigationSrc79Evid445Params>} LogObject<gridManagerInPageNavigationSrc79Evid445Params>
 */
export function gridManagerInPageNavigationSrc79Evid445(params) {
    return { evid: 445, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'componentCodeStatusSrc79Evid446' event (evid: 446)
 *
 * Event description: The event checks whether the component is connected to code or not.
 *
 * This event is sent to provide exposure for  a test , allowing us to analyze user interactions with different components.
 *
 *
 * @param {componentCodeStatusSrc79Evid446Params} params - The parameters for this event
 * @property {string} componentId - component_id
 * @property {string} componentType - compo
 * @property {boolean} hasCode - has_code
 * @return {LogObject<componentCodeStatusSrc79Evid446Params>} LogObject<componentCodeStatusSrc79Evid446Params>
 */
export function componentCodeStatusSrc79Evid446(params) {
    return { evid: 446, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'importSuggestionShownSrc79Evid447' event (evid: 447)
 *
 * Event description: This event is triggered whenever a user types an import statement in the code editor, and the autocomplete suggestions appear with EDM or SDK options.
 *
 *
 * @param {importSuggestionShownSrc79Evid447Params} params - The parameters for this event
 * @property {string} renderingFlow - Rendering flow
 * @property {GUID} msid - {}
 * @property {string} line_of_code - line_of_code
 * @property {string} suggestion_origin - suggestion_origin
 * @return {LogObject<importSuggestionShownSrc79Evid447Params>} LogObject<importSuggestionShownSrc79Evid447Params>
 */
export function importSuggestionShownSrc79Evid447(params) {
    return { evid: 447, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'dsDeleteFilterClickedSrc79Evid448' event (evid: 448)
 *
 * Event description: DS delete filter clicked
 *
 * @param {dsDeleteFilterClickedSrc79Evid448Params} params - The parameters for this event
 * @property {string} ds_type - ds_type
 * @property {string} panel_id - panel_id
 * @property {string} panel_name - panel_name
 * @property {string} filter - filter (as json)
 * @property {string} pageId - page_id
 * @property {GUID} msid - msid
 * @property {string} ds_id - ds_id
 * @property {string} collection_id - collection_id
 * @property {string} origin - origin
 * @return {LogObject<dsDeleteFilterClickedSrc79Evid448Params>} LogObject<dsDeleteFilterClickedSrc79Evid448Params>
 */
export function dsDeleteFilterClickedSrc79Evid448(params) {
    return { evid: 448, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'dsSortMenuClickedSrc79Evid449' event (evid: 449)
 *
 * Event description: When a user opens a DS sort menu button from DS settings panel
 *
 * @param {dsSortMenuClickedSrc79Evid449Params} params - The parameters for this event
 * @property {string} app_name - app_name
 * @property {string} builderType - builder_type
 * @property {string} ds_type - ds_type
 * @property {string} panel_id - panel_id
 * @property {string} panel_name - panel_name
 * @property {string} pageId - page_id
 * @property {GUID} msid - msid
 * @property {string} ds_id - ds_id
 * @property {string} collection_id - collection_id
 * @property {string} action - edit\ delete\ open
 * @property {string} sort_name - sort_name
 * @return {LogObject<dsSortMenuClickedSrc79Evid449Params>} LogObject<dsSortMenuClickedSrc79Evid449Params>
 */
export function dsSortMenuClickedSrc79Evid449(params) {
    return { evid: 449, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'dsDeleteFilterClicked' event (evid: 45)
 *
 * Event description: DS delete filter clicked
 *
 * @param {dsDeleteFilterClickedParams} params - The parameters for this event
 * @property {string} app_name - app_name
 * @property {string} app_id - app_id
 * @property {number} users_with_write_devmode_count - users_with_write_devmode_count
 * @property {string} builderType - builder_type
 * @property {GUID} visitorId - visitor_id
 * @property {string} ds_type - ds_type
 * @property {GUID} site_id - site_id
 * @property {string} panel_id - panel_id
 * @property {string} appName - app_name
 * @property {number} users_on_site_count - users_on_site_count
 * @property {number} ts - ts
 * @property {string} panel_name - panel_name
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} filter - filter (as json)
 * @property {string} pageId - page_id
 * @property {string} token - token
 * @property {number} users_with_devmode_count - users_with_devmode_count
 * @property {string} ver - version
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {string} dev_mode - dev_mode
 * @property {string} appId - app_id
 * @property {GUID} esi - esi
 * @property {GUID} msid - msid
 * @property {string} ds_id - ds_id
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<dsDeleteFilterClickedParams>} LogObject<dsDeleteFilterClickedParams>
 */
export function dsDeleteFilterClicked(params) {
    return { evid: 45, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'dsEditSortRuleClicked' event (evid: 46)
 *
 * Event description: DS edit sort rule clicked
 *
 * @param {dsEditSortRuleClickedParams} params - The parameters for this event
 * @property {string} app_name - app_name
 * @property {string} app_id - app_id
 * @property {number} users_with_write_devmode_count - users_with_write_devmode_count
 * @property {string} builderType - builder_type
 * @property {GUID} visitorId - visitor_id
 * @property {string} sort_rule - sort_rule
 * @property {string} ds_type - ds_type
 * @property {GUID} site_id - site_id
 * @property {string} panel_id - panel_id
 * @property {string} appName - app_name
 * @property {number} users_on_site_count - users_on_site_count
 * @property {number} ts - ts
 * @property {string} panel_name - panel_name
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} pageId - page_id
 * @property {string} token - token
 * @property {number} users_with_devmode_count - users_with_devmode_count
 * @property {string} ver - version
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {string} dev_mode - dev_mode
 * @property {string} sort - sort(json)
 * @property {string} appId - app_id
 * @property {GUID} esi - esi
 * @property {GUID} msid - msid
 * @property {string} ds_id - ds_id
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<dsEditSortRuleClickedParams>} LogObject<dsEditSortRuleClickedParams>
 */
export function dsEditSortRuleClicked(params) {
    return { evid: 46, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'gitConnection' event (evid: 4601)
 *
 * Event description: Fires when the site successfully connects/disconnects to git
 *
 * @param {gitConnectionParams} params - The parameters for this event
 * @property {GUID} uuid - uuid
 * @property {GUID} msid - msid
 * @property {string} action - connect/ disconnect
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<gitConnectionParams>} LogObject<gitConnectionParams>
 */
export function gitConnection(params) {
    return { evid: 4601, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'dsDeleteSortRuleClicked' event (evid: 47)
 *
 * Event description: DS delete sort rule clicked
 *
 * @param {dsDeleteSortRuleClickedParams} params - The parameters for this event
 * @property {string} app_name - app_name
 * @property {string} app_id - app_id
 * @property {number} users_with_write_devmode_count - users_with_write_devmode_count
 * @property {string} builderType - builder_type
 * @property {GUID} visitorId - visitor_id
 * @property {string} sort_rule - sort_rule
 * @property {string} ds_type - ds_type
 * @property {GUID} site_id - site_id
 * @property {string} panel_id - panel_id
 * @property {string} appName - app_name
 * @property {number} users_on_site_count - users_on_site_count
 * @property {number} ts - ts
 * @property {string} panel_name - panel_name
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} pageId - page_id
 * @property {string} token - token
 * @property {number} users_with_devmode_count - users_with_devmode_count
 * @property {string} ver - version
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {string} dev_mode - dev_mode
 * @property {string} sort - sort(json)
 * @property {string} appId - app_id
 * @property {GUID} esi - esi
 * @property {GUID} msid - msid
 * @property {string} ds_id - ds_id
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<dsDeleteSortRuleClickedParams>} LogObject<dsDeleteSortRuleClickedParams>
 */
export function dsDeleteSortRuleClicked(params) {
    return { evid: 47, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'connectPanelAddDatasetButtonClicked' event (evid: 49)
 *
 * Event description: DS add dataset button clicked
 *
 * @param {connectPanelAddDatasetButtonClickedParams} params - The parameters for this event
 * @property {string} app_name - app_name
 * @property {string} app_id - app_id
 * @property {number} users_with_write_devmode_count - users_with_write_devmode_count
 * @property {string} builderType - builder_type
 * @property {GUID} visitorId - visitor_id
 * @property {string} ds_type - ds_type
 * @property {GUID} site_id - site_id
 * @property {string} panel_id - panel_id
 * @property {string} appName - app_name
 * @property {number} users_on_site_count - users_on_site_count
 * @property {number} ts - ts
 * @property {string} panel_name - panel_name
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} pageId - page_id
 * @property {string} origin - from where the user click add dataset = connect_component_empty_state_dataset/ connect_component_create_new_dataset
 * @property {string} token - token
 * @property {number} users_with_devmode_count - users_with_devmode_count
 * @property {string} ver - version
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {string} dev_mode - dev_mode
 * @property {string} appId - app_id
 * @property {GUID} esi - esi
 * @property {string} component_id - component_id
 * @property {string} component_type - component_type
 * @property {GUID} msid - msid
 * @property {string} ds_id - ds_id
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<connectPanelAddDatasetButtonClickedParams>} LogObject<connectPanelAddDatasetButtonClickedParams>
 */
export function connectPanelAddDatasetButtonClicked(params) {
    return { evid: 49, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'addDsPanelCollectionSelected' event (evid: 50)
 *
 * Event description: Add DS panel collection selected
 *
 * @param {addDsPanelCollectionSelectedParams} params - The parameters for this event
 * @property {string} c1 - collection_id
 * @property {string} app_name - app_name
 * @property {string} app_id - app_id
 * @property {number} users_with_write_devmode_count - users_with_write_devmode_count
 * @property {string} builderType - builder_type
 * @property {GUID} visitorId - visitor_id
 * @property {string} ds_type - ds_type
 * @property {GUID} site_id - site_id
 * @property {string} panel_id - panel_id
 * @property {string} appName - app_name
 * @property {number} users_on_site_count - users_on_site_count
 * @property {number} ts - ts
 * @property {string} collection_id - collection_id
 * @property {string} panel_name - panel_name
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} pageId - page_id
 * @property {string} token - token
 * @property {number} users_with_devmode_count - users_with_devmode_count
 * @property {string} ver - version
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {string} dev_mode - dev_mode
 * @property {string} appId - app_id
 * @property {GUID} esi - esi
 * @property {boolean} is_single_item_collection - (true/false)
 * @property {string} collection_name - collection_name
 * @property {GUID} msid - msid
 * @property {string} ds_id - ds_id
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<addDsPanelCollectionSelectedParams>} LogObject<addDsPanelCollectionSelectedParams>
 */
export function addDsPanelCollectionSelected(params) {
    return { evid: 50, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'installedPackageChanged' event (evid: 500)
 *
 * Event description: Fired from server whenever there is a change in a package (event is sent per package per site)
 *
 * @param {installedPackageChangedParams} params - The parameters for this event
 * @property {string} newVersion - the new version of the package
 * @property {string} packageName - the name of the package
 * @property {string} service - The service the event was sent from (Wix-CLI / Editor)
 * @property {string} gridAppId - the grid app id
 * @property {string} packageType - the package type (internal_packages npm_packages my_packages)
 * @property {string} previousVersion - the old version of the package
 * @property {boolean} installed - false when the package is removed
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<installedPackageChangedParams>} LogObject<installedPackageChangedParams>
 */
export function installedPackageChanged(params) {
    return { evid: 500, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'addDsPanelDsNameChanged' event (evid: 51)
 *
 * Event description: Add DS panel ds name changed
 *
 * @param {addDsPanelDsNameChangedParams} params - The parameters for this event
 * @property {string} app_name - app_name
 * @property {string} app_id - app_id
 * @property {string} name - name
 * @property {number} users_with_write_devmode_count - users_with_write_devmode_count
 * @property {string} builderType - builder_type
 * @property {GUID} visitorId - visitor_id
 * @property {string} ds_type - ds_type
 * @property {GUID} site_id - site_id
 * @property {string} panel_id - panel_id
 * @property {string} appName - app_name
 * @property {number} users_on_site_count - users_on_site_count
 * @property {number} ts - ts
 * @property {string} collection_id - collection_id
 * @property {string} panel_name - panel_name
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} pageId - page_id
 * @property {string} token - token
 * @property {number} users_with_devmode_count - users_with_devmode_count
 * @property {string} ver - version
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {string} dev_mode - dev_mode
 * @property {string} appId - app_id
 * @property {GUID} esi - esi
 * @property {string} collection_name - collection_name
 * @property {GUID} msid - msid
 * @property {string} ds_id - ds_id
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<addDsPanelDsNameChangedParams>} LogObject<addDsPanelDsNameChangedParams>
 */
export function addDsPanelDsNameChanged(params) {
    return { evid: 51, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'allPlatformsCodeChanged' event (evid: 5101)
 *
 * Event description: Code was changed by the user with an indication for the platform from which the change was made (online editor / vs code and etc.)
 *
 * @param {allPlatformsCodeChangedParams} params - The parameters for this event
 * @property {string} renderingFlow - Rendering flow
 * @property {string} grid_app_id - grid_app_id
 * @property {string} platform - the platform from which the change was done (vscode_online / wix_editor and etc.)
 *
 *
 * github_hookshot - synced from GitHub on push event, for GitHub enabled sites.
 * http_client_library - calls via various http clients, not browser.
 * wix_cli - it is when the CLI itself updates the files.
 * @property {string} file_name - file_name (page_id)
 * @return {LogObject<allPlatformsCodeChangedParams>} LogObject<allPlatformsCodeChangedParams>
 */
export function allPlatformsCodeChanged(params) {
    return { evid: 5101, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'allPlatformsFileActions' event (evid: 5102)
 *
 * Event description: File actions (create / move) made by the user with an indication for the platform from which the actions was made (online editor / vs code and etc.)
 *
 * @param {allPlatformsFileActionsParams} params - The parameters for this event
 * @property {string} fileName - the full path of the code file
 * @property {string} platform - the platform from which the change was done (vscode_online / wix_editor and etc.)
 * @property {string} action - the action that was done on the file (create / move)
 * @property {string} grid_app_id - grid_app_id
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<allPlatformsFileActionsParams>} LogObject<allPlatformsFileActionsParams>
 */
export function allPlatformsFileActions(params) {
    return { evid: 5102, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'addDsPanelAddButtonClicked' event (evid: 52)
 *
 * Event description: Add DS panel 'create' button clicked
 *
 * @param {addDsPanelAddButtonClickedParams} params - The parameters for this event
 * @property {string} app_name - app_name
 * @property {string} app_id - app_id
 * @property {number} users_with_write_devmode_count - users_with_write_devmode_count
 * @property {string} builderType - builder_type
 * @property {GUID} visitorId - visitor_id
 * @property {string} ds_type - ds_type
 * @property {GUID} site_id - site_id
 * @property {string} panel_id - panel_id
 * @property {string} data_set_name - {}
 * @property {string} appName - app_name
 * @property {number} users_on_site_count - users_on_site_count
 * @property {number} ts - ts
 * @property {string} collection_id - collection_id
 * @property {string} panel_name - panel_name
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} pageId - page_id
 * @property {string} origin - from where the user added the dataset = connect_component_empty_state_dataset/ connect_component_create_new_dataset
 * @property {string} token - token
 * @property {number} users_with_devmode_count - users_with_devmode_count
 * @property {string} ver - version
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {string} mode - READ, WRITE, READ_WRITE
 * @property {string} dev_mode - dev_mode
 * @property {string} appId - app_id
 * @property {GUID} esi - esi
 * @property {string} component_id - {}
 * @property {boolean} is_single_item_collection - (true/false)
 * @property {string} collection_name - collection_name
 * @property {string} component_type - {}
 * @property {GUID} msid - msid
 * @property {string} ds_id - ds_id
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<addDsPanelAddButtonClickedParams>} LogObject<addDsPanelAddButtonClickedParams>
 */
export function addDsPanelAddButtonClicked(params) {
    return { evid: 52, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'addDynamicPageManageItemPagePanel' event (evid: 5301)
 *
 * Event description: Add Dynamic Page - Manage item page panel actions
 *
 * @param {addDynamicPageManageItemPagePanelParams} params - The parameters for this event
 * @property {string} action - show / close / create_page / back / learn_more
 * @property {string} collection_id - colleciton id
 * @property {string} collection_name - collection name
 * @property {string} msid - msid
 * @property {string} origin - pagesPanel etc.
 * @property {string} collection_permissions - collection permissions
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<addDynamicPageManageItemPagePanelParams>} LogObject<addDynamicPageManageItemPagePanelParams>
 */
export function addDynamicPageManageItemPagePanel(params) {
    return { evid: 5301, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'panelOpenedSrc79Evid54' event (evid: 54)
 *
 * Event description: panel opened
 *
 * @param {panelOpenedSrc79Evid54Params} params - The parameters for this event
 * @property {string} app_name - app_name
 * @property {string} app_id - app_id
 * @property {number} users_with_write_devmode_count - users_with_write_devmode_count
 * @property {string} builderType - builder_type
 * @property {GUID} visitorId - visitor_id
 * @property {string} ds_type - ds_type
 * @property {GUID} site_id - site_id
 * @property {string} panel_id - panel_id
 * @property {string} appName - app_name
 * @property {number} users_on_site_count - users_on_site_count
 * @property {number} ts - ts
 * @property {string} panel_name - panel_name(intro panel,choose dynamic page type,etc...)
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} controller_type - controller_type
 * @property {string} controller_id - controller_id
 * @property {string} pageId - page_id
 * @property {string} origin - intro, pick type
 * @property {string} token - token
 * @property {number} users_with_devmode_count - users_with_devmode_count
 * @property {string} ver - version
 * @property {string} token_id - token_id
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {string} dev_mode - dev_mode
 * @property {string} appId - app_id
 * @property {GUID} esi - esi
 * @property {GUID} msid - msid
 * @property {string} ds_id - ds_id
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<panelOpenedSrc79Evid54Params>} LogObject<panelOpenedSrc79Evid54Params>
 */
export function panelOpenedSrc79Evid54(params) {
    return { evid: 54, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'panelClosedSrc79Evid55' event (evid: 55)
 *
 * Event description: panel closed
 *
 * @param {panelClosedSrc79Evid55Params} params - The parameters for this event
 * @property {string} app_name - app_name
 * @property {string} app_id - app_id
 * @property {number} users_with_write_devmode_count - users_with_write_devmode_count
 * @property {string} builderType - builder_type
 * @property {GUID} visitorId - visitor_id
 * @property {string} ds_type - ds_type
 * @property {GUID} site_id - site_id
 * @property {string} panel_id - panel_id
 * @property {string} appName - app_name
 * @property {number} users_on_site_count - users_on_site_count
 * @property {number} ts - ts
 * @property {string} panel_name - panel_name(intro panel,choose dynamic page type,etc...)
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} controller_type - controller_type
 * @property {string} controller_id - controller_id
 * @property {string} pageId - page_id
 * @property {string} token - token
 * @property {number} users_with_devmode_count - users_with_devmode_count
 * @property {string} ver - version
 * @property {string} token_id - token_id
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {string} dev_mode - dev_mode
 * @property {string} appId - app_id
 * @property {GUID} esi - esi
 * @property {GUID} msid - msid
 * @property {string} ds_id - ds_id
 * @property {string} step - step
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<panelClosedSrc79Evid55Params>} LogObject<panelClosedSrc79Evid55Params>
 */
export function panelClosedSrc79Evid55(params) {
    return { evid: 55, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'devtoolsProvisionSrc79Evid555' event (evid: 555)
 *
 * Event description: Triggered when DevTools app is being provisioned.
 * There are 3 stages:
 * 1. Provision started
 * 2. Provision succeeded
 * 3. Provision failed
 *
 * @param {devtoolsProvisionSrc79Evid555Params} params - The parameters for this event
 * @property {string} stage - stage
 * @property {string} errorMessage - errorMessage
 * @property {string} provisionType - provisionType
 * @return {LogObject<devtoolsProvisionSrc79Evid555Params>} LogObject<devtoolsProvisionSrc79Evid555Params>
 */
export function devtoolsProvisionSrc79Evid555(params) {
    return { evid: 555, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'dynamicPageCustomRouterStartCreatingClicked' event (evid: 56)
 *
 * Event description: Dynamic page/Custom router - start creating clicked
 *
 * @param {dynamicPageCustomRouterStartCreatingClickedParams} params - The parameters for this event
 * @property {string} app_name - app_name
 * @property {string} app_id - app_id
 * @property {number} users_with_write_devmode_count - users_with_write_devmode_count
 * @property {string} builderType - builder_type
 * @property {GUID} visitorId - visitor_id
 * @property {string} ds_type - ds_type
 * @property {GUID} site_id - site_id
 * @property {string} panel_id - panel_id
 * @property {string} appName - app_name
 * @property {number} users_on_site_count - users_on_site_count
 * @property {number} ts - ts
 * @property {string} panel_name - panel_name
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} pageId - page_id
 * @property {string} origin - {pages menu add, page menu set, left tree pages, left tree database, 6th button}
 * @property {string} token - token
 * @property {number} users_with_devmode_count - users_with_devmode_count
 * @property {string} ver - version
 * @property {string} token_id - token_id
 * @property {boolean} is_default_collection - is_default_collection(T,F)
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {string} dev_mode - dev_mode
 * @property {string} appId - app_id
 * @property {GUID} esi - esi
 * @property {string} collection_name - collection_name
 * @property {string} page_type - page_type(item, collection)
 * @property {GUID} msid - msid
 * @property {string} ds_id - ds_id
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<dynamicPageCustomRouterStartCreatingClickedParams>} LogObject<dynamicPageCustomRouterStartCreatingClickedParams>
 */
export function dynamicPageCustomRouterStartCreatingClicked(params) {
    return { evid: 56, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'clickOnFindOutMore' event (evid: 57)
 *
 * Event description: click on find out more
 *
 * @param {clickOnFindOutMoreParams} params - The parameters for this event
 * @property {string} app_name - app_name
 * @property {string} app_id - app_id
 * @property {number} users_with_write_devmode_count - users_with_write_devmode_count
 * @property {string} builderType - builder_type
 * @property {GUID} visitorId - visitor_id
 * @property {string} ds_type - ds_type
 * @property {GUID} site_id - site_id
 * @property {string} panel_id - panel_id
 * @property {string} appName - app_name
 * @property {number} users_on_site_count - users_on_site_count
 * @property {number} ts - ts
 * @property {string} panel_name - panel_name
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} pageId - page_id
 * @property {string} token - token
 * @property {number} users_with_devmode_count - users_with_devmode_count
 * @property {string} ver - version
 * @property {string} token_id - token_id
 * @property {boolean} is_default_collection - is_default_collection(T,F)
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {string} dev_mode - dev_mode
 * @property {string} appId - app_id
 * @property {GUID} esi - esi
 * @property {string} collection_name - collection_name
 * @property {string} page_type - page_type(item, collection)
 * @property {GUID} msid - msid
 * @property {string} ds_id - ds_id
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<clickOnFindOutMoreParams>} LogObject<clickOnFindOutMoreParams>
 */
export function clickOnFindOutMore(params) {
    return { evid: 57, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'dynamicPageCustomRouterNextClicked' event (evid: 58)
 *
 * Event description: Dynamic page/Custom router - next clicked
 *
 * @param {dynamicPageCustomRouterNextClickedParams} params - The parameters for this event
 * @property {string} app_name - app_name
 * @property {string} app_id - app_id
 * @property {number} users_with_write_devmode_count - users_with_write_devmode_count
 * @property {string} builderType - builder_type
 * @property {GUID} visitorId - visitor_id
 * @property {string} ds_type - ds_type
 * @property {GUID} site_id - site_id
 * @property {string} panel_id - panel_id
 * @property {string} appName - app_name
 * @property {number} users_on_site_count - users_on_site_count
 * @property {number} ts - ts
 * @property {string} collection_id - collection_id
 * @property {string} panel_name - panel_name
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} pageId - page_id
 * @property {string} origin - {pages menu add, page menu set, left tree pages, left tree database, 6th button}
 * @property {string} token - token
 * @property {number} users_with_devmode_count - users_with_devmode_count
 * @property {string} ver - version
 * @property {string} token_id - token_id
 * @property {boolean} is_default_collection - is_default_collection(T,F)
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {string} dev_mode - dev_mode
 * @property {string} appId - app_id
 * @property {GUID} esi - esi
 * @property {string} collection_name - collection_name
 * @property {string} page_type - page_type(item, collection)
 * @property {GUID} msid - msid
 * @property {string} ds_id - ds_id
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<dynamicPageCustomRouterNextClickedParams>} LogObject<dynamicPageCustomRouterNextClickedParams>
 */
export function dynamicPageCustomRouterNextClicked(params) {
    return { evid: 58, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'helpClicked' event (evid: 59)
 *
 * Event description: help clicked
 *
 * @param {helpClickedParams} params - The parameters for this event
 * @property {string} app_name - app_name
 * @property {string} app_id - app_id
 * @property {number} users_with_write_devmode_count - users_with_write_devmode_count
 * @property {string} builderType - builder_type
 * @property {GUID} visitorId - visitor_id
 * @property {string} ds_type - ds_type
 * @property {GUID} site_id - site_id
 * @property {string} panel_id - panel_id
 * @property {string} appName - app_name
 * @property {number} users_on_site_count - users_on_site_count
 * @property {number} ts - ts
 * @property {string} panel_name - panel_name(intro panel,choose dynamic page type,etc...)
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} pageId - page_id
 * @property {string} token - token
 * @property {number} users_with_devmode_count - users_with_devmode_count
 * @property {string} ver - version
 * @property {string} token_id - token_id
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {string} dev_mode - dev_mode
 * @property {string} appId - app_id
 * @property {GUID} esi - esi
 * @property {GUID} msid - msid
 * @property {string} ds_id - ds_id
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<helpClickedParams>} LogObject<helpClickedParams>
 */
export function helpClicked(params) {
    return { evid: 59, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'testSiteDeploymentModalSrc79Evid591' event (evid: 591)
 *
 * Event description: Fires when the user interacts with the test site deployment modal
 *
 * @param {testSiteDeploymentModalSrc79Evid591Params} params - The parameters for this event
 * @property {GUID} uuid - uuid
 * @property {GUID} msid - msid
 * @property {string} action - deploy, x , ?, show
 * @property {boolean} dont_show_again_indication - true/ false
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<testSiteDeploymentModalSrc79Evid591Params>} LogObject<testSiteDeploymentModalSrc79Evid591Params>
 */
export function testSiteDeploymentModalSrc79Evid591(params) {
    return { evid: 591, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'startCodingExampleInteractionSrc79Evid593' event (evid: 593)
 *
 * Event description: when a user interacts with the start coding example page (DMO - start with an example )
 *
 * @param {startCodingExampleInteractionSrc79Evid593Params} params - The parameters for this event
 * @property {string} builderType - builder_type
 * @property {string} action - click/ copy
 * @property {string} item_name - item_name
 * @property {string} item_type - link, code, button
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<startCodingExampleInteractionSrc79Evid593Params>} LogObject<startCodingExampleInteractionSrc79Evid593Params>
 */
export function startCodingExampleInteractionSrc79Evid593(params) {
    return { evid: 593, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'addFieldClicked' event (evid: 60)
 *
 * Event description: add field clicked
 *
 * @param {addFieldClickedParams} params - The parameters for this event
 * @property {string} app_name - app_name
 * @property {string} app_id - app_id
 * @property {number} users_with_write_devmode_count - users_with_write_devmode_count
 * @property {string} builderType - builder_type
 * @property {GUID} visitorId - visitor_id
 * @property {string} ds_type - ds_type
 * @property {GUID} site_id - site_id
 * @property {string} panel_id - panel_id
 * @property {string} appName - app_name
 * @property {number} users_on_site_count - users_on_site_count
 * @property {number} ts - ts
 * @property {string} panel_name - panel_name(intro panel,choose dynamic page type,etc...)
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} pageId - page_id
 * @property {string} token - token
 * @property {number} users_with_devmode_count - users_with_devmode_count
 * @property {string} ver - version
 * @property {string} token_id - token_id
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {string} dev_mode - dev_mode
 * @property {string} appId - app_id
 * @property {GUID} esi - esi
 * @property {GUID} msid - msid
 * @property {string} ds_id - ds_id
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<addFieldClickedParams>} LogObject<addFieldClickedParams>
 */
export function addFieldClicked(params) {
    return { evid: 60, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'addFieldToUrl' event (evid: 61)
 *
 * Event description: add field to url
 *
 * @param {addFieldToUrlParams} params - The parameters for this event
 * @property {string} app_name - app_name
 * @property {string} app_id - app_id
 * @property {number} users_with_write_devmode_count - users_with_write_devmode_count
 * @property {string} insert_method - insert_method(add field,manually)
 * @property {string} builderType - builder_type
 * @property {GUID} visitorId - visitor_id
 * @property {string} ds_type - ds_type
 * @property {GUID} site_id - site_id
 * @property {string} panel_id - panel_id
 * @property {string} appName - app_name
 * @property {number} users_on_site_count - users_on_site_count
 * @property {number} ts - ts
 * @property {string} panel_name - panel_name
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} pageId - page_id
 * @property {string} token - token
 * @property {number} users_with_devmode_count - users_with_devmode_count
 * @property {string} ver - version
 * @property {string} token_id - token_id
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {string} dev_mode - dev_mode
 * @property {string} appId - app_id
 * @property {GUID} esi - esi
 * @property {string} page_type - list_page, item_page
 * @property {GUID} msid - msid
 * @property {string} field_name - field_name
 * @property {string} ds_id - ds_id
 * @property {string} originalUrlSlug - Original URL slug before field was added\ changed
 * @property {string} newUrlSlug - new URL slug after change
 * @property {string} collectionId - collection_id
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<addFieldToUrlParams>} LogObject<addFieldToUrlParams>
 */
export function addFieldToUrl(params) {
    return { evid: 61, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'dynamicPageCustomRouterCreatePageClicked' event (evid: 62)
 *
 * Event description: Dynamic page/Custom router - create page clicked
 *
 * @param {dynamicPageCustomRouterCreatePageClickedParams} params - The parameters for this event
 * @property {string} app_name - app_name
 * @property {string} app_id - app_id
 * @property {number} users_with_write_devmode_count - users_with_write_devmode_count
 * @property {string} default_url - default_url
 * @property {string} builderType - builder_type
 * @property {GUID} visitorId - visitor_id
 * @property {boolean} is_first_data_modal - {true,false}
 * @property {string} ds_type - ds_type
 * @property {GUID} site_id - site_id
 * @property {string} panel_id - panel_id
 * @property {boolean} is_url_changed - is_url_changed(T,F)
 * @property {string} appName - app_name
 * @property {number} users_on_site_count - users_on_site_count
 * @property {string} new_url - new_url
 * @property {number} ts - ts
 * @property {string} collection_id - collection_id
 * @property {string} collection_origin - one of: 'manual', 'external', 'preset', 'template'
 * @property {string} panel_name - panel_name
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {boolean} is_auto_bound - {true if action includes data binding, else false}
 * @property {string} pageId - page_id
 * @property {string} origin - pages_panel, leftree, etc
 * @property {string} token - token
 * @property {number} users_with_devmode_count - users_with_devmode_count
 * @property {string} ver - version
 * @property {string} token_id - token_id
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {boolean} new_collection - True if collection was created through the process of adding DP
 * @property {string} dev_mode - dev_mode
 * @property {string} appId - app_id
 * @property {GUID} esi - esi
 * @property {string} collection_name - collection_name
 * @property {string} page_type - page_type(item, collection)
 * @property {GUID} msid - msid
 * @property {string} action_type - duplicate_dynamic_page
 * @property {string} ds_id - ds_id
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<dynamicPageCustomRouterCreatePageClickedParams>} LogObject<dynamicPageCustomRouterCreatePageClickedParams>
 */
export function dynamicPageCustomRouterCreatePageClicked(params) {
    return { evid: 62, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'changeUrl' event (evid: 64)
 *
 * Event description: add prefix to url
 *
 * @param {changeUrlParams} params - The parameters for this event
 * @property {string} app_name - app_name
 * @property {string} app_id - app_id
 * @property {number} users_with_write_devmode_count - users_with_write_devmode_count
 * @property {string} builderType - builder_type
 * @property {GUID} visitorId - visitor_id
 * @property {string} ds_type - ds_type
 * @property {GUID} site_id - site_id
 * @property {string} panel_id - panel_id
 * @property {string} appName - app_name
 * @property {number} users_on_site_count - users_on_site_count
 * @property {string} new_url - new_url
 * @property {number} ts - ts
 * @property {string} panel_name - panel_name
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} old_url - old_url
 * @property {string} pageId - page_id
 * @property {string} token - token
 * @property {number} users_with_devmode_count - users_with_devmode_count
 * @property {string} ver - version
 * @property {string} token_id - token_id
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {string} dev_mode - dev_mode
 * @property {string} appId - app_id
 * @property {GUID} esi - esi
 * @property {GUID} msid - msid
 * @property {string} field_name - Deprecated
 * @property {string} ds_id - ds_id
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<changeUrlParams>} LogObject<changeUrlParams>
 */
export function changeUrl(params) {
    return { evid: 64, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'addOrEditCode' event (evid: 65)
 *
 * Event description: add or edit code
 *
 * @param {addOrEditCodeParams} params - The parameters for this event
 * @property {string} app_name - app_name
 * @property {string} app_id - app_id
 * @property {number} users_with_write_devmode_count - users_with_write_devmode_count
 * @property {string} builderType - builder_type
 * @property {GUID} visitorId - visitor_id
 * @property {string} ds_type - ds_type
 * @property {GUID} site_id - site_id
 * @property {string} panel_id - panel_id
 * @property {boolean} restore_ind - restore_ind (on,off)
 * @property {string} appName - app_name
 * @property {number} users_on_site_count - users_on_site_count
 * @property {number} ts - ts
 * @property {string} panel_name - panel_name
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} pageId - page_id
 * @property {string} token - token
 * @property {number} users_with_devmode_count - users_with_devmode_count
 * @property {string} ver - version
 * @property {string} token_id - token_id
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {string} dev_mode - dev_mode
 * @property {string} appId - app_id
 * @property {GUID} esi - esi
 * @property {GUID} msid - msid
 * @property {string} field_name - Deprecated
 * @property {string} ds_id - ds_id
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<addOrEditCodeParams>} LogObject<addOrEditCodeParams>
 */
export function addOrEditCode(params) {
    return { evid: 65, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'routerActionClicked' event (evid: 66)
 *
 * Event description: user clicks on an action (CTA,Cancel,X) in a platform dialog  (e.g delete router panel)
 *
 * @param {routerActionClickedParams} params - The parameters for this event
 * @property {string} app_name - app_name
 * @property {string} app_id - app_id
 * @property {number} users_with_write_devmode_count - users_with_write_devmode_count
 * @property {string} builderType - builder_type
 * @property {GUID} visitorId - Deprecated
 * @property {string} ds_type - ds_type
 * @property {GUID} site_id - site_id
 * @property {string} panel_id - panel_id
 * @property {string} appName - app_name
 * @property {number} users_on_site_count - users_on_site_count
 * @property {number} ts - ts
 * @property {string} panel_name - panel_name
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} pageId - page_id
 * @property {string} origin - cancel button,x etc.
 * @property {string} token - token
 * @property {number} users_with_devmode_count - users_with_devmode_count
 * @property {string} ver - version
 * @property {string} token_id - token_id
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {string} dev_mode - dev_mode
 * @property {string} appId - app_id
 * @property {GUID} esi - esi
 * @property {GUID} msid - msid
 * @property {string} action - CTA,cancel
 * @property {string} ds_id - ds_id
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<routerActionClickedParams>} LogObject<routerActionClickedParams>
 */
export function routerActionClicked(params) {
    return { evid: 66, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'clickOnFunction' event (evid: 67)
 *
 * Event description: Click on function
 *
 * @param {clickOnFunctionParams} params - The parameters for this event
 * @property {string} app_name - app_name
 * @property {string} app_id - app_id
 * @property {string} function_name - function_name
 * @property {number} users_with_write_devmode_count - users_with_write_devmode_count
 * @property {string} builderType - builder_type
 * @property {GUID} visitorId - visitor_id
 * @property {string} ds_type - ds_type
 * @property {GUID} site_id - site_id
 * @property {string} panel_id - panel_id
 * @property {string} appName - app_name
 * @property {number} users_on_site_count - users_on_site_count
 * @property {number} ts - ts
 * @property {string} panel_name - panel name
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} pageId - page_id
 * @property {string} token - token
 * @property {number} users_with_devmode_count - users_with_devmode_count
 * @property {string} ver - version
 * @property {string} token_id - token_id
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {string} dev_mode - dev_mode
 * @property {string} appId - app_id
 * @property {GUID} esi - esi
 * @property {GUID} msid - msid
 * @property {string} ds_id - ds_id
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<clickOnFunctionParams>} LogObject<clickOnFunctionParams>
 */
export function clickOnFunction(params) {
    return { evid: 67, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'clickOnAddPage' event (evid: 68)
 *
 * Event description: click on add page
 *
 * @param {clickOnAddPageParams} params - The parameters for this event
 * @property {string} app_name - app_name
 * @property {string} app_id - app_id
 * @property {number} users_with_write_devmode_count - users_with_write_devmode_count
 * @property {string} builderType - builder_type
 * @property {GUID} visitorId - visitor_id
 * @property {string} ds_type - ds_type
 * @property {GUID} site_id - site_id
 * @property {string} panel_id - panel_id
 * @property {string} appName - app_name
 * @property {number} users_on_site_count - users_on_site_count
 * @property {number} ts - ts
 * @property {string} panel_name - panel_name
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {GUID} page_id - page_id
 * @property {string} page_name - page_name
 * @property {string} pageId - page_id
 * @property {string} token - token
 * @property {number} users_with_devmode_count - users_with_devmode_count
 * @property {string} ver - version
 * @property {string} token_id - token_id
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {string} dev_mode - dev_mode
 * @property {string} appId - app_id
 * @property {GUID} esi - esi
 * @property {string} page_type - page_type(new,existing)
 * @property {GUID} msid - msid
 * @property {string} ds_id - ds_id
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<clickOnAddPageParams>} LogObject<clickOnAddPageParams>
 */
export function clickOnAddPage(params) {
    return { evid: 68, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'clickOnChangeCustomRouter' event (evid: 69)
 *
 * Event description: Click on change custom router
 *
 * @param {clickOnChangeCustomRouterParams} params - The parameters for this event
 * @property {string} app_name - app_name
 * @property {string} app_id - app_id
 * @property {number} users_with_write_devmode_count - users_with_write_devmode_count
 * @property {string} builderType - builder_type
 * @property {GUID} visitorId - visitor_id
 * @property {string} ds_type - ds_type
 * @property {GUID} site_id - site_id
 * @property {string} panel_id - panel_id
 * @property {string} appName - app_name
 * @property {number} users_on_site_count - users_on_site_count
 * @property {string} prefix - prefix
 * @property {number} ts - ts
 * @property {string} panel_name - panel_name
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {GUID} page_id - page_id
 * @property {string} page_name - page_name
 * @property {string} pageId - page_id
 * @property {string} token - token
 * @property {number} users_with_devmode_count - users_with_devmode_count
 * @property {string} ver - version
 * @property {string} token_id - token_id
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {string} dev_mode - dev_mode
 * @property {string} appId - app_id
 * @property {GUID} esi - esi
 * @property {string} page_type - page_type(new,existing)
 * @property {GUID} msid - msid
 * @property {string} ds_id - ds_id
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<clickOnChangeCustomRouterParams>} LogObject<clickOnChangeCustomRouterParams>
 */
export function clickOnChangeCustomRouter(params) {
    return { evid: 69, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'connectOpenTriggeredFromWorker' event (evid: 71)
 *
 * Event description: Connect open triggered (from worker)
 *
 * @param {connectOpenTriggeredFromWorkerParams} params - The parameters for this event
 * @property {string} app_name - app_name
 * @property {string} app_id - app_id
 * @property {string} source - source of panel opening
 * @property {number} users_with_write_devmode_count - users_with_write_devmode_count
 * @property {string} worker_id - worker_id
 * @property {string} builderType - builder_type
 * @property {GUID} visitorId - visitor_id
 * @property {string} ds_type - ds_type
 * @property {GUID} site_id - site_id
 * @property {string} panel_id - panel_id
 * @property {string} appName - app_name
 * @property {number} users_on_site_count - users_on_site_count
 * @property {number} ts - ts
 * @property {string} panel_name - panel_name
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} pageId - page_id
 * @property {string} token - token
 * @property {number} users_with_devmode_count - users_with_devmode_count
 * @property {string} ver - version
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {string} dev_mode - dev_mode
 * @property {string} appId - app_id
 * @property {GUID} esi - esi
 * @property {string} component_id - component_id
 * @property {string} component_type - comp type
 * @property {GUID} msid - msid
 * @property {string} ds_id - ds_id
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<connectOpenTriggeredFromWorkerParams>} LogObject<connectOpenTriggeredFromWorkerParams>
 */
export function connectOpenTriggeredFromWorker(params) {
    return { evid: 71, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'connectPanelLoaded' event (evid: 72)
 *
 * Event description: Connect panel loaded
 *
 * @param {connectPanelLoadedParams} params - The parameters for this event
 * @property {string} app_name - app_name
 * @property {string} app_id - app_id
 * @property {number} duration - duration in ms
 * @property {string} source - source of panel opening
 * @property {number} users_with_write_devmode_count - users_with_write_devmode_count
 * @property {string} worker_id - worker id
 * @property {string} builderType - builder_type
 * @property {GUID} visitorId - visitor_id
 * @property {string} ds_type - ds_type
 * @property {GUID} site_id - site_id
 * @property {string} panel_id - panel_id
 * @property {string} appName - app_name
 * @property {number} users_on_site_count - users_on_site_count
 * @property {number} ts - ts
 * @property {string} panel_name - panel_name
 * @property {string} viewmode - viewmode
 * @property {boolean} is_suggest_offer_options_enabled - {}
 * @property {string} input_name - input_name
 * @property {string} pageId - page_id
 * @property {string} origin - {gfpp, manage content panel,binding_suggestion, public_api}
 * @property {string} token - token
 * @property {number} users_with_devmode_count - users_with_devmode_count
 * @property {string} ver - version
 * @property {string} userEnv - user_env
 * @property {string} repeater_parent_id - repeater_parent_id
 * @property {string} editor_working_mode - editor_working_mode
 * @property {string} pageType - {DP_All_items/ DP_items_title/other}
 * @property {string} dev_mode - dev_mode
 * @property {string} appId - app_id
 * @property {GUID} esi - esi
 * @property {string} component_id - component_id
 * @property {string} component_type - comp type
 * @property {GUID} msid - msid
 * @property {string} ds_id - ds_id
 * @property {string} initiator - {manually, automatically}
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<connectPanelLoadedParams>} LogObject<connectPanelLoadedParams>
 */
export function connectPanelLoaded(params) {
    return { evid: 72, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'connectDatasetChanged' event (evid: 73)
 *
 * Event description: User connects element to dataset
 *
 * @param {connectDatasetChangedParams} params - The parameters for this event
 * @property {string} app_name - app_name
 * @property {string} app_id - app_id
 * @property {number} duration - duration in ms
 * @property {string} source - source of panel opening
 * @property {number} users_with_write_devmode_count - users_with_write_devmode_count
 * @property {string} builderType - builder_type
 * @property {GUID} visitorId - visitor_id
 * @property {string} ds_type - ds_type
 * @property {GUID} site_id - site_id
 * @property {string} panel_id - panel_id
 * @property {string} appName - app_name
 * @property {number} users_on_site_count - users_on_site_count
 * @property {number} ts - ts
 * @property {string} collection_id - collection_id
 * @property {string} panel_name - panel_name
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} pageId - page_id
 * @property {string} token - token
 * @property {number} users_with_devmode_count - users_with_devmode_count
 * @property {string} ver - version
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {string} old_ds_id - old_ds_id
 * @property {string} dev_mode - dev_mode
 * @property {string} appId - app_id
 * @property {GUID} esi - esi
 * @property {string} component_id - component_id
 * @property {string} new_ds_id - new_ds_id
 * @property {boolean} is_single_item_collection - (true/false)
 * @property {string} collection_name - collection_name
 * @property {string} component_type - comp type
 * @property {GUID} msid - msid
 * @property {string} ds_id - ds_id
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<connectDatasetChangedParams>} LogObject<connectDatasetChangedParams>
 */
export function connectDatasetChanged(params) {
    return { evid: 73, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'connectPropertyConnected' event (evid: 74)
 *
 * Event description: User connects element to field of collection
 *
 * @param {connectPropertyConnectedParams} params - The parameters for this event
 * @property {string} app_name - app_name
 * @property {string} app_id - app_id
 * @property {string} renderingFlow - Rendering flow
 * @property {string} source - source of panel opening
 * @property {number} users_with_write_devmode_count - users_with_write_devmode_count
 * @property {string} builderType - builder_type
 * @property {GUID} visitorId - visitor_id
 * @property {string} ds_type - ds_type
 * @property {GUID} site_id - site_id
 * @property {string} panel_id - panel_id
 * @property {string} data_set_name - data_set_name
 * @property {string} appName - app_name
 * @property {number} users_on_site_count - users_on_site_count
 * @property {string} field_type - field_type
 * @property {number} ts - ts
 * @property {string} collection_id - collection id
 * @property {string} ref_field_name - reference field name
 * @property {string} collection_origin - one of: 'manual', 'external', 'preset', 'template'
 * @property {string} panel_name - panel_name
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} pageId - page_id
 * @property {string} origin - {confirm_suggestion, drop_down}
 * @property {string} token - token
 * @property {number} users_with_devmode_count - users_with_devmode_count
 * @property {string} ver - version
 * @property {string} userEnv - user_env
 * @property {string} repeater_parent_id - if attached to repeater - this will be the repeater id
 * @property {string} editor_working_mode - editor_working_mode
 * @property {string} property - property
 * @property {string} dev_mode - dev_mode
 * @property {string} appId - app_id
 * @property {GUID} esi - esi
 * @property {string} component_id - component_id
 * @property {boolean} is_single_item_collection - (true/false)
 * @property {string} collection_name - collection name
 * @property {string} component_type - comp type
 * @property {GUID} msid - msid
 * @property {string} field_name - field_name
 * @property {string} ds_id - ds_id
 * @property {string} ref_field_type - reference  field type
 * @property {string} property_open_method - open link in new\ existing window
 * @property {string} rel_values - selected rel attributes
 * @return {LogObject<connectPropertyConnectedParams>} LogObject<connectPropertyConnectedParams>
 */
export function connectPropertyConnected(params) {
    return { evid: 74, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'connectPropertyDisconnected' event (evid: 75)
 *
 * Event description: Connect property disconnected
 *
 * @param {connectPropertyDisconnectedParams} params - The parameters for this event
 * @property {string} app_name - app_name
 * @property {string} app_id - app_id
 * @property {string} renderingFlow - Rendering flow
 * @property {number} users_with_write_devmode_count - users_with_write_devmode_count
 * @property {string} builderType - builder_type
 * @property {GUID} visitorId - visitor_id
 * @property {string} ds_type - ds_type
 * @property {GUID} site_id - site_id
 * @property {string} panel_id - panel_id
 * @property {string} appName - app_name
 * @property {number} users_on_site_count - users_on_site_count
 * @property {number} ts - ts
 * @property {string} panel_name - panel_name
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} pageId - page_id
 * @property {string} token - token
 * @property {number} users_with_devmode_count - users_with_devmode_count
 * @property {string} ver - version
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {string} property - property
 * @property {string} dev_mode - dev_mode
 * @property {string} appId - app_id
 * @property {GUID} esi - esi
 * @property {string} component_id - component_id
 * @property {string} component_type - comp type
 * @property {GUID} msid - msid
 * @property {string} ds_id - ds_id
 * @property {string} field_type - field_type
 * @return {LogObject<connectPropertyDisconnectedParams>} LogObject<connectPropertyDisconnectedParams>
 */
export function connectPropertyDisconnected(params) {
    return { evid: 75, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'connectDropdownOptionsToggleChanged' event (evid: 76)
 *
 * Event description: Connect dropdown options toggle changed
 *
 * @param {connectDropdownOptionsToggleChangedParams} params - The parameters for this event
 * @property {string} app_name - app_name
 * @property {string} app_id - app_id
 * @property {string} source - source of panel opening
 * @property {number} users_with_write_devmode_count - users_with_write_devmode_count
 * @property {string} builderType - builder_type
 * @property {GUID} visitorId - visitor_id
 * @property {string} state - on,off
 * @property {string} ds_type - ds_type
 * @property {GUID} site_id - site_id
 * @property {string} panel_id - panel_id
 * @property {string} appName - app_name
 * @property {number} users_on_site_count - users_on_site_count
 * @property {number} ts - ts
 * @property {string} panel_name - panel_name
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} pageId - page_id
 * @property {string} token - token
 * @property {number} users_with_devmode_count - users_with_devmode_count
 * @property {string} ver - version
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {string} dev_mode - dev_mode
 * @property {string} appId - app_id
 * @property {GUID} esi - esi
 * @property {string} component_id - component_id
 * @property {string} component_type - comp type
 * @property {GUID} msid - msid
 * @property {string} ds_id - ds_id
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<connectDropdownOptionsToggleChangedParams>} LogObject<connectDropdownOptionsToggleChangedParams>
 */
export function connectDropdownOptionsToggleChanged(params) {
    return { evid: 76, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'connectDropdownOptionsDsChanged' event (evid: 77)
 *
 * Event description: Connect dropdown options DS changed
 *
 * @param {connectDropdownOptionsDsChangedParams} params - The parameters for this event
 * @property {string} app_name - app_name
 * @property {string} app_id - app_id
 * @property {number} duration - duration in ms
 * @property {string} source - source of panel opening
 * @property {number} users_with_write_devmode_count - users_with_write_devmode_count
 * @property {string} builderType - builder_type
 * @property {GUID} visitorId - visitor_id
 * @property {string} ds_type - ds_type
 * @property {GUID} site_id - site_id
 * @property {string} panel_id - panel_id
 * @property {string} appName - app_name
 * @property {number} users_on_site_count - users_on_site_count
 * @property {number} ts - ts
 * @property {string} panel_name - panel_name
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} pageId - page_id
 * @property {string} token - token
 * @property {number} users_with_devmode_count - users_with_devmode_count
 * @property {string} ver - version
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {string} old_ds_id - old_ds_id
 * @property {string} dev_mode - dev_mode
 * @property {string} appId - app_id
 * @property {GUID} esi - esi
 * @property {string} component_id - component_id
 * @property {string} new_ds_id - new_ds_id
 * @property {boolean} is_single_item_collection - (true/false)
 * @property {string} component_type - comp type
 * @property {GUID} msid - msid
 * @property {string} ds_id - ds_id
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<connectDropdownOptionsDsChangedParams>} LogObject<connectDropdownOptionsDsChangedParams>
 */
export function connectDropdownOptionsDsChanged(params) {
    return { evid: 77, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'connectDropdownOptionsValuesConnected' event (evid: 78)
 *
 * Event description: Connect dropdown options values connected
 *
 * @param {connectDropdownOptionsValuesConnectedParams} params - The parameters for this event
 * @property {string} app_name - app_name
 * @property {string} app_id - app_id
 * @property {string} source - source of panel opening
 * @property {number} users_with_write_devmode_count - users_with_write_devmode_count
 * @property {string} builderType - builder_type
 * @property {GUID} visitorId - visitor_id
 * @property {string} ds_type - ds_type
 * @property {GUID} site_id - site_id
 * @property {string} panel_id - panel_id
 * @property {string} data_set_name - data_set_name
 * @property {string} appName - app_name
 * @property {number} users_on_site_count - users_on_site_count
 * @property {string} field_type - field_type
 * @property {number} ts - ts
 * @property {string} panel_name - panel_name
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} pageId - page_id
 * @property {string} origin - {confirm_suggestion, drop_down}
 * @property {string} token - token
 * @property {number} users_with_devmode_count - users_with_devmode_count
 * @property {string} ver - version
 * @property {string} userEnv - user_env
 * @property {string} repeater_parent_id - if attached to repeater - this will be the repeater id
 * @property {string} editor_working_mode - editor_working_mode
 * @property {string} property - property
 * @property {string} dev_mode - dev_mode
 * @property {string} appId - app_id
 * @property {GUID} esi - esi
 * @property {string} component_id - component_id
 * @property {boolean} is_single_item_collection - (true/false)
 * @property {string} component_type - comp type
 * @property {GUID} msid - msid
 * @property {string} field_name - field_name
 * @property {string} ds_id - ds_id
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<connectDropdownOptionsValuesConnectedParams>} LogObject<connectDropdownOptionsValuesConnectedParams>
 */
export function connectDropdownOptionsValuesConnected(params) {
    return { evid: 78, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'connectTableLinkButtonClicked' event (evid: 79)
 *
 * Event description: Connect table link button clicked
 *
 * @param {connectTableLinkButtonClickedParams} params - The parameters for this event
 * @property {string} app_name - app_name
 * @property {string} app_id - app_id
 * @property {string} source - source of panel opening
 * @property {number} users_with_write_devmode_count - users_with_write_devmode_count
 * @property {string} builderType - builder_type
 * @property {GUID} visitorId - visitor_id
 * @property {string} state - on,off
 * @property {string} ds_type - ds_type
 * @property {GUID} site_id - site_id
 * @property {string} panel_id - panel_id
 * @property {string} appName - app_name
 * @property {number} users_on_site_count - users_on_site_count
 * @property {number} ts - ts
 * @property {string} panel_name - panel_name
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} pageId - page_id
 * @property {string} token - token
 * @property {number} users_with_devmode_count - users_with_devmode_count
 * @property {string} ver - version
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {string} property - property
 * @property {string} dev_mode - dev_mode
 * @property {string} appId - app_id
 * @property {GUID} esi - esi
 * @property {string} component_id - component_id
 * @property {string} component_type - component_type
 * @property {GUID} msid - msid
 * @property {string} ds_id - ds_id
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<connectTableLinkButtonClickedParams>} LogObject<connectTableLinkButtonClickedParams>
 */
export function connectTableLinkButtonClicked(params) {
    return { evid: 79, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'deprecatedControllerStateChanged' event (evid: 8)
 *
 * Event description: Controller state changed
 *
 * @param {deprecatedControllerStateChangedParams} params - The parameters for this event
 * @property {string} app_name - app_name
 * @property {string} app_id - app_id
 * @property {string} curr_state - curr_state
 * @property {number} users_with_write_devmode_count - users_with_write_devmode_count
 * @property {string} builderType - builder_type
 * @property {GUID} visitorId - visitor_id
 * @property {string} prev_state - prev_state
 * @property {string} ds_type - ds_type
 * @property {GUID} site_id - site_id
 * @property {string} panel_id - panel_id
 * @property {string} appName - app_name
 * @property {number} users_on_site_count - users_on_site_count
 * @property {number} ts - ts
 * @property {string} controller_name - controller_name
 * @property {string} panel_name - panel_name
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} controller_type - controller_type
 * @property {string} pageId - page_id
 * @property {string} token - token
 * @property {number} users_with_devmode_count - users_with_devmode_count
 * @property {string} ver - version
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {string} dev_mode - dev_mode
 * @property {string} appId - app_id
 * @property {GUID} esi - esi
 * @property {GUID} msid - msid
 * @property {string} ds_id - ds_id
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<deprecatedControllerStateChangedParams>} LogObject<deprecatedControllerStateChangedParams>
 */
export function deprecatedControllerStateChanged(params) {
    return { evid: 8, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'connectTableLinkSet' event (evid: 80)
 *
 * Event description: Connect table link set
 *
 * @param {connectTableLinkSetParams} params - The parameters for this event
 * @property {string} app_name - app_name
 * @property {string} app_id - app_id
 * @property {string} source - source of panel opening
 * @property {number} users_with_write_devmode_count - users_with_write_devmode_count
 * @property {string} builderType - builder_type
 * @property {GUID} visitorId - visitor_id
 * @property {string} ds_type - ds_type
 * @property {GUID} site_id - site_id
 * @property {string} panel_id - panel_id
 * @property {string} appName - app_name
 * @property {number} users_on_site_count - users_on_site_count
 * @property {string} field_type - field_type
 * @property {number} ts - ts
 * @property {string} ref_field_name - ref_field_name
 * @property {string} panel_name - panel_name
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} pageId - page_id
 * @property {string} token - token
 * @property {number} users_with_devmode_count - users_with_devmode_count
 * @property {string} ver - version
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {string} property - property
 * @property {string} dev_mode - dev_mode
 * @property {string} appId - app_id
 * @property {GUID} esi - esi
 * @property {string} component_id - component_id
 * @property {string} component_type - comp type
 * @property {GUID} msid - msid
 * @property {string} field_name - field_name
 * @property {string} ds_id - ds_id
 * @property {string} ref_field_type - ref_field_type
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<connectTableLinkSetParams>} LogObject<connectTableLinkSetParams>
 */
export function connectTableLinkSet(params) {
    return { evid: 80, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'connectTableAddColumnClicked' event (evid: 81)
 *
 * Event description: Connect table add column clicked
 *
 * @param {connectTableAddColumnClickedParams} params - The parameters for this event
 * @property {string} app_name - app_name
 * @property {string} app_id - app_id
 * @property {number} users_with_write_devmode_count - users_with_write_devmode_count
 * @property {string} builderType - builder_type
 * @property {GUID} visitorId - visitor_id
 * @property {string} ds_type - ds_type
 * @property {GUID} site_id - site_id
 * @property {string} panel_id - panel_id
 * @property {string} appName - app_name
 * @property {number} users_on_site_count - users_on_site_count
 * @property {number} ts - ts
 * @property {string} panel_name - panel_name
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} pageId - page_id
 * @property {string} token - token
 * @property {number} users_with_devmode_count - users_with_devmode_count
 * @property {string} ver - version
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {string} dev_mode - dev_mode
 * @property {string} appId - app_id
 * @property {GUID} esi - esi
 * @property {string} component_id - component_id
 * @property {string} component_type - comp type
 * @property {GUID} msid - msid
 * @property {string} ds_id - ds_id
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<connectTableAddColumnClickedParams>} LogObject<connectTableAddColumnClickedParams>
 */
export function connectTableAddColumnClicked(params) {
    return { evid: 81, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'connectTableAddColumnPanelFieldSelected' event (evid: 82)
 *
 * Event description: Connect table add column panel field selected
 *
 * @param {connectTableAddColumnPanelFieldSelectedParams} params - The parameters for this event
 * @property {string} app_name - app_name
 * @property {string} app_id - app_id
 * @property {number} users_with_write_devmode_count - users_with_write_devmode_count
 * @property {string} builderType - builder_type
 * @property {GUID} visitorId - visitor_id
 * @property {string} ds_type - ds_type
 * @property {GUID} site_id - site_id
 * @property {string} panel_id - panel_id
 * @property {string} appName - app_name
 * @property {number} users_on_site_count - users_on_site_count
 * @property {string} field_type - field_type
 * @property {number} ts - ts
 * @property {string} ref_field_name - ref_field_name
 * @property {string} panel_name - panel_name
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} pageId - page_id
 * @property {string} token - token
 * @property {number} users_with_devmode_count - users_with_devmode_count
 * @property {string} ver - version
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {string} property - property
 * @property {string} dev_mode - dev_mode
 * @property {string} appId - app_id
 * @property {GUID} esi - esi
 * @property {string} component_id - component_id
 * @property {string} component_type - comp type
 * @property {GUID} msid - msid
 * @property {string} field_name - field_name
 * @property {string} ds_id - ds_id
 * @property {string} ref_field_type - ref_field_type
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<connectTableAddColumnPanelFieldSelectedParams>} LogObject<connectTableAddColumnPanelFieldSelectedParams>
 */
export function connectTableAddColumnPanelFieldSelected(params) {
    return { evid: 82, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'connectTableAddColumnPanelAddButtonClicked' event (evid: 83)
 *
 * Event description: Connect table add column panel add button clicked
 *
 * @param {connectTableAddColumnPanelAddButtonClickedParams} params - The parameters for this event
 * @property {string} app_name - app_name
 * @property {string} app_id - app_id
 * @property {number} users_with_write_devmode_count - users_with_write_devmode_count
 * @property {string} builderType - builder_type
 * @property {GUID} visitorId - visitor_id
 * @property {string} ds_type - ds_type
 * @property {GUID} site_id - site_id
 * @property {string} panel_id - panel_id
 * @property {string} appName - app_name
 * @property {number} users_on_site_count - users_on_site_count
 * @property {number} ts - ts
 * @property {string} panel_name - panel_name
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} pageId - page_id
 * @property {string} token - token
 * @property {number} users_with_devmode_count - users_with_devmode_count
 * @property {string} ver - version
 * @property {string} userEnv - user_env
 * @property {string} repeater_parent_id - if attached to repeater - this will be the repeater id
 * @property {string} editor_working_mode - editor_working_mode
 * @property {string} dev_mode - dev_mode
 * @property {string} appId - app_id
 * @property {GUID} esi - esi
 * @property {string} component_id - component_id
 * @property {string} component_type - comp type
 * @property {GUID} msid - msid
 * @property {string} ds_id - ds_id
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<connectTableAddColumnPanelAddButtonClickedParams>} LogObject<connectTableAddColumnPanelAddButtonClickedParams>
 */
export function connectTableAddColumnPanelAddButtonClicked(params) {
    return { evid: 83, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'connectSuccessfulSubmitGoesTo' event (evid: 86)
 *
 * Event description: Connect successful submit goes to
 *
 * @param {connectSuccessfulSubmitGoesToParams} params - The parameters for this event
 * @property {string} app_name - app_name
 * @property {string} app_id - app_id
 * @property {number} users_with_write_devmode_count - users_with_write_devmode_count
 * @property {string} target_value - target_value
 * @property {string} builderType - builder_type
 * @property {GUID} visitorId - visitor_id
 * @property {string} ds_type - ds_type
 * @property {GUID} site_id - site_id
 * @property {string} panel_id - panel_id
 * @property {string} appName - app_name
 * @property {number} users_on_site_count - users_on_site_count
 * @property {number} ts - ts
 * @property {string} panel_name - panel_name
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} pageId - page_id
 * @property {string} token - token
 * @property {number} users_with_devmode_count - users_with_devmode_count
 * @property {string} ver - version
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {string} target_type - target_type
 * @property {string} dev_mode - dev_mode
 * @property {string} appId - app_id
 * @property {GUID} esi - esi
 * @property {string} component_id - component_id
 * @property {string} component_type - comp type
 * @property {GUID} msid - msid
 * @property {string} target_kind - target_kind
 * @property {string} ds_id - ds_id
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<connectSuccessfulSubmitGoesToParams>} LogObject<connectSuccessfulSubmitGoesToParams>
 */
export function connectSuccessfulSubmitGoesTo(params) {
    return { evid: 86, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'previewClicked' event (evid: 87)
 *
 * Event description: dynamic page - editor: preview clicked
 *
 * @param {previewClickedParams} params - The parameters for this event
 * @property {string} app_name - app_name
 * @property {string} app_id - app_id
 * @property {string} item_url - ()
 * @property {number} users_with_write_devmode_count - users_with_write_devmode_count
 * @property {string} builderType - builder_type
 * @property {GUID} visitorId - visitor_id
 * @property {string} ds_type - ds_type
 * @property {GUID} site_id - site_id
 * @property {string} panel_id - panel_id
 * @property {string} appName - app_name
 * @property {number} users_on_site_count - users_on_site_count
 * @property {number} ts - ts
 * @property {boolean} isTPA - {boolean value for tpa pages}
 * @property {string} panel_name - panel_name
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} pageId - page_id
 * @property {string} item_name - ()
 * @property {string} token - token
 * @property {number} users_with_devmode_count - users_with_devmode_count
 * @property {string} ver - version
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {string} dev_mode - dev_mode
 * @property {string} appId - app_id
 * @property {GUID} esi - esi
 * @property {string} editMode - (mobile,desktop)
 * @property {GUID} msid - msid
 * @property {string} ds_id - ds_id
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<previewClickedParams>} LogObject<previewClickedParams>
 */
export function previewClicked(params) {
    return { evid: 87, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'previewSelectPage' event (evid: 88)
 *
 * Event description: dynamic page - editor: preview_select_page
 *
 * @param {previewSelectPageParams} params - The parameters for this event
 * @property {string} app_name - app_name
 * @property {string} app_id - app_id
 * @property {string} item_url - ()
 * @property {number} users_with_write_devmode_count - users_with_write_devmode_count
 * @property {string} builderType - builder_type
 * @property {GUID} visitorId - visitor_id
 * @property {string} ds_type - ds_type
 * @property {GUID} site_id - site_id
 * @property {string} panel_id - panel_id
 * @property {string} appName - app_name
 * @property {number} users_on_site_count - users_on_site_count
 * @property {number} ts - ts
 * @property {boolean} isTPA - {boolean value for tpa pages}
 * @property {string} panel_name - panel_name
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} pageId - page_id
 * @property {string} item_name - ()
 * @property {string} token - token
 * @property {number} users_with_devmode_count - users_with_devmode_count
 * @property {string} ver - version
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {string} dev_mode - dev_mode
 * @property {string} appId - app_id
 * @property {GUID} esi - esi
 * @property {string} editMode - (mobile,desktop)
 * @property {GUID} msid - msid
 * @property {string} ds_id - ds_id
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<previewSelectPageParams>} LogObject<previewSelectPageParams>
 */
export function previewSelectPage(params) {
    return { evid: 88, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'previewNevigate' event (evid: 89)
 *
 * Event description: dynamic page - editor: preview_nevigate
 *
 * @param {previewNevigateParams} params - The parameters for this event
 * @property {string} app_name - app_name
 * @property {string} app_id - app_id
 * @property {string} item_url - ()
 * @property {number} users_with_write_devmode_count - users_with_write_devmode_count
 * @property {string} builderType - builder_type
 * @property {GUID} visitorId - visitor_id
 * @property {string} ds_type - ds_type
 * @property {GUID} site_id - site_id
 * @property {string} panel_id - panel_id
 * @property {string} appName - app_name
 * @property {number} users_on_site_count - users_on_site_count
 * @property {number} ts - ts
 * @property {string} panel_name - panel_name
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} pageId - page_id
 * @property {string} item_name - ()
 * @property {string} token - token
 * @property {number} users_with_devmode_count - users_with_devmode_count
 * @property {string} ver - version
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {string} dev_mode - dev_mode
 * @property {string} appId - app_id
 * @property {GUID} esi - esi
 * @property {string} editMode - (mobile,desktop)
 * @property {GUID} msid - msid
 * @property {string} ds_id - ds_id
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<previewNevigateParams>} LogObject<previewNevigateParams>
 */
export function previewNevigate(params) {
    return { evid: 89, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'previewClosed' event (evid: 90)
 *
 * Event description: dynamic page - editor: preview_closed
 *
 * @param {previewClosedParams} params - The parameters for this event
 * @property {string} app_name - app_name
 * @property {string} app_id - app_id
 * @property {number} users_with_write_devmode_count - users_with_write_devmode_count
 * @property {string} builderType - builder_type
 * @property {GUID} visitorId - visitor_id
 * @property {string} ds_type - ds_type
 * @property {GUID} site_id - site_id
 * @property {string} panel_id - panel_id
 * @property {string} appName - app_name
 * @property {number} users_on_site_count - users_on_site_count
 * @property {number} ts - ts
 * @property {string} panel_name - panel_name
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} pageId - page_id
 * @property {string} token - token
 * @property {number} users_with_devmode_count - users_with_devmode_count
 * @property {string} ver - version
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {string} dev_mode - dev_mode
 * @property {string} appId - app_id
 * @property {GUID} esi - esi
 * @property {string} editMode - (mobile,desktop)
 * @property {GUID} msid - msid
 * @property {string} ds_id - ds_id
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<previewClosedParams>} LogObject<previewClosedParams>
 */
export function previewClosed(params) {
    return { evid: 90, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'pageDeleteDataset' event (evid: 91)
 *
 * Event description: dynamic page - editor: page_delete_dataset
 *
 * @param {pageDeleteDatasetParams} params - The parameters for this event
 * @property {string} app_name - app_name
 * @property {string} app_id - app_id
 * @property {number} users_with_write_devmode_count - users_with_write_devmode_count
 * @property {string} builderType - builder_type
 * @property {GUID} visitorId - visitor_id
 * @property {string} ds_type - ds_type
 * @property {GUID} site_id - site_id
 * @property {string} panel_id - panel_id
 * @property {string} appName - app_name
 * @property {number} users_on_site_count - users_on_site_count
 * @property {number} ts - ts
 * @property {string} panel_name - panel_name
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} controller_type - ()
 * @property {string} controller_id - ()
 * @property {string} pageId - page_id
 * @property {string} token - token
 * @property {number} users_with_devmode_count - users_with_devmode_count
 * @property {string} ver - version
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {string} dev_mode - dev_mode
 * @property {string} appId - app_id
 * @property {GUID} esi - esi
 * @property {GUID} msid - msid
 * @property {string} ds_id - ds_id
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<pageDeleteDatasetParams>} LogObject<pageDeleteDatasetParams>
 */
export function pageDeleteDataset(params) {
    return { evid: 91, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'leftTreeClickOnAnItem' event (evid: 93)
 *
 * Event description: Click on an item in the left tree (item types are: dynamic_page, file, folder, page, light_box, Collection, master_page, resource)
 *
 * @param {leftTreeClickOnAnItemParams} params - The parameters for this event
 * @property {string} app_name - app_name
 * @property {string} app_id - app_id
 * @property {number} users_with_write_devmode_count - users_with_write_devmode_count
 * @property {string} builderType - builder_type
 * @property {GUID} visitorId - deprecated
 * @property {string} ds_type - ds_type
 * @property {GUID} site_id - site_id
 * @property {string} panel_id - panel_id
 * @property {string} appName - app_name
 * @property {number} users_on_site_count - users_on_site_count
 * @property {string} tab_name - tab_name
 * @property {number} ts - ts
 * @property {string} panel_name - panel_name
 * @property {string} viewmode - viewmode
 * @property {string} input_name - deprecated
 * @property {string} pageId - page_id
 * @property {string} item_name - item_name
 * @property {string} origin - {pop up, left tree]
 * @property {string} token - token
 * @property {number} users_with_devmode_count - users_with_devmode_count
 * @property {string} ver - version
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {string} dev_mode - dev_mode
 * @property {string} appId - app_id
 * @property {GUID} esi - esi
 * @property {string} item_type - item_type(page,dynamic_page,light_box,file,folder,collection)
 * @property {GUID} msid - msid
 * @property {string} ds_id - ds_id
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<leftTreeClickOnAnItemParams>} LogObject<leftTreeClickOnAnItemParams>
 */
export function leftTreeClickOnAnItem(params) {
    return { evid: 93, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'leftTreeClickOnASection' event (evid: 94)
 *
 * Event description: Left tree - Click on a section
 *
 * @param {leftTreeClickOnASectionParams} params - The parameters for this event
 * @property {string} app_name - app_name
 * @property {string} app_id - app_id
 * @property {number} users_with_write_devmode_count - users_with_write_devmode_count
 * @property {string} builderType - builder_type
 * @property {GUID} visitorId - deprecated
 * @property {string} section_name - section_name
 * @property {string} ds_type - ds_type
 * @property {GUID} site_id - site_id
 * @property {string} panel_id - panel_id
 * @property {string} appName - app_name
 * @property {number} users_on_site_count - users_on_site_count
 * @property {number} ts - ts
 * @property {string} panel_name - panel_name
 * @property {string} viewmode - viewmode
 * @property {string} input_name - deprecated
 * @property {string} pageId - page_id
 * @property {string} token - token
 * @property {number} users_with_devmode_count - users_with_devmode_count
 * @property {string} ver - version
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {string} sub_section - sub_section
 * @property {string} dev_mode - dev_mode
 * @property {string} appId - app_id
 * @property {GUID} esi - esi
 * @property {GUID} msid - msid
 * @property {string} action - action (open,close)
 * @property {string} ds_id - ds_id
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<leftTreeClickOnASectionParams>} LogObject<leftTreeClickOnASectionParams>
 */
export function leftTreeClickOnASection(params) {
    return { evid: 94, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'leftTreeClickOnMenuSettingIconItem' event (evid: 95)
 *
 * Event description: Left tree - Click on settings icon
 *
 * @param {leftTreeClickOnMenuSettingIconItemParams} params - The parameters for this event
 * @property {string} app_name - app_name
 * @property {string} app_id - app_id
 * @property {string} renderingFlow - Rendering flow
 * @property {number} users_with_write_devmode_count - users_with_write_devmode_count
 * @property {string} builderType - builder_type
 * @property {GUID} visitorId - deprecated
 * @property {string} ds_type - ds_type
 * @property {GUID} site_id - site_id
 * @property {string} panel_id - panel_id
 * @property {string} appName - app_name
 * @property {number} users_on_site_count - users_on_site_count
 * @property {number} ts - ts
 * @property {string} panel_name - panel_name
 * @property {string} viewmode - viewmode
 * @property {string} input_name - deprecated
 * @property {string} pageId - page_id
 * @property {string} item_name - item_name
 * @property {string} token - token
 * @property {number} users_with_devmode_count - users_with_devmode_count
 * @property {string} ver - version
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {string} dev_mode - dev_mode
 * @property {string} appId - app_id
 * @property {GUID} esi - esi
 * @property {string} item_type - item_type (page,file,collection)
 * @property {GUID} msid - msid
 * @property {string} ds_id - ds_id
 * @property {string} collection_privacy - shared / not shared
 * @property {string} num_items_type - single / multiple
 * @property {string} Collection_owner - user / external / wix app
 * @property {string} can_write_to_schema - true / false
 * @return {LogObject<leftTreeClickOnMenuSettingIconItemParams>} LogObject<leftTreeClickOnMenuSettingIconItemParams>
 */
export function leftTreeClickOnMenuSettingIconItem(params) {
    return { evid: 95, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'leftTreeClickOnPlusIcon' event (evid: 96)
 *
 * Event description: Left tree -Click on plus icon
 *
 * @param {leftTreeClickOnPlusIconParams} params - The parameters for this event
 * @property {string} app_name - app_name
 * @property {string} app_id - app_id
 * @property {number} users_with_write_devmode_count - users_with_write_devmode_count
 * @property {string} builderType - builder_type
 * @property {GUID} visitorId - deprecated
 * @property {string} section_name - section_name
 * @property {string} ds_type - ds_type
 * @property {GUID} site_id - site_id
 * @property {string} panel_id - panel_id
 * @property {string} appName - app_name
 * @property {number} users_on_site_count - users_on_site_count
 * @property {number} ts - ts
 * @property {string} panel_name - panel_name
 * @property {string} viewmode - viewmode
 * @property {string} input_name - deprecated
 * @property {string} pageId - page_id
 * @property {string} token - token
 * @property {number} users_with_devmode_count - users_with_devmode_count
 * @property {string} ver - version
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {string} sub_section - sub section name
 * @property {string} dev_mode - dev_mode
 * @property {string} appId - app_id
 * @property {GUID} esi - esi
 * @property {GUID} msid - msid
 * @property {string} ds_id - ds_id
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<leftTreeClickOnPlusIconParams>} LogObject<leftTreeClickOnPlusIconParams>
 */
export function leftTreeClickOnPlusIcon(params) {
    return { evid: 96, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'leftTreeClickInMenuSettingOptionItem' event (evid: 97)
 *
 * Event description: Left tree -Click in an item settings menu
 *
 * @param {leftTreeClickInMenuSettingOptionItemParams} params - The parameters for this event
 * @property {string} app_name - app_name
 * @property {string} app_id - app_id
 * @property {number} users_with_write_devmode_count - users_with_write_devmode_count
 * @property {string} builderType - builder_type
 * @property {GUID} visitorId - deprecated
 * @property {string} ds_type - ds_type
 * @property {GUID} site_id - site_id
 * @property {string} panel_id - panel_id
 * @property {string} appName - app_name
 * @property {number} users_on_site_count - users_on_site_count
 * @property {number} ts - ts
 * @property {string} panel_name - panel_name
 * @property {string} viewmode - viewmode
 * @property {string} input_name - deprecated
 * @property {string} pageId - page_id
 * @property {string} item_name - item_name
 * @property {string} menu_entry_name - menu_entry_name
 * @property {string} token - token
 * @property {number} users_with_devmode_count - users_with_devmode_count
 * @property {string} ver - version
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {string} dev_mode - dev_mode
 * @property {string} appId - app_id
 * @property {GUID} esi - esi
 * @property {string} item_type - item_type (page,file,collection)
 * @property {GUID} msid - msid
 * @property {string} ds_id - ds_id
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<leftTreeClickInMenuSettingOptionItemParams>} LogObject<leftTreeClickInMenuSettingOptionItemParams>
 */
export function leftTreeClickInMenuSettingOptionItem(params) {
    return { evid: 97, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'leftTreeClickAddNewQuickLink' event (evid: 98)
 *
 * Event description: Click on empty state "Add a new..." in the left tree
 *
 * @param {leftTreeClickAddNewQuickLinkParams} params - The parameters for this event
 * @property {string} app_name - app_name
 * @property {string} app_id - app_id
 * @property {number} users_with_write_devmode_count - users_with_write_devmode_count
 * @property {string} builderType - builder_type
 * @property {GUID} visitorId - deprecated
 * @property {string} section_name - section_name
 * @property {string} ds_type - ds_type
 * @property {GUID} site_id - site_id
 * @property {string} panel_id - panel_id
 * @property {string} appName - app_name
 * @property {number} users_on_site_count - users_on_site_count
 * @property {number} ts - ts
 * @property {string} panel_name - panel_name
 * @property {string} viewmode - viewmode
 * @property {string} input_name - deprecated
 * @property {string} pageId - page_id
 * @property {string} token - token
 * @property {number} users_with_devmode_count - users_with_devmode_count
 * @property {string} ver - version
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {string} sub_section - NPM_packages, internal_packages, external_databases
 * @property {string} dev_mode - dev_mode
 * @property {string} appId - app_id
 * @property {GUID} esi - esi
 * @property {GUID} msid - msid
 * @property {string} ds_id - ds_id
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<leftTreeClickAddNewQuickLinkParams>} LogObject<leftTreeClickAddNewQuickLinkParams>
 */
export function leftTreeClickAddNewQuickLink(params) {
    return { evid: 98, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'leftTreeClickInMenuPlusOptionSection' event (evid: 99)
 *
 * Event description: Left tree - Click in a section plus menu
 *
 * @param {leftTreeClickInMenuPlusOptionSectionParams} params - The parameters for this event
 * @property {string} app_name - app_name
 * @property {string} app_id - app_id
 * @property {number} users_with_write_devmode_count - users_with_write_devmode_count
 * @property {string} builderType - builder_type
 * @property {GUID} visitorId - deprecated
 * @property {string} section_name - section_name
 * @property {string} ds_type - ds_type
 * @property {GUID} site_id - site_id
 * @property {string} panel_id - panel_id
 * @property {string} appName - app_name
 * @property {number} users_on_site_count - users_on_site_count
 * @property {number} ts - ts
 * @property {string} panel_name - panel_name
 * @property {string} viewmode - viewmode
 * @property {string} input_name - deprecated
 * @property {string} pageId - page_id
 * @property {string} menu_entry_name - menu_entry_name
 * @property {string} token - token
 * @property {number} users_with_devmode_count - users_with_devmode_count
 * @property {string} ver - version
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {string} sub_section - sub section name
 * @property {string} dev_mode - dev_mode
 * @property {string} appId - app_id
 * @property {GUID} esi - esi
 * @property {GUID} msid - msid
 * @property {string} ds_id - ds_id
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<leftTreeClickInMenuPlusOptionSectionParams>} LogObject<leftTreeClickInMenuPlusOptionSectionParams>
 */
export function leftTreeClickInMenuPlusOptionSection(params) {
    return { evid: 99, src: 79, endpoint: '', params };
}
/**
 * A function to create a log object for the 'vmErrorEvent' event (evid: 10)
 *
 * Event description: vm error event.
 *
 * @param {vmErrorEventParams} params - The parameters for this event
 * @property {string} dsc - The full error massage
 * @property {number} response_time - ts
 * @property {boolean} is_rollout - is_rollout
 * @property {string} initiator_name - initiator_name
 * @property {string} builderType - builder_type
 * @property {string} errscp - errscp
 * @property {GUID} site_id - site_id
 * @property {string} errn - The name of error massage
 * @property {string} p4 - p4
 * @property {string} majorver - majorver
 * @property {string} trgt - trgt
 * @property {boolean} isLiveEditor - is_live_editor
 * @property {number} ts - ts
 * @property {string} playerVersion - player_version
 * @property {string} artifactVersion - artifact_version
 * @property {number} usersOnPageCount - users_on_page_count
 * @property {string} viewmode - viewmode
 * @property {string} error_description_short - error_description_short
 * @property {string} data_center - data_center
 * @property {string} screenResolution - screen_resolution
 * @property {string} p3 - p3
 * @property {GUID} did - site_id
 * @property {boolean} isSg - is_sg
 * @property {string} dsOrigin - ds_origin
 * @property {string} p2 - p2
 * @property {string} token - token
 * @property {string} sessionId - session_id
 * @property {number} errc - The number of error massage
 * @property {string} stack - stacktrace
 * @property {string} viewerName - viewer_name
 * @property {string} app_id - app_id
 * @property {number} sev - sev
 * @property {number} cat - cat
 * @property {string} errormsg - The full error massage
 * @property {number} usersOnSiteCount - users_on_site_count
 * @property {string} p1 - p1
 * @property {GUID} msid - msid
 * @property {number} base_version - base_version
 * @property {number} iss - iss
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<vmErrorEventParams>} LogObject<vmErrorEventParams>
 */
export function vmErrorEvent(params) {
    return { evid: 10, src: 83, endpoint: '', params };
}
/**
 * A function to create a log object for the 'cmCreateCollectionClicked' event (evid: 100)
 *
 * Event description: CM Create Collection clicked
 *
 * @param {cmCreateCollectionClickedParams} params - The parameters for this event
 * @property {string} site_id - site_id
 * @property {number} ts - ts
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} version - version
 * @property {string} origin - ["ideTree", "add_panel", "DS_settings"]
 * @property {string} environment - Editor / business_manager
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {GUID} esi - esi
 * @property {GUID} msid - msid
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<cmCreateCollectionClickedParams>} LogObject<cmCreateCollectionClickedParams>
 */
export function cmCreateCollectionClicked(params) {
    return { evid: 100, src: 83, endpoint: '', params };
}
/**
 * A function to create a log object for the 'cmCollectionCreated' event (evid: 101)
 *
 * Event description: CM collection created
 *
 * @param {cmCollectionCreatedParams} params - The parameters for this event
 * @property {boolean} is_single_item - (true, false)
 * @property {boolean} is_first_data_modal - {true,false}
 * @property {string} site_id - site_id
 * @property {number} ts - ts
 * @property {string} collection_id - collection_id
 * @property {string} permissions - json
 * @property {boolean} is_draft_publish_enabled - {true, false}
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} version - version
 * @property {string} collection_type - collection_type
 * @property {string} origin - ["ideTree", "add_panel", "DS_settings"]
 * @property {string} environment - EDITOR, BUSINESS_MANAGER
 * @property {boolean} is_sandbox_enabled - (true/false)
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {string} default_new_item_status - (draft/published)
 * @property {GUID} esi - esi
 * @property {string} collection_name - collection name
 * @property {GUID} msid - msid
 * @property {boolean} default_content - {true/false}
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<cmCollectionCreatedParams>} LogObject<cmCollectionCreatedParams>
 */
export function cmCollectionCreated(params) {
    return { evid: 101, src: 83, endpoint: '', params };
}
/**
 * A function to create a log object for the 'cmOpeningFromSiteStructure' event (evid: 102)
 *
 * Event description: CM Opening from Site Structure
 *
 * @param {cmOpeningFromSiteStructureParams} params - The parameters for this event
 * @property {string} site_id - site_id
 * @property {number} ts - ts
 * @property {string} collection_id - collection id
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} version - version
 * @property {string} origin - site structure, add menu, etc...
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {GUID} esi - esi
 * @property {string} collection_name - collection name
 * @property {GUID} msid - msid
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<cmOpeningFromSiteStructureParams>} LogObject<cmOpeningFromSiteStructureParams>
 */
export function cmOpeningFromSiteStructure(params) {
    return { evid: 102, src: 83, endpoint: '', params };
}
/**
 * A function to create a log object for the 'clickOnDbCollectionInCm' event (evid: 103)
 *
 * Event description: User clicked on DB collection in database tab in biz mgr
 *
 * @param {clickOnDbCollectionInCmParams} params - The parameters for this event
 * @property {string} site_id - site_id
 * @property {number} ts - ts
 * @property {string} collection_id - collection id
 * @property {number} number_of_items_in_collection - In One App, it's the total number of items available in the active view, not the total number of items in the collection.
 * @property {string} view_type - (TABLE, LIST, GALLERY)
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {boolean} is_shared - true/false
 * @property {number} time_to_open - time_to_open in ms
 * @property {string} version - version
 * @property {string} origin - site structure, add menu, etc... In One App, the origins are "manage_tab" and "platforms_tab".
 * @property {string} environment - (Editor/Dashboard/one_app)
 * @property {boolean} is_sandbox_enabled - (true/false)
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {GUID} esi - esi
 * @property {boolean} is_single_item_collection - is single item collection
 * @property {string} collection_name - In One App, it's a collection ID.
 * @property {GUID} msid - msid
 * @property {string} columns - json of colNames & colTypes
 * @property {string} collectionType - SiteCollection/WixAppCollection
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<clickOnDbCollectionInCmParams>} LogObject<clickOnDbCollectionInCmParams>
 */
export function clickOnDbCollectionInCm(params) {
    return { evid: 103, src: 83, endpoint: '', params };
}
/**
 * A function to create a log object for the 'cmAddFieldClicked' event (evid: 104)
 *
 * Event description: CM Add Field Clicked
 *
 * @param {cmAddFieldClickedParams} params - The parameters for this event
 * @property {string} site_id - site_id
 * @property {string} field_type - type of field
 * @property {number} ts - ts
 * @property {string} collection_id - collection id
 * @property {boolean} description_edited - (true/false)
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} version - version
 * @property {boolean} is_live_data - (true/false)
 * @property {string} origin - ["FIELD_HEADER",
 * "LIST_VISIBLE_FIELDS_BUTTON",
 * "TABLE_VISIBLE_FIELDS_BUTTON",
 * "ITEM_VISIBLE_FIELD_BUTTON",
 * "ADD_COLUMN_BUTTON",
 * "GALLERY_VISIBLE_FIELDS_BUTTON",
 * "SINGLES_ADD_FIELD","MULTIPLES_ADD_FIELD"]
 * @property {string} environment - Editor / business_manager
 * @property {boolean} is_sandbox_enabled - (true/false)
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {GUID} esi - esi
 * @property {string} collection_name - collection name
 * @property {GUID} msid - msid
 * @property {string} field_name - field_name
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<cmAddFieldClickedParams>} LogObject<cmAddFieldClickedParams>
 */
export function cmAddFieldClicked(params) {
    return { evid: 104, src: 83, endpoint: '', params };
}
/**
 * A function to create a log object for the 'cmFieldAdded' event (evid: 106)
 *
 * Event description: CM Field Added
 *
 * @param {cmFieldAddedParams} params - The parameters for this event
 * @property {string} ref_collection_name - Referenced_collection_name (if field type is Reference)
 * @property {number} old_number_of_fields - old_number_of_fields
 * @property {string} site_id - site_id
 * @property {number} ts - ts
 * @property {number} number_of_fields - number_of_fields (inc added field)
 * @property {string} collection_id - collection_id
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} version - version
 * @property {string} origin - ["add_field_form", "ADD_COLUMN_BUTTON", "DS_ADD_FIELD_BUTTON"]
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {GUID} esi - esi
 * @property {string} field_json - field_json
 * @property {string} collection_name - collection name
 * @property {GUID} msid - msid
 * @property {string} ref_collection_id - referenced_collection_id (if field type is Reference)
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<cmFieldAddedParams>} LogObject<cmFieldAddedParams>
 */
export function cmFieldAdded(params) {
    return { evid: 106, src: 83, endpoint: '', params };
}
/**
 * A function to create a log object for the 'cmMenuVisibleFieldsClicked' event (evid: 109)
 *
 * Event description: CM Menu visible fields clicked
 *
 * @param {cmMenuVisibleFieldsClickedParams} params - The parameters for this event
 * @property {string} name - view name
 * @property {string} site_id - site_id
 * @property {number} ts - ts
 * @property {string} collection_id - collection id
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {GUID} view_id - view id
 * @property {string} version - version
 * @property {boolean} is_live_data - (true/false)
 * @property {string} hidden_fields - field_json
 * @property {string} origin - site structure, add menu, etc...
 * @property {string} fields - field_json
 * @property {string} environment - (Editor/Dashboard)
 * @property {boolean} is_sandbox_enabled - (true/false)
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {GUID} esi - esi
 * @property {string} collection_name - collection name
 * @property {GUID} msid - msid
 * @property {string} view - view
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<cmMenuVisibleFieldsClickedParams>} LogObject<cmMenuVisibleFieldsClickedParams>
 */
export function cmMenuVisibleFieldsClicked(params) {
    return { evid: 109, src: 83, endpoint: '', params };
}
/**
 * A function to create a log object for the 'cmsNotificationSrc83Evid11' event (evid: 11)
 *
 * Event description: CMS Notification
 *
 * @param {cmsNotificationSrc83Evid11Params} params - The parameters for this event
 * @property {string} collectionId - collection_id
 * @property {string} pageId - page_id
 * @property {string} urlSlug - url slug that triggered the change
 * @property {number} num_of_items - number of affected items
 * @property {string} collection_name - collection_name
 * @property {string} affected_object - item\ page
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<cmsNotificationSrc83Evid11Params>} LogObject<cmsNotificationSrc83Evid11Params>
 */
export function cmsNotificationSrc83Evid11(params) {
    return { evid: 11, src: 83, endpoint: '', params };
}
/**
 * A function to create a log object for the 'cmVisibleFieldsChanged' event (evid: 110)
 *
 * Event description: CM Visible Fields changed
 *
 * @param {cmVisibleFieldsChangedParams} params - The parameters for this event
 * @property {string} name - view name
 * @property {string} site_id - site_id
 * @property {string} field_type - String, Image
 * @property {number} ts - ts
 * @property {string} collection_id - collection id
 * @property {boolean} is_draft_publish_enabled - (true, false)
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {GUID} view_id - view id
 * @property {string} version - version
 * @property {boolean} is_live_data - (true/false)
 * @property {string} hidden_fields - field_json
 * @property {string} origin - site structure, add menu, etc...
 * @property {string} fields - field_json
 * @property {string} environment - (Editor/Dashboard)
 * @property {boolean} is_sandbox_enabled - (true/false)
 * @property {string} view_layout - (table, list, gallery)
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {number} number_of_visible_fields - number_of_visible_fields
 * @property {GUID} esi - esi
 * @property {string} field_json - field_json
 * @property {string} collection_name - collection name
 * @property {GUID} msid - msid
 * @property {string} field_name - field_name
 * @property {string} action - ["show"/"hide"]
 * @property {number} number_of_hidden_fields - number_of_hidden_fields
 * @property {string} view - view
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<cmVisibleFieldsChangedParams>} LogObject<cmVisibleFieldsChangedParams>
 */
export function cmVisibleFieldsChanged(params) {
    return { evid: 110, src: 83, endpoint: '', params };
}
/**
 * A function to create a log object for the 'cmMenuFilterClicked' event (evid: 111)
 *
 * Event description: CM Menu filter clicked
 *
 * @param {cmMenuFilterClickedParams} params - The parameters for this event
 * @property {string} name - view name
 * @property {string} site_id - site_id
 * @property {number} ts - ts
 * @property {string} collection_id - collection id
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {GUID} view_id - view id
 * @property {string} version - version
 * @property {boolean} is_live_data - (true/false)
 * @property {string} origin - site structure, add menu, etc...
 * @property {string} environment - (Editor/Dashboard/one_app)
 * @property {boolean} is_sandbox_enabled - (true/false)
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {GUID} esi - esi
 * @property {string} collection_name - collection name
 * @property {GUID} msid - msid
 * @property {string} view - view
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<cmMenuFilterClickedParams>} LogObject<cmMenuFilterClickedParams>
 */
export function cmMenuFilterClicked(params) {
    return { evid: 111, src: 83, endpoint: '', params };
}
/**
 * A function to create a log object for the 'cmFilterAdded' event (evid: 112)
 *
 * Event description: CM Menu filter added
 *
 * @param {cmFilterAddedParams} params - The parameters for this event
 * @property {string} c1 - collection id
 * @property {string} name - view name
 * @property {string} site_id - site_id
 * @property {string} field_type - field_type
 * @property {number} ts - ts
 * @property {number} number_of_items_after_filter - number of items(rows) after filter
 * @property {string} collection_id - collection id
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} filter_value - filter_value
 * @property {GUID} view_id - view id
 * @property {string} version - version
 * @property {boolean} is_live_data - (true/false)
 * @property {string} operator - operator
 * @property {string} origin - site structure, add menu, etc...
 * @property {number} time_to_load_collection - time_to_load_collection (in ms)
 * @property {string} environment - (Editor/Dashboard)
 * @property {boolean} is_sandbox_enabled - (true/false)
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {number} number_of_filter_predicates - number_of_filter_predicates
 * @property {GUID} esi - esi
 * @property {string} collection_name - collection name
 * @property {GUID} msid - msid
 * @property {string} field_name - field_name
 * @property {string} view - view
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<cmFilterAddedParams>} LogObject<cmFilterAddedParams>
 */
export function cmFilterAdded(params) {
    return { evid: 112, src: 83, endpoint: '', params };
}
/**
 * A function to create a log object for the 'cmFilterChanged' event (evid: 113)
 *
 * Event description: CM Menu filter changed
 *
 * @param {cmFilterChangedParams} params - The parameters for this event
 * @property {string} name - view name
 * @property {string} site_id - site_id
 * @property {string} field_type - field_type
 * @property {number} ts - ts
 * @property {number} number_of_items_after_filter - number of items(rows) after filter
 * @property {string} collection_id - collection id
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {GUID} view_id - view id
 * @property {string} version - version
 * @property {boolean} is_live_data - (true/false)
 * @property {string} origin - site structure, add menu, etc...
 * @property {number} time_to_load_collection - time_to_load_collection (in ms)
 * @property {string} environment - (Editor/Dashboard)
 * @property {boolean} is_sandbox_enabled - (true/false)
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {number} number_of_filter_predicates - the number of filters in the filter menu
 * @property {GUID} esi - esi
 * @property {string} collection_name - collection name
 * @property {GUID} msid - msid
 * @property {string} field_name - field_name
 * @property {string} action - update, delete
 * @property {string} view - view
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<cmFilterChangedParams>} LogObject<cmFilterChangedParams>
 */
export function cmFilterChanged(params) {
    return { evid: 113, src: 83, endpoint: '', params };
}
/**
 * A function to create a log object for the 'cmMenuSortClicked' event (evid: 114)
 *
 * Event description: CM Menu sort clicked
 *
 * @param {cmMenuSortClickedParams} params - The parameters for this event
 * @property {string} name - view name
 * @property {string} site_id - site_id
 * @property {number} ts - ts
 * @property {string} collection_id - collection id
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {GUID} view_id - view id
 * @property {string} version - version
 * @property {boolean} is_live_data - (true/false)
 * @property {string} origin - site structure, add menu, etc...
 * @property {string} environment - (Editor/Dashboard)
 * @property {boolean} is_sandbox_enabled - (true/false)
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {GUID} esi - esi
 * @property {string} collection_name - collection name
 * @property {GUID} msid - msid
 * @property {string} view - view
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<cmMenuSortClickedParams>} LogObject<cmMenuSortClickedParams>
 */
export function cmMenuSortClicked(params) {
    return { evid: 114, src: 83, endpoint: '', params };
}
/**
 * A function to create a log object for the 'cmSortChanged' event (evid: 115)
 *
 * Event description: CM Sort Changed
 *
 * @param {cmSortChangedParams} params - The parameters for this event
 * @property {string} renderingFlow - Rendering flow
 * @property {string} name - view name
 * @property {string} site_id - site_id
 * @property {string} field_type - field_type
 * @property {number} ts - ts
 * @property {string} collection_id - collection id
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {number} number_of_filter_sort - number_of_filter_sort
 * @property {GUID} view_id - view id
 * @property {string} version - version
 * @property {boolean} is_live_data - (true/false)
 * @property {string} origin - site structure, add menu, etc...
 * @property {number} time_to_load_collection - time_to_load_collection (should be in ms)
 * @property {string} environment - (Editor/Dashboard)
 * @property {boolean} is_sandbox_enabled - (true/false)
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {number} number_of_items_after_sort - deprecated
 * @property {GUID} esi - esi
 * @property {string} collection_name - collection name
 * @property {GUID} msid - msid
 * @property {string} field_name - field_name
 * @property {string} action - update, delete
 * @property {string} view - view
 * @property {string} sort_type - asc\ desc
 * @return {LogObject<cmSortChangedParams>} LogObject<cmSortChangedParams>
 */
export function cmSortChanged(params) {
    return { evid: 115, src: 83, endpoint: '', params };
}
/**
 * A function to create a log object for the 'cmMenuSyncClicked' event (evid: 116)
 *
 * Event description: CM Menu Sync clicked
 *
 * @param {cmMenuSyncClickedParams} params - The parameters for this event
 * @property {string} site_id - site_id
 * @property {number} ts - ts
 * @property {string} collection_id - collection id
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} version - version
 * @property {string} origin - site structure, add menu, etc...
 * @property {string} environment - Editor / Business_manager
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {GUID} esi - esi
 * @property {string} collection_name - collection name
 * @property {GUID} msid - msid
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<cmMenuSyncClickedParams>} LogObject<cmMenuSyncClickedParams>
 */
export function cmMenuSyncClicked(params) {
    return { evid: 116, src: 83, endpoint: '', params };
}
/**
 * A function to create a log object for the 'cmSynced' event (evid: 117)
 *
 * Event description: CM Sync done
 *
 * @param {cmSyncedParams} params - The parameters for this event
 * @property {string} site_id - site_id
 * @property {number} ts - ts
 * @property {string} collection_id - collection id
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} version - version
 * @property {string} origin - site structure, add menu, etc...
 * @property {string} environment - Editor / business_manager
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {GUID} esi - esi
 * @property {string} sync_operation - sync_operation
 * @property {string} collection_name - collection name
 * @property {GUID} msid - msid
 * @property {number} time_to_complete - time_to_complete (in ms)
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<cmSyncedParams>} LogObject<cmSyncedParams>
 */
export function cmSynced(params) {
    return { evid: 117, src: 83, endpoint: '', params };
}
/**
 * A function to create a log object for the 'cmMenuHooksClicked' event (evid: 118)
 *
 * Event description: CM Menu Hooks clicked
 *
 * @param {cmMenuHooksClickedParams} params - The parameters for this event
 * @property {string} site_id - site_id
 * @property {number} ts - ts
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} version - version
 * @property {string} origin - site structure, add menu, etc...
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {GUID} esi - esi
 * @property {string} collection_name - collection name
 * @property {GUID} msid - msid
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<cmMenuHooksClickedParams>} LogObject<cmMenuHooksClickedParams>
 */
export function cmMenuHooksClicked(params) {
    return { evid: 118, src: 83, endpoint: '', params };
}
/**
 * A function to create a log object for the 'cmHooksDefined' event (evid: 119)
 *
 * Event description: CM Hooks defined
 *
 * @param {cmHooksDefinedParams} params - The parameters for this event
 * @property {string} site_id - site_id
 * @property {string} hook_type - hook_type
 * @property {number} ts - ts
 * @property {string} collection_id - collection id
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} version - version
 * @property {string} origin - site structure, add menu, etc...
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {GUID} esi - esi
 * @property {string} collection_name - collection name
 * @property {GUID} msid - msid
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<cmHooksDefinedParams>} LogObject<cmHooksDefinedParams>
 */
export function cmHooksDefined(params) {
    return { evid: 119, src: 83, endpoint: '', params };
}
/**
 * A function to create a log object for the 'cmsElementConnectionSettingsSrc83Evid12' event (evid: 12)
 *
 * Event description: connection settings actions of any element in a site
 *
 * @param {cmsElementConnectionSettingsSrc83Evid12Params} params - The parameters for this event
 * @property {string} element_name - the element which we edit its settings. e.g button,image, text etc.
 * @property {string} action_name - the name of the action in the menu e.g 'choose a dataset', 'click action connects to'
 * @property {string} action_selected_value - selected value per action e.g: dataset name, connected page in site
 * @property {string} origin - gfpp_click, element_connection_settings
 * @property {string} environment - studio, editor
 * @property {string} page_id - the page_id where the element is located at
 * @property {string} component_id - component_id
 * @property {string} element_id - element_id
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<cmsElementConnectionSettingsSrc83Evid12Params>} LogObject<cmsElementConnectionSettingsSrc83Evid12Params>
 */
export function cmsElementConnectionSettingsSrc83Evid12(params) {
    return { evid: 12, src: 83, endpoint: '', params };
}
/**
 * A function to create a log object for the 'cmMenuPermissionsClicked' event (evid: 120)
 *
 * Event description: Open Collection Permissions clicked
 *
 * @param {cmMenuPermissionsClickedParams} params - The parameters for this event
 * @property {string} site_id - site_id
 * @property {number} ts - ts
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} version - version
 * @property {string} origin - site structure, add menu, etc...
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {GUID} esi - esi
 * @property {string} collection_name - collection name
 * @property {GUID} msid - msid
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<cmMenuPermissionsClickedParams>} LogObject<cmMenuPermissionsClickedParams>
 */
export function cmMenuPermissionsClicked(params) {
    return { evid: 120, src: 83, endpoint: '', params };
}
/**
 * A function to create a log object for the 'cmPermissionsChanged' event (evid: 121)
 *
 * Event description: Collection Permissions changed
 *
 * @param {cmPermissionsChangedParams} params - The parameters for this event
 * @property {string} renderingFlow - Rendering flow
 * @property {string} delete_permission_value - delete_permission_value
 * @property {boolean} update_permissions - update_permissions(boolean T/F)
 * @property {string} read_permission_value - read_permission_value
 * @property {string} site_id - site_id
 * @property {string} insert_permission_value - insert_permission_value
 * @property {number} ts - ts
 * @property {string} collection_id - collection id
 * @property {string} permissions - json
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} update_permissions_value - update_permissions_value
 * @property {string} version - version
 * @property {string} collection_type - collection _type
 * @property {string} old_collection_type - old_collection_type
 * @property {string} old_permissions - json
 * @property {string} origin - site structure, add menu, etc...
 * @property {string} environment - EDITOR, BUSINESS_MANAGER
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {boolean} insert_permission - insert_permission(boolean T/F)
 * @property {boolean} read_permission - read_permission (boolean T/F)
 * @property {boolean} delete_permission - delete_permission(boolean T/F)
 * @property {GUID} esi - esi
 * @property {string} collection_name - collection name
 * @property {GUID} msid - msid
 * @property {string} permissions_type - by the name of the permissions category tab: 'show_content'\ 'collect_content'\'advanced'\'custom'
 * @return {LogObject<cmPermissionsChangedParams>} LogObject<cmPermissionsChangedParams>
 */
export function cmPermissionsChanged(params) {
    return { evid: 121, src: 83, endpoint: '', params };
}
/**
 * A function to create a log object for the 'cmPermissionsDialogCancelled' event (evid: 122)
 *
 * Event description: Collection Permissions dialog cancelled
 *
 * @param {cmPermissionsDialogCancelledParams} params - The parameters for this event
 * @property {string} site_id - site_id
 * @property {number} ts - ts
 * @property {string} permissions - json
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} version - version
 * @property {string} collection_type - collection_type
 * @property {string} origin - site structure, add menu, etc...
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {GUID} esi - esi
 * @property {string} collection_name - Deprecated for the moment
 * @property {GUID} msid - msid
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<cmPermissionsDialogCancelledParams>} LogObject<cmPermissionsDialogCancelledParams>
 */
export function cmPermissionsDialogCancelled(params) {
    return { evid: 122, src: 83, endpoint: '', params };
}
/**
 * A function to create a log object for the 'cmFieldPopupQuickSortClicked' event (evid: 123)
 *
 * Event description: CM Column Popup quick sort clicked
 *
 * @param {cmFieldPopupQuickSortClickedParams} params - The parameters for this event
 * @property {string} site_id - site_id
 * @property {number} ts - ts
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} version - version
 * @property {string} origin - site structure, add menu, etc...
 * @property {string} environment - (Editor/Dashboard)
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {GUID} esi - esi
 * @property {string} field_json - field_json
 * @property {string} collection_name - Deprecated for the moment
 * @property {GUID} msid - msid
 * @property {string} view - view
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<cmFieldPopupQuickSortClickedParams>} LogObject<cmFieldPopupQuickSortClickedParams>
 */
export function cmFieldPopupQuickSortClicked(params) {
    return { evid: 123, src: 83, endpoint: '', params };
}
/**
 * A function to create a log object for the 'cmFieldPopupFilterClicked' event (evid: 124)
 *
 * Event description: CM Column Popup filter clicked
 *
 * @param {cmFieldPopupFilterClickedParams} params - The parameters for this event
 * @property {string} site_id - site_id
 * @property {number} ts - ts
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} version - version
 * @property {string} origin - COLUMN_CONTEXT_MENU
 * @property {string} environment - (Editor/Dashboard)
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {GUID} esi - esi
 * @property {string} field_json - field_json
 * @property {string} collection_name - Deprecated for the moment
 * @property {GUID} msid - msid
 * @property {string} view - view
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<cmFieldPopupFilterClickedParams>} LogObject<cmFieldPopupFilterClickedParams>
 */
export function cmFieldPopupFilterClicked(params) {
    return { evid: 124, src: 83, endpoint: '', params };
}
/**
 * A function to create a log object for the 'cmFieldPopupPinClicked' event (evid: 125)
 *
 * Event description: CM Column Popup pin
 *
 * @param {cmFieldPopupPinClickedParams} params - The parameters for this event
 * @property {string} site_id - site_id
 * @property {number} ts - ts
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} version - version
 * @property {string} origin - site structure, add menu, etc...
 * @property {string} environment - (Editor/Dashboard)
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {GUID} esi - esi
 * @property {string} field_json - field_json
 * @property {string} collection_name - Deprecated for the moment
 * @property {GUID} msid - msid
 * @property {string} view - view
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<cmFieldPopupPinClickedParams>} LogObject<cmFieldPopupPinClickedParams>
 */
export function cmFieldPopupPinClicked(params) {
    return { evid: 125, src: 83, endpoint: '', params };
}
/**
 * A function to create a log object for the 'cmFieldPopupHideClicked' event (evid: 126)
 *
 * Event description: CM Column Popup hide
 *
 * @param {cmFieldPopupHideClickedParams} params - The parameters for this event
 * @property {string} site_id - site_id
 * @property {number} ts - ts
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} version - version
 * @property {string} hidden_fields - field_json
 * @property {string} origin - site structure, add menu, etc...
 * @property {string} fields - field_json
 * @property {string} environment - (Editor/Dashboard)
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {GUID} esi - esi
 * @property {string} field_json - field_json
 * @property {string} collection_name - Deprecated for the moment
 * @property {GUID} msid - msid
 * @property {string} view - view
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<cmFieldPopupHideClickedParams>} LogObject<cmFieldPopupHideClickedParams>
 */
export function cmFieldPopupHideClicked(params) {
    return { evid: 126, src: 83, endpoint: '', params };
}
/**
 * A function to create a log object for the 'cmFieldPopupManagePropertiesClicked' event (evid: 127)
 *
 * Event description: CM Column Popup edit properties clicked
 *
 * @param {cmFieldPopupManagePropertiesClickedParams} params - The parameters for this event
 * @property {string} site_id - site_id
 * @property {number} ts - ts
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} version - version
 * @property {string} origin - site structure, add menu, etc...
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {GUID} esi - esi
 * @property {string} field_json - field_json
 * @property {string} collection_name - Deprecated for the moment
 * @property {GUID} msid - msid
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<cmFieldPopupManagePropertiesClickedParams>} LogObject<cmFieldPopupManagePropertiesClickedParams>
 */
export function cmFieldPopupManagePropertiesClicked(params) {
    return { evid: 127, src: 83, endpoint: '', params };
}
/**
 * A function to create a log object for the 'cmFieldPropertiesChanged' event (evid: 128)
 *
 * Event description: CM Field Properties changed
 *
 * @param {cmFieldPropertiesChangedParams} params - The parameters for this event
 * @property {string} ref_collection_name - ref_collection_name
 * @property {boolean} type_changed - type_changed
 * @property {string} site_id - site_id
 * @property {string} field_type - ["reference","text","id" etc.]
 * @property {number} ts - ts
 * @property {string} collection_id - collection_id
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} version - version
 * @property {string} origin - site structure, add menu, etc...
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {boolean} name_changed - name_changed
 * @property {GUID} esi - esi
 * @property {string} field_json - includes title and type fields
 * @property {string} collection_name - collection name
 * @property {GUID} msid - msid
 * @property {string} ref_collection_id - referenced_collection_id
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<cmFieldPropertiesChangedParams>} LogObject<cmFieldPropertiesChangedParams>
 */
export function cmFieldPropertiesChanged(params) {
    return { evid: 128, src: 83, endpoint: '', params };
}
/**
 * A function to create a log object for the 'wixCodeViewDocsPage' event (evid: 129)
 *
 * Event description: Shoots when a user get into the Corvid mini-site
 *
 * @param {wixCodeViewDocsPageParams} params - The parameters for this event
 * @property {GUID} session_id - session_id
 * @property {Url} referral_url - {}
 * @property {string} site_id - site_id
 * @property {number} ts - ts
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} page_name - example_name/request_title/etc.
 * @property {string} version - version
 * @property {string} origin - {menu/search result/ main_content/ outside}
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {string} category - {articles, examples, tutorials, etc.} for example: https://www.wix.com/corvid/example/add-to-cart-gallery-  category='example'
 * @property {GUID} esi - esi
 * @property {GUID} msid - msid
 * @property {string} page_url - pages_url
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<wixCodeViewDocsPageParams>} LogObject<wixCodeViewDocsPageParams>
 */
export function wixCodeViewDocsPage(params) {
    return { evid: 129, src: 83, endpoint: '', params };
}
/**
 * A function to create a log object for the 'gfppElementClickOnConnectionSettingsSrc83Evid13' event (evid: 13)
 *
 * Event description: when a user clicks on 'connection settings' option in element panel
 *
 * @param {gfppElementClickOnConnectionSettingsSrc83Evid13Params} params - The parameters for this event
 * @property {string} 'panel name' - the element panel name e.g linkPanel, text box
 * @property {string} element_id - ID of the edited element
 * @property {string} action_name - connection_settings_click
 * @property {string} page_id - the id of the page the element is located in
 * @property {string} ds_id - ds_id
 * @property {string} environment - editor\ studio
 * @property {string} origin - gfpp
 * @property {string} element_type - button, image, text box etc.
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<gfppElementClickOnConnectionSettingsSrc83Evid13Params>} LogObject<gfppElementClickOnConnectionSettingsSrc83Evid13Params>
 */
export function gfppElementClickOnConnectionSettingsSrc83Evid13(params) {
    return { evid: 13, src: 83, endpoint: '', params };
}
/**
 * A function to create a log object for the 'wixCodeViewExampleClick' event (evid: 130)
 *
 * Event description: Shoots when a user click on view example
 *
 * @param {wixCodeViewExampleClickParams} params - The parameters for this event
 * @property {string} example_name - example_name
 * @property {GUID} session_id - session_id
 * @property {Url} referral_url - referral_url
 * @property {string} site_id - site_id
 * @property {number} ts - ts
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} version - version
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {GUID} esi - esi
 * @property {GUID} msid - msid
 * @property {string} page_url - pages_url
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<wixCodeViewExampleClickParams>} LogObject<wixCodeViewExampleClickParams>
 */
export function wixCodeViewExampleClick(params) {
    return { evid: 130, src: 83, endpoint: '', params };
}
/**
 * A function to create a log object for the 'wixCodeEditExampleClick' event (evid: 131)
 *
 * Event description: Shoots when a user click on open editorexample
 *
 * @param {wixCodeEditExampleClickParams} params - The parameters for this event
 * @property {string} example_name - example_name
 * @property {GUID} session_id - mini site session id
 * @property {Url} referral_url - referral_url
 * @property {string} site_id - site_id
 * @property {number} ts - ts
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} version - version
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {GUID} esi - esi
 * @property {GUID} msid - msid
 * @property {string} page_url - pages_url
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<wixCodeEditExampleClickParams>} LogObject<wixCodeEditExampleClickParams>
 */
export function wixCodeEditExampleClick(params) {
    return { evid: 131, src: 83, endpoint: '', params };
}
/**
 * A function to create a log object for the 'cmAddRowClicked' event (evid: 132)
 *
 * Event description: CM Add Row
 *
 * @param {cmAddRowClickedParams} params - The parameters for this event
 * @property {string} site_id - site_id
 * @property {number} ts - ts
 * @property {string} collection_id - collection id
 * @property {boolean} is_draft_publish_enabled - (true, false)
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} item_draft_status - item_draft_status
 * @property {boolean} is_shared - is shared
 * @property {string} version - version
 * @property {number} row_number - 2,3,4,5,6,etc...
 * @property {boolean} is_live_data - {Live=true, Sandbox=false}
 * @property {string} origin - PLUS_BUTTON / KEYBOARD_ARROW_DOWN / KEYBOARD_TAB / ITEM_PAGE / new_item_action (one_app), empty_substate_action (one_app)
 * @property {string} environment - (Editor/Dashboard/one_app)
 * @property {boolean} is_sandbox_enabled - (true/false)
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {GUID} esi - esi
 * @property {string} collection_name - It's a collection ID in One App.
 * @property {GUID} msid - msid
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<cmAddRowClickedParams>} LogObject<cmAddRowClickedParams>
 */
export function cmAddRowClicked(params) {
    return { evid: 132, src: 83, endpoint: '', params };
}
/**
 * A function to create a log object for the 'cmInsertedDataToCell' event (evid: 133)
 *
 * Event description: CM Insert Data to Field
 *
 * @param {cmInsertedDataToCellParams} params - The parameters for this event
 * @property {string} renderingFlow - Rendering flow
 * @property {string} site_id - site_id
 * @property {string} field_key - field_key
 * @property {string} field_type - text,image,etc...
 * @property {number} ts - ts
 * @property {string} collection_id - collection_id
 * @property {boolean} is_draft_publish_enabled - (true, false)
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} item_draft_status - (draft, published, scheduled_to_publish, scheduled_to_draft)
 * @property {boolean} is_shared - is_shared
 * @property {string} version - version
 * @property {boolean} is_live_data - {Live=true, Sandbox=false}
 * @property {string} origin - site structure, add menu, etc... In One App, the origins are "manage_tab" and "platforms_tab".
 * @property {string} environment - (Editor/Dashboard/one_app)
 * @property {boolean} is_sandbox_enabled - (true/false)
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {GUID} esi - esi
 * @property {boolean} is_single_item_collection - is single item collection
 * @property {string} collection_name - collection name
 * @property {GUID} msid - msid
 * @property {string} field_name - field_name
 * @property {string} item_id - ID of the edited item in the collection
 * @return {LogObject<cmInsertedDataToCellParams>} LogObject<cmInsertedDataToCellParams>
 */
export function cmInsertedDataToCell(params) {
    return { evid: 133, src: 83, endpoint: '', params };
}
/**
 * A function to create a log object for the 'wixCodeCodePaneVideoPlayStatus' event (evid: 134)
 *
 * Event description: Video play status - Code Pan will send the event, it can be under users or anonymous .
 * It will be sent 3 times at start, 20 seconds and end
 *
 * @param {wixCodeCodePaneVideoPlayStatusParams} params - The parameters for this event
 * @property {GUID} session_id - session_id
 * @property {string} site_id - site_id
 * @property {number} ts - ts
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} video_name - video_name
 * @property {string} version - version
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {string} video_status - start, 20 seconds, end
 * @property {GUID} esi - esi
 * @property {GUID} msid - msid
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<wixCodeCodePaneVideoPlayStatusParams>} LogObject<wixCodeCodePaneVideoPlayStatusParams>
 */
export function wixCodeCodePaneVideoPlayStatus(params) {
    return { evid: 134, src: 83, endpoint: '', params };
}
/**
 * A function to create a log object for the 'cmFieldPopupMoveToTrashClicked' event (evid: 135)
 *
 * Event description: CM Field move to trash start
 *
 * @param {cmFieldPopupMoveToTrashClickedParams} params - The parameters for this event
 * @property {string} site_id - site_id
 * @property {string} field_type - field_type
 * @property {number} ts - ts
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} version - version
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {GUID} esi - esi
 * @property {string} collection_name - collection_name
 * @property {GUID} msid - msid
 * @property {string} field_name - field_name
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<cmFieldPopupMoveToTrashClickedParams>} LogObject<cmFieldPopupMoveToTrashClickedParams>
 */
export function cmFieldPopupMoveToTrashClicked(params) {
    return { evid: 135, src: 83, endpoint: '', params };
}
/**
 * A function to create a log object for the 'cmFieldMovedToTrash' event (evid: 136)
 *
 * Event description: CM Field move to trash confirmed
 *
 * @param {cmFieldMovedToTrashParams} params - The parameters for this event
 * @property {string} site_id - site_id
 * @property {string} field_type - field_type
 * @property {number} ts - ts
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} version - version
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {GUID} esi - esi
 * @property {string} collection_name - collection_name
 * @property {GUID} msid - msid
 * @property {string} field_name - field_name
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<cmFieldMovedToTrashParams>} LogObject<cmFieldMovedToTrashParams>
 */
export function cmFieldMovedToTrash(params) {
    return { evid: 136, src: 83, endpoint: '', params };
}
/**
 * A function to create a log object for the 'cmFieldMoveToTrashDialogCanceled' event (evid: 137)
 *
 * Event description: CM Field move to trash canceled
 *
 * @param {cmFieldMoveToTrashDialogCanceledParams} params - The parameters for this event
 * @property {string} site_id - site_id
 * @property {string} field_type - field_type
 * @property {number} ts - ts
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} version - version
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {GUID} esi - esi
 * @property {string} collection_name - collection_name
 * @property {GUID} msid - msid
 * @property {string} field_name - field_name
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<cmFieldMoveToTrashDialogCanceledParams>} LogObject<cmFieldMoveToTrashDialogCanceledParams>
 */
export function cmFieldMoveToTrashDialogCanceled(params) {
    return { evid: 137, src: 83, endpoint: '', params };
}
/**
 * A function to create a log object for the 'cmMenuDeletedFieldsClicked' event (evid: 138)
 *
 * Event description: CM Deleted fields menu clicked
 *
 * @param {cmMenuDeletedFieldsClickedParams} params - The parameters for this event
 * @property {string} site_id - site_id
 * @property {string} field_type - field_type
 * @property {number} ts - ts
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} version - version
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {GUID} esi - esi
 * @property {string} collection_name - collection_name
 * @property {GUID} msid - msid
 * @property {string} field_name - field_name
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<cmMenuDeletedFieldsClickedParams>} LogObject<cmMenuDeletedFieldsClickedParams>
 */
export function cmMenuDeletedFieldsClicked(params) {
    return { evid: 138, src: 83, endpoint: '', params };
}
/**
 * A function to create a log object for the 'cmFieldRestored' event (evid: 139)
 *
 * Event description: CM Field restored
 *
 * @param {cmFieldRestoredParams} params - The parameters for this event
 * @property {string} site_id - site_id
 * @property {string} field_type - field_type
 * @property {number} ts - ts
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} version - version
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {GUID} esi - esi
 * @property {string} collection_name - collection_name
 * @property {GUID} msid - msid
 * @property {string} field_name - field_name
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<cmFieldRestoredParams>} LogObject<cmFieldRestoredParams>
 */
export function cmFieldRestored(params) {
    return { evid: 139, src: 83, endpoint: '', params };
}
/**
 * A function to create a log object for the 'cmsAddSpecificRoleSrc83Evid14' event (evid: 14)
 *
 * Event description: User interactions with CMS 'Add roles' model for collection permissions
 *
 * @param {cmsAddSpecificRoleSrc83Evid14Params} params - The parameters for this event
 * @property {string} action - select values\ search\ roles_and_permissions\ save\ cancel
 * @property {string} collection_id - collection_id
 * @property {string} collection_name - collection_name
 * @property {string} origin - origin
 * @property {string} selected_roles - string of selected roles
 * @property {string} is_predefind_role - yes- if user selected one of the predefined roles. o- if a user selected a role he created on his own by roles & permissions settings
 * @property {string} environment - editor\ business_manager
 * @return {LogObject<cmsAddSpecificRoleSrc83Evid14Params>} LogObject<cmsAddSpecificRoleSrc83Evid14Params>
 */
export function cmsAddSpecificRoleSrc83Evid14(params) {
    return { evid: 14, src: 83, endpoint: '', params };
}
/**
 * A function to create a log object for the 'cmChangeDataViewClicked' event (evid: 140)
 *
 * Event description: when the user changes collection view to Live/Sandbox
 *
 * @param {cmChangeDataViewClickedParams} params - The parameters for this event
 * @property {string} site_id - site_id
 * @property {string} selected_view - (sandbox/live)
 * @property {number} ts - ts
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} version - version
 * @property {string} environment - (Editor/Dashboard)
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {GUID} esi - esi
 * @property {string} collection_name - collection_name
 * @property {GUID} msid - msid
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<cmChangeDataViewClickedParams>} LogObject<cmChangeDataViewClickedParams>
 */
export function cmChangeDataViewClicked(params) {
    return { evid: 140, src: 83, endpoint: '', params };
}
/**
 * A function to create a log object for the 'cmRefreshed' event (evid: 141)
 *
 * Event description: CM Refreshed
 *
 * @param {cmRefreshedParams} params - The parameters for this event
 * @property {string} site_id - site_id
 * @property {number} ts - ts
 * @property {string} collection_id - collection id
 * @property {string} viewmode - viewmode
 * @property {string} input_name - (save_conflict, update_notification)
 * @property {string} version - version
 * @property {string} environment - (Editor/Dashboard)
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {GUID} esi - esi
 * @property {string} collection_name - collection_name
 * @property {GUID} msid - msid
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<cmRefreshedParams>} LogObject<cmRefreshedParams>
 */
export function cmRefreshed(params) {
    return { evid: 141, src: 83, endpoint: '', params };
}
/**
 * A function to create a log object for the 'cmFieldSetAsPrimary' event (evid: 142)
 *
 * Event description: CM Field set as primary
 *
 * @param {cmFieldSetAsPrimaryParams} params - The parameters for this event
 * @property {string} site_id - site_id
 * @property {string} field_type - fieldType
 * @property {number} ts - ts
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} version - version
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {GUID} esi - esi
 * @property {string} collection_name - collection_name
 * @property {GUID} msid - msid
 * @property {string} field_name - field_name
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<cmFieldSetAsPrimaryParams>} LogObject<cmFieldSetAsPrimaryParams>
 */
export function cmFieldSetAsPrimary(params) {
    return { evid: 142, src: 83, endpoint: '', params };
}
/**
 * A function to create a log object for the 'cmSortAdded' event (evid: 144)
 *
 * Event description: CM Menu Sort Added
 *
 * @param {cmSortAddedParams} params - The parameters for this event
 * @property {string} site_id - site_id
 * @property {string} field_type - field_type
 * @property {number} ts - ts
 * @property {string} collection_id - collection id
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {number} number_of_filter_sort - number_of_filter_sort
 * @property {GUID} view_id - view id
 * @property {string} version - version
 * @property {boolean} is_live_data - (true/false)
 * @property {string} origin - site structure, add menu, etc...
 * @property {string} environment - (Editor/Dashboard)
 * @property {boolean} is_sandbox_enabled - (true/false)
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {GUID} esi - esi
 * @property {string} collection_name - collection name
 * @property {GUID} msid - msid
 * @property {string} field_name - field_name
 * @property {string} view - view
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<cmSortAddedParams>} LogObject<cmSortAddedParams>
 */
export function cmSortAdded(params) {
    return { evid: 144, src: 83, endpoint: '', params };
}
/**
 * A function to create a log object for the 'cmCollectionRestored' event (evid: 147)
 *
 * Event description: CM Collection restored
 *
 * @param {cmCollectionRestoredParams} params - The parameters for this event
 * @property {number} num_of_reference_fields_to_collection - {}
 * @property {string} site_id - site_id
 * @property {number} ts - ts
 * @property {string} collection_id - collection id
 * @property {string} viewmode - viewmode
 * @property {string} input_name - {}
 * @property {string} version - version
 * @property {string} collection_type - collection_type
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {GUID} esi - {}
 * @property {string} collection_name - collection_name
 * @property {GUID} msid - {}
 * @property {number} num_of_connected_dynamic_pages - {}
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<cmCollectionRestoredParams>} LogObject<cmCollectionRestoredParams>
 */
export function cmCollectionRestored(params) {
    return { evid: 147, src: 83, endpoint: '', params };
}
/**
 * A function to create a log object for the 'removeCollectionDialogClicked' event (evid: 148)
 *
 * Event description: CM Collection Removed
 *
 * @param {removeCollectionDialogClickedParams} params - The parameters for this event
 * @property {number} num_of_reference_fields_to_collection - {}
 * @property {string} site_id - site_id
 * @property {number} ts - ts
 * @property {string} collection_id - collection id
 * @property {string} viewmode - viewmode
 * @property {string} input_name - {}
 * @property {string} version - version
 * @property {string} collection_type - {}
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {string} category - {}
 * @property {GUID} esi - {}
 * @property {string} collection_name - {}
 * @property {GUID} msid - {}
 * @property {number} num_of_connected_dynamic_pages - {}
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<removeCollectionDialogClickedParams>} LogObject<removeCollectionDialogClickedParams>
 */
export function removeCollectionDialogClicked(params) {
    return { evid: 148, src: 83, endpoint: '', params };
}
/**
 * A function to create a log object for the 'cmCollectionPermissionsSavedSrc83Evid15' event (evid: 15)
 *
 * Event description: When a user hits 'save' on collection 'permissions& privacy'  modal
 *
 * @param {cmCollectionPermissionsSavedSrc83Evid15Params} params - The parameters for this event
 * @property {string} collection_id - collection_id
 * @property {string} origin - cm_permissions_page\ databinding right panel etc.
 * @property {string} environment - environment
 * @property {string} permissions - json with all defined permissions
 * @property {string} permissions_type - show_content\ collect_content\ advavnced
 * @property {string} collection_name - collection_name
 * @property {string} old_permissions - previous permissions settings before user saved settings
 * @property {string} collection_type - collection_type
 * @property {boolean} is_using_custom_role - if including a custom role created by the user then 'yes' else 'no'
 * @return {LogObject<cmCollectionPermissionsSavedSrc83Evid15Params>} LogObject<cmCollectionPermissionsSavedSrc83Evid15Params>
 */
export function cmCollectionPermissionsSavedSrc83Evid15(params) {
    return { evid: 15, src: 83, endpoint: '', params };
}
/**
 * A function to create a log object for the 'cmCellDataDeletedSrc83Evid150' event (evid: 150)
 *
 * Event description: When a user clicks on the "selected all items" button (checkbox)
 *
 * @param {cmCellDataDeletedSrc83Evid150Params} params - The parameters for this event
 * @property {string} site_id - site_id
 * @property {string} field_key - deprecated - please ignore it
 * @property {string} field_type - deprecated - please ignore it
 * @property {number} ts - ts
 * @property {string} collection_id - collection_id
 * @property {string} viewmode - viewmode
 * @property {string} input_name - deprecated - please ignore it
 * @property {string} number_of_item - the number of the selected items the user selected
 * @property {string} version - version
 * @property {string} environment - environment {EDITOR/ BUSINESS_MANAGER}
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {GUID} esi - esi
 * @property {string} collection_name - collection name
 * @property {GUID} msid - msid
 * @property {string} field_name - deprecated - please ignore it
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<cmCellDataDeletedSrc83Evid150Params>} LogObject<cmCellDataDeletedSrc83Evid150Params>
 */
export function cmCellDataDeletedSrc83Evid150(params) {
    return { evid: 150, src: 83, endpoint: '', params };
}
/**
 * A function to create a log object for the 'cmItemAction' event (evid: 151)
 *
 * Event description: CM Cell Data Deleted
 *
 * @param {cmItemActionParams} params - The parameters for this event
 * @property {string} site_id - site_id
 * @property {number} ts - ts
 * @property {string} collection_id - collection id
 * @property {string} viewmode - viewmode
 * @property {string} input_name - deprecated - please ignore it
 * @property {string} number_of_item - number_of_item, in One App it's currently always 1 because only one item can be removed at a time
 * @property {string} version - version
 * @property {boolean} is_live_data - {Live=true, Sandbox=false}
 * @property {string} origin - ITEM_PAGE, RIGHT_CLICK_MENU, collection_item_list_screen (one_app)
 * @property {string} environment - EDITOR, BUSINESS_MANAGER, one_app
 * @property {boolean} is_sandbox_enabled - (true/false)
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {GUID} esi - esi
 * @property {string} collection_name - In One App, it's a collection ID
 * @property {GUID} msid - msid
 * @property {string} action - Copied, Deleted, Duplicated
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<cmItemActionParams>} LogObject<cmItemActionParams>
 */
export function cmItemAction(params) {
    return { evid: 151, src: 83, endpoint: '', params };
}
/**
 * A function to create a log object for the 'apiSearchReturnsZeroResults' event (evid: 152)
 *
 * Event description: Triggers when API search returns nothing
 *
 * @param {apiSearchReturnsZeroResultsParams} params - The parameters for this event
 * @property {string} site_id - site_id
 * @property {number} ts - ts
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} version - version
 * @property {string} search_term - search term
 * @property {GUID} vsi - vsi
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {GUID} esi - esi
 * @property {GUID} msid - msid
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<apiSearchReturnsZeroResultsParams>} LogObject<apiSearchReturnsZeroResultsParams>
 */
export function apiSearchReturnsZeroResults(params) {
    return { evid: 152, src: 83, endpoint: '', params };
}
/**
 * A function to create a log object for the 'copyCodeFromDocsPageClicked' event (evid: 153)
 *
 * Event description: Shoots when a user clicks on "copy code" button in a wix code resource
 *
 * @param {copyCodeFromDocsPageClickedParams} params - The parameters for this event
 * @property {string} example_name - example_name
 * @property {GUID} session_id - session_id
 * @property {Url} referral_url - {}
 * @property {string} site_id - site_id
 * @property {string} file_name - file_name
 * @property {number} ts - ts
 * @property {string} viewmode - viewmode
 * @property {string} input_name - {}
 * @property {string} version - version
 * @property {GUID} vsi - {}
 * @property {string} origin - {}
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {GUID} esi - esi
 * @property {GUID} msid - {}
 * @property {string} page_url - page_url
 * @property {string} level - user level: {All Levels, Beginner, Intermediate, Advanced}
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<copyCodeFromDocsPageClickedParams>} LogObject<copyCodeFromDocsPageClickedParams>
 */
export function copyCodeFromDocsPageClicked(params) {
    return { evid: 153, src: 83, endpoint: '', params };
}
/**
 * A function to create a log object for the 'cmMenuImportExportClicked' event (evid: 154)
 *
 * Event description: CM Menu Import/Export Clicked
 *
 * @param {cmMenuImportExportClickedParams} params - The parameters for this event
 * @property {string} site_id - site_id
 * @property {number} ts - ts
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} version - version
 * @property {boolean} is_live_data - {Live=true, Sandbox=false}
 * @property {string} environment - environment
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {GUID} esi - esi
 * @property {string} collection_name - collection name
 * @property {GUID} msid - msid
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<cmMenuImportExportClickedParams>} LogObject<cmMenuImportExportClickedParams>
 */
export function cmMenuImportExportClicked(params) {
    return { evid: 154, src: 83, endpoint: '', params };
}
/**
 * A function to create a log object for the 'cmMenuImportExportImportClicked' event (evid: 155)
 *
 * Event description: CM Menu Import/Export Import Clicked
 *
 * @param {cmMenuImportExportImportClickedParams} params - The parameters for this event
 * @property {string} site_id - site_id
 * @property {number} ts - ts
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} version - version
 * @property {boolean} is_live_data - {Live=true, Sandbox=false}
 * @property {string} environment - environment
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {GUID} esi - esi
 * @property {string} collection_name - collection name
 * @property {GUID} msid - msid
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<cmMenuImportExportImportClickedParams>} LogObject<cmMenuImportExportImportClickedParams>
 */
export function cmMenuImportExportImportClicked(params) {
    return { evid: 155, src: 83, endpoint: '', params };
}
/**
 * A function to create a log object for the 'cmImportSelectFileUploadClicked' event (evid: 156)
 *
 * Event description: CM Import Select File Upload Clicked
 *
 * @param {cmImportSelectFileUploadClickedParams} params - The parameters for this event
 * @property {string} site_id - site_id
 * @property {number} ts - ts
 * @property {string} collection_id - collection id
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} version - version
 * @property {boolean} is_live_data - {Live=true, Sandbox=false}
 * @property {string} environment - environment
 * @property {boolean} is_sandbox_enabled - (true/false)
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {GUID} esi - esi
 * @property {string} collection_name - collection name
 * @property {GUID} msid - msid
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<cmImportSelectFileUploadClickedParams>} LogObject<cmImportSelectFileUploadClickedParams>
 */
export function cmImportSelectFileUploadClicked(params) {
    return { evid: 156, src: 83, endpoint: '', params };
}
/**
 * A function to create a log object for the 'cmImportSelectFileErrorShown' event (evid: 157)
 *
 * Event description: CM Import Select File Error Shown
 *
 * @param {cmImportSelectFileErrorShownParams} params - The parameters for this event
 * @property {string} site_id - site_id
 * @property {string} file_name - file_name
 * @property {number} file_size - file_size
 * @property {number} ts - ts
 * @property {string} collection_id - collection id
 * @property {string} viewmode - viewmode
 * @property {number} number_of_rows - number_of_rows
 * @property {string} input_name - input_name
 * @property {string} version - version
 * @property {string} error_type - error_type (file_too_large, too_many_columns, too_many_rows, parsing_error)
 * @property {string} number_rows - number_rows(deprecated)
 * @property {boolean} is_live_data - {Live=true, Sandbox=false}
 * @property {string} environment - environment
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {GUID} esi - esi
 * @property {string} collection_name - collection name
 * @property {GUID} msid - msid
 * @property {number} number_of_columns - number_of_columns
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<cmImportSelectFileErrorShownParams>} LogObject<cmImportSelectFileErrorShownParams>
 */
export function cmImportSelectFileErrorShown(params) {
    return { evid: 157, src: 83, endpoint: '', params };
}
/**
 * A function to create a log object for the 'cmImportSelectFileNextClicked' event (evid: 158)
 *
 * Event description: CM Import Select File Next Clicked
 *
 * @param {cmImportSelectFileNextClickedParams} params - The parameters for this event
 * @property {string} site_id - site_id
 * @property {string} file_name - file_name
 * @property {number} file_size - file_size
 * @property {number} ts - ts
 * @property {string} collection_id - collection id
 * @property {string} viewmode - viewmode
 * @property {number} number_of_rows - number_of_rows
 * @property {string} input_name - input_name
 * @property {string} version - version
 * @property {boolean} is_live_data - {Live=true, Sandbox=false}
 * @property {string} environment - environment
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {GUID} esi - esi
 * @property {string} collection_name - collection name
 * @property {GUID} msid - msid
 * @property {number} number_of_columns - number_of_columns
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<cmImportSelectFileNextClickedParams>} LogObject<cmImportSelectFileNextClickedParams>
 */
export function cmImportSelectFileNextClicked(params) {
    return { evid: 158, src: 83, endpoint: '', params };
}
/**
 * A function to create a log object for the 'cmImportConfigColumnClicked' event (evid: 159)
 *
 * Event description: CM Import Config Column Clicked
 *
 * @param {cmImportConfigColumnClickedParams} params - The parameters for this event
 * @property {string} site_id - site_id
 * @property {string} column_name - column_name
 * @property {number} ts - ts
 * @property {string} collection_id - collection id
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} version - version
 * @property {boolean} is_live_data - {Live=true, Sandbox=false}
 * @property {string} environment - environment
 * @property {boolean} is_sandbox_enabled - (true/false)
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {GUID} esi - esi
 * @property {string} collection_name - collection name
 * @property {GUID} msid - msid
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<cmImportConfigColumnClickedParams>} LogObject<cmImportConfigColumnClickedParams>
 */
export function cmImportConfigColumnClicked(params) {
    return { evid: 159, src: 83, endpoint: '', params };
}
/**
 * A function to create a log object for the 'cmCollectionPermissionsModalCancelledSrc83Evid16' event (evid: 16)
 *
 * Event description: When a user hits 'cancel' on collection 'permissions& privacy' modal
 *
 * @param {cmCollectionPermissionsModalCancelledSrc83Evid16Params} params - The parameters for this event
 * @property {string} collection_id - collection_id
 * @property {string} origin - cm_permissions_page\ databinding right panel etc.
 * @property {string} environment - environment
 * @property {string} permissions - json with all defined permissions
 * @property {string} permissions_type - show_content\ collect_content\ advavnced
 * @property {string} collection_name - collection_name
 * @property {string} collection_type - collection_type
 * @property {boolean} is_using_custom_role - if including a custom role created by the user then 'yes' else 'no'
 * @return {LogObject<cmCollectionPermissionsModalCancelledSrc83Evid16Params>} LogObject<cmCollectionPermissionsModalCancelledSrc83Evid16Params>
 */
export function cmCollectionPermissionsModalCancelledSrc83Evid16(params) {
    return { evid: 16, src: 83, endpoint: '', params };
}
/**
 * A function to create a log object for the 'cmImportConfigColumnToggled' event (evid: 160)
 *
 * Event description: CM Import Config Column Toggled
 *
 * @param {cmImportConfigColumnToggledParams} params - The parameters for this event
 * @property {string} site_id - site_id
 * @property {string} column_name - column_name
 * @property {number} ts - ts
 * @property {string} collection_id - collection id
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} version - version
 * @property {boolean} is_live_data - {Live=true, Sandbox=false}
 * @property {string} environment - environment
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {GUID} esi - esi
 * @property {string} collection_name - collection name
 * @property {GUID} msid - msid
 * @property {string} value - (on/off)
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<cmImportConfigColumnToggledParams>} LogObject<cmImportConfigColumnToggledParams>
 */
export function cmImportConfigColumnToggled(params) {
    return { evid: 160, src: 83, endpoint: '', params };
}
/**
 * A function to create a log object for the 'cmImportConfigColumnImportTypeChanged' event (evid: 161)
 *
 * Event description: CM Import Config Column Import Type Changed
 *
 * @param {cmImportConfigColumnImportTypeChangedParams} params - The parameters for this event
 * @property {string} site_id - site_id
 * @property {string} column_name - column_name
 * @property {number} ts - ts
 * @property {string} collection_id - collection id
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} version - version
 * @property {string} import_type - (map_to_existing, import_as_new)
 * @property {boolean} is_live_data - {Live=true, Sandbox=false}
 * @property {string} environment - environment
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {GUID} esi - esi
 * @property {string} collection_name - collection name
 * @property {GUID} msid - msid
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<cmImportConfigColumnImportTypeChangedParams>} LogObject<cmImportConfigColumnImportTypeChangedParams>
 */
export function cmImportConfigColumnImportTypeChanged(params) {
    return { evid: 161, src: 83, endpoint: '', params };
}
/**
 * A function to create a log object for the 'cmImportConfigNextClicked' event (evid: 162)
 *
 * Event description: CM Import Config Next Clicked
 *
 * @param {cmImportConfigNextClickedParams} params - The parameters for this event
 * @property {string} action_of_matching - {}
 * @property {string} site_id - site_id
 * @property {string} file_name - {}
 * @property {number} file_size - {}
 * @property {number} ts - ts
 * @property {string} collection_id - collection id
 * @property {boolean} is_mapping_to_id - {}
 * @property {string} viewmode - viewmode
 * @property {number} number_of_rows - {}
 * @property {string} input_name - input_name
 * @property {string} version - version
 * @property {boolean} is_live_data - {Live=true, Sandbox=false}
 * @property {string} environment - environment
 * @property {boolean} is_sandbox_enabled - (true/false)
 * @property {string} userEnv - user_env
 * @property {number} number_of_columns_to_import - {}
 * @property {string} editor_working_mode - editor_working_mode
 * @property {GUID} esi - esi
 * @property {string} new_fields - field_json
 * @property {string} collection_name - collection name
 * @property {GUID} msid - msid
 * @property {number} number_of_columns - {}
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<cmImportConfigNextClickedParams>} LogObject<cmImportConfigNextClickedParams>
 */
export function cmImportConfigNextClicked(params) {
    return { evid: 162, src: 83, endpoint: '', params };
}
/**
 * A function to create a log object for the 'cmImportConfigBackClicked' event (evid: 163)
 *
 * Event description: CM Import Config Back Clicked
 *
 * @param {cmImportConfigBackClickedParams} params - The parameters for this event
 * @property {string} site_id - site_id
 * @property {number} ts - ts
 * @property {string} collection_id - collection id
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} version - version
 * @property {boolean} is_live_data - {Live=true, Sandbox=false}
 * @property {string} environment - environment
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {GUID} esi - esi
 * @property {string} collection_name - collection name
 * @property {GUID} msid - msid
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<cmImportConfigBackClickedParams>} LogObject<cmImportConfigBackClickedParams>
 */
export function cmImportConfigBackClicked(params) {
    return { evid: 163, src: 83, endpoint: '', params };
}
/**
 * A function to create a log object for the 'cmImportReviewImportClicked' event (evid: 164)
 *
 * Event description: CM Import Review Import Clicked
 *
 * @param {cmImportReviewImportClickedParams} params - The parameters for this event
 * @property {string} action_of_matching - (update,skip)
 * @property {string} site_id - site_id
 * @property {string} file_name - file_name
 * @property {number} file_size - file_size
 * @property {number} ts - ts
 * @property {string} collection_id - collection id
 * @property {boolean} is_mapping_to_id - is_mapping_to_id
 * @property {string} viewmode - viewmode
 * @property {number} number_of_rows - number_of_rows
 * @property {string} input_name - input_name
 * @property {string} version - version
 * @property {boolean} is_live_data - {Live=true, Sandbox=false}
 * @property {string} environment - environment
 * @property {boolean} is_sandbox_enabled - (true/false)
 * @property {string} userEnv - user_env
 * @property {number} number_of_columns_to_import - {}
 * @property {string} editor_working_mode - editor_working_mode
 * @property {GUID} esi - esi
 * @property {string} new_fields - field_json
 * @property {string} collection_name - collection name
 * @property {GUID} msid - msid
 * @property {number} number_of_columns - number_of_columns
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<cmImportReviewImportClickedParams>} LogObject<cmImportReviewImportClickedParams>
 */
export function cmImportReviewImportClicked(params) {
    return { evid: 164, src: 83, endpoint: '', params };
}
/**
 * A function to create a log object for the 'cmImportReviewBackClicked' event (evid: 165)
 *
 * Event description: CM Import Review Back Clicked
 *
 * @param {cmImportReviewBackClickedParams} params - The parameters for this event
 * @property {string} site_id - site_id
 * @property {number} ts - ts
 * @property {string} collection_id - collection id
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} version - version
 * @property {boolean} is_live_data - {Live=true, Sandbox=false}
 * @property {string} environment - environment
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {GUID} esi - esi
 * @property {string} collection_name - collection name
 * @property {GUID} msid - msid
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<cmImportReviewBackClickedParams>} LogObject<cmImportReviewBackClickedParams>
 */
export function cmImportReviewBackClicked(params) {
    return { evid: 165, src: 83, endpoint: '', params };
}
/**
 * A function to create a log object for the 'cmImportErrorShown' event (evid: 166)
 *
 * Event description: CM Import Error Shown
 *
 * @param {cmImportErrorShownParams} params - The parameters for this event
 * @property {string} action_of_matching - (update,skip)
 * @property {string} site_id - site_id
 * @property {string} file_name - file_name
 * @property {number} file_size - file_size
 * @property {number} ts - ts
 * @property {string} collection_id - collection id
 * @property {boolean} is_mapping_to_id - is_mapping_to_id
 * @property {string} viewmode - viewmode
 * @property {number} number_of_rows - number_of_rows
 * @property {string} input_name - input_name
 * @property {string} version - version
 * @property {boolean} is_live_data - {Live=true, Sandbox=false}
 * @property {string} environment - environment
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {string} error_message - error_message
 * @property {GUID} esi - esi
 * @property {string} collection_name - collection name
 * @property {GUID} msid - msid
 * @property {number} number_of_columns - number_of_columns
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<cmImportErrorShownParams>} LogObject<cmImportErrorShownParams>
 */
export function cmImportErrorShown(params) {
    return { evid: 166, src: 83, endpoint: '', params };
}
/**
 * A function to create a log object for the 'cmImportFinished' event (evid: 167)
 *
 * Event description: CM Import Finished
 *
 * @param {cmImportFinishedParams} params - The parameters for this event
 * @property {string} action_of_matching - {}
 * @property {string} site_id - site_id
 * @property {string} file_name - {}
 * @property {number} file_size - {}
 * @property {number} ts - ts
 * @property {string} collection_id - collection id
 * @property {boolean} is_mapping_to_id - {}
 * @property {string} viewmode - viewmode
 * @property {number} number_of_rows - {}
 * @property {string} input_name - input_name
 * @property {string} version - version
 * @property {boolean} is_live_data - {Live=true, Sandbox=false}
 * @property {string} environment - environment
 * @property {boolean} is_sandbox_enabled - (true/false)
 * @property {string} userEnv - user_env
 * @property {number} number_of_columns_to_import - {}
 * @property {string} editor_working_mode - editor_working_mode
 * @property {GUID} esi - esi
 * @property {string} new_fields - field_json
 * @property {string} collection_name - collection name
 * @property {GUID} msid - msid
 * @property {number} number_of_columns - {}
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<cmImportFinishedParams>} LogObject<cmImportFinishedParams>
 */
export function cmImportFinished(params) {
    return { evid: 167, src: 83, endpoint: '', params };
}
/**
 * A function to create a log object for the 'cmMenuImportExportExportClicked' event (evid: 168)
 *
 * Event description: CM Export Option Selected
 *
 * @param {cmMenuImportExportExportClickedParams} params - The parameters for this event
 * @property {string} export_type - all / schema / filtered
 * @property {string} site_id - site_id
 * @property {number} ts - ts
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} version - version
 * @property {boolean} is_live_data - {Live=true, Sandbox=false}
 * @property {string} environment - environment
 * @property {boolean} is_sandbox_enabled - is_sandbox_enabled
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {GUID} esi - esi
 * @property {string} collection_name - collection name
 * @property {GUID} msid - msid
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<cmMenuImportExportExportClickedParams>} LogObject<cmMenuImportExportExportClickedParams>
 */
export function cmMenuImportExportExportClicked(params) {
    return { evid: 168, src: 83, endpoint: '', params };
}
/**
 * A function to create a log object for the 'cmExportClicked' event (evid: 169)
 *
 * Event description: CM Export Clicked
 *
 * @param {cmExportClickedParams} params - The parameters for this event
 * @property {string} export_type - (all, filtered, schema)
 * @property {string} site_id - site_id
 * @property {number} ts - ts
 * @property {string} collection_id - collection id
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {boolean} is_shared - is_shared
 * @property {string} version - version
 * @property {boolean} is_live_data - {Live=true, Sandbox=false}
 * @property {string} environment - environment
 * @property {boolean} is_sandbox_enabled - (true/false)
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {GUID} esi - esi
 * @property {string} collection_name - collection name
 * @property {GUID} msid - msid
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<cmExportClickedParams>} LogObject<cmExportClickedParams>
 */
export function cmExportClicked(params) {
    return { evid: 169, src: 83, endpoint: '', params };
}
/**
 * A function to create a log object for the 'collectionPermissionsDeleteCustomRoleSrc83Evid17' event (evid: 17)
 *
 * Event description: When a user removes a specific role in 'Advanced' collection permissions modal
 *
 * @param {collectionPermissionsDeleteCustomRoleSrc83Evid17Params} params - The parameters for this event
 * @property {string} collection_id - collection_id
 * @property {string} origin - origin
 * @property {string} role_name - the name of the deleted_role
 * @property {string} permissions - a json string of the deleted role's permissions
 * @property {string} collection_name - collection_name
 * @property {string} is_custom_role - if this is a custom role then true else false
 * @property {string} permissions_type - the name of the tab in the permissions modal
 * @return {LogObject<collectionPermissionsDeleteCustomRoleSrc83Evid17Params>} LogObject<collectionPermissionsDeleteCustomRoleSrc83Evid17Params>
 */
export function collectionPermissionsDeleteCustomRoleSrc83Evid17(params) {
    return { evid: 17, src: 83, endpoint: '', params };
}
/**
 * A function to create a log object for the 'cmEditLiveDataClicked' event (evid: 171)
 *
 * Event description: CM Edit Live Data Clicked
 *
 * @param {cmEditLiveDataClickedParams} params - The parameters for this event
 * @property {string} site_id - site_id
 * @property {number} ts - ts
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} version - version
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {GUID} esi - esi
 * @property {string} collection_name - collection name
 * @property {GUID} msid - msid
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<cmEditLiveDataClickedParams>} LogObject<cmEditLiveDataClickedParams>
 */
export function cmEditLiveDataClicked(params) {
    return { evid: 171, src: 83, endpoint: '', params };
}
/**
 * A function to create a log object for the 'firstContentManagerPanelOpened' event (evid: 172)
 *
 * Event description: This panel opens on the first time a user adds Wix Data to the site. This panel allows a choice between starting with a preset and a normal collection
 *
 * @param {firstContentManagerPanelOpenedParams} params - The parameters for this event
 * @property {string} site_id - site_id
 * @property {GUID} uuid - {uuid}
 * @property {number} ts - ts
 * @property {string} panel_name - panel name
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} version - version
 * @property {string} origin - {add_panel_teaser, data_panel, pagesPanel}
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {GUID} esi - {esi}
 * @property {GUID} msid - {msid}
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<firstContentManagerPanelOpenedParams>} LogObject<firstContentManagerPanelOpenedParams>
 */
export function firstContentManagerPanelOpened(params) {
    return { evid: 172, src: 83, endpoint: '', params };
}
/**
 * A function to create a log object for the 'firstContentManagerPanelAction' event (evid: 173)
 *
 * Event description: This panel opens on the first time a user adds Wix Data to the site. This panel allows a choice between starting with a preset and a normal collection
 *
 * @param {firstContentManagerPanelActionParams} params - The parameters for this event
 * @property {string} site_id - site_id
 * @property {GUID} uuid - {uuid}
 * @property {number} ts - ts
 * @property {string} option - {preset, collection}
 * @property {string} panel_name - panel name
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} version - version
 * @property {string} origin - origin
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {GUID} esi - {esi}
 * @property {GUID} msid - {msid}
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<firstContentManagerPanelActionParams>} LogObject<firstContentManagerPanelActionParams>
 */
export function firstContentManagerPanelAction(params) {
    return { evid: 173, src: 83, endpoint: '', params };
}
/**
 * A function to create a log object for the 'collectionChanged' event (evid: 174)
 *
 * Event description: Fires when the content of a collection is changed from the default content by a user / UoU
 *
 * @param {collectionChangedParams} params - The parameters for this event
 * @property {string} db_type - (Sandbox/Live)
 * @property {GUID} uuid - uuid
 * @property {string} collection_id - collection id
 * @property {GUID} msid - msid
 * @property {string} action - insert/update/save/remove
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<collectionChangedParams>} LogObject<collectionChangedParams>
 */
export function collectionChanged(params) {
    return { evid: 174, src: 83, endpoint: '', params };
}
/**
 * A function to create a log object for the 'externalCollectionsAddClicked' event (evid: 175)
 *
 * Event description: Fires when the user clicks on the 'add' button in the add external collections panel
 *
 * @param {externalCollectionsAddClickedParams} params - The parameters for this event
 * @property {string} endpoint_url - endpoint url
 * @property {string} site_id - site_id
 * @property {string} collections_namespace - namespace
 * @property {GUID} uuid - uuid
 * @property {number} ts - ts
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} version - version
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {GUID} esi - esi
 * @property {GUID} msid - msid
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<externalCollectionsAddClickedParams>} LogObject<externalCollectionsAddClickedParams>
 */
export function externalCollectionsAddClicked(params) {
    return { evid: 175, src: 83, endpoint: '', params };
}
/**
 * A function to create a log object for the 'externalCollectionsAddedSuccessfullySrc83Evid176' event (evid: 176)
 *
 * Event description: Fires when the endpoint for the external collections is connected successfully
 *
 * @param {externalCollectionsAddedSuccessfullySrc83Evid176Params} params - The parameters for this event
 * @property {string} site_id - site_id
 * @property {GUID} uuid - uuid
 * @property {number} ts - ts
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {number} number_of_collections_exposed - number of collections exposed on endpoint setting
 * @property {string} version - version
 * @property {number} external_collections_added_for_the_first_time - **Deprecated**
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {GUID} esi - esi
 * @property {GUID} msid - msid
 * @property {string} namespace - namespace
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<externalCollectionsAddedSuccessfullySrc83Evid176Params>} LogObject<externalCollectionsAddedSuccessfullySrc83Evid176Params>
 */
export function externalCollectionsAddedSuccessfullySrc83Evid176(params) {
    return { evid: 176, src: 83, endpoint: '', params };
}
/**
 * A function to create a log object for the 'externalCollectionsSettingsChanged' event (evid: 177)
 *
 * Event description: Fires when the user clicks on the 'save' button in the edit external collections panel
 *
 * @param {externalCollectionsSettingsChangedParams} params - The parameters for this event
 * @property {string} endpoint_url - endpoint url
 * @property {string} site_id - site_id
 * @property {string} collections_namespace - namespace
 * @property {GUID} uuid - uuid
 * @property {number} ts - ts
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} version - version
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {GUID} esi - esi
 * @property {GUID} msid - msid
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<externalCollectionsSettingsChangedParams>} LogObject<externalCollectionsSettingsChangedParams>
 */
export function externalCollectionsSettingsChanged(params) {
    return { evid: 177, src: 83, endpoint: '', params };
}
/**
 * A function to create a log object for the 'removeExternalCollectionsClicked' event (evid: 179)
 *
 * Event description: Fires when the user clicks on the 'delete' button in the delete external collections popup panel
 *
 * @param {removeExternalCollectionsClickedParams} params - The parameters for this event
 * @property {string} endpoint_url - endpoint url
 * @property {string} site_id - site_id
 * @property {string} collections_namespace - namespace
 * @property {GUID} uuid - uuid
 * @property {number} ts - ts
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} version - version
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {GUID} esi - esi
 * @property {GUID} msid - msid
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<removeExternalCollectionsClickedParams>} LogObject<removeExternalCollectionsClickedParams>
 */
export function removeExternalCollectionsClicked(params) {
    return { evid: 179, src: 83, endpoint: '', params };
}
/**
 * A function to create a log object for the 'cmDuplicateCollectionSrc83Evid18' event (evid: 18)
 *
 * Event description: When a user duplicates CMS collection
 *
 * @param {cmDuplicateCollectionSrc83Evid18Params} params - The parameters for this event
 * @property {string} collection_id - collection id of the new collection
 * @property {string} origin - origin
 * @property {number} num_of_connected_pages - number of pages connected to the origial collection
 * @property {number} num_of_items - number of items in the original collection
 * @property {number} num_of_fields - number of fields in the original collection
 * @property {string} original_collection_id - the id of the original collection
 * @property {string} collection_name - name of the new collection
 * @property {string} original_collection_name - the name of the original collection
 * @property {string} environment - (Editor/Dashboard/one_app)
 * @property {string} columns - json of columns names & columns types
 * @return {LogObject<cmDuplicateCollectionSrc83Evid18Params>} LogObject<cmDuplicateCollectionSrc83Evid18Params>
 */
export function cmDuplicateCollectionSrc83Evid18(params) {
    return { evid: 18, src: 83, endpoint: '', params };
}
/**
 * A function to create a log object for the 'panelOpened' event (evid: 180)
 *
 * Event description: Fires when a panel is loaded
 *
 * @param {panelOpenedParams} params - The parameters for this event
 * @property {string} site_id - site_id
 * @property {GUID} uuid - uuid
 * @property {number} ts - ts
 * @property {string} panel_name - panel name
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} version - version
 * @property {string} origin - collection_page
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {GUID} esi - esi
 * @property {GUID} msid - msid
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<panelOpenedParams>} LogObject<panelOpenedParams>
 */
export function panelOpened(params) {
    return { evid: 180, src: 83, endpoint: '', params };
}
/**
 * A function to create a log object for the 'panelClosed' event (evid: 181)
 *
 * Event description: Fires when a panel is closed
 *
 * @param {panelClosedParams} params - The parameters for this event
 * @property {string} site_id - site_id
 * @property {GUID} uuid - uuid
 * @property {number} ts - ts
 * @property {string} panel_name - panel name
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} version - version
 * @property {string} origin - collection_page
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {GUID} esi - esi
 * @property {GUID} msid - msid
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<panelClosedParams>} LogObject<panelClosedParams>
 */
export function panelClosed(params) {
    return { evid: 181, src: 83, endpoint: '', params };
}
/**
 * A function to create a log object for the 'cmCreateCollectionPermissionsClicked' event (evid: 182)
 *
 * Event description: Fires when a user changes the permissions in the collection creation process
 *
 * @param {cmCreateCollectionPermissionsClickedParams} params - The parameters for this event
 * @property {string} site_id - site_id
 * @property {GUID} uuid - uuid
 * @property {number} ts - ts
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} version - version
 * @property {string} collection_type - {CONTENT_FOR_YOUR_SITE,FORM_INPUTS,CUSTOM,etc}
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {GUID} esi - esi
 * @property {GUID} msid - msid
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<cmCreateCollectionPermissionsClickedParams>} LogObject<cmCreateCollectionPermissionsClickedParams>
 */
export function cmCreateCollectionPermissionsClicked(params) {
    return { evid: 182, src: 83, endpoint: '', params };
}
/**
 * A function to create a log object for the 'cmOpenItemPage' event (evid: 183)
 *
 * Event description: CM Open item page
 *
 * @param {cmOpenItemPageParams} params - The parameters for this event
 * @property {string} site_id - site_id
 * @property {number} ts - ts
 * @property {string} collection_id - collection_id
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} version - version
 * @property {boolean} is_live_data - Live=true, Sandbox=false
 * @property {string} origin - NEW_ITEM_BUTTON, RIGHT_CLICK_MENU, MORE_ACTIONS_BUTTON. In One App, the origins are "manage_tab" and "platforms_tab".
 * @property {string} environment - (Editor/Dashboard/one_app)
 * @property {boolean} is_sandbox_enabled - (true/false)
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {GUID} esi - ()
 * @property {string} collection_name - collection name
 * @property {GUID} msid - ()
 * @property {string} action - NEW_ITEM,EDIT_ITEM
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<cmOpenItemPageParams>} LogObject<cmOpenItemPageParams>
 */
export function cmOpenItemPage(params) {
    return { evid: 183, src: 83, endpoint: '', params };
}
/**
 * A function to create a log object for the 'cmPopupOpened' event (evid: 184)
 *
 * Event description: CM Popup Opened
 *
 * @param {cmPopupOpenedParams} params - The parameters for this event
 * @property {string} name - (delete...)
 * @property {string} site_id - site_id
 * @property {number} ts - ts
 * @property {string} collection_id - collection_id
 * @property {string} viewmode - viewmode
 * @property {string} input_name - {dbAddFieldPanel, editCollection, editCollectionPermissions, autoCMS}
 * @property {boolean} is_shared - is_shared
 * @property {GUID} view_id - view id
 * @property {string} version - version
 * @property {boolean} is_live_data - (true/false)
 * @property {string} origin - CANCEL_BUTTON,CLICK_ON_BREADCRUMBS, ITEM_PAGE, COLLECTION_PAGE, collection_item_list_screen (one_app)
 * @property {string} environment - (Editor/Dashboard/one_app)
 * @property {boolean} is_sandbox_enabled - (true/false)
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {GUID} esi - esi
 * @property {string} collection_name - collection_name
 * @property {GUID} msid - msid
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<cmPopupOpenedParams>} LogObject<cmPopupOpenedParams>
 */
export function cmPopupOpened(params) {
    return { evid: 184, src: 83, endpoint: '', params };
}
/**
 * A function to create a log object for the 'cmDiscardPopup' event (evid: 185)
 *
 * Event description: CM Discard popup
 *
 * @param {cmDiscardPopupParams} params - The parameters for this event
 * @property {string} name - (Discard changes / Delete Item...)
 * @property {string} site_id - site_id
 * @property {number} ts - ts
 * @property {string} collection_id - collection_id
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {GUID} view_id - view id
 * @property {string} version - version
 * @property {boolean} is_live_data - (true/false)
 * @property {string} origin - DISCARD_CHANGES, X, collection_item_list_screen (one_app)
 * @property {string} environment - (Editor/Dashboard/one_app)
 * @property {boolean} is_sandbox_enabled - (true/false)
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {GUID} esi - esi
 * @property {string} collection_name - collection_name
 * @property {GUID} msid - msid
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<cmDiscardPopupParams>} LogObject<cmDiscardPopupParams>
 */
export function cmDiscardPopup(params) {
    return { evid: 185, src: 83, endpoint: '', params };
}
/**
 * A function to create a log object for the 'cmNewViewCreated' event (evid: 186)
 *
 * Event description: CM New View Created
 *
 * @param {cmNewViewCreatedParams} params - The parameters for this event
 * @property {string} name - view name
 * @property {string} site_id - site_id
 * @property {number} ts - ts
 * @property {string} collection_id - collection_id
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {GUID} view_id - view id
 * @property {string} version - version
 * @property {boolean} is_live_data - (true/false)
 * @property {string} origin - ["add_field_form", "ADD_COLUMN_BUTTON", "DS_ADD_FIELD_BUTTON"]
 * @property {string} environment - editor/dashboard
 * @property {boolean} is_sandbox_enabled - (true/false)
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {GUID} esi - ()
 * @property {string} collection_name - collection name
 * @property {GUID} msid - ()
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<cmNewViewCreatedParams>} LogObject<cmNewViewCreatedParams>
 */
export function cmNewViewCreated(params) {
    return { evid: 186, src: 83, endpoint: '', params };
}
/**
 * A function to create a log object for the 'cmSwitchView' event (evid: 187)
 *
 * Event description: CM switch View
 *
 * @param {cmSwitchViewParams} params - The parameters for this event
 * @property {string} name - target view name
 * @property {string} site_id - site_id
 * @property {number} ts - ts
 * @property {string} collection_id - collection_id
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {GUID} src_view_id - source  view id
 * @property {GUID} view_id - target view id
 * @property {string} version - version
 * @property {boolean} is_live_data - (true/false)
 * @property {string} environment - editor/dashboard/one_app
 * @property {boolean} is_sandbox_enabled - (true/false)
 * @property {string} view_layout - table/list/gallery
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {GUID} esi - ()
 * @property {string} collection_name - collection name
 * @property {GUID} msid - ()
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<cmSwitchViewParams>} LogObject<cmSwitchViewParams>
 */
export function cmSwitchView(params) {
    return { evid: 187, src: 83, endpoint: '', params };
}
/**
 * A function to create a log object for the 'dbCollectionOpenedInCm' event (evid: 188)
 *
 * Event description: DB collection is opened in database tab in biz mgr
 *
 * @param {dbCollectionOpenedInCmParams} params - The parameters for this event
 * @property {string} site_id - site_id
 * @property {number} ts - ts
 * @property {number} number_of_items_in_collection - number_of_items_in_collection
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {number} time_to_open - time_to_open in ms
 * @property {string} version - version
 * @property {string} origin - business_manager/deep_link
 * @property {string} environment - (Editor/Dashboard)
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {GUID} esi - esi
 * @property {string} collection_name - collection name
 * @property {GUID} msid - msid
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<dbCollectionOpenedInCmParams>} LogObject<dbCollectionOpenedInCmParams>
 */
export function dbCollectionOpenedInCm(params) {
    return { evid: 188, src: 83, endpoint: '', params };
}
/**
 * A function to create a log object for the 'cmChangingViewLayout' event (evid: 189)
 *
 * Event description: the user changed a view layout
 *
 * @param {cmChangingViewLayoutParams} params - The parameters for this event
 * @property {string} site_id - site_id
 * @property {number} ts - ts
 * @property {string} collection_id - collection id
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {GUID} view_id - view id
 * @property {string} version - version
 * @property {boolean} is_live_data - (true/false)
 * @property {string} origin - site structure, add menu, etc...
 * @property {string} environment - (Editor/Dashboard)
 * @property {boolean} is_sandbox_enabled - (true/false)
 * @property {string} view_layout - (TABLE/LIST/GALLERY)
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {GUID} esi - esi
 * @property {string} collection_name - collection name
 * @property {GUID} msid - msid
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<cmChangingViewLayoutParams>} LogObject<cmChangingViewLayoutParams>
 */
export function cmChangingViewLayout(params) {
    return { evid: 189, src: 83, endpoint: '', params };
}
/**
 * A function to create a log object for the 'addSitePagesToMenuSrc83Evid19' event (evid: 19)
 *
 * Event description: Add site pages to site menu
 *
 * @param {addSitePagesToMenuSrc83Evid19Params} params - The parameters for this event
 * @property {string} page_id - ID of the selected page
 * @property {string} page_name - name of the selected page
 * @property {string} page_type - list\ item\ static page
 * @property {string} collection_id - id of the collection of the selected page
 * @property {number} num_of_collection_items - num of items in the collection
 * @property {string} action - add\ cancel\ close\help
 * @property {number} num_of_page_items - num of items presented in the page
 * @property {string} menu_id - id of the menu
 * @property {number} num_pages_in_menu - total num of pages in the relevant menu
 * @return {LogObject<addSitePagesToMenuSrc83Evid19Params>} LogObject<addSitePagesToMenuSrc83Evid19Params>
 */
export function addSitePagesToMenuSrc83Evid19(params) {
    return { evid: 19, src: 83, endpoint: '', params };
}
/**
 * A function to create a log object for the 'wixCodeClickOnResourcesLinkInBm' event (evid: 190)
 *
 * Event description: user clicked on a link in resources widget in BM dashboard
 *
 * @param {wixCodeClickOnResourcesLinkInBmParams} params - The parameters for this event
 * @property {string} name - name of clicked item
 * @property {string} site_id - site_id
 * @property {number} ts - ts
 * @property {string} viewmode - viewmode
 * @property {string} input_name - not sent
 * @property {string} version - version
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {GUID} esi - not sent
 * @property {GUID} msid - msid
 * @property {string} page_url - clicked link url
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<wixCodeClickOnResourcesLinkInBmParams>} LogObject<wixCodeClickOnResourcesLinkInBmParams>
 */
export function wixCodeClickOnResourcesLinkInBm(params) {
    return { evid: 190, src: 83, endpoint: '', params };
}
/**
 * A function to create a log object for the 'collectionRenamed' event (evid: 191)
 *
 * Event description: user renames a data collection
 *
 * @param {collectionRenamedParams} params - The parameters for this event
 * @property {string} site_id - site_id
 * @property {string} collection_previous_name - collection previous name
 * @property {number} ts - ts
 * @property {string} collection_id - collection_id
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} version - version
 * @property {string} origin - e.g data_panel,ide_tree
 * @property {string} environment - EDITOR, BUSINESS_MANAGER
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {GUID} esi - esi
 * @property {string} collection_name - new collection name
 * @property {GUID} msid - msid
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<collectionRenamedParams>} LogObject<collectionRenamedParams>
 */
export function collectionRenamed(params) {
    return { evid: 191, src: 83, endpoint: '', params };
}
/**
 * A function to create a log object for the 'cmNewViewClicked' event (evid: 192)
 *
 * Event description: CM New View Clicked
 *
 * @param {cmNewViewClickedParams} params - The parameters for this event
 * @property {string} site_id - site_id
 * @property {number} ts - ts
 * @property {string} collection_id - collection_id
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} version - version
 * @property {boolean} is_live_data - (true/false)
 * @property {string} environment - editor/dashboard
 * @property {boolean} is_sandbox_enabled - (true/false)
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {GUID} esi - ()
 * @property {string} collection_name - collection name
 * @property {GUID} msid - ()
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<cmNewViewClickedParams>} LogObject<cmNewViewClickedParams>
 */
export function cmNewViewClicked(params) {
    return { evid: 192, src: 83, endpoint: '', params };
}
/**
 * A function to create a log object for the 'cmViewChanged' event (evid: 193)
 *
 * Event description: CM View Changed
 *
 * @param {cmViewChangedParams} params - The parameters for this event
 * @property {string} name - view name
 * @property {string} site_id - site_id
 * @property {number} ts - ts
 * @property {string} collection_id - collection_id
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {boolean} order_used_on_site - true / false
 * @property {GUID} view_id - view_id
 * @property {string} version - version
 * @property {boolean} is_live_data - (true/false)
 * @property {string} environment - editor/dashboard
 * @property {boolean} is_sandbox_enabled - (true/false)
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {GUID} esi - ()
 * @property {string} collection_name - collection name
 * @property {GUID} msid - ()
 * @property {string} action - edit/delete/name_changed
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<cmViewChangedParams>} LogObject<cmViewChangedParams>
 */
export function cmViewChanged(params) {
    return { evid: 193, src: 83, endpoint: '', params };
}
/**
 * A function to create a log object for the 'wixCodeSearchAction' event (evid: 194)
 *
 * Event description: Shoots when a user search in wix code minisite
 *
 * @param {wixCodeSearchActionParams} params - The parameters for this event
 * @property {GUID} session_id - session_id
 * @property {Url} referral_url - referral_url
 * @property {string} site_id - site_id
 * @property {number} ts - ts
 * @property {string} viewmode - viewmode
 * @property {string} text - what was the search text
 * @property {string} input_name - input_name
 * @property {number} num_of_results - num of results of the search action {0=no results, 1, 2...}
 * @property {string} version - version
 * @property {string} origin - {menu/search result/ main_content/ outside}
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {string} category - {articles, examples, tutorials, etc.} for example: https://www.wix.com/corvid/example/add-to-cart-gallery- category='example'
 * @property {string} topic - {All Topics, Interactions, content, etc.}
 * @property {string} type - type filterd - all/api/feature
 * @property {GUID} esi - esi
 * @property {GUID} msid - msid
 * @property {string} page_url - pages_url
 * @property {string} level - {All Levels, Beginner, Intermediate, Advenced}
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<wixCodeSearchActionParams>} LogObject<wixCodeSearchActionParams>
 */
export function wixCodeSearchAction(params) {
    return { evid: 194, src: 83, endpoint: '', params };
}
/**
 * A function to create a log object for the 'wixCodeLevelSelected' event (evid: 195)
 *
 * Event description: Shoots when a user select level in wix code examples
 *
 * @param {wixCodeLevelSelectedParams} params - The parameters for this event
 * @property {GUID} session_id - session_id
 * @property {Url} referral_url - referral_url
 * @property {string} site_id - site_id
 * @property {number} ts - ts
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} version - version
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {string} category - {articles, examples, tutorials, etc.} for example: https://www.wix.com/corvid/example/add-to-cart-gallery-  category='example'
 * @property {string} topic - {All Topics, Interactions, content, etc.}
 * @property {GUID} esi - esi
 * @property {GUID} msid - msid
 * @property {string} page_url - pages_url
 * @property {string} level - {All Levels, Beginner, Intermediate, Advenced}
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<wixCodeLevelSelectedParams>} LogObject<wixCodeLevelSelectedParams>
 */
export function wixCodeLevelSelected(params) {
    return { evid: 195, src: 83, endpoint: '', params };
}
/**
 * A function to create a log object for the 'wixCodeTopicSelected' event (evid: 196)
 *
 * Event description: Shoots when a user select topic in wix code mini site
 *
 * @param {wixCodeTopicSelectedParams} params - The parameters for this event
 * @property {GUID} session_id - session_id
 * @property {Url} referral_url - referral_url
 * @property {string} site_id - site_id
 * @property {number} ts - ts
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} version - version
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {string} category - {articles, examples, tutorials, etc.} for example: https://www.wix.com/corvid/example/add-to-cart-gallery-  category='example'
 * @property {string} topic - {All Topics, Interactions, content, etc.}
 * @property {string} type - all/api/feature
 * @property {GUID} esi - esi
 * @property {GUID} msid - msid
 * @property {string} page_url - pages_url
 * @property {string} level - {All Levels, Beginner, Intermediate, Advanced}
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<wixCodeTopicSelectedParams>} LogObject<wixCodeTopicSelectedParams>
 */
export function wixCodeTopicSelected(params) {
    return { evid: 196, src: 83, endpoint: '', params };
}
/**
 * A function to create a log object for the 'wixCodeNavigateInExamplePageReachAnchor' event (evid: 197)
 *
 * Event description: send when a user gets to an anchor of pages in wix code examples
 *
 * @param {wixCodeNavigateInExamplePageReachAnchorParams} params - The parameters for this event
 * @property {string} example_name - example_name
 * @property {GUID} session_id - session_id
 * @property {Url} referral_url - referral_url
 * @property {string} anchor_name - anchor_name
 * @property {string} site_id - site_id
 * @property {number} ts - ts
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} page_name - page name
 * @property {string} version - version
 * @property {string} origin - {scroll, anchor_menu}
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {string} topic - topic
 * @property {GUID} esi - esi
 * @property {GUID} msid - msid
 * @property {string} page_url - pages_url
 * @property {string} level - user level: {All Levels, Beginner, Intermediate, Advanced}
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<wixCodeNavigateInExamplePageReachAnchorParams>} LogObject<wixCodeNavigateInExamplePageReachAnchorParams>
 */
export function wixCodeNavigateInExamplePageReachAnchor(params) {
    return { evid: 197, src: 83, endpoint: '', params };
}
/**
 * A function to create a log object for the 'wixCodeCodeFileViewChanged' event (evid: 198)
 *
 * Event description: Shoots when a user select level in wix code examples
 *
 * @param {wixCodeCodeFileViewChangedParams} params - The parameters for this event
 * @property {string} example_name - example_name
 * @property {GUID} session_id - session_id
 * @property {Url} referral_url - referral_url
 * @property {string} site_id - site_id
 * @property {string} file_name - file_name
 * @property {number} ts - ts
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} version - version
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {string} topic - {All Topics, Interactions, content, etc.}
 * @property {GUID} esi - esi
 * @property {GUID} msid - msid
 * @property {string} page_url - pages_url
 * @property {string} level - user level: {All Levels, Beginner, Intermediate, Advanced}
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<wixCodeCodeFileViewChangedParams>} LogObject<wixCodeCodeFileViewChangedParams>
 */
export function wixCodeCodeFileViewChanged(params) {
    return { evid: 198, src: 83, endpoint: '', params };
}
/**
 * A function to create a log object for the 'wixCodeRelatedLinksClicked' event (evid: 199)
 *
 * Event description: send when a user clicks on a used resource link or related example in wix code example
 *
 * @param {wixCodeRelatedLinksClickedParams} params - The parameters for this event
 * @property {string} example_name - example_name - if example
 * @property {GUID} session_id - session_id
 * @property {Url} referral_url - The referral url - from which url the user came to this current page
 * @property {string} site_id - site_id
 * @property {number} ts - ts
 * @property {string} viewmode - viewmode
 * @property {string} input_name - deprecated
 * @property {string} page_name - The page title
 * @property {string} version - version
 * @property {string} origin - Identifier of specific button
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {string} category - articles, examples, tutorials, etc.
 * @property {string} topic - {All Topics, Interactions, content, etc.}
 * @property {string} link_type - the link type the user clicks on (used_resource_link, corvid_project, related_example, related_tutorial, related_artical, etc.){
 * @property {string} type - all/feature/api
 * @property {GUID} esi - editor session id - if exists
 * @property {GUID} msid - msid
 * @property {string} page_url - The current page url
 * @property {string} related_link - The related link - to which url the user will go (next page) - the url that linked
 * @property {string} level - user level: {All Levels, Beginner, Intermediate, Advanced} this field is sent  if there is a level. If there is no level, please ignore it.
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<wixCodeRelatedLinksClickedParams>} LogObject<wixCodeRelatedLinksClickedParams>
 */
export function wixCodeRelatedLinksClicked(params) {
    return { evid: 199, src: 83, endpoint: '', params };
}
/**
 * A function to create a log object for the 'cmsConflictIndicationShown' event (evid: 2)
 *
 * Event description: When a conflict indication that originates from the permissions settings is raised in terms of red info icon or notification or from the info icon's tooltip
 *
 * @param {cmsConflictIndicationShownParams} params - The parameters for this event
 * @property {string} renderingFlow - Rendering flow
 * @property {string} conflictType - alert for conflicts that require a fix, standard o.w
 * @property {string} collection_id - collection_id
 * @property {string} collection_name - collection_name
 * @property {string} widget_id - widget ID- in blocks widget properties binding
 * @property {string} environment - EDITOR / BUSINESS_MANAGER
 * @property {string} componentType - info_icon / notification_banner / info_icon_tooltip
 * @property {string} ctaName - the cta shown
 * @property {string} origin - permissions/dataset_summary
 * @property {string} alertType - collection/items/filters/sorts
 * @property {string} ds_id - dataset's id
 * @property {string} ds_name - dataset's name
 * @property {string} element_id - the id of the element which the alert refers to e.g image, text
 * @return {LogObject<cmsConflictIndicationShownParams>} LogObject<cmsConflictIndicationShownParams>
 */
export function cmsConflictIndicationShown(params) {
    return { evid: 2, src: 83, endpoint: '', params };
}
/**
 * A function to create a log object for the 'dynamicMenuItemsSettingsSrc83Evid20' event (evid: 20)
 *
 * Event description: When a user adds a page as a new item to the site's menu
 *
 * @param {dynamicMenuItemsSettingsSrc83Evid20Params} params - The parameters for this event
 * @property {string} collection_name - collection_name
 * @property {string} collection_id - collection_id
 * @property {string} page_id - id of the page  added to the menu
 * @property {string} page_name - name of the page added to the menu
 * @property {string} action - item_name/filter/sort/items_displayed
 * @property {string} action_value - value of action e.g selected item name, sort properties
 * @property {string} action_properties - additional properties
 * @property {string} menu_id - id of the menu
 * @property {string} item_id - ID of the link item added to the menu
 * @property {string} component_type - component_type
 * @property {string} component_id - component_id
 * @property {string} panel_type - panel_type
 * @return {LogObject<dynamicMenuItemsSettingsSrc83Evid20Params>} LogObject<dynamicMenuItemsSettingsSrc83Evid20Params>
 */
export function dynamicMenuItemsSettingsSrc83Evid20(params) {
    return { evid: 20, src: 83, endpoint: '', params };
}
/**
 * A function to create a log object for the 'dataBackupsOpenBackups' event (evid: 200)
 *
 * Event description: open database backups in the business manager
 *
 * @param {dataBackupsOpenBackupsParams} params - The parameters for this event
 * @property {string} site_id - site_id
 * @property {number} ts - ts
 * @property {number} num_of_backups - number of backups, if none then 0
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} version - version
 * @property {string} origin - from where the user opened Backups?
 * @property {string} environment - {Editor / business_manager}
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {GUID} esi - esi
 * @property {GUID} msid - msid
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<dataBackupsOpenBackupsParams>} LogObject<dataBackupsOpenBackupsParams>
 */
export function dataBackupsOpenBackups(params) {
    return { evid: 200, src: 83, endpoint: '', params };
}
/**
 * A function to create a log object for the 'dataBackupsBackupNowClick' event (evid: 201)
 *
 * Event description: user clicked on the backup now button or from the sync sandbox to live dialog
 *
 * @param {dataBackupsBackupNowClickParams} params - The parameters for this event
 * @property {number} num_of_collections - number of collections that will be backed up
 * @property {string} site_id - site_id
 * @property {number} ts - ts
 * @property {number} num_of_backups - number of backups, if none then 0
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} version - version
 * @property {string} origin - backups_page , sync_dialog
 * @property {string} environment - {Editor / business_manager}
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {GUID} esi - esi
 * @property {GUID} msid - msid
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<dataBackupsBackupNowClickParams>} LogObject<dataBackupsBackupNowClickParams>
 */
export function dataBackupsBackupNowClick(params) {
    return { evid: 201, src: 83, endpoint: '', params };
}
/**
 * A function to create a log object for the 'dataBackupsBackupNowSuccess' event (evid: 202)
 *
 * Event description: user clicked on the backup now button and backup has been completed
 *
 * @param {dataBackupsBackupNowSuccessParams} params - The parameters for this event
 * @property {number} num_of_collections - number of collections that were backed up
 * @property {string} site_id - site_id
 * @property {number} ts - ts
 * @property {number} num_of_backups - number of backups, if none then 0
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} version - version
 * @property {string} origin - backups, sync_dialog
 * @property {string} environment - {Editor / business_manager}
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {GUID} esi - esi
 * @property {GUID} msid - msid
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<dataBackupsBackupNowSuccessParams>} LogObject<dataBackupsBackupNowSuccessParams>
 */
export function dataBackupsBackupNowSuccess(params) {
    return { evid: 202, src: 83, endpoint: '', params };
}
/**
 * A function to create a log object for the 'dataBackupsRestoreClickApprove' event (evid: 203)
 *
 * Event description: user clicked on the restore button or clicked on approve restore in dialog (filter by action field)
 *
 * @param {dataBackupsRestoreClickApproveParams} params - The parameters for this event
 * @property {number} num_of_collections - number of collections that before restoring
 * @property {string} site_id - site_id
 * @property {number} ts - ts
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} version - version
 * @property {DateTime} backup_date - date backup was created
 * @property {string} environment - {Editor / business_manager}
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {GUID} esi - esi
 * @property {GUID} msid - msid
 * @property {string} action - click
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<dataBackupsRestoreClickApproveParams>} LogObject<dataBackupsRestoreClickApproveParams>
 */
export function dataBackupsRestoreClickApprove(params) {
    return { evid: 203, src: 83, endpoint: '', params };
}
/**
 * A function to create a log object for the 'dataBackupsRestoreSuccess' event (evid: 204)
 *
 * Event description: backup version restored successfully
 *
 * @param {dataBackupsRestoreSuccessParams} params - The parameters for this event
 * @property {number} num_of_collections - number of collections that were restored
 * @property {string} site_id - site_id
 * @property {number} ts - ts
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} version - version
 * @property {DateTime} backup_date - date backup was created
 * @property {string} environment - {Editor / business_manager}
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {GUID} esi - esi
 * @property {GUID} msid - msid
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<dataBackupsRestoreSuccessParams>} LogObject<dataBackupsRestoreSuccessParams>
 */
export function dataBackupsRestoreSuccess(params) {
    return { evid: 204, src: 83, endpoint: '', params };
}
/**
 * A function to create a log object for the 'dataBackupsDeleteClickApprove' event (evid: 205)
 *
 * Event description: user clicked on the delete button or clicked on approve delete in dialog (filter by action field)
 *
 * @param {dataBackupsDeleteClickApproveParams} params - The parameters for this event
 * @property {number} num_of_collections - number of collections in backup
 * @property {string} site_id - site_id
 * @property {number} ts - ts
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} version - version
 * @property {DateTime} backup_date - date backup was created
 * @property {string} environment - {Editor / business_manager}
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {GUID} esi - esi
 * @property {GUID} msid - msid
 * @property {string} action - delete, approve
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<dataBackupsDeleteClickApproveParams>} LogObject<dataBackupsDeleteClickApproveParams>
 */
export function dataBackupsDeleteClickApprove(params) {
    return { evid: 205, src: 83, endpoint: '', params };
}
/**
 * A function to create a log object for the 'dataBackupsDeleteSuccess' event (evid: 206)
 *
 * Event description: backup version deleted successfully
 *
 * @param {dataBackupsDeleteSuccessParams} params - The parameters for this event
 * @property {number} num_of_collections - number of collections in deleted backup
 * @property {string} site_id - site_id
 * @property {number} ts - ts
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} version - version
 * @property {DateTime} backup_date - date backup was created
 * @property {string} environment - {Editor / business_manager}
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {GUID} esi - esi
 * @property {GUID} msid - msid
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<dataBackupsDeleteSuccessParams>} LogObject<dataBackupsDeleteSuccessParams>
 */
export function dataBackupsDeleteSuccess(params) {
    return { evid: 206, src: 83, endpoint: '', params };
}
/**
 * A function to create a log object for the 'cmCollectionMenuActionsClick' event (evid: 207)
 *
 * Event description: when a user clicks on the three dots menu actions of collection
 *
 * @param {cmCollectionMenuActionsClickParams} params - The parameters for this event
 * @property {string} renderingFlow - Rendering flow
 * @property {string} site_id - site_id
 * @property {number} ts - ts
 * @property {string} collection_id - collection_id
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {boolean} is_shared - is shared collection
 * @property {string} version - version
 * @property {string} origin - {collections_page, BM_collections_tab}
 * @property {string} environment - (Editor/Dashboard)
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {GUID} esi - ()
 * @property {string} collection_name - collection name
 * @property {GUID} msid - ()
 * @property {string} dev_mode - on / off
 * @property {string} num_items_type - single / multiple
 * @property {string} collection_privacy - shared / not shared
 * @property {string} Collection_owner - user / external / wix app
 * @property {string} can_write_to_schema - true / false
 * @return {LogObject<cmCollectionMenuActionsClickParams>} LogObject<cmCollectionMenuActionsClickParams>
 */
export function cmCollectionMenuActionsClick(params) {
    return { evid: 207, src: 83, endpoint: '', params };
}
/**
 * A function to create a log object for the 'cmCollectionMenuActionSelectClick' event (evid: 208)
 *
 * Event description: when a user clicks on a action in the three dots menu actions of collection
 *
 * @param {cmCollectionMenuActionSelectClickParams} params - The parameters for this event
 * @property {string} site_id - site_id
 * @property {number} ts - ts
 * @property {string} collection_id - collection_id
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {boolean} is_shared - is shared collection
 * @property {string} version - version
 * @property {string} origin - {collections_page}
 * @property {string} environment - (Editor/Dashboard)
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {GUID} esi - ()
 * @property {string} collection_name - collection name
 * @property {GUID} msid - ()
 * @property {string} action - {removeCollectionClicked, openCollectionPermissionsClicked}
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<cmCollectionMenuActionSelectClickParams>} LogObject<cmCollectionMenuActionSelectClickParams>
 */
export function cmCollectionMenuActionSelectClick(params) {
    return { evid: 208, src: 83, endpoint: '', params };
}
/**
 * A function to create a log object for the 'cmMoreActionsFieldClick' event (evid: 209)
 *
 * Event description: when a user clicks on an action in the menu of manage fields menu
 *
 * @param {cmMoreActionsFieldClickParams} params - The parameters for this event
 * @property {string} site_id - site_id
 * @property {string} field_type - the type of the field - text, datetime, number
 * @property {number} ts - ts
 * @property {string} collection_id - collection id
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} version - version
 * @property {boolean} is_live_data - (true/false)
 * @property {string} origin - FIELD_HEADER/LIST_VISIBLE_FIELDS_BUTTON/TABLE_VISIBLE_FIELDS_BUTTON/ITEM_VISIBLE_FIELD_BUTTON/ADD_COLUMN_BUTTON/GALLERY_VISIBLE_FIELDS_BUTTON/SINGLES_ADD_FIELD/MULTIPLES_ADD_FIELD
 * @property {string} environment - (Editor/Business_manager)
 * @property {boolean} is_sandbox_enabled - (true/false)
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {GUID} esi - editor session id
 * @property {string} collection_name - collection name
 * @property {GUID} msid - msid
 * @property {string} field_name - the name (title) of the field
 * @property {string} action - edit_field/delete_field/add_field
 * @property {string} view - sandbox/live
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<cmMoreActionsFieldClickParams>} LogObject<cmMoreActionsFieldClickParams>
 */
export function cmMoreActionsFieldClick(params) {
    return { evid: 209, src: 83, endpoint: '', params };
}
/**
 * A function to create a log object for the 'dynamicPageInMenuActionsSrc83Evid21' event (evid: 21)
 *
 * Event description: When a user chooses one of the additional actions available for a dynamic item in site's menu
 *
 * @param {dynamicPageInMenuActionsSrc83Evid21Params} params - The parameters for this event
 * @property {string} page_id - id of the selected page in the menu
 * @property {string} page_name - name of the selected page in the menu
 * @property {number} num_of_items - num of items shown in the page
 * @property {string} page_type - list\ item page
 * @property {string} collection_id - id of the related collection
 * @property {string} action - action
 * @property {string} menu_id - id of the edited menu
 * @property {number} num_pages_in_menu - total num of pages in the relevant menu
 * @return {LogObject<dynamicPageInMenuActionsSrc83Evid21Params>} LogObject<dynamicPageInMenuActionsSrc83Evid21Params>
 */
export function dynamicPageInMenuActionsSrc83Evid21(params) {
    return { evid: 21, src: 83, endpoint: '', params };
}
/**
 * A function to create a log object for the 'cmSearchAction' event (evid: 210)
 *
 * Event description: Shoots when a user searches and the marker results appear
 *  Due to the quick results - the event is sent a 1-second (delay) after showing
 *
 * @param {cmSearchActionParams} params - The parameters for this event
 * @property {string} site_id - site_id
 * @property {number} ts - ts
 * @property {string} collection_id - collection id
 * @property {string} viewmode - viewmode
 * @property {string} text - what was the search text
 * @property {string} input_name - input_name
 * @property {number} num_of_results - num of results of the search action {0=no results, 1, 2...}
 * @property {string} version - version
 * @property {string} origin - for mobile: the screen name
 * @property {string} fields - list of results' fields
 * @property {string} environment - editor/business_manager/one_app
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {GUID} esi - esi=editor session id
 * @property {string} collection_name - In One App, it's a collection ID.
 * @property {GUID} msid - msid
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<cmSearchActionParams>} LogObject<cmSearchActionParams>
 */
export function cmSearchAction(params) {
    return { evid: 210, src: 83, endpoint: '', params };
}
/**
 * A function to create a log object for the 'dataKmsList' event (evid: 2101)
 *
 * Event description: Data_key_management_service_review(list)
 *
 * @param {dataKmsListParams} params - The parameters for this event
 * @property {string} appDefId - appDefId of the calling app
 * @property {string} instanceId - wixcode instance_id for which keys are requested
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<dataKmsListParams>} LogObject<dataKmsListParams>
 */
export function dataKmsList(params) {
    return { evid: 2101, src: 83, endpoint: '', params };
}
/**
 * A function to create a log object for the 'dataKmsInit' event (evid: 2102)
 *
 * Event description: data-kms init new key
 *
 * @param {dataKmsInitParams} params - The parameters for this event
 * @property {string} instanceId - wixcode instance_id for which initialization is requested
 * @property {string} appDefId - appDefId of the calling app
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<dataKmsInitParams>} LogObject<dataKmsInitParams>
 */
export function dataKmsInit(params) {
    return { evid: 2102, src: 83, endpoint: '', params };
}
/**
 * A function to create a log object for the 'dataKmsRevoke' event (evid: 2103)
 *
 * Event description: Data_key_management_service_revoke
 *
 * @param {dataKmsRevokeParams} params - The parameters for this event
 * @property {string} instanceId - wixcode instance_id for which revoke is requested
 * @property {string} appDefId - appDefId of the calling app
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<dataKmsRevokeParams>} LogObject<dataKmsRevokeParams>
 */
export function dataKmsRevoke(params) {
    return { evid: 2103, src: 83, endpoint: '', params };
}
/**
 * A function to create a log object for the 'cmSearchBarOpen' event (evid: 211)
 *
 * Event description: Shoots when the search bar open in the CM
 *
 * @param {cmSearchBarOpenParams} params - The parameters for this event
 * @property {string} site_id - site_id
 * @property {number} ts - ts
 * @property {string} collection_id - collection id
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} version - version
 * @property {string} origin - for mobile: name of the screen
 * @property {string} environment - editor/business_manager/one_app
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {GUID} esi - esi=editor session id
 * @property {string} collection_name - In One App it's a collection ID
 * @property {GUID} msid - msid
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<cmSearchBarOpenParams>} LogObject<cmSearchBarOpenParams>
 */
export function cmSearchBarOpen(params) {
    return { evid: 211, src: 83, endpoint: '', params };
}
/**
 * A function to create a log object for the 'wixCodeBannerClick' event (evid: 212)
 *
 * Event description: Shoots when a user click on a banner in the corvid mini-site
 *
 * @param {wixCodeBannerClickParams} params - The parameters for this event
 * @property {string} banner_title - banner_title
 * @property {GUID} session_id - session_id
 * @property {Url} referral_url - referral_url
 * @property {string} site_id - site_id
 * @property {string} banner_name - banner_name
 * @property {number} ts - ts
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} version - version
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {GUID} esi - esi
 * @property {GUID} msid - msid
 * @property {string} bannerLocationOnPage - bannerLocationOnPage
 * @property {string} page_url - pages_url
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<wixCodeBannerClickParams>} LogObject<wixCodeBannerClickParams>
 */
export function wixCodeBannerClick(params) {
    return { evid: 212, src: 83, endpoint: '', params };
}
/**
 * A function to create a log object for the 'wixCodeLightBoxOpened' event (evid: 213)
 *
 * Event description: Shoots when a lightbox opened in the corvid mini-site
 *
 * @param {wixCodeLightBoxOpenedParams} params - The parameters for this event
 * @property {GUID} session_id - session_id
 * @property {Url} referral_url - the previous url
 * @property {string} site_id - site_id
 * @property {number} ts - ts
 * @property {string} light_box_guid - light_box_guid
 * @property {string} viewmode - viewmode
 * @property {string} input_name - deprecated
 * @property {string} version - version
 * @property {string} light_box_name - light_box_name
 * @property {string} light_box_origin - How the light box was triggered
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {GUID} esi - deprecated
 * @property {GUID} msid - msid
 * @property {string} page_url - the current url
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<wixCodeLightBoxOpenedParams>} LogObject<wixCodeLightBoxOpenedParams>
 */
export function wixCodeLightBoxOpened(params) {
    return { evid: 213, src: 83, endpoint: '', params };
}
/**
 * A function to create a log object for the 'wixCodeClickOnSubmitInLightBox' event (evid: 214)
 *
 * Event description: Shoots when a user click on submit in lightbox in the corvid mini-site
 *
 * @param {wixCodeClickOnSubmitInLightBoxParams} params - The parameters for this event
 * @property {GUID} session_id - session_id
 * @property {Url} referral_url - referral_url
 * @property {string} site_id - site_id
 * @property {number} ts - ts
 * @property {string} light_box_guid - light_box_guid
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} version - version
 * @property {string} light_box_name - light_box_name
 * @property {string} light_box_origin - How the light box was triggered
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {GUID} esi - esi
 * @property {GUID} msid - msid
 * @property {string} page_url - pages_url
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<wixCodeClickOnSubmitInLightBoxParams>} LogObject<wixCodeClickOnSubmitInLightBoxParams>
 */
export function wixCodeClickOnSubmitInLightBox(params) {
    return { evid: 214, src: 83, endpoint: '', params };
}
/**
 * A function to create a log object for the 'wixCodeDiscardLightBox' event (evid: 215)
 *
 * Event description: Shoots when a user discards lightbox in the corvid mini-site
 *
 * @param {wixCodeDiscardLightBoxParams} params - The parameters for this event
 * @property {GUID} session_id - session_id
 * @property {Url} referral_url - referral_url
 * @property {string} site_id - site_id
 * @property {number} ts - ts
 * @property {string} light_box_guid - light_box_guid
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} version - version
 * @property {string} light_box_name - light_box_name
 * @property {string} origin - X / delete / close
 * @property {string} light_box_origin - How the light box was triggered
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {GUID} esi - esi
 * @property {GUID} msid - msid
 * @property {string} page_url - pages_url
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<wixCodeDiscardLightBoxParams>} LogObject<wixCodeDiscardLightBoxParams>
 */
export function wixCodeDiscardLightBox(params) {
    return { evid: 215, src: 83, endpoint: '', params };
}
/**
 * A function to create a log object for the 'wixCodeFilterSelected' event (evid: 217)
 *
 * Event description: send when a user changes the filter in page of Corvid mini-site
 *
 * @param {wixCodeFilterSelectedParams} params - The parameters for this event
 * @property {GUID} session_id - session_id
 * @property {Url} referral_url - referral_url
 * @property {string} site_id - site_id
 * @property {string} releasedate - The release date filter was chosen by the user
 * @property {number} ts - ts
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} version - version
 * @property {string} status - The status filter was chosen by the user - all/rolling_out/released
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {string} category - {articles, examples, tutorials, etc.} for example: https://www.wix.com/corvid/example/add-to-cart-gallery-  category='example'
 * @property {string} topic - {All Topics, Interactions, content, etc.}
 * @property {string} type - The type filter was chosen by the user - all/api/feature
 * @property {GUID} esi - esi
 * @property {GUID} msid - msid
 * @property {string} page_url - pages_url
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<wixCodeFilterSelectedParams>} LogObject<wixCodeFilterSelectedParams>
 */
export function wixCodeFilterSelected(params) {
    return { evid: 217, src: 83, endpoint: '', params };
}
/**
 * A function to create a log object for the 'wixCodeSubmitFeatureRequest' event (evid: 218)
 *
 * Event description: Send when a user submits of feature request in the corvid mini-site
 *
 * @param {wixCodeSubmitFeatureRequestParams} params - The parameters for this event
 * @property {string} request_details - the request details for this feature
 * @property {GUID} session_id - session_id
 * @property {Url} referral_url - referral_url
 * @property {string} site_id - site_id
 * @property {number} ts - ts
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} page_name - title of the page
 * @property {string} version - version
 * @property {string} userEnv - user_env
 * @property {GUID} request_id - request_id as it's saved in site collection
 * @property {string} editor_working_mode - editor_working_mode
 * @property {string} category - updates_and_release, article, example, tutorial, etc.
 * @property {GUID} esi - esi
 * @property {string} request_topic - summery of the feedback request that wrote by the user
 * @property {GUID} msid - msid
 * @property {string} page_url - pages_url
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<wixCodeSubmitFeatureRequestParams>} LogObject<wixCodeSubmitFeatureRequestParams>
 */
export function wixCodeSubmitFeatureRequest(params) {
    return { evid: 218, src: 83, endpoint: '', params };
}
/**
 * A function to create a log object for the 'wixCodeExpandCollapseDescription' event (evid: 219)
 *
 * Event description: send when a user expands or collapses description (of request) by clicking on  +/-
 *
 * @param {wixCodeExpandCollapseDescriptionParams} params - The parameters for this event
 * @property {GUID} session_id - session_id
 * @property {Url} referral_url - referral_url
 * @property {string} site_id - site_id
 * @property {string} tags - its tags
 * @property {number} ts - ts
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} page_name - page_name
 * @property {string} version - version
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {string} category - articles, examples, tutorials, etc.
 * @property {string} topic - all_roadmap/planning/in_developement/in_beta
 * @property {GUID} esi - esi
 * @property {GUID} msid - msid
 * @property {string} page_url - pages_url
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<wixCodeExpandCollapseDescriptionParams>} LogObject<wixCodeExpandCollapseDescriptionParams>
 */
export function wixCodeExpandCollapseDescription(params) {
    return { evid: 219, src: 83, endpoint: '', params };
}
/**
 * A function to create a log object for the 'siteMenuFilterItemPageSrc83Evid22' event (evid: 22)
 *
 * Event description: Filter item added to the site's menu
 *
 * @param {siteMenuFilterItemPageSrc83Evid22Params} params - The parameters for this event
 * @property {string} field - field name
 * @property {string} condition - filter's condition
 * @property {string} value - filter's value
 * @property {string} page_id - id of the filtered page
 * @property {string} menu_id - the id of the menu
 * @property {string} collection_id - id of the related collection
 * @property {string} field_type - type of the filtered field
 * @return {LogObject<siteMenuFilterItemPageSrc83Evid22Params>} LogObject<siteMenuFilterItemPageSrc83Evid22Params>
 */
export function siteMenuFilterItemPageSrc83Evid22(params) {
    return { evid: 22, src: 83, endpoint: '', params };
}
/**
 * A function to create a log object for the 'wixCodeCommentPostedOnFeatureRequest' event (evid: 220)
 *
 * Event description: Send when a user posts comment on feature request in the corvid mini-site
 *
 * @param {wixCodeCommentPostedOnFeatureRequestParams} params - The parameters for this event
 * @property {GUID} session_id - session_id
 * @property {Url} referral_url - referral_url
 * @property {string} site_id - site_id
 * @property {number} ts - ts
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {GUID} comment_id - guid of a comment
 * @property {string} page_name - the title of the page
 * @property {string} version - version
 * @property {string} comment - the comment was posted
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {string} category - articles, examples, tutorials, etc.
 * @property {GUID} esi - esi
 * @property {GUID} msid - msid
 * @property {string} page_url - pages_url
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<wixCodeCommentPostedOnFeatureRequestParams>} LogObject<wixCodeCommentPostedOnFeatureRequestParams>
 */
export function wixCodeCommentPostedOnFeatureRequest(params) {
    return { evid: 220, src: 83, endpoint: '', params };
}
/**
 * A function to create a log object for the 'wixCodeRunCodeFromDocsExamplePageClicked' event (evid: 221)
 *
 * Event description: send when a user runs code file of example/doc/etc. in the Corvid mini-site
 *
 * @param {wixCodeRunCodeFromDocsExamplePageClickedParams} params - The parameters for this event
 * @property {GUID} session_id - session_id
 * @property {Url} referral_url - {}
 * @property {string} site_id - site_id
 * @property {string} file_name - file_name
 * @property {number} ts - ts
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} page_name - page_name/example_name
 * @property {string} version - version
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {string} category - articles, examples, tutorials, etc.
 * @property {GUID} esi - esi
 * @property {GUID} msid - {}
 * @property {string} page_url - page_url
 * @property {string} level - user level: {All Levels, Beginner, Intermediate, Advanced}
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<wixCodeRunCodeFromDocsExamplePageClickedParams>} LogObject<wixCodeRunCodeFromDocsExamplePageClickedParams>
 */
export function wixCodeRunCodeFromDocsExamplePageClicked(params) {
    return { evid: 221, src: 83, endpoint: '', params };
}
/**
 * A function to create a log object for the 'dataBackupsLearnMoreClicked' event (evid: 222)
 *
 * Event description: Click on learn more in the backups page in the business manager
 *
 * @param {dataBackupsLearnMoreClickedParams} params - The parameters for this event
 * @property {string} site_id - site_id
 * @property {number} ts - ts
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} version - version
 * @property {string} origin - {Home/PopUp}
 * @property {string} environment - {Editor / business_manager}
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {GUID} esi - {}
 * @property {GUID} msid - msid
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<dataBackupsLearnMoreClickedParams>} LogObject<dataBackupsLearnMoreClickedParams>
 */
export function dataBackupsLearnMoreClicked(params) {
    return { evid: 222, src: 83, endpoint: '', params };
}
/**
 * A function to create a log object for the 'wixCodeSortSelected' event (evid: 224)
 *
 * Event description: send when a user selects sort in page of Corvid mini-site
 *
 * @param {wixCodeSortSelectedParams} params - The parameters for this event
 * @property {GUID} session_id - session_id
 * @property {Url} referral_url - referral_url
 * @property {string} site_id - site_id
 * @property {number} ts - ts
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} version - version
 * @property {string} sort_by - the selected sort
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {string} category - {articles, examples, tutorials, etc.} for example: https://www.wix.com/corvid/example/add-to-cart-gallery-  category='example'
 * @property {string} topic - {All Topics, Interactions, content, etc.}
 * @property {GUID} esi - esi
 * @property {GUID} msid - msid
 * @property {string} page_url - pages_url
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<wixCodeSortSelectedParams>} LogObject<wixCodeSortSelectedParams>
 */
export function wixCodeSortSelected(params) {
    return { evid: 224, src: 83, endpoint: '', params };
}
/**
 * A function to create a log object for the 'createAutoDpsWithCollectionToggleChanged' event (evid: 226)
 *
 * Event description: When a user ask to create a collection we proposing him to create also dynamic pages automatically. He is able to switch this toggle off or on (default is on)
 *
 * @param {createAutoDpsWithCollectionToggleChangedParams} params - The parameters for this event
 * @property {string} site_id - site_id
 * @property {number} ts - ts
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} version - version
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {GUID} esi - {}
 * @property {GUID} msid - {}
 * @property {string} toggle_value - {On,Off}
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<createAutoDpsWithCollectionToggleChangedParams>} LogObject<createAutoDpsWithCollectionToggleChangedParams>
 */
export function createAutoDpsWithCollectionToggleChanged(params) {
    return { evid: 226, src: 83, endpoint: '', params };
}
/**
 * A function to create a log object for the 'cmCopyPasteActions' event (evid: 227)
 *
 * Event description: Copy/Paste/Cut/Undo within CM
 *
 * @param {cmCopyPasteActionsParams} params - The parameters for this event
 * @property {string} renderingFlow - Rendering flow
 * @property {string} site_id - site_id
 * @property {number} ts - ts
 * @property {string} collection_id - collection id
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} version - version
 * @property {boolean} is_live_data - {Live=true, Sandbox=false}
 * @property {string} origin - {shortcut,button}
 * @property {string} environment - (Editor/Dashboard)
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {GUID} esi - esi
 * @property {string} collection_name - collection name
 * @property {GUID} msid - msid
 * @property {string} action - {copy,paste,cut,undo}
 * @property {string} num_of_new_rows - if the user pasted values on new rows or overlapped existing rows
 * @property {number} num_of_items - number of items the user is performing the action on
 * @return {LogObject<cmCopyPasteActionsParams>} LogObject<cmCopyPasteActionsParams>
 */
export function cmCopyPasteActions(params) {
    return { evid: 227, src: 83, endpoint: '', params };
}
/**
 * A function to create a log object for the 'cmVideoTutorialsClickToOpen' event (evid: 228)
 *
 * Event description: open video tutorial within the CM
 *
 * @param {cmVideoTutorialsClickToOpenParams} params - The parameters for this event
 * @property {string} site_id - site_id
 * @property {number} ts - ts
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} version - version
 * @property {string} origin - from where the user opened Video Tutorial?
 * @property {string} environment - {EDITOR / BUSINESS_MANAGER}
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {GUID} esi - Editor Session Id
 * @property {GUID} msid - msid
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<cmVideoTutorialsClickToOpenParams>} LogObject<cmVideoTutorialsClickToOpenParams>
 */
export function cmVideoTutorialsClickToOpen(params) {
    return { evid: 228, src: 83, endpoint: '', params };
}
/**
 * A function to create a log object for the 'cmVideoTutorialsYouTubePanelOpened' event (evid: 229)
 *
 * Event description: open youtube panel of video tutorials
 *
 * @param {cmVideoTutorialsYouTubePanelOpenedParams} params - The parameters for this event
 * @property {string} site_id - site_id
 * @property {number} ts - ts
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} version - version
 * @property {string} origin - from where the user opened the panel?
 * @property {string} environment - {EDITOR / BUSINESS_MANAGER}
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {GUID} esi - Editor Session Id
 * @property {GUID} msid - msid
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<cmVideoTutorialsYouTubePanelOpenedParams>} LogObject<cmVideoTutorialsYouTubePanelOpenedParams>
 */
export function cmVideoTutorialsYouTubePanelOpened(params) {
    return { evid: 229, src: 83, endpoint: '', params };
}
/**
 * A function to create a log object for the 'siteMenuFilterItemPagePanelCloseSrc83Evid23' event (evid: 23)
 *
 * Event description: Filter item added to the site's menu- user clicks save\ cancel
 *
 * @param {siteMenuFilterItemPagePanelCloseSrc83Evid23Params} params - The parameters for this event
 * @property {string} field - field name
 * @property {string} condition - filter's condition
 * @property {string} value - filter's value
 * @property {string} page_id - id of the filtered page
 * @property {string} menu_id - the id of the menu
 * @property {string} collection_id - id of the related collection
 * @property {string} field_type - type of the filtered field
 * @property {number} num_of_items - num of items shown on the page after filter is implemented
 * @property {string} action - add_filter\ cancel\close
 * @return {LogObject<siteMenuFilterItemPagePanelCloseSrc83Evid23Params>} LogObject<siteMenuFilterItemPagePanelCloseSrc83Evid23Params>
 */
export function siteMenuFilterItemPagePanelCloseSrc83Evid23(params) {
    return { evid: 23, src: 83, endpoint: '', params };
}
/**
 * A function to create a log object for the 'cmVideoTutorialsChoseASpecificVideoFromPlaylist' event (evid: 230)
 *
 * Event description: Click on a specific video from the list
 *
 * @param {cmVideoTutorialsChoseASpecificVideoFromPlaylistParams} params - The parameters for this event
 * @property {string} site_id - site_id
 * @property {number} ts - ts
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} video_name - {}
 * @property {string} version - version
 * @property {string} environment - {EDITOR / BUSINESS_MANAGER}
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {GUID} esi - Editor Session Id
 * @property {GUID} msid - msid
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<cmVideoTutorialsChoseASpecificVideoFromPlaylistParams>} LogObject<cmVideoTutorialsChoseASpecificVideoFromPlaylistParams>
 */
export function cmVideoTutorialsChoseASpecificVideoFromPlaylist(params) {
    return { evid: 230, src: 83, endpoint: '', params };
}
/**
 * A function to create a log object for the 'cloudDataPiiFieldMarked' event (evid: 2301)
 *
 * Event description: Event is fired when schema field marked as PII
 *
 * @param {cloudDataPiiFieldMarkedParams} params - The parameters for this event
 * @property {GUID} instanceId - WIX Code instance id
 * @property {string} schemaId - WIX Data collection name
 * @property {string} fieldName - WIX Data collection field name
 * @property {GUID} appId - Grid app id
 * @property {string} action - ADD_FIELD/EDIT_FIELD
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<cloudDataPiiFieldMarkedParams>} LogObject<cloudDataPiiFieldMarkedParams>
 */
export function cloudDataPiiFieldMarked(params) {
    return { evid: 2301, src: 83, endpoint: '', params };
}
/**
 * A function to create a log object for the 'cloudDataPiiFieldUnmarked' event (evid: 2302)
 *
 * Event description: Event is fired when schema PII field is unmarked
 *
 * @param {cloudDataPiiFieldUnmarkedParams} params - The parameters for this event
 * @property {GUID} instanceId - WIX Code instance id
 * @property {string} schemaId - WIX Data collection name
 * @property {string} fieldName - WIX Data collection field name
 * @property {GUID} appId - Grid app id
 * @property {string} action - ADD_FIELD/EDIT_FIELD
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<cloudDataPiiFieldUnmarkedParams>} LogObject<cloudDataPiiFieldUnmarkedParams>
 */
export function cloudDataPiiFieldUnmarked(params) {
    return { evid: 2302, src: 83, endpoint: '', params };
}
/**
 * A function to create a log object for the 'dataOperation' event (evid: 2303)
 *
 * Event description: Data operation. It will be used to show user the RPM, how far from limits he/she is.
 *
 * @param {dataOperationParams} params - The parameters for this event
 * @property {string} displayName - The name of the collection that is displayed to the user
 * @property {string} broadErrorCode - Broad operation code WD_SCHEMA_NOT_FOUND, etc.
 * @property {number} duration - duration of the operation in milliseconds, measured at entry point in Platformized API
 * @property {boolean} success - false if operation did not complete
 * @property {number} suboperations - number of suboperations (cost) we log in our throttler, so sum of suboperation per time should match how much we log in throttler
 * @property {string} collection - name of the primary collection operation is executed against
 * @property {string} fields_filter - ist of fields that are used to filter the collection.
 * @property {string} segment - refer to Live/Sandbox environment
 * @property {string} fields_sort - a list of fields (and the direction ASC/DESC of each field) that are used to sort the filtered result.
 * @property {string} operation - insert, update, bulkInsert, query, ... should match our operations
 * @property {string} storage - "dynamicApplication" - these are Forms schemas;  "external" - external connector schemas; "driver" - db driver schemas; "docstore" - our native schemas; "staticAppSchema" - schemas created by blocks
 * @property {string} wdErrorCode - if operation failed - WDE code
 * @property {boolean} is_cache_hit - indicates if the request was served from cache
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<dataOperationParams>} LogObject<dataOperationParams>
 */
export function dataOperation(params) {
    return { evid: 2303, src: 83, endpoint: '', params };
}
/**
 * A function to create a log object for the 'schemaPermissionsChanged' event (evid: 2307)
 *
 * Event description: Triggered after schema permissions were changed.
 * Value that have permissions - {insert, update, remove, read}
 *
 * @param {schemaPermissionsChangedParams} params - The parameters for this event
 * @property {string} new_permissions - json of new setted permissions, {insert, update, remove, read} + their statuses
 * @property {string} old_permissions - json of old permissions, {insert, update, remove, read} + their statuses
 * @property {string} collection_name - collection_name
 * @property {GUID} msid - msid
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<schemaPermissionsChangedParams>} LogObject<schemaPermissionsChangedParams>
 */
export function schemaPermissionsChanged(params) {
    return { evid: 2307, src: 83, endpoint: '', params };
}
/**
 * A function to create a log object for the 'wixDataExternalConnectorOperationRequests' event (evid: 2308)
 *
 * Event description: Fiers when triggering requests on an external connector
 *
 * @param {wixDataExternalConnectorOperationRequestsParams} params - The parameters for this event
 * @property {string} displayName - The name of the collection that is displayed to the user
 * @property {number} duration - duration of the operation in milliseconds, measured at entry point in Platformized API
 * @property {string} collection - name of the primary collection operation is executed against
 * @property {string} fields_filter - ist of fields that are used to filter the collection.
 * @property {string} segment - refer to Live/Sandbox environment
 * @property {string} operation - operation
 * @property {string} storage - external
 * @property {GUID} msid - msid
 * @property {string} protocol_version - protocol_version
 * @property {string} wdErrorCode - if operation failed - WDE code
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<wixDataExternalConnectorOperationRequestsParams>} LogObject<wixDataExternalConnectorOperationRequestsParams>
 */
export function wixDataExternalConnectorOperationRequests(params) {
    return { evid: 2308, src: 83, endpoint: '', params };
}
/**
 * A function to create a log object for the 'collectionAutoOptInForCmsMlIntegration' event (evid: 2309)
 *
 * Event description: Translations were automatically enabled for a collection by Velo Platform backend. This happens when ML app is installed on a site if site has eligible collections (native storage, under 1K items, data binding, etc.)
 *
 * @param {collectionAutoOptInForCmsMlIntegrationParams} params - The parameters for this event
 * @property {GUID} _event_hash - event_hash
 * @property {GUID} visitor_id - visitor_id
 * @property {string} collection_id - Data Collection id
 * @property {GUID} msid - msid
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<collectionAutoOptInForCmsMlIntegrationParams>} LogObject<collectionAutoOptInForCmsMlIntegrationParams>
 */
export function collectionAutoOptInForCmsMlIntegration(params) {
    return { evid: 2309, src: 83, endpoint: '', params };
}
/**
 * A function to create a log object for the 'cmVideoTutorialsClickPlayVideo' event (evid: 231)
 *
 * Event description: Click on the play button for a specific tutorial
 *
 * @param {cmVideoTutorialsClickPlayVideoParams} params - The parameters for this event
 * @property {string} site_id - site_id
 * @property {number} ts - ts
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} version - version
 * @property {string} origin - {autoplay/ discretionary}
 * @property {string} environment - {EDITOR / BUSINESS_MANAGER}
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {GUID} esi - Editor Session Id
 * @property {string} videoName - {}
 * @property {GUID} msid - msid
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<cmVideoTutorialsClickPlayVideoParams>} LogObject<cmVideoTutorialsClickPlayVideoParams>
 */
export function cmVideoTutorialsClickPlayVideo(params) {
    return { evid: 231, src: 83, endpoint: '', params };
}
/**
 * A function to create a log object for the 'cmVideoTutorialsStopPlayingVideo' event (evid: 232)
 *
 * Event description: stop playing a specific video
 *
 * @param {cmVideoTutorialsStopPlayingVideoParams} params - The parameters for this event
 * @property {string} site_id - site_id
 * @property {number} ts - ts
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {number} videoDuration - {the time the video played in sec}
 * @property {string} reason - {video_ended/ exit_panel/ other}
 * @property {number} video_full_duration - {the full time of the video in sec}
 * @property {string} version - version
 * @property {string} environment - {EDITOR / BUSINESS_MANAGER}
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {GUID} esi - Editor Session Id
 * @property {string} videoName - {}
 * @property {GUID} msid - msid
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<cmVideoTutorialsStopPlayingVideoParams>} LogObject<cmVideoTutorialsStopPlayingVideoParams>
 */
export function cmVideoTutorialsStopPlayingVideo(params) {
    return { evid: 232, src: 83, endpoint: '', params };
}
/**
 * A function to create a log object for the 'cmVideoTutorialsYouTubePanelClosed' event (evid: 233)
 *
 * Event description: the user closed the youtube panel of video tutorials
 *
 * @param {cmVideoTutorialsYouTubePanelClosedParams} params - The parameters for this event
 * @property {string} site_id - site_id
 * @property {number} ts - ts
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} version - version
 * @property {string} environment - {EDITOR / BUSINESS_MANAGER}
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {GUID} esi - Editor Session Id
 * @property {GUID} msid - msid
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<cmVideoTutorialsYouTubePanelClosedParams>} LogObject<cmVideoTutorialsYouTubePanelClosedParams>
 */
export function cmVideoTutorialsYouTubePanelClosed(params) {
    return { evid: 233, src: 83, endpoint: '', params };
}
/**
 * A function to create a log object for the 'cmOptionalSandboxSettingsToggled' event (evid: 235)
 *
 * Event description: User toggles optional sandbox on/off
 *
 * @param {cmOptionalSandboxSettingsToggledParams} params - The parameters for this event
 * @property {string} site_id - site_id
 * @property {number} ts - ts
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} version - version
 * @property {boolean} new_state - (true/false)
 * @property {string} environment - EDITOR / BUSINESS_MANAGER
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {GUID} esi - esi
 * @property {GUID} msid - msid
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<cmOptionalSandboxSettingsToggledParams>} LogObject<cmOptionalSandboxSettingsToggledParams>
 */
export function cmOptionalSandboxSettingsToggled(params) {
    return { evid: 235, src: 83, endpoint: '', params };
}
/**
 * A function to create a log object for the 'cmOptionalSandboxSettingsViewed' event (evid: 236)
 *
 * Event description: Triggers upon user landing on the SB settings page within CM
 *
 * @param {cmOptionalSandboxSettingsViewedParams} params - The parameters for this event
 * @property {boolean} is_wix_collections_visibility_enabled - (true/false)
 * @property {string} site_id - site_id
 * @property {number} ts - ts
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} version - version
 * @property {string} origin - CM_SETTINGS_ICON
 * @property {string} environment - EDITOR / BUSINESS_MANAGER
 * @property {boolean} is_sandbox_enabled - (true/false)
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {GUID} esi - esi
 * @property {GUID} msid - msid
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<cmOptionalSandboxSettingsViewedParams>} LogObject<cmOptionalSandboxSettingsViewedParams>
 */
export function cmOptionalSandboxSettingsViewed(params) {
    return { evid: 236, src: 83, endpoint: '', params };
}
/**
 * A function to create a log object for the 'cmClickedInMoreActionsButtonOrTheExpandedValuesOfIt' event (evid: 238)
 *
 * Event description: Clicked More Actions Button or the expanded menu in the content manager page
 *
 * @param {cmClickedInMoreActionsButtonOrTheExpandedValuesOfItParams} params - The parameters for this event
 * @property {string} click_name - {more_actions,backups, settings,learning_center,release_notes,submit_feedback}
 * @property {string} site_id - site_id
 * @property {number} ts - ts
 * @property {string} collection_id - collection_id
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} version - version
 * @property {string} environment - {editor / business_manager}
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {GUID} esi - Editor Session Id
 * @property {GUID} msid - msid
 * @property {string} origin - origin
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<cmClickedInMoreActionsButtonOrTheExpandedValuesOfItParams>} LogObject<cmClickedInMoreActionsButtonOrTheExpandedValuesOfItParams>
 */
export function cmClickedInMoreActionsButtonOrTheExpandedValuesOfIt(params) {
    return { evid: 238, src: 83, endpoint: '', params };
}
/**
 * A function to create a log object for the 'cmClickedInExploreTheBasicsOnContentManagerPage' event (evid: 239)
 *
 * Event description: Clicked any value in the explore basics menu on the CM page
 *
 * @param {cmClickedInExploreTheBasicsOnContentManagerPageParams} params - The parameters for this event
 * @property {string} click_name - {connect_content_to_your_website,release_notes,submit_feedback}
 * @property {string} site_id - site_id
 * @property {number} ts - ts
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} version - version
 * @property {string} environment - {editor / business_manager}
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {GUID} esi - Editor Session Id
 * @property {GUID} msid - msid
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<cmClickedInExploreTheBasicsOnContentManagerPageParams>} LogObject<cmClickedInExploreTheBasicsOnContentManagerPageParams>
 */
export function cmClickedInExploreTheBasicsOnContentManagerPage(params) {
    return { evid: 239, src: 83, endpoint: '', params };
}
/**
 * A function to create a log object for the 'cmsCollectionPublishSiteNotificationTriggerSrc83Evid24' event (evid: 24)
 *
 * Event description: A user should get in the collection page a notification to publish his site on 2 cases- after duplicating a DP or adding a preset. This event is for the trigger that determines if the notification should appear (For AB test exposure)
 *
 * @param {cmsCollectionPublishSiteNotificationTriggerSrc83Evid24Params} params - The parameters for this event
 * @property {string} collection_id - collection_id
 * @property {string} origin - origin of the modal where the user opened the collection from
 * @return {LogObject<cmsCollectionPublishSiteNotificationTriggerSrc83Evid24Params>} LogObject<cmsCollectionPublishSiteNotificationTriggerSrc83Evid24Params>
 */
export function cmsCollectionPublishSiteNotificationTriggerSrc83Evid24(params) {
    return { evid: 24, src: 83, endpoint: '', params };
}
/**
 * A function to create a log object for the 'cmConcurrentEditingError' event (evid: 240)
 *
 * Event description: Trigger when concurrent editing error pops up
 *
 * @param {cmConcurrentEditingErrorParams} params - The parameters for this event
 * @property {string} site_id - site_id
 * @property {number} ts - ts
 * @property {string} collection_id - Conflicting schema name
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} version - version
 * @property {string} edit_source - {dbAddFieldPanel, editCollection, editCollectionPermissions, autoCMS}
 * @property {string} origin - (Save)
 * @property {string} environment - EDITOR/BUSINESS_MANAGER
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {GUID} esi - esi
 * @property {string} collection_name - the collection name
 * @property {GUID} msid - msid
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<cmConcurrentEditingErrorParams>} LogObject<cmConcurrentEditingErrorParams>
 */
export function cmConcurrentEditingError(params) {
    return { evid: 240, src: 83, endpoint: '', params };
}
/**
 * A function to create a log object for the 'cmDashboardPageView' event (evid: 241)
 *
 * Event description: Trigger upon user navigation to the CM dashboard page
 *
 * @param {cmDashboardPageViewParams} params - The parameters for this event
 * @property {number} num_of_collections - number (0, 1, ... , n)
 * @property {string} site_id - site_id
 * @property {number} ts - ts
 * @property {string} empty_state_type - (no_ddmo, no_collections)
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} version - version
 * @property {string} environment - (EDITOR, BUSINESS_MANAGER)
 * @property {boolean} is_sandbox_enabled - (true/false)
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {GUID} esi - esi
 * @property {GUID} msid - msid
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<cmDashboardPageViewParams>} LogObject<cmDashboardPageViewParams>
 */
export function cmDashboardPageView(params) {
    return { evid: 241, src: 83, endpoint: '', params };
}
/**
 * A function to create a log object for the 'getStartedWithWixDataClicked' event (evid: 242)
 *
 * Event description: When a user clicks on "get started" with Wix data
 *
 * @param {getStartedWithWixDataClickedParams} params - The parameters for this event
 * @property {string} site_id - site_id
 * @property {number} ts - ts
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} version - version
 * @property {string} origin - from where the user clciked? cm_dashboard_empty_state,
 * @property {string} environment - {EDITOR / BUSINESS_MANAGER}
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {GUID} esi - Editor Session Id
 * @property {GUID} msid - msid
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<getStartedWithWixDataClickedParams>} LogObject<getStartedWithWixDataClickedParams>
 */
export function getStartedWithWixDataClicked(params) {
    return { evid: 242, src: 83, endpoint: '', params };
}
/**
 * A function to create a log object for the 'cmHoverMessageInBizManagerGoToEditor' event (evid: 243)
 *
 * Event description: CM Hover message in bizManager - go to editor.
 * for stuff you cannot do in bizmanager like change scheme,primary keys, delete collection
 *
 * @param {cmHoverMessageInBizManagerGoToEditorParams} params - The parameters for this event
 * @property {string} site_id - site_id
 * @property {string} page - {content_manager, collection_page}
 * @property {number} ts - ts
 * @property {string} collection_id - collection id
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} version - version
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {string} message - the hover message the user sees
 * @property {GUID} esi - esi
 * @property {string} collection_name - collection name
 * @property {GUID} msid - msid
 * @property {string} action - {add_collection,edit_delete_collection ,make_primary,delete,add_column,field_actions_menu,field_actions_menu,general}
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<cmHoverMessageInBizManagerGoToEditorParams>} LogObject<cmHoverMessageInBizManagerGoToEditorParams>
 */
export function cmHoverMessageInBizManagerGoToEditor(params) {
    return { evid: 243, src: 83, endpoint: '', params };
}
/**
 * A function to create a log object for the 'cmUserClickDeepLinkForContentManager' event (evid: 244)
 *
 * Event description: user click a cta deep link from the content manger collection, that leads to the editor and opens the content manager
 *
 * @param {cmUserClickDeepLinkForContentManagerParams} params - The parameters for this event
 * @property {string} site_id - site_id
 * @property {string} page - {content_manager, collection_page, folder}
 * @property {number} ts - ts
 * @property {string} collection_id - collection id
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} version - version
 * @property {string} environment - {dashboard,editor}
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {string} message - the hover message the user sees
 * @property {GUID} esi - esi
 * @property {string} collection_name - collection name
 * @property {GUID} msid - msid
 * @property {string} action - {add_collection,edit_delete_collection ,make_primary,delete,add_column,field_actions_menu,general}
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<cmUserClickDeepLinkForContentManagerParams>} LogObject<cmUserClickDeepLinkForContentManagerParams>
 */
export function cmUserClickDeepLinkForContentManager(params) {
    return { evid: 244, src: 83, endpoint: '', params };
}
/**
 * A function to create a log object for the 'cmClickedLearnInCmEmptyState' event (evid: 245)
 *
 * Event description: Clicked Learn in CM empty state
 *
 * @param {cmClickedLearnInCmEmptyStateParams} params - The parameters for this event
 * @property {string} click_name - {learn_how, learn_more}
 * @property {string} site_id - site_id
 * @property {number} ts - ts
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} version - version
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {GUID} esi - esi
 * @property {GUID} msid - msid
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<cmClickedLearnInCmEmptyStateParams>} LogObject<cmClickedLearnInCmEmptyStateParams>
 */
export function cmClickedLearnInCmEmptyState(params) {
    return { evid: 245, src: 83, endpoint: '', params };
}
/**
 * A function to create a log object for the 'bmToggleSidebarMenu' event (evid: 246)
 *
 * Event description: User toggles the BM sidebar show/hide button
 *
 * @param {bmToggleSidebarMenuParams} params - The parameters for this event
 * @property {number} num_of_collections - number (0, 1, ... , n)
 * @property {string} site_id - site_id
 * @property {string} page - {content_manager, collection_page, settings}
 * @property {number} ts - ts
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} version - version
 * @property {boolean} new_state - {true = show, false = hide}
 * @property {string} environment - (EDITOR, BUSINESS_MANAGER)
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {GUID} esi - esi
 * @property {GUID} msid - msid
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<bmToggleSidebarMenuParams>} LogObject<bmToggleSidebarMenuParams>
 */
export function bmToggleSidebarMenu(params) {
    return { evid: 246, src: 83, endpoint: '', params };
}
/**
 * A function to create a log object for the 'cmFieldDescriptionHoverInteraction' event (evid: 247)
 *
 * Event description: Triggers when users hovers over a user-generated message regarding a field.
 *
 * @param {cmFieldDescriptionHoverInteractionParams} params - The parameters for this event
 * @property {string} site_id - site_id
 * @property {string} page - {cm_dashboard, collection_page}
 * @property {number} ts - ts
 * @property {string} collection_id - collection id
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} version - version
 * @property {string} environment - EDITOR, BUSINESS_MANAGER
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {string} message - the hover message the user sees
 * @property {GUID} esi - esi
 * @property {string} collection_name - collection name
 * @property {GUID} msid - msid
 * @property {string} field_name - field_name
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<cmFieldDescriptionHoverInteractionParams>} LogObject<cmFieldDescriptionHoverInteractionParams>
 */
export function cmFieldDescriptionHoverInteraction(params) {
    return { evid: 247, src: 83, endpoint: '', params };
}
/**
 * A function to create a log object for the 'cmOpenFieldTypeMenu' event (evid: 248)
 *
 * Event description: Triggers when a user open the field type dropdown menu inside the CMS
 *
 * @param {cmOpenFieldTypeMenuParams} params - The parameters for this event
 * @property {string} site_id - site_id
 * @property {number} ts - ts
 * @property {string} collection_id - collection id
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} version - version
 * @property {boolean} is_live_data - (true/false)
 * @property {string} environment - (Editor/Business_manager)
 * @property {boolean} is_sandbox_enabled - (true/false)
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {GUID} esi - editor session id
 * @property {string} collection_name - collection name
 * @property {GUID} msid - msid
 * @property {string} field_name - the name (title) of the field
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<cmOpenFieldTypeMenuParams>} LogObject<cmOpenFieldTypeMenuParams>
 */
export function cmOpenFieldTypeMenu(params) {
    return { evid: 248, src: 83, endpoint: '', params };
}
/**
 * A function to create a log object for the 'cmDbDriversSettingsToggled' event (evid: 249)
 *
 * Event description: User toggles wix collections visibilty (aka db drivers)
 *
 * @param {cmDbDriversSettingsToggledParams} params - The parameters for this event
 * @property {string} site_id - site_id
 * @property {number} ts - ts
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} version - version
 * @property {boolean} new_state - (true/false)
 * @property {string} environment - EDITOR / BUSINESS_MANAGER
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {GUID} esi - esi
 * @property {GUID} msid - msid
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<cmDbDriversSettingsToggledParams>} LogObject<cmDbDriversSettingsToggledParams>
 */
export function cmDbDriversSettingsToggled(params) {
    return { evid: 249, src: 83, endpoint: '', params };
}
/**
 * A function to create a log object for the 'restoreCollectionToANewCollectionSrc83Evid25' event (evid: 25)
 *
 * Event description: User restores a collection backup into a new collection. This is the new collection definition modal
 *
 * @param {restoreCollectionToANewCollectionSrc83Evid25Params} params - The parameters for this event
 * @property {number} amount_selected - number of collections selected for restore
 * @property {DateTime} backup_date - date backup was created
 * @property {string} environment - {Editor / business_manager}
 * @property {string} action - cancel, restore
 * @property {string} restore_type - create_new
 * @property {string} collection_id - id of the new collection
 * @property {string} collection_name - name of the new collection
 * @property {string} original_collection_id - id of the restored collection
 * @property {number} num_of_rows - num of rows in new collection
 * @property {number} num_of_fields - num of fields in new collection
 * @return {LogObject<restoreCollectionToANewCollectionSrc83Evid25Params>} LogObject<restoreCollectionToANewCollectionSrc83Evid25Params>
 */
export function restoreCollectionToANewCollectionSrc83Evid25(params) {
    return { evid: 25, src: 83, endpoint: '', params };
}
/**
 * A function to create a log object for the 'cmCollectionSettingsViewed' event (evid: 250)
 *
 * Event description: User views settings menu of collection. Triggers for every tab that is viewed.
 *
 * @param {cmCollectionSettingsViewedParams} params - The parameters for this event
 * @property {string} site_id - site_id
 * @property {string} tab_name - (basic_info, properties)
 * @property {number} ts - ts
 * @property {string} collection_id - collection id
 * @property {boolean} is_draft_publish_enabled - (true/false)
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {boolean} is_shared - is shared collection
 * @property {string} version - version
 * @property {string} origin - (cm_dashboard, collection)
 * @property {string} environment - EDITOR / BUSINESS_MANAGER
 * @property {boolean} is_sandbox_enabled - (true/false)
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {string} default_new_item_status - (draft, published)
 * @property {GUID} esi - esi
 * @property {string} collection_name - collection_name
 * @property {GUID} msid - msid
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<cmCollectionSettingsViewedParams>} LogObject<cmCollectionSettingsViewedParams>
 */
export function cmCollectionSettingsViewed(params) {
    return { evid: 250, src: 83, endpoint: '', params };
}
/**
 * A function to create a log object for the 'wixDataStorageUsage' event (evid: 2501)
 *
 * Event description: Total usage of collection's data. Updating eventually in case if site's collections were updated. Calculate both sandbox/live collections. Not relevant for external DB.
 *
 * @param {wixDataStorageUsageParams} params - The parameters for this event
 * @property {GUID} msid - msid
 * @property {number} storageUsage - current storage usage in bytes
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<wixDataStorageUsageParams>} LogObject<wixDataStorageUsageParams>
 */
export function wixDataStorageUsage(params) {
    return { evid: 2501, src: 83, endpoint: '', params };
}
/**
 * A function to create a log object for the 'wixDataStorageQuota' event (evid: 2502)
 *
 * Event description: Sending relevant quota size in bytes per site. Changing in case when site size quota were changed.
 *
 * @param {wixDataStorageQuotaParams} params - The parameters for this event
 * @property {GUID} msid - msid
 * @property {number} storageQuota - current storage quota in bytes
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<wixDataStorageQuotaParams>} LogObject<wixDataStorageQuotaParams>
 */
export function wixDataStorageQuota(params) {
    return { evid: 2502, src: 83, endpoint: '', params };
}
/**
 * A function to create a log object for the 'wixDataCollectionStorageUsage' event (evid: 2503)
 *
 * Event description: Usage of data for distinct collection. Updating every eventually, in case collection was updated. Calculate both sandbox/live collections. Not relevant for external DB.
 *
 * @param {wixDataCollectionStorageUsageParams} params - The parameters for this event
 * @property {string} data_store - public/dev, public - live connection, dev - sandbox
 * @property {string} displayName - the collection name that is displayed to the user
 * @property {string} dataStore - public/dev, public - live connection, dev - sandbox
 * @property {string} collectionId - collection_name
 * @property {string} collection_name - the collection name
 * @property {GUID} msid - msid
 * @property {number} storageUsage - current storage usage in bytes for distinct collection
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<wixDataCollectionStorageUsageParams>} LogObject<wixDataCollectionStorageUsageParams>
 */
export function wixDataCollectionStorageUsage(params) {
    return { evid: 2503, src: 83, endpoint: '', params };
}
/**
 * A function to create a log object for the 'cmCollectionSettingsChanged' event (evid: 251)
 *
 * Event description: CM Collection Settings Changed.
 *
 * @param {cmCollectionSettingsChangedParams} params - The parameters for this event
 * @property {string} site_id - site_id
 * @property {number} ts - ts
 * @property {string} collection_id - collection id
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {boolean} is_shared - is shared collection
 * @property {string} version - version
 * @property {string} origin - (cm_dashboard, collection_page, collection_creation_flow)
 * @property {string} environment - EDITOR / BUSINESS_MANAGER
 * @property {boolean} is_sandbox_enabled - is_sandbox_enabled
 * @property {string} userEnv - user_env
 * @property {string} new_value - (collection_name, collection_id, single_item, multi_item, permissions, draft/published, enabled/disabled)
 * @property {string} editor_working_mode - editor_working_mode
 * @property {string} setting_name - (collection_name, permissions, default_new_item_status, draft_publish_mechanism, hide_table_layout)
 * @property {GUID} esi - esi
 * @property {string} collection_name - collection_name (original)
 * @property {GUID} msid - msid
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<cmCollectionSettingsChangedParams>} LogObject<cmCollectionSettingsChangedParams>
 */
export function cmCollectionSettingsChanged(params) {
    return { evid: 251, src: 83, endpoint: '', params };
}
/**
 * A function to create a log object for the 'cmCollectionSettingsSaved' event (evid: 252)
 *
 * Event description: User clicks 'Save' on collection settings modal.
 *
 * @param {cmCollectionSettingsSavedParams} params - The parameters for this event
 * @property {string} site_id - site_id
 * @property {number} ts - ts
 * @property {string} collection_id - collection id
 * @property {boolean} is_draft_publish_enabled - (true/false)
 * @property {string} viewmode - viewmode
 * @property {boolean} changed_draft_publish - (true/false)
 * @property {string} input_name - input_name
 * @property {boolean} is_shared - is shared collection
 * @property {string} version - version
 * @property {boolean} changed_default_new_item_status - (true/false)
 * @property {string} origin - (cm_dashboard, collection)
 * @property {string} environment - EDITOR / BUSINESS_MANAGER
 * @property {boolean} is_sandbox_enabled - (true/false)
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {string} default_new_item_status - (draft, published)
 * @property {GUID} esi - esi
 * @property {string} collection_name - collection_id
 * @property {GUID} msid - msid
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<cmCollectionSettingsSavedParams>} LogObject<cmCollectionSettingsSavedParams>
 */
export function cmCollectionSettingsSaved(params) {
    return { evid: 252, src: 83, endpoint: '', params };
}
/**
 * A function to create a log object for the 'cmDraftStatusMenuOpened' event (evid: 253)
 *
 * Event description: User opens the draft/publish status menu of item(s) within a collection
 *
 * @param {cmDraftStatusMenuOpenedParams} params - The parameters for this event
 * @property {string} site_id - site_id
 * @property {number} ts - ts
 * @property {string} collection_id - collection id
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} version - version
 * @property {boolean} is_live_data - (true/false)
 * @property {string} origin - (item_status, bulk_actions)
 * @property {string} environment - (Editor/Dashboard)
 * @property {boolean} is_sandbox_enabled - (true/false)
 * @property {string} view_layout - (table, list, gallery)
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {GUID} esi - esi
 * @property {string} current_status - draft, published, scheduled_to_publish, scheduled_to_draft
 * @property {string} collection_name - collection name
 * @property {GUID} msid - msid
 * @property {number} number_of_items - number of items selected
 * @property {string} itemId - Unique ID of collection item (Row)
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<cmDraftStatusMenuOpenedParams>} LogObject<cmDraftStatusMenuOpenedParams>
 */
export function cmDraftStatusMenuOpened(params) {
    return { evid: 253, src: 83, endpoint: '', params };
}
/**
 * A function to create a log object for the 'cmDraftStatusChanged' event (evid: 254)
 *
 * Event description: CMS When a user changes the item visibility Status (hidden/visible)
 *
 * @param {cmDraftStatusChangedParams} params - The parameters for this event
 * @property {string} site_id - site_id
 * @property {number} ts - ts
 * @property {string} collection_id - collection_id
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} version - version
 * @property {boolean} is_live_data - (true/false)
 * @property {string} new_status - draft, published, scheduled_to_publish, scheduled_to_draft
 * @property {string} origin - (item_status, bulk_actions)
 * @property {string} environment - (Editor/Dashboard)
 * @property {boolean} is_sandbox_enabled - (true/false)
 * @property {string} view_layout - (table, list, gallery)
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {GUID} esi - esi
 * @property {string} collection_name - collection name
 * @property {GUID} msid - msid
 * @property {number} number_of_items - number of items selected
 * @property {string} itemId - Unique ID of collection item (Row)
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<cmDraftStatusChangedParams>} LogObject<cmDraftStatusChangedParams>
 */
export function cmDraftStatusChanged(params) {
    return { evid: 254, src: 83, endpoint: '', params };
}
/**
 * A function to create a log object for the 'cmFieldTypeChanged' event (evid: 255)
 *
 * Event description: CM Field Type Changed.
 *
 * @param {cmFieldTypeChangedParams} params - The parameters for this event
 * @property {string} site_id - site_id
 * @property {string} field_type - String, Image
 * @property {number} ts - ts
 * @property {string} collection_id - collection id
 * @property {boolean} is_draft_publish_enabled - (true, false)
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} version - version
 * @property {boolean} is_live_data - (true/false)
 * @property {string} origin - (TABLE_VISIBLE_FIELDS_BUTTON, TABLE_FIELD_HEADER,LIST_VISIBLE_FIELDS_BUTTON, GALLERY_VISIBLE_FIELDS_BUTTON, ITEM_VISIBLE_FIELD_BUTTON)
 * @property {string} environment - (Editor/Dashboard)
 * @property {boolean} is_sandbox_enabled - (true/false)
 * @property {string} view_layout - (table, list, gallery)
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {GUID} esi - esi
 * @property {string} collection_name - collection name
 * @property {GUID} msid - msid
 * @property {string} old_field_type - old_field_type
 * @property {string} field_name - field_name
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<cmFieldTypeChangedParams>} LogObject<cmFieldTypeChangedParams>
 */
export function cmFieldTypeChanged(params) {
    return { evid: 255, src: 83, endpoint: '', params };
}
/**
 * A function to create a log object for the 'cmFieldRenamed' event (evid: 256)
 *
 * Event description: User changes field name in a collection. Trigger upon save.
 *
 * @param {cmFieldRenamedParams} params - The parameters for this event
 * @property {string} old_field_name - old_field_type
 * @property {string} site_id - site_id
 * @property {string} field_type - String, Image
 * @property {number} ts - ts
 * @property {string} collection_id - collection id
 * @property {boolean} is_draft_publish_enabled - (true, false)
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} version - version
 * @property {boolean} is_live_data - (true/false)
 * @property {string} origin - (TABLE_VISIBLE_FIELDS_BUTTON, TABLE_FIELD_HEADER,LIST_VISIBLE_FIELDS_BUTTON, GALLERY_VISIBLE_FIELDS_BUTTON, ITEM_VISIBLE_FIELD_BUTTON)
 * @property {string} environment - (Editor/Dashboard)
 * @property {boolean} is_sandbox_enabled - (true/false)
 * @property {string} view_layout - (table, list, gallery)
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {GUID} esi - esi
 * @property {string} collection_name - collection name
 * @property {GUID} msid - msid
 * @property {string} field_name - field_name
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<cmFieldRenamedParams>} LogObject<cmFieldRenamedParams>
 */
export function cmFieldRenamed(params) {
    return { evid: 256, src: 83, endpoint: '', params };
}
/**
 * A function to create a log object for the 'veloEventCorvidMiniSiteResultInSearchPage' event (evid: 257)
 *
 * Event description: sent by velo events(implemented by velo wix on wix)
 *
 * when user got into a search result page in the new united search resource pages.
 * https://www.velo-search.com/?search=stores
 *
 * @param {veloEventCorvidMiniSiteResultInSearchPageParams} params - The parameters for this event
 * @property {string} site_id - site_id
 * @property {string} correlation_id - unique identifier per search session
 * @property {string} tab_name - [Deprecated]on which tab the user has landed, currently should be -->all
 * @property {number} ts - ts
 * @property {number} number_of_results_total - how many results are shown in total
 * @property {string} search_term_before_suggestion_chosen - relevant only when user is choosing a suggestion. what the user wrote before suggestion was chosen.
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} results_map - {"examples":17, "help_center":5, "api_reference":2, "velo blog":10}
 * @property {string} version - version
 * @property {string} search_term - what the user searched for
 * @property {string} origin - from where the user got to the result page: search_bar/search_bar_suggestion/direct/selected_resources
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {number} number_of_results_of_selected_resources - results of the selected resources
 * @property {GUID} esi - esi
 * @property {GUID} msid - msid
 * @property {string} selected_resources - which categories the user chose: help_center/api_ref/examples...
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<veloEventCorvidMiniSiteResultInSearchPageParams>} LogObject<veloEventCorvidMiniSiteResultInSearchPageParams>
 */
export function veloEventCorvidMiniSiteResultInSearchPage(params) {
    return { evid: 257, src: 83, endpoint: '', params };
}
/**
 * A function to create a log object for the 'veloEventCorvidMiniSiteResultInSearchPageChangeTab' event (evid: 258)
 *
 * Event description: Converting special value in collection. Triggered in case when user have unconverted record in fields, and decided then to convert it.
 *
 * @param {veloEventCorvidMiniSiteResultInSearchPageChangeTabParams} params - The parameters for this event
 * @property {string} site_id - site_id
 * @property {string} click_type - 'convert'/'clear'
 * @property {string} field_type - new(converted) type; String, Image,other
 * @property {number} ts - ts
 * @property {string} collection_id - collection id
 * @property {boolean} is_draft_publish_enabled - (true/false)
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} field_id - field_id
 * @property {string} version - version
 * @property {boolean} is_live_data - (true/false)
 * @property {string} origin - origin
 * @property {string} environment - environment
 * @property {boolean} is_sandbox_enabled - (true/false)
 * @property {string} view_layout - (table, list, gallery)
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {GUID} esi - esi
 * @property {string} collection_name - collection name
 * @property {GUID} msid - msid
 * @property {string} old_field_type - old unconverted type of the field
 * @property {string} field_name - field_name
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<veloEventCorvidMiniSiteResultInSearchPageChangeTabParams>} LogObject<veloEventCorvidMiniSiteResultInSearchPageChangeTabParams>
 */
export function veloEventCorvidMiniSiteResultInSearchPageChangeTab(params) {
    return { evid: 258, src: 83, endpoint: '', params };
}
/**
 * A function to create a log object for the 'veloEventCorvidMiniSiteResultInSearchPageClickItem' event (evid: 259)
 *
 * Event description: sent by velo events(implemented by velo wix on wix)
 *
 * when user click result item in a search result page in the new united search resource pages.
 * https://www.velo-search.com/?search=stores
 *
 * @param {veloEventCorvidMiniSiteResultInSearchPageClickItemParams} params - The parameters for this event
 * @property {string} site_id - site_id
 * @property {string} correlation_id - unique identifier per search session
 * @property {string} tab_name - [Deprecated]on which tab the user has landed, currently should be -->all
 * @property {number} ts - ts
 * @property {number} number_of_results_total - how many results are shown in total
 * @property {string} content_resource - help_center/api_reference/examples..
 * @property {string} search_term_before_suggestion_chosen - relevant only when user is choosing a suggestion. what the user wrote before suggestion was chosen.
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} results_map - {"example":17, "help_center":5, "api_reference":2, "velo blog":10}
 * @property {string} content_name - the header of the content, for example: wix-stores:getProductVariants
 * @property {string} version - version
 * @property {string} search_term - what the user searched for
 * @property {string} content_url - the url the uou clicked on
 * @property {string} origin - from where the user got to the result page: search_bar/search_bar_suggestion/direct/selected_resources
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {number} number_of_results_of_selected_resources - results of selected resources
 * @property {number} search_index - index of the clicked item from search result set
 * @property {GUID} esi - esi
 * @property {GUID} msid - msid
 * @property {string} selected_resources - which categories the user chose: help_center/api_ref/examples...
 * @property {number} page_number - the page number the user is on
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<veloEventCorvidMiniSiteResultInSearchPageClickItemParams>} LogObject<veloEventCorvidMiniSiteResultInSearchPageClickItemParams>
 */
export function veloEventCorvidMiniSiteResultInSearchPageClickItem(params) {
    return { evid: 259, src: 83, endpoint: '', params };
}
/**
 * A function to create a log object for the 'cmConvertingWarningIconClick' event (evid: 260)
 *
 * Event description: CM - Converting warning tooltip shown.
 *
 * @param {cmConvertingWarningIconClickParams} params - The parameters for this event
 * @property {number} is_converable - true if this field have ability to be coveted, false if not.
 * @property {string} site_id - site_id
 * @property {string} field_type - new type of field; String, Image,other
 * @property {number} ts - ts
 * @property {string} collection_id - collection id
 * @property {boolean} is_draft_publish_enabled - is_draft_publish_enabled
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} field_id - field_id
 * @property {string} version - version
 * @property {boolean} is_live_data - (true/false)
 * @property {string} origin - table/form/single_item
 * @property {string} environment - (Editor/Dashboard)
 * @property {boolean} is_sandbox_enabled - is_sandbox_enabled
 * @property {string} view_layout - view_layout
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {GUID} esi - esi
 * @property {string} collection_name - collection_name
 * @property {GUID} msid - msid
 * @property {boolean} is_convertable - true if this field have ability to be coveted, false if not.
 * @property {string} field_name - field_name
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<cmConvertingWarningIconClickParams>} LogObject<cmConvertingWarningIconClickParams>
 */
export function cmConvertingWarningIconClick(params) {
    return { evid: 260, src: 83, endpoint: '', params };
}
/**
 * A function to create a log object for the 'externalDatabaseSittings' event (evid: 262)
 *
 * Event description: Triggered after first time the external db adapter is connected. Give us information on external databases and collections that are set up.
 *
 * @param {externalDatabaseSittingsParams} params - The parameters for this event
 * @property {string} db_type - name of the database connected here; postgres / mysql / mongo etc.
 * @property {GUID} uuid - uuid
 * @property {string} adapter_version - running version
 * @property {string} cloud_vendor - cloud_vendor
 * @property {GUID} msid - msid
 * @property {string} protocol_version - protocol_version
 * @property {string} namespace - namespace
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<externalDatabaseSittingsParams>} LogObject<externalDatabaseSittingsParams>
 */
export function externalDatabaseSittings(params) {
    return { evid: 262, src: 83, endpoint: '', params };
}
/**
 * A function to create a log object for the 'indexesOpenClick' event (evid: 264)
 *
 * Event description: Event should be triggered when user clicks on “Indexes” from the “...” menu from the collection view in CMS.
 *
 * @param {indexesOpenClickParams} params - The parameters for this event
 * @property {string} site_id - site_id
 * @property {number} ts - ts
 * @property {string} collection_id - collection_id
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} version - version
 * @property {string} environment - Editor/Dashboard
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {GUID} esi - esi
 * @property {string} collection_name - collection name
 * @property {GUID} msid - msid
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<indexesOpenClickParams>} LogObject<indexesOpenClickParams>
 */
export function indexesOpenClick(params) {
    return { evid: 264, src: 83, endpoint: '', params };
}
/**
 * A function to create a log object for the 'indexesSaveClick' event (evid: 265)
 *
 * Event description: Triggered when user clicks to “Save” on indexes page/modal
 *
 * @param {indexesSaveClickParams} params - The parameters for this event
 * @property {string} index_name - The title user gave to the index
 * @property {string} site_id - site_id
 * @property {number} ts - ts
 * @property {string} collection_id - collection_id
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} version - version
 * @property {string} index_fields - JSON of fields selected to be indexed in the same order as selected by the user and the direction of indexing (asc/desc). Can have up to 3 fields.
 * @property {string} environment - EDITOR, BUSINESS_MANAGER
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {GUID} esi - esi
 * @property {string} collection_name - collection name
 * @property {GUID} msid - msid
 * @property {string} index_type - Regular or Unique
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<indexesSaveClickParams>} LogObject<indexesSaveClickParams>
 */
export function indexesSaveClick(params) {
    return { evid: 265, src: 83, endpoint: '', params };
}
/**
 * A function to create a log object for the 'indexesAddNewClick' event (evid: 266)
 *
 * Event description: Indexes - Add new click.
 *
 * @param {indexesAddNewClickParams} params - The parameters for this event
 * @property {string} site_id - site_id
 * @property {number} ts - ts
 * @property {string} collection_id - collection_id
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} version - version
 * @property {string} environment - Editor/Dashboard
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {GUID} esi - esi
 * @property {string} collection_name - collection name
 * @property {GUID} msid - msid
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<indexesAddNewClickParams>} LogObject<indexesAddNewClickParams>
 */
export function indexesAddNewClick(params) {
    return { evid: 266, src: 83, endpoint: '', params };
}
/**
 * A function to create a log object for the 'indexesDeleteClick' event (evid: 267)
 *
 * Event description: Triggered when user click on 'Delete' button.
 *
 * @param {indexesDeleteClickParams} params - The parameters for this event
 * @property {string} index_name - The title user gave to the index
 * @property {string} site_id - site_id
 * @property {number} ts - ts
 * @property {string} collection_id - collection_id
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} version - version
 * @property {string} index_fields - JSON of fields selected to be indexed in the same order as selected by the user and the direction of indexing (asc/desc). Can have up to 3 fields.
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {GUID} esi - esi
 * @property {string} collection_name - collection_name
 * @property {GUID} msid - msid
 * @property {string} index_type - index_type
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<indexesDeleteClickParams>} LogObject<indexesDeleteClickParams>
 */
export function indexesDeleteClick(params) {
    return { evid: 267, src: 83, endpoint: '', params };
}
/**
 * A function to create a log object for the 'indexesCancelClick' event (evid: 268)
 *
 * Event description: Click on cancel button.
 *
 * @param {indexesCancelClickParams} params - The parameters for this event
 * @property {string} site_id - site_id
 * @property {number} ts - ts
 * @property {string} collection_id - collection_id
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} version - version
 * @property {string} origin - origin
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {GUID} esi - esi
 * @property {string} collection_name - collection name
 * @property {GUID} msid - msid
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<indexesCancelClickParams>} LogObject<indexesCancelClickParams>
 */
export function indexesCancelClick(params) {
    return { evid: 268, src: 83, endpoint: '', params };
}
/**
 * A function to create a log object for the 'indexesDeleteFieldClick' event (evid: 269)
 *
 * Event description: Triggered when user click on delete for specific field in index settings.
 *
 * @param {indexesDeleteFieldClickParams} params - The parameters for this event
 * @property {string} site_id - site_id
 * @property {number} ts - ts
 * @property {string} collection_id - collection_id
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} version - version
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {GUID} esi - esi
 * @property {string} collection_name - collection name
 * @property {GUID} msid - msid
 * @property {string} field_name - deleted field name
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<indexesDeleteFieldClickParams>} LogObject<indexesDeleteFieldClickParams>
 */
export function indexesDeleteFieldClick(params) {
    return { evid: 269, src: 83, endpoint: '', params };
}
/**
 * A function to create a log object for the 'clickOnCmsIntroVideosSrc83Evid27' event (evid: 27)
 *
 * Event description: When a user clicks on any of the CMS training videos in the CMS panel
 *
 * @param {clickOnCmsIntroVideosSrc83Evid27Params} params - The parameters for this event
 * @property {string} button_name - button_name
 * @property {string} panel_name - panel_name
 * @return {LogObject<clickOnCmsIntroVideosSrc83Evid27Params>} LogObject<clickOnCmsIntroVideosSrc83Evid27Params>
 */
export function clickOnCmsIntroVideosSrc83Evid27(params) {
    return { evid: 27, src: 83, endpoint: '', params };
}
/**
 * A function to create a log object for the 'indexesAddFieldClick' event (evid: 270)
 *
 * Event description: Indexes -Add field click.
 *
 * @param {indexesAddFieldClickParams} params - The parameters for this event
 * @property {string} site_id - site_id
 * @property {number} ts - ts
 * @property {string} collection_id - collection_id
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} version - version
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {GUID} esi - esi
 * @property {string} collection_name - collection name
 * @property {GUID} msid - msid
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<indexesAddFieldClickParams>} LogObject<indexesAddFieldClickParams>
 */
export function indexesAddFieldClick(params) {
    return { evid: 270, src: 83, endpoint: '', params };
}
/**
 * A function to create a log object for the 'copyOfClouddbProvisioningFlow' event (evid: 2701)
 *
 * Event description: fires at different stages  (start, success, failed) of the connecting managed external DB flow
 *
 * @param {copyOfClouddbProvisioningFlowParams} params - The parameters for this event
 * @property {string} stage - start_provisioning/ db_ready/ creation_failed
 * @property {string} failureReason - failure_reason
 * @property {string} deploymentId - deployment_id
 * @property {string} failureStep - failure_step
 * @property {string} cloudProvider - google
 * @property {string} dbType - postgres / mysql
 * @property {string} namespace - namespace
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<copyOfClouddbProvisioningFlowParams>} LogObject<copyOfClouddbProvisioningFlowParams>
 */
export function copyOfClouddbProvisioningFlow(params) {
    return { evid: 2701, src: 83, endpoint: '', params };
}
/**
 * A function to create a log object for the 'indexesChooseIndexTypeClick' event (evid: 271)
 *
 * Event description: Triggered after user click to choose index type.
 *
 * @param {indexesChooseIndexTypeClickParams} params - The parameters for this event
 * @property {string} site_id - site_id
 * @property {number} ts - ts
 * @property {string} collection_id - collection_id
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} version - version
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {GUID} esi - esi
 * @property {string} collection_name - collection_name
 * @property {GUID} msid - msid
 * @property {string} index_type - Regular/Unique
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<indexesChooseIndexTypeClickParams>} LogObject<indexesChooseIndexTypeClickParams>
 */
export function indexesChooseIndexTypeClick(params) {
    return { evid: 271, src: 83, endpoint: '', params };
}
/**
 * A function to create a log object for the 'indexesLearnMoreClick' event (evid: 272)
 *
 * Event description: Click to learn more about indexes. Can be from different "index pages".
 *
 * @param {indexesLearnMoreClickParams} params - The parameters for this event
 * @property {string} site_id - site_id
 * @property {number} ts - ts
 * @property {string} collection_id - collection_id
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} version - version
 * @property {string} origin - Index page name: "main_page”/empty_main_page/“setup”
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {GUID} esi - esi
 * @property {string} collection_name - collection name
 * @property {GUID} msid - msid
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<indexesLearnMoreClickParams>} LogObject<indexesLearnMoreClickParams>
 */
export function indexesLearnMoreClick(params) {
    return { evid: 272, src: 83, endpoint: '', params };
}
/**
 * A function to create a log object for the 'cmCopyFieldId' event (evid: 273)
 *
 * Event description: User copies field_id within collection settings
 *
 * @param {cmCopyFieldIdParams} params - The parameters for this event
 * @property {string} site_id - site_id
 * @property {number} ts - ts
 * @property {string} collection_id - collection id
 * @property {string} viewmode - viewmode
 * @property {string} input_name - field_id
 * @property {string} version - version
 * @property {boolean} is_live_data - {Live=true, Sandbox=false}
 * @property {string} origin - {field_creation,field_view}
 * @property {string} environment - (Editor/Dashboard)
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {GUID} esi - esi
 * @property {string} collection_name - collection name
 * @property {GUID} msid - msid
 * @property {string} action - copy
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<cmCopyFieldIdParams>} LogObject<cmCopyFieldIdParams>
 */
export function cmCopyFieldId(params) {
    return { evid: 273, src: 83, endpoint: '', params };
}
/**
 * A function to create a log object for the 'cmFieldIdRenamed' event (evid: 274)
 *
 * Event description: User changes field id while adding new field to the collection.
 *
 * @param {cmFieldIdRenamedParams} params - The parameters for this event
 * @property {string} site_id - site_id
 * @property {string} old_field_id - old_field_id
 * @property {number} ts - ts
 * @property {string} collection_id - collection id
 * @property {boolean} is_draft_publish_enabled - (true, false)
 * @property {string} viewmode - viewmode
 * @property {string} input_name - new_field_id
 * @property {string} version - version
 * @property {boolean} is_live_data - (true/false)
 * @property {string} origin - (TABLE_VISIBLE_FIELDS_BUTTON, TABLE_FIELD_HEADER,LIST_VISIBLE_FIELDS_BUTTON, GALLERY_VISIBLE_FIELDS_BUTTON, ITEM_VISIBLE_FIELD_BUTTON)
 * @property {string} environment - Editor
 * @property {boolean} is_sandbox_enabled - (true/false)
 * @property {string} view_layout - (table, list, gallery)
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {GUID} esi - esi
 * @property {string} collection_name - collection name
 * @property {GUID} msid - metasite id
 * @property {string} field_name - field_name
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<cmFieldIdRenamedParams>} LogObject<cmFieldIdRenamedParams>
 */
export function cmFieldIdRenamed(params) {
    return { evid: 274, src: 83, endpoint: '', params };
}
/**
 * A function to create a log object for the 'mobileCmInfoPopup' event (evid: 277)
 *
 * Event description: Mobile CM Popup Opened on Collections List
 *
 * @param {mobileCmInfoPopupParams} params - The parameters for this event
 * @property {string} site_id - site_id
 * @property {string} tab - manage_tab / platforms_tab
 * @property {number} ts - ts
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} version - version
 * @property {string} screen - collection_list / collection_item_list / collection_item / collection_view
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {string} message - collection_creation_instructions
 * @property {GUID} esi - esi
 * @property {GUID} msid - msid
 * @property {string} action - show/hide
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<mobileCmInfoPopupParams>} LogObject<mobileCmInfoPopupParams>
 */
export function mobileCmInfoPopup(params) {
    return { evid: 277, src: 83, endpoint: '', params };
}
/**
 * A function to create a log object for the 'mobileCmErrorShown' event (evid: 278)
 *
 * Event description: Mobile CM Error Shown.
 *
 * @param {mobileCmErrorShownParams} params - The parameters for this event
 * @property {string} site_id - site_id
 * @property {string} tab - manage_tab / platforms_tab
 * @property {number} ts - ts
 * @property {string} collection_id - collection id
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} version - version
 * @property {string} error_type - offline / failure / empty / empty_search / potentially_outdated_item_listing_warning / empty_state_secondary_action_url_opening_failure / page_link_field_preview_url_opening_failure / url_field_url_opening_failure
 * @property {string} screen - collection_list / collection_item_list / collection_item / collection_view
 * @property {string} error_format - state / toast / substate
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {GUID} esi - esi
 * @property {GUID} msid - guid
 * @property {string} action - open_screen / back_online_in_offline_state / back_online_in_offline_substate / trigger_failure_state_action / trigger_failure_substate_action / change_view / search / clear_search / pull_list_to_refresh / scroll_list_to_end_for_infinite_loading / change_item / create_item_via_new_item_action / create_item_via_empty_substate_action / duplicate_item / remove_item / trigger_potentially_outdated_item_listing_warning_toast_action / open_empty_state_secondary_action_url / save_item / open_page_link_field_preview_url / open_url_field_url
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<mobileCmErrorShownParams>} LogObject<mobileCmErrorShownParams>
 */
export function mobileCmErrorShown(params) {
    return { evid: 278, src: 83, endpoint: '', params };
}
/**
 * A function to create a log object for the 'cmCollectionItemSettingsViewed' event (evid: 279)
 *
 * Event description: User views settings menu of collection item.
 *
 * @param {cmCollectionItemSettingsViewedParams} params - The parameters for this event
 * @property {string} site_id - site_id
 * @property {number} ts - ts
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} version - version
 * @property {string} origin - mobile_item_page, mobile_collection, etc. In One App, the origins are "manage_tab" and "platforms_tab".
 * @property {string} environment - EDITOR / BUSINESS_MANAGER / one_app
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {GUID} esi - esi
 * @property {string} collection_name - In One App, it's a collection ID.
 * @property {GUID} msid - msid
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<cmCollectionItemSettingsViewedParams>} LogObject<cmCollectionItemSettingsViewedParams>
 */
export function cmCollectionItemSettingsViewed(params) {
    return { evid: 279, src: 83, endpoint: '', params };
}
/**
 * A function to create a log object for the 'mobileCmClickErrorScreenButton' event (evid: 280)
 *
 * Event description: User clicks any of Error Screen Buttons
 *
 * @param {mobileCmClickErrorScreenButtonParams} params - The parameters for this event
 * @property {string} site_id - site_id
 * @property {string} tab - manage_tab / platforms_tab
 * @property {number} ts - ts
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} version - version
 * @property {string} error_type - failure / empty / potentially_outdated_item_listing_warning
 * @property {string} screen - collection_list / collection_item_list / collection_item / collection_view
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {GUID} esi - esi
 * @property {GUID} msid - msid
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<mobileCmClickErrorScreenButtonParams>} LogObject<mobileCmClickErrorScreenButtonParams>
 */
export function mobileCmClickErrorScreenButton(params) {
    return { evid: 280, src: 83, endpoint: '', params };
}
/**
 * A function to create a log object for the 'addEditFieldAdditionalInfoHover' event (evid: 281)
 *
 * Event description: User hovers to 'i' button to learn more about PII/help_text/field_id
 *
 * @param {addEditFieldAdditionalInfoHoverParams} params - The parameters for this event
 * @property {string} site_id - site_id
 * @property {number} ts - ts
 * @property {string} collection_id - collection_id
 * @property {string} button_name - pii/help_text/field_id
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} version - version
 * @property {string} origin - add_field/edit_field
 * @property {string} environment - EDITOR/BUSINESS_MANAGER
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {GUID} esi - esi
 * @property {string} collection_name - collection name
 * @property {GUID} msid - msid
 * @property {string} field_name - field_name
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<addEditFieldAdditionalInfoHoverParams>} LogObject<addEditFieldAdditionalInfoHoverParams>
 */
export function addEditFieldAdditionalInfoHover(params) {
    return { evid: 281, src: 83, endpoint: '', params };
}
/**
 * A function to create a log object for the 'cmAddFieldDescriptionHelpText' event (evid: 282)
 *
 * Event description: Triggers when users adds a message regarding a field.
 *
 * @param {cmAddFieldDescriptionHelpTextParams} params - The parameters for this event
 * @property {string} site_id - site_id
 * @property {string} field_type - text/image/bool etc.
 * @property {number} ts - ts
 * @property {string} collection_id - collection_id
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} version - version
 * @property {string} origin - add_field/edit_field
 * @property {string} environment - EDITOR, BUSINESS_MANAGER
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {GUID} esi - esi
 * @property {string} collection_name - collection name
 * @property {GUID} msid - msid
 * @property {string} field_name - field_name
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<cmAddFieldDescriptionHelpTextParams>} LogObject<cmAddFieldDescriptionHelpTextParams>
 */
export function cmAddFieldDescriptionHelpText(params) {
    return { evid: 282, src: 83, endpoint: '', params };
}
/**
 * A function to create a log object for the 'indexesChooseField' event (evid: 285)
 *
 * Event description: Triggers after user choose from dropdown list one of field.
 *
 * @param {indexesChooseFieldParams} params - The parameters for this event
 * @property {string} collection - collection
 * @property {string} site_id - site_id
 * @property {string} field_type - field type
 * @property {number} ts - ts
 * @property {GUID} direction - direction
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} version - version
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {GUID} esi - esi
 * @property {GUID} msid - msid
 * @property {string} field_name - field name
 * @property {string} index_type - index type
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<indexesChooseFieldParams>} LogObject<indexesChooseFieldParams>
 */
export function indexesChooseField(params) {
    return { evid: 285, src: 83, endpoint: '', params };
}
/**
 * A function to create a log object for the 'indexesConfirmDeleteIndexClick' event (evid: 286)
 *
 * Event description: Triggers after user confirming index deleting on confirmation modal.
 *
 * @param {indexesConfirmDeleteIndexClickParams} params - The parameters for this event
 * @property {string} collection - collection
 * @property {string} index_name - index_name
 * @property {string} site_id - site_id
 * @property {GUID} uuid - uuid
 * @property {number} ts - ts
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} version - version
 * @property {string} index_fields - index fields
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {GUID} esi - esi
 * @property {GUID} msid - msid
 * @property {string} index_type - index type
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<indexesConfirmDeleteIndexClickParams>} LogObject<indexesConfirmDeleteIndexClickParams>
 */
export function indexesConfirmDeleteIndexClick(params) {
    return { evid: 286, src: 83, endpoint: '', params };
}
/**
 * A function to create a log object for the 'indexesChooseDirection' event (evid: 287)
 *
 * Event description: Triggers after user choose/change direction order for field.
 *
 * @param {indexesChooseDirectionParams} params - The parameters for this event
 * @property {string} collection - collection
 * @property {string} site_id - site_id
 * @property {GUID} uuid - uuid
 * @property {string} field_type - field_type
 * @property {number} ts - ts
 * @property {string} direction - direction
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} version - version
 * @property {string} environment - EDITOR, BUSINESS_MANAGER
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {GUID} esi - esi
 * @property {GUID} msid - msid
 * @property {string} field_name - field_name
 * @property {string} index_type - index type
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<indexesChooseDirectionParams>} LogObject<indexesChooseDirectionParams>
 */
export function indexesChooseDirection(params) {
    return { evid: 287, src: 83, endpoint: '', params };
}
/**
 * A function to create a log object for the 'piiToggleChangeInCollectionFieldSettings' event (evid: 288)
 *
 * Event description: Event is fired when PII toggle value is changed
 *
 * @param {piiToggleChangeInCollectionFieldSettingsParams} params - The parameters for this event
 * @property {string} site_id - site_id
 * @property {number} ts - ts
 * @property {string} collection_id - collection id
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} version - version
 * @property {boolean} new_toggle_value - new toggle value
 * @property {string} origin - add_field/edit_field
 * @property {string} environment - environment
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {GUID} esi - esi
 * @property {string} collection_name - collection_name
 * @property {GUID} msid - metasite id
 * @property {string} field_name - WIX Data collection field name
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<piiToggleChangeInCollectionFieldSettingsParams>} LogObject<piiToggleChangeInCollectionFieldSettingsParams>
 */
export function piiToggleChangeInCollectionFieldSettings(params) {
    return { evid: 288, src: 83, endpoint: '', params };
}
/**
 * A function to create a log object for the 'cmCollectionsFolderCreated' event (evid: 289)
 *
 * Event description: CM Collections Folder Created
 *
 * @param {cmCollectionsFolderCreatedParams} params - The parameters for this event
 * @property {string} site_id - site_id
 * @property {string} description - which description was written, null if none
 * @property {number} ts - ts
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} color - which colour was picked, null if none
 * @property {string} version - version
 * @property {string} origin - collections_page / intro_banner
 * @property {string} environment - EDITOR / BUSINESS MANAGER
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {GUID} esi - esi
 * @property {GUID} msid - msid
 * @property {string} folder_name - folder name
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<cmCollectionsFolderCreatedParams>} LogObject<cmCollectionsFolderCreatedParams>
 */
export function cmCollectionsFolderCreated(params) {
    return { evid: 289, src: 83, endpoint: '', params };
}
/**
 * A function to create a log object for the 'cmCollectionsFolderOpened' event (evid: 290)
 *
 * Event description: CM Collections Folder Opened
 *
 * @param {cmCollectionsFolderOpenedParams} params - The parameters for this event
 * @property {string} site_id - site_id
 * @property {string} description - folder description
 * @property {number} ts - ts
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} version - version
 * @property {string} origin - collections_page
 * @property {string} environment - EDITOR / BUSINESS MANAGER
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {GUID} esi - esi
 * @property {GUID} msid - msid
 * @property {string} folder_name - folder name
 * @property {number} number_of_items - number of collections in folder
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<cmCollectionsFolderOpenedParams>} LogObject<cmCollectionsFolderOpenedParams>
 */
export function cmCollectionsFolderOpened(params) {
    return { evid: 290, src: 83, endpoint: '', params };
}
/**
 * A function to create a log object for the 'wixDataChangeGreyhoundMessageSrc83Evid2901' event (evid: 2901)
 *
 * Event description: fiers on any changes in wix-data while a greyhound message is sent to the editor team to reindex site pages
 *
 * @param {wixDataChangeGreyhoundMessageSrc83Evid2901Params} params - The parameters for this event
 * @property {GUID} uuid - uuid
 * @property {string} pageType - static/ dynamic
 * @property {GUID} msid - msid
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<wixDataChangeGreyhoundMessageSrc83Evid2901Params>} LogObject<wixDataChangeGreyhoundMessageSrc83Evid2901Params>
 */
export function wixDataChangeGreyhoundMessageSrc83Evid2901(params) {
    return { evid: 2901, src: 83, endpoint: '', params };
}
/**
 * A function to create a log object for the 'cmMoveCollectionToFolder' event (evid: 291)
 *
 * Event description: CM Move Collection To Folder
 *
 * @param {cmMoveCollectionToFolderParams} params - The parameters for this event
 * @property {string} site_id - site_id
 * @property {number} ts - ts
 * @property {string} collection_id - collection id
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} version - version
 * @property {string} origin - collection_settings
 * @property {string} environment - EDITOR / BUSINESS MANAGER
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {GUID} esi - esi
 * @property {string} collection_name - collection name
 * @property {GUID} msid - msid
 * @property {string} folder_name - folder name
 * @property {number} number_of_items - number of collections in folder
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<cmMoveCollectionToFolderParams>} LogObject<cmMoveCollectionToFolderParams>
 */
export function cmMoveCollectionToFolder(params) {
    return { evid: 291, src: 83, endpoint: '', params };
}
/**
 * A function to create a log object for the 'cmCollectionsFolderDeleted' event (evid: 292)
 *
 * Event description: CM Collections Folder Deleted
 *
 * @param {cmCollectionsFolderDeletedParams} params - The parameters for this event
 * @property {string} site_id - site_id
 * @property {string} description - folder description
 * @property {number} ts - ts
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} version - version
 * @property {string} origin - collections_page
 * @property {string} environment - EDITOR / BUSINESS MANAGER
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {GUID} esi - esi
 * @property {GUID} msid - msid
 * @property {string} folder_name - folder name
 * @property {number} number_of_items - number of collections in folder
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<cmCollectionsFolderDeletedParams>} LogObject<cmCollectionsFolderDeletedParams>
 */
export function cmCollectionsFolderDeleted(params) {
    return { evid: 292, src: 83, endpoint: '', params };
}
/**
 * A function to create a log object for the 'cmRemoveCollectionFromFolder' event (evid: 293)
 *
 * Event description: CM Remove Collection From Folder
 *
 * @param {cmRemoveCollectionFromFolderParams} params - The parameters for this event
 * @property {string} site_id - site_id
 * @property {number} ts - ts
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} version - version
 * @property {string} origin - collection_settings
 * @property {string} environment - EDITOR / BUSINESS MANAGER
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {GUID} esi - esi
 * @property {string} collection_name - collection name
 * @property {GUID} msid - msid
 * @property {string} folder_name - folder name
 * @property {number} number_of_items - number of collections in folder
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<cmRemoveCollectionFromFolderParams>} LogObject<cmRemoveCollectionFromFolderParams>
 */
export function cmRemoveCollectionFromFolder(params) {
    return { evid: 293, src: 83, endpoint: '', params };
}
/**
 * A function to create a log object for the 'cmCollectionsPageSearchAction' event (evid: 294)
 *
 * Event description: User Performs Search Action among Collections and Folders within Collections Page
 *
 * @param {cmCollectionsPageSearchActionParams} params - The parameters for this event
 * @property {string} site_id - site_id
 * @property {number} ts - ts
 * @property {string} viewmode - viewmode
 * @property {string} text - what was the search text
 * @property {string} input_name - input_name
 * @property {number} num_of_results - num of total results of the search action {0=no results, 1, 2...}
 * @property {string} version - version
 * @property {string} num_of_folders - num of folders resulted of the search action
 * @property {string} environment - EDITOR / BUSINESS_MANAGER
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {GUID} esi - esi=editor session id
 * @property {GUID} msid - msid
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<cmCollectionsPageSearchActionParams>} LogObject<cmCollectionsPageSearchActionParams>
 */
export function cmCollectionsPageSearchAction(params) {
    return { evid: 294, src: 83, endpoint: '', params };
}
/**
 * A function to create a log object for the 'cmCollectionsFolderChanged' event (evid: 295)
 *
 * Event description: CM Collections Folder's settings were changed by the user
 *
 * @param {cmCollectionsFolderChangedParams} params - The parameters for this event
 * @property {string} site_id - site_id
 * @property {number} ts - ts
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} version - version
 * @property {string} origin - collections_page
 * @property {string} environment - EDITOR / BUSINESS MANAGER
 * @property {string} userEnv - user_env
 * @property {string} new_value - new value of the changed parameter
 * @property {string} editor_working_mode - editor_working_mode
 * @property {string} setting_name - what was changed
 * @property {GUID} esi - esi
 * @property {GUID} msid - msid
 * @property {string} folder_name - folder name
 * @property {number} number_of_items - number of collections in folder
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<cmCollectionsFolderChangedParams>} LogObject<cmCollectionsFolderChangedParams>
 */
export function cmCollectionsFolderChanged(params) {
    return { evid: 295, src: 83, endpoint: '', params };
}
/**
 * A function to create a log object for the 'cmImportPressCancelBackup' event (evid: 296)
 *
 * Event description: User clicks Cancel Backup while importing file
 *
 * @param {cmImportPressCancelBackupParams} params - The parameters for this event
 * @property {string} site_id - site_id
 * @property {number} ts - ts
 * @property {string} collection_id - collection id
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} version - version
 * @property {boolean} is_live_data - {Live=true, Sandbox=false}
 * @property {string} environment - environment
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {GUID} esi - esi
 * @property {string} collection_name - collection name
 * @property {GUID} msid - msid
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<cmImportPressCancelBackupParams>} LogObject<cmImportPressCancelBackupParams>
 */
export function cmImportPressCancelBackup(params) {
    return { evid: 296, src: 83, endpoint: '', params };
}
/**
 * A function to create a log object for the 'versionlessSchemaClickUndoFieldActions' event (evid: 297)
 *
 * Event description: User Clicks Undo after making some changes with fields in Teamwork feature
 *
 * @param {versionlessSchemaClickUndoFieldActionsParams} params - The parameters for this event
 * @property {string} action_name - delete / change_type
 * @property {string} site_id - site_id
 * @property {string} field_type - new field type, null if deleted
 * @property {number} ts - ts
 * @property {string} collection_id - collection id
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} version - version
 * @property {boolean} is_live_data - (true/false)
 * @property {string} origin - TABLE_VISIBLE_FIELDS_BUTTON, TABLE_FIELD_HEADER,LIST_VISIBLE_FIELDS_BUTTON, GALLERY_VISIBLE_FIELDS_BUTTON, ITEM_VISIBLE_FIELD_BUTTON
 * @property {string} environment - EDITOR/BUSINESS MANAGER
 * @property {boolean} is_sandbox_enabled - (true/false)
 * @property {string} view_layout - table, list, gallery
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {GUID} esi - esi
 * @property {string} collection_name - collection name
 * @property {GUID} msid - msid
 * @property {string} old_field_type - type of field before change / delete
 * @property {string} field_name - field_name
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<versionlessSchemaClickUndoFieldActionsParams>} LogObject<versionlessSchemaClickUndoFieldActionsParams>
 */
export function versionlessSchemaClickUndoFieldActions(params) {
    return { evid: 297, src: 83, endpoint: '', params };
}
/**
 * A function to create a log object for the 'versionlessSchemaClickUndoCollectionRemove' event (evid: 298)
 *
 * Event description: User Clicks Undo after removing collection in Teamwork feature
 *
 * @param {versionlessSchemaClickUndoCollectionRemoveParams} params - The parameters for this event
 * @property {string} site_id - site_id
 * @property {number} ts - ts
 * @property {string} collection_id - collection id
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} version - version
 * @property {boolean} is_live_data - (true/false)
 * @property {string} origin - origin of delete collection action
 * @property {string} environment - EDITOR/BUSINESS MANAGER
 * @property {boolean} is_sandbox_enabled - (true/false)
 * @property {string} view_layout - table, list, gallery
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {GUID} esi - esi
 * @property {string} collection_name - collection name
 * @property {GUID} msid - msid
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<versionlessSchemaClickUndoCollectionRemoveParams>} LogObject<versionlessSchemaClickUndoCollectionRemoveParams>
 */
export function versionlessSchemaClickUndoCollectionRemove(params) {
    return { evid: 298, src: 83, endpoint: '', params };
}
/**
 * A function to create a log object for the 'versionlessSchemaCollectionRemoved' event (evid: 299)
 *
 * Event description: Action performed in Delete a collection modal (confirm removing or cancel) in Teamwork feature
 *
 * @param {versionlessSchemaCollectionRemovedParams} params - The parameters for this event
 * @property {string} renderingFlow - Rendering flow
 * @property {number} num_of_reference_fields_to_collection - num_of_reference_fields_to_collection
 * @property {string} site_id - site_id
 * @property {number} ts - ts
 * @property {string} collection_id - collection id
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {boolean} is_shared - is shared collection
 * @property {string} version - version
 * @property {string} collection_type - collection type
 * @property {string} origin - CM / Editor
 * @property {string} environment - evironment
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {string} category - remove / close
 * @property {GUID} esi - esi
 * @property {string} collection_name - collection name
 * @property {GUID} msid - msid
 * @property {number} num_of_connected_dynamic_pages - num_of_connected_dynamic_pages
 * @property {number} num_of_rows - num of rows in the deleted collection
 * @return {LogObject<versionlessSchemaCollectionRemovedParams>} LogObject<versionlessSchemaCollectionRemovedParams>
 */
export function versionlessSchemaCollectionRemoved(params) {
    return { evid: 299, src: 83, endpoint: '', params };
}
/**
 * A function to create a log object for the 'cmsConflictIndicationClicked' event (evid: 3)
 *
 * Event description: When a conflict indication that originates from the permissions settings is clicked only relevant from notification / info tooltip
 *
 * @param {cmsConflictIndicationClickedParams} params - The parameters for this event
 * @property {string} ctaName - change_permissions/go_to_page/dismiss
 * @property {string} collection_name - collection_name
 * @property {string} collection_id - collection_id
 * @property {string} conflictType - info_icon / notification_banner / info_icon_tooltip
 * @property {string} environment - EDITOR / BUSINESS_MANAGER
 * @property {string} origin - permissions/dataset_summary
 * @property {string} alertType - collection/items/filters/sorts
 * @property {string} ds_id - dataset's id
 * @property {string} ds_name - dataset's name
 * @property {string} componentType - component's type
 * @property {string} element_id - id of the element which the alert is relevant to, e.g image, text box
 * @property {string} page_id - page_id
 * @property {string} element_type - the type of the edited element e.g $w.image
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<cmsConflictIndicationClickedParams>} LogObject<cmsConflictIndicationClickedParams>
 */
export function cmsConflictIndicationClicked(params) {
    return { evid: 3, src: 83, endpoint: '', params };
}
/**
 * A function to create a log object for the 'versionlessSchemaDeleteAFieldModal' event (evid: 300)
 *
 * Event description: Action performed in Delete a field modal (confirm removing or cancel) in Teamwork feature
 *
 * @param {versionlessSchemaDeleteAFieldModalParams} params - The parameters for this event
 * @property {string} site_id - site_id
 * @property {string} field_type - string, image etc.
 * @property {number} ts - ts
 * @property {string} collection_id - collection id
 * @property {boolean} is_draft_publish_enabled - (true, false)
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} version - version
 * @property {boolean} is_live_data - (true/false)
 * @property {string} origin - (TABLE_VISIBLE_FIELDS_BUTTON, TABLE_FIELD_HEADER,LIST_VISIBLE_FIELDS_BUTTON, GALLERY_VISIBLE_FIELDS_BUTTON, ITEM_VISIBLE_FIELD_BUTTON)
 * @property {string} environment - EDITOR / BUSINESS MANAGER
 * @property {boolean} is_sandbox_enabled - (true/false)
 * @property {string} view_layout - (table, list, gallery)
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {string} category - delete / cancel
 * @property {GUID} esi - esi
 * @property {string} collection_name - collection name
 * @property {GUID} msid - msid
 * @property {string} field_name - field_name
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<versionlessSchemaDeleteAFieldModalParams>} LogObject<versionlessSchemaDeleteAFieldModalParams>
 */
export function versionlessSchemaDeleteAFieldModal(params) {
    return { evid: 300, src: 83, endpoint: '', params };
}
/**
 * A function to create a log object for the 'versionlessSchemaFieldTypeChangeModal' event (evid: 301)
 *
 * Event description: After changing field type user performs action in Change Field Type Modal (confirms or cancels)
 *
 * @param {versionlessSchemaFieldTypeChangeModalParams} params - The parameters for this event
 * @property {string} site_id - site_id
 * @property {string} field_type - string, image
 * @property {number} ts - ts
 * @property {string} collection_id - collection id
 * @property {boolean} is_draft_publish_enabled - (true, false)
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} version - version
 * @property {boolean} is_live_data - (true/false)
 * @property {string} origin - (TABLE_VISIBLE_FIELDS_BUTTON, TABLE_FIELD_HEADER,LIST_VISIBLE_FIELDS_BUTTON, GALLERY_VISIBLE_FIELDS_BUTTON, ITEM_VISIBLE_FIELD_BUTTON)
 * @property {string} environment - EDITOR / BUSINESS MANAGER
 * @property {boolean} is_sandbox_enabled - (true/false)
 * @property {string} view_layout - (table, list, gallery)
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {string} category - remove / close
 * @property {GUID} esi - esi
 * @property {string} collection_name - collection name
 * @property {GUID} msid - msid
 * @property {string} old_field_type - old_field_type
 * @property {string} field_name - field_name
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<versionlessSchemaFieldTypeChangeModalParams>} LogObject<versionlessSchemaFieldTypeChangeModalParams>
 */
export function versionlessSchemaFieldTypeChangeModal(params) {
    return { evid: 301, src: 83, endpoint: '', params };
}
/**
 * A function to create a log object for the 'cmFieldDuplicated' event (evid: 302)
 *
 * Event description: User duplicates field in a collection.
 *
 * @param {cmFieldDuplicatedParams} params - The parameters for this event
 * @property {string} site_id - site_id
 * @property {string} field_type - String, Image
 * @property {number} ts - ts
 * @property {string} collection_id - collection id
 * @property {boolean} is_draft_publish_enabled - (true, false)
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} version - version
 * @property {boolean} is_live_data - (true/false)
 * @property {string} origin - (TABLE_VISIBLE_FIELDS_BUTTON, TABLE_FIELD_HEADER,LIST_VISIBLE_FIELDS_BUTTON, GALLERY_VISIBLE_FIELDS_BUTTON, ITEM_VISIBLE_FIELD_BUTTON)
 * @property {string} environment - (Editor/Dashboard)
 * @property {boolean} is_sandbox_enabled - (true/false)
 * @property {string} view_layout - (table, list, gallery)
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {GUID} esi - esi
 * @property {string} collection_name - collection name
 * @property {GUID} msid - msid
 * @property {string} field_name - field_name
 * @property {string} parameters - with content or no
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<cmFieldDuplicatedParams>} LogObject<cmFieldDuplicatedParams>
 */
export function cmFieldDuplicated(params) {
    return { evid: 302, src: 83, endpoint: '', params };
}
/**
 * A function to create a log object for the 'cmCollectionsFolderIntroBannerClosed' event (evid: 303)
 *
 * Event description: CM Collections Folder Intro Banner Closed
 *
 * @param {cmCollectionsFolderIntroBannerClosedParams} params - The parameters for this event
 * @property {string} site_id - site_id
 * @property {number} ts - ts
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} version - version
 * @property {string} origin - collections_page
 * @property {string} environment - EDITOR / BUSINESS MANAGER
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {string} category - close / add_folder
 * @property {GUID} esi - esi
 * @property {GUID} msid - msid
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<cmCollectionsFolderIntroBannerClosedParams>} LogObject<cmCollectionsFolderIntroBannerClosedParams>
 */
export function cmCollectionsFolderIntroBannerClosed(params) {
    return { evid: 303, src: 83, endpoint: '', params };
}
/**
 * A function to create a log object for the 'cmManageFieldsPanelClosed' event (evid: 304)
 *
 * Event description: Fires when a Manage Fields panel is closed
 *
 * @param {cmManageFieldsPanelClosedParams} params - The parameters for this event
 * @property {string} site_id - site_id
 * @property {GUID} uuid - uuid
 * @property {number} ts - ts
 * @property {string} collection_id - collection id
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} version - version
 * @property {string} origin - add_field, edit_field
 * @property {string} environment - EDITOR, BUSINESS_MANAGER
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {GUID} esi - esi
 * @property {string} collection_name - collection_name
 * @property {GUID} msid - msid
 * @property {string} dataJson - all panel's attributes
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<cmManageFieldsPanelClosedParams>} LogObject<cmManageFieldsPanelClosedParams>
 */
export function cmManageFieldsPanelClosed(params) {
    return { evid: 304, src: 83, endpoint: '', params };
}
/**
 * A function to create a log object for the 'cmExportProgressBarShown' event (evid: 305)
 *
 * Event description: CM Export Progress Bar Shown
 *
 * @param {cmExportProgressBarShownParams} params - The parameters for this event
 * @property {string} export_type - (all, filtered, schema)
 * @property {string} site_id - site_id
 * @property {number} ts - ts
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} version - version
 * @property {boolean} is_live_data - is_live_data
 * @property {string} environment - environment
 * @property {boolean} is_sandbox_enabled - (true/false)
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {GUID} esi - esi
 * @property {string} collection_name - collection name
 * @property {GUID} msid - msid
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<cmExportProgressBarShownParams>} LogObject<cmExportProgressBarShownParams>
 */
export function cmExportProgressBarShown(params) {
    return { evid: 305, src: 83, endpoint: '', params };
}
/**
 * A function to create a log object for the 'cmExportClickCancelOnProgressBar' event (evid: 306)
 *
 * Event description: CM Export - Click Cancel on Progress Bar
 *
 * @param {cmExportClickCancelOnProgressBarParams} params - The parameters for this event
 * @property {string} export_type - (all, filtered, schema)
 * @property {string} site_id - site_id
 * @property {number} ts - ts
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} version - version
 * @property {boolean} is_live_data - is_live_data
 * @property {string} environment - environment
 * @property {boolean} is_sandbox_enabled - (true/false)
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {GUID} esi - esi
 * @property {string} collection_name - collection name
 * @property {GUID} msid - msid
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<cmExportClickCancelOnProgressBarParams>} LogObject<cmExportClickCancelOnProgressBarParams>
 */
export function cmExportClickCancelOnProgressBar(params) {
    return { evid: 306, src: 83, endpoint: '', params };
}
/**
 * A function to create a log object for the 'cmExportDownloadIsReadyShown' event (evid: 307)
 *
 * Event description: CM Export - Download is Ready Shown
 *
 * @param {cmExportDownloadIsReadyShownParams} params - The parameters for this event
 * @property {string} export_type - (all, filtered, schema)
 * @property {string} site_id - site_id
 * @property {number} ts - ts
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} version - version
 * @property {boolean} is_live_data - is_live_data
 * @property {string} environment - environment
 * @property {boolean} is_sandbox_enabled - (true/false)
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {GUID} esi - esi
 * @property {string} collection_name - collection name
 * @property {GUID} msid - msid
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<cmExportDownloadIsReadyShownParams>} LogObject<cmExportDownloadIsReadyShownParams>
 */
export function cmExportDownloadIsReadyShown(params) {
    return { evid: 307, src: 83, endpoint: '', params };
}
/**
 * A function to create a log object for the 'cmExportErrorShown' event (evid: 308)
 *
 * Event description: CM Export - Error Shown
 *
 * @param {cmExportErrorShownParams} params - The parameters for this event
 * @property {string} export_type - (all, filtered, schema)
 * @property {string} site_id - site_id
 * @property {number} ts - ts
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} version - version
 * @property {string} error_type - error type
 * @property {boolean} is_live_data - {Live=true, Sandbox=false}
 * @property {string} environment - environment
 * @property {boolean} is_sandbox_enabled - (true/false)
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {GUID} esi - esi
 * @property {string} collection_name - collection name
 * @property {GUID} msid - msid
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<cmExportErrorShownParams>} LogObject<cmExportErrorShownParams>
 */
export function cmExportErrorShown(params) {
    return { evid: 308, src: 83, endpoint: '', params };
}
/**
 * A function to create a log object for the 'cmExportDownloadFileClicked' event (evid: 309)
 *
 * Event description: CM Export - Download File Clicked
 *
 * @param {cmExportDownloadFileClickedParams} params - The parameters for this event
 * @property {string} export_type - (all, filtered, schema)
 * @property {string} site_id - site_id
 * @property {number} ts - ts
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} version - version
 * @property {boolean} is_live_data - {Live=true, Sandbox=false}
 * @property {string} environment - environment
 * @property {boolean} is_sandbox_enabled - (true/false)
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {GUID} esi - esi
 * @property {string} collection_name - collection name
 * @property {GUID} msid - msid
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<cmExportDownloadFileClickedParams>} LogObject<cmExportDownloadFileClickedParams>
 */
export function cmExportDownloadFileClicked(params) {
    return { evid: 309, src: 83, endpoint: '', params };
}
/**
 * A function to create a log object for the 'fieldsValidationAdditionalTabsNavigation' event (evid: 310)
 *
 * Event description: User Changes value of 'Make Field Required' Toggle
 *
 * @param {fieldsValidationAdditionalTabsNavigationParams} params - The parameters for this event
 * @property {boolean} toggle_state - True/False
 * @property {string} action_name - ADD_FIELD / EDIT_FIELD
 * @property {string} site_id - site_id
 * @property {string} field_type - type of field
 * @property {number} ts - ts
 * @property {boolean} description_edited - (true/false)
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} version - version
 * @property {boolean} is_live_data - (true/false)
 * @property {string} origin - ["FIELD_HEADER",
 * "LIST_VISIBLE_FIELDS_BUTTON",
 * "TABLE_VISIBLE_FIELDS_BUTTON",
 * "ITEM_VISIBLE_FIELD_BUTTON",
 * "ADD_COLUMN_BUTTON",
 * "GALLERY_VISIBLE_FIELDS_BUTTON",
 * "SINGLES_ADD_FIELD","MULTIPLES_ADD_FIELD"]
 * @property {string} environment - Editor / business_manager
 * @property {boolean} is_sandbox_enabled - (true/false)
 * @property {string} view_layout - table / gallery / list
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {GUID} esi - esi
 * @property {string} collection_name - collection name
 * @property {GUID} msid - msid
 * @property {string} field_name - field_name
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<fieldsValidationAdditionalTabsNavigationParams>} LogObject<fieldsValidationAdditionalTabsNavigationParams>
 */
export function fieldsValidationAdditionalTabsNavigation(params) {
    return { evid: 310, src: 83, endpoint: '', params };
}
/**
 * A function to create a log object for the 'cmFieldsValidationNavigateBetweenFieldSettingsTabs' event (evid: 311)
 *
 * Event description: User Navigates Between Field Settings Tabs
 *
 * @param {cmFieldsValidationNavigateBetweenFieldSettingsTabsParams} params - The parameters for this event
 * @property {string} action_name - ADD_FIELD / EDIT_FIELD
 * @property {string} site_id - site_id
 * @property {string} tab_name - validation / default_value
 * @property {string} field_type - type of field
 * @property {number} ts - ts
 * @property {boolean} description_edited - (true/false)
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} version - version
 * @property {boolean} is_live_data - (true/false)
 * @property {string} origin - ["FIELD_HEADER",
 * "LIST_VISIBLE_FIELDS_BUTTON",
 * "TABLE_VISIBLE_FIELDS_BUTTON",
 * "ITEM_VISIBLE_FIELD_BUTTON",
 * "ADD_COLUMN_BUTTON",
 * "GALLERY_VISIBLE_FIELDS_BUTTON",
 * "SINGLES_ADD_FIELD","MULTIPLES_ADD_FIELD"]
 * @property {string} environment - Editor / business_manager
 * @property {boolean} is_sandbox_enabled - (true/false)
 * @property {string} view_layout - table / gallery / list
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {GUID} esi - esi
 * @property {string} collection_name - collection name
 * @property {GUID} msid - msid
 * @property {string} field_name - field_name
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<cmFieldsValidationNavigateBetweenFieldSettingsTabsParams>} LogObject<cmFieldsValidationNavigateBetweenFieldSettingsTabsParams>
 */
export function cmFieldsValidationNavigateBetweenFieldSettingsTabs(params) {
    return { evid: 311, src: 83, endpoint: '', params };
}
/**
 * A function to create a log object for the 'cmFieldsValidationShowValidationsAreNotSupportedMessage' event (evid: 312)
 *
 * Event description: CM Fields Validation - Show  'Validations are not supported...' or ' fields do not support default values' Message
 *
 * @param {cmFieldsValidationShowValidationsAreNotSupportedMessageParams} params - The parameters for this event
 * @property {string} action_name - action_name
 * @property {string} site_id - site_id
 * @property {string} tab_name - validation / default_value
 * @property {string} field_type - type of field
 * @property {number} ts - ts
 * @property {boolean} description_edited - (true/false)
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} version - version
 * @property {string} error_type - validation / dafault_value
 * @property {boolean} is_live_data - (true/false)
 * @property {string} origin - ["FIELD_HEADER",
 * "LIST_VISIBLE_FIELDS_BUTTON",
 * "TABLE_VISIBLE_FIELDS_BUTTON",
 * "ITEM_VISIBLE_FIELD_BUTTON",
 * "ADD_COLUMN_BUTTON",
 * "GALLERY_VISIBLE_FIELDS_BUTTON",
 * "SINGLES_ADD_FIELD","MULTIPLES_ADD_FIELD"]
 * @property {string} environment - Editor / business_manager
 * @property {boolean} is_sandbox_enabled - (true/false)
 * @property {string} view_layout - table / gallery / list
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {GUID} esi - esi
 * @property {string} collection_name - collection name
 * @property {GUID} msid - msid
 * @property {string} field_name - field_name
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<cmFieldsValidationShowValidationsAreNotSupportedMessageParams>} LogObject<cmFieldsValidationShowValidationsAreNotSupportedMessageParams>
 */
export function cmFieldsValidationShowValidationsAreNotSupportedMessage(params) {
    return { evid: 312, src: 83, endpoint: '', params };
}
/**
 * A function to create a log object for the 'cmFieldsValidationClickLearnMore' event (evid: 313)
 *
 * Event description: CM Fields Validation - Click 'Learn more'
 *
 * @param {cmFieldsValidationClickLearnMoreParams} params - The parameters for this event
 * @property {string} action_name - ADD_FIELD / EDIT_FIELD
 * @property {string} site_id - site_id
 * @property {string} tab_name - validation / default_value
 * @property {string} field_type - type of field
 * @property {number} ts - ts
 * @property {boolean} description_edited - (true/false)
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} version - version
 * @property {boolean} is_live_data - (true/false)
 * @property {string} origin - ["FIELD_HEADER",
 * "LIST_VISIBLE_FIELDS_BUTTON",
 * "TABLE_VISIBLE_FIELDS_BUTTON",
 * "ITEM_VISIBLE_FIELD_BUTTON",
 * "ADD_COLUMN_BUTTON",
 * "GALLERY_VISIBLE_FIELDS_BUTTON",
 * "SINGLES_ADD_FIELD","MULTIPLES_ADD_FIELD"]
 * @property {string} environment - Editor / business_manager
 * @property {boolean} is_sandbox_enabled - (true/false)
 * @property {string} view_layout - table / gallery / list
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {GUID} esi - esi
 * @property {string} collection_name - collection name
 * @property {GUID} msid - msid
 * @property {string} field_name - field_name
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<cmFieldsValidationClickLearnMoreParams>} LogObject<cmFieldsValidationClickLearnMoreParams>
 */
export function cmFieldsValidationClickLearnMore(params) {
    return { evid: 313, src: 83, endpoint: '', params };
}
/**
 * A function to create a log object for the 'cmFieldsValidationClickSubmitARequest' event (evid: 314)
 *
 * Event description: CM Fields Validation - Click 'Submit a request'
 *
 * @param {cmFieldsValidationClickSubmitARequestParams} params - The parameters for this event
 * @property {string} action_name - ADD_FIELD / EDIT_FIELD
 * @property {string} site_id - site_id
 * @property {string} tab_name - validation / default_value
 * @property {string} field_type - type of field
 * @property {number} ts - ts
 * @property {boolean} description_edited - (true/false)
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} version - version
 * @property {boolean} is_live_data - (true/false)
 * @property {string} origin - ["FIELD_HEADER",
 * "LIST_VISIBLE_FIELDS_BUTTON",
 * "TABLE_VISIBLE_FIELDS_BUTTON",
 * "ITEM_VISIBLE_FIELD_BUTTON",
 * "ADD_COLUMN_BUTTON",
 * "GALLERY_VISIBLE_FIELDS_BUTTON",
 * "SINGLES_ADD_FIELD","MULTIPLES_ADD_FIELD"]
 * @property {string} environment - Editor / business_manager
 * @property {boolean} is_sandbox_enabled - (true/false)
 * @property {string} view_layout - table / gallery / list
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {GUID} esi - esi
 * @property {string} collection_name - collection name
 * @property {GUID} msid - msid
 * @property {string} field_name - field_name
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<cmFieldsValidationClickSubmitARequestParams>} LogObject<cmFieldsValidationClickSubmitARequestParams>
 */
export function cmFieldsValidationClickSubmitARequest(params) {
    return { evid: 314, src: 83, endpoint: '', params };
}
/**
 * A function to create a log object for the 'cmFieldsValidationInfoHover' event (evid: 315)
 *
 * Event description: CM Fields Validation - info hover
 *
 * @param {cmFieldsValidationInfoHoverParams} params - The parameters for this event
 * @property {string} action_name - ADD_FIELD / EDIT_FIELD
 * @property {string} site_id - site_id
 * @property {string} tab_name - validation / default_value
 * @property {string} field_type - type of field
 * @property {number} ts - ts
 * @property {string} collection_id - collection_id
 * @property {boolean} description_edited - (true/false)
 * @property {string} viewmode - viewmode
 * @property {string} input_name - message shown
 * @property {string} version - version
 * @property {boolean} is_live_data - (true/false)
 * @property {string} origin - ["FIELD_HEADER",
 * "LIST_VISIBLE_FIELDS_BUTTON",
 * "TABLE_VISIBLE_FIELDS_BUTTON",
 * "ITEM_VISIBLE_FIELD_BUTTON",
 * "ADD_COLUMN_BUTTON",
 * "GALLERY_VISIBLE_FIELDS_BUTTON",
 * "SINGLES_ADD_FIELD","MULTIPLES_ADD_FIELD"]
 * @property {string} environment - Editor / business_manager
 * @property {boolean} is_sandbox_enabled - (true/false)
 * @property {string} view_layout - table / gallery / list
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {GUID} esi - esi
 * @property {string} collection_name - collection name
 * @property {GUID} msid - msid
 * @property {string} field_name - field_name
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<cmFieldsValidationInfoHoverParams>} LogObject<cmFieldsValidationInfoHoverParams>
 */
export function cmFieldsValidationInfoHover(params) {
    return { evid: 315, src: 83, endpoint: '', params };
}
/**
 * A function to create a log object for the 'cmFieldsValidationAddDefaultValueSrc83Evid316' event (evid: 316)
 *
 * Event description: User sets default value for the field within fields validation feature
 *
 * @param {cmFieldsValidationAddDefaultValueSrc83Evid316Params} params - The parameters for this event
 * @property {string} action_name - ADD_FIELD / EDIT_FIELD
 * @property {string} site_id - site_id
 * @property {string} field_type - type of field
 * @property {number} ts - ts
 * @property {boolean} description_edited - (true/false)
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} version - version
 * @property {boolean} is_live_data - (true/false)
 * @property {string} origin - ["FIELD_HEADER",
 * "LIST_VISIBLE_FIELDS_BUTTON",
 * "TABLE_VISIBLE_FIELDS_BUTTON",
 * "ITEM_VISIBLE_FIELD_BUTTON",
 * "ADD_COLUMN_BUTTON",
 * "GALLERY_VISIBLE_FIELDS_BUTTON",
 * "SINGLES_ADD_FIELD","MULTIPLES_ADD_FIELD"]
 * @property {string} environment - Editor / business_manager
 * @property {boolean} is_sandbox_enabled - (true/false)
 * @property {string} view_layout - table / gallery / list
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {GUID} esi - esi
 * @property {string} collection_name - collection name
 * @property {GUID} msid - msid
 * @property {string} field_name - field_name
 * @property {string} value - input_value
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<cmFieldsValidationAddDefaultValueSrc83Evid316Params>} LogObject<cmFieldsValidationAddDefaultValueSrc83Evid316Params>
 */
export function cmFieldsValidationAddDefaultValueSrc83Evid316(params) {
    return { evid: 316, src: 83, endpoint: '', params };
}
/**
 * A function to create a log object for the 'cmFieldsValidationValidationErrorShown' event (evid: 317)
 *
 * Event description: CM Fields Validation - Validation Error Shown
 *
 * @param {cmFieldsValidationValidationErrorShownParams} params - The parameters for this event
 * @property {string} site_id - site_id
 * @property {string} field_type - type of a field with warning
 * @property {number} ts - ts
 * @property {string} collection_id - collection id
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} version - version
 * @property {string} error_type - required_empty_form, required_empty_table, required_empty_gallery, required_empty_list, hide_required_field
 * @property {boolean} is_live_data - (true/false)
 * @property {string} origin - ['CELL', 'SAVE_IN_CANCEL_POPUP','ITEM_PAGE']
 * @property {string} environment - Editor / business_manager
 * @property {boolean} is_sandbox_enabled - is_sandbox_enabled
 * @property {string} view_layout - table / gallery / list
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {GUID} esi - esi
 * @property {string} collection_name - collection name
 * @property {GUID} msid - msid
 * @property {string} field_name - name of a field with warning
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<cmFieldsValidationValidationErrorShownParams>} LogObject<cmFieldsValidationValidationErrorShownParams>
 */
export function cmFieldsValidationValidationErrorShown(params) {
    return { evid: 317, src: 83, endpoint: '', params };
}
/**
 * A function to create a log object for the 'cmFieldsValidationAddDefaultValue' event (evid: 318)
 *
 * Event description: User adds default value for the field after fields validation warning
 *
 * @param {cmFieldsValidationAddDefaultValueParams} params - The parameters for this event
 * @property {string} site_id - site_id
 * @property {string} field_type - type of field
 * @property {number} ts - ts
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} version - version
 * @property {boolean} is_live_data - (true/false)
 * @property {string} origin - ['CELL','ITEM_PAGE']
 * @property {string} environment - Editor / business_manager
 * @property {boolean} is_sandbox_enabled - (true/false)
 * @property {string} view_layout - table / gallery / list
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {GUID} esi - esi
 * @property {string} collection_name - collection name
 * @property {GUID} msid - msid
 * @property {string} field_name - field_name
 * @property {string} value - input_value
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<cmFieldsValidationAddDefaultValueParams>} LogObject<cmFieldsValidationAddDefaultValueParams>
 */
export function cmFieldsValidationAddDefaultValue(params) {
    return { evid: 318, src: 83, endpoint: '', params };
}
/**
 * A function to create a log object for the 'dataBackupsCollectionsSidePanelActions' event (evid: 319)
 *
 * Event description: Data backups - User clicked one of the action buttons at the Collections side panel (either Cancel or Confirm Restore of X Collections)
 *
 * @param {dataBackupsCollectionsSidePanelActionsParams} params - The parameters for this event
 * @property {number} num_of_collections - total number of collections available for restoring
 * @property {number} amount_selected - collections that were selected, if all then amount_selected=num_of_collections
 * @property {string} site_id - site_id
 * @property {number} ts - ts
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} version - version
 * @property {DateTime} backup_date - date backup was created
 * @property {string} environment - {Editor / business_manager}
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {GUID} esi - esi
 * @property {GUID} msid - msid
 * @property {string} action - cancel, restore
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<dataBackupsCollectionsSidePanelActionsParams>} LogObject<dataBackupsCollectionsSidePanelActionsParams>
 */
export function dataBackupsCollectionsSidePanelActions(params) {
    return { evid: 319, src: 83, endpoint: '', params };
}
/**
 * A function to create a log object for the 'dataBackupsRestoreApproveModal' event (evid: 320)
 *
 * Event description: user clicked on the restore button or clicked on cancel at the final restore modal
 *
 * @param {dataBackupsRestoreApproveModalParams} params - The parameters for this event
 * @property {string} renderingFlow - Rendering flow
 * @property {number} amount_selected - number of collections selected for restore
 * @property {string} site_id - site_id
 * @property {number} ts - ts
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} version - version
 * @property {DateTime} backup_date - date backup was created
 * @property {string} environment - {Editor / business_manager}
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {GUID} esi - esi
 * @property {GUID} msid - msid
 * @property {string} action - cancel, restore
 * @property {string} restore_type - replace_collection\ new_collection
 * @return {LogObject<dataBackupsRestoreApproveModalParams>} LogObject<dataBackupsRestoreApproveModalParams>
 */
export function dataBackupsRestoreApproveModal(params) {
    return { evid: 320, src: 83, endpoint: '', params };
}
/**
 * A function to create a log object for the 'mobileCmCollectionsPanelIsOpened' event (evid: 321)
 *
 * Event description: Collections panel is opened in Mobile CM
 *
 * @param {mobileCmCollectionsPanelIsOpenedParams} params - The parameters for this event
 * @property {number} num_of_collections - number (0, 1, ... , n)
 * @property {string} site_id - site_id
 * @property {string} tab - manage_tab / platforms_tab
 * @property {string} uuid - uuid
 * @property {number} ts - ts
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} version - version
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {GUID} esi - esi
 * @property {GUID} msid - msid
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<mobileCmCollectionsPanelIsOpenedParams>} LogObject<mobileCmCollectionsPanelIsOpenedParams>
 */
export function mobileCmCollectionsPanelIsOpened(params) {
    return { evid: 321, src: 83, endpoint: '', params };
}
/**
 * A function to create a log object for the 'mobileCmClickOnCmButton' event (evid: 322)
 *
 * Event description: User clicks CM button within existing entry points
 *
 * @param {mobileCmClickOnCmButtonParams} params - The parameters for this event
 * @property {string} site_id - site_id
 * @property {string} tab - manage_tab / platforms_tab
 * @property {string} uuid - uuid
 * @property {number} ts - ts
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} version - version
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {GUID} esi - esi
 * @property {GUID} msid - msid
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<mobileCmClickOnCmButtonParams>} LogObject<mobileCmClickOnCmButtonParams>
 */
export function mobileCmClickOnCmButton(params) {
    return { evid: 322, src: 83, endpoint: '', params };
}
/**
 * A function to create a log object for the 'cmCollectionTypeSelectorInteraction' event (evid: 323)
 *
 * Event description: User interacts with Collection Type Selector during collection creation
 *
 * @param {cmCollectionTypeSelectorInteractionParams} params - The parameters for this event
 * @property {boolean} is_first_data_modal - {true,false}
 * @property {string} site_id - site_id
 * @property {number} ts - ts
 * @property {string} collection_id - collection_id
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} version - version
 * @property {string} origin - ["ideTree", "add_panel", "DS_settings","autocms"]
 * @property {string} environment - EDITOR, BUSINESS_MANAGER
 * @property {boolean} is_sandbox_enabled - (true/false)
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {GUID} esi - esi
 * @property {string} collection_name - collection name
 * @property {GUID} msid - msid
 * @property {string} action - info_hover, open_dropdown, select_single_item, select_multiple_item
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<cmCollectionTypeSelectorInteractionParams>} LogObject<cmCollectionTypeSelectorInteractionParams>
 */
export function cmCollectionTypeSelectorInteraction(params) {
    return { evid: 323, src: 83, endpoint: '', params };
}
/**
 * A function to create a log object for the 'enableDataPremiumForSite' event (evid: 324)
 *
 * Event description: fires after purchasing the Velo scale premium plan when migration to Data premium infra starts and completes, along with the status
 *
 * @param {enableDataPremiumForSiteParams} params - The parameters for this event
 * @property {GUID} _event_hash - event_hash
 * @property {GUID} visitor_id - visitor_id
 * @property {string} feature - The name of the migrated feature
 * @property {string} status - The migration status (start/success/fail)
 * @property {GUID} msid - msid
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<enableDataPremiumForSiteParams>} LogObject<enableDataPremiumForSiteParams>
 */
export function enableDataPremiumForSite(params) {
    return { evid: 324, src: 83, endpoint: '', params };
}
/**
 * A function to create a log object for the 'enableComputePremiumForSite' event (evid: 325)
 *
 * Event description: fires after purchasing the Velo scale premium plan when migration to Compute premium infra starts and completes, along with the status
 *
 * @param {enableComputePremiumForSiteParams} params - The parameters for this event
 * @property {string} feature - The name of the migrated feature
 * @property {string} status - The migration status (start/success/fail)
 * @property {GUID} msid - The migrated msid
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<enableComputePremiumForSiteParams>} LogObject<enableComputePremiumForSiteParams>
 */
export function enableComputePremiumForSite(params) {
    return { evid: 325, src: 83, endpoint: '', params };
}
/**
 * A function to create a log object for the 'cmSubtabsNavigationInBizManager' event (evid: 326)
 *
 * Event description: User Navigates between sub-tabs of the Content Manager tab in BM
 *
 * @param {cmSubtabsNavigationInBizManagerParams} params - The parameters for this event
 * @property {string} sub_tab - {site_content, collections}
 * @property {string} site_id - site_id
 * @property {string} uuid - uuid
 * @property {number} ts - ts
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} version - version
 * @property {string} environment - BUSINESS_MANAGER
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {GUID} esi - esi
 * @property {GUID} msid - msid
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<cmSubtabsNavigationInBizManagerParams>} LogObject<cmSubtabsNavigationInBizManagerParams>
 */
export function cmSubtabsNavigationInBizManager(params) {
    return { evid: 326, src: 83, endpoint: '', params };
}
/**
 * A function to create a log object for the 'cmSiteContentPageLoadedInBm' event (evid: 327)
 *
 * Event description: CM 'Site Content' Page Loaded in BM
 *
 * @param {cmSiteContentPageLoadedInBmParams} params - The parameters for this event
 * @property {string} sub_tab - site_content
 * @property {boolean} buttonEnabled - true / false
 * @property {string} site_id - site_id
 * @property {string} uuid - uuid
 * @property {number} ts - ts
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} version - version
 * @property {string} environment - BUSINESS_MANAGER
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {boolean} floating_helper_shown - true / false
 * @property {GUID} esi - esi
 * @property {GUID} msid - msid
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<cmSiteContentPageLoadedInBmParams>} LogObject<cmSiteContentPageLoadedInBmParams>
 */
export function cmSiteContentPageLoadedInBm(params) {
    return { evid: 327, src: 83, endpoint: '', params };
}
/**
 * A function to create a log object for the 'cmSiteContentPageActionInBm' event (evid: 328)
 *
 * Event description: CM 'Site Content' Page Action in BM
 *
 * @param {cmSiteContentPageActionInBmParams} params - The parameters for this event
 * @property {string} sub_tab - site_content
 * @property {boolean} buttonEnabled - true / false
 * @property {string} site_id - site_id
 * @property {string} uuid - uuid
 * @property {number} ts - ts
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} version - version
 * @property {string} environment - BUSINESS_MANAGER
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {boolean} floating_helper_shown - true / false
 * @property {GUID} esi - esi
 * @property {GUID} msid - msid
 * @property {string} action - edit_site / learn_more / close_helper
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<cmSiteContentPageActionInBmParams>} LogObject<cmSiteContentPageActionInBmParams>
 */
export function cmSiteContentPageActionInBm(params) {
    return { evid: 328, src: 83, endpoint: '', params };
}
/**
 * A function to create a log object for the 'cmHomePageInBmExpandCollapseCollectionsSections' event (evid: 330)
 *
 * Event description: CM Home Page in BM: Expand/Collapse Collections Sections
 *
 * @param {cmHomePageInBmExpandCollapseCollectionsSectionsParams} params - The parameters for this event
 * @property {string} site_id - site_id
 * @property {string} uuid - uuid
 * @property {number} ts - ts
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} section - collections / db_drivers / external
 * @property {boolean} db_drivers_on - true / false
 * @property {string} version - version
 * @property {string} environment - environment
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {GUID} esi - esi
 * @property {GUID} msid - msid
 * @property {string} action - expand / collapse
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<cmHomePageInBmExpandCollapseCollectionsSectionsParams>} LogObject<cmHomePageInBmExpandCollapseCollectionsSectionsParams>
 */
export function cmHomePageInBmExpandCollapseCollectionsSections(params) {
    return { evid: 330, src: 83, endpoint: '', params };
}
/**
 * A function to create a log object for the 'cmCollectionBadgesHoverInteraction' event (evid: 331)
 *
 * Event description: CM Collection Badges hover interaction
 *
 * @param {cmCollectionBadgesHoverInteractionParams} params - The parameters for this event
 * @property {string} site_id - site_id
 * @property {number} ts - ts
 * @property {string} collection_id - collection_id
 * @property {boolean} is_draft_publish_enabled - draft_publish
 * @property {string} view_type - table / gallery / list
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} version - version
 * @property {string} badge_type - external / db_driver / blocks
 * @property {string} environment - EDITOR, BUSINESS_MANAGER
 * @property {boolean} is_sandbox_enabled - sandbox
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {string} message - the hover message the user sees
 * @property {GUID} esi - esi
 * @property {string} collection_name - collection name
 * @property {GUID} msid - msid
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<cmCollectionBadgesHoverInteractionParams>} LogObject<cmCollectionBadgesHoverInteractionParams>
 */
export function cmCollectionBadgesHoverInteraction(params) {
    return { evid: 331, src: 83, endpoint: '', params };
}
/**
 * A function to create a log object for the 'mobileCmIntroBannerInteractions' event (evid: 332)
 *
 * Event description: Mobile CM - Intro Banner Interactions
 *
 * @param {mobileCmIntroBannerInteractionsParams} params - The parameters for this event
 * @property {string} site_id - site_id
 * @property {string} uuid - uuid
 * @property {number} ts - ts
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} version - version
 * @property {string} origin - collections_page
 * @property {string} environment - EDITOR / BUSINESS MANAGER
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {string} category - show / close / click_submit
 * @property {GUID} esi - esi
 * @property {GUID} msid - msid
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<mobileCmIntroBannerInteractionsParams>} LogObject<mobileCmIntroBannerInteractionsParams>
 */
export function mobileCmIntroBannerInteractions(params) {
    return { evid: 332, src: 83, endpoint: '', params };
}
/**
 * A function to create a log object for the 'indexesErrorBannerExceedTheAmount' event (evid: 333)
 *
 * Event description: Indexes - Error Banner (Exceed the Amount)
 *
 * @param {indexesErrorBannerExceedTheAmountParams} params - The parameters for this event
 * @property {string} site_id - site_id
 * @property {number} ts - ts
 * @property {string} collection_id - collection_id
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} version - version
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {GUID} esi - esi
 * @property {string} collection_name - collection_name
 * @property {GUID} msid - msid
 * @property {string} action - show / click_x / got_it / learn_more
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<indexesErrorBannerExceedTheAmountParams>} LogObject<indexesErrorBannerExceedTheAmountParams>
 */
export function indexesErrorBannerExceedTheAmount(params) {
    return { evid: 333, src: 83, endpoint: '', params };
}
/**
 * A function to create a log object for the 'indexesEmptyStateLoadingError' event (evid: 334)
 *
 * Event description: Indexes - Empty State (Loading Error)
 *
 * @param {indexesEmptyStateLoadingErrorParams} params - The parameters for this event
 * @property {string} site_id - site_id
 * @property {number} ts - ts
 * @property {string} collection_id - collection_id
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} version - version
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {GUID} esi - esi
 * @property {string} collection_name - collection_name
 * @property {GUID} msid - msid
 * @property {string} action - show / click_refresh
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<indexesEmptyStateLoadingErrorParams>} LogObject<indexesEmptyStateLoadingErrorParams>
 */
export function indexesEmptyStateLoadingError(params) {
    return { evid: 334, src: 83, endpoint: '', params };
}
/**
 * A function to create a log object for the 'indexesWarningHoverDuplicates' event (evid: 335)
 *
 * Event description: Indexes - Warning Hover
 *
 * @param {indexesWarningHoverDuplicatesParams} params - The parameters for this event
 * @property {string} index_name - index_name
 * @property {string} site_id - site_id
 * @property {number} ts - ts
 * @property {string} collection_id - collection_id
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} version - version
 * @property {string} index_fields - index_fields
 * @property {string} origin - unknown / duplicate_key / too_large_document
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {GUID} esi - esi
 * @property {string} collection_name - collection_name
 * @property {GUID} msid - msid
 * @property {string} index_type - unique / regular
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<indexesWarningHoverDuplicatesParams>} LogObject<indexesWarningHoverDuplicatesParams>
 */
export function indexesWarningHoverDuplicates(params) {
    return { evid: 335, src: 83, endpoint: '', params };
}
/**
 * A function to create a log object for the 'externalCollectionsConnectionIssue' event (evid: 336)
 *
 * Event description: fires when a user gets a 409 error code while adding an external database
 *
 * @param {externalCollectionsConnectionIssueParams} params - The parameters for this event
 * @property {string} site_id - site_id
 * @property {GUID} uuid - uuid
 * @property {number} ts - ts
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {number} number_of_collections_exposed - number of collections exposed on endpoint setting
 * @property {string} version - version
 * @property {number} errorCode - error_code
 * @property {number} external_collections_added_for_the_first_time - **Deprecated**
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {GUID} esi - esi
 * @property {GUID} msid - msid
 * @property {string} namespace - namespace
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<externalCollectionsConnectionIssueParams>} LogObject<externalCollectionsConnectionIssueParams>
 */
export function externalCollectionsConnectionIssue(params) {
    return { evid: 336, src: 83, endpoint: '', params };
}
/**
 * A function to create a log object for the 'clickAddToSiteButtonFromTheCollectionInCm' event (evid: 337)
 *
 * Event description: User clicked 'Add to Site' button from the collection in CM
 *
 * @param {clickAddToSiteButtonFromTheCollectionInCmParams} params - The parameters for this event
 * @property {string} site_id - site_id
 * @property {number} ts - ts
 * @property {string} collection_id - collection ID
 * @property {number} number_of_items_in_collection - number of items in collection
 * @property {string} view_type - (TABLE, LIST, GALLERY)
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {number} time_to_open - time_to_open in ms
 * @property {string} version - version
 * @property {string} origin - currently only 'collection'
 * @property {string} environment - (Editor/Dashboard/one_app)
 * @property {boolean} is_sandbox_enabled - (true/false)
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {GUID} esi - esi
 * @property {boolean} is_single_item_collection - is single item collection
 * @property {string} collection_name - collection name
 * @property {GUID} msid - msid
 * @property {string} action - show / close / learn_more / add_dp
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<clickAddToSiteButtonFromTheCollectionInCmParams>} LogObject<clickAddToSiteButtonFromTheCollectionInCmParams>
 */
export function clickAddToSiteButtonFromTheCollectionInCm(params) {
    return { evid: 337, src: 83, endpoint: '', params };
}
/**
 * A function to create a log object for the 'cmAddFieldHoverOnTheFieldType' event (evid: 338)
 *
 * Event description: CM Add Field - User Hovers on the Field Type
 *
 * @param {cmAddFieldHoverOnTheFieldTypeParams} params - The parameters for this event
 * @property {string} site_id - site_id
 * @property {string} field_type - text / image / etc.
 * @property {number} ts - ts
 * @property {string} collection_id - collection id
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} version - version
 * @property {string} origin - collection_empty_state / column_header etc
 * @property {string} environment - environment
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {GUID} esi - esi
 * @property {string} collection_name - collection name
 * @property {GUID} msid - msid
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<cmAddFieldHoverOnTheFieldTypeParams>} LogObject<cmAddFieldHoverOnTheFieldTypeParams>
 */
export function cmAddFieldHoverOnTheFieldType(params) {
    return { evid: 338, src: 83, endpoint: '', params };
}
/**
 * A function to create a log object for the 'cmExportCountErrorOccurred' event (evid: 339)
 *
 * Event description: CM Export - Count Error Occurred
 *
 * @param {cmExportCountErrorOccurredParams} params - The parameters for this event
 * @property {string} export_type - (all, filtered, schema)
 * @property {string} site_id - site_id
 * @property {number} ts - ts
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} version - version
 * @property {string} error_type - error (case 1 or 2)
 * @property {boolean} is_live_data - {Live=true, Sandbox=false}
 * @property {string} environment - environment
 * @property {boolean} is_sandbox_enabled - (true/false)
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {GUID} esi - esi
 * @property {string} collection_name - collection name
 * @property {GUID} msid - msid
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<cmExportCountErrorOccurredParams>} LogObject<cmExportCountErrorOccurredParams>
 */
export function cmExportCountErrorOccurred(params) {
    return { evid: 339, src: 83, endpoint: '', params };
}
/**
 * A function to create a log object for the 'cmPremiumBannerInteractions' event (evid: 340)
 *
 * Event description: Trigger upon premium banner show & interactions within CM Dashboard/ Monitoring Dashboard
 *
 * @param {cmPremiumBannerInteractionsParams} params - The parameters for this event
 * @property {number} failed_requests - failed (BE/ DATA) requests last 30 days
 * @property {number} num_of_collections - number of collections user has
 * @property {number} items_quota_status - number of items site has/ limit of items available to the site
 * @property {string} site_id - site_id
 * @property {number} collections_quota_status - number of collections site has/ limit of collections available to the site
 * @property {number} ts - ts
 * @property {number} items_limit - limit of items available to user
 * @property {string} empty_state_type - (no_ddmo, no_collections, null)
 * @property {number} collections_limit - limit of collections available to user
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {number} total_requests - total (BE/ DATA) requests last 30 days
 * @property {string} version - version
 * @property {string} bannerType - yellow/ red/ white
 * @property {string} origin - bo_cm, bo_home, editor_cm, collection, monitoring_dashboard
 * @property {number} collections_size - in GB
 * @property {string} environment - (EDITOR, BUSINESS_MANAGER)
 * @property {boolean} is_sandbox_enabled - (true/false)
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {number} num_of_items - number of items user has
 * @property {boolean} isPremium - is user premium
 * @property {GUID} esi - esi
 * @property {GUID} msid - msid
 * @property {string} action - show, expand, click_upgrade
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<cmPremiumBannerInteractionsParams>} LogObject<cmPremiumBannerInteractionsParams>
 */
export function cmPremiumBannerInteractions(params) {
    return { evid: 340, src: 83, endpoint: '', params };
}
/**
 * A function to create a log object for the 'mPremiumModalInteractions' event (evid: 341)
 *
 * Event description: Trigger upon premium banner show & interactions within CM Dashboard
 *
 * @param {mPremiumModalInteractionsParams} params - The parameters for this event
 * @property {number} num_of_collections - number of collections user has
 * @property {string} site_id - site_id
 * @property {number} ts - ts
 * @property {number} items_limit - limit of items available to user
 * @property {number} collections_limit - limit of collections available to user
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} version - version
 * @property {string} origin - bo_cm, bo_home, editor_cm, collection
 * @property {string} trigger_action - add_collection / add_item
 * @property {string} environment - (EDITOR, BUSINESS_MANAGER)
 * @property {boolean} is_sandbox_enabled - (true/false)
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {number} num_of_items - number of items user has
 * @property {boolean} isPremium - is user premium
 * @property {GUID} esi - esi
 * @property {GUID} msid - msid
 * @property {string} action - show, close, learn_more, maybe_later, click_upgrade
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<mPremiumModalInteractionsParams>} LogObject<mPremiumModalInteractionsParams>
 */
export function mPremiumModalInteractions(params) {
    return { evid: 341, src: 83, endpoint: '', params };
}
/**
 * A function to create a log object for the 'mPremiumUpgradeApproveModal' event (evid: 342)
 *
 * Event description: Trigger upon premium approval modal show & interactions
 *
 * @param {mPremiumUpgradeApproveModalParams} params - The parameters for this event
 * @property {number} num_of_collections - number of collections user has
 * @property {string} premium_product_id - premium_product_id
 * @property {string} site_id - site_id
 * @property {number} ts - ts
 * @property {number} items_limit - new limit of items available to user
 * @property {number} collections_limit - new limit of collections available to user
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} version - version
 * @property {string} origin - bo_cm, bo_home, editor_cm, collection
 * @property {string} environment - (EDITOR, BUSINESS_MANAGER)
 * @property {boolean} is_sandbox_enabled - (true/false)
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {number} num_of_items - number of items user has
 * @property {GUID} esi - esi
 * @property {GUID} msid - msid
 * @property {string} action - show, close, learn_more, create_collection
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<mPremiumUpgradeApproveModalParams>} LogObject<mPremiumUpgradeApproveModalParams>
 */
export function mPremiumUpgradeApproveModal(params) {
    return { evid: 342, src: 83, endpoint: '', params };
}
/**
 * A function to create a log object for the 'cmPremiumInfoHover' event (evid: 343)
 *
 * Event description: Trigger upon info hover within Premium modals
 *
 * @param {cmPremiumInfoHoverParams} params - The parameters for this event
 * @property {number} num_of_collections - number of collections user has
 * @property {string} site_id - site_id
 * @property {number} ts - ts
 * @property {number} items_limit - limit of items available to user
 * @property {string} empty_state_type - (no_ddmo, no_collections, null)
 * @property {number} collections_limit - limit of collections available to user
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} section - collections_created / items_added / main_message etc
 * @property {string} version - version
 * @property {string} origin - bo_cm, bo_home, editor_cm, collection
 * @property {string} environment - (EDITOR, BUSINESS_MANAGER)
 * @property {boolean} is_sandbox_enabled - (true/false)
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {number} num_of_items - number of items user has
 * @property {boolean} isPremium - is user premium
 * @property {GUID} esi - esi
 * @property {GUID} msid - msid
 * @property {string} action - hover, click_learn_more
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<cmPremiumInfoHoverParams>} LogObject<cmPremiumInfoHoverParams>
 */
export function cmPremiumInfoHover(params) {
    return { evid: 343, src: 83, endpoint: '', params };
}
/**
 * A function to create a log object for the 'mPremiumUpgradeButtonClick' event (evid: 344)
 *
 * Event description: Trigger upon premium button click within new Wix Data entry point - Editor CM Panel subtab
 *
 * @param {mPremiumUpgradeButtonClickParams} params - The parameters for this event
 * @property {number} num_of_collections - number of collections user has
 * @property {string} site_id - site_id
 * @property {number} ts - ts
 * @property {number} items_limit - limit of items available to user
 * @property {number} collections_limit - limit of collections available to user
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} version - version
 * @property {string} origin - editor_6th_button, bo_cm, bo_home etc
 * @property {string} environment - (EDITOR, BUSINESS_MANAGER)
 * @property {boolean} is_sandbox_enabled - (true/false)
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {number} num_of_items - number of items user has
 * @property {boolean} isPremium - is user premium
 * @property {GUID} esi - esi
 * @property {GUID} msid - msid
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<mPremiumUpgradeButtonClickParams>} LogObject<mPremiumUpgradeButtonClickParams>
 */
export function mPremiumUpgradeButtonClick(params) {
    return { evid: 344, src: 83, endpoint: '', params };
}
/**
 * A function to create a log object for the 'cmPremiumUpgradeClick' event (evid: 345)
 *
 * Event description: Trigger once the user clicks on the main upgrade button from the diff CM entry points
 *
 * @param {cmPremiumUpgradeClickParams} params - The parameters for this event
 * @property {number} num_of_collections - number of collections user has
 * @property {string} site_id - site_id
 * @property {number} ts - ts
 * @property {number} items_limit - limit of items available to user
 * @property {string} empty_state_type - (no_ddmo, no_collections, null)
 * @property {number} collections_limit - limit of collections available to user
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} version - version
 * @property {string} origin - bo_cm, bo_home, editor_cm, collection, content_collection_storage
 * @property {string} environment - (EDITOR, BUSINESS_MANAGER, monitoring_dashboard)
 * @property {boolean} is_sandbox_enabled - (true/false)
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {number} num_of_items - number of items user has
 * @property {boolean} isPremium - is user premium
 * @property {GUID} esi - esi
 * @property {GUID} msid - msid
 * @property {string} action - upgrade_click
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<cmPremiumUpgradeClickParams>} LogObject<cmPremiumUpgradeClickParams>
 */
export function cmPremiumUpgradeClick(params) {
    return { evid: 345, src: 83, endpoint: '', params };
}
/**
 * A function to create a log object for the 'mobileCmRequestAFeature' event (evid: 346)
 *
 * Event description: Mobile CM Request a Feature
 *
 * @param {mobileCmRequestAFeatureParams} params - The parameters for this event
 * @property {string} featureName - requested feature
 * @property {string} site_id - site_id
 * @property {string} tab - manage_tab / platforms_tab
 * @property {number} ts - ts
 * @property {string} collection_id - collection id
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} version - version
 * @property {string} screen - collection_list / collection_item_list / collection_item / collection_view
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {GUID} esi - esi
 * @property {GUID} msid - guid
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<mobileCmRequestAFeatureParams>} LogObject<mobileCmRequestAFeatureParams>
 */
export function mobileCmRequestAFeature(params) {
    return { evid: 346, src: 83, endpoint: '', params };
}
/**
 * A function to create a log object for the 'mobileCmTooltipInCollectionInteraction' event (evid: 347)
 *
 * Event description: Mobile CM Tooltip in Collection Interaction
 *
 * @param {mobileCmTooltipInCollectionInteractionParams} params - The parameters for this event
 * @property {string} site_id - site_id
 * @property {number} ts - ts
 * @property {string} collection_id - collection ID
 * @property {number} number_of_items_in_collection - number of items in collection
 * @property {string} view_type - (TABLE, LIST, GALLERY)
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {number} time_to_open - time_to_open in ms
 * @property {string} version - version
 * @property {string} environment - (Editor/Business Manager)
 * @property {boolean} is_sandbox_enabled - (true/false)
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {GUID} esi - esi
 * @property {boolean} is_single_item_collection - is single item collection
 * @property {string} collection_name - collection name
 * @property {GUID} msid - msid
 * @property {string} action - show / close / click
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<mobileCmTooltipInCollectionInteractionParams>} LogObject<mobileCmTooltipInCollectionInteractionParams>
 */
export function mobileCmTooltipInCollectionInteraction(params) {
    return { evid: 347, src: 83, endpoint: '', params };
}
/**
 * A function to create a log object for the 'editFromMobileCmClicked' event (evid: 348)
 *
 * Event description: Edit from Mobile CM Clicked (More Actions)
 *
 * @param {editFromMobileCmClickedParams} params - The parameters for this event
 * @property {string} site_id - site_id
 * @property {number} ts - ts
 * @property {string} collection_id - collection ID
 * @property {number} number_of_items_in_collection - number of items in collection
 * @property {string} view_type - (TABLE, LIST, GALLERY)
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {number} time_to_open - time_to_open in ms
 * @property {string} version - version
 * @property {string} environment - (Editor/Business Manager)
 * @property {boolean} is_sandbox_enabled - (true/false)
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {GUID} esi - esi
 * @property {boolean} is_single_item_collection - is single item collection
 * @property {string} collection_name - collection name
 * @property {GUID} msid - msid
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<editFromMobileCmClickedParams>} LogObject<editFromMobileCmClickedParams>
 */
export function editFromMobileCmClicked(params) {
    return { evid: 348, src: 83, endpoint: '', params };
}
/**
 * A function to create a log object for the 'mFormCollectionsMovementInfoInteraction' event (evid: 349)
 *
 * Event description: СM Form Collections Movement Info Interaction
 *
 * @param {mFormCollectionsMovementInfoInteractionParams} params - The parameters for this event
 * @property {number} num_of_collections - number of collections user has
 * @property {string} site_id - site_id
 * @property {number} ts - ts
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {boolean} db_drivers_on - true if they are visible
 * @property {string} version - version
 * @property {string} origin - bo_cm, editor_cm
 * @property {number} num_of_form_collections - number of form collections
 * @property {string} environment - (EDITOR, BUSINESS_MANAGER)
 * @property {boolean} is_sandbox_enabled - (true/false)
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {number} num_of_items - number of items user has
 * @property {boolean} isPremium - is user premium
 * @property {GUID} esi - esi
 * @property {GUID} msid - msid
 * @property {string} action - show, close, go_to_forms, go_to_settings
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<mFormCollectionsMovementInfoInteractionParams>} LogObject<mFormCollectionsMovementInfoInteractionParams>
 */
export function mFormCollectionsMovementInfoInteraction(params) {
    return { evid: 349, src: 83, endpoint: '', params };
}
/**
 * A function to create a log object for the 'mobileCmRichTextElementInteraction' event (evid: 350)
 *
 * Event description: Mobile CM Rich Text Element Interaction
 *
 * @param {mobileCmRichTextElementInteractionParams} params - The parameters for this event
 * @property {string} site_id - site_id
 * @property {string} tab - manage_tab / platforms_tab
 * @property {number} ts - ts
 * @property {string} collection_id - collection id
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} field_value - field value
 * @property {string} version - version
 * @property {string} screen - collection_list / collection_item_list / collection_item / collection_view
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {GUID} esi - esi
 * @property {GUID} msid - guid
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<mobileCmRichTextElementInteractionParams>} LogObject<mobileCmRichTextElementInteractionParams>
 */
export function mobileCmRichTextElementInteraction(params) {
    return { evid: 350, src: 83, endpoint: '', params };
}
/**
 * A function to create a log object for the 'cmAddLinkToMediaGallery' event (evid: 351)
 *
 * Event description: CM Add Link to Media Gallery
 *
 * @param {cmAddLinkToMediaGalleryParams} params - The parameters for this event
 * @property {string} site_id - site_id
 * @property {string} field_type - media_gallery
 * @property {number} ts - ts
 * @property {string} collection_id - collection_id
 * @property {boolean} is_draft_publish_enabled - (true, false)
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} item_draft_status - (draft, published, scheduled_to_publish, scheduled_to_draft)
 * @property {string} link_to - page / web_address etc.
 * @property {string} version - version
 * @property {boolean} is_live_data - {Live=true, Sandbox=false}
 * @property {string} origin - cell, item_page etc.
 * @property {string} environment - (Editor/Dashboard/one_app)
 * @property {boolean} is_sandbox_enabled - (true/false)
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {GUID} esi - esi
 * @property {boolean} is_single_item_collection - is single item collection
 * @property {string} collection_name - collection name
 * @property {GUID} msid - msid
 * @property {string} field_name - field_name
 * @property {string} open_in - new_window / current_window
 * @property {string} action - add_link / delete_link
 * @property {boolean} advanced_settings - true if are set
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<cmAddLinkToMediaGalleryParams>} LogObject<cmAddLinkToMediaGalleryParams>
 */
export function cmAddLinkToMediaGallery(params) {
    return { evid: 351, src: 83, endpoint: '', params };
}
/**
 * A function to create a log object for the 'enterpriseCmShareCollection' event (evid: 352)
 *
 * Event description: User clicked to share collection
 *
 * @param {enterpriseCmShareCollectionParams} params - The parameters for this event
 * @property {string} site_id - site_id
 * @property {number} ts - ts
 * @property {string} collection_id - collection id
 * @property {number} number_of_items_in_collection - In One App, it's the total number of items available in the active view, not the total number of items in the collection.
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} version - version
 * @property {string} origin - collections_card / collection
 * @property {string} environment - enterprise_cm
 * @property {boolean} is_sandbox_enabled - (true/false)
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {GUID} esi - esi
 * @property {boolean} is_single_item_collection - is single item collection
 * @property {string} collection_name - In One App, it's a collection ID.
 * @property {GUID} msid - msid
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<enterpriseCmShareCollectionParams>} LogObject<enterpriseCmShareCollectionParams>
 */
export function enterpriseCmShareCollection(params) {
    return { evid: 352, src: 83, endpoint: '', params };
}
/**
 * A function to create a log object for the 'enterpriseCmListOfSitesLoaded' event (evid: 353)
 *
 * Event description: Enterprise CM: List of sites loaded
 *
 * @param {enterpriseCmListOfSitesLoadedParams} params - The parameters for this event
 * @property {boolean} folders_filtered - are folders filtered
 * @property {number} number_of_sites_available - number_of_sites_available
 * @property {string} site_id - site_id
 * @property {string} tab_name - sites / templates
 * @property {number} ts - ts
 * @property {string} collection_id - collection id
 * @property {number} number_of_items_in_collection - In One App, it's the total number of items available in the active view, not the total number of items in the collection.
 * @property {boolean} search_applied - search_applied
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} version - version
 * @property {string} origin - collections_card / collection
 * @property {string} environment - enterprise_cm
 * @property {boolean} is_sandbox_enabled - (true/false)
 * @property {number} number_of_sites_loaded - number of sites (or templates)
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {GUID} esi - esi
 * @property {boolean} is_single_item_collection - is single item collection
 * @property {string} collection_name - In One App, it's a collection ID.
 * @property {GUID} msid - msid
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<enterpriseCmListOfSitesLoadedParams>} LogObject<enterpriseCmListOfSitesLoadedParams>
 */
export function enterpriseCmListOfSitesLoaded(params) {
    return { evid: 353, src: 83, endpoint: '', params };
}
/**
 * A function to create a log object for the 'cmFieldsValidationLimitCharactersToggleChange' event (evid: 354)
 *
 * Event description: CM Fields Validation - Limit Characters Toggle Change
 *
 * @param {cmFieldsValidationLimitCharactersToggleChangeParams} params - The parameters for this event
 * @property {boolean} toggle_state - True/False
 * @property {string} action_name - ADD_FIELD / EDIT_FIELD
 * @property {string} site_id - site_id
 * @property {string} field_type - type of field
 * @property {number} ts - ts
 * @property {string} collection_id - collection_id
 * @property {boolean} description_edited - (true/false)
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} version - version
 * @property {boolean} is_live_data - (true/false)
 * @property {string} origin - ["FIELD_HEADER",
 * "LIST_VISIBLE_FIELDS_BUTTON",
 * "TABLE_VISIBLE_FIELDS_BUTTON",
 * "ITEM_VISIBLE_FIELD_BUTTON",
 * "ADD_COLUMN_BUTTON",
 * "GALLERY_VISIBLE_FIELDS_BUTTON",
 * "SINGLES_ADD_FIELD","MULTIPLES_ADD_FIELD"]
 * @property {string} environment - Editor / business_manager
 * @property {boolean} is_sandbox_enabled - (true/false)
 * @property {string} view_layout - table / gallery / list
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {GUID} esi - esi
 * @property {string} collection_name - collection name
 * @property {GUID} msid - msid
 * @property {string} field_name - field_name
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<cmFieldsValidationLimitCharactersToggleChangeParams>} LogObject<cmFieldsValidationLimitCharactersToggleChangeParams>
 */
export function cmFieldsValidationLimitCharactersToggleChange(params) {
    return { evid: 354, src: 83, endpoint: '', params };
}
/**
 * A function to create a log object for the 'cmFieldsValidationAcceptSpecificToggleChange' event (evid: 355)
 *
 * Event description: CM Fields Validation - Accept Specific Toggle Change
 *
 * @param {cmFieldsValidationAcceptSpecificToggleChangeParams} params - The parameters for this event
 * @property {boolean} toggle_state - True/False
 * @property {string} action_name - ADD_FIELD / EDIT_FIELD
 * @property {string} site_id - site_id
 * @property {string} field_type - type of field
 * @property {number} ts - ts
 * @property {string} collection_id - collection_id
 * @property {boolean} description_edited - (true/false)
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} version - version
 * @property {boolean} is_live_data - (true/false)
 * @property {string} origin - ["FIELD_HEADER",
 * "LIST_VISIBLE_FIELDS_BUTTON",
 * "TABLE_VISIBLE_FIELDS_BUTTON",
 * "ITEM_VISIBLE_FIELD_BUTTON",
 * "ADD_COLUMN_BUTTON",
 * "GALLERY_VISIBLE_FIELDS_BUTTON",
 * "SINGLES_ADD_FIELD","MULTIPLES_ADD_FIELD"]
 * @property {string} environment - Editor / business_manager
 * @property {boolean} is_sandbox_enabled - (true/false)
 * @property {string} view_layout - table / gallery / list
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {GUID} esi - esi
 * @property {string} collection_name - collection name
 * @property {GUID} msid - msid
 * @property {string} field_name - field_name
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<cmFieldsValidationAcceptSpecificToggleChangeParams>} LogObject<cmFieldsValidationAcceptSpecificToggleChangeParams>
 */
export function cmFieldsValidationAcceptSpecificToggleChange(params) {
    return { evid: 355, src: 83, endpoint: '', params };
}
/**
 * A function to create a log object for the 'cmFieldsValidationSpecificValuesSet' event (evid: 356)
 *
 * Event description: CM Fields Validation - Specific Values Set
 *
 * @param {cmFieldsValidationSpecificValuesSetParams} params - The parameters for this event
 * @property {string} action_name - ADD_FIELD / EDIT_FIELD
 * @property {string} site_id - site_id
 * @property {string} field_type - type of field
 * @property {number} number_of_values_added - number_of_values_added
 * @property {number} ts - ts
 * @property {string} collection_id - collection_id
 * @property {boolean} description_edited - (true/false)
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} version - version
 * @property {boolean} is_live_data - (true/false)
 * @property {string} origin - ["FIELD_HEADER",
 * "LIST_VISIBLE_FIELDS_BUTTON",
 * "TABLE_VISIBLE_FIELDS_BUTTON",
 * "ITEM_VISIBLE_FIELD_BUTTON",
 * "ADD_COLUMN_BUTTON",
 * "GALLERY_VISIBLE_FIELDS_BUTTON",
 * "SINGLES_ADD_FIELD","MULTIPLES_ADD_FIELD"]
 * @property {string} environment - Editor / business_manager
 * @property {boolean} is_sandbox_enabled - (true/false)
 * @property {string} view_layout - table / gallery / list
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {GUID} esi - esi
 * @property {string} collection_name - collection name
 * @property {GUID} msid - msid
 * @property {string} field_name - field_name
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<cmFieldsValidationSpecificValuesSetParams>} LogObject<cmFieldsValidationSpecificValuesSetParams>
 */
export function cmFieldsValidationSpecificValuesSet(params) {
    return { evid: 356, src: 83, endpoint: '', params };
}
/**
 * A function to create a log object for the 'enterpriseCmSiteShared' event (evid: 357)
 *
 * Event description: Enterprise CM: Collection Shared with Sites/Templates
 *
 * @param {enterpriseCmSiteSharedParams} params - The parameters for this event
 * @property {boolean} folders_filtered - are folders filtered
 * @property {number} number_of_sites_available - number_of_sites_available
 * @property {string} site_id - site_id
 * @property {string} tab_name - sites / templates
 * @property {number} ts - ts
 * @property {string} collection_id - collection id
 * @property {string} selectionType - bulk, item
 * @property {number} number_of_items_in_collection - In One App, it's the total number of items available in the active view, not the total number of items in the collection.
 * @property {boolean} search_applied - search_applied
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} version - version
 * @property {string} origin - collections_card / collection
 * @property {string} share_type - entire_collection, filtered
 * @property {string} environment - enterprise_cm
 * @property {boolean} is_sandbox_enabled - (true/false)
 * @property {number} number_of_sites_loaded - number_of_sites
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {GUID} esi - esi
 * @property {boolean} is_single_item_collection - is single item collection
 * @property {string} collection_name - In One App, it's a collection ID.
 * @property {GUID} msid - msid (or msids) selected
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<enterpriseCmSiteSharedParams>} LogObject<enterpriseCmSiteSharedParams>
 */
export function enterpriseCmSiteShared(params) {
    return { evid: 357, src: 83, endpoint: '', params };
}
/**
 * A function to create a log object for the 'enterpriseCmCollectionUnsharedWithSitesTemplates' event (evid: 358)
 *
 * Event description: Enterprise CM: Referred Collection Info Modals
 *
 * @param {enterpriseCmCollectionUnsharedWithSitesTemplatesParams} params - The parameters for this event
 * @property {boolean} folders_filtered - are folders filtered
 * @property {number} number_of_sites_available - number_of_sites_available
 * @property {string} site_id - site_id
 * @property {string} tab_name - sites / templates
 * @property {number} ts - ts
 * @property {string} collection_id - collection id
 * @property {string} selectionType - bulk, item
 * @property {number} number_of_items_in_collection - In One App, it's the total number of items available in the active view, not the total number of items in the collection.
 * @property {boolean} search_applied - search_applied
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} version - version
 * @property {string} origin - collections_card / collection
 * @property {string} trigger_action - share / unshare
 * @property {string} share_type - entire_collection, filtered
 * @property {string} environment - enterprise_cm
 * @property {boolean} is_sandbox_enabled - (true/false)
 * @property {number} number_of_sites_loaded - number_of_sites
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {GUID} esi - esi
 * @property {boolean} is_single_item_collection - is single item collection
 * @property {string} collection_name - In One App, it's a collection ID.
 * @property {GUID} msid - msid (or msids) selected
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<enterpriseCmCollectionUnsharedWithSitesTemplatesParams>} LogObject<enterpriseCmCollectionUnsharedWithSitesTemplatesParams>
 */
export function enterpriseCmCollectionUnsharedWithSitesTemplates(params) {
    return { evid: 358, src: 83, endpoint: '', params };
}
/**
 * A function to create a log object for the 'enterpriseCmReferredCollectionInfoModals' event (evid: 359)
 *
 * Event description: Enterprise CM: Referred Collection Info Modals
 *
 * @param {enterpriseCmReferredCollectionInfoModalsParams} params - The parameters for this event
 * @property {boolean} folders_filtered - are folders filtered
 * @property {number} number_of_sites_available - number_of_sites_available
 * @property {string} site_id - site_id
 * @property {string} tab_name - sites / templates
 * @property {number} ts - ts
 * @property {string} collection_id - collection id
 * @property {string} selectionType - bulk, item
 * @property {number} number_of_items_in_collection - In One App, it's the total number of items available in the active view, not the total number of items in the collection.
 * @property {boolean} search_applied - search_applied
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} version - version
 * @property {string} origin - collections_card / collection
 * @property {string} trigger_action - share / unshare
 * @property {string} share_type - entire_collection, filtered
 * @property {string} environment - enterprise_cm
 * @property {boolean} is_sandbox_enabled - (true/false)
 * @property {number} number_of_sites_loaded - number_of_sites
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {GUID} esi - esi
 * @property {boolean} is_single_item_collection - is single item collection
 * @property {string} collection_name - In One App, it's a collection ID.
 * @property {GUID} msid - msid (or msids) selected
 * @property {string} action - show / close / dont_show_again /cancel
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<enterpriseCmReferredCollectionInfoModalsParams>} LogObject<enterpriseCmReferredCollectionInfoModalsParams>
 */
export function enterpriseCmReferredCollectionInfoModals(params) {
    return { evid: 359, src: 83, endpoint: '', params };
}
/**
 * A function to create a log object for the 'enterpriseCmInfoHover' event (evid: 360)
 *
 * Event description: Enterprise CM: Info Hover
 *
 * @param {enterpriseCmInfoHoverParams} params - The parameters for this event
 * @property {string} site_id - site_id
 * @property {number} ts - ts
 * @property {string} collection_id - collection id
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} version - version
 * @property {string} origin - where was it triggered
 * @property {string} environment - enterprise_cm / Editor / Business_Manager
 * @property {boolean} is_sandbox_enabled - (true/false)
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {string} message - message
 * @property {GUID} esi - esi
 * @property {boolean} is_single_item_collection - is single item collection
 * @property {string} collection_name - In One App, it's a collection ID.
 * @property {GUID} msid - msid (or msids) selected
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<enterpriseCmInfoHoverParams>} LogObject<enterpriseCmInfoHoverParams>
 */
export function enterpriseCmInfoHover(params) {
    return { evid: 360, src: 83, endpoint: '', params };
}
/**
 * A function to create a log object for the 'cmFieldsValidationCharactersLimitSet' event (evid: 361)
 *
 * Event description: CM Fields Validation - Characters Limit Set
 *
 * @param {cmFieldsValidationCharactersLimitSetParams} params - The parameters for this event
 * @property {string} action_name - ADD_FIELD / EDIT_FIELD
 * @property {string} site_id - site_id
 * @property {string} field_type - type of field
 * @property {number} ts - ts
 * @property {string} collection_id - collection_id
 * @property {boolean} description_edited - (true/false)
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} version - version
 * @property {boolean} is_live_data - (true/false)
 * @property {string} origin - ["FIELD_HEADER",
 * "LIST_VISIBLE_FIELDS_BUTTON",
 * "TABLE_VISIBLE_FIELDS_BUTTON",
 * "ITEM_VISIBLE_FIELD_BUTTON",
 * "ADD_COLUMN_BUTTON",
 * "GALLERY_VISIBLE_FIELDS_BUTTON",
 * "SINGLES_ADD_FIELD","MULTIPLES_ADD_FIELD"]
 * @property {string} environment - Editor / business_manager
 * @property {boolean} is_sandbox_enabled - (true/false)
 * @property {string} view_layout - table / gallery / list
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {number} max_value_set - max_value_set
 * @property {GUID} esi - esi
 * @property {number} min_value_set - min_value_set
 * @property {string} collection_name - collection name
 * @property {GUID} msid - msid
 * @property {string} field_name - field_name
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<cmFieldsValidationCharactersLimitSetParams>} LogObject<cmFieldsValidationCharactersLimitSetParams>
 */
export function cmFieldsValidationCharactersLimitSet(params) {
    return { evid: 361, src: 83, endpoint: '', params };
}
/**
 * A function to create a log object for the 'cmFieldTypeCardFieldSelection' event (evid: 362)
 *
 * Event description: CM Field Type Card - Field Selection
 *
 * @param {cmFieldTypeCardFieldSelectionParams} params - The parameters for this event
 * @property {string} site_id - site_id
 * @property {string} field_type - type of field
 * @property {number} ts - ts
 * @property {string} collection_id - collection id
 * @property {boolean} description_edited - (true/false)
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} version - version
 * @property {boolean} is_live_data - (true/false)
 * @property {string} origin - ["FIELD_HEADER",
 * "LIST_VISIBLE_FIELDS_BUTTON",
 * "TABLE_VISIBLE_FIELDS_BUTTON",
 * "ITEM_VISIBLE_FIELD_BUTTON",
 * "ADD_COLUMN_BUTTON",
 * "GALLERY_VISIBLE_FIELDS_BUTTON",
 * "SINGLES_ADD_FIELD","MULTIPLES_ADD_FIELD"]
 * @property {string} environment - Editor / business_manager
 * @property {boolean} is_sandbox_enabled - (true/false)
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {GUID} esi - esi
 * @property {string} collection_name - collection name
 * @property {GUID} msid - msid
 * @property {string} field_name - field_name
 * @property {string} action - add_field / edit_field
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<cmFieldTypeCardFieldSelectionParams>} LogObject<cmFieldTypeCardFieldSelectionParams>
 */
export function cmFieldTypeCardFieldSelection(params) {
    return { evid: 362, src: 83, endpoint: '', params };
}
/**
 * A function to create a log object for the 'cmFieldTypeCardInteractions' event (evid: 363)
 *
 * Event description: CM Field Type Card Interactions
 *
 * @param {cmFieldTypeCardInteractionsParams} params - The parameters for this event
 * @property {string} site_id - site_id
 * @property {number} ts - ts
 * @property {string} collection_id - collection id
 * @property {boolean} description_edited - (true/false)
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} version - version
 * @property {boolean} is_live_data - (true/false)
 * @property {string} origin - ["FIELD_HEADER",
 * "LIST_VISIBLE_FIELDS_BUTTON",
 * "TABLE_VISIBLE_FIELDS_BUTTON",
 * "ITEM_VISIBLE_FIELD_BUTTON",
 * "ADD_COLUMN_BUTTON",
 * "GALLERY_VISIBLE_FIELDS_BUTTON",
 * "SINGLES_ADD_FIELD","MULTIPLES_ADD_FIELD"]
 * @property {string} environment - Editor / business_manager
 * @property {boolean} is_sandbox_enabled - (true/false)
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {string} type - show / cancel / learn_more / close
 * @property {GUID} esi - esi
 * @property {string} collection_name - collection name
 * @property {GUID} msid - msid
 * @property {string} action - add_field / edit_field
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<cmFieldTypeCardInteractionsParams>} LogObject<cmFieldTypeCardInteractionsParams>
 */
export function cmFieldTypeCardInteractions(params) {
    return { evid: 363, src: 83, endpoint: '', params };
}
/**
 * A function to create a log object for the 'veloMonitoringDashboardOverviewCardInteraction' event (evid: 364)
 *
 * Event description: Trigger upon user clicks on the 'upgrade the site' button in the monitoring dashboard overview cards
 *
 * @param {veloMonitoringDashboardOverviewCardInteractionParams} params - The parameters for this event
 * @property {number} failed_requests - failed (BE/ DATA) requests last 30 days
 * @property {number} num_of_collections - number of collections user has
 * @property {number} items_quota_status - number of items site has/ limit of items available to the site
 * @property {string} site_id - site_id
 * @property {number} collections_quota_status - number of collections site has/ limit of collections available to the site
 * @property {number} ts - ts
 * @property {number} items_limit - limit of items available to user
 * @property {string} empty_state_type - (no_ddmo, no_collections, null)
 * @property {number} collections_limit - limit of collections available to user
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {number} total_requests - total (BE/ DATA) requests last 30 days
 * @property {string} version - version
 * @property {string} origin - data_tab, compute_tab, collections_tab
 * @property {number} collections_size - in GB
 * @property {string} environment - monitoring_dashboard
 * @property {boolean} is_sandbox_enabled - (true/false)
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {number} num_of_items - number of items user has
 * @property {boolean} isPremium - is user premium
 * @property {GUID} esi - esi
 * @property {GUID} msid - msid
 * @property {string} action - click_upgrade
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<veloMonitoringDashboardOverviewCardInteractionParams>} LogObject<veloMonitoringDashboardOverviewCardInteractionParams>
 */
export function veloMonitoringDashboardOverviewCardInteraction(params) {
    return { evid: 364, src: 83, endpoint: '', params };
}
/**
 * A function to create a log object for the 'cmUrlSlugFieldInfoHover' event (evid: 365)
 *
 * Event description: CM URL Slug Field Info Hover
 *
 * @param {cmUrlSlugFieldInfoHoverParams} params - The parameters for this event
 * @property {string} site_id - site_id
 * @property {string} field_type - text / image / etc.
 * @property {number} ts - ts
 * @property {string} collection_id - collection id
 * @property {string} view_type - table / list / gallery etc.
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} version - version
 * @property {string} origin - collection / column_header etc
 * @property {string} environment - environment
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {GUID} esi - esi
 * @property {string} collection_name - collection name
 * @property {GUID} msid - msid
 * @property {string} field_name - field_name
 * @property {string} action - hover_icon / click_learn_more / show_empty_warning
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<cmUrlSlugFieldInfoHoverParams>} LogObject<cmUrlSlugFieldInfoHoverParams>
 */
export function cmUrlSlugFieldInfoHover(params) {
    return { evid: 365, src: 83, endpoint: '', params };
}
/**
 * A function to create a log object for the 'cmDynamicPagesFieldsTooltipsInteraction' event (evid: 366)
 *
 * Event description: CM Dynamic Pages Fields Tooltips Interaction
 *
 * @param {cmDynamicPagesFieldsTooltipsInteractionParams} params - The parameters for this event
 * @property {string} site_id - site_id
 * @property {string} field_type - text / image / etc.
 * @property {number} ts - ts
 * @property {string} collection_id - collection id
 * @property {string} view_type - table / list / gallery etc.
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} version - version
 * @property {string} origin - collection / column_header etc
 * @property {string} environment - environment
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {GUID} esi - esi
 * @property {string} collection_name - collection name
 * @property {string} page_type - item_page, list_page
 * @property {GUID} msid - msid
 * @property {string} field_name - field_name
 * @property {string} action - hover / 'go_to_page_settings'
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<cmDynamicPagesFieldsTooltipsInteractionParams>} LogObject<cmDynamicPagesFieldsTooltipsInteractionParams>
 */
export function cmDynamicPagesFieldsTooltipsInteraction(params) {
    return { evid: 366, src: 83, endpoint: '', params };
}
/**
 * A function to create a log object for the 'cmsDeleteASeoRelatedFieldModal' event (evid: 367)
 *
 * Event description: CMS - Delete a SEO-related Field Modal
 *
 * @param {cmsDeleteASeoRelatedFieldModalParams} params - The parameters for this event
 * @property {string} site_id - site_id
 * @property {string} field_type - string, image etc.
 * @property {string} seo_settings_type - in which settings field is used
 * @property {number} ts - ts
 * @property {string} collection_id - collection id
 * @property {boolean} is_draft_publish_enabled - (true, false)
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} version - version
 * @property {boolean} is_live_data - (true/false)
 * @property {string} origin - (TABLE_VISIBLE_FIELDS_BUTTON, TABLE_FIELD_HEADER,LIST_VISIBLE_FIELDS_BUTTON, GALLERY_VISIBLE_FIELDS_BUTTON, ITEM_VISIBLE_FIELD_BUTTON)
 * @property {string} environment - EDITOR / BUSINESS MANAGER
 * @property {boolean} is_sandbox_enabled - (true/false)
 * @property {string} view_layout - (table, list, gallery)
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {GUID} esi - esi
 * @property {string} collection_name - collection name
 * @property {GUID} msid - msid
 * @property {string} field_name - field_name
 * @property {string} action - delete / cancel / click_hide
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<cmsDeleteASeoRelatedFieldModalParams>} LogObject<cmsDeleteASeoRelatedFieldModalParams>
 */
export function cmsDeleteASeoRelatedFieldModal(params) {
    return { evid: 367, src: 83, endpoint: '', params };
}
/**
 * A function to create a log object for the 'mlManageTranslationsClicked' event (evid: 368)
 *
 * Event description: ML Manage Translations Clicked (More Actions)
 *
 * @param {mlManageTranslationsClickedParams} params - The parameters for this event
 * @property {string} site_id - site_id
 * @property {number} ts - ts
 * @property {string} collection_id - collection ID
 * @property {number} number_of_items_in_collection - number of items in collection
 * @property {string} view_type - (TABLE, LIST, GALLERY)
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {number} time_to_open - time_to_open in ms
 * @property {string} version - version
 * @property {string} environment - (Editor/Business Manager)
 * @property {boolean} is_sandbox_enabled - (true/false)
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {GUID} esi - esi
 * @property {boolean} is_single_item_collection - is single item collection
 * @property {string} collection_name - collection name
 * @property {GUID} msid - msid
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<mlManageTranslationsClickedParams>} LogObject<mlManageTranslationsClickedParams>
 */
export function mlManageTranslationsClicked(params) {
    return { evid: 368, src: 83, endpoint: '', params };
}
/**
 * A function to create a log object for the 'mlOptInOutACollection' event (evid: 369)
 *
 * Event description: ML: Opt in/out a Collection
 *
 * @param {mlOptInOutACollectionParams} params - The parameters for this event
 * @property {string} site_id - site_id
 * @property {number} ts - ts
 * @property {string} collection_id - collection id
 * @property {number} number_of_items_in_collection - In One App, it's the total number of items available in the active view, not the total number of items in the collection.
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} version - version
 * @property {boolean} new_state - new_state
 * @property {string} environment - environment
 * @property {boolean} is_sandbox_enabled - (true/false)
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {GUID} esi - esi
 * @property {boolean} is_single_item_collection - is single item collection
 * @property {string} collection_name - In One App, it's a collection ID.
 * @property {GUID} msid - msid
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<mlOptInOutACollectionParams>} LogObject<mlOptInOutACollectionParams>
 */
export function mlOptInOutACollection(params) {
    return { evid: 369, src: 83, endpoint: '', params };
}
/**
 * A function to create a log object for the 'mlListOfCollectionsLoaded' event (evid: 370)
 *
 * Event description: ML: List of collections loaded
 *
 * @param {mlListOfCollectionsLoadedParams} params - The parameters for this event
 * @property {string} site_id - site_id
 * @property {number} ts - ts
 * @property {number} number_of_collections_unavailable - number_of_collections_unavailable
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {number} number_of_collections_available - number_of_collections_available
 * @property {string} version - version
 * @property {string} origin - ml_left_panel
 * @property {string} environment - environment
 * @property {boolean} is_sandbox_enabled - (true/false)
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {number} number_of_collections_selected - number_of_collections_selected
 * @property {GUID} esi - esi
 * @property {boolean} is_single_item_collection - is single item collection
 * @property {GUID} msid - msid
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<mlListOfCollectionsLoadedParams>} LogObject<mlListOfCollectionsLoadedParams>
 */
export function mlListOfCollectionsLoaded(params) {
    return { evid: 370, src: 83, endpoint: '', params };
}
/**
 * A function to create a log object for the 'mlListOfCollectionsActions' event (evid: 371)
 *
 * Event description: ML: List of collections actions
 *
 * @param {mlListOfCollectionsActionsParams} params - The parameters for this event
 * @property {string} site_id - site_id
 * @property {number} ts - ts
 * @property {number} number_of_collections_unavailable - number_of_collections_unavailable
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {number} number_of_collections_available - number_of_collections_available
 * @property {string} version - version
 * @property {string} origin - ml_left_panel
 * @property {string} environment - environment
 * @property {boolean} is_sandbox_enabled - (true/false)
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {number} number_of_collections_selected - number_of_collections_selected
 * @property {GUID} esi - esi
 * @property {boolean} is_single_item_collection - is single item collection
 * @property {GUID} msid - msid
 * @property {string} action - close, learn_more, click_save
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<mlListOfCollectionsActionsParams>} LogObject<mlListOfCollectionsActionsParams>
 */
export function mlListOfCollectionsActions(params) {
    return { evid: 371, src: 83, endpoint: '', params };
}
/**
 * A function to create a log object for the 'mlListOfCollectionsError' event (evid: 372)
 *
 * Event description: ML: List of collections error
 *
 * @param {mlListOfCollectionsErrorParams} params - The parameters for this event
 * @property {string} site_id - site_id
 * @property {number} ts - ts
 * @property {number} number_of_collections_unavailable - number_of_collections_unavailable
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {number} number_of_collections_available - number_of_collections_available
 * @property {string} version - version
 * @property {string} error_type - no_permissions, no_connection, etc.
 * @property {string} origin - ml_left_panel
 * @property {string} environment - environment
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {number} number_of_collections_selected - number_of_collections_selected
 * @property {GUID} esi - esi
 * @property {GUID} msid - msid
 * @property {string} action - show, close, click_retry
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<mlListOfCollectionsErrorParams>} LogObject<mlListOfCollectionsErrorParams>
 */
export function mlListOfCollectionsError(params) {
    return { evid: 372, src: 83, endpoint: '', params };
}
/**
 * A function to create a log object for the 'aiCollectionCreationModal' event (evid: 373)
 *
 * Event description: AI Collection Creation Modal
 *
 * @param {aiCollectionCreationModalParams} params - The parameters for this event
 * @property {boolean} is_first_data_modal - {true,false}
 * @property {string} site_id - site_id
 * @property {number} ts - ts
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} version - version
 * @property {string} modal_name - start_with, name_description, fields_selection,
 * @property {string} origin - ["ideTree", "add_panel", "DS_settings"]
 * @property {string} environment - environment
 * @property {boolean} is_sandbox_enabled - (true/false)
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {GUID} esi - esi
 * @property {GUID} msid - msid
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<aiCollectionCreationModalParams>} LogObject<aiCollectionCreationModalParams>
 */
export function aiCollectionCreationModal(params) {
    return { evid: 373, src: 83, endpoint: '', params };
}
/**
 * A function to create a log object for the 'aiCollectionCreationModalActions' event (evid: 374)
 *
 * Event description: AI Collection Creation Modal Actions
 *
 * @param {aiCollectionCreationModalActionsParams} params - The parameters for this event
 * @property {boolean} is_first_data_modal - {true,false}
 * @property {string} site_id - site_id
 * @property {number} ts - ts
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} version - version
 * @property {string} modal_name - start_with, name_description, fields_selection,
 * @property {string} origin - ["ideTree", "add_panel", "DS_settings"]
 * @property {string} environment - environment
 * @property {boolean} is_sandbox_enabled - (true/false)
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {GUID} esi - esi
 * @property {GUID} msid - msid
 * @property {string} action - click_empty, click_ai, click_next, give_name, give_desc, opt_content, deselect_field, click_create_coll
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<aiCollectionCreationModalActionsParams>} LogObject<aiCollectionCreationModalActionsParams>
 */
export function aiCollectionCreationModalActions(params) {
    return { evid: 374, src: 83, endpoint: '', params };
}
/**
 * A function to create a log object for the 'aiCollectionCreationModalError' event (evid: 375)
 *
 * Event description: AI Collection Creation Modal Error
 *
 * @param {aiCollectionCreationModalErrorParams} params - The parameters for this event
 * @property {boolean} is_first_data_modal - {true,false}
 * @property {string} site_id - site_id
 * @property {number} ts - ts
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} version - version
 * @property {string} error_type - error_type
 * @property {string} modal_name - start_with, name_description, fields_selection,
 * @property {string} origin - ["ideTree", "add_panel", "DS_settings"]
 * @property {string} environment - environment
 * @property {boolean} is_sandbox_enabled - (true/false)
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {GUID} esi - esi
 * @property {GUID} msid - msid
 * @property {string} action - close, retry, etc.
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<aiCollectionCreationModalErrorParams>} LogObject<aiCollectionCreationModalErrorParams>
 */
export function aiCollectionCreationModalError(params) {
    return { evid: 375, src: 83, endpoint: '', params };
}
/**
 * A function to create a log object for the 'collectionDetailsTableLearnMoreClicked' event (evid: 376)
 *
 * Event description: Click on learn more in the collection table in the monitoring dashboard CMS tab
 *
 * @param {collectionDetailsTableLearnMoreClickedParams} params - The parameters for this event
 * @property {string} site_id - site_id
 * @property {number} ts - ts
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} version - version
 * @property {string} environment - {business_manager}
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {GUID} esi - {}
 * @property {GUID} msid - msid
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<collectionDetailsTableLearnMoreClickedParams>} LogObject<collectionDetailsTableLearnMoreClickedParams>
 */
export function collectionDetailsTableLearnMoreClicked(params) {
    return { evid: 376, src: 83, endpoint: '', params };
}
/**
 * A function to create a log object for the 'cmsManualSortApplied' event (evid: 378)
 *
 * Event description: CMS Manual Sort Applied
 *
 * @param {cmsManualSortAppliedParams} params - The parameters for this event
 * @property {string} site_id - site_id
 * @property {string} view_name - Default / Name of the view
 * @property {number} ts - ts
 * @property {string} collection_id - collection id
 * @property {boolean} is_draft_publish_enabled - (true, false)
 * @property {string} viewmode - viewmode
 * @property {number} number_of_rows - number_of_rows
 * @property {string} input_name - input_name
 * @property {boolean} order_used_on_site - true / false
 * @property {string} item_draft_status - item_draft status
 * @property {boolean} is_shared - is shared
 * @property {string} version - version
 * @property {boolean} is_live_data - {Live=true, Sandbox=false}
 * @property {number} old_row_number - old_row_number
 * @property {string} environment - (Editor/Dashboard/one_app)
 * @property {boolean} is_sandbox_enabled - (true/false)
 * @property {string} view_layout - table / gallery / list
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {number} time_to_enable_custom_sort - time_to_enable_custom_sort
 * @property {GUID} esi - esi
 * @property {string} collection_name - It's a collection ID in One App.
 * @property {GUID} msid - msid
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<cmsManualSortAppliedParams>} LogObject<cmsManualSortAppliedParams>
 */
export function cmsManualSortApplied(params) {
    return { evid: 378, src: 83, endpoint: '', params };
}
/**
 * A function to create a log object for the 'cmsSortingModalInteraction' event (evid: 379)
 *
 * Event description: CMS Sorting Modal Interaction
 *
 * @param {cmsSortingModalInteractionParams} params - The parameters for this event
 * @property {string} site_id - site_id
 * @property {string} view_name - Default / Name of the view
 * @property {number} ts - ts
 * @property {string} collection_id - collection id
 * @property {number} connections_affected - 1,2,3
 * @property {boolean} is_draft_publish_enabled - (true, false)
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} item_draft_status - item_draft status
 * @property {boolean} is_shared - is shared
 * @property {string} version - version
 * @property {boolean} is_live_data - {Live=true, Sandbox=false}
 * @property {string} origin - Use_order_on_site / Replace_order / Apply_custom_sort
 * @property {string} environment - (Editor/Dashboard/one_app)
 * @property {boolean} is_sandbox_enabled - (true/false)
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {GUID} esi - esi
 * @property {string} collection_name - It's a collection ID in One App.
 * @property {GUID} msid - msid
 * @property {string} action - cancel / close / apply / learn_more / replace / continue
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<cmsSortingModalInteractionParams>} LogObject<cmsSortingModalInteractionParams>
 */
export function cmsSortingModalInteraction(params) {
    return { evid: 379, src: 83, endpoint: '', params };
}
/**
 * A function to create a log object for the 'cmsUseOrderOnSiteToggleChange' event (evid: 380)
 *
 * Event description: CMS Use Order on Site Toggle Change
 *
 * @param {cmsUseOrderOnSiteToggleChangeParams} params - The parameters for this event
 * @property {string} site_id - site_id
 * @property {string} view_name - Default / Name of the view
 * @property {number} ts - ts
 * @property {string} collection_id - collection id
 * @property {boolean} is_draft_publish_enabled - (true, false)
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} item_draft_status - item_draft status
 * @property {boolean} is_shared - is shared
 * @property {string} version - version
 * @property {boolean} is_live_data - {Live=true, Sandbox=false}
 * @property {string} environment - (Editor/Dashboard/one_app)
 * @property {boolean} is_sandbox_enabled - (true/false)
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {GUID} esi - esi
 * @property {string} collection_name - It's a collection ID in One App.
 * @property {GUID} msid - msid
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<cmsUseOrderOnSiteToggleChangeParams>} LogObject<cmsUseOrderOnSiteToggleChangeParams>
 */
export function cmsUseOrderOnSiteToggleChange(params) {
    return { evid: 380, src: 83, endpoint: '', params };
}
/**
 * A function to create a log object for the 'cmsCollectionSortAppliedOnSite' event (evid: 381)
 *
 * Event description: CMS Collection Sort Applied on Site
 *
 * @param {cmsCollectionSortAppliedOnSiteParams} params - The parameters for this event
 * @property {string} site_id - site_id
 * @property {number} old_view_name - old view that was applied
 * @property {string} view_name - Default / Name of the view
 * @property {number} ts - ts
 * @property {string} collection_id - collection id
 * @property {boolean} is_draft_publish_enabled - (true, false)
 * @property {string} viewmode - viewmode
 * @property {number} number_of_rows - number_of_rows
 * @property {string} input_name - input_name
 * @property {string} item_draft_status - item_draft status
 * @property {boolean} is_shared - is shared
 * @property {string} version - version
 * @property {boolean} is_live_data - {Live=true, Sandbox=false}
 * @property {string} environment - (Editor/Dashboard/one_app)
 * @property {boolean} is_sandbox_enabled - (true/false)
 * @property {string} view_layout - table / gallery / list
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {GUID} esi - esi
 * @property {string} collection_name - It's a collection ID in One App.
 * @property {GUID} msid - msid
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<cmsCollectionSortAppliedOnSiteParams>} LogObject<cmsCollectionSortAppliedOnSiteParams>
 */
export function cmsCollectionSortAppliedOnSite(params) {
    return { evid: 381, src: 83, endpoint: '', params };
}
/**
 * A function to create a log object for the 'cmsColorFieldInteraction' event (evid: 382)
 *
 * Event description: CMS - Color Field Interaction
 *
 * @param {cmsColorFieldInteractionParams} params - The parameters for this event
 * @property {string} site_id - site_id
 * @property {number} ts - ts
 * @property {string} collection_id - collection_id
 * @property {boolean} is_draft_publish_enabled - (true, false)
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} item_draft_status - (draft, published, scheduled_to_publish, scheduled_to_draft)
 * @property {boolean} is_shared - is_shared
 * @property {string} version - version
 * @property {boolean} is_live_data - {Live=true, Sandbox=false}
 * @property {string} origin - add_field, edit_field, add_content, edit_content
 * @property {string} environment - (Editor/Dashboard/one_app)
 * @property {boolean} is_sandbox_enabled - (true/false)
 * @property {string} userEnv - user_env
 * @property {string} selected_value - selected_value (color)
 * @property {string} editor_working_mode - editor_working_mode
 * @property {string} type - theme_colors, my_colors, user_input
 * @property {GUID} esi - esi
 * @property {boolean} is_single_item_collection - is single item collection
 * @property {string} collection_name - collection name
 * @property {GUID} msid - msid
 * @property {string} field_name - field_name
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<cmsColorFieldInteractionParams>} LogObject<cmsColorFieldInteractionParams>
 */
export function cmsColorFieldInteraction(params) {
    return { evid: 382, src: 83, endpoint: '', params };
}
/**
 * A function to create a log object for the 'cmsRefreshButtonInteraction' event (evid: 383)
 *
 * Event description: CMS Refresh Button Interaction
 *
 * @param {cmsRefreshButtonInteractionParams} params - The parameters for this event
 * @property {string} site_id - site_id
 * @property {string} view_name - Default / Name of the view
 * @property {number} ts - ts
 * @property {string} collection_id - collection id
 * @property {boolean} is_draft_publish_enabled - (true, false)
 * @property {string} viewmode - viewmode
 * @property {number} number_of_rows - number_of_rows
 * @property {string} input_name - input_name
 * @property {string} item_draft_status - item_draft status
 * @property {boolean} is_shared - is shared
 * @property {string} version - version
 * @property {boolean} is_live_data - {Live=true, Sandbox=false}
 * @property {string} reason_shown - item_added, item_updated
 * @property {string} environment - (Editor/Dashboard/one_app)
 * @property {boolean} is_sandbox_enabled - (true/false)
 * @property {string} view_layout - table / gallery / list
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {GUID} esi - esi
 * @property {string} collection_name - It's a collection ID in One App.
 * @property {GUID} msid - msid
 * @property {string} action - show, click
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<cmsRefreshButtonInteractionParams>} LogObject<cmsRefreshButtonInteractionParams>
 */
export function cmsRefreshButtonInteraction(params) {
    return { evid: 383, src: 83, endpoint: '', params };
}
/**
 * A function to create a log object for the 'cmUrlSlugFieldEditing' event (evid: 384)
 *
 * Event description: CM URL Slug Field Editing
 *
 * @param {cmUrlSlugFieldEditingParams} params - The parameters for this event
 * @property {string} renderingFlow - Rendering flow
 * @property {string} site_id - site_id
 * @property {number} ts - ts
 * @property {string} collection_id - collection id
 * @property {string} view_type - table / list / gallery etc.
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} field_id - field id
 * @property {string} version - version
 * @property {string} origin - cell
 * @property {string} environment - environment
 * @property {boolean} isPremiumSite - is site premium
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {GUID} esi - esi
 * @property {string} collection_name - collection name
 * @property {GUID} msid - msid
 * @property {string} action - hover_info / save / cancel
 * @property {boolean} is_published - is site published
 * @property {string} old_url - old_url
 * @property {string} new_url - new_url
 * @return {LogObject<cmUrlSlugFieldEditingParams>} LogObject<cmUrlSlugFieldEditingParams>
 */
export function cmUrlSlugFieldEditing(params) {
    return { evid: 384, src: 83, endpoint: '', params };
}
/**
 * A function to create a log object for the 'cmUrlSlugFieldEditingMessage' event (evid: 385)
 *
 * Event description: CM URL Slug Field Editing Message
 *
 * @param {cmUrlSlugFieldEditingMessageParams} params - The parameters for this event
 * @property {string} site_id - site_id
 * @property {number} ts - ts
 * @property {string} collection_id - collection id
 * @property {string} view_type - table / list / gallery etc.
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} version - version
 * @property {string} origin - cell
 * @property {string} environment - environment
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {GUID} esi - esi
 * @property {string} collection_name - collection name
 * @property {GUID} msid - msid
 * @property {string} action - show / close / click_connect
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<cmUrlSlugFieldEditingMessageParams>} LogObject<cmUrlSlugFieldEditingMessageParams>
 */
export function cmUrlSlugFieldEditingMessage(params) {
    return { evid: 385, src: 83, endpoint: '', params };
}
/**
 * A function to create a log object for the 'cmUrlAutomaticRedirectToggleValue' event (evid: 386)
 *
 * Event description: CM URL Automatic Redirect Toggle Value
 *
 * @param {cmUrlAutomaticRedirectToggleValueParams} params - The parameters for this event
 * @property {string} site_id - site_id
 * @property {number} ts - ts
 * @property {string} collection_id - collection id
 * @property {string} view_type - table / list / gallery etc.
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} version - version
 * @property {string} origin - cell
 * @property {string} environment - environment
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {GUID} esi - esi
 * @property {string} collection_name - collection name
 * @property {GUID} msid - msid
 * @property {string} value - on / off
 * @property {string} action - show / change
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<cmUrlAutomaticRedirectToggleValueParams>} LogObject<cmUrlAutomaticRedirectToggleValueParams>
 */
export function cmUrlAutomaticRedirectToggleValue(params) {
    return { evid: 386, src: 83, endpoint: '', params };
}
/**
 * A function to create a log object for the 'cmUrlSlugErrorMessage' event (evid: 387)
 *
 * Event description: CM URL Slug Error Message
 *
 * @param {cmUrlSlugErrorMessageParams} params - The parameters for this event
 * @property {string} site_id - site_id
 * @property {number} ts - ts
 * @property {string} collection_id - collection id
 * @property {string} view_type - table / list / gallery etc.
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} version - version
 * @property {string} error_type - error_type
 * @property {string} origin - cell / pages_panel
 * @property {string} environment - environment
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {GUID} esi - esi
 * @property {string} collection_name - collection name
 * @property {GUID} msid - msid
 * @property {string} action - show / change
 * @property {string} conflictType - alert for conflicts that require a fix, standard (FYI only)
 * @property {string} componentType - info_icon / notification_banner / info_icon_tooltip
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<cmUrlSlugErrorMessageParams>} LogObject<cmUrlSlugErrorMessageParams>
 */
export function cmUrlSlugErrorMessage(params) {
    return { evid: 387, src: 83, endpoint: '', params };
}
/**
 * A function to create a log object for the 'cmsVisibilityBannerEligiblePopulation' event (evid: 388)
 *
 * Event description: trigger every time the user opens a page in the product where a visibility banner should show
 *
 * @param {cmsVisibilityBannerEligiblePopulationParams} params - The parameters for this event
 * @property {number} num_of_collections - number of collections user has
 * @property {number} items_quota_status - number of items site has/ limit of items available to the site
 * @property {string} site_id - site_id
 * @property {number} collections_quota_status - number of collections site has/ limit of collections available to the site
 * @property {number} ts - ts
 * @property {number} items_limit - limit of items available to user
 * @property {number} collections_limit - limit of collections available to user
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} version - version
 * @property {string} bannerType - ok/ exceeding/ approaching
 * @property {string} premium_plan - premium_plan
 * @property {string} origin - bo_cm, bo_home, editor_cm, collection, monitoring_dashboard
 * @property {number} collections_size - in GB
 * @property {string} environment - (EDITOR, BUSINESS_MANAGER)
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {number} num_of_items - number of items user has
 * @property {boolean} isPremium - is user premium
 * @property {GUID} esi - esi
 * @property {GUID} msid - msid
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<cmsVisibilityBannerEligiblePopulationParams>} LogObject<cmsVisibilityBannerEligiblePopulationParams>
 */
export function cmsVisibilityBannerEligiblePopulation(params) {
    return { evid: 388, src: 83, endpoint: '', params };
}
/**
 * A function to create a log object for the 'cmPermissionsDialogClick' event (evid: 4)
 *
 * Event description: when a user clicks inside the Permissions settings dialog
 *
 * @param {cmPermissionsDialogClickParams} params - The parameters for this event
 * @property {string} collection_id - collection_id
 * @property {string} collection_name - collection_name
 * @property {string} clickType - main / permissions and privacy/ cancel / learn more
 * @property {string} environment - EDITOR / BUSINESS_MANAGER
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<cmPermissionsDialogClickParams>} LogObject<cmPermissionsDialogClickParams>
 */
export function cmPermissionsDialogClick(params) {
    return { evid: 4, src: 83, endpoint: '', params };
}
/**
 * A function to create a log object for the 'userCollectionSizeChanged' event (evid: 400)
 *
 * Event description: Emitted when the size of a users collection changes. since March 29th 2021 the Kafka stopped sending this event, instead of it we get the data from the production table
 *
 * @param {userCollectionSizeChangedParams} params - The parameters for this event
 * @property {GUID} _event_hash - event_hash
 * @property {GUID} trace_id - The trace ID of the batch
 * @property {GUID} visitor_id - visitor_id
 * @property {number} sizeOnDisk - The size of user data on disk
 * @property {string} dc - The Data Center from which the event originated
 * @property {number} totalIndexSize - The size of user indexes
 * @property {string} service - The origin service
 * @property {string} clusterId - The id of the cluster where user data resides
 * @property {string} mongoDatabaseName - The name of the mongo database where user collections are stored.
 * @property {string} collectionName - The name of the collection (as provided by user)
 * @property {string} databaseName - The name of the database (usually public or dev)
 * @property {GUID} appId - The ID of the owning app
 * @property {GUID} traceId - The trace ID of the batch
 * @property {number} dataSize - The size of user data
 * @property {GUID} msid - msid
 * @property {number} itemCount - Number of documents in user collection
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<userCollectionSizeChangedParams>} LogObject<userCollectionSizeChangedParams>
 */
export function userCollectionSizeChanged(params) {
    return { evid: 400, src: 83, endpoint: '', params };
}
/**
 * A function to create a log object for the 'newIndexCreated' event (evid: 401)
 *
 * Event description: Creating new index
 *
 * @param {newIndexCreatedParams} params - The parameters for this event
 * @property {string} index_fields - List of fields selected to be indexed in the same order as selected by the user and the direction of indexing (asc/desc). Can have up to 3 fields.
 * @property {string} errorMessage - null if operation was success
 * @property {GUID} uuid - uuid
 * @property {string} collectionId - collection_Id
 * @property {string} indexType - Regular/Unique
 * @property {boolean} isSuccess - true if operation was success
 * @property {string} indexFields - List of fields selected to be indexed in the same order as selected by the user and the direction of indexing (asc/desc). Can have up to 3 fields.
 * @property {string} indexName - index_name
 * @property {string} collection_name - the collection name
 * @property {GUID} msid - msid
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<newIndexCreatedParams>} LogObject<newIndexCreatedParams>
 */
export function newIndexCreated(params) {
    return { evid: 401, src: 83, endpoint: '', params };
}
/**
 * A function to create a log object for the 'deleteIndex' event (evid: 402)
 *
 * Event description: Deleting existing index
 *
 * @param {deleteIndexParams} params - The parameters for this event
 * @property {string} index_name - index_name
 * @property {string} index_fields - List of fields selected to be indexed in the same order as selected by the user and the direction of indexing (asc/desc). Can have up to 3 fields.
 * @property {string} errorMessage - null if operation was success
 * @property {GUID} uuid - uuid
 * @property {string} collectionId - collection_id
 * @property {string} indexType - Regular/Unique
 * @property {boolean} isSuccess - true if operation was success
 * @property {string} indexFields - List of fields selected to be indexed in the same order as selected by the user and the direction of indexing (asc/desc). Can have up to 3 fields.
 * @property {string} indexName - index_name
 * @property {string} collection_name - the collection name
 * @property {GUID} msid - msid
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<deleteIndexParams>} LogObject<deleteIndexParams>
 */
export function deleteIndex(params) {
    return { evid: 402, src: 83, endpoint: '', params };
}
/**
 * A function to create a log object for the 'wixDataChangeGreyhoundMessage' event (evid: 403)
 *
 * Event description: fiers on any changes in wix-data while a greyhound message is sent to the editor team to reindex site pages
 *
 * @param {wixDataChangeGreyhoundMessageParams} params - The parameters for this event
 * @property {GUID} uuid - uuid
 * @property {string} pageType - static/ dynamic
 * @property {GUID} msid - msid
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<wixDataChangeGreyhoundMessageParams>} LogObject<wixDataChangeGreyhoundMessageParams>
 */
export function wixDataChangeGreyhoundMessage(params) {
    return { evid: 403, src: 83, endpoint: '', params };
}
/**
 * A function to create a log object for the 'cmsClickManageTags' event (evid: 404)
 *
 * Event description: CMS - Click Manage Tags
 *
 * @param {cmsClickManageTagsParams} params - The parameters for this event
 * @property {string} site_id - site_id
 * @property {number} ts - ts
 * @property {string} collection_id - collection_id
 * @property {boolean} is_draft_publish_enabled - is_draft_publish_enabled
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} item_draft_status - item_draft_status
 * @property {boolean} is_shared - is_shared
 * @property {string} version - version
 * @property {boolean} is_live_data - is_live_data
 * @property {string} origin - origin
 * @property {string} environment - environment
 * @property {boolean} is_sandbox_enabled - is_sandbox_enabled
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {GUID} esi - esi
 * @property {boolean} is_single_item_collection - is_single_item_collection
 * @property {string} collection_name - collection_name
 * @property {GUID} msid - msid
 * @property {string} field_name - field_name
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<cmsClickManageTagsParams>} LogObject<cmsClickManageTagsParams>
 */
export function cmsClickManageTags(params) {
    return { evid: 404, src: 83, endpoint: '', params };
}
/**
 * A function to create a log object for the 'cmsManageTagsPanelActions' event (evid: 405)
 *
 * Event description: CMS - Manage Tags Panel Actions
 *
 * @param {cmsManageTagsPanelActionsParams} params - The parameters for this event
 * @property {string} site_id - site_id
 * @property {number} ts - ts
 * @property {string} collection_id - collection_id
 * @property {boolean} is_draft_publish_enabled - is_draft_publish_enabled
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} item_draft_status - item_draft_status
 * @property {boolean} is_shared - is_shared
 * @property {string} version - version
 * @property {boolean} is_live_data - is_live_data
 * @property {string} origin - origin
 * @property {string} environment - environment
 * @property {boolean} is_sandbox_enabled - is_sandbox_enabled
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {GUID} esi - esi
 * @property {boolean} is_single_item_collection - is_single_item_collection
 * @property {string} collection_name - collection_name
 * @property {GUID} msid - msid
 * @property {string} field_name - field_name
 * @property {string} action - click_search, 3dot_menu, close_panel
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<cmsManageTagsPanelActionsParams>} LogObject<cmsManageTagsPanelActionsParams>
 */
export function cmsManageTagsPanelActions(params) {
    return { evid: 405, src: 83, endpoint: '', params };
}
/**
 * A function to create a log object for the 'cmsActionsWithTags' event (evid: 406)
 *
 * Event description: CMS - Actions with Tags
 *
 * @param {cmsActionsWithTagsParams} params - The parameters for this event
 * @property {string} site_id - site_id
 * @property {number} ts - ts
 * @property {string} collection_id - collection_id
 * @property {boolean} is_draft_publish_enabled - is_draft_publish_enabled
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} item_draft_status - item_draft_status
 * @property {boolean} is_shared - is_shared
 * @property {string} version - version
 * @property {boolean} is_live_data - is_live_data
 * @property {string} origin - origin
 * @property {string} environment - environment
 * @property {boolean} is_sandbox_enabled - is_sandbox_enabled
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {GUID} esi - esi
 * @property {boolean} is_single_item_collection - is_single_item_collection
 * @property {string} collection_name - collection_name
 * @property {GUID} msid - msid
 * @property {string} field_name - field_name
 * @property {string} action - merge, delete, rename
 * @property {string} tag - tag
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<cmsActionsWithTagsParams>} LogObject<cmsActionsWithTagsParams>
 */
export function cmsActionsWithTags(params) {
    return { evid: 406, src: 83, endpoint: '', params };
}
/**
 * A function to create a log object for the 'cmsTagsMergeActions' event (evid: 407)
 *
 * Event description: CMS - Tags Merge Actions
 *
 * @param {cmsTagsMergeActionsParams} params - The parameters for this event
 * @property {string} site_id - site_id
 * @property {number} ts - ts
 * @property {string} collection_id - collection_id
 * @property {boolean} is_draft_publish_enabled - is_draft_publish_enabled
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} item_draft_status - item_draft_status
 * @property {boolean} is_shared - is_shared
 * @property {string} version - version
 * @property {boolean} is_live_data - is_live_data
 * @property {string} origin - 3dot_menu, rename etc.
 * @property {string} environment - environment
 * @property {boolean} is_sandbox_enabled - is_sandbox_enabled
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {GUID} esi - esi
 * @property {boolean} is_single_item_collection - is_single_item_collection
 * @property {string} collection_name - collection_name
 * @property {GUID} msid - msid
 * @property {string} field_name - field_name
 * @property {string} action - merge, undo
 * @property {string} tags - tags
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<cmsTagsMergeActionsParams>} LogObject<cmsTagsMergeActionsParams>
 */
export function cmsTagsMergeActions(params) {
    return { evid: 407, src: 83, endpoint: '', params };
}
/**
 * A function to create a log object for the 'cmsTagsRenameActions' event (evid: 408)
 *
 * Event description: CMS - Tags Rename Actions
 *
 * @param {cmsTagsRenameActionsParams} params - The parameters for this event
 * @property {string} site_id - site_id
 * @property {number} ts - ts
 * @property {string} collection_id - collection_id
 * @property {boolean} is_draft_publish_enabled - is_draft_publish_enabled
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} item_draft_status - item_draft_status
 * @property {boolean} is_shared - is_shared
 * @property {string} version - version
 * @property {boolean} is_live_data - is_live_data
 * @property {string} origin - cell
 * @property {string} environment - environment
 * @property {boolean} is_sandbox_enabled - is_sandbox_enabled
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {GUID} esi - esi
 * @property {boolean} is_single_item_collection - is_single_item_collection
 * @property {string} collection_name - collection_name
 * @property {GUID} msid - msid
 * @property {string} field_name - field_name
 * @property {string} action - merge, undo
 * @property {string} old_tag - old tag
 * @property {string} new_tag - new tag
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<cmsTagsRenameActionsParams>} LogObject<cmsTagsRenameActionsParams>
 */
export function cmsTagsRenameActions(params) {
    return { evid: 408, src: 83, endpoint: '', params };
}
/**
 * A function to create a log object for the 'cmsTagsErrors' event (evid: 409)
 *
 * Event description: CMS - Tags Errors
 *
 * @param {cmsTagsErrorsParams} params - The parameters for this event
 * @property {string} site_id - site_id
 * @property {number} ts - ts
 * @property {string} collection_id - collection_id
 * @property {boolean} is_draft_publish_enabled - is_draft_publish_enabled
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} item_draft_status - item_draft_status
 * @property {boolean} is_shared - is_shared
 * @property {string} version - version
 * @property {boolean} is_live_data - is_live_data
 * @property {string} environment - environment
 * @property {boolean} is_sandbox_enabled - is_sandbox_enabled
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {GUID} esi - esi
 * @property {boolean} is_single_item_collection - is_single_item_collection
 * @property {string} collection_name - collection_name
 * @property {GUID} msid - msid
 * @property {string} error_type - search_no_results, cannot_load
 * @property {string} action - show, retry, close
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<cmsTagsErrorsParams>} LogObject<cmsTagsErrorsParams>
 */
export function cmsTagsErrors(params) {
    return { evid: 409, src: 83, endpoint: '', params };
}
/**
 * A function to create a log object for the 'cmsViewPageConnections' event (evid: 410)
 *
 * Event description: CMS - View Page Connections
 *
 * @param {cmsViewPageConnectionsParams} params - The parameters for this event
 * @property {string} site_id - site_id
 * @property {number} ts - ts
 * @property {string} collection_id - collection_id
 * @property {boolean} is_draft_publish_enabled - is_draft_publish_enabled
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} item_draft_status - item_draft_status
 * @property {boolean} is_shared - is_shared
 * @property {string} version - version
 * @property {boolean} is_live_data - is_live_data
 * @property {string} environment - environment
 * @property {boolean} is_sandbox_enabled - is_sandbox_enabled
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {GUID} esi - esi
 * @property {boolean} is_single_item_collection - is_single_item_collection
 * @property {string} collection_name - collection_name
 * @property {GUID} msid - msid
 * @property {string} origin - more_actions
 * @property {number} number_of_items_in_collection - number_of_items_in_collection
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<cmsViewPageConnectionsParams>} LogObject<cmsViewPageConnectionsParams>
 */
export function cmsViewPageConnections(params) {
    return { evid: 410, src: 83, endpoint: '', params };
}
/**
 * A function to create a log object for the 'cmsPageConnectionsEmptyState' event (evid: 411)
 *
 * Event description: CMS - Page Connections Empty State
 *
 * @param {cmsPageConnectionsEmptyStateParams} params - The parameters for this event
 * @property {string} site_id - site_id
 * @property {number} ts - ts
 * @property {string} collection_id - collection_id
 * @property {boolean} is_draft_publish_enabled - is_draft_publish_enabled
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} item_draft_status - item_draft_status
 * @property {boolean} is_shared - is_shared
 * @property {string} version - version
 * @property {boolean} is_live_data - is_live_data
 * @property {string} environment - environment
 * @property {boolean} is_sandbox_enabled - is_sandbox_enabled
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {GUID} esi - esi
 * @property {boolean} is_single_item_collection - is_single_item_collection
 * @property {string} collection_name - collection_name
 * @property {GUID} msid - msid
 * @property {string} origin - more_actions
 * @property {number} number_of_items_in_collection - number_of_items_in_collection
 * @property {string} type - no_content, no_connections
 * @property {string} action - close, back_to_collection, connect
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<cmsPageConnectionsEmptyStateParams>} LogObject<cmsPageConnectionsEmptyStateParams>
 */
export function cmsPageConnectionsEmptyState(params) {
    return { evid: 411, src: 83, endpoint: '', params };
}
/**
 * A function to create a log object for the 'cmsPageConnections' event (evid: 412)
 *
 * Event description: CMS - Page Connections
 *
 * @param {cmsPageConnectionsParams} params - The parameters for this event
 * @property {string} site_id - site_id
 * @property {number} ts - ts
 * @property {string} collection_id - collection_id
 * @property {boolean} is_draft_publish_enabled - is_draft_publish_enabled
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} item_draft_status - item_draft_status
 * @property {boolean} is_shared - is_shared
 * @property {string} version - version
 * @property {boolean} is_live_data - is_live_data
 * @property {string} environment - environment
 * @property {boolean} is_sandbox_enabled - is_sandbox_enabled
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {GUID} esi - esi
 * @property {boolean} is_single_item_collection - is_single_item_collection
 * @property {string} collection_name - collection_name
 * @property {GUID} msid - msid
 * @property {string} origin - more_actions
 * @property {number} number_of_items_in_collection - number_of_items_in_collection
 * @property {string} action - close, back_to_collection, connect
 * @property {number} total_connections - total_connections
 * @property {string} static_connections - static_connections
 * @property {string} dynamic_connections - dynamic_connections
 * @property {number} static_connections_num - static_connections_num
 * @property {string} dynamic_connections_num - dynamic_connections_num
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<cmsPageConnectionsParams>} LogObject<cmsPageConnectionsParams>
 */
export function cmsPageConnections(params) {
    return { evid: 412, src: 83, endpoint: '', params };
}
/**
 * A function to create a log object for the 'cmsPageConnectionsError' event (evid: 413)
 *
 * Event description: CMS - Page Connections Error
 *
 * @param {cmsPageConnectionsErrorParams} params - The parameters for this event
 * @property {string} site_id - site_id
 * @property {number} ts - ts
 * @property {string} collection_id - collection_id
 * @property {boolean} is_draft_publish_enabled - is_draft_publish_enabled
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} item_draft_status - item_draft_status
 * @property {boolean} is_shared - is_shared
 * @property {string} version - version
 * @property {boolean} is_live_data - is_live_data
 * @property {string} environment - environment
 * @property {boolean} is_sandbox_enabled - is_sandbox_enabled
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {GUID} esi - esi
 * @property {boolean} is_single_item_collection - is_single_item_collection
 * @property {string} collection_name - collection_name
 * @property {GUID} msid - msid
 * @property {string} origin - more_actions
 * @property {string} action - show, retry, close
 * @property {string} error_type - loading
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<cmsPageConnectionsErrorParams>} LogObject<cmsPageConnectionsErrorParams>
 */
export function cmsPageConnectionsError(params) {
    return { evid: 413, src: 83, endpoint: '', params };
}
/**
 * A function to create a log object for the 'cmsPageConnectionsSrc83Evid414' event (evid: 414)
 *
 * Event description: CMS - Page Connections
 *
 * @param {cmsPageConnectionsSrc83Evid414Params} params - The parameters for this event
 * @property {string} renderingFlow - Rendering flow
 * @property {string} site_id - site_id
 * @property {number} ts - ts
 * @property {string} collection_id - collection_id
 * @property {boolean} is_draft_publish_enabled - is_draft_publish_enabled
 * @property {string} viewmode - viewmode
 * @property {string} input_name - input_name
 * @property {string} item_draft_status - item_draft_status
 * @property {boolean} is_shared - is_shared
 * @property {string} version - version
 * @property {boolean} is_live_data - is_live_data
 * @property {string} environment - environment
 * @property {boolean} is_sandbox_enabled - is_sandbox_enabled
 * @property {string} userEnv - user_env
 * @property {string} editor_working_mode - editor_working_mode
 * @property {GUID} esi - esi
 * @property {boolean} is_single_item_collection - is_single_item_collection
 * @property {string} collection_name - collection_name
 * @property {GUID} msid - msid
 * @property {string} origin - more_actions
 * @property {number} number_of_items_in_collection - number_of_items_in_collection
 * @property {string} action - close, back_to_collection, connect
 * @property {number} num_total_connections - total_connections
 * @property {number} num_static_connections - static_connections
 * @property {number} num_dynamic_connections - dynamic_connections
 * @property {string} static_pages_names - json array of connected static pages names
 * @property {string} dynamic_pages_names - json array of connected dynamic pages names
 * @return {LogObject<cmsPageConnectionsSrc83Evid414Params>} LogObject<cmsPageConnectionsSrc83Evid414Params>
 */
export function cmsPageConnectionsSrc83Evid414(params) {
    return { evid: 414, src: 83, endpoint: '', params };
}
/**
 * A function to create a log object for the 'cmsCollectionRenamingErrorSrc83Evid415' event (evid: 415)
 *
 * Event description: CMS - Collection Renaming Error
 *
 * @param {cmsCollectionRenamingErrorSrc83Evid415Params} params - The parameters for this event
 * @property {string} collection_id - collection_id
 * @property {string} collection_name - collection_name
 * @property {string} origin - origin
 * @property {string} error_type - error_type
 * @property {string} environment - environment
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<cmsCollectionRenamingErrorSrc83Evid415Params>} LogObject<cmsCollectionRenamingErrorSrc83Evid415Params>
 */
export function cmsCollectionRenamingErrorSrc83Evid415(params) {
    return { evid: 415, src: 83, endpoint: '', params };
}
/**
 * A function to create a log object for the 'cmsPermissionsConflictIsResolved' event (evid: 5)
 *
 * Event description: Fired when a permission conflict in a collection's permissions is resolved
 *
 * @param {cmsPermissionsConflictIsResolvedParams} params - The parameters for this event
 * @property {string} collection_id - collection_id
 * @property {string} collection_name - collection_name
 * @property {string} environment - environment
 * @property {string} origin - origin
 * @property {string} msid - msid
 * @property {string} conflictType - conflict_type
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<cmsPermissionsConflictIsResolvedParams>} LogObject<cmsPermissionsConflictIsResolvedParams>
 */
export function cmsPermissionsConflictIsResolved(params) {
    return { evid: 5, src: 83, endpoint: '', params };
}
/**
 * A function to create a log object for the 'cmsNotificationShownSrc83Evid6' event (evid: 6)
 *
 * Event description: CMS Notification shown
 *
 * @param {cmsNotificationShownSrc83Evid6Params} params - The parameters for this event
 * @property {GUID} site_id - siteID
 * @property {string} collection_id	 - collection_id
 * @property {string} collection_name - collection_name
 * @property {string} page_type - item_page, list_page, blank_page, edit_page
 * @property {string} notificationText - notification text
 * @property {string} environment - EDITOR, BUSINESS_MANAGER, one_app
 * @property {string} notification_type - notification_type
 * @property {string} builder_type - Builder type - editor / studio / blocks
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<cmsNotificationShownSrc83Evid6Params>} LogObject<cmsNotificationShownSrc83Evid6Params>
 */
export function cmsNotificationShownSrc83Evid6(params) {
    return { evid: 6, src: 83, endpoint: '', params };
}
/**
 * A function to create a log object for the 'cmExportFinished' event (evid: 600)
 *
 * Event description: CM Export Finished
 *
 * @param {cmExportFinishedParams} params - The parameters for this event
 * @property {string} export_type - (all, filtered, schema)
 * @property {string} collection_id - collection id
 * @property {GUID} view_id - view id
 * @property {boolean} is_live_data - {Live=true, Sandbox=false}
 * @property {string} environment - (pub/dev)
 * @property {boolean} is_sandbox_enabled - (true/false)
 * @property {string} collection_name - collection name
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<cmExportFinishedParams>} LogObject<cmExportFinishedParams>
 */
export function cmExportFinished(params) {
    return { evid: 600, src: 83, endpoint: '', params };
}
/**
 * A function to create a log object for the 'cmFieldHeaderMenuOpenedSrc83Evid7' event (evid: 7)
 *
 * Event description: CM - Field Header Menu Opened
 *
 * @param {cmFieldHeaderMenuOpenedSrc83Evid7Params} params - The parameters for this event
 * @property {string} field_name - name of field
 * @property {string} field_type - type of field
 * @property {boolean} is_live_data - (true/false)
 * @property {boolean} is_sandbox_enabled - (true/false)
 * @property {string} collection_id - collection_id
 * @property {string} collection_name - collection name
 * @property {string} environment	 - Editor / business_manager
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<cmFieldHeaderMenuOpenedSrc83Evid7Params>} LogObject<cmFieldHeaderMenuOpenedSrc83Evid7Params>
 */
export function cmFieldHeaderMenuOpenedSrc83Evid7(params) {
    return { evid: 7, src: 83, endpoint: '', params };
}
/**
 * A function to create a log object for the 'openCollectionDirectlyFromNowOnClickSrc83Evid8' event (evid: 8)
 *
 * Event description: Open collection / translation directly from now on checkbox is clicked
 *
 * @param {openCollectionDirectlyFromNowOnClickSrc83Evid8Params} params - The parameters for this event
 * @property {string} collection_id - collection id
 * @property {string} collection_name - collection name
 * @property {string} origin - origin
 * @property {string} editor_type - Studio / Classic / Blocks
 * @property {boolean} open_directly_flag - open directly flag
 * true - to open directly from now on
 * false - Not to open directly
 * @property {string} Panel_type - collection / translation
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<openCollectionDirectlyFromNowOnClickSrc83Evid8Params>} LogObject<openCollectionDirectlyFromNowOnClickSrc83Evid8Params>
 */
export function openCollectionDirectlyFromNowOnClickSrc83Evid8(params) {
    return { evid: 8, src: 83, endpoint: '', params };
}
/**
 * A function to create a log object for the 'sitePagesMenuInteractionsSrc83Evid9' event (evid: 9)
 *
 * Event description: When  a user clicks on different panels or options in site pages menu panels
 *
 * @param {sitePagesMenuInteractionsSrc83Evid9Params} params - The parameters for this event
 * @property {string} pageId - unique ID of the page the action performed on
 * @property {string} action - Description of the button the user clicked on
 * @property {string} collectionId - collection_id
 * @property {string} environment - Editor\ Studio
 * @property {string} pageType - list, item, blank
 * @property {string} urlSlug - URL Slug in SEO slug edit
 * @property {number} num_of_items - number of affected items
 * @property {string} affected_object - item\ page
 * @property {string} panel_name - Name of panel where the user clicked in
 * @property {string} renderingFlow - Rendering flow
 * @return {LogObject<sitePagesMenuInteractionsSrc83Evid9Params>} LogObject<sitePagesMenuInteractionsSrc83Evid9Params>
 */
export function sitePagesMenuInteractionsSrc83Evid9(params) {
    return { evid: 9, src: 83, endpoint: '', params };
}
/**
 * A function to create a log object for the 'durations' event (evid: 101)
 *
 * Event description: Duration of events in Elementory
 *
 * @param {durationsParams} params - The parameters for this event
 * @property {GUID} _event_hash - event_hash
 * @property {number} duration - Event duration
 * @property {GUID} session_id - session_id
 * @property {string} user_agent - user_agent
 * @property {string} action - Action label
 * @return {LogObject<durationsParams>} LogObject<durationsParams>
 */
export function durations(params) {
    return { evid: 101, src: 95, endpoint: '', params };
}
/**
 * A function to create a log object for the 'fetchingASecret' event (evid: 102)
 *
 * Event description: Fired when fetching a value through the wix-secrets-manager EDM
 *
 * @param {fetchingASecretParams} params - The parameters for this event
 * @property {GUID} _event_hash - event_hash
 * @property {number} duration - The duration it took to fetch the value
 * @property {number} responseCode - Response code from cloud-secrets-vault-server
 * @property {GUID} session_id - session_id
 * @property {string} user_agent - user_agent
 * @property {GUID} msid - metaSiteId
 * @return {LogObject<fetchingASecretParams>} LogObject<fetchingASecretParams>
 */
export function fetchingASecret(params) {
    return { evid: 102, src: 95, endpoint: '', params };
}
/**
 * A function to create a log object for the 'arraySortInUserCode' event (evid: 103)
 *
 * Event description: Monitor usage of Array.sort() in user-code in Elementory
 *
 * @param {arraySortInUserCodeParams} params - The parameters for this event
 * @property {GUID} _event_hash - event_hash
 * @property {GUID} session_id - session_id
 * @property {number} array_length - Length of sorted array
 * @property {GUID} uuid - userId
 * @property {string} user_agent - user_agent
 * @property {string} module_name - module name
 * @property {GUID} msid - metasite-id
 * @return {LogObject<arraySortInUserCodeParams>} LogObject<arraySortInUserCodeParams>
 */
export function arraySortInUserCode(params) {
    return { evid: 103, src: 95, endpoint: '', params };
}
/**
 * A function to create a log object for the 'secretCreated' event (evid: 300)
 *
 * Event description: for auditing purposes, per the requests by the security team. fired when secret created
 *
 * @param {secretCreatedParams} params - The parameters for this event
 * @property {GUID} instanceId - instance-id of the creating app
 * @property {GUID} secretId - Secret-id generated by secrets-vault
 * @property {GUID} appDefId - appDefId of the creating app
 * @property {string} secretName - secret-name provided by user
 * @property {GUID} msid - metsite-id
 * @return {LogObject<secretCreatedParams>} LogObject<secretCreatedParams>
 */
export function secretCreated(params) {
    return { evid: 300, src: 95, endpoint: '', params };
}
/**
 * A function to create a log object for the 'secretUpdated' event (evid: 301)
 *
 * Event description: for auditing purposes, per the requests by the security team. Fired when a secret is updated
 *
 * @param {secretUpdatedParams} params - The parameters for this event
 * @property {GUID} instanceId - instance-id of the updating app
 * @property {GUID} secretId - Secret-id
 * @property {GUID} appDefId - appDefId of the updating app
 * @property {string} newSecretName - New secret-name provided by user
 * @property {GUID} msid - metsite-id
 * @return {LogObject<secretUpdatedParams>} LogObject<secretUpdatedParams>
 */
export function secretUpdated(params) {
    return { evid: 301, src: 95, endpoint: '', params };
}
/**
 * A function to create a log object for the 'secretDeleted' event (evid: 302)
 *
 * Event description: for auditing purposes, per the requests by the security team. Fired when a secret is deleted
 *
 * @param {secretDeletedParams} params - The parameters for this event
 * @property {GUID} instanceId - instance-id of the deleting app
 * @property {GUID} secretId - Secret-id
 * @property {GUID} appDefId - appDefId of the deleting app
 * @property {string} secretName - Secret-name provided by user
 * @property {GUID} msid - metsite-id
 * @return {LogObject<secretDeletedParams>} LogObject<secretDeletedParams>
 */
export function secretDeleted(params) {
    return { evid: 302, src: 95, endpoint: '', params };
}
/**
 * A function to create a log object for the 'fallbackIsUsed' event (evid: 400)
 *
 * Event description: Fallback is used.
 *
 * @param {fallbackIsUsedParams} params - The parameters for this event
 * @return {LogObject<fallbackIsUsedParams>} LogObject<fallbackIsUsedParams>
 */
export function fallbackIsUsed(params) {
    return { evid: 400, src: 95, endpoint: '', params };
}
/**
 * A function to create a log object for the 'restrictedGridAppId' event (evid: 401)
 *
 * Event description: restricted gridApp id
 *
 * @param {restrictedGridAppIdParams} params - The parameters for this event
 * @property {GUID} requestGridAppId - The grid app id in request
 * @property {GUID} actualGridAppId - The resolved grid app id
 * @property {GUID} htmlSiteId - The html site id of the site
 * @property {GUID} tenantId - The tenant id of the grid app in request
 * @return {LogObject<restrictedGridAppIdParams>} LogObject<restrictedGridAppIdParams>
 */
export function restrictedGridAppId(params) {
    return { evid: 401, src: 95, endpoint: '', params };
}
/**
 * A function to create a log object for the 'dbFailure' event (evid: 402)
 *
 * Event description: db failed to respond
 *
 * @param {dbFailureParams} params - The parameters for this event
 * @return {LogObject<dbFailureParams>} LogObject<dbFailureParams>
 */
export function dbFailure(params) {
    return { evid: 402, src: 95, endpoint: '', params };
}
/**
 * A function to create a log object for the 'fallbackFailed' event (evid: 403)
 *
 * Event description: fallback failed to respond
 *
 * @param {fallbackFailedParams} params - The parameters for this event
 * @return {LogObject<fallbackFailedParams>} LogObject<fallbackFailedParams>
 */
export function fallbackFailed(params) {
    return { evid: 403, src: 95, endpoint: '', params };
}
/**
 * A function to create a log object for the 'gracePeriodGridAppIds' event (evid: 404)
 *
 * Event description: Calls to old grid app ids, which are still in grace period
 *
 * @param {gracePeriodGridAppIdsParams} params - The parameters for this event
 * @property {GUID} requestGridAppId - The grid app id in request
 * @property {GUID} htmlSiteId - The html site id of the site
 * @property {GUID} tenantId - The tenant id of the grid app in request
 * @return {LogObject<gracePeriodGridAppIdsParams>} LogObject<gracePeriodGridAppIdsParams>
 */
export function gracePeriodGridAppIds(params) {
    return { evid: 404, src: 95, endpoint: '', params };
}
/**
 * A function to create a log object for the 'searchResultsSettingsDisplayVisibilityChanged' event (evid: 10)
 *
 * Event description: Change visibility of specific vertical in search results
 *
 * @param {searchResultsSettingsDisplayVisibilityChangedParams} params - The parameters for this event
 * @property {boolean} visible - is category visible
 * @property {string} category - vertical name
 * @return {LogObject<searchResultsSettingsDisplayVisibilityChangedParams>} LogObject<searchResultsSettingsDisplayVisibilityChangedParams>
 */
export function searchResultsSettingsDisplayVisibilityChanged(params) {
    return { evid: 10, src: 99, endpoint: '', params };
}
/**
 * A function to create a log object for the 'dataFeedingOperation' event (evid: 101)
 *
 * Event description: Data feeding operation
 *
 * @param {dataFeedingOperationParams} params - The parameters for this event
 * @property {boolean} success - success
 * @property {GUID} visitorId - visitor id
 * @property {string} errorMessage - error message
 * @property {number} timeSpent - time spent
 * @property {GUID} appDefId - app def id
 * @property {number} docCount - doc count
 * @property {string} operation - operation
 * @property {string} condition - condition expression
 * @property {string} collectionName - collection name
 * @property {GUID} msid - msid
 * @return {LogObject<dataFeedingOperationParams>} LogObject<dataFeedingOperationParams>
 */
export function dataFeedingOperation(params) {
    return { evid: 101, src: 99, endpoint: '', params };
}
/**
 * A function to create a log object for the 'searchResultsSettingsToggleSearchbar' event (evid: 11)
 *
 * Event description: toggle between show or don't show the search bar.
 * from search results settings panel
 *
 * @param {searchResultsSettingsToggleSearchbarParams} params - The parameters for this event
 * @property {GUID} msid - msid
 * @property {boolean} toggleState - the state after toggle {true/false}
 * @return {LogObject<searchResultsSettingsToggleSearchbarParams>} LogObject<searchResultsSettingsToggleSearchbarParams>
 */
export function searchResultsSettingsToggleSearchbar(params) {
    return { evid: 11, src: 99, endpoint: '', params };
}
/**
 * A function to create a log object for the 'searchResultsSettingsDisplayOrderChanged' event (evid: 12)
 *
 * Event description: order of category tabs in search results
 *
 * @param {searchResultsSettingsDisplayOrderChangedParams} params - The parameters for this event
 * @property {string} newOrder - order after change
 * @property {string} oldOrder - order before change
 * @return {LogObject<searchResultsSettingsDisplayOrderChangedParams>} LogObject<searchResultsSettingsDisplayOrderChangedParams>
 */
export function searchResultsSettingsDisplayOrderChanged(params) {
    return { evid: 12, src: 99, endpoint: '', params };
}
/**
 * A function to create a log object for the 'searchResultsSettingsDisplayCategoryOverwriteToggle' event (evid: 13)
 *
 * Event description: Enable title overwriting for specific category
 *
 * @param {searchResultsSettingsDisplayCategoryOverwriteToggleParams} params - The parameters for this event
 * @property {boolean} isOverwritten - is overwrite enabled
 * @property {string} category - category
 * @return {LogObject<searchResultsSettingsDisplayCategoryOverwriteToggleParams>} LogObject<searchResultsSettingsDisplayCategoryOverwriteToggleParams>
 */
export function searchResultsSettingsDisplayCategoryOverwriteToggle(params) {
    return { evid: 13, src: 99, endpoint: '', params };
}
/**
 * A function to create a log object for the 'searchResultsSettingsDisplayCategoryLabelChanged' event (evid: 14)
 *
 * Event description: Custom title the category will be overwritten with
 *
 * @param {searchResultsSettingsDisplayCategoryLabelChangedParams} params - The parameters for this event
 * @property {string} categoryTitle - overwrite value
 * @property {string} category - selected category
 * @return {LogObject<searchResultsSettingsDisplayCategoryLabelChangedParams>} LogObject<searchResultsSettingsDisplayCategoryLabelChangedParams>
 */
export function searchResultsSettingsDisplayCategoryLabelChanged(params) {
    return { evid: 14, src: 99, endpoint: '', params };
}
/**
 * A function to create a log object for the 'searchResultsSettingsDisplayAdd2CartToggle' event (evid: 17)
 *
 * Event description: Used toggled "Add to Cart" button visibility
 *
 * @param {searchResultsSettingsDisplayAdd2CartToggleParams} params - The parameters for this event
 * @property {boolean} status - true/false
 * @return {LogObject<searchResultsSettingsDisplayAdd2CartToggleParams>} LogObject<searchResultsSettingsDisplayAdd2CartToggleParams>
 */
export function searchResultsSettingsDisplayAdd2CartToggle(params) {
    return { evid: 17, src: 99, endpoint: '', params };
}
/**
 * A function to create a log object for the 'searchResultsSettingsDisplayAdd2CartLabel' event (evid: 18)
 *
 * Event description: Tracks the new "Add to Cart" button label a user decided to have
 *
 * @param {searchResultsSettingsDisplayAdd2CartLabelParams} params - The parameters for this event
 * @property {string} text - New text for "Add to Cart" button
 * @return {LogObject<searchResultsSettingsDisplayAdd2CartLabelParams>} LogObject<searchResultsSettingsDisplayAdd2CartLabelParams>
 */
export function searchResultsSettingsDisplayAdd2CartLabel(params) {
    return { evid: 18, src: 99, endpoint: '', params };
}
/**
 * A function to create a log object for the 'searchResultsSettingsDisplayProductToggle' event (evid: 19)
 *
 * Event description: Used toggled "Product" button visibility
 *
 * @param {searchResultsSettingsDisplayProductToggleParams} params - The parameters for this event
 * @property {boolean} status - true/false
 * @return {LogObject<searchResultsSettingsDisplayProductToggleParams>} LogObject<searchResultsSettingsDisplayProductToggleParams>
 */
export function searchResultsSettingsDisplayProductToggle(params) {
    return { evid: 19, src: 99, endpoint: '', params };
}
/**
 * A function to create a log object for the 'tabClick' event (evid: 2)
 *
 * Event description: Click on any tab in Settings section
 *
 * @param {tabClickParams} params - The parameters for this event
 * @property {string} tabName - main | sections | settings | layout | design | support | customize_results
 * @return {LogObject<tabClickParams>} LogObject<tabClickParams>
 */
export function tabClick(params) {
    return { evid: 2, src: 99, endpoint: '', params };
}
/**
 * A function to create a log object for the 'search' event (evid: 201)
 *
 * Event description: Search event
 *
 * @param {searchParams} params - The parameters for this event
 * @property {string} searchTerms - search terms
 * @property {string} vespaRequest - vespa request
 * @property {GUID} visitorId - dummy
 * @property {string} errorMessage - error message
 * @property {number} timeSpent - request time spent
 * @property {boolean} isSuccess - request was successful
 * @property {number} matchedCount - matched document count(total)
 * @property {string} filter - filter
 * @property {number} offset - offset
 * @property {GUID} msid - msid
 * @property {string} settings - search settings json
 * @return {LogObject<searchParams>} LogObject<searchParams>
 */
export function search(params) {
    return { evid: 201, src: 99, endpoint: '', params };
}
/**
 * A function to create a log object for the 'searchResultsSettingsDisplayFacetsToggle' event (evid: 21)
 *
 * Event description: Used toggled Facets button visibility
 *
 * @param {searchResultsSettingsDisplayFacetsToggleParams} params - The parameters for this event
 * @property {boolean} status - true/false
 * @return {LogObject<searchResultsSettingsDisplayFacetsToggleParams>} LogObject<searchResultsSettingsDisplayFacetsToggleParams>
 */
export function searchResultsSettingsDisplayFacetsToggle(params) {
    return { evid: 21, src: 99, endpoint: '', params };
}
/**
 * A function to create a log object for the 'searchSettingsDisplayChooseResultPageNamePropertyToggleCheckbox' event (evid: 22)
 *
 * Event description: This events happens when user enter
 * search result setting> Display> choose category(product, Forum, Booking..) > Togglingqcheckbox Something
 *
 * older events that we removed since 2021-11-01 and changed to this generic events are:
 * 99:21- toggle filter in products (facets)
 * 99:17- toggle add to cart in products
 *
 * @param {searchSettingsDisplayChooseResultPageNamePropertyToggleCheckboxParams} params - The parameters for this event
 * @property {boolean} status - status
 * @property {string} category - products/bookings/forum_posts/all/pages/blog_posts     the category user chose to change the disply
 * @property {string} property - property
 * @return {LogObject<searchSettingsDisplayChooseResultPageNamePropertyToggleCheckboxParams>} LogObject<searchSettingsDisplayChooseResultPageNamePropertyToggleCheckboxParams>
 */
export function searchSettingsDisplayChooseResultPageNamePropertyToggleCheckbox(params) {
    return { evid: 22, src: 99, endpoint: '', params };
}
/**
 * A function to create a log object for the 'searchSettingsSearchSeoHiddenToggled' event (evid: 23)
 *
 * Event description: Fired when a user changes whether or not search through SEO hidden items
 *
 * @param {searchSettingsSearchSeoHiddenToggledParams} params - The parameters for this event
 * @property {boolean} status - true/false
 * @return {LogObject<searchSettingsSearchSeoHiddenToggledParams>} LogObject<searchSettingsSearchSeoHiddenToggledParams>
 */
export function searchSettingsSearchSeoHiddenToggled(params) {
    return { evid: 23, src: 99, endpoint: '', params };
}
/**
 * A function to create a log object for the 'searchBoxSearchSubmit' event (evid: 240)
 *
 * Event description: Should be sent after visitor submitted search in SearchBox by clicking 'Enter'
 *
 * @param {searchBoxSearchSubmitParams} params - The parameters for this event
 * @property {boolean} isDemo - True|False
 * @property {string} target - what does user search for (term, phrase, word)
 * @property {GUID} correlationId - guid should be unique for the search per visitor
 * @return {LogObject<searchBoxSearchSubmitParams>} LogObject<searchBoxSearchSubmitParams>
 */
export function searchBoxSearchSubmit(params) {
    return { evid: 240, src: 99, endpoint: '', params };
}
/**
 * A function to create a log object for the 'searchBoxSuggestionsRequestStarted' event (evid: 241)
 *
 * Event description: Should be sent after visitor change query in SearchBox
 *
 * @param {searchBoxSuggestionsRequestStartedParams} params - The parameters for this event
 * @property {GUID} correlationId - guid should be unique for the search per visitor
 * @property {string} target - what does user search for (term, phrase, word)
 * @return {LogObject<searchBoxSuggestionsRequestStartedParams>} LogObject<searchBoxSuggestionsRequestStartedParams>
 */
export function searchBoxSuggestionsRequestStarted(params) {
    return { evid: 241, src: 99, endpoint: '', params };
}
/**
 * A function to create a log object for the 'searchBoxSuggestionsRequestFinished' event (evid: 242)
 *
 * Event description: Sent every time we show new set of suggestions to the visitor while she's typing into the search box
 *
 * @param {searchBoxSuggestionsRequestFinishedParams} params - The parameters for this event
 * @property {boolean} success - success
 * @property {number} resultCount - result count
 * @property {string} resultsArray - {"all":17, "pages":5, "products":2, "blog":10}
 * @property {string} documentIds - {"public/stores/products": ["id1", "id2", ...],   "public/blog/posts": ["id1", "id2", ...], ...}
 * @property {string} error - error
 * @property {string} target - what does user search for (term, phrase, word)
 * @property {GUID} correlationId - guid should be unique for the search per visitor
 * @property {string} type - trending/suggestions
 * @property {number} loadingDuration - time between load start and load.finish
 * @return {LogObject<searchBoxSuggestionsRequestFinishedParams>} LogObject<searchBoxSuggestionsRequestFinishedParams>
 */
export function searchBoxSuggestionsRequestFinished(params) {
    return { evid: 242, src: 99, endpoint: '', params };
}
/**
 * A function to create a log object for the 'searchBoxSuggestionClick' event (evid: 243)
 *
 * Event description: Should be sent after visitor submitted search in SearchBox by clicking 'Enter'
 *
 * @param {searchBoxSuggestionClickParams} params - The parameters for this event
 * @property {string} resultsArray - {"all":17, "pages":5, "products":2, "blog":10}
 * @property {string} pageUrl - url
 * @property {string} resultClicked - the result title the user clicked on
 * @property {string} documentType - pages|products|blog
 * @property {number} searchIndex - index of the clicked item from search result set (search_index starts from 0)
 * @property {string} target - what does user search for (term, phrase, word)
 * @property {string} documentId - Unique id for each vertical's document
 * @property {string} correlationId - correlation_id
 * @property {string} type - trending/suggestions
 * @return {LogObject<searchBoxSuggestionClickParams>} LogObject<searchBoxSuggestionClickParams>
 */
export function searchBoxSuggestionClick(params) {
    return { evid: 243, src: 99, endpoint: '', params };
}
/**
 * A function to create a log object for the 'searchBoxSuggestionShowAllClick' event (evid: 244)
 *
 * Event description: Should be sent after visitor submitted search in SearchBox by clicking 'Enter'
 *
 * @param {searchBoxSuggestionShowAllClickParams} params - The parameters for this event
 * @property {string} documentType - pages|products|blog | all
 * @property {string} target - what does user search for (term, phrase, word)
 * @property {GUID} correlationId - guid should be unique for the search per visitor
 * @return {LogObject<searchBoxSuggestionShowAllClickParams>} LogObject<searchBoxSuggestionShowAllClickParams>
 */
export function searchBoxSuggestionShowAllClick(params) {
    return { evid: 244, src: 99, endpoint: '', params };
}
/**
 * A function to create a log object for the 'searchBoxFocused' event (evid: 245)
 *
 * Event description: A search box was focused
 *
 * @param {searchBoxFocusedParams} params - The parameters for this event
 * @property {boolean} isDemo - Indicates that the event was fired from Editor/Preview (true) or live site (false)
 * @property {boolean} isFullscreen - Indicates whether UoU was shown a fullscreen search box experience in mobile
 * @return {LogObject<searchBoxFocusedParams>} LogObject<searchBoxFocusedParams>
 */
export function searchBoxFocused(params) {
    return { evid: 245, src: 99, endpoint: '', params };
}
/**
 * A function to create a log object for the 'searchBoxStartedWritingAQuery' event (evid: 246)
 *
 * Event description: Fires when input length changes from 0 to 1.
 *
 * @param {searchBoxStartedWritingAQueryParams} params - The parameters for this event
 * @property {boolean} isDemo - true | false
 * @return {LogObject<searchBoxStartedWritingAQueryParams>} LogObject<searchBoxStartedWritingAQueryParams>
 */
export function searchBoxStartedWritingAQuery(params) {
    return { evid: 246, src: 99, endpoint: '', params };
}
/**
 * A function to create a log object for the 'searchBoxCleared' event (evid: 247)
 *
 * Event description: Search Box > Cleared.
 *
 * @param {searchBoxClearedParams} params - The parameters for this event
 * @property {string} resultsArray - results_array
 * @property {boolean} isDemo - is_demo
 * @property {string} target - what does user search for (term, phrase, word)true/false
 * @property {string} correlationId - Used to match many different BI events with the same search
 * @return {LogObject<searchBoxClearedParams>} LogObject<searchBoxClearedParams>
 */
export function searchBoxCleared(params) {
    return { evid: 247, src: 99, endpoint: '', params };
}
/**
 * A function to create a log object for the 'searchBoxAutoCompleteShown' event (evid: 248)
 *
 * Event description: Should be sent after visitor got auto complete in search box
 *
 * @param {searchBoxAutoCompleteShownParams} params - The parameters for this event
 * @property {string} suggestion - what was suggested?
 * @property {string} target - what does user search for (term, phrase, word)
 * @property {GUID} correlationId - guid should be unique for the search per visitor
 * @return {LogObject<searchBoxAutoCompleteShownParams>} LogObject<searchBoxAutoCompleteShownParams>
 */
export function searchBoxAutoCompleteShown(params) {
    return { evid: 248, src: 99, endpoint: '', params };
}
/**
 * A function to create a log object for the 'searchBoxAutoCompleteShownSuggestionApproved' event (evid: 249)
 *
 * Event description: Should be sent after visitor got auto complete in search box and approved it
 *
 * @param {searchBoxAutoCompleteShownSuggestionApprovedParams} params - The parameters for this event
 * @property {string} previousTarget - what was before the suggestion approved?
 * @property {string} target - what does user search for (term, phrase, word)
 * @property {GUID} correlationId - guid should be unique for the search per visitor
 * @return {LogObject<searchBoxAutoCompleteShownSuggestionApprovedParams>} LogObject<searchBoxAutoCompleteShownSuggestionApprovedParams>
 */
export function searchBoxAutoCompleteShownSuggestionApproved(params) {
    return { evid: 249, src: 99, endpoint: '', params };
}
/**
 * A function to create a log object for the 'renderingFinished' event (evid: 3)
 *
 * Event description: Should be sent when rendering is finished after GFPP click/click from right menu (to calculate success rate of opened-panel rendering)
 *
 * @param {renderingFinishedParams} params - The parameters for this event
 * @property {number} loadingDuration - loading time (between click on Settings tab and finish rendering), ms
 * @return {LogObject<renderingFinishedParams>} LogObject<renderingFinishedParams>
 */
export function renderingFinished(params) {
    return { evid: 3, src: 99, endpoint: '', params };
}
/**
 * A function to create a log object for the 'searchSubmit' event (evid: 300)
 *
 * Event description: Should be sent after visitor submitted search in Search Result by clicking 'Enter'
 *
 * @param {searchSubmitParams} params - The parameters for this event
 * @property {boolean} isDemo - True|False
 * @property {string} target - what does user search for (term, phrase, word)
 * @property {GUID} correlationId - guid should be unique for the search per visitor
 * @return {LogObject<searchSubmitParams>} LogObject<searchSubmitParams>
 */
export function searchSubmit(params) {
    return { evid: 300, src: 99, endpoint: '', params };
}
/**
 * A function to create a log object for the 'documentFeeding' event (evid: 3000)
 *
 * Event description: Document feeding for Visual and Text Semantic Search Service
 *
 * @param {documentFeedingParams} params - The parameters for this event
 * @property {GUID} clientId - clientId field
 * @property {string} errorMessage - Error message if the document feeding operation failed
 * @property {string} platformName - platform name
 * @property {string} doc_id - Document ID
 * @property {boolean} isSuccess - True if the document feeding operation succeeded
 * @property {string} feedType - VFeeder Producer or Consumer type
 * @property {string} operation - Feeding operation (put, update-or-create, delete, etc.)
 * @property {string} condition - Feeding operation condition
 * @property {string} schema - Document schema
 * @property {string} topicName - Greyhound topic name
 * @property {string} appdefid - appDefId
 * @property {string} tenantId - Document tenant id for streaming schema
 * @property {string} type - Document type
 * @property {string} batchID - ID of the feeding documents batch
 * @property {number} number_of_items - Number of feeding documents
 * @property {string} application_id - Application associated with the document
 * @property {string} isCrossDCRequest - Indicates whether the request was sent to a cluster in the same data center or to a cluster in another data center
 * @return {LogObject<documentFeedingParams>} LogObject<documentFeedingParams>
 */
export function documentFeeding(params) {
    return { evid: 3000, src: 99, endpoint: '', params };
}
/**
 * A function to create a log object for the 'loadingStarted' event (evid: 301)
 *
 * Event description: Should be sent after visitor has been redirected to the search results page and rendering process for widget started
 *
 * @param {loadingStartedParams} params - The parameters for this event
 * @return {LogObject<loadingStartedParams>} LogObject<loadingStartedParams>
 */
export function loadingStarted(params) {
    return { evid: 301, src: 99, endpoint: '', params };
}
/**
 * A function to create a log object for the 'loadingFinished' event (evid: 302)
 *
 * Event description: Event should be sent when widget rendering finished
 *
 * @param {loadingFinishedParams} params - The parameters for this event
 * @property {boolean} success - True|False
 * @property {string} error - error message
 * @property {number} loadingDuration - time  between loading.started and loading.finished,ms
 * @return {LogObject<loadingFinishedParams>} LogObject<loadingFinishedParams>
 */
export function loadingFinished(params) {
    return { evid: 302, src: 99, endpoint: '', params };
}
/**
 * A function to create a log object for the 'searchRequestStarted' event (evid: 303)
 *
 * Event description: Should be sent after visitor has been submitted search and request was sent to server
 *
 * @param {searchRequestStartedParams} params - The parameters for this event
 * @property {string} orderingDirection - Ordering direction, one of:  ASC, DESC.
 * @property {string} orderingFieldName - Ordering field name
 * @property {boolean} isDemo - True|False
 * @property {string} documentType - pages|products|blog
 * @property {string} target - what does user search for (term, phrase, word)
 * @property {GUID} correlationId - guid should be unique for the search per visitor
 * @return {LogObject<searchRequestStartedParams>} LogObject<searchRequestStartedParams>
 */
export function searchRequestStarted(params) {
    return { evid: 303, src: 99, endpoint: '', params };
}
/**
 * A function to create a log object for the 'searchRequestFinished' event (evid: 304)
 *
 * Event description: Should be sent after visitor has been submitted search and request result was received
 *
 * @param {searchRequestFinishedParams} params - The parameters for this event
 * @property {boolean} success - true|false
 * @property {number} resultCount - result count for specific document type
 * @property {string} resultsArray - {"all":17, "pages":5, "products":2, "blog":10}
 * @property {string} orderingDirection - Ordering direction, one of: ASC, DESC.
 * @property {string} documentIds - {"public/stores/products": ["id1", "id2", ...], "public/blog/posts": ["id1", "id2", ...], ...}
 * @property {string} orderingFieldName - Ordering field name
 * @property {boolean} isDemo - True|False
 * @property {string} selectedFacets - json with all selected facets, in case no facets chosen this is null!
 * @property {string} error - error message
 * @property {string} origin - why the result page was loaded?  tab_change/editor_search_bar/result_page_search_bar/direct_visit/sort/page_change/facets
 * @property {string} documentType - pages|products|blog
 * @property {string} availableFacets - json with all availabel facets
 * @property {string} target - what does user search for (term, phrase, word)
 * @property {GUID} correlationId - guid should be unique for the search per visitor
 * @property {number} loadingDuration - time between request.sent and request.resultReceived
 * @return {LogObject<searchRequestFinishedParams>} LogObject<searchRequestFinishedParams>
 */
export function searchRequestFinished(params) {
    return { evid: 304, src: 99, endpoint: '', params };
}
/**
 * A function to create a log object for the 'documentClick' event (evid: 305)
 *
 * Event description: Event should be sent on visitor's click on Search Result
 *
 * @param {documentClickParams} params - The parameters for this event
 * @property {string} clickOrigin - image/title/add_to_cart
 * @property {string} resultsArray - {"all":17, "pages":5, "products":2, "blog":10}
 * @property {string} pageUrl - URL
 * @property {string} resultClicked - the result title the user clicked on
 * @property {boolean} isDemo - True|False
 * @property {string} documentType - all|pages|products|blogPosts
 * @property {number} searchIndex - index of the clicked item from search result set (search_index starts from 0)
 * @property {string} target - past was page title since 03-21 it is the search term  the user wrote
 * @property {string} documentId - Unique id for each vertical's document
 * @property {GUID} correlationId - guid should be unique for the search per visitor
 * @return {LogObject<documentClickParams>} LogObject<documentClickParams>
 */
export function documentClick(params) {
    return { evid: 305, src: 99, endpoint: '', params };
}
/**
 * A function to create a log object for the 'documentTypeChange' event (evid: 306)
 *
 * Event description: Event should be sent on result tab click (one of All/Pages/Products/BlogPost) or viewAll
 *
 * @param {documentTypeChangeParams} params - The parameters for this event
 * @property {string} source - ViewAll|Tab
 * @property {string} tabName - the tab the user clicked in the search results page, in case user navigates between tabs
 * @property {boolean} isDemo - True|False
 * @property {string} target - all|pages|products|blogPosts
 * @property {GUID} correlationId - guid should be unique for the search per visitor
 * @return {LogObject<documentTypeChangeParams>} LogObject<documentTypeChangeParams>
 */
export function documentTypeChange(params) {
    return { evid: 306, src: 99, endpoint: '', params };
}
/**
 * A function to create a log object for the 'searchResultsClickResetFacets' event (evid: 307)
 *
 * Event description: user clicked clear filters in result page
 *
 * @param {searchResultsClickResetFacetsParams} params - The parameters for this event
 * @property {string} clickOrigin - empty_result_page
 * @property {boolean} isDemo - True|False
 * @property {string} selectedFacets - json with all selected facets, in case no facets chosen this is null!
 * @property {string} availableFacets - json with all availabel facets
 * @property {string} target - what does user search for (term, phrase, word)
 * @property {GUID} correlationId - guid should be unique for the search per visitor
 * @return {LogObject<searchResultsClickResetFacetsParams>} LogObject<searchResultsClickResetFacetsParams>
 */
export function searchResultsClickResetFacets(params) {
    return { evid: 307, src: 99, endpoint: '', params };
}
/**
 * A function to create a log object for the 'wixSearchBarClickX' event (evid: 309)
 *
 * Event description: Should be sent after visitor submitted search in Search Result by clicking 'Enter'
 *
 * @param {wixSearchBarClickXParams} params - The parameters for this event
 * @property {boolean} isDemo - True|False
 * @return {LogObject<wixSearchBarClickXParams>} LogObject<wixSearchBarClickXParams>
 */
export function wixSearchBarClickX(params) {
    return { evid: 309, src: 99, endpoint: '', params };
}
/**
 * A function to create a log object for the 'searchRequest' event (evid: 3500)
 *
 * Event description: Search query details
 *
 * @param {searchRequestParams} params - The parameters for this event
 * @property {string} searchTerms - search terms
 * @property {string} executionDetails - execution details (json formatted)
 * @property {string} requestURI - request URI
 * @property {number} responseCode - response code
 * @property {string} filterClause - native Vespa filter clause
 * @property {GUID} clientId - system client_id
 * @property {string} errorMessage - error message
 * @property {number} requestTime - request time
 * @property {string} grouping - custom grouping statement
 * @property {string} platformName - platform name
 * @property {number} scannedDocsCount - scanned documents count
 * @property {number} matchedDocsCount - matched docs count
 * @property {string} coverage - search execution coverage
 * @property {boolean} isSuccess - success response status
 * @property {string} filter - platformize API filter
 * @property {number} offset - offset
 * @property {string} queryProfileParams - query profile params
 * @property {string} responseMessage - response message
 * @property {string} queryProfile - query profile
 * @property {string} appdefid - appDefId
 * @property {string} rankProfile - rank profile
 * @property {number} limit - limit
 * @property {string} customRankFeatures - custom rank features (json formatted)
 * @property {string} isCrossDCRequest - Indicates whether the request was sent to a cluster in the same data center or to a cluster in another data center
 * @return {LogObject<searchRequestParams>} LogObject<searchRequestParams>
 */
export function searchRequest(params) {
    return { evid: 3500, src: 99, endpoint: '', params };
}
/**
 * A function to create a log object for the 'searchBarPlaceholderTextChanged' event (evid: 4)
 *
 * Event description: Should be sent only when text changed on the page
 *
 * @param {searchBarPlaceholderTextChangedParams} params - The parameters for this event
 * @property {string} target - placeholder text
 * @return {LogObject<searchBarPlaceholderTextChangedParams>} LogObject<searchBarPlaceholderTextChangedParams>
 */
export function searchBarPlaceholderTextChanged(params) {
    return { evid: 4, src: 99, endpoint: '', params };
}
/**
 * A function to create a log object for the 'clickOnSearchIconInStudioEditorSrc99Evid400' event (evid: 400)
 *
 * Event description: User Clicked on Search icon in Studio Editor
 *
 * @param {clickOnSearchIconInStudioEditorSrc99Evid400Params} params - The parameters for this event
 * @return {LogObject<clickOnSearchIconInStudioEditorSrc99Evid400Params>} LogObject<clickOnSearchIconInStudioEditorSrc99Evid400Params>
 */
export function clickOnSearchIconInStudioEditorSrc99Evid400(params) {
    return { evid: 400, src: 99, endpoint: '', params };
}
/**
 * A function to create a log object for the 'showSuggestionsToggleSrc99Evid401' event (evid: 401)
 *
 * Event description: User clicked on "Show result suggestions..." in Wix Site Search "Search Bar Settings" Modal
 *
 * @param {showSuggestionsToggleSrc99Evid401Params} params - The parameters for this event
 * @property {boolean} toggle - If option enabled
 * @return {LogObject<showSuggestionsToggleSrc99Evid401Params>} LogObject<showSuggestionsToggleSrc99Evid401Params>
 */
export function showSuggestionsToggleSrc99Evid401(params) {
    return { evid: 401, src: 99, endpoint: '', params };
}
/**
 * A function to create a log object for the 'showTrendingToggleSrc99Evid402' event (evid: 402)
 *
 * Event description: User clicked on "Show trending items..." in Wix Site Search "Search Bar Settings" Modal
 *
 * @param {showTrendingToggleSrc99Evid402Params} params - The parameters for this event
 * @property {boolean} toggle - If option is on
 * @return {LogObject<showTrendingToggleSrc99Evid402Params>} LogObject<showTrendingToggleSrc99Evid402Params>
 */
export function showTrendingToggleSrc99Evid402(params) {
    return { evid: 402, src: 99, endpoint: '', params };
}
/**
 * A function to create a log object for the 'showPlaceholderTextToggleSrc99Evid403' event (evid: 403)
 *
 * Event description: User clicked on "Show placeholder text" in Wix Site Search "Search Bar Settings" Modal
 *
 * @param {showPlaceholderTextToggleSrc99Evid403Params} params - The parameters for this event
 * @property {boolean} toggle - If option is on
 * @return {LogObject<showPlaceholderTextToggleSrc99Evid403Params>} LogObject<showPlaceholderTextToggleSrc99Evid403Params>
 */
export function showPlaceholderTextToggleSrc99Evid403(params) {
    return { evid: 403, src: 99, endpoint: '', params };
}
/**
 * A function to create a log object for the 'showIconToggleSrc99Evid404' event (evid: 404)
 *
 * Event description: User clicked on "Show icon" in Wix Site Search "Search Bar Settings" Modal
 *
 * @param {showIconToggleSrc99Evid404Params} params - The parameters for this event
 * @property {boolean} toggle - If option is on
 * @return {LogObject<showIconToggleSrc99Evid404Params>} LogObject<showIconToggleSrc99Evid404Params>
 */
export function showIconToggleSrc99Evid404(params) {
    return { evid: 404, src: 99, endpoint: '', params };
}
/**
 * A function to create a log object for the 'buttonTypeDropdownSrc99Evid405' event (evid: 405)
 *
 * Event description: User clicked on "Button type" in Wix Site Search "Search Bar Settings" Modal
 *
 * @param {buttonTypeDropdownSrc99Evid405Params} params - The parameters for this event
 * @property {string} chosen_option - "Text and icon" / "Icon only" / "Text only"
 * @return {LogObject<buttonTypeDropdownSrc99Evid405Params>} LogObject<buttonTypeDropdownSrc99Evid405Params>
 */
export function buttonTypeDropdownSrc99Evid405(params) {
    return { evid: 405, src: 99, endpoint: '', params };
}
/**
 * A function to create a log object for the 'suggestionSettingsToggleSrc99Evid406' event (evid: 406)
 *
 * Event description: User clicked on toggle in Wix Site Search "Suggestion Settings" Modal
 *
 * @param {suggestionSettingsToggleSrc99Evid406Params} params - The parameters for this event
 * @property {boolean} toggle - If option is on
 * @property {string} toggle_name - Popular terms / Clicking outside
 * @return {LogObject<suggestionSettingsToggleSrc99Evid406Params>} LogObject<suggestionSettingsToggleSrc99Evid406Params>
 */
export function suggestionSettingsToggleSrc99Evid406(params) {
    return { evid: 406, src: 99, endpoint: '', params };
}
/**
 * A function to create a log object for the 'suggestionElementsCheckboxSrc99Evid407' event (evid: 407)
 *
 * Event description: User checked / unchecked on checkboxes in Wix Site Search "Suggestion Elements" Modal
 *
 * @param {suggestionElementsCheckboxSrc99Evid407Params} params - The parameters for this event
 * @property {boolean} check_box - If option is on
 * @property {string} check_box_name - Search Suggestions / Result Suggestions / Show All
 * @property {boolean} is_enabled - Is checkbox enabled
 * @return {LogObject<suggestionElementsCheckboxSrc99Evid407Params>} LogObject<suggestionElementsCheckboxSrc99Evid407Params>
 */
export function suggestionElementsCheckboxSrc99Evid407(params) {
    return { evid: 407, src: 99, endpoint: '', params };
}
/**
 * A function to create a log object for the 'widgetElementsCheckboxSrc99Evid408' event (evid: 408)
 *
 * Event description: User checked / unchecked on checkboxes in Wix Site Search "Widget Elements" Modal
 *
 * @param {widgetElementsCheckboxSrc99Evid408Params} params - The parameters for this event
 * @property {boolean} check_box - If option is on
 * @property {string} check_box_name - Title / Paragraph / image
 * @return {LogObject<widgetElementsCheckboxSrc99Evid408Params>} LogObject<widgetElementsCheckboxSrc99Evid408Params>
 */
export function widgetElementsCheckboxSrc99Evid408(params) {
    return { evid: 408, src: 99, endpoint: '', params };
}
/**
 * A function to create a log object for the 'uoUClickOnShowAllResultsSrc99Evid409' event (evid: 409)
 *
 * Event description: User of User Click on Show All Results button
 *
 * @param {uoUClickOnShowAllResultsSrc99Evid409Params} params - The parameters for this event
 * @return {LogObject<uoUClickOnShowAllResultsSrc99Evid409Params>} LogObject<uoUClickOnShowAllResultsSrc99Evid409Params>
 */
export function uoUClickOnShowAllResultsSrc99Evid409(params) {
    return { evid: 409, src: 99, endpoint: '', params };
}
/**
 * A function to create a log object for the 'uoUClickOnSearchIconSrc99Evid410' event (evid: 410)
 *
 * Event description: User of User Click on Search icon, srtating the search flow
 *
 * @param {uoUClickOnSearchIconSrc99Evid410Params} params - The parameters for this event
 * @return {LogObject<uoUClickOnSearchIconSrc99Evid410Params>} LogObject<uoUClickOnSearchIconSrc99Evid410Params>
 */
export function uoUClickOnSearchIconSrc99Evid410(params) {
    return { evid: 410, src: 99, endpoint: '', params };
}
/**
 * A function to create a log object for the 'searchButtonAddedSrc99Evid411' event (evid: 411)
 *
 * Event description: Search button added to the website (either by User or automatically during Search App Installation)
 *
 * @param {searchButtonAddedSrc99Evid411Params} params - The parameters for this event
 * @property {string} button_type - Search Button / Search Bar
 * @return {LogObject<searchButtonAddedSrc99Evid411Params>} LogObject<searchButtonAddedSrc99Evid411Params>
 */
export function searchButtonAddedSrc99Evid411(params) {
    return { evid: 411, src: 99, endpoint: '', params };
}
/**
 * A function to create a log object for the 'numberPerPageChanged' event (evid: 5)
 *
 * Event description: Number of results per page was changed (Slider on Settings tab)
 *
 * @param {numberPerPageChangedParams} params - The parameters for this event
 * @property {string} target - number of results per page (25,13,10)
 * @return {LogObject<numberPerPageChangedParams>} LogObject<numberPerPageChangedParams>
 */
export function numberPerPageChanged(params) {
    return { evid: 5, src: 99, endpoint: '', params };
}
/**
 * A function to create a log object for the 'checkboxChanged' event (evid: 6)
 *
 * Event description: Select message that will be displayed for the categories 'Results found' and 'No Results'
 *
 * @param {checkboxChangedParams} params - The parameters for this event
 * @property {string} name - results_message_1, results_message_2, no_results_message_1, no_results_message_2
 * @property {boolean} isEnabled - True|False
 * @return {LogObject<checkboxChangedParams>} LogObject<checkboxChangedParams>
 */
export function checkboxChanged(params) {
    return { evid: 6, src: 99, endpoint: '', params };
}
/**
 * A function to create a log object for the 'layoutChanged' event (evid: 7)
 *
 * Event description: Change layout for Search Results -> Settings -> Layout tab
 * (possible changes for pagination bar and menu section)
 *
 * @param {layoutChangedParams} params - The parameters for this event
 * @property {string} choice - left/center/right
 * @property {string} target - menu/pagination alignment
 * @property {string} initiatorName - {auto or manual}
 * @return {LogObject<layoutChangedParams>} LogObject<layoutChangedParams>
 */
export function layoutChanged(params) {
    return { evid: 7, src: 99, endpoint: '', params };
}
/**
 * A function to create a log object for the 'customizeResultsButtonClick' event (evid: 8)
 *
 * Event description: Click on Customize Results button
 *
 * @param {customizeResultsButtonClickParams} params - The parameters for this event
 * @return {LogObject<customizeResultsButtonClickParams>} LogObject<customizeResultsButtonClickParams>
 */
export function customizeResultsButtonClick(params) {
    return { evid: 8, src: 99, endpoint: '', params };
}
/**
 * A function to create a log object for the 'searchResultsSettingsDesignChanged' event (evid: 9)
 *
 * Event description: Change color, opacity or font style of a field in the search settings design tab
 *
 * @param {searchResultsSettingsDesignChangedParams} params - The parameters for this event
 * @property {string} name - the field that was styled (e.g searchbar.text searchresults.highlight.background)
 * @property {string} type - what design was styled (e.g color, opacity, font style)
 * @property {GUID} msid - msid
 * @return {LogObject<searchResultsSettingsDesignChangedParams>} LogObject<searchResultsSettingsDesignChangedParams>
 */
export function searchResultsSettingsDesignChanged(params) {
    return { evid: 9, src: 99, endpoint: '', params };
}
//# sourceMappingURL=index.js.map